// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * English translations for the AesirX ComplianceOne.
 * Covers all modules through Phase 8 (Sprint 11.3.0 — full i18n coverage).
 */
export const en = {
    // ---------- Login & Authentication (Sprint 11.3.0) ----------
    login: {
        title: 'AesirX ComplianceOne',
        subtitle: 'Sign in to your account',
        email: 'Email',
        password: 'Password',
        sign_in: 'Sign In',
        signing_in: 'Signing in...',
        mfa_code: 'MFA Code',
        mfa_placeholder: 'Enter 6-digit code',
        mfa_instruction: 'Enter the 6-digit code from your authenticator app.',
        verify: 'Verify',
        verifying: 'Verifying...',
        forgot_password: 'Forgot password?',
        account_locked: 'Account is locked. Please try again later.',
        account_deactivated: 'Account has been deactivated. Contact your administrator.',
        invalid_mfa: 'Invalid MFA code. Please try again.',
        invalid_credentials: 'Invalid email or password.',
        login_failed: 'Login failed',
        session_expired: 'Your session has expired. Please reload the page and try again.',
    },
    // ---------- Auth pages (Sprint 11.3.0) ----------
    auth: {
        forgot_password: {
            title: 'Forgot Password',
            subtitle: 'Enter your email address and we will send you a link to reset your password.',
            send: 'Send Reset Link',
            sending: 'Sending...',
            success: 'If that email exists, a password reset link has been sent.',
            back_to_login: 'Back to Login',
        },
        reset_password: {
            title: 'Reset Password',
            subtitle: 'Enter your new password below.',
            new_password: 'New Password',
            confirm_password: 'Confirm Password',
            reset: 'Reset Password',
            resetting: 'Resetting...',
            success: 'Your password has been reset successfully.',
            go_to_login: 'Go to Login',
            password_mismatch: 'Passwords do not match.',
            invalid_token: 'Invalid or missing reset token. Please request a new password reset link.',
            requirements_title: 'Password requirements:',
            password_requirements: 'Password must meet the following requirements:',
            rules: {
                min_length: 'At least 12 characters',
                uppercase: 'At least one uppercase letter',
                lowercase: 'At least one lowercase letter',
                digit: 'At least one digit',
                special: 'At least one special character',
            },
        },
        verify_email: {
            title: 'Email Verification',
            verifying: 'Verifying your email address...',
            success: 'Your email has been verified successfully.',
            invalid_token: 'Invalid or missing verification token.',
            failed: 'Email verification failed. The token may be invalid or expired.',
            go_to_login: 'Go to Login',
        },
        offline: {
            title: 'You are offline',
            message: "It looks like you've lost your internet connection. Please check your network and try again.",
        },
    },
    // ---------- Public intake form (Sprint 1.0.0) ----------
    intake: {
        title: 'Data Rights Request',
        subtitle: 'Submit a data rights request to our organization',
        fields: {
            full_name: 'Full Name',
            email: 'Email Address',
            phone: 'Phone Number (optional)',
            request_type: 'Request Type',
            description: 'Describe Your Request',
            preferred_language: 'Preferred Language',
            consent: 'I consent to the processing of this request',
        },
        submit: 'Submit Request',
        validation: {
            name_required: 'Full name is required',
            email_required: 'A valid email address is required',
            type_required: 'Please select a request type',
            description_required: 'Please describe your request',
            consent_required: 'You must consent to processing to continue',
        },
        error_title: 'Error',
        config_load_error: 'Unable to load intake configuration. Please check the URL.',
        organization_label: 'Organization',
        customer_reference: 'Customer Reference Number',
        country: 'Country / Jurisdiction',
        preferred_channel: 'Preferred Response Channel',
        channels: {
            email: 'Email',
            postal: 'Postal',
            phone: 'Phone',
            portal: 'Portal',
        },
        confirmation: {
            title: 'Request Received',
            message: 'Your request has been successfully recorded. Your reference number is:',
            next_steps: 'We will process your request as soon as possible in accordance with applicable law. The expected processing time is 30 working days.',
        },
    },
    // ---------- Request type labels ----------
    request_types: {
        access: 'Access (DSAR)',
        deletion: 'Deletion',
        correction: 'Correction / Update',
        objection: 'Objection',
        portability: 'Data Portability',
        restriction: 'Restriction of Processing',
        consent_withdrawal: 'Consent Withdrawal',
    },
    // ---------- Case statuses ----------
    statuses: {
        new: 'New',
        in_review: 'In Review',
        waiting_on_requester: 'Waiting on Requester',
        waiting_on_internal_data: 'Waiting on Internal Data',
        approved: 'Approved',
        closed: 'Closed',
        rejected: 'Rejected',
    },
    // ---------- Admin UI ----------
    admin: {
        nav: {
            rights_requests: 'Rights Requests',
            templates: 'Templates',
            response_templates: 'Response Templates',
            data_mapping: 'Data Mapping',
            governance: 'Governance',
            consent: 'Consent',
            assessments: 'Assessments',
            classification: 'Classification',
            monitoring: 'Monitoring',
            incidents: 'Incidents',
            onboarding: 'Onboarding',
            connectors: 'Connectors',
            vendor_risk: 'Vendor Risk',
            deletion_orchestration: 'Deletion Orchestration',
            discovery: 'Data Discovery',
            incident_ops: 'Incident Operations',
            program_governance: 'Program Governance',
            access_accountability: 'Access Accountability',
            consent_properties: 'Consent Properties',
            localization: 'Localization',
            partner_workspace: 'Partner Workspace',
            audit_trail: 'Audit Trail',
            // Sprint 47.5.2 — Forseti AI Legal Advisor menu label.
            forseti: 'Forseti',
            settings: 'Settings',
            help_center: 'Help Center',
            dashboard: 'Dashboard',
            platform: 'Platform',
            integrations: 'Integrations',
            cmp_dsar_sync: 'CMP DSAR Sync',
            cmp_integration: 'CMP Integration',
            purpose_mapping: 'Purpose Mapping',
            webhook_config: 'Webhook Config',
            bulk_sync: 'Re-map Records',
            privacy_scanner: 'Privacy Scanner',
            privacy_monitoring: 'Privacy Monitoring',
            real_time_scanner: 'Real-Time Scanner',
            compliance_forms: 'Compliance Forms',
            forms_dashboard: 'Forms Dashboard',
            form_templates: 'Templates',
            form_catalog: 'Form Catalog',
            pack_catalog: 'Pack Catalog',
            org_groups: 'Org Groups',
            consolidation: 'Consolidation',
            organizations: 'Organizations',
            org_list: 'All Organizations',
            intake_configs: 'Intake Configs',
            task_management: 'Task Management',
            unified_audit: 'Unified Audit',
            evidence_packs: 'Evidence Packs',
            audit_periods: 'Audit Periods',
            regulatory_updates: 'Regulatory Updates',
            departments: 'Departments',
        },
        sidebar_category_data_privacy_rights: 'Data Privacy & Rights',
        sidebar_category_vendor_third_party: 'Vendor & Third-Party Management',
        sidebar_category_risk_assessment: 'Risk & Assessment',
        sidebar_category_operations_automation: 'Operations & Automation',
        sidebar_category_task_workflow: 'Task & Workflow Management',
        // Sidebar sub-group labels (3rd-tier nav). Added in the sidebar-shortening
        // sprint; EN + VI only. Remaining 14 locales fall back to EN via deepMerge
        // until Sprint 51.0.0 adds proper translations.
        sidebar_groups: {
            // Consent
            consent_cmp_webhooks: 'CMP & Webhooks',
            // Data Mapping
            data_mapping_ropa: 'RoPA Register',
            data_mapping_classification: 'Classification',
            data_mapping_governance: 'Governance',
            // Access & Accountability
            access_live_monitoring: 'Live Monitoring',
            access_reviews_investigations: 'Reviews & Investigations',
            access_evidence_policies: 'Evidence & Policies',
            // Classification
            classification_taxonomies: 'Taxonomies & Labels',
            classification_workflow: 'Workflow',
            classification_insights: 'Insights',
            // Connectors
            connectors_runtime: 'Runtime',
            connectors_due_diligence: 'Due Diligence',
            connectors_coverage: 'Coverage',
            // Consent Properties
            cp_definitions: 'Definitions',
            cp_lifecycle: 'Lifecycle',
            cp_quality: 'Quality & Evidence',
            // Deletion Orchestration
            deletion_execution: 'Execution',
            deletion_vendors: 'Vendors',
            deletion_evidence: 'Evidence & Integration',
            // Discovery
            discovery_rules: 'Rules & Templates',
            discovery_scanning: 'Scanning',
            discovery_review: 'Review Workflow',
            discovery_insights: 'Insights',
            // Governance
            governance_oversight: 'Oversight',
            governance_reporting: 'Reporting',
            governance_vendors: 'Vendors & Cross-Border',
            governance_lifecycle: 'Lifecycle',
            // Incident Ops
            incident_ops_dashboards: 'Dashboards',
            incident_ops_sla: 'SLA Config',
            incident_ops_playbooks: 'Playbooks',
            incident_ops_vendor_regulator: 'Vendor & Regulator',
            incident_ops_evidence: 'Evidence & Review',
            // Incidents
            incidents_workload: 'Workload',
            incidents_regulatory: 'Regulatory',
            incidents_reporting: 'Reporting',
            // Localization
            localization_content: 'Content',
            localization_regulatory: 'Regulatory',
            localization_observability: 'Observability',
            // Monitoring
            monitoring_metrics: 'Metrics',
            monitoring_programs: 'Programs',
            monitoring_tasks: 'Tasks',
            monitoring_controls: 'Controls',
            // Organizations
            org_entities: 'Entities',
            org_config: 'Configuration',
            org_frameworks: 'Frameworks & Rollup',
            // Partner Workspace
            partner_overview: 'Overview',
            partner_partners: 'Partners',
            partner_delivery: 'Delivery',
            partner_support: 'Support',
            // Platform
            platform_identity: 'Identity & Roles',
            platform_access_control: 'Access Control',
            platform_auth_session: 'Authentication & Session',
            platform_access_reviews: 'Access Reviews',
            platform_files: 'Files',
            platform_notifications: 'Notifications',
            platform_module_config: 'Module Config',
            platform_ops: 'Ops & Logs',
            // Program Governance
            pg_health: 'Program Health',
            pg_maturity: 'Maturity',
            pg_audit: 'Audit & Evidence',
            pg_obligations: 'Obligations',
            pg_documents: 'Documents',
            // Vendor Risk
            vr_overview: 'Overview',
            vr_onboarding: 'Onboarding',
            vr_assessment: 'Assessment',
            vr_oversight: 'Oversight',
        },
        tickets: {
            title: 'Rights Requests',
            create: 'Create Request',
            filters: 'Filters',
            search_placeholder: 'Search by ticket ID or requester...',
            export_csv: 'Export CSV',
            exporting: 'Exporting...',
            bulk_export_failed: 'Bulk export failed',
            // Sprint 43.2.0 batch 4 (E3) — Bulk export menu on TicketListPage.
            bulk_export: {
                label: 'Export',
                format: {
                    pdf: 'PDF Summary',
                    zip: 'ZIP (per-case evidence packs)',
                    csv: 'CSV',
                },
                warning_over_50: 'This will generate approximately {count} case packs - may take several minutes. Continue?',
                blocked_over_500: 'Narrow filter to 500 or fewer cases before exporting ZIP (current: {count}).',
                success: 'Bulk export saved to Files Browser ({count} cases)',
                failed: 'Bulk export failed',
                continue: 'Continue',
                cancel: 'Cancel',
            },
            no_results: 'No requests found',
            ticket_id: 'Ticket ID',
            request_type: 'Request Type',
            requester: 'Requester',
            source: 'Source',
            all_sources: 'All Sources',
            assigned_to: 'Assigned To',
            created: 'Created',
            load_error: 'Failed to load cases. Please try again.',
            case_creation_failed: 'Case creation failed',
            business_unit: 'Business Unit',
            reference: 'Reference',
            sla: {
                title: 'SLA Status',
                on_track: '{days} days remaining',
                approaching: 'Approaching deadline',
                overdue: '{days} days overdue',
                ack_overdue: '{hours}h past acknowledgment deadline',
                ack_warning: 'Acknowledgment due soon',
                closed: 'Closed',
                completed_in: 'Completed in {days} days',
            },
        },
        templates: {
            title: 'Response Templates',
            new: 'New Template',
            edit: 'Edit Template',
            all_types: 'All Types',
            all_locales: 'All Locales',
            type_access: 'Access/DSAR',
            type_deletion: 'Deletion',
            type_correction: 'Correction',
            type_objection: 'Objection',
            type_portability: 'Portability',
            type_other: 'Other',
            type_restriction: 'Restriction',
            type_all: 'All',
            locale_en: 'English',
            locale_vi: 'Vietnamese',
            locale_all: 'Both',
            name: 'Name',
            request_type: 'Request Type',
            locale: 'Locale',
            subject: 'Subject',
            body: 'Body',
            name_placeholder: 'Template name...',
            subject_placeholder: 'Email/letter subject...',
            body_placeholder: 'Template body. Use {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} as placeholders.',
            available_placeholders: 'Available placeholders:',
            create_template: 'Create Template',
            preview: 'Preview',
            close_preview: 'Close Preview',
            default_badge: 'Default',
            no_templates: 'No templates found',
            confirm_delete: 'Delete this template?',
            load_error: 'Failed to load templates. Please try again.',
            create_error: 'Failed to create template',
            update_error: 'Failed to update template',
            delete_error: 'Failed to delete template',
            preview_error: 'Failed to preview template',
            response_templates_badge: 'Response Templates',
            browse_form_templates: 'Browse Compliance Form Templates',
            templates_loaded: 'Templates loaded',
            sample_organization: 'Sample Organization',
        },
        detail: {
            requester_info: 'Requester Information',
            status: 'Status',
            assignment: 'Assignment',
            notes: 'Notes',
            audit_trail: 'Audit Trail',
            add_note: 'Add Note',
            case_not_found: 'Case not found',
            load_error: 'Failed to load case. Please try again.',
            note_error: 'Failed to add note. Please try again.',
            submitted_from: 'Submitted from',
            via_cmp: 'via AesirX CMP',
            cmp_ticket: 'CMP Ticket:',
            select_template: 'Select a Template',
            direction_inbound: 'CMP → GRC',
            direction_outbound: 'GRC → CMP',
            tabs: {
                details: 'Details',
                evidence: 'Evidence',
                communications: 'Communications',
                escalations: 'Escalations',
                audit_trail: 'Audit Trail',
                fulfillment: 'Fulfillment',
                response: 'Response',
                extension: 'Extension',
            },
            requester: {
                name: 'Name',
                email: 'Email',
                phone: 'Phone',
                language: 'Language',
                source: 'Source',
                customer_ref: 'Customer Ref',
                country: 'Country',
                preferred_channel: 'Preferred Channel',
            },
            sla: {
                title: 'SLA',
                on_track: 'On Track',
                due_soon: 'Due Soon',
                overdue: 'Overdue',
                days_remaining: 'd remaining',
                due_date: 'Due Date',
                breached: 'Breached',
                paused: 'Paused',
                total_paused_days: 'Total Paused Days',
                days_elapsed: 'Days Elapsed',
                progress: 'Progress',
            },
            identity: {
                title: 'Identity Verification',
                verified: 'Verified',
                not_verified: 'Not verified',
                method: 'Method',
                notes: 'Notes',
                verified_by: 'Verified By',
                verified_at: 'Verified At',
                warning: 'Identity not yet verified.',
                verify_button: 'Verify Identity',
                select_method: '-- Select --',
                methods: {
                    email_confirmation: 'Email Confirmation',
                    id_document: 'ID Document',
                    phone_call: 'Phone Call',
                    in_person: 'In Person',
                    other: 'Other',
                },
            },
            closure: {
                reason: 'Closure Reason',
                summary: 'Closure Summary',
                summary_placeholder: 'Summary of how the request was resolved...',
                reason_required: 'Closure summary and reason are required',
                response_not_sent: 'A formal response must be sent to the data subject before closing this case',
                reasons: {
                    fulfilled: 'Fulfilled',
                    partially_fulfilled: 'Partially Fulfilled',
                    unfounded: 'Unfounded',
                    excessive: 'Excessive',
                    withdrawn: 'Withdrawn',
                    duplicate: 'Duplicate',
                    identity_unverified: 'Identity Unverified',
                },
            },
            export_title: 'Export',
            export_pdf: 'Export PDF',
            export_zip: 'Export ZIP',
            // Sprint 43.1.0 batch 4 — Case File PDF / Evidence Pack ZIP labels
            case_file_pdf: 'Export PDF (Case File)',
            evidence_pack_zip: 'Export ZIP (Evidence Pack)',
            cross_border_warning: 'Cross-border transfer detected - cross-border dossier required',
            // Sprint 43.2.0 batch 4 (E5) — Breach-trigger UI on TicketDetailPage.
            breach: {
                flag_btn: 'Flag as Breach-Causing',
                modal: {
                    title: 'Flag case as breach-causing',
                    type_label: 'Breach type',
                    type: {
                        misdirected_response: 'Misdirected response',
                        unauthorised_disclosure: 'Unauthorised disclosure',
                        identity_mismatch: 'Identity mismatch',
                        other: 'Other',
                    },
                    description: 'Description (optional)',
                    description_placeholder: 'What happened? (max 1000 chars)',
                    confirm: 'Flag and start 72h filing clock',
                    cancel: 'Cancel',
                    flagging: 'Flagging...',
                },
                banner: {
                    title: 'Breach flagged: {type}',
                    detected_at: 'Detected at {timestamp}',
                    filing_deadline: '72h MPS filing deadline: in {hours} hours',
                    overdue: '72h MPS filing deadline: OVERDUE',
                },
                toasts: {
                    flagged: 'Case flagged as breach-causing - 72h clock started',
                    flag_failed: 'Failed to flag case as breach-causing',
                },
            },
            export: {
                generating: 'Generating... please wait',
                success: 'Export saved to Files Browser',
                failed: 'Export failed - check audit log',
            },
            no_notes: 'No notes yet',
            assign_placeholder: 'User ID to assign...',
            reassign: 'Reassign',
            evidence: {
                checklist_title: 'Evidence Checklist',
                complete_pct: '% complete',
                no_checklist: 'No checklist items',
                attachments: 'Attachments',
                upload_attachment: 'Upload Attachment',
                upload_button: 'Upload',
                uploading: 'Uploading...',
                attachment_uploaded: 'Attachment uploaded successfully',
                upload_error: 'Failed to upload attachment',
                category: 'Category',
                categories: {
                    identity_proof: 'Identity Proof',
                    correspondence: 'Correspondence',
                    internal_data: 'Internal Data',
                    legal: 'Legal',
                    other: 'Other',
                },
                no_attachments: 'No attachments',
                references_title: 'Evidence References',
                no_references: 'No references',
                add_reference: 'Add Reference',
                ref_type: 'Type',
                ref_value: 'Value',
                ref_desc: 'Description',
                ref_value_placeholder: 'URL, email ID, or reference...',
                ref_desc_placeholder: 'Description...',
                ref_types: {
                    url: 'URL',
                    email_id: 'Email ID',
                    crm_record: 'CRM Record',
                    ticket_ref: 'Ticket Reference',
                    other: 'Other',
                },
            },
            communications: {
                title: 'Communications',
                add: 'Add Message',
                no_records: 'No communications recorded',
                direction: 'Direction',
                channel: 'Channel',
                visibility: 'Visibility',
                subject: 'Subject',
                body: 'Body',
                send: 'Send',
                inbound: 'Inbound',
                outbound: 'Outbound',
                internal: 'Internal',
                external: 'External',
                tab_all: 'All',
                tab_internal: 'Internal',
                tab_external: 'External',
                subject_placeholder: 'Subject (optional)',
                body_placeholder: 'Message body...',
                channels: {
                    email: 'Email',
                    portal: 'Portal',
                    phone: 'Phone',
                    postal: 'Postal',
                    in_person: 'In Person',
                },
                apply_template: 'Apply Template',
                select_template: 'Select a template...',
                no_templates: 'No templates available',
                template_applied: 'Template applied to response.',
                template_apply_error: 'Failed to apply template.',
            },
            escalations: {
                title: 'Escalation History',
                no_events: 'No escalation events',
                tier: 'Tier',
                threshold: 'Threshold',
                action: 'Action',
                target: 'Target',
                triggered_at: 'Triggered At',
            },
            cmp_sync: {
                title: 'AesirX CMP Sync',
                sync_status: 'Sync Status',
                last_synced: 'Last Synced',
                not_synced: 'Not Synced',
                resync: 'Resync Now',
                syncing: 'Syncing...',
                sync_history: 'Sync History (Last 10)',
                direction: 'Direction',
                time: 'Time',
                retries: 'Retries',
                error_col: 'Error',
            },
            audit: {
                filter_event_placeholder: 'Filter by event type...',
                filter_actor_placeholder: 'Filter by actor...',
            },
            fulfillment: {
                title: 'Department Fulfillment',
                assign_departments: 'Assign Departments',
                department: 'Department',
                assignee: 'Assignee',
                deadline: 'Deadline',
                evidence_count: 'Evidence',
                signoff: 'Sign-off',
                signed_off: 'Signed Off',
                pending_signoff: 'Pending',
                start_button: 'Start',
                complete_button: 'Mark Complete',
                signoff_button: 'Sign Off',
                no_tasks: 'No fulfillment tasks assigned yet.',
                tasks_table: 'Fulfillment Tasks',
                progress_label: 'Fulfillment Progress',
                overdue: 'overdue',
                assigned_success: 'Department fulfillment task assigned successfully.',
                assign_error: 'Failed to assign fulfillment task.',
                signoff_success: 'Department sign-off recorded.',
                signoff_error: 'Failed to record sign-off.',
                identity_required: 'Identity verification is required before managing fulfillment tasks.',
                remove_task: 'Remove',
                remove_success: 'Task removed',
                add_evidence_btn: 'Add Evidence',
                evidence_uploaded: 'Evidence uploaded and linked',
                evidence_upload_error: 'Failed to upload evidence',
                suggest_data_stores: 'Suggest Data Stores',
                suggest_error: 'Failed to load data store suggestions.',
                suggest_stores_title: 'Suggested Data Stores',
                suggest_stores_none: 'No data stores match this case. Configure systems in Data Mapping to improve suggestions.',
                suggest_stores_add: 'Add to case',
                suggest_stores_added: 'Data store added to case',
                no_departments_title: 'No departments configured',
                no_departments_body: 'Departments let you route DSR fulfillment tasks to a function (Privacy, Legal, HR, IT Ops). Install sample departments or ask your admin to configure departments in the Organization settings.',
                no_departments_cta: 'Install sample data',
                department_placeholder: 'Department name...',
                assignee_placeholder: 'User ID to assign...',
                assign_button: 'Assign',
                status_pending: 'Pending',
                status_in_progress: 'In Progress',
                status_completed: 'Completed',
                status_verified: 'Verified',
                confirm_approve: 'Are you sure you want to approve this extension?',
                confirm_deny: 'Are you sure you want to deny this extension?',
            },
            response: {
                title: 'Response Generation',
                select_template: 'Response Template',
                choose_template: 'Select a template...',
                generate: 'Generate Response',
                preview: 'Response Preview',
                send: 'Send Response',
                // Sprint 43.1.0 batch 4 — delivery_method converted to object
                // (label kept for existing callers; sub-keys for picker options).
                delivery_method: {
                    label: 'Delivery Method',
                    email: 'Email',
                    portal: 'Portal',
                    postal: 'Postal mail',
                    in_person: 'In person',
                },
                recipient: {
                    email: 'Recipient email',
                    portal: 'Portal user ID',
                    address: 'Postal address',
                },
                auto_generate_btn: 'Generate and send response',
                auto_generate_success: 'Response generated and delivered',
                auto_generate_missing_recipient: 'Please provide a recipient before sending.',
                auto_fields: 'Auto-populated fields',
                case_id: 'Case ID',
                requester_name: 'Requester Name',
                rights_type: 'Rights Type',
                history: 'Sent Responses',
                generated_at: 'Generated',
                confirmation: 'Confirmation',
                sent_label: 'Sent',
                draft_label: 'Draft',
                generated_success: 'Response generated successfully.',
                generate_error: 'Failed to generate response.',
                sent_success: 'Response sent successfully.',
                send_error: 'Failed to send response.',
                load_error: 'Failed to load response templates.',
                pdf_title: 'Data Subject Response Letter',
                download_pdf: 'Download PDF',
                download_all: 'Download All',
                download_error: 'Failed to download response',
            },
            extension: {
                title: 'Deadline Extensions',
                acknowledgment_deadline: 'Acknowledgment Deadline',
                fulfillment_deadline: 'Fulfillment Deadline',
                request_extension: 'Request Extension',
                reason: 'Reason',
                reason_placeholder: 'Reason for requesting additional time...',
                additional_days: 'Additional Days',
                submit_request: 'Submit Extension Request',
                history: 'Extension History',
                requested_at: 'Requested',
                approve: 'Approve',
                deny: 'Deny',
                request_success: 'Extension request submitted.',
                request_error: 'Failed to submit extension request.',
                approved_success: 'Extension approved.',
                denied_success: 'Extension denied.',
                decision_error: 'Failed to process extension decision.',
                status_pending: 'Pending',
                status_approved: 'Approved',
                status_denied: 'Denied',
                confirm_approve: 'Are you sure you want to approve this extension request?',
                confirm_deny: 'Are you sure you want to deny this extension request?',
                decided_by: 'Decided By',
            },
        },
        audit: {
            export_csv: 'Export CSV',
            exporting: 'Exporting...',
            search_placeholder: 'Search audit details...',
            filter_by_actor: 'Filter by actor...',
            all_event_types: 'All Event Types',
            seq: '#',
            timestamp: 'Timestamp',
            event: 'Event',
            case_col: 'Case',
            actor: 'Actor',
            summary: 'Summary',
            no_events: 'No audit events found',
            export_success: 'Audit trail exported successfully.',
            export_error: 'Failed to export audit CSV',
            load_error: 'Failed to load audit trail. Please try again.',
            announce_loaded: 'Audit trail loaded',
            start_date: 'Start date',
            end_date: 'End date',
            per_page: '{count} / page',
            unknown: 'Unknown',
            // Sprint 47.9.0a — Source filter on the global audit feed.
            // "All" UNIONs Forseti AI events with the per-module trail; "Module"
            // shows only grc_case_audit_events; "Forseti" shows only
            // grc_ai_audit_events rows already returned by the UNION.
            source_filter: {
                label: 'Source',
                all: 'All',
                module: 'Module',
                forseti: 'Forseti',
                aria_label: 'Filter audit events by source',
            },
            event_types: {
                case_created: 'Case Created',
                status_changed: 'Status Changed',
                case_assigned: 'Case Assigned',
                note_added: 'Note Added',
                attachment_added: 'Attachment Added',
                attachment_deleted: 'Attachment Deleted',
                reference_added: 'Reference Added',
                checklist_updated: 'Checklist Updated',
                identity_verified: 'Identity Verified',
                closure_summary_added: 'Closure Summary',
                approval_granted: 'Approval Granted',
                template_applied: 'Template Applied',
                export_generated: 'Export Generated',
                sla_warning: 'SLA Warning',
                sla_overdue: 'SLA Overdue',
            },
            unified_audit: {
                title: 'Unified Audit Trail',
                module_filter: 'Module',
                framework_filter: 'Framework',
                select_framework: 'Select a framework...',
                entity_type_filter: 'Entity Type',
                action_filter: 'Action',
                actor_filter: 'Actor',
                select_actor: 'Select an actor...',
                search_filter: 'Search',
                search_placeholder: 'Search audit events...',
                filters_heading: 'Filters',
                date_range: 'Date Range',
                no_events_found: 'No audit events found.',
                event_count: 'Event Count',
                action: 'Action',
                actor: 'Actor',
                date_range_start: 'Date Range Start',
                date_range_end: 'Date Range End',
                filtering_events: 'Filtering audit events',
                framework_codes: 'Framework Codes',
                entity_type_col: 'Entity Type',
                aria_table: 'Unified Audit Events',
                page_of: 'Page {page} of {total}',
                entity_types: {
                    user: 'User',
                    case: 'Case',
                    incident: 'Incident',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    consent: 'Consent',
                    policy: 'Policy',
                    data_map: 'Data Map',
                },
                actions: {
                    create: 'Create',
                    update: 'Update',
                    delete: 'Delete',
                    view: 'View',
                    export: 'Export',
                    status_change: 'Status Change',
                },
            },
            evidence_packs: {
                title: 'Audit Evidence Packs',
                assemble_pack: 'Assemble Evidence Pack',
                framework: 'Framework',
                select_framework: 'Select a framework...',
                framework_code_label: 'Framework Code',
                entity_types: 'Entity Types',
                select_entity_types: 'Select entity types...',
                period: 'Period',
                period_start: 'Period Start',
                period_end: 'Period End',
                completeness: 'Completeness',
                no_packs_found: 'No evidence packs found.',
                verify: 'Verify',
                export_pack: 'Export',
                exporting: 'Exporting...',
                pack_assembled: 'Evidence pack assembled',
                pack_exported: 'Evidence pack exported',
                aria_table: 'Audit Evidence Packs',
                aria_export_pack: 'Export pack',
                export_format: 'Export format',
                export_in_progress: 'Export in progress...',
                status_draft: 'Draft',
                status_assembled: 'Assembled',
                status_verified: 'Verified',
                status_submitted: 'Submitted',
                entity_type_labels: {
                    user: 'User',
                    case: 'Case',
                    incident: 'Incident',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    consent: 'Consent',
                    policy: 'Policy',
                    data_map: 'Data Map',
                },
                template_id: 'Template ID',
                csv_headers: {
                    module: 'Module',
                    action: 'Action',
                    actor_id: 'Actor',
                    entity_type: 'Entity Type',
                    entity_id: 'Entity ID',
                    summary: 'Summary',
                    timestamp: 'Timestamp',
                    framework_code: 'Framework',
                },
                export_header_framework: 'Evidence Pack — Framework: {framework}',
                export_header_period: 'Period: {start} to {end}',
                export_header_completeness: 'Completeness: {score}%',
                export_header_status: 'Status: {status}',
            },
            audit_periods: {
                title: 'Audit Periods',
                create_period: 'Create Audit Period',
                framework_code_label: 'Framework Code',
                select_framework: 'Select a framework...',
                period_label: 'Period Label',
                period_start: 'Period Start',
                period_end: 'Period End',
                start: 'Start',
                end: 'End',
                framework: 'Framework',
                retention_days: 'Retention Days',
                no_periods_found: 'No audit periods found.',
                period_created: 'Audit period created',
                aria_table: 'Audit Periods',
                suggested_boundaries: 'Suggested Boundaries',
                suggested_boundaries_info: "Suggested period boundaries are derived from each framework's regulatory reporting cycle. Adjust as needed for your organization.",
                reporting_cycle: 'Reporting Cycle',
                suggested_start: 'Suggested Start',
                suggested_end: 'Suggested End',
                suggested_next: 'Suggested Next Period',
                cycle_monthly: 'Monthly',
                cycle_quarterly: 'Quarterly',
                cycle_semi_annual: 'Semi-Annual',
                cycle_annual: 'Annual',
                cycle_custom: 'Custom',
                period_deleted: 'Audit period deleted',
            },
        },
        create_case: {
            customer_ref: 'Customer Reference',
            country: 'Country / Jurisdiction',
            preferred_channel: 'Preferred Channel',
            channels: {
                email: 'Email',
                postal: 'Postal',
                phone: 'Phone',
                portal: 'Portal',
            },
            source: 'Source',
            sources: {
                admin_manual: 'Admin Manual',
                public_form: 'Public Form',
                cmp_gateway: 'CMP Gateway',
                aesirx_cmp: 'AesirX CMP',
                phone_received: 'Phone Received',
                postal_received: 'Postal Received',
                email_received: 'Email Received',
                in_person: 'In-Person',
                legacy_import: 'Legacy Import',
            },
            required_fields: 'Request type, name, and email are required',
            create_error: 'Failed to create case',
        },
        settings: {
            title: 'Settings',
            request_types: 'Request Types',
            request_types_desc: 'Configure which request types are enabled for the public intake form.',
            notifications: 'Notifications',
            ticket_numbering: 'Ticket Numbering',
            ticket_format: 'Current format',
            ticket_prefix: 'Prefix',
            ticket_prefix_note: 'not configurable in Sprint 1.0.0',
            sla_deadlines: 'SLA Deadlines',
            sla_desc: 'Set the maximum response time (in calendar days) for each request type. Cases approaching the threshold will show warnings.',
            days: 'days',
            warning_threshold: 'Warning threshold',
            escalation_label: 'Enable automatic escalation on SLA breach',
            save_sla: 'Save SLA Configuration',
            saving: 'Saving...',
            saved: 'Saved successfully',
            announce_loaded: 'Settings loaded',
            email_on_creation: 'Email notification on ticket creation',
            send_confirmation: 'Send confirmation email to requester',
            users: 'Users',
            roles: 'Roles',
            api_keys: 'API Keys',
            mfa: 'MFA',
            sessions: 'Sessions',
            webhooks: 'Webhooks',
            modules: 'Modules',
            deadline_labels: {
                access: 'Access (DSAR)',
                deletion: 'Deletion',
                correction: 'Correction',
                portability: 'Portability',
                objection: 'Objection',
                restriction: 'Restriction',
                consent_withdrawal: 'Consent Withdrawal',
            },
        },
        section_assignments: {
            title: 'Section Assignments',
            assign_section: 'Assign Section',
            section_ref: 'Section Ref',
            section_label: 'Section Label',
            department_id: 'Department',
            assignee_id: 'Assignee',
            mandatory: 'Mandatory',
            assign: 'Assign',
            no_assignments: 'No section assignments',
            status: 'Status',
            completion: 'Completion',
            mark_complete: 'Complete',
        },
        consolidation: {
            title: 'Consolidation Progress',
            add_child: 'Add Child Task',
            child_task_id: 'Child Task ID',
            department_id: 'Department',
            link: 'Link',
            overall_completion: 'Overall Completion',
            departments_complete: 'Departments Complete',
            overdue: 'Overdue',
            ready: 'Ready',
            not_ready: 'Not Ready',
            master_status: 'Master Status',
            department_breakdown: 'Department Breakdown',
            department: 'Department',
            status: 'Status',
            completion: 'Completion',
            due_date: 'Due Date',
            overdue_flag: 'Overdue',
            child_tasks: 'Child Tasks',
            task: 'Task',
        },
        conflict_detection: {
            title: 'Conflict Detection',
            unresolved: 'unresolved',
            no_conflicts: 'No conflicts detected',
            section: 'Section',
            field: 'Field',
            dept_a: 'Department A',
            value_a: 'Value A',
            dept_b: 'Department B',
            value_b: 'Value B',
            severity: 'Severity',
            status: 'Status',
            resolved: 'Resolved',
            open: 'Open',
            resolve: 'Resolve',
        },
        org_maturity: {
            title: 'Organization Maturity',
            departments: 'Departments',
            department_hierarchy: 'Department Hierarchy',
            create_department: 'Create Department',
            department_name: 'Department Name',
            department_code: 'Code',
            level: 'Level',
            division: 'Division',
            department: 'Department',
            team: 'Team',
            parent_department: 'Parent Department',
            head_user: 'Department Head',
            no_departments: 'No departments configured.',
            user_mappings: 'User Mappings',
            map_user: 'Map User',
            user_id: 'User ID',
            role: 'Role',
            no_users: 'No users mapped to this department.',
            remove: 'Remove',
            framework_assignments: 'Framework Assignments',
            assign_framework: 'Assign Framework',
            entity_id: 'Entity ID',
            entity_type: 'Entity Type',
            framework_id: 'Framework ID',
            framework_code: 'Framework Code',
            no_assignments: 'No framework assignments.',
            compliance_rollup: 'Compliance Rollup',
            entity_name: 'Entity',
            total_assessments: 'Total Assessments',
            completed: 'Completed',
            completion_pct: 'Completion %',
            open_gaps: 'Open Gaps',
            critical_gaps: 'Critical',
            compliance_score: 'Score',
            no_rollup: 'No compliance rollup data available.',
            entity_summary: 'Entity Compliance Summary',
            frameworks_assigned: 'Frameworks',
            overall_score: 'Overall Score',
            load_departments_error: 'Failed to load departments',
            load_users_error: 'Failed to load users',
            department_created: 'Department created',
            department_created_announce: 'Department created',
            create_department_error: 'Failed to create department',
            user_mapped: 'User mapped',
            user_mapped_announce: 'User mapped to department',
            map_user_error: 'Failed to map user',
            user_removed: 'User removed',
            load_rollup_error: 'Failed to load rollup',
            load_assignments_error: 'Failed to load assignments',
            framework_assigned: 'Framework assigned',
            framework_assigned_announce: 'Framework assigned to entity',
            assign_framework_error: 'Failed to assign framework',
            assignment_removed: 'Assignment removed',
            enter_entity_id_placeholder: 'Enter entity ID to view assignments',
            entity_type_organization: 'Organization',
            entity_type_department: 'Department',
        },
        onboarding_maturity: {
            title: 'Onboarding Maturity',
            quick_starts: 'Quick Starts',
            progress: 'Onboarding Progress',
            personas: 'Persona Paths',
            persona_onboarding: 'Persona Onboarding',
            quick_start_scenarios: 'Quick-Start Scenarios',
            start: 'Start',
            restart: 'Restart',
            estimated_minutes: 'Estimated Minutes',
            steps: 'Steps',
            scenario: 'Scenario',
            no_scenarios: 'No quick-start scenarios available.',
            persona_id: 'Persona ID',
            persona_label: 'Persona',
            role_description: 'Role Description',
            primary_modules: 'Primary Modules',
            recommended_tours: 'Recommended Tours',
            sample_data_modules: 'Sample Data Modules',
            quick_start_scenario: 'Quick-Start Scenario',
            no_personas: 'No persona paths available.',
            framework_setup: 'Regulatory Frameworks',
            framework_code: 'Framework Code',
            framework_name: 'Framework Name',
            related_modules: 'Related Modules',
            persona_relevance: 'Relevant Personas',
            no_frameworks: 'No framework setup options available.',
            tier_config: 'Tier Module Configuration',
            tier: 'Tier',
            available_modules: 'Available Modules',
            module_count: 'Module Count',
            overall_completion: 'Overall Completion',
            wizard_completed: 'Wizard Completed',
            tours_completed: 'Tours Completed',
            quick_starts_completed: 'Quick Starts Completed',
            selected_frameworks: 'Selected Frameworks',
            no_progress: 'No onboarding progress data available.',
            detected_persona: 'Detected Persona',
            current_tier: 'Current Tier',
            completion_pct: 'Completion',
            progress_page: {
                loaded: 'Progress loaded',
                no_progress: 'No onboarding progress data available.',
                overall: 'Overall Progress',
                detected_persona: 'Detected Persona',
                tier: 'Tier',
                modules: 'modules',
                wizard: 'Wizard',
                completed: 'Completed',
                pending: 'Pending',
                tours: 'Tours',
                quick_starts: 'Quick Starts',
                frameworks: 'Selected Frameworks',
                no_frameworks: 'No frameworks selected.',
                go_to_wizard: 'Complete Setup Wizard',
                framework_selection: 'Framework Selection',
                framework_toggle: 'Enable or disable frameworks for your compliance program.',
                framework_updated: 'Framework selection updated.',
                view_tours: 'View Tours',
                view_quick_starts: 'View Quick Starts',
            },
            persona_page: {
                description: 'Select a persona to see recommended modules, tours, and quick-start scenarios.',
                no_personas: 'No persona paths available.',
                modules: 'Modules',
                tours: 'Tours',
                quick_start: 'Quick Start',
                details: 'Details',
                primary_modules: 'Primary Modules',
                recommended_tours: 'Recommended Tours',
                framework_options: 'Regulatory Frameworks',
                no_frameworks: 'No framework setup options available.',
                framework_code: 'Framework Code',
                framework_label: 'Label',
                region: 'Region',
                category: 'Category',
                enabled: 'Enabled',
                disabled: 'Disabled',
                tier_modules: 'Tier Module Configuration',
                tiers_loaded: 'Tier configurations loaded',
                no_tier_config: 'No tier configuration available.',
                view_tours: 'View Module Tours',
                view_quick_start: 'Start Quick Start',
                toggle_framework: 'Toggle framework',
                framework_updated: 'Framework selection updated.',
                tier_desc_light: 'Core privacy essentials — PDPL compliance basics',
                tier_desc_standard: 'Full compliance suite — all modules with workflows',
                tier_desc_enterprise: 'Advanced governance & automation — multi-entity, group reporting',
                persona_highlight: 'Your modules',
                // fix-sprint-20260411-0417 Findings 4 + 5 — replace hardcoded
                // English aria-label literals across PersonaOnboardingPage and
                // OnboardingProgressPage so screen-reader users in non-EN
                // locales hear the right text. All values use the {tpl}{vars}
                // pattern resolved by the page's `fmt` helper.
                aria_go_to_module: '{name} — go to module',
                aria_start_tour: 'Start {title}',
                aria_quick_start: 'Quick start: {title}',
                aria_help_article: 'Help article: {title}',
                aria_view_framework: '{label}{primary} — view framework details',
                aria_primary_for_persona: '{name} (primary for this persona)',
                primary_for_persona_suffix: ' (primary for this persona)',
                aria_view_in_persona_paths: '{name} — view in Persona Paths',
                progress_of_total: '{completed} of {total} completed',
            },
            quick_starts_page: {
                description: 'Practice common workflows with guided quick-start scenarios.',
                no_scenarios: 'No quick-start scenarios available.',
                started: 'Quick start started',
                status_completed: 'Completed',
                status_in_progress: 'In Progress',
                status_not_started: 'Not Started',
                minutes: 'min',
                steps: 'steps',
                step: 'Step',
                restart: 'Restart',
                start: 'Start',
                go_to_module: 'Go to Module',
                next_step: 'Next Step',
                complete: 'Complete',
                reset: 'Reset',
                step_of: 'of',
                action_navigate: 'Navigate',
                action_form_fill: 'Form Fill',
                action_review: 'Review',
                action_confirm: 'Confirm',
                step_changed: 'Step {current} of {total}: {title}',
                completed_message: 'Quick start completed successfully!',
                reset_message: 'Quick start has been reset.',
            },
            // Persona Path i18n — labels, descriptions, and quick start titles
            persona_p_vn_01_label: 'Enterprise DPO',
            persona_p_vn_01_description: 'Data Protection Officer managing enterprise-wide compliance programs',
            persona_p_vn_02_label: 'Department Owner',
            persona_p_vn_02_description: 'Department lead responsible for sectional compliance reporting',
            persona_p_vn_03_label: 'E-Commerce Compliance',
            persona_p_vn_03_description: 'Compliance officer focused on e-commerce data protection and consent',
            persona_p_vn_04_label: 'Telecom DPO',
            persona_p_vn_04_description: 'Telecom-sector DPO managing subscriber data and incident reporting',
            persona_p_vn_05_label: 'Data Governance Lead',
            persona_p_vn_05_description: 'Lead responsible for data mapping, classification, and governance',
            persona_p_vn_06_label: 'Cybersecurity & Legal Ops',
            persona_p_vn_06_description: 'Cybersecurity officer managing incident response and vendor risk',
            persona_p_vn_07_label: 'AI Governance Lead',
            persona_p_vn_07_description: 'Lead overseeing AI system governance, risk assessments, and classification',
            persona_p_vn_08_label: 'Cross-Border & Vendor Gov',
            persona_p_vn_08_description: 'Officer managing cross-border transfers and vendor governance',
            persona_p_vn_09_label: 'Breach Response Lead',
            persona_p_vn_09_description: 'Lead coordinating breach notification and incident response',
            persona_p_vn_10_label: 'Internal Audit Lead',
            persona_p_vn_10_description: 'Lead responsible for compliance auditing and program governance review',
            persona_p_vn_11_label: 'HR & Employee Data Steward',
            persona_p_vn_11_description: 'HR manager handling employee consent records and internal DSARs',
            persona_p_vn_12_label: 'Multinational Subsidiary Compliance Lead',
            persona_p_vn_12_description: 'DPO at foreign company Vietnam subsidiary needing local compliance layer',
            persona_p_vn_13_label: 'Education Compliance Lead',
            persona_p_vn_13_description: 'Compliance lead at foreign school managing student and parent data protection',
            qs_hr_consent_title: 'Register employee consent records',
            qs_hr_consent_description: 'Walk through registering employee consent and generating a coverage report.',
            qs_subsidiary_dpia_title: 'Create a subsidiary DPIA assessment',
            qs_subsidiary_dpia_description: 'Walk through creating a DPIA for a subsidiary processing activity.',
            qs_student_consent_title: 'Set up student consent collection',
            qs_student_consent_description: 'Walk through creating parent/guardian consent policies for student data.',
            persona_p_vn_14_label: 'External Legal Advisor',
            persona_p_vn_14_description: 'Senior law firm partner advising multiple client organizations on PDPL compliance',
            persona_p_vn_15_label: 'Law Firm Operations Manager',
            persona_p_vn_15_description: 'Client engagement coordinator managing deliverable tracking and deadline coordination',
            persona_p_vn_16_label: 'Business Owner / CEO',
            persona_p_vn_16_description: 'CEO or GM who is legally the data controller under PDPL, accountable for compliance investment',
            // Quick Start scenario titles (keyed by scenario_id with - replaced by _)
            qs_dpia_filing_title: 'Complete your first DPIA filing',
            qs_dept_compliance_title: 'Submit your first department compliance section',
            qs_ecommerce_consent_title: 'Configure e-commerce consent collection',
            qs_telecom_data_title: 'Set up telecom subscriber data handling',
            qs_data_flow_title: 'Map your first data flow',
            qs_incident_response_title: 'Configure incident response workflow',
            qs_ai_risk_title: 'Run an AI system risk assessment',
            qs_cross_border_title: 'Assess a cross-border data transfer',
            qs_breach_notification_title: 'Simulate a breach notification workflow',
            qs_audit_trail_title: 'Review a compliance audit trail',
            qs_legal_advisor_portfolio_title: 'Review multi-client compliance portfolio',
            qs_legal_advisor_portfolio_description: 'Review compliance status across multiple client organizations.',
            qs_law_firm_ops_title: 'Track client deliverable status',
            qs_law_firm_ops_description: 'Monitor advisory deliverable progress and client engagement.',
            qs_ceo_dashboard_title: 'View executive compliance dashboard',
            qs_ceo_dashboard_description: 'Review high-level compliance status and investment metrics.',
        },
        // Sprint 43.2.0 batch 3 (E1) — Response Template admin page
        rights_requests: {
            response_templates: {
                title: 'Response Templates',
                subtitle: 'Manage the DSR response letter templates that ResponseGenerator uses to draft replies to data subjects.',
                create_btn: 'New Template',
                edit_btn: 'Edit Template',
                filter_right_type: 'Filter by Right Type',
                filter_locale: 'Filter by Locale',
                filter_status: 'Filter by Status',
                all_right_types: 'All Right Types',
                any_right_type: 'Any (applies to all types)',
                all_locales: 'All Locales',
                all_statuses: 'All',
                status_active: 'Active',
                status_inactive: 'Inactive',
                empty_state: 'No templates match the current filters.',
                columns: {
                    template_id: 'Template ID',
                    outcome: 'Outcome',
                    locale: 'Locale',
                    right_type: 'Right Type',
                    is_active: 'Status',
                    last_updated: 'Last Updated',
                    actions: 'Actions',
                },
                actions: {
                    edit: 'Edit',
                    clone: 'Clone',
                    deactivate: 'Deactivate',
                    reactivate: 'Reactivate',
                    preview: 'Preview',
                },
                form: {
                    template_id: 'Template ID',
                    template_id_help: 'Lowercase identifier, e.g. dsr_access_fulfilled_vi',
                    outcome: 'Outcome',
                    locale: 'Locale',
                    right_type: 'Right Type',
                    subject: 'Subject',
                    body: 'Body Template',
                    body_help: 'Use placeholders like {case_reference}, {data_subject_name}, {fulfilled_at}. Minimum 15 lines for multi-section letters.',
                    is_active: 'Active',
                    official_form_id: 'Official Form ID (optional)',
                },
                outcomes: {
                    fulfilled: 'Fulfilled',
                    partially_fulfilled: 'Partially Fulfilled',
                    denied: 'Denied',
                },
                clone_modal_title: 'Clone Template',
                clone_target_locale: 'Target Locale',
                clone_help: 'A new template will be created in the target locale using this template as a starting point.',
                preview_modal_title: 'Template Preview',
                preview_note: 'Preview uses demo case DSR-DEMO-0020 placeholders. Real delivery substitutes live case data.',
                preview_unavailable: 'Preview requires an existing case. Attach this template to a case and use the Draft Response action on the case detail page.',
                deactivate_confirm: 'Deactivate this template? It will continue to apply to existing cases that reference it.',
                toasts: {
                    created: 'Template created',
                    updated: 'Template updated',
                    deactivated: 'Template deactivated',
                    reactivated: 'Template reactivated',
                    cloned: 'Template cloned',
                    load_error: 'Failed to load templates',
                    save_error: 'Failed to save template',
                    clone_error: 'Failed to clone template',
                },
            },
        },
        // Sprint 43.2.0 batch 3 (E2 + E4) — Org admin pages
        organizations: {
            departments: {
                title: 'Departments',
                subtitle: 'Manage the departments that route DSR cases, DPIA tasks, and compliance workflows.',
                create_btn: 'New Department',
                edit_btn: 'Edit Department',
                columns: {
                    name: 'Name',
                    description: 'Description',
                    active_cases: 'Active Cases',
                    members: 'Members',
                    status: 'Status',
                    actions: 'Actions',
                },
                status_active: 'Active',
                status_inactive: 'Inactive',
                form: {
                    name: 'Name',
                    description: 'Description',
                },
                actions: {
                    edit: 'Edit',
                    deactivate: 'Deactivate',
                    reactivate: 'Reactivate',
                    delete: 'Delete',
                    manage_members: 'Manage members',
                },
                delete_modal_title: 'Delete Department',
                delete_warning: 'This department still has active cases. Choose a department to reassign them to before deletion.',
                delete_no_cases: 'This department has no active cases and can be deleted safely.',
                reassign_prompt: 'Reassign active cases to:',
                reassign_none_available: 'No other active departments available. Create another department first.',
                member_section_title: 'Members',
                add_member: 'Add Member',
                remove_member: 'Remove',
                member_role: 'Role',
                no_members: 'No members mapped to this department.',
                toasts: {
                    created: 'Department created',
                    updated: 'Department updated',
                    deactivated: 'Department deactivated',
                    reactivated: 'Department reactivated',
                    deleted: 'Department deleted',
                    reassigned_count: '{count} case(s) reassigned',
                    member_added: 'Member added',
                    member_removed: 'Member removed',
                    load_error: 'Failed to load departments',
                    save_error: 'Failed to save department',
                    delete_error: 'Failed to delete department',
                },
            },
            retention_policies: {
                title: 'Retention Policies',
                subtitle: 'Configure per-target-type retention windows. Identity-verification documents use this to stamp retention_expires_at on future uploads.',
                create_btn: 'New Policy',
                edit_btn: 'Edit Policy',
                columns: {
                    target_type: 'Target Type',
                    retention_days: 'Retention (days)',
                    owner: 'Policy Owner',
                    effective_from: 'Effective From',
                    last_reviewed: 'Last Reviewed',
                    status: 'Status',
                    actions: 'Actions',
                },
                status_active: 'Active',
                status_inactive: 'Inactive',
                form: {
                    target_type: 'Target Type',
                    target_type_help: 'What data category the retention window applies to. Currently only DSR identity-verification documents are supported.',
                    retention_days: 'Retention window (days)',
                    retention_days_help: 'Between 1 and 3650 days (10 years max).',
                    owner: 'Policy Owner',
                    owner_help: 'Usually the DPO. Accountable for the retention decision under DG-2.',
                    effective_from: 'Effective From',
                },
                target_types: {
                    dsr_identity_verification_document: 'DSR identity-verification documents',
                },
                deactivate_notice: 'Deactivating does not affect already-stamped attachments — only future uploads will no longer receive a retention window.',
                deactivate_confirm: 'Deactivate this retention policy?',
                toasts: {
                    created: 'Retention policy created',
                    updated: 'Retention policy updated',
                    deactivated: 'Retention policy deactivated',
                    load_error: 'Failed to load retention policies',
                    save_error: 'Failed to save retention policy',
                },
            },
        },
    },
    // ---------- Data Mapping module (Sprint 1.2.0) ----------
    data_mapping: {
        nav: {
            systems: 'Systems',
            categories: 'Data Categories',
            // Sprint 45.4.0 — Data Registry nav entry.
            registry: 'Data Registry',
            purposes: 'Processing Purposes',
            retention: 'Retention',
            flows: 'Data Flows',
            activities: 'Processing Activities',
            evidence: 'Evidence',
            exports: 'Exports',
            attestation: 'Attestation Cycles',
            governance: 'Governance',
            review: 'Review Tasks',
            inventory: 'Inventory',
            exceptions: 'Exceptions',
            history: 'History',
            ropa_completeness: 'ROPA Completeness',
        },
        // Sprint 48.1.2 (finish) — Forseti autofix inline action on the
        // RoPA completeness page.
        ropa_completeness_autofix: {
            ask_button: 'Ask Forseti',
            drafting: 'Drafting…',
            accept: 'Accept',
            reject: 'Reject',
            accepted: 'Accepted — apply via the activity editor.',
            rejected: 'Rejected.',
            needs_human_input: 'Forseti needs more activity context (e.g. a documented purpose) before drafting this field.',
        },
        // Sprint 46.0.0 — ROPA Completeness Report (per-field hygiene)
        ropa_completeness: {
            title: 'ROPA Completeness Report',
            subtitle: 'Per-field hygiene audit of your Records of Processing Activities, scoped by regulatory framework.',
            framework_label: 'Framework',
            kpi_total: 'Activities scored',
            kpi_critical: 'Critical',
            kpi_high: 'High',
            kpi_conditional: 'Conditional',
            field_chart_heading: 'Missing fields by frequency',
            drilldown_heading: 'Activities with gaps',
            col_activity: 'Activity',
            col_field: 'Field',
            col_severity: 'Severity',
            col_condition: 'Condition',
            col_action: 'Fix',
            action_fix: 'Fix',
            severity_critical: 'Critical',
            severity_high: 'High',
            severity_conditional: 'Conditional',
            severity_informational: 'Informational',
            include_informational: 'Include informational (ISO 27701)',
            export_csv: 'Download CSV',
            exporting: 'Exporting...',
            loading: 'Loading…',
            empty_state: 'All active activities have complete ROPA records for this framework.',
            error_fetch: 'Failed to load the completeness report.',
            error_unsupported_framework: 'This framework is not supported.',
            error_framework_not_installed: 'This framework is not installed for your organization.',
            no_misses_for_field: 'No activities missing this field.',
            framework_PDPL: 'PDPL (Law 91/2025 + Decree 356)',
            framework_GDPR: 'GDPR Article 30',
            framework_DECREE_165: 'Decree 165 (Vietnam Data Law)',
            framework_ISO_27701: 'ISO 27701 (informational)',
        },
        // Sprint 45.4.0 — Data Registry (Light-tier sub-module)
        registry: {
            page: {
                title: 'Data Registry',
                subtitle: 'Personal data elements your organization holds, organized under Data Categories.',
                empty_state: 'No registry items yet. Add an item manually or import from a regulatory register.',
                empty_filter_state: 'No items match your filters.',
            },
            filter: {
                category: 'Category',
                sensitivity: 'Sensitivity',
                data_subject: 'Data subject',
                status: 'Status',
                data_tier: 'Data tier',
                cross_border: 'Cross-border',
                has_owner: 'Has owner',
                has_form_source: 'Imported from form',
                search_placeholder: 'Search name, description, or field identifier…',
                clear: 'Clear filters',
                option_all: 'All',
            },
            col: {
                name: 'Name',
                category: 'Category',
                sensitivity: 'Sensitivity',
                data_subjects: 'Data subjects',
                systems: 'Systems',
                activities: 'Activities',
                owner: 'Owner',
                retention: 'Retention',
                data_tier: 'Data tier',
                status: 'Status',
                last_reviewed: 'Last reviewed',
                actions: 'Actions',
            },
            action: {
                add_manual: 'Add item',
                add_from_form: 'Add from regulatory register',
                import_history: 'Import past submissions',
                export: 'Export',
                archive: 'Archive',
                restore: 'Restore',
                edit: 'Edit',
                assign_owner: 'Assign owner',
                attach_evidence: 'Attach evidence',
                open_lineage: 'Open lineage',
            },
            modal_editor: {
                section_identity: 'Identity',
                section_classification: 'Classification',
                section_subjects: 'Subjects & Volume',
                section_location: 'Location & Transfer',
                section_systems: 'Systems & Activities',
                section_retention: 'Retention & Legal',
                section_governance: 'Governance',
                section_notes: 'Notes',
                hint_inherited: '(inherited from category)',
                hint_overridden: '(overridden)',
                placeholder_field_identifiers: 'e.g., customers.primary_email, hris.employee_id',
                validation_name_required: 'Name is required',
                validation_category_required: 'Category is required',
                validation_data_subjects_required: 'At least one data subject type is required',
                label_data_type: 'Data type',
                label_field_identifiers: 'Field identifiers (comma-separated)',
                label_data_subject_types: 'Data subject types',
                label_volume_band: 'Volume band',
                label_storage_locations: 'Storage locations (comma-separated)',
                placeholder_storage_locations: 'ISO-3166 codes or free text (e.g., VN, Hetzner EU)',
                label_cross_border: 'Cross-border transfer',
                label_source_systems: 'Source systems',
                label_processing_activities: 'Processing activities',
                label_collection_method: 'Collection method',
                label_legal_basis: 'Legal basis',
                label_purposes: 'Purposes',
                hint_multi_select: 'Hold Cmd/Ctrl to select multiple.',
                option_inherit_category: '— (inherit from category)',
                option_dash: '—',
            },
            modal_import: {
                dry_run_heading: 'Import preview',
                label_to_create: 'To create',
                label_to_update: 'To update',
                label_skipped_archived: 'Skipped (archived)',
                label_conflicts: 'Conflicts',
                commit: 'Import',
                cancel: 'Cancel',
                conflict_row: '{field} — keeping your edit ({current}); mapper wanted {mapper}.',
            },
            drawer: {
                section_category: 'Category',
                section_systems: 'Source systems',
                section_activities: 'Processing activities',
                section_flows: 'Data flows',
                section_retention: 'Effective retention',
                section_governance: 'Governance',
                section_evidence: 'Evidence',
                section_audit: 'Recent audit trail',
                section_source_form: 'Source form submission',
                badge_inherited: 'Inherited',
                badge_overridden: 'Overridden',
            },
            toast: {
                created: 'Registry item created.',
                updated: 'Registry item updated.',
                archived: 'Registry item archived.',
                imported: '{count} items imported.',
                conflict_warning: '{count} field(s) kept your edits — see conflict list.',
            },
        },
        systems: {
            title: 'Systems Inventory',
            new: 'New System',
            edit: 'Edit System',
            name: 'Name',
            owner: 'Owner',
            criticality: 'Criticality',
            dpa_status: 'DPA Status',
            location: 'Location',
            system_type: 'System Type',
            metadata: 'Metadata (JSON)',
            department: 'Department',
            hosting_location: 'Hosting Location',
            hosting_provider: 'Hosting Provider',
            data_residency_country: 'Data Residency Country',
            hosting: 'Hosting',
            no_systems: 'No systems found',
            confirm_archive: 'Archive system "{name}"?',
            count: 'systems',
            count_one: 'system',
            type_internal: 'Internal',
            type_external: 'External',
            type_cloud_service: 'Cloud Service',
            type_on_premise: 'On Premise',
            type_hybrid: 'Hybrid',
            status_active: 'Active',
            status_decommissioning: 'Decommissioning',
            status_decommissioned: 'Decommissioned',
            status_planned: 'Planned',
            dpa_not_required: 'Not Required',
            dpa_required_missing: 'Required Missing',
            dpa_in_progress: 'In Progress',
            dpa_signed: 'Signed',
            all_criticalities: 'All Criticalities',
        },
        categories: {
            title: 'Data Categories',
            new: 'New Category',
            edit: 'Edit Category',
            name: 'Name',
            sensitivity: 'Sensitivity',
            tags: 'Tags',
            used_in: 'Used In',
            examples: 'Examples',
            examples_hint: 'Examples (comma-separated)',
            default: 'Default',
            all_sensitivities: 'All Sensitivities',
            all_statuses: 'All Statuses',
            no_categories: 'No categories found',
            confirm_delete: 'Delete category "{name}"?',
            count: 'categories',
            count_one: 'category',
            status_draft: 'Draft',
            status_proposed: 'Proposed',
            status_under_review: 'Under Review',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            status_updated: 'Category status updated',
            propose: 'Propose',
            approve: 'Approve',
            deprecate: 'Deprecate',
        },
        purposes: {
            title: 'Processing Purposes',
            new: 'New Purpose',
            edit: 'Edit Purpose',
            name: 'Name',
            legal_basis: 'Legal Basis',
            retention_period: 'Retention Period (days)',
            justification: 'Legitimate Interest Justification',
            legal_basis_detail: 'Legal Basis Detail',
            all_legal_bases: 'All Legal Bases',
            no_purposes: 'No purposes found',
            confirm_delete: 'Delete "{name}"?',
            default: 'Default',
            count: '{count} purposes',
            count_one: '{count} purpose',
            basis_consent: 'Consent',
            basis_contract: 'Contract',
            basis_legal_obligation: 'Legal Obligation',
            basis_vital_interest: 'Vital Interest',
            basis_public_interest: 'Public Interest',
            basis_legitimate_interest: 'Legitimate Interest',
        },
        retention: {
            title: 'Retention Policies',
            new: 'New Policy',
            edit: 'Edit Policy',
            name: 'Name',
            retention_days: 'Retention Days',
            disposal_method: 'Disposal Method',
            data_categories: 'Data Categories',
            activities: 'Activities',
            review_frequency: 'Review Frequency',
            review_frequency_days: 'Review Frequency (days)',
            no_policies: 'No retention policies found',
            confirm_delete: 'Delete "{name}"?',
            count: '{count} policies',
            count_one: '{count} policy',
            days: 'days',
            disposal_deletion: 'Deletion',
            disposal_anonymization: 'Anonymization',
            disposal_archival: 'Archival',
            review_overdue: 'Review Overdue',
            review_status: 'Review Status',
            review_ok: 'On Track',
            department: 'Department',
            all_departments: 'All Departments',
        },
        flows: {
            // fix-sprint-20260415-0434 F14 — List/Map view tablist keys.
            view_tabs_aria: 'Flows view',
            view_list: 'List',
            view_map: 'Map',
            title: 'Data Flows',
            new: 'New Flow',
            edit: 'Edit Flow',
            name: 'Name',
            from_system: 'From System',
            to_system: 'To System',
            flow_type: 'Flow Type',
            cross_border: 'Cross-Border',
            risk_level: 'Risk Level',
            mechanism: 'Mechanism',
            transfer_mechanism: 'Transfer Mechanism',
            frequency: 'Frequency',
            source_country: 'Source Country',
            destination_country: 'Destination Country',
            transfer_safeguard: 'Transfer Safeguard',
            all_risk_levels: 'All Risk Levels',
            no_flows: 'No data flows found',
            confirm_archive: 'Archive "{name}"?',
            count: '{count} flows',
            count_one: '{count} flow',
            status_active: 'Active',
            status_inactive: 'Inactive',
            status_under_review: 'Under Review',
            mechanism_api: 'API',
            mechanism_file_transfer: 'File Transfer',
            mechanism_database_replication: 'Database Replication',
            mechanism_manual: 'Manual',
            mechanism_email: 'Email',
            mechanism_other: 'Other',
            frequency_real_time: 'Real Time',
            frequency_hourly: 'Hourly',
            frequency_daily: 'Daily',
            frequency_weekly: 'Weekly',
            frequency_monthly: 'Monthly',
            frequency_on_demand: 'On Demand',
            frequency_one_time: 'One Time',
            safeguard_adequacy_decision: 'Adequacy Decision',
            safeguard_standard_contractual_clauses: 'Standard Contractual Clauses',
            safeguard_binding_corporate_rules: 'Binding Corporate Rules',
            safeguard_explicit_consent: 'Explicit Consent',
            safeguard_legal_obligation: 'Legal Obligation',
            safeguard_none: 'None',
            safeguard_other: 'Other',
        },
        activities: {
            title: 'Processing Activities',
            new: 'New Activity',
            edit: 'Edit Activity',
            activity_title: 'Title',
            category: 'Category',
            legal_basis: 'Legal Basis',
            dpa_required: 'DPA Required',
            ropa_generated: 'RoPA Generated',
            version: 'Version',
            generate_ropa: 'Generate RoPA',
            controller: 'Controller',
            controller_name: 'Controller Name',
            controller_contact: 'Controller Contact',
            legal_basis_detail: 'Legal Basis Detail',
            organization_info: 'Organization Information (Mẫu số 02a)',
            org_address: 'Organization Address',
            tax_code: 'Tax Code',
            contact_person: 'Contact Person',
            contact_email: 'Contact Email',
            contact_phone: 'Contact Phone',
            select_entity: 'Select…',
            no_entities: 'No items found',
            dpo_name: 'DPO Name',
            dpo_contact: 'DPO Contact',
            processor_name: 'Processor Name',
            processor_contact: 'Processor Contact',
            data_subjects: 'Data Subjects',
            data_subjects_hint: 'Data Subjects (comma-separated)',
            recipients: 'Recipients',
            recipients_hint: 'Recipients (comma-separated)',
            security_measures: 'Security Measures',
            dpia_reference: 'DPIA Reference',
            no_activities: 'No processing activities found',
            confirm_archive: 'Archive "{name}"?',
            count: '{count} activities',
            count_one: '{count} activity',
            ropa_success: 'RoPA record generated. Go to Exports to download it.',
            ropa_error: 'Failed to generate RoPA',
            required_label: 'Required',
            status_draft: 'Draft',
            status_active: 'Active',
            status_under_review: 'Under Review',
            status_archived: 'Archived',
            // Sprint 46.1.0 — Decree 165 data-tier fields.
            decree_165_heading: 'Decree 165 data tier (Vietnam Data Law)',
            decree_165_helper: 'Classify the data this activity processes under Vietnam Decree 165. Regular data gets the baseline cross-border regime; important and core data trigger additional obligations.',
            data_tier_label: 'Data tier',
            data_tier_help: 'Decree 165 classifies data as regular, important, or core — see the help article for guidance.',
            data_tier_column_header: 'Data tier',
            data_tier_regular: 'Regular',
            data_tier_important: 'Important',
            data_tier_core: 'Core',
            transfer_mechanism_label: 'Cross-border transfer mechanism',
            transfer_mechanism_placeholder: 'e.g. Standard Contractual Clauses, adequacy decision',
            security_assessment_reference_label: 'Security assessment reference',
            security_assessment_reference_placeholder: 'e.g. MPS-SA-2026-001',
        },
        evidence: {
            title: 'Evidence Vault',
            new: 'New Evidence',
            edit: 'Edit Evidence',
            name: 'Name',
            evidence_type: 'Evidence Type',
            linked_entities: 'Linked Entities',
            uploaded: 'Uploaded',
            expiry: 'Expiry',
            file_url: 'File URL',
            // Sprint 41.3.0 F1 — source-mode toggle (upload file vs link URL)
            source_mode_label: 'Evidence source',
            source_mode_file: 'Upload file',
            source_mode_url: 'Link external URL',
            attachment_label: 'Attachment',
            link_to: 'Link Evidence To',
            entity_type: 'Entity Type',
            entity_id: 'Entity ID',
            no_evidence: 'No evidence items found',
            manage_links: 'Manage links',
            unlink: 'Unlink',
            confirm_delete: 'Delete evidence "{name}"?',
            count: '{count} items',
            count_one: '{count} item',
            type_document: 'Document',
            type_screenshot: 'Screenshot',
            type_policy: 'Policy',
            type_contract: 'Contract',
            type_assessment: 'Assessment',
            type_correspondence: 'Correspondence',
            type_other: 'Other',
            linked_type_system: 'System',
            linked_type_data_flow: 'Data Flow',
            linked_type_processing_activity: 'Processing Activity',
            linked_type_data_category: 'Data Category',
            linked_type_purpose: 'Purpose',
            linked_type_retention_policy: 'Retention Policy',
            link_entity: 'Link to Entity',
            link_success: 'Entity linked successfully',
            confirm_unlink: 'Are you sure you want to unlink this entity?',
            unlink_success: 'Entity unlinked successfully',
            no_linked_entities: 'No linked entities',
            select_entity: 'Select…',
            no_entities: 'No items',
        },
        exports: {
            title: 'Data Exports',
            recent: 'Recent Exports',
            export_type: 'Export Type',
            format: 'Format',
            requested_by: 'Requested By',
            requested_at: 'Requested At',
            download: 'Download',
            csv_export: 'CSV Export',
            csv_description: 'Download data in CSV format for spreadsheet analysis.',
            pdf_reports: 'PDF Reports',
            pdf_description: 'Generate formatted reports for compliance documentation.',
            csv_systems: 'Systems Inventory',
            csv_categories: 'Data Categories',
            csv_flows: 'Data Flows',
            csv_activities: 'Processing Activities',
            csv_cross_border: 'Cross-Border Flows',
            csv_retention_policies: 'Retention Policies',
            csv_evidence: 'Evidence Items',
            csv_governance_assignments: 'Governance Assignments',
            pdf_ropa: 'RoPA Register',
            pdf_systems: 'Systems Report',
            pdf_cross_border: 'Cross-Border Report',
            pdf_flow_map: 'Flow Map Data',
            export_failed: 'Export failed',
            pdf_generated: 'PDF generated successfully.',
            ropa: 'RoPA Register',
            retention_policies: 'Retention Policies',
            governance_assignments: 'Governance Assignments',
            col_entity: 'Export Entity',
            col_description: 'Description',
            desc_ropa: 'Full ROPA register of all processing activities with legal basis, DPIA status, and controls.',
            desc_systems: 'Systems inventory with owner, data category tags, and compliance status.',
            desc_activities: 'Individual processing activities with controller, legal basis, and DPIA classification.',
            desc_categories: 'Data categories and sensitivity tiers used across classification and audits.',
            desc_purposes: 'Processing purposes with legal basis citations per Decree 13/2023 and Telecommunications Law 2023.',
            desc_flows: 'Data flows between systems, including internal and cross-border routes.',
            desc_cross_border: 'Cross-border flows only, with transfer mechanism and risk assessment.',
            desc_retention_policies: 'Retention policies, disposal methods, and review cadence.',
            desc_evidence: 'Evidence attached to processing activities: certificates, DPA contracts, DPIA reports.',
            desc_governance_assignments: 'Governance assignments: owners, approvers, and ISO 27001 references.',
            unified_heading: 'Export Entities',
            unified_description: 'Export any data-mapping entity in CSV, XLSX, PDF, or DOCX format. All exports are saved to the File Browser.',
            export_button_label: 'Export',
            csv_download_success: 'CSV downloaded successfully.',
        },
        attestation: {
            title: 'Attestation Cycles',
            create: 'New Cycle',
            name: 'Cycle Name',
            year: 'Year',
            deadline: 'Deadline',
            scope: 'Scope',
            status: 'Status',
            tasks: 'Department Tasks',
            assign_tasks: 'Assign Tasks',
            department: 'Department',
            assignee: 'Assignee',
            progress: 'Progress',
            stale_entries: 'Stale Entries',
            detect_stale: 'Detect Stale',
            export_bundle: 'Export Bundle',
            no_cycles: 'No attestation cycles yet.',
            create_success: 'Attestation cycle created.',
            assign_success: 'Tasks assigned successfully.',
            complete_success: 'Task completed.',
            stale_detected: 'Stale entries detected.',
            stale_systems: 'Stale Systems',
            stale_flows: 'Stale Flows',
            no_stale: 'No stale entries detected.',
            stale_name: 'Name',
            stale_announcement: 'Found {0} stale systems and {1} stale flows',
            last_updated: 'Last Updated',
            status_draft: 'Draft',
            status_active: 'Active',
            status_completed: 'Completed',
            task_status_pending: 'Pending',
            task_status_in_progress: 'In Progress',
            task_status_completed: 'Completed',
            scope_systems: 'Systems',
            scope_flows: 'Flows',
            scope_activities: 'Activities',
            scope_categories: 'Categories',
            scope_purposes: 'Purposes',
            scope_retention: 'Retention',
        },
        governance: {
            title: 'Governance Assignments',
            assign: 'Assign Ownership',
            entity_type: 'Entity Type',
            entity: 'Entity',
            role: 'Role',
            assigned_by: 'Assigned By',
            assigned_at: 'Assigned At',
            remove: 'Remove',
            no_assignments: 'No governance assignments.',
            assign_success: 'Ownership assigned.',
            remove_success: 'Assignment removed.',
            assignee: 'Assignee',
            department: 'Department',
            all_departments: 'All Departments',
            roles: {
                data_owner: 'Data Owner',
                data_steward: 'Data Steward',
                data_custodian: 'Data Custodian',
                informed: 'Informed',
            },
            entity_types: {
                system: 'System',
                flow: 'Flow',
                activity: 'Activity',
                category: 'Category',
                purpose: 'Purpose',
                retention_policy: 'Retention Policy',
            },
        },
        risk_assessment: {
            title: 'Risk Assessment',
            create: 'New Assessment',
            safeguard_type: 'Safeguard Type',
            adequacy_status: 'Adequacy Status',
            risk_factors: 'Risk Factors',
            mitigations: 'Mitigation Measures',
            reviewer: 'Reviewer',
            decision: 'Decision',
            approve: 'Approve',
            reject: 'Reject',
            no_assessments: 'No risk assessments.',
            create_success: 'Risk assessment created.',
            approve_success: 'Assessment approved.',
            reject_success: 'Assessment rejected.',
            one_per_line: 'One per line',
            safeguard_adequacy_decision: 'Adequacy Decision',
            safeguard_standard_contractual_clauses: 'Standard Contractual Clauses',
            safeguard_binding_corporate_rules: 'Binding Corporate Rules',
            safeguard_explicit_consent: 'Explicit Consent',
            safeguard_legal_obligation: 'Legal Obligation',
            safeguard_none: 'None',
            safeguard_other: 'Other',
            adequacy_adequate: 'Adequate',
            adequacy_inadequate: 'Inadequate',
            adequacy_under_review: 'Under Review',
            adequacy_not_assessed: 'Not Assessed',
            decision_approved: 'Approved',
            decision_rejected: 'Rejected',
        },
        // Sprint 27.8.1 — Data Discovery Workflow (UC-VN-10)
        review: {
            title: 'Review Tasks',
            description: 'Review tasks are created automatically when a data category is proposed. To create a new task, go to Data Categories and click Propose on any draft category.',
            count: 'review tasks',
            summary: 'Review Summary',
            pending: 'Pending',
            resolved_today: 'Resolved Today',
            escalated: 'Escalated',
            overdue: 'Overdue (>5 days)',
            all_statuses: 'All Statuses',
            status_pending: 'Pending',
            status_resolved: 'Resolved',
            status_escalated: 'Escalated',
            category_name: 'Category',
            system_name: 'System',
            owner: 'Owner',
            resolve: 'Resolve',
            resolve_task: 'Resolve Review Task',
            resolution: 'Resolution',
            resolution_approve: 'Approve',
            resolution_modify: 'Modify & Approve',
            resolution_reject: 'Reject',
            resolution_escalate: 'Escalate',
            notes: 'Resolution Notes',
            notes_required: 'Notes required (minimum 10 characters) for Modify, Reject, or Escalate',
            resolved_success: 'Review task resolved',
            bulk_approve: 'Bulk Approve',
            bulk_reject: 'Bulk Reject',
            bulk_success: '{count} tasks processed',
            no_tasks: 'No review tasks found',
        },
        inventory: {
            title: 'Classification Inventory',
            count: 'systems',
            summary: 'Inventory Summary',
            total_systems: 'Total Systems',
            total_labels: 'Total Labels',
            system_name: 'System',
            label_count: 'Labels',
            approved_count: 'Approved',
            pending_count: 'Pending',
            no_items: 'No inventory items found',
        },
        exceptions: {
            title: 'Classification Exceptions',
            count: 'exceptions',
            all_severities: 'All Severities',
            all_statuses: 'All Statuses',
            system_name: 'System',
            severity_label: 'Severity',
            status_open: 'Open',
            status_resolved: 'Resolved',
            status_accepted_risk: 'Accepted Risk',
            resolve: 'Resolve',
            resolve_exception: 'Resolve Exception',
            resolution_type: 'Resolution Type',
            notes: 'Notes',
            justification_required: 'Justification required (minimum 10 characters) for accepted risk',
            resolved_success: 'Exception resolved',
            no_exceptions: 'No exceptions found',
        },
        history: {
            title: 'Classification History',
            count: 'events',
            actor: 'Actor',
            action: 'Action',
            entity_type: 'Entity Type',
            changes: 'Changes',
            view_lineage: 'View Lineage',
            lineage_title: 'Classification Lineage',
            no_events: 'No history events found',
            no_lineage: 'No lineage data available',
            modified: 'Modified',
            created: 'Created',
            removed: 'Removed',
            action_created: 'Created',
            action_updated: 'Updated',
            action_archived: 'Archived',
            action_approved: 'Approved',
            action_deprecated: 'Deprecated',
            entity_label: 'Label',
            entity_taxonomy: 'Taxonomy',
            entity_review_task: 'Review Task',
            entity_exception: 'Exception',
            before: 'Before',
            after: 'After',
        },
    },
    // ---------- Governance module (Sprint 1.3.0) ----------
    governance: {
        // Sprint 41.2.0 E6 — Governance Audit Trail page keys
        audit_trail_title: 'Governance Audit Trail',
        audit_trail_subtitle: 'Cross-entity audit stream for governance actions — approvals, playbook executions, evidence links, and more.',
        audit_trail_item_count: 'audit events',
        audit_trail_fetch_error: 'Failed to fetch audit events.',
        audit_trail_no_events: 'No audit events found for the current filters.',
        audit_trail_entity_type: 'Entity type',
        audit_trail_entity_id: 'Entity ID',
        audit_trail_action: 'Action',
        audit_trail_actor: 'Actor',
        audit_trail_timestamp: 'Timestamp',
        audit_trail_date_from: 'From',
        audit_trail_date_to: 'To',
        audit_trail_no_rows_to_export: 'No rows to export.',
        nav: {
            vendors: 'Vendors',
            subprocessors: 'Sub-Processors',
            cross_border: 'Cross-Border',
            drift: 'Drift Monitor',
            scores: 'Compliance Score',
            playbooks: 'Playbooks',
            approvals: 'Approvals',
            audit_trail: 'Audit Trail',
            board_report: 'Board Report',
            evidence: 'Evidence',
            onboarding: 'Onboarding Pipeline',
            dpa_reviews: 'DPA Reviews',
            periodic_reviews: 'Periodic Reviews',
            offboarding: 'Offboarding',
        },
        vendors: {
            title: 'Vendor Register',
            new: 'New Vendor',
            edit: 'Edit Vendor',
            name: 'Name',
            jurisdiction: 'Jurisdiction',
            dpa_status: 'DPA Status',
            risk_level: 'Risk Level',
            onboarding_status: 'Onboarding Status',
            contact_email: 'Contact Email',
            contact_name: 'Contact Name',
            contact_phone: 'Contact Phone',
            website: 'Website',
            services_csv: 'Services (comma-separated)',
            country: 'Country',
            country_placeholder: 'e.g. VN',
            last_reviewed: 'Last Reviewed',
            offboard: 'Offboard',
            offboard_vendor: 'Offboard Vendor',
            offboard_reason: 'Offboarding reason',
            no_vendors: 'No vendors found',
            vendor_count: '{count} vendor(s)',
            all_dpa_status: 'All DPA Status',
            all_risk_levels: 'All Risk Levels',
            all_onboarding: 'All Onboarding',
            fetch_error: 'Failed. Please try again.',
            save_error: 'Failed to save vendor',
            offboard_error: 'Failed to offboard',
        },
        vendor_types: {
            processor: 'Processor',
            sub_processor: 'Sub Processor',
            controller: 'Controller',
            joint_controller: 'Joint Controller',
        },
        onboarding_statuses: {
            pending_review: 'Pending Review',
            approved: 'Approved',
            active: 'Active',
            suspended: 'Suspended',
            offboarded: 'Offboarded',
        },
        dpa_statuses: {
            not_required: 'Not Required',
            required_missing: 'Required Missing',
            draft: 'Draft',
            under_review: 'Under Review',
            signed: 'Signed',
            expired: 'Expired',
        },
        risk_levels: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        approval_statuses: {
            pending: 'Pending',
            approved: 'Approved',
            rejected: 'Rejected',
            expired: 'Expired',
        },
        approval_action_types: {
            vendor_onboarding: 'Vendor Onboarding',
            vendor_offboarding: 'Vendor Offboarding',
            cross_border_acceptance: 'Cross-Border Acceptance',
            playbook_activation: 'Playbook Activation',
            drift_accept_risk: 'Drift Accept Risk',
            activity_activation: 'Activity Activation',
        },
        drift_check_types: {
            dpa_expiry: 'DPA Expiry',
            dpa_missing: 'DPA Missing',
            review_overdue: 'Review Overdue',
            cross_border_gap: 'Cross-Border Gap',
            sub_processor_chain: 'Sub-Processor Chain',
            risk_assessment_overdue: 'Risk Assessment Overdue',
            retention_policy_missing: 'Retention Policy Missing',
            evidence_expiring: 'Evidence Expiring',
        },
        drift_statuses: {
            open: 'Open',
            acknowledged: 'Acknowledged',
            in_progress: 'In Progress',
            resolved: 'Resolved',
            accepted_risk: 'Accepted Risk',
        },
        drift_entity_types: {
            vendor: 'Vendor',
            system: 'System',
            data_flow: 'Data Flow',
            processing_activity: 'Processing Activity',
            evidence_item: 'Evidence Item',
        },
        playbook_statuses: {
            draft: 'Draft',
            active: 'Active',
            archived: 'Archived',
        },
        playbook_action_types: {
            delete_data: 'Delete Data',
            verify_deletion: 'Verify Deletion',
            notify_vendor: 'Notify Vendor',
            confirm_receipt: 'Confirm Receipt',
            document_evidence: 'Document Evidence',
            other: 'Other',
        },
        playbook_target_types: {
            system: 'System',
            vendor: 'Vendor',
        },
        dpa_chain_statuses: {
            complete: 'Complete',
            incomplete: 'Incomplete',
            unknown: 'Unknown',
        },
        sub_processor_statuses: {
            active: 'Active',
            terminated: 'Terminated',
        },
        evidence_types: {
            dpa_agreement: 'DPA Agreement',
            risk_assessment: 'Risk Assessment',
            audit_report: 'Audit Report',
            certification: 'Certification',
            deletion_confirmation: 'Deletion Confirmation',
            training_record: 'Training Record',
            incident_report: 'Incident Report',
            document: 'Document',
            screenshot: 'Screenshot',
            policy: 'Policy',
            contract: 'Contract',
            assessment: 'Assessment',
            correspondence: 'Correspondence',
            other: 'Other',
        },
        linkable_entity_types: {
            vendor: 'Vendor',
            transfer_mechanism: 'Transfer Mechanism',
            playbook: 'Playbook',
            execution: 'Execution',
            system: 'System',
            activity: 'Activity',
        },
        subprocessors: {
            title: 'Sub-Processor Relationships',
            new: 'New Link',
            vendor: 'Vendor',
            subprocessor: 'Sub-Processor',
            relationship_type: 'Relationship Type',
            countries: 'Countries',
            countries_csv: 'Countries (comma-separated)',
            dpa_chain: 'DPA Chain',
            dpa_chain_status: 'DPA Chain Status',
            no_processors: 'No processors found.',
            no_subprocessors: 'No sub-processors found.',
            processor_vendor: 'Processor Vendor',
            select_processor: 'Select Processor',
            select_vendor: 'Select Vendor',
            services: 'Services',
            sub_processor_vendor_id: 'Sub-Processor Vendor ID',
            terminate: 'Terminate',
            confirm_terminate: 'Are you sure you want to terminate this relationship?',
            create_error: 'Failed to create link.',
            fetch_error: 'Failed to fetch sub-processors.',
            processor: 'Processor',
            services_csv: 'Services (comma-separated)',
            terminate_error: 'Failed to terminate.',
            // --- AC info boxes (versioning) ---
            version_history_heading: 'Sub-Processor Change Versioning',
            change_tracking_note: 'All sub-processor relationship changes are versioned. Each modification records the before/after state for audit trail.',
            history_retention_note: 'Change history is retained for the full data retention period. Review change logs via the vendor detail view.',
            vendor_risk_cross_view_heading: 'Sub-processor register (Vendor Risk)',
            vendor_risk_cross_view_source: 'Source: Vendor Risk Register',
            vendor_risk_cross_view_empty: 'No entries in the Vendor Risk register.',
        },
        cross_border: {
            title: 'Cross-Border Governance Report',
            flows: 'Cross-Border Flows',
            transfers: 'Cross-Border Transfers',
            detected_gaps: 'Detected Gaps',
            safeguards: 'Safeguards',
            cross_border_vendors: 'Cross-Border Vendors',
            destination: 'Destination',
            no_cross_border_vendors: 'No cross-border vendors found.',
            no_safeguard: 'No safeguard',
            processing_locations: 'Processing Locations',
            export_error: 'Failed to export report.',
            export_pdf: 'Export PDF',
            export_success: 'Report exported successfully.',
            exporting: 'Exporting...',
            fetch_error: 'Failed to fetch cross-border data.',
            source: 'Source',
            // --- AC info boxes (PDPL cross-border) ---
            pdpl_p2_heading: 'PDPL-P2 Cross-Border Transfer Compliance',
            dossier_prep_note: 'Dossier preparation: assemble impact assessment, legal basis, and safeguard documentation before filing.',
            filing_tracking_note: 'Filing tracking: monitor submission status with the Authority (PDPC) from initial application through approval.',
            supplement_loop_note: 'Supplement loop: respond to Authority information requests and re-submit amended dossiers as needed.',
            official_forms_note: 'Official forms: use Mau so 01a (Application), Mau so 01b (Supplementary Info), and Mau so 09 (Transfer Impact Assessment) per PDPL-P2 regulations.',
            vendor_search_label: 'Vendor search',
            vendor_search_placeholder: 'Search by vendor name…',
            jurisdiction_filter_label: 'Jurisdiction',
            // --- Sprint 41.5.0 G1 — Cross-Border Filings lifecycle ---
            filings_section_heading: 'Cross-Border Filings',
            filings_empty: 'No filings yet. Create one to start a dossier.',
            filings_create_button: 'New filing',
            filings_table_id: 'Filing ID',
            filings_table_jurisdiction: 'Jurisdiction',
            filings_table_type: 'Form type',
            filings_table_status: 'Status',
            filings_table_updated: 'Updated',
            filings_table_open: 'Open',
            filings_detail_title: 'Cross-Border Filing',
            filings_back_to_list: 'Back to Cross-Border',
            filings_status_heading: 'Lifecycle',
            filings_evidence_heading: 'Evidence',
            filings_history_heading: 'Audit trail',
            filings_field_filing_id: 'Filing ID',
            filings_field_jurisdiction: 'Jurisdiction',
            filings_field_type: 'Form type',
            filings_field_status: 'Status',
            filings_field_reference: 'Authority reference',
            filings_field_mps_receipt: 'MPS receipt',
            filings_field_submitted_at: 'Submitted at',
            filings_field_acknowledged_at: 'Acknowledged at',
            filings_field_archived_at: 'Archived at',
            filings_field_version: 'Version',
            filings_field_notes: 'Notes',
            filings_action_advance: 'Advance status',
            filings_action_reject: 'Reject',
            filings_action_archive: 'Archive',
            filings_action_submit: 'Mark submitted',
            filings_action_acknowledge: 'Mark acknowledged',
            filings_action_resume_draft: 'Resume as draft',
            filings_status_draft: 'Draft',
            filings_status_assessed: 'Assessed',
            filings_status_pending_review: 'Pending review',
            filings_status_ready_to_file: 'Ready to file',
            filings_status_submitted: 'Submitted',
            filings_status_acknowledged: 'Acknowledged',
            filings_status_rejected: 'Rejected',
            filings_status_archived: 'Archived',
            filings_type_mau_01a: 'Mẫu 01a — Transfer application',
            filings_type_mau_03a: 'Mẫu 03a — Supplement dossier',
            filings_type_mau_09: 'Mẫu 09 — Transfer impact assessment',
            filings_new_dialog_title: 'New cross-border filing',
            filings_new_form_jurisdiction: 'Jurisdiction (e.g. VN-MPS)',
            filings_new_form_type: 'Form type',
            filings_new_form_notes: 'Notes (optional)',
            filings_new_submit: 'Create',
            filings_new_cancel: 'Cancel',
            filings_reason_label: 'Reason',
            filings_reference_label: 'Authority reference number',
            filings_mps_receipt_label: 'MPS receipt reference',
            filings_create_error: 'Failed to create filing.',
            filings_create_success: 'Filing created.',
            filings_update_error: 'Failed to update filing.',
            filings_update_success: 'Filing updated.',
            filings_fetch_error: 'Failed to fetch filings.',
            filings_archive_confirm: 'Archive this filing? This cannot be undone.',
            // Sprint 41.5.0 G9.1 / G9.3 — UX polish keys
            filings_status_rejected_detour: 'Filing was rejected after review. Resume as draft to restart the flow.',
            recent_exports_heading: 'Recent exports',
            recent_exports_empty: 'No exports yet. Click "Export PDF" to generate one.',
        },
        drift: {
            title: 'Configuration Drift Monitor',
            drift_type: 'Drift Type',
            severity: 'Severity',
            detected_at: 'Detected At',
            resolve: 'Resolve',
            all_check_types: 'All Check Types',
            all_entity_types: 'All Entity Types',
            event_count: 'Event Count',
            accept_risk: 'Accept Risk',
            acknowledge: 'Acknowledge',
            assign_error: 'Failed to assign.',
            assign_event: 'Assign',
            assigned: 'Assigned',
            enter_resolution_notes: 'Enter resolution notes...',
            enter_user_id: 'Enter user ID...',
            entity: 'Entity',
            fetch_error: 'Failed to fetch drift events.',
            no_events: 'No drift events found.',
            resolution_notes: 'Resolution Notes',
            update_status_error: 'Failed to update status.',
        },
        scores: {
            title: 'Compliance Score Dashboard',
            overall: 'Overall Compliance Score',
            breakdown: 'Score Breakdown by Module',
            trend: 'Historical Trend',
            module: 'Module',
            score: 'Score',
            grade: 'Grade',
            calculated_at: 'Calculated At',
            generate_board_report: 'Generate Board Report',
            lang_english: 'English',
            lang_vietnamese: 'Vietnamese',
            fetch_error: 'Failed to fetch compliance scores.',
            generate_report: 'Generate Report',
            generating: 'Generating...',
            recommendations: 'Recommendations',
            report_error: 'Failed to generate report.',
            factor_owner_assigned: 'Owner assigned',
            factor_data_categories_linked: 'Data categories linked',
            factor_purposes_linked: 'Purposes linked',
            factor_data_flows_documented: 'Data flows documented',
            factor_processing_activity_linked: 'Processing activity linked',
            factor_dpa_status: 'DPA status',
            factor_retention_policy_linked: 'Retention policy linked',
            factor_last_reviewed: 'Last reviewed within threshold',
            factor_risk_assessment_current: 'Risk assessment current',
            factor_sub_processors_documented: 'Sub-processors documented',
            factor_cross_border_safeguards: 'Cross-border safeguards',
            factor_contact_info_complete: 'Contact information complete',
            factor_onboarding_approved: 'Onboarding approved',
            factor_avg_system_score: 'Average system score',
            factor_avg_vendor_score: 'Average vendor score',
            factor_processing_activity_coverage: 'Processing activity coverage',
            factor_ropa_completeness: 'RoPA completeness',
            factor_open_drift_events: 'Open drift events',
        },
        playbooks: {
            title: 'Deletion Playbooks',
            new: 'New Playbook',
            edit: 'Edit Playbook',
            steps: 'Steps',
            add_step: 'Add Step',
            remove_step: 'Remove Step',
            data_categories_csv: 'Data Categories (comma-separated)',
            estimated_duration: 'Estimated Duration',
            estimated_duration_label: 'Estimated Duration',
            evidence_description: 'Evidence Description',
            no_playbooks: 'No playbooks found.',
            playbook_count: 'Playbook Count',
            requires_evidence: 'Requires Evidence',
            step_label: 'Step Label',
            target_name: 'Target Name',
            target_type: 'Target Type',
            action_type: 'Action Type',
            archive_error: 'Failed to archive.',
            confirm_archive: 'Are you sure you want to archive this playbook?',
            data_categories: 'Data Categories',
            days: 'days',
            fetch_error: 'Failed to fetch playbooks.',
            sla_days: 'SLA (days)',
            target_id: 'Target ID',
            // --- AC info boxes (template classification) ---
            template_class_heading: 'Template Classification',
            official: 'Official',
            internal: 'Internal',
            classification: 'Classification',
            official_note: 'Official: regulatory-mandated templates aligned with PDPL, GDPR, or sector-specific requirements.',
            internal_note: 'Internal: organization-specific procedures not derived from regulatory templates.',
            active_executions_heading: 'Active executions',
            no_active_executions: 'No active executions.',
            playbook_label: 'Playbook',
            ticket_id_label: 'Ticket',
            progress_label: 'Progress',
            actions_label: 'Actions',
            view_steps_label: 'View steps',
            execution_detail_title: 'Execution detail',
            playbook_missing: 'Playbook not found.',
            evidence_required_label: 'Evidence required',
            upload_evidence_label: 'Upload evidence',
            mark_complete_label: 'Mark step complete',
            evidence_required_toast: 'Evidence required before completing this step.',
            evidence_blocking_message: 'Attach an evidence file to continue.',
            step_completed_toast: 'Step completed.',
            executions_fetch_error: 'Failed to load executions.',
            step_evidence_default_name: 'Playbook execution step evidence',
            attached_evidence_label: 'Attached evidence',
        },
        approvals: {
            title: 'Approval Workflows',
            new: 'New Approval',
            edit: 'Edit Approval',
            request_type: 'Request Type',
            requester: 'Requester',
            approve: 'Approve',
            reject: 'Reject',
            justification: 'Justification',
            all_action_types: 'All Action Types',
            decided_by: 'Decided By',
            no_approvals: 'No approvals found.',
            approval_count: 'Approval Count',
            approval_notes_optional: 'Notes (optional)',
            approve_error: 'Failed to approve.',
            approve_request: 'Approve Request',
            decision: 'Decision',
            entity: 'Entity',
            fetch_error: 'Failed to fetch approvals.',
            reject_error: 'Failed to reject.',
            reject_request: 'Reject Request',
            rejection_notes_required: 'Rejection notes required',
            rejection_notes_required_msg: 'Please provide a reason for rejection.',
            requested_at: 'Requested At',
            // --- AC info boxes (regulatory context) ---
            multi_step_heading: 'Multi-Step Approval Workflow',
            legal_review_note: 'Step 1 — Legal Review: Legal counsel reviews the request for regulatory compliance.',
            dpo_review_note: 'Step 2 — DPO Review: Data Protection Officer validates data protection implications.',
            management_review_note: 'Step 3 — Management Sign-Off: Senior management provides final authorization.',
            supporting_evidence_label: 'Supporting evidence (optional)',
            decision_evidence_name: 'Decision Evidence',
        },
        evidence: {
            title: 'Governance Evidence',
            new: 'New Evidence',
            link_to: 'Link To',
            entity_id_placeholder: 'Entity ID',
            file_path_placeholder: '/path/to/file',
            evidence_label: 'Evidence Label',
            file_path: 'File Path',
            source_mode_label: 'Evidence source',
            source_mode_file: 'Upload file',
            source_mode_url: 'Link external URL',
            attachment_label: 'Attachment',
            item_count: 'Item Count',
            link_button: 'Link Evidence',
            confirm_delete: 'Are you sure you want to delete this evidence?',
            enter_entity_id: 'Enter entity ID...',
            entity_id: 'Entity ID',
            entity_type: 'Entity Type',
            expires: 'Expires',
            expires_at: 'Expires At',
            fetch_error: 'Failed to fetch evidence.',
            link_error: 'Failed to link evidence.',
            no_evidence: 'No evidence found.',
            tags_csv: 'Tags (comma-separated)',
            uploaded: 'Uploaded',
            url: 'URL',
            // --- AC info boxes (framework-tagged evidence) ---
            framework_tag_heading: 'Framework-Tagged Evidence',
            framework_tag_note: 'Every evidence record captures framework code, actor, and timestamp. Filter by framework to isolate audit-relevant records.',
            framework_code: 'Framework Code',
            filter_by_framework: 'Filter by framework code...',
            actor: 'Actor',
        },
        certification: {
            not_applicable: 'Not Applicable',
            not_certified: 'Not Certified',
            pending: 'Pending',
            certified: 'Certified',
            revoked: 'Revoked',
        },
        onboarding_pipeline: {
            title: 'Onboarding Pipeline',
            new_workflow: 'New Onboarding Workflow',
            vendor_id: 'Vendor ID',
            stages: 'Stages',
            current_stage: 'Current Stage',
            advance: 'Advance Stage',
            advance_success: 'Stage advanced successfully',
            workflow_status: 'Workflow Status',
            initiated: 'Initiated',
            in_progress: 'In Progress',
            completed: 'Completed',
            cancelled: 'Cancelled',
            no_workflows: 'No onboarding workflows found',
            stages_placeholder: 'Stage 1, Stage 2, Stage 3',
            assigned_to: 'Assigned To',
            notes: 'Notes',
            // --- AC info boxes (PDPL onboarding) ---
            pdpl_lifecycle_heading: 'PDPL Vendor Lifecycle — Onboarding',
            due_diligence_note: 'Due diligence stage is mandatory before onboarding any processor under PDPL. Verify data processing scope, legal basis, and security measures.',
            contract_review_note: 'Contract review must confirm DPA clauses, data retention limits, and sub-processor restrictions.',
            create_dpa_review_cta: 'Create DPA review',
            dpa_review_linked: 'DPA review linked',
        },
        dpa_review: {
            title: 'DPA Reviews',
            new_review: 'New DPA Review',
            review_type: 'Review Type',
            findings: 'Findings',
            outcome: 'Outcome',
            complete_review: 'Complete Review',
            complete_success: 'Review completed successfully',
            link_assessment: 'Link Security Assessment',
            link_success: 'Assessment linked successfully',
            assessment_id: 'Assessment ID',
            combined_title: 'Combined Assessment',
            no_reviews: 'No DPA reviews found',
            // --- AC info boxes (telecom disclosure governance) ---
            telecom_disclosure_heading: 'Telecom Disclosure Governance',
            disclosure_trigger_note: 'Disclosure-triggered reviews capture telecom subscriber data sharing events per VN Telecom Law.',
            telecom_t2_note: 'TELECOM-T2 tier approval is required before disclosing subscriber data to authorities.',
            quarterly_log_note: 'Use the quarterly log to document all disclosure events and maintain a continuous audit trail.',
        },
        periodic_review: {
            title: 'Periodic Reviews',
            new_review: 'New Periodic Review',
            review_type: 'Review Type',
            due_date: 'Due Date',
            next_review: 'Next Review Date',
            trigger_reassessment: 'Trigger Reassessment',
            reassessment_reason: 'Reassessment Reason',
            reassessment_success: 'Reassessment triggered successfully',
            complete_success: 'Review completed successfully',
            scheduled: 'Scheduled',
            in_progress: 'In Progress',
            completed: 'Completed',
            overdue: 'Overdue',
            material_change: 'Material Change',
            no_reviews: 'No periodic reviews found',
            mark_complete: 'Mark as Complete',
            // --- AC info boxes (PDPL periodic reviews) ---
            pdpl_periodic_heading: 'PDPL Vendor Lifecycle — Periodic Reviews',
            scheduled_review_note: 'Periodic reviews must be scheduled at least annually for all active processors. Overdue reviews trigger compliance alerts.',
            material_change_note: 'Material changes in data processing require immediate reassessment regardless of schedule.',
            overdue_badge: 'Overdue',
            overdue_aria: 'Overdue review',
            only_overdue_filter: 'Only overdue',
        },
        offboarding: {
            title: 'Vendor Offboarding',
            initiate: 'Initiate Offboarding',
            initiate_success: 'Offboarding initiated successfully',
            reason: 'Offboarding Reason',
            data_return_required: 'Data Return Required',
            verify_data: 'Verify Data Return',
            verify_success: 'Data return verified successfully',
            sign_off: 'Sign Off',
            sign_off_success: 'Offboarding signed off successfully',
            status_initiated: 'Initiated',
            status_data_pending: 'Data Return Pending',
            status_data_verified: 'Data Return Verified',
            status_completed: 'Completed',
            status_cancelled: 'Cancelled',
            task_id: 'Task ID',
            no_tasks: 'No offboarding tasks found',
            evidence_id: 'Evidence ID',
            // --- AC info boxes (PDPL offboarding) ---
            pdpl_offboarding_heading: 'PDPL Vendor Lifecycle — Offboarding & Deletion',
            deletion_capture_note: 'Offboarding must capture data deletion confirmation from the vendor. Verify data return before sign-off.',
            evidence_attachment_note: 'Attach deletion evidence (certificates, screenshots, vendor confirmations) to complete the offboarding record.',
            verify_mode_label: 'Verification method',
            verify_mode_upload: 'Upload new file',
            verify_mode_link: 'Link existing evidence',
            verify_mode_none: 'No data to return',
            upload_data_return_evidence: 'Upload data return evidence',
            no_data_justification_label: 'Justification',
            no_data_justification_placeholder: 'Explain why no data needs to be returned',
            no_data_justification_hint: 'Minimum 20 characters',
            upload_required_toast: 'Attach a file to continue',
            evidence_link_required_toast: 'Select an evidence record to continue',
            no_data_justification_required_toast: 'Provide a justification of at least 20 characters',
            data_return_evidence_name: 'Data Return Evidence',
        },
        dpa_review_types: {
            initial: 'Initial',
            annual: 'Annual',
            renewal: 'Renewal',
            incident_triggered: 'Incident Triggered',
        },
        periodic_review_types: {
            initial: 'Initial',
            annual: 'Annual',
            renewal: 'Renewal',
            incident_triggered: 'Incident Triggered',
            material_change: 'Material Change',
            disclosure_triggered: 'Disclosure Triggered',
            quarterly_log: 'Quarterly Log',
        },
        dpa_review_statuses: {
            pending: 'Pending',
            in_progress: 'In Progress',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
        onboarding_stage_names: {
            due_diligence: 'Due Diligence',
            contract_review: 'Contract Review',
            security_assessment: 'Security Assessment',
            data_mapping: 'Data Mapping',
            approval: 'Approval',
            integration: 'Integration',
        },
        access_denied_title: 'Access Denied',
        access_denied: 'You do not have permission to view this page. Please contact your administrator.',
    },
    // ---------- Consent module (Sprint 1.4.0) ----------
    consent: {
        nav: {
            policies: 'Policies',
            purposes: 'Purpose Catalog',
            workflows: 'Workflows',
            snapshots: 'CMP Snapshots',
            change_awareness: 'Change Awareness',
            audit: 'Consent Audit',
        },
        // Sprint 48.1.4 — Forseti consent gap analysis page
        gap_analysis: {
            nav_label: 'Gap Analysis',
            page_title: 'Consent Gap Analysis',
            page_subtitle: 'Forseti checks your active consent purposes against installed-framework requirements (GDPR Art.7 + Art.6 + Art.9, PDPL Art.13 + Art.20, PIPL Art.13 + Art.29) and surfaces gaps with citation + suggested remediation.',
            framework_picker_label: 'Frameworks',
            org_flags_label: 'Organisation context',
            flag_withdrawal: 'Has global withdrawal mechanism documented',
            flag_parental: 'Has parental-consent flow for under-16 subjects',
            flag_cross_border: 'Has cross-border filing on record',
            run_label: 'Run gap analysis',
            running_label: 'Running…',
            requires_advisor_label: 'Requires ai_advisor:chat permission.',
            results_heading: 'Gaps surfaced ({count})',
            tokens_label: '≈ {count} tokens',
            empty_label: 'No gaps detected for the selected frameworks.',
            remediation_label: 'Suggested remediation:',
            dismiss_label: 'Dismiss as not applicable',
            resolve_label: 'Resolve',
            dismissed_label: 'Dismissed as not applicable',
            resolved_label: 'Resolved',
        },
        import: {
            title: 'Import Consent Workflows',
            nav_title: 'Bulk Import',
            description: 'Upload a CSV file to bulk import consent workflows.',
            select_file: 'Select CSV File',
            file_help: 'CSV with headers matching workflow fields.',
            required_columns_help: 'Required columns: Workflow Type, Data Subject, Channel.',
            file_too_large: 'File too large. Maximum size is 2MB.',
            preview: 'Preview',
            columns_detected: 'Columns detected: {count}',
            column_mapping: 'Column Mapping',
            import_button: 'Import',
            import_complete: 'Import complete',
            import_error: 'Import failed',
            result: 'Import Result',
            total: 'Total',
            imported: 'Imported',
            skipped: 'Skipped',
            failed: 'Failed',
            errors: 'Errors',
            error_row: 'Row',
            error_field: 'Field',
            error_message: 'Message',
            history: 'Import History',
            no_history: 'No import history.',
            history_file: 'Filename',
            history_date: 'Date',
            create_profiles_label: 'Create Data Subject profiles for new subjects',
            create_profiles_help: 'Imports without a matching profile will create one automatically. Uncheck to import workflows only.',
            profiles_created: 'Profiles created',
            profiles_matched: 'Profiles matched',
            subject_kind_help: 'Optional columns: subject_kind, subject_email, subject_display_name, subject_phone, external_ref_key.',
            error_subject_invalid_email: 'Invalid email in subject_email column.',
            error_subject_unknown_kind: 'Unknown subject_kind value. Expected: email, visitor_uuid, employee_id, student_id, external_ref.',
            error_missing_external_ref_key: 'external_ref_key is required when subject_kind=external_ref',
        },
        policies: {
            title: 'Consent Policies',
            new: 'New Policy',
            edit: 'Edit Policy',
            name: 'Name',
            version: 'Version',
            policy_text: 'Policy Text',
            publish: 'Publish',
            versions: 'Versions',
            hide_versions: 'Hide Versions',
            submit_for_review: 'Submit for Review',
            suspend: 'Suspend',
            retire: 'Retire',
            jurisdictions: 'Jurisdictions',
            jurisdictions_csv: 'Jurisdictions (comma-separated)',
            approval_mode: 'Approval Mode',
            reviewer_ids: 'Reviewer IDs (comma-separated)',
            tags_csv: 'Tags (comma-separated)',
            effective_date: 'Effective Date',
            review_date: 'Review Date',
            consent_model_statement: 'Consent Model Statement',
            consent_model_placeholder: 'Optional consent model statement...',
            default_settings: 'Default Settings (JSON)',
            changes: 'Changes',
            fetch_error: 'Failed to fetch consent policies. Please try again.',
            save_error: 'Failed to save consent policy. Please try again.',
            submit_review_error: 'Failed to submit policy for review. Please try again.',
            activate_error: 'Failed to activate policy. Please try again.',
            suspend_error: 'Failed to suspend policy. Please try again.',
            retire_error: 'Failed to retire policy. Please try again.',
            submit_review_success: 'Policy submitted for review.',
            activate_success: 'Policy activated.',
            suspend_success: 'Policy suspended.',
            retire_success: 'Policy retired.',
            versions_error: 'Failed to fetch policy versions. Please try again.',
            invalid_json: 'Invalid JSON in Default Settings field. Please try again.',
            page_title: 'Consent Policies',
            version_updated: 'Updated',
            version_initial: 'Initial version',
            json_format: 'JSON format',
            sensitive: 'Sensitive',
            flag_sensitive: 'Flag Sensitive',
            flag_sensitive_title: 'Flag Policy as Sensitive',
            require_enhanced_review: 'Require Enhanced Review',
            enhanced_review_success: 'Enhanced review requirement enabled.',
            select_categories: 'Select the applicable PDPL Article 2 sensitive data categories:',
            no_policies: 'No consent policies found. Create your first policy to get started.',
            approval_success: 'Policy approved.',
            reviewers_label: 'Reviewers',
            // Sprint 28.1.3 — Enhanced Review Workflow
            enhanced_review_title: 'Enhanced Review Required',
            enhanced_review_badge: 'Enhanced Review Required',
            enhanced_review_status_pending: 'Pending Review',
            enhanced_review_status_approved: 'Review Approved',
            enhanced_review_status_rejected: 'Review Rejected',
            enhanced_review_reviewed_by: 'Reviewed by',
            enhanced_review_notes: 'Review Notes',
            enhanced_review_approve: 'Approve Review',
            enhanced_review_reject: 'Reject Review',
            enhanced_review_notes_required: 'Review notes are required',
            enhanced_review_approved: 'Enhanced review approved',
            enhanced_review_rejected: 'Enhanced review rejected — policy reset to draft',
            enhanced_review_reset_to_draft: 'Enhanced review rejected — policy reset to draft',
            enhanced_review_reject_warning: 'Rejection will reset this policy to Draft status. The DPO must resubmit for review after addressing the issues.',
            submitted_modal_title: 'Submitted for Enhanced Review',
            submitted_modal_body: 'This policy has been submitted. A Legal/Privacy reviewer must approve it before it can be activated.',
        },
        sensitive_categories: {
            health: 'Health Data',
            biometric: 'Biometric Data',
            political: 'Political Opinions',
            ethnic: 'Ethnic Origin',
            genetic: 'Genetic Data',
            criminal: 'Criminal Records',
        },
        purposes: {
            title: 'Purpose Catalog',
            new: 'New Purpose',
            edit: 'Edit Purpose',
            name: 'Name',
            legal_basis: 'Legal Basis',
            retention_days: 'Retention Period (days)',
            is_active: 'Active',
            filter_by_category: 'Filter by category',
            policy_ids: 'Policy IDs (comma-separated)',
            ropa_activity_ids: 'ROPA Activity IDs (comma-separated)',
            policies: 'Policies',
            deprecate: 'Deprecate',
            fetch_error: 'Failed to fetch consent purposes. Please try again.',
            save_error: 'Failed to save consent purpose. Please try again.',
            deprecate_error: 'Failed to deprecate consent purpose. Please try again.',
            page_title: 'Purpose Catalog',
            no_purposes: 'No consent purposes found. Create your first purpose to get started.',
            category_personal_data: 'Personal Data',
            category_sensitive_data: 'Sensitive Data',
            category_health_data: 'Health Data',
            category_financial_data: 'Financial Data',
            category_biometric_data: 'Biometric Data',
            category_children_data: "Children's Data",
            category_location_data: 'Location Data',
            category_communications_data: 'Communications Data',
            category_other: 'Other',
        },
        workflows: {
            title: 'Consent Workflows',
            new: 'New Workflow',
            edit: 'Edit Workflow',
            workflow_type: 'Workflow Type',
            user_id: 'User ID',
            purpose_id: 'Purpose ID',
            purpose_ids_csv: 'Purpose ID (comma-separated)',
            reason: 'Reason',
            process: 'Process',
            data_subject_ref: 'Data Subject Reference',
            data_subject: 'Data Subject',
            channel: 'Channel',
            policy_id: 'Policy ID',
            scope: 'Scope',
            legal_grounds: 'Legal Grounds',
            assessment: 'Assessment',
            select_outcome: 'Select Outcome',
            partially_approved: 'Partially Approved',
            denied: 'Denied',
            outcome_upheld: 'Upheld',
            outcome_pending: 'Pending',
            outcome_denied: 'Denied',
            status_count: 'Status Counts',
            channel_filter: 'Filter by channel',
            add_action: '+ Action',
            by_type: 'By Type',
            by_status: 'By Status',
            downstream_actions: 'Downstream Actions',
            downstream_actions_for: 'Downstream Actions for {subject} ({type})',
            add_downstream_action: 'Add Downstream Action',
            assigned_to: 'Assigned To',
            fetch_error: 'Failed to fetch consent workflows. Please try again.',
            dashboard_error: 'Failed to fetch workflow dashboard. Please try again.',
            save_error: 'Failed to save consent workflow. Please try again.',
            status_update_error: 'Failed to update workflow status. Please try again.',
            assessment_error: 'Failed to update workflow assessment. Please try again.',
            add_action_error: 'Failed to add workflow action. Please try again.',
            update_action_error: 'Failed to update workflow action. Please try again.',
            type_refusal: 'Refusal',
            type_withdrawal: 'Withdrawal',
            type_restriction: 'Restriction',
            type_objection: 'Objection',
            status_received: 'Received',
            status_processing: 'Processing',
            status_under_assessment: 'Under Assessment',
            status_implemented: 'Implemented',
            // Sprint 41.3.0 F8 — workflow evidence attachments
            evidence_button: 'Evidence',
            evidence_modal_title: 'Workflow Evidence',
            no_evidence: 'No evidence attached to this workflow.',
            upload_evidence_label: 'Upload workflow evidence',
            completed_at: 'Completed At',
            effective_date: 'Effective Date',
            page_title: 'Consent Workflows',
            no_workflows: 'No consent workflows found. Workflows are created when data subjects exercise their rights.',
            type_grant: 'Grant',
            propagation_none: 'None',
            propagation_pending: 'Pending',
            propagation_pending_warning: 'Cannot complete — withdrawal propagation still pending',
            propagation_completed: 'Completed',
            propagation_partial: 'Partial',
            propagation_incomplete_error: 'Cannot complete workflow while withdrawal propagation is still pending',
            propagation_status: 'Propagation Status',
        },
        snapshots: {
            title: 'CMP Snapshots',
            new: 'New Snapshot',
            label: 'Label',
            snapshot_date: 'Snapshot Date',
            purposes_count: 'Purposes Count',
            compare: 'Compare',
            snapshot_data: 'Snapshot Data (JSON)',
            select_left: 'Select Left Snapshot',
            select_right: 'Select Right Snapshot',
            comparison_results: 'Comparison Results',
            field: 'Field',
            left: 'Left',
            right: 'Right',
            close_comparison: 'Close Comparison',
            trigger: 'Trigger',
            trigger_manual: 'Manual',
            trigger_policy_approval: 'Policy Approval',
            cmp_version: 'CMP Version',
            policy_version_id: 'Policy Version ID',
            banner_config: 'Banner Config (JSON)',
            active_purposes: 'Active Purposes (JSON)',
            active_legal_bases: 'Active Legal Bases (JSON)',
            domain_scope: 'Domain Scope',
            domain_scope_csv: 'Domain Scope (comma-separated)',
            raw_config: 'Raw Config (JSON)',
            checksum: 'Checksum',
            created: 'Created',
            fetch_error: 'Failed to fetch CMP snapshots. Please try again.',
            save_error: 'Failed to save CMP snapshot. Please try again.',
            compare_error: 'Failed to compare snapshots. Please try again.',
            page_title: 'Consent Snapshots',
            json_format: 'JSON format',
            json_array_format: 'JSON array format',
            manual_snapshot_note: 'For admin/developer use only. In normal operation, snapshots are created automatically via CMP webhook or on policy approval. Paste JSON exported directly from your CMP vendor (e.g. OneTrust, Cookiebot).',
            banner_config_hint: 'Paste the banner configuration object from your CMP export.',
            active_purposes_hint: 'JSON array of active purpose objects copied from your CMP export.',
            active_legal_bases_hint: 'JSON array of active legal basis objects copied from your CMP export.',
            raw_config_hint: 'Full raw configuration JSON from your CMP. Other JSON fields are extracted from this. A SHA-256 checksum is computed automatically.',
            changed_field: 'Changed field: ',
            no_snapshots: 'No CMP snapshots found. Snapshots are created when policies are approved or manually triggered.',
            detail_title: 'Snapshot Detail',
            view_snapshot_detail: 'View snapshot detail',
            policy_version: 'Policy Version',
            removed: 'Before',
            added: 'After',
        },
        change_awareness: {
            title: 'Consent Change Awareness',
            change_type: 'Change Type',
            entity_type: 'Entity Type',
            entity_id: 'Entity ID',
            detected_at: 'Detected At',
            acknowledge: 'Acknowledge',
            new_alert: 'New Alert',
            new_alert_form: 'New Change Awareness Alert',
            total_open: 'Total Open',
            by_severity: 'By Severity',
            snapshot_id: 'Snapshot ID',
            update_status: 'Update Status',
            resolution_notes: 'Resolution Notes',
            resolution_required: 'Resolution Notes (required)',
            resolution_required_msg: 'Resolution notes are required when resolving an item.',
            status_open: 'Open',
            status_under_review: 'Under Review',
            status_resolved: 'Resolved',
            status_accepted: 'Accepted',
            severity_informational: 'Informational',
            severity_requires_review: 'Requires Review',
            severity_policy_violation: 'Policy Violation',
            create_reconciliation: 'Create Reconciliation Task',
            reconciliation_created: 'Reconciliation task created successfully.',
            reconciliation_error: 'Failed to create reconciliation task. Please try again.',
            fetch_error: 'Failed to fetch change awareness items. Please try again.',
            dashboard_error: 'Failed to fetch change awareness dashboard. Please try again.',
            create_error: 'Failed to create change awareness item. Please try again.',
            status_update_error: 'Failed to update change awareness status. Please try again.',
            page_title: 'Change Awareness',
            no_items: 'No change awareness items found. Items are created when consent configuration changes are detected.',
        },
        audit: {
            title: 'Consent Audit Trail',
            event_type: 'Event Type',
            user_id: 'User ID',
            timestamp: 'Timestamp',
            details: 'Details',
            actor: 'Actor',
            filter_by_entity_type: 'Filter by entity type',
            filter_by_action: 'Filter by action',
            fetch_error: 'Failed to fetch consent audit trail. Please try again.',
            page_title: 'Consent Audit',
            export_csv: 'Export CSV',
            exporting: 'Exporting\u2026',
            export_success: 'Audit trail exported successfully.',
            export_error: 'Failed to export consent audit trail. Please try again.',
            no_entries: 'No audit entries found. Audit events are recorded automatically when consent data changes.',
            entity_policy: 'Policy',
            entity_purpose: 'Purpose',
            entity_workflow: 'Workflow',
            entity_consent_record: 'Consent Record',
            entity_change_awareness: 'Change Awareness',
            entity_cmp_snapshot: 'CMP Snapshot',
            action_created: 'Created',
            action_updated: 'Updated',
            action_status_changed: 'Status Changed',
            action_drift_detected: 'Drift Detected',
            action_snapshot_created: 'Snapshot Created',
            action_approval_submitted: 'Approval Submitted',
            action_archived: 'Archived',
            action_purpose_auto_mapped: 'Purpose Auto Mapped',
            action_purpose_mapping_updated: 'Purpose Mapping Updated',
            action_webhook_configured: 'Webhook Configured',
            entity_propagation: 'Propagation',
            entity_reconciliation: 'Reconciliation',
            action_propagation_confirmed: 'Propagation Confirmed',
            action_propagation_failed: 'Propagation Failed',
            action_consent_captured: 'Consent Captured',
            action_reconciliation_created: 'Reconciliation Created',
            action_reconciliation_resolved: 'Reconciliation Resolved',
            action_submitted_for_review: 'Submitted for Review',
            action_activated: 'Activated',
            action_suspended: 'Suspended',
            action_retired: 'Retired',
            action_enhanced_review_required: 'Enhanced Review Required',
            action_enhanced_review_approved: 'Enhanced review approved',
            action_enhanced_review_rejected: 'Enhanced review rejected — policy reset to draft',
            action_consented: 'Consented',
            action_consented_customize: 'Consent Customized',
            action_revoked: 'Revoked',
            action_rejected: 'Rejected',
        },
        web_records: {
            title: 'Web Consent Records',
            page_title: 'Web Consent Records',
            domain: 'Domain',
            visitor_id: 'Visitor ID',
            consent_action: 'Consent Action',
            received_at: 'Received At',
            admin_status: 'Admin Status',
            revoke: 'Revoke',
            reject: 'Reject',
            reason: 'Reason',
            reason_placeholder: 'Reason for this action (optional)',
            revoke_confirm: 'Revoke this consent record?',
            reject_confirm: 'Reject this consent record?',
            override_success: 'Record status updated successfully.',
            override_error: 'Failed to update record status.',
            no_records: 'No web consent records found.',
            fetch_error: 'Failed to load web consent records.',
            filter_by_domain: 'Filter by domain',
            filter_by_action: 'Filter by action',
            active: 'Active',
        },
        legal_bases: {
            all: 'All Legal Bases',
            consent: 'Consent',
            legitimate_interest: 'Legitimate Interest',
            contract: 'Contract',
            legal_obligation: 'Legal Obligation',
            vital_interest: 'Vital Interest',
            public_interest: 'Public Interest',
        },
        common_statuses: {
            suspended: 'Suspended',
            retired: 'Retired',
            deprecated: 'Deprecated',
        },
        approval_modes: {
            all: 'All',
            any: 'Any',
        },
        cmp_integration: {
            title: 'AesirX CMP Integration',
            back_to_consent: 'Back to Consent',
            back_to_integration: 'Back to Integration',
            webhook_status: 'Webhook Status',
            events_received: 'Events Received',
            web_consent_records: 'Web Consent Records',
            unmapped_purposes: 'Unmapped Purposes',
            integration_management: 'Integration Management',
            webhook_configuration: 'Webhook Configuration',
            webhook_config_desc: 'Configure the CMP webhook endpoint and manage the HMAC secret.',
            purpose_mapping: 'Purpose Mapping',
            purpose_mapping_desc: 'Map CMP purpose identifiers to GRC consent catalog purposes.',
            bulk_sync: 'Re-map Records',
            bulk_sync_desc: 'Re-apply purpose mappings to all unmapped consent records.',
            consent_analytics: 'Web Consent Analytics (Last 30 Days)',
            consent_grant_rate: 'Consent Grant Rate',
            top_domain: 'Top Domain',
            records: 'records',
            banner_interactions: 'Banner Interactions',
            consent_actions: 'Consent Actions',
            opt_in_trend: 'Opt-in/Opt-out Trend',
            records_by_domain: 'Records by Domain',
            date: 'Date',
            grants: 'Grants',
            denials: 'Denials',
            not_configured: 'Not Configured',
            load_error: 'Failed to load integration data',
            webhook_not_configured: 'Webhook not configured.',
            webhook_not_configured_msg: 'Please {link} first.',
            configure_webhook_link: 'configure the webhook',
            setup_wizard: 'Setup Wizard',
            step_configure_webhook: 'Configure Webhook',
            step_map_purposes: 'Map Purposes',
            step_run_sync: 'Run Initial Bulk Sync',
            step_verify_data: 'Verify Data in Dashboard',
            configure: 'Configure',
            view_dashboard: 'View Dashboard',
            syncing: 'Syncing...',
            sync_now: 'Sync Now',
            sync_completed: 'Sync completed: {imported} imported, {skipped} skipped, {failed} failed.',
            sync_failed: 'Sync failed',
            last_sync_result: 'Last Sync Result',
            imported: 'Imported',
            skipped: 'Skipped',
            errors: 'Errors',
            index: 'Index',
            error: 'Error',
            sync_history: 'Sync History',
            no_sync_jobs: 'No sync jobs recorded yet.',
            load_sync_error: 'Failed to load sync data',
            purpose_mapping_title: 'CMP Purpose Mapping',
            auto_mapping: 'Auto-Mapping...',
            auto_map_purposes: 'Auto-Map Purposes',
            unmapped_warning: '{count} unmapped purpose(s) -- these CMP purposes need to be mapped to GRC purposes.',
            mapping_saved: 'Mapping saved.',
            mapping_deleted: 'Mapping deleted.',
            auto_map_complete: 'Auto-map complete: {mapped} mapped, {unmatched} unmatched.',
            auto_map_failed: 'Auto-map failed',
            save_mapping_error: 'Failed to save mapping',
            delete_mapping_error: 'Failed to delete mapping',
            load_data_error: 'Failed to load data',
            cmp_purpose: 'CMP Purpose',
            grc_purpose: 'GRC Purpose',
            select_grc_purpose: '-- Select GRC Purpose --',
            auto_mapped: 'Auto-Mapped',
            mapped: 'Mapped',
            unmapped: 'Unmapped',
            no_mappings: 'No purpose mappings found. Mappings are created automatically when consent events are received.',
            total_mappings: 'Total: {count} mapping(s)',
            webhook_config_title: 'Webhook Configuration',
            current_configuration: 'Current Configuration',
            setup_webhook: 'Setup Webhook',
            setup_webhook_desc: 'Configure the AesirX CMP webhook to receive consent events.',
            allowed_domains: 'Allowed Domains (one per line)',
            webhook_secret_warning: 'Webhook Secret (save this now -- it will not be shown again):',
            last_event: 'Last Event',
            none_received: 'None',
            update_domains: 'Update Domains',
            pause_webhook: 'Pause Webhook',
            resume_webhook: 'Resume Webhook',
            regenerate_secret: 'Regenerate Secret',
            configuring: 'Configuring...',
            create_webhook_config: 'Create Webhook Configuration',
            webhook_endpoint: 'Webhook Endpoint',
            webhook_endpoint_desc: 'Configure your AesirX CMP to send consent events to:',
            webhook_signature_note: 'Include the HMAC-SHA256 signature in the {header} header.',
            configured_success: 'Webhook configured successfully.',
            config_updated: 'Configuration updated.',
            config_error: 'Failed to configure webhook',
            update_error: 'Failed to update configuration',
            toggle_error: 'Failed to toggle webhook',
            deactivated: 'Webhook deactivated.',
            deactivate_error: 'Failed to deactivate webhook',
            page_title: 'CMP Integration',
            webhook_config_page_title: 'CMP Webhook Configuration',
            bulk_sync_page_title: 'Re-map Records',
            purpose_mapping_page_title: 'Purpose Mapping',
            data_loaded: 'CMP integration data loaded',
            sync_all: 'Sync All',
            sync_selected: 'Sync Selected',
            sync_status: 'Sync Status',
            last_sync: 'Last Sync',
            no_records: 'No records found.',
            sync_in_progress: 'Sync in progress...',
            sync_complete: 'Sync complete.',
            sync_error: 'Sync failed. Please try again.',
            map_purpose: 'Map Purpose',
            unmap: 'Unmap',
            mapping_status: 'Mapping Status',
            save_mapping: 'Save Mapping',
            remap_unmapped_records: 'Re-map Unmapped Records',
            remap_records: 'Re-map Records',
            add_mappings_first: 'Add purpose mappings first (Step 2)',
            remap_result: 'Re-mapping Result',
            remap_result_desc: 'Processed all consent records with unmapped or partially mapped purposes.',
            total_processed: 'Total Processed',
            newly_mapped: 'Newly Mapped',
            still_unmapped_label: 'Still Unmapped',
            still_unmapped_msg: '{count} record(s) still have unmapped purposes. {link} then click Re-map again.',
            add_more_mappings: 'Add more mappings',
            remap_complete: 'Re-mapping complete: {remapped} newly mapped, {still_unmapped} still unmapped.',
            add_mapping: 'Add Mapping',
            cmp_purpose_id_label: 'CMP Purpose ID *',
            cmp_purpose_id_required: 'CMP Purpose ID is required',
            cmp_purpose_name_label: 'CMP Purpose Name',
            grc_purpose_label: 'GRC Purpose',
        },
        propagation: {
            title: 'Withdrawal Propagation',
            systems: 'Linked Systems',
            status: 'Status',
            confirmed: 'Confirmed',
            pending: 'Pending',
            failed: 'Failed',
            confirm: 'Confirm Withdrawal',
            fail: 'Mark Failed',
            failure_reason: 'Failure Reason',
            can_close: 'All systems confirmed — workflow can be closed',
            cannot_close: 'Waiting for system confirmations',
            no_tasks: 'No propagation tasks.',
            confirm_success: 'System withdrawal confirmed.',
            fail_success: 'System marked as failed.',
            propagate_success: 'Withdrawal propagated to linked systems.',
            propagate: 'Propagate to Systems',
            add_system: 'Add System',
            remove_system: 'Remove',
            system_id: 'System ID',
            system_name: 'System Name',
            owner_id: 'Owner ID',
            propagate_error: 'Failed to propagate withdrawal. Please try again.',
            no_systems: 'Add at least one system.',
            page_title: 'Withdrawal Propagation',
            workflow_id: 'Select Withdrawal Workflow',
            system_id_hint: 'e.g. crm-001, erp-prod',
            owner_id_hint: 'System owner',
            elapsed: 'Elapsed',
        },
        sensitive: {
            title: 'Sensitive Data Policies',
            flag: 'Flag as Sensitive',
            categories: 'Sensitive Categories',
            enhanced_review: 'Require Enhanced Review',
            enhanced_review_success: 'Enhanced review required.',
            flag_success: 'Policy flagged as sensitive.',
            no_sensitive: 'No sensitive data policies.',
        },
        reconciliation: {
            title: 'Mismatch Reconciliation',
            create: 'New Reconciliation Task',
            system: 'System',
            owner: 'Owner',
            description: 'Description',
            resolution: 'Resolution',
            resolve: 'Resolve',
            update_grc: 'Update GRC',
            update_cmp: 'Update CMP',
            accept_difference: 'Accept Difference',
            escalate: 'Escalate',
            resolution_notes: 'Resolution Notes',
            no_tasks: 'No reconciliation tasks.',
            resolve_success: 'Reconciliation task resolved.',
            create_success: 'Reconciliation task created.',
            open: 'Open',
            resolved: 'Resolved',
            escalated: 'Escalated',
            dashboard_open: 'Open Tasks',
            dashboard_resolved: 'Resolved Tasks',
            dashboard_escalated: 'Escalated Tasks',
            page_title: 'Mismatch Reconciliation',
            my_tasks: 'My Tasks',
            all_tasks: 'All Tasks',
            severity: 'Severity',
        },
        sync: {
            title: 'CMP Sync',
            direction: 'Direction',
            cmp_to_grc: 'CMP → GRC',
            grc_to_cmp: 'GRC → CMP',
            conflict: 'Conflict',
            resolve_conflict: 'Resolve Conflict',
            no_events: 'No sync events.',
            resolve_success: 'Sync conflict resolved.',
            create_success: 'Sync event recorded.',
            page_title: 'CMP Sync',
            detect_drift: 'Detect Conflicts',
            detecting: 'Detecting...',
            drift_result: '{detected} conflict(s) detected out of {total} purpose(s) compared.',
            no_drift: 'No new conflicts detected.',
            drift_error: 'Failed to run conflict detection.',
            resolve_modal_title: 'Resolve Conflict',
            resolve_field: 'Field',
            resolve_grc_value: 'GRC Value',
            resolve_cmp_value: 'CMP Value',
            accept_cmp: 'Accept CMP Value',
            accept_cmp_desc: 'Update GRC to match the CMP — the CMP value becomes the new GRC value.',
            keep_grc: 'Keep GRC Value',
            keep_grc_desc: 'Close this conflict without changing GRC — notify the CMP owner to align.',
            resolve_applied: 'Conflict resolved. GRC purpose updated to match CMP.',
        },
    },
    // ---------- Assessments module (Sprint 1.5.0) ----------
    assessments: {
        // Sprint 48.1.9 — Dossier pre-flight + auto-assembly panel
        dossier_preflight: {
            advisory_notice: 'This readiness report is advisory. It does not approve, assemble, materialise, file, or send the dossier.',
            run_label: 'Run pre-flight',
            running: 'Running pre-flight…',
            assemble_label: 'Auto-assemble package',
            assembling: 'Assembling…',
            verdict_label: 'Pre-flight verdict',
            tokens_label: '≈ {count} tokens',
            remediation_label: 'Remediation',
            no_gaps: 'No pre-flight gaps detected. The filing is ready to assemble.',
            override_label: 'Override block verdict — assembly will be audit-stamped.',
            accept_label: 'Accept report',
            reject_label: 'Reject report',
            assembled_label: 'Submission package assembled',
            package_id_label: 'Package id',
            package_filename_label: 'Filename',
            package_hash_label: 'Package hash',
            evidence_count_label: '{count} evidence items bundled',
            error_generic: 'Could not run pre-flight or assemble the package. Try again or check the filing status.',
        },
        errors: {
            fetch: 'Failed to load assessments.',
            save: 'Failed to save assessment.',
            fetch_gap_report_failed: 'Failed to fetch gap report.',
            fetch_transfers_failed: 'Failed to fetch transfers.',
            save_transfer_failed: 'Failed to save transfer.',
            submit_review_failed: 'Failed to submit for review.',
            process_decision_failed: 'Failed to process decision.',
            deprecate_failed: 'Failed to deprecate.',
            evaluate_risk_failed: 'Failed to evaluate risk.',
            fetch_safeguards_failed: 'Failed to fetch safeguards.',
            save_safeguard_failed: 'Failed to save safeguard.',
            update_safeguard_status_failed: 'Failed to update safeguard status.',
        },
        nav: {
            dpias: 'DPIAs',
            transfers: 'Transfer Assessments',
            dashboard: 'Dashboard',
            gap_report: 'Gap Report',
        },
        risk_level_critical: 'Critical Risk',
        risk_level_high: 'High Risk',
        risk_level_medium: 'Medium Risk',
        risk_level_low: 'Low Risk',
        severity_critical: 'Critical severity',
        severity_high: 'High severity',
        severity_medium: 'Medium severity',
        severity_low: 'Low severity',
        overdue: 'Overdue',
        framework_filter_label: 'Framework',
        aria_filter_by_framework: 'Filter by framework',
        dashboard: {
            title: 'Assessment Dashboard',
            page_title: 'Assessments Dashboard',
            announce_loaded: 'Assessment dashboard loaded',
            announce_chart_view: 'Showing chart view',
            announce_table_view: 'Showing data table view',
            aria_metrics_table: 'Assessment dashboard metrics',
            metric_col: 'Metric',
            value_col: 'Value',
            dpia_prefix: 'DPIA',
            transfer_prefix: 'Transfer',
            risk_prefix: 'Risk',
            aria_dpias_by_status: 'DPIAs by status',
            aria_transfers_by_status: 'Transfers by status',
            aria_top_risks: 'Top risks',
            aria_top_risks_table: 'Top risks across DPIAs',
            overdue_dpias_attention: '{count} overdue DPIA assessments requiring attention',
            no_overdue_dpias: 'No overdue DPIA assessments',
            overdue_transfers_attention: '{count} overdue transfer assessments requiring attention',
            no_overdue_transfers: 'No overdue transfer assessments',
            dpias_overview: 'DPIAs Overview',
            transfers_overview: 'Transfer Assessments Overview',
            overdue_items: 'Overdue Items',
            overdue_dpias: 'Overdue DPIAs',
            overdue_transfers: 'Overdue Transfers',
            count: 'Count',
            no_data: 'No data available.',
            no_risks: 'No risks found.',
            no_dashboard_data: 'No dashboard data available.',
            fetch_error: 'Failed to fetch dashboard data. Please try again.',
        },
        dpias: {
            page_title: 'DPIAs',
            announce_submitted_for_review: 'DPIA submitted for review',
            announce_deprecated: 'DPIA deprecated',
            announce_risks_loaded: '{count} risk(s) loaded',
            announce_mitigations_loaded: '{count} mitigation(s) loaded',
            announce_risk_deleted: 'Risk deleted',
            announce_mitigation_status_updated: 'Mitigation status updated',
            aria_filter: 'Filter DPIAs',
            aria_filter_by_status: 'Filter by status',
            aria_filter_by_lead: 'Filter by assessment lead',
            aria_table: 'Data Protection Impact Assessments',
            announce_found: '{count} DPIA(s) found',
            announce_decision: 'DPIA {decision}',
            mitigation_start: 'Start',
            mitigation_mark_implemented: 'Mark Implemented',
            mitigation_verify: 'Verify',
            title: 'Data Protection Impact Assessments',
            new: 'New DPIA',
            edit: 'Edit DPIA',
            dpia_title: 'Title',
            activity: 'Activity',
            risk_score: 'Risk Score',
            risks: 'Risks',
            mitigations: 'Mitigations',
            add_risk: 'Add Risk',
            add_mitigation: 'Add Mitigation',
            necessity: 'Necessity / Proportionality',
            likelihood: 'Likelihood',
            impact: 'Impact',
            severity_label: 'Severity',
            purpose: 'Purpose',
            consultation_notes: 'Consultation Notes',
            dpo_opinion: 'DPO Opinion',
            processing_activity_ids: 'Processing Activity IDs (comma-separated)',
            system_ids: 'System IDs (comma-separated)',
            vendor_ids: 'Vendor IDs (comma-separated)',
            data_types: 'Data Types (comma-separated)',
            data_types_short: 'Data Types',
            data_subjects: 'Data Subjects (comma-separated)',
            assessment_lead_id: 'Assessment Lead ID',
            assessment_lead: 'Assessment Lead',
            dpo_id: 'DPO ID',
            reviewer_ids: 'Reviewer IDs (comma-separated)',
            approval_mode: 'Approval Mode',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Require DPO Sign-off',
            review_date: 'Review Date',
            submit_for_review: 'Submit for Review',
            approve_reject: 'Approve/Reject',
            deprecate: 'Deprecate',
            deprecated: 'Deprecated',
            hide_risks: 'Hide Risks',
            hide_mitigations: 'Hide Mitigations',
            comments: 'Comments',
            submit_decision: 'Submit Decision',
            category_label: 'Category',
            category_data_breach: 'Data Breach',
            category_unauthorized_access: 'Unauthorized Access',
            category_function_creep: 'Function Creep',
            category_re_identification: 'Re-identification',
            category_discrimination: 'Discrimination',
            category_surveillance: 'Surveillance',
            category_other: 'Other',
            affected_rights: 'Affected Rights (comma-separated)',
            no_risks: 'No risks found.',
            no_mitigations: 'No mitigations found.',
            no_dpias: 'No DPIAs found.',
            mitigation_type: 'Type',
            mitigation_technical: 'Technical',
            mitigation_organizational: 'Organizational',
            mitigation_contractual: 'Contractual',
            risk_ids: 'Risk IDs (comma-separated)',
            responsible_party: 'Responsible Party',
            responsible_party_placeholder: 'Or enter name manually',
            affected_rights_placeholder: 'Selected rights appear here',
            affected_rights_freetext_placeholder: 'Enter affected rights (comma-separated)',
            responsible: 'Responsible',
            target_date: 'Target Date',
            created: 'Created',
            fetch_error: 'Failed to fetch DPIAs. Please try again.',
            save_error: 'Failed to save DPIA. Please try again.',
            submit_review_error: 'Failed to submit DPIA for review. Please try again.',
            decision_error: 'Failed to process decision. Please try again.',
            deprecate_error: 'Failed to deprecate DPIA. Please try again.',
            fetch_risks_error: 'Failed to fetch risks. Please try again.',
            save_risk_error: 'Failed to save risk. Please try again.',
            delete_risk_error: 'Failed to delete risk. Please try again.',
            fetch_mitigations_error: 'Failed to fetch mitigations. Please try again.',
            save_mitigation_error: 'Failed to save mitigation. Please try again.',
            update_mitigation_status_error: 'Failed to update mitigation status. Please try again.',
            link_form_template: 'Link Regulatory Form Template',
            link_form_template_placeholder: 'Select a DPIA form template (optional)',
            link_form_template_none: 'None',
            linked_form: 'Linked Form',
            linked_form_creating: 'Creating linked form...',
            linked_form_error: 'Failed to create linked form instance. The DPIA was created without a linked form.',
            view_linked_form: 'View Form',
            announce_created_with_form: 'DPIA created with linked form instance',
            creation_mode: 'DPIA Creation Mode',
            mode_standard: 'Standard DPIA',
            mode_template: 'Use Regulatory Form Template',
            select_form_template: 'Select Form Template',
            create_and_fill_form: 'Create & Fill Form',
            template_sections_title: 'Template Sections',
            field_singular: 'field',
            field_plural: 'fields',
            no_sections: 'No sections found in this template.',
            template_mode_hint: 'After creation, you will be redirected to fill out the full regulatory form.',
            linked_dossier_filings: 'Linked Dossier Filings',
            no_linked_dossier_filings: 'No dossier filings linked to this DPIA.',
            view_dossier_filing: 'View Filing',
            hide_dossier_filings: 'Hide Dossier Filings',
            // Sprint 41.3.0 F5 — supporting evidence
            supporting_evidence: 'Supporting Evidence',
            hide_supporting_evidence: 'Hide Evidence',
            no_supporting_evidence: 'No supporting evidence attached.',
            upload_supporting_evidence: 'Upload supporting evidence',
            view_form_responses: 'View Form Responses',
            regulatory_framework: 'Regulatory Framework',
            all_frameworks: 'All Frameworks',
            no_templates_for_market: 'No templates available for the selected market. Please select a different market or create a template.',
        },
        transfers: {
            page_title: 'Data Transfers',
            announce_found: '{count} transfer assessment(s) found',
            announce_submitted_for_review: 'Transfer submitted for review',
            announce_deprecated: 'Transfer deprecated',
            announce_decision: 'Transfer {decision}',
            announce_risk_eval_completed: 'Risk evaluation completed',
            announce_safeguard_status_updated: 'Safeguard status updated',
            announce_safeguards_loaded: '{count} safeguard(s) loaded',
            aria_filter: 'Filter transfer assessments',
            aria_filter_by_status: 'Filter by status',
            aria_filter_by_mechanism: 'Filter by transfer mechanism',
            aria_table: 'Data Transfer Assessments',
            title: 'Cross-Border Transfer Assessments',
            new: 'New Transfer',
            edit: 'Edit Transfer',
            create_success: 'Transfer assessment created.',
            update_success: 'Transfer assessment updated.',
            name: 'Name',
            from_country: 'From Country',
            to_country: 'To Country',
            safeguards: 'Safeguards',
            risk_level: 'Risk Level',
            assessment_notes: 'Assessment Notes',
            mechanism: 'Mechanism',
            transfer_mechanism: 'Transfer Mechanism',
            transfer_mechanism_detail: 'Transfer Mechanism Detail',
            all_mechanisms: 'All Mechanisms',
            mechanism_adequacy_decision: 'Adequacy Decision',
            mechanism_sccs: 'SCCs',
            mechanism_bcrs: 'BCRs',
            mechanism_consent: 'Consent',
            mechanism_derogation: 'Derogation',
            mechanism_other: 'Other',
            data_types: 'Data Types (comma-separated)',
            data_subjects: 'Data Subjects (comma-separated)',
            purpose: 'Purpose',
            recipient_name: 'Recipient Name',
            recipient_details: 'Recipient Details',
            recipient_details_placeholder: 'Recipient role, location, systems, and onward-transfer details',
            vendor_id: 'Vendor ID',
            processing_activity_ids: 'Processing Activity IDs (comma-separated)',
            reviewer_ids: 'Reviewer IDs (comma-separated)',
            approval_mode: 'Approval Mode',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Require DPO Sign-off',
            review_date: 'Review Date',
            created: 'Created',
            deprecated: 'Deprecated',
            submit_for_review: 'Submit for Review',
            approve_reject: 'Approve/Reject',
            deprecate: 'Deprecate',
            risk_eval: 'Risk Eval',
            hide_safeguards: 'Hide Safeguards',
            // Sprint 41.3.0 F6 — mechanism proof
            mechanism_proof: 'Mechanism Proof',
            hide_mechanism_proof: 'Hide Mechanism Proof',
            mechanism_proof_hint: 'Upload the signed transfer mechanism (SCCs, BCRs, adequacy decision, etc.) proving this transfer assessment.',
            no_mechanism_proof: 'No mechanism proof attached.',
            upload_mechanism_proof: 'Upload mechanism proof',
            comments: 'Comments',
            submit_decision: 'Submit Decision',
            data_sensitivity: 'Data Sensitivity (1-5)',
            country_risk: 'Country Risk (1-5)',
            safeguard_adequacy: 'Safeguard Adequacy (1-5)',
            volume_factor: 'Volume Factor (1-5)',
            evaluate: 'Evaluate',
            add_safeguard: 'Add Safeguard',
            responsible_party: 'Responsible Party',
            responsible_party_placeholder: 'Or enter name manually',
            responsible: 'Responsible',
            implementation_date: 'Implementation Date',
            implementation: 'Implementation',
            expiry_date: 'Expiry Date',
            expiry: 'Expiry',
            expire: 'Expire',
            no_safeguards: 'No safeguards found.',
            no_transfers: 'No transfer assessments found.',
            fetch_error: 'Failed to fetch transfer assessments. Please try again.',
            save_error: 'Failed to save transfer assessment. Please try again.',
            submit_review_error: 'Failed to submit for review. Please try again.',
            decision_error: 'Failed to process decision. Please try again.',
            deprecate_error: 'Failed to deprecate transfer. Please try again.',
            risk_eval_error: 'Failed to evaluate risk. Please try again.',
            fetch_safeguards_error: 'Failed to fetch safeguards. Please try again.',
            save_safeguard_error: 'Failed to save safeguard. Please try again.',
            update_safeguard_status_error: 'Failed to update safeguard status. Please try again.',
            safeguard_type_encryption_in_transit: 'Encryption in Transit',
            safeguard_type_encryption_at_rest: 'Encryption at Rest',
            safeguard_type_access_controls: 'Access Controls',
            safeguard_type_contractual_clauses: 'Contractual Clauses',
            safeguard_type_data_minimization: 'Data Minimization',
            safeguard_type_pseudonymization: 'Pseudonymization',
            safeguard_type_audit_rights: 'Audit Rights',
            safeguard_type_breach_notification: 'Breach Notification',
            safeguard_type_data_localization: 'Data Localization',
            safeguard_type_other: 'Other',
            activate: 'Activate',
            all_statuses: 'All Statuses',
            any: 'Any',
            mechanism_detail: 'Mechanism Detail',
            mechanisms: {
                adequacy_decision: 'Adequacy Decision',
                sccs: 'SCCs',
                bcr: 'BCR',
                bcrs: 'BCRs',
                derogation: 'Derogation',
                consent: 'Consent',
                other: 'Other',
            },
            safeguard_types: {
                encryption_in_transit: 'Encryption in Transit',
                encryption_at_rest: 'Encryption at Rest',
                access_controls: 'Access Controls',
                contractual_clauses: 'Contractual Clauses',
                data_minimization: 'Data Minimization',
                pseudonymization: 'Pseudonymization',
                audit_rights: 'Audit Rights',
                breach_notification: 'Breach Notification',
                data_localization: 'Data Localization',
                other: 'Other',
            },
        },
        gap_report: {
            page_title: 'Assessment Gap Report',
            announce_loaded: 'Gap report loaded. {count} total gap(s) identified',
            aria_activities_without_dpia: 'Activities without DPIA assessment',
            // Sprint 44.7.0 — unclassified DPIA requirement section
            aria_activities_unclassified: 'Activities with unclassified DPIA requirement',
            activities_unclassified_section_title: 'Activities with unclassified DPIA requirement',
            activities_unclassified_description: 'These activities have not been reviewed to determine whether a DPIA is legally required. Classify each to clear the signal.',
            activities_unclassified_empty: 'All active activities have been classified.',
            aria_unmitigated_risks: 'Unmitigated high and critical risks',
            aria_transfers_without_safeguards: 'Transfers without adequate safeguards',
            aria_expired_assessments: 'Expired assessments requiring renewal',
            title: 'Assessment Gap Report',
            no_dpia: 'Activities Without DPIA',
            no_assessment: 'Transfers Without Assessment',
            unmitigated: 'DPIAs with Unmitigated Risks',
            activity_id: 'Activity ID',
            dpia_id: 'DPIA ID',
            assessment_id: 'Assessment ID',
            expired_assessments: 'Expired Assessments',
            no_gaps_activities: 'No gaps found. All activities have DPIAs.',
            no_unmitigated_risks: 'No unmitigated high/critical risks found.',
            no_gaps_safeguards: 'No gaps found. All transfers have adequate safeguards.',
            no_expired: 'No expired assessments found.',
            no_report_data: 'No gap report data available.',
            fetch_error: 'Failed to fetch gap report. Please try again.',
            no_expired_assessments: 'No expired assessments.',
            no_gaps_dpia: 'No DPIA gaps found.',
            no_data: 'No data available.',
        },
        dossier_filings: {
            page_title_detail: 'Dossier Filing Detail',
            page_title_list: 'Dossier Filing Cases',
            announce_filings_found: '{count} filing(s) found',
            announce_results: '{count} results',
            aria_filter_by_status: 'Filter by status',
            title: 'Dossier Filing Cases',
            nav_label: 'Dossier Filings',
            create: 'Create Dossier Filing',
            form_type: 'Form Type',
            phu_luc_i_label: 'Mẫu số 02a (Standard DPIA, Decree 356)',
            phu_luc_ii_label: 'Mẫu số 02b (Cross-border / High-risk DPIA, Decree 356)',
            dpia_reference: 'DPIA Reference',
            submitted_date: 'Submitted Date',
            outcome: 'Outcome',
            parent_filing: 'Parent',
            no_filings: 'No dossier filings found.',
            fetch_error: 'Failed to fetch dossier filings. Please try again.',
            save_error: 'Failed to create dossier filing. Please try again.',
            created: 'Dossier filing created.',
            loaded: 'Dossier filing loaded.',
            not_found: 'Dossier filing not found.',
            back_to_list: 'Back to list',
            section_assignments: 'Section Assignments',
            assign_sections: 'Assign Sections',
            section_id: 'Section',
            department: 'Department',
            assignee: 'Assignee',
            deadline: 'Deadline',
            no_assignments: 'No section assignments yet.',
            sections_assigned_msg: 'Sections assigned.',
            assign_error: 'Failed to assign sections. Please try again.',
            lifecycle: 'Filing Lifecycle',
            submission: 'Submission Details',
            submission_channel: 'Submission Channel',
            mps_portal: 'MPS Portal',
            in_person: 'In Person',
            postal: 'Postal',
            confirmation_number: 'Confirmation Number',
            submit_for_review: 'Submit for Review',
            submitted_for_review: 'Submitted for internal review.',
            submit_review_error: 'Failed to submit for review. Please try again.',
            legal_signoff: 'Legal Signoff',
            legal_signoff_done: 'Legal signoff completed.',
            legal_signoff_error: 'Failed to complete legal signoff. Please try again.',
            approve: 'Approve',
            approved_msg: 'Filing approved.',
            approve_error: 'Failed to approve filing. Please try again.',
            // Sprint 44.4.0 — submission artifact materialisation status + retry
            materialisation_status_column: 'Submission artifact',
            materialisation_na: 'Not applicable',
            materialisation_pending: 'Pending',
            materialisation_success: 'Materialised',
            materialisation_failed: 'Failed',
            materialisation_banner_title: 'Submission artifact could not be created',
            materialisation_banner_body: 'The filing was approved, but the underlying dossier record could not be created. Cover-letter and submission exports are unavailable until you retry.',
            materialisation_retry_button: 'Retry',
            materialisation_retry_count: 'Previous retry attempts:',
            materialisation_retry_success: 'Submission artifact materialised successfully.',
            materialisation_retry_announce: 'Retry succeeded',
            filter_materialisation_failed: 'Show only materialisation failures',
            column_title: 'Title',
            column_form_type: 'Form',
            submit_to_mps: 'Submit to MPS',
            submitted_to_mps_msg: 'Submitted to MPS.',
            submit_mps_error: 'Failed to submit to MPS. Please try again.',
            supplement_request: 'Supplement Request',
            supplement_requests: 'Supplement Requests',
            supplement_response: 'Supplement Response',
            record_supplement: 'Record Supplement Request',
            supplement_recorded: 'Supplement request recorded.',
            supplement_error: 'Failed to record supplement request. Please try again.',
            submit_supplement: 'Submit Supplement Response',
            supplement_submitted_msg: 'Supplement response submitted.',
            supplement_submit_error: 'Failed to submit supplement response. Please try again.',
            response_status: 'Response Status',
            response_notes: 'Response Notes',
            resubmit: 'Resubmit',
            resubmitted_msg: 'Dossier resubmitted.',
            resubmit_error: 'Failed to resubmit dossier. Please try again.',
            record_outcome: 'Record Outcome',
            outcome_recorded: 'Outcome recorded.',
            outcome_error: 'Failed to record outcome. Please try again.',
            accepted: 'Accepted',
            rejected: 'Rejected',
            close: 'Close',
            closed_msg: 'Filing closed.',
            close_error: 'Failed to close filing. Please try again.',
            change_filing: 'Create Change Filing',
            change_filing_created: 'Change filing created.',
            change_filing_error: 'Failed to create change filing. Please try again.',
            change_reason: 'Reason for Change',
            filing_lineage: 'Filing Lineage',
            timers: 'Active Timers',
            timer_mps_review: 'MPS Review',
            timer_supplement: 'Supplement Deadline',
            timer_resubmission: 'Resubmission Deadline',
            timer_department: 'Department Deadline',
            days_remaining: 'days remaining',
            days_overdue: 'days overdue',
            cover_letter: 'Cover Letter',
            checklist: 'Checklist',
            evidence_index: 'Evidence Index',
            status_draft: 'Draft',
            status_sections_assigned: 'Sections Assigned',
            status_internal_review: 'Internal Review',
            status_legal_signoff: 'Legal Signoff',
            status_approved: 'Approved',
            status_submitted_to_mps: 'Submitted to MPS',
            status_supplement_requested: 'Supplement Requested',
            status_supplement_submitted: 'Supplement Submitted',
            status_resubmitted: 'Resubmitted',
            status_accepted: 'Accepted',
            status_rejected: 'Rejected',
            status_closed: 'Closed',
            linked_dpia: 'Linked DPIA',
            linked_dpia_none: 'No DPIA linked to this filing.',
            view_linked_dpia: 'View DPIA',
            linked_dpia_label: 'This dossier filing is linked to the following DPIA:',
        },
        // Sprint 44.4.0 — submission artifact health (admin surface)
        materialisation_health: {
            nav_label: 'Submission artifact health',
            page_title: 'Submission Artifact Health',
            page_description: 'Detect and remediate dossier materialisation issues. Lists are scoped to your organization.',
            failed_title: 'Failed materialisations',
            failed_empty: 'No failed materialisations — good.',
            pending_title: 'Approved but not materialised',
            pending_empty: 'All approved filings have a backing dossier.',
            orphan_title: 'Orphan dossiers',
            orphan_description: 'Dossiers created by materialisation that are no longer referenced by any filing — typically the loser of a concurrent race. Listed for investigation; cleanup is manual.',
            orphan_empty: 'No orphan dossiers detected.',
            materialise_button: 'Materialise now',
            column_failure_reason: 'Failure reason',
            column_failed_at: 'Failed at',
            column_retries: 'Retries',
            column_approved_at: 'Approved at',
            column_dossier_id: 'Dossier ID',
            column_source_filing_id: 'Source filing',
            column_dossier_type: 'Type',
            column_created_at: 'Created at',
        },
    },
    // ---------- Classification module (Sprint 1.6.0) ----------
    classification: {
        access_denied_title: 'Access Denied',
        access_denied: 'You do not have the required permissions to access the Classification module. Contact your administrator.',
        nav: {
            taxonomies: 'Taxonomies',
            labeling: 'Labeling',
            lifecycle: 'Lifecycle',
            gaps: 'Gap Report',
            exports: 'Exports',
        },
        exports: {
            title: 'Classification Exports',
            csv_section: 'CSV Exports',
            pdf_section: 'PDF Reports',
            inventory: 'Inventory',
            classification_register: 'Classification Register',
            raci: 'RACI Matrix',
            exceptions: 'Exceptions',
            evidence_pack: 'Evidence Pack',
            delta_report: 'Delta Report',
            executive_summary: 'Executive Summary',
            exporting: 'Exporting…',
            export_success: 'Export completed successfully.',
            export_error: 'Export failed. Please try again.',
            csv_download: 'CSV download ready.',
            pdf_download: 'PDF generation started.',
            no_data: 'No data available for export.',
        },
        delta: {
            title: 'Delta Drill-Down',
            added: 'Added',
            removed: 'Removed',
            changed: 'Changed',
            added_count: 'Added ({count})',
            removed_count: 'Removed ({count})',
            changed_count: 'Changed ({count})',
            object_id: 'Object ID',
            object_type: 'Object Type',
            taxonomy: 'Taxonomy',
            level: 'Level',
            previous: 'Previous',
            current: 'Current',
            no_records: 'No records in this section.',
            empty_section: 'This section is empty.',
        },
        schedule: {
            title: 'Schedule Discovery Cycle',
            enabled: 'Enabled',
            interval_days: 'Interval (days)',
            interval_label: 'Run every N days',
            manual_only: 'Manual only',
            every_n_days: 'Every {n} days',
            save: 'Save Schedule',
            cancel: 'Cancel',
            schedule_updated: 'Schedule updated successfully.',
        },
        taxonomies: {
            title: 'Classification Taxonomies',
            new: 'New Taxonomy',
            edit: 'Edit Taxonomy',
            name: 'Name',
            levels: 'Classification Levels',
            seed_defaults: 'Seed Defaults',
            scope: 'Scope',
            scope_organization: 'Organization',
            scope_module_specific: 'Module Specific',
            deprecate: 'Deprecate',
            hide_levels: 'Hide Levels',
            versions: 'Versions',
            hide_versions: 'Hide Versions',
            add_level: 'Add Level',
            new_level: 'New Level',
            edit_level: 'Edit Level',
            code: 'Code',
            label: 'Label',
            color: 'Color',
            ordinal: 'Ordinal',
            handling_requirements: 'Handling Requirements',
            version_history: 'Version History',
            changes: 'Changes',
            failed_fetch: 'Failed to fetch taxonomies. Please try again.',
            failed_save: 'Failed to save taxonomy. Please try again.',
            failed_activate: 'Failed to activate taxonomy. Please try again.',
            failed_deprecate: 'Failed to deprecate taxonomy. Please try again.',
            failed_seed: 'Failed to seed default taxonomies. Please try again.',
            failed_fetch_levels: 'Failed to fetch taxonomy levels. Please try again.',
            failed_save_level: 'Failed to save taxonomy level. Please try again.',
            failed_delete_level: 'Failed to delete taxonomy level. Please try again.',
            failed_fetch_versions: 'Failed to fetch taxonomy versions. Please try again.',
            loaded_count: 'Loaded {count} taxonomies',
            defaults_seeded: 'Default taxonomies seeded.',
            no_levels: 'No classification levels defined — add levels to define your classification hierarchy.',
            no_versions: 'No version history yet — versions are created when an active taxonomy is updated.',
        },
        labeling: {
            title: 'Data Labeling',
            new: 'New Label',
            edit: 'Edit Label',
            entity_type: 'Entity Type',
            entity_id: 'Entity ID',
            taxonomy: 'Taxonomy',
            classification_level: 'Classification Level',
            applied_by: 'Applied By',
            applied_at: 'Applied At',
            bulk_apply: 'Bulk Apply',
            bulk_remove: 'Bulk Remove',
            all_taxonomies: 'All Taxonomies',
            system: 'System',
            dataset: 'Dataset',
            level_code_placeholder: 'Level code...',
            select_taxonomy: 'Select taxonomy...',
            select_level: 'Select level...',
            rationale: 'Rationale',
            review_date: 'Review Date',
            review: 'Review',
            selected: 'selected',
            select_all_labels: 'Select all labels',
            loaded_count: 'Loaded {count} labels',
            hide_stale_labels: 'Hide Stale Labels',
            show_conflicts: 'Show Conflicts',
            hide_conflicts: 'Hide Conflicts',
            label_conflicts: 'Label Conflicts',
            conflict_reason: 'Conflict Reason',
            labels: 'Labels',
            failed_fetch: 'Failed to fetch labels. Please try again.',
            failed_fetch_taxonomies: 'Failed to fetch taxonomies. Please try again.',
            failed_fetch_levels: 'Failed to fetch taxonomy levels. Please try again.',
            failed_save: 'Failed to save label. Please try again.',
            failed_review: 'Failed to review label. Please try again.',
            failed_bulk: 'Failed to bulk label. Please try again.',
            failed_stale: 'Failed to fetch stale labels. Please try again.',
            failed_conflicts: 'Failed to fetch label conflicts. Please try again.',
            review_permission_denied: 'You do not have permission to review this label.',
            review_not_found: 'Label not found — it may have been deleted.',
            approve_selected: 'Approve Selected',
            reject_selected: 'Reject Selected',
            approved: 'Approved',
            rejected: 'Rejected',
            labels_count: 'labels',
            failed_count: 'failed',
            confirm_approve: 'Confirm Approve',
            confirm_reject: 'Confirm Reject',
            approve_n_labels: 'Approve {count} labels?',
            reject_n_labels: 'Reject {count} labels?',
            submit_for_review: 'Submit for Review',
            approve: 'Approve',
            reject: 'Reject',
            parent_system: 'Parent System',
            parent_system_placeholder: 'Select parent system...',
            parent_system_help: 'Used for conflict detection — select the system that owns this dataset.',
            dataset_level: 'Dataset Level',
            parent_level: 'Parent Level',
            bulk_created: 'Created {created} label(s)',
            bulk_skipped: '{skipped} skipped (already labelled)',
            bulk_all_skipped: 'All {skipped} skipped — entities already have a label for this taxonomy',
            ids_hint: 'Enter one or more IDs separated by commas, e.g. system-1, system-2',
            delete_confirm: 'Delete label for "{id}"?',
        },
        lifecycle: {
            title: 'Data Lifecycle Monitoring',
            new: 'New Entry',
            current_stage: 'Current Stage',
            last_transition: 'Last Transition',
            transition: 'Transition',
            view_history: 'View History',
            stages: {
                creation: 'Creation',
                collection: 'Collection',
                processing: 'Processing',
                storage: 'Storage',
                retention: 'Retention',
                disposal: 'Disposal',
                archived: 'Archived',
                active: 'Active',
                pending_deletion: 'Pending Deletion',
                deleted: 'Deleted',
                on_hold: 'On Hold',
            },
            all_stages: 'All Stages',
            object_id: 'Object ID',
            object_type: 'Object Type',
            policy_reference: 'Policy Reference',
            retention_period_days: 'Retention Period (days)',
            retention_basis: 'Retention Basis',
            deletion_method: 'Deletion Method',
            deletion_secure_erase: 'Secure Erase',
            deletion_anonymization: 'Anonymization',
            deletion_physical_destruction: 'Physical Destruction',
            deletion_other: 'Other',
            responsible_party: 'Responsible Party',
            responsible_user_id: 'Responsible User ID',
            retention_expiry: 'Retention Expiry',
            hold_reason: 'Hold Reason',
            hold: 'Hold',
            release: 'Release',
            hide_events: 'Hide Events',
            events_timeline: 'Events Timeline',
            add_event: 'Add Event',
            event_type: 'Event Type',
            create_event: 'Create Event',
            from_stage: 'From Stage',
            to_stage: 'To Stage',
            actor: 'Actor',
            reason_placeholder: 'Reason...',
            go: 'Go',
            show_expired_retentions: 'Show Expired Retentions',
            hide_expired_retentions: 'Hide Expired Retentions',
            expired_retentions_alert: 'Expired Retentions Alert',
            failed_fetch: 'Failed to fetch lifecycle records. Please try again.',
            failed_save: 'Failed to save lifecycle record. Please try again.',
            failed_transition: 'Failed to transition lifecycle stage. Please try again.',
            failed_hold: 'Failed to place hold. Please try again.',
            failed_release: 'Failed to release hold. Please try again.',
            failed_fetch_events: 'Failed to fetch lifecycle events. Please try again.',
            failed_create_event: 'Failed to create lifecycle event. Please try again.',
            failed_expired: 'Failed to fetch expired retentions. Please try again.',
            hold_reason_prompt: 'Enter hold reason:',
            loaded_count: 'Loaded {count} lifecycle records',
            stage_transition_for: 'Stage transition for {id}',
            event_created: 'Created',
            event_updated: 'Updated',
            event_approved: 'Approved',
            event_deprecated: 'Deprecated',
            event_proposed: 'Proposed',
            event_reviewed: 'Reviewed',
            event_archived: 'Archived',
            event_restored: 'Restored',
        },
        gap_report: {
            title: 'Classification Gap Report',
            unlabeled: 'Unlabeled Data',
            missing_lifecycle: 'Missing Lifecycle',
            stale_labels: 'Stale Labels',
            run_detection: 'Run Gap Detection',
            detecting: 'Detecting...',
            detection_complete: 'Gap detection complete. {count} gap(s) detected.',
            detection_failed: 'Gap detection failed.',
            by_severity: 'By Severity',
            by_gap_type: 'By Gap Type',
            all_gap_types: 'All Gap Types',
            conflicting_labels: 'Conflicting Labels',
            expired_retention: 'Expired Retention',
            missing_handling: 'Missing Handling',
            info: 'Info',
            gap_type: 'Gap Type',
            object_id: 'Object ID',
            object_type: 'Object Type',
            detected_at: 'Detected At',
            open: 'Open',
            acknowledged: 'Acknowledged',
            remediated: 'Remediated',
            accepted: 'Accepted',
            acknowledge: 'Acknowledge',
            remediate: 'Remediate',
            accept_risk: 'Accept Risk',
            accept_risk_prompt: 'Enter risk acceptance justification:',
            failed_fetch: 'Failed to fetch gaps. Please try again.',
            failed_acknowledge: 'Failed to acknowledge gap. Please try again.',
            failed_remediate: 'Failed to remediate gap. Please try again.',
            failed_accept_risk: 'Failed to accept gap risk. Please try again.',
            failed_detect: 'Failed to run gap detection. Please try again.',
            loaded_count: 'Loaded {count} classification gaps',
            related_object_id: 'Related Object ID',
            related_object_type: 'Related Object Type',
            gap_types: {
                unlabeled_asset: 'Unlabeled Asset',
                missing_lifecycle: 'Missing Lifecycle',
                expired_retention: 'Expired Retention',
                extended_pending_deletion: 'Extended Pending Deletion',
            },
        },
        label_workflow: {
            title: 'Label Workflow',
            proposed: 'Proposed',
            under_review: 'Under Review',
            approved: 'Approved',
            deprecated: 'Deprecated',
            submit_review: 'Submit for Review',
            approve: 'Approve Label',
            deprecate: 'Deprecate',
            bulk_approve: 'Bulk Approve',
            bulk_approve_success: 'Labels approved successfully.',
            submit_success: 'Label submitted for review.',
            approve_success: 'Label approved.',
            deprecate_success: 'Label deprecated.',
        },
        review_tasks: {
            title: 'Review Tasks',
            create: 'New Review Task',
            system: 'System',
            owner: 'Owner',
            description: 'Description',
            resolution: 'Resolution',
            resolve: 'Resolve',
            approve: 'Approve',
            modify: 'Modify',
            reject: 'Reject',
            escalate: 'Escalate',
            escalated: 'Escalated Tasks',
            no_tasks: 'No review tasks.',
            resolve_success: 'Review task resolved.',
            status_pending: 'Pending',
            status_resolved: 'Resolved',
            status_escalated: 'Escalated',
            notes: 'Notes',
            create_success: 'Review task created.',
            no_tasks_detail: 'No review tasks yet — create a task to assign label reviews to system owners.',
            label_id: 'Label ID',
            system_id: 'System ID',
            system_name: 'System Name',
            owner_id: 'Owner ID',
            resolution_col: 'Resolution',
            notes_col: 'Notes',
        },
        exceptions: {
            title: 'Exception Queue',
            create: 'New Exception',
            severity: 'Severity',
            system: 'System',
            owner: 'Owner',
            resolve: 'Resolve',
            no_exceptions: 'No exceptions found.',
            resolve_success: 'Exception resolved.',
            dashboard_open: 'Open Exceptions',
            dashboard_resolved: 'Resolved Exceptions',
            dashboard_total: 'Total',
            dashboard_by_severity: 'By Severity',
            status_open: 'Open',
            status_resolved: 'Resolved',
            status_accepted_risk: 'Accepted Risk',
            create_success: 'Exception created.',
            no_exceptions_detail: 'No exceptions yet — exceptions are created from high-severity gaps that require special handling.',
            gap_id: 'Gap ID',
            system_id: 'System ID',
            system_name: 'System Name',
            owner_id: 'Owner ID',
            resolution_notes: 'Resolution Notes',
            resolution_notes_placeholder: 'Describe how this exception was resolved...',
        },
        enterprise_map: {
            title: 'Enterprise Data Map',
            inventory: 'Data Inventory',
            total_systems: 'Total Systems',
            total_labels: 'Total Labels',
            approved_count: 'Approved Labels',
            proposed_count: 'Proposed Labels',
            no_data: 'No classification data available.',
            no_approved_labels: 'No approved labels yet — labels must be reviewed and approved before appearing on the data map.',
            loaded_count: 'Loaded enterprise data map',
        },
        discovery: {
            title: 'Discovery Cycles',
            create: 'New Cycle',
            cycle_name: 'Cycle Name',
            run_delta: 'Run Delta Detection',
            delta_success: 'Delta detection completed.',
            create_success: 'Discovery cycle created.',
            no_cycles: 'No discovery cycles.',
            status_created: 'Created',
            status_scanning: 'Scanning',
            status_review: 'Review',
            status_approved: 'Approved',
            status_published: 'Published',
            status_draft: 'Draft',
            running: 'Running',
            completed: 'Completed',
            cancelled: 'Cancelled',
            delta_result_summary: 'Found {added} added, {removed} removed, {changed} changed labels.',
            delta_results: 'Delta Results',
            view_results: 'View Results',
            scope_type: 'Scope Type',
            scope_type_all: 'All',
            scope_type_systems: 'Systems',
            scope_type_datasets: 'Datasets',
            scope_ids: 'Scope IDs',
            scope_ids_placeholder: 'Enter IDs separated by commas',
            cycle_explanation: 'A discovery cycle scans your data infrastructure to identify, classify, and track data assets. Configure the scope to target specific systems or datasets.',
            empty_state_title: 'No Discovery Cycles Yet',
            empty_state_description: 'Create your first discovery cycle to begin scanning and classifying your data assets.',
            empty_state_cta: 'Create First Cycle',
            validation_name_required: 'Cycle name is required',
            validation_scope_type_required: 'Scope type is required',
            validation_scope_ids_required: 'At least one scope ID is required',
        },
        regulatory: {
            sensitivity_importance_info: 'Data Law (Law 60/2024/QH15, req. 18e4bfd1) requires classification by both sensitivity and importance as separate dimensions. Create one taxonomy for each dimension to satisfy this requirement.',
            handling_rules_info: 'Each classification level should define handling rules per req. b89981ac ("Define handling rules by classification level").',
            template_internal_ids_info: 'All classification templates use internal IDs (e.g. vn_data_law_data_classification_record). No template is presented as an official ministry form.',
            ai_risk_tiers_info: 'AI Law (Law 134/2025/QH15, req. 3a87ad63) defines four AI system risk tiers: minimal, limited, high, and unacceptable. Create an AI Risk taxonomy with these levels.',
            ai_risk_tier_minimal: 'Minimal',
            ai_risk_tier_limited: 'Limited',
            ai_risk_tier_high: 'High',
            ai_risk_tier_unacceptable: 'Unacceptable',
            ai_register_info: 'AI classification records should link to an AI System Register entry (req. 0f4475d3) capturing: system name, owner, purpose, deployment context, risk level, vendor involvement.',
            ai_template_internal_info: 'AI classification templates use internal IDs (e.g. AI-RISK-01). No official AI placeholder IDs (AI-HC-01 through AI-HC-08) are used.',
            ai_audit_trail_info: 'AI classification changes produce a versioned audit trail per UC-VN-06: actor, timestamp, previous value, new value, and rationale.',
            lifecycle_data_d1_info: 'Lifecycle transitions map to Data Law procedure DATA-D1 steps: catalog, classify, define handling rules, capture evidence, schedule reviews.',
            lifecycle_deletion_link_info: 'A classified data asset reaching end-of-life should trigger a deletion schedule record via the Deletion Orchestration module.',
            lifecycle_evidence_info: 'Every lifecycle state change (draft, active, under-review, archived, deleted) captures actor, timestamp, and justification as an audit trail entry.',
            escalation_timers_info: 'UC-VN-10 escalation deadlines: 5 business days for owner review, 48 hours for exception escalation, 72 hours for high-risk/sensitive items.',
            escalation_owner_review: '5 business days',
            escalation_exception: '48 hours',
            escalation_high_risk: '72 hours',
            gap_risk_areas_info: 'Data Law req. f6c3060a ("Data risk assessment") requires gap analysis across 4 risk areas: privacy, cybersecurity, access control, and operational risk.',
            gap_risk_area_privacy: 'Privacy',
            gap_risk_area_cybersecurity: 'Cybersecurity',
            gap_risk_area_access: 'Access Control',
            gap_risk_area_operational: 'Operational',
            gap_framework_filter_info: 'Filter gaps by regulatory framework (VN_DATA_LAW or VN_AI_LAW_2026) to see relevant gaps for each regulatory scope.',
            framework_vn_data_law: 'VN Data Law',
            framework_vn_ai_law: 'VN AI Law 2026',
            enterprise_map_approved_info: 'This data map shows only approved classification records per UC-VN-10 workflow step 10 ("Approved labels become the current enterprise truth"). Draft and proposed records are excluded.',
            exports_bundle_info: 'Export bundles include: classification register, handling rules, ownership/RACI, and evidence pack — matching UC-VN-10 export requirements and DATA-EVD-01 evidence sheet format.',
        },
    },
    // ---------- Monitoring module (Sprint 1.7.0) ----------
    monitoring: {
        no_permission_title: 'Insufficient Permissions',
        no_permission_message: 'You do not have permission to access this monitoring module.',
        errors: {
            fetch: 'Failed to load.',
            save: 'Failed to save.',
            fetch_programs_failed: 'Failed to fetch programs.',
            fetch_kpi_failed: 'Failed to fetch KPI data.',
            create_snapshot_failed: 'Failed to create snapshot.',
            fetch_periods_failed: 'Failed to fetch periods.',
            create_period_failed: 'Failed to create period.',
            auto_generate_failed: 'Failed to auto-generate periods.',
            start_period_failed: 'Failed to start period.',
            close_period_failed: 'Failed to close period.',
            save_program_failed: 'Failed to save program.',
            activate_program_failed: 'Failed to activate program.',
            pause_program_failed: 'Failed to pause program.',
            complete_program_failed: 'Failed to complete program.',
            archive_program_failed: 'Failed to archive program.',
            fetch_ric_templates_failed: 'Failed to fetch RIC templates.',
            fetch_ric_cases_failed: 'Failed to fetch RIC cases.',
            create_ric_case_failed: 'Failed to create RIC case.',
            transition_ric_failed: 'Failed to transition RIC case.',
            add_follow_up_failed: 'Failed to add follow-up.',
            respond_follow_up_failed: 'Failed to respond to follow-up.',
            link_evidence_failed: 'Failed to link evidence.',
            fetch_task_templates_failed: 'Failed to fetch task templates.',
            save_task_template_failed: 'Failed to save task template.',
            delete_task_template_failed: 'Failed to delete task template.',
            delete_framework_link_failed: 'Failed to delete framework link.',
            seed_defaults_failed: 'Failed to seed defaults.',
            fetch_tasks_failed: 'Failed to fetch tasks.',
            create_task_failed: 'Failed to create task.',
            instantiate_tasks_failed: 'Failed to instantiate tasks.',
            start_task_failed: 'Failed to start task.',
            complete_task_failed: 'Failed to complete task.',
            defer_task_failed: 'Failed to defer task.',
            block_task_failed: 'Failed to block task.',
            override_complete_failed: 'Failed to override complete.',
            assign_task_failed: 'Failed to assign task.',
            update_checklist_failed: 'Failed to update checklist.',
        },
        nav: {
            programs: 'Programs',
            periods: 'Periods',
            task_templates: 'Task Templates',
            tasks: 'Tasks',
            kpi: 'KPI Dashboard',
            ric: 'Regulator Inquiries',
            framework_link: 'Framework Monitoring',
            authority_readiness: 'Authority Readiness',
            change_review: 'Change Review',
            raci: 'RACI Matrix',
        },
        programs: {
            page_title: 'Monitoring Programs',
            title: 'Monitoring Programs',
            new: 'New Program',
            edit: 'Edit Program',
            name: 'Name',
            scope: 'Scope',
            frequency: 'Frequency',
            owner: 'Owner',
            activate: 'Activate',
            pause: 'Pause',
            resume: 'Resume',
            complete: 'Complete',
            archive: 'Archive',
            custom_frequency_days: 'Custom Frequency (days)',
            select_user: 'Select user\u2026',
            assign_training: 'Assign Training',
            linked_forms: 'Linked Forms',
            linked_forms_none: 'No linked forms yet',
            loaded_count: 'Loaded {0} programs',
            scope_labels: {
                full: 'Full',
                partial: 'Partial',
                targeted: 'Targeted',
                consent: 'Consent',
                dpia: 'DPIA',
                vendors: 'Vendors',
                classification: 'Classification',
                rights_requests: 'Rights Requests',
                data_mapping: 'Data Mapping',
                transfers: 'Transfers',
                security_monitoring: 'Security Monitoring',
                authority_response: 'Authority Response',
                evidence_preservation: 'Evidence Preservation',
                localization_review: 'Localization Review',
                ai_system_registration: 'AI System Registration',
                ai_pre_deployment: 'AI Pre-Deployment Assessment',
                ai_transparency: 'AI Transparency Check',
                ai_incident_review: 'AI Incident Review',
            },
            framework_cadence_info: 'Cybersecurity Law programs typically use continuous, monthly, or quarterly cadences. AI Law programs typically use annual review cadences for system register updates.',
        },
        periods: {
            page_title: 'Monitoring Periods',
            title: 'Review Periods',
            new: 'New Period',
            label: 'Period Label',
            start_date: 'Start Date',
            end_date: 'End Date',
            reviewers: 'Reviewers',
            auto_generate: 'Auto-Generate Periods',
            start: 'Start',
            close: 'Close',
            change_digest: 'Change Digest',
            no_digest: 'No digest available.',
            reviewers_count: 'Reviewers Count',
            select_program: 'Select Program',
            select_program_prompt: 'Select a program to view periods.',
            hide_digest: 'Hide Digest',
            loaded_count: 'Loaded {0} periods',
            periods_auto_generated: 'Periods auto-generated',
            label_placeholder: 'e.g., 2026-Q1',
        },
        task_templates: {
            page_title: 'Task Templates',
            title: 'Task Templates',
            new: 'New Template',
            edit: 'Edit Template',
            category: 'Category',
            priority: 'Priority',
            checklist_items: 'Checklist Items',
            effort: 'Estimated Effort (hours)',
            pre_seeded: 'Pre-seeded',
            seed_defaults: 'Seed Defaults',
            add_checklist: 'Add Checklist Item',
            guidance: 'Guidance Notes',
            all_categories: 'All Categories',
            loaded_count: 'Loaded {0} task templates',
            defaults_seeded: 'Default task templates seeded',
            checklist_item_label: 'Checklist item {0}',
            remove_checklist_item: 'Remove checklist item {0}',
            category_info: 'Categories cover PDPL compliance (Consent, DPIA, Vendors, etc.), Cybersecurity Law tasks (Security Monitoring, Authority Response, etc.), and AI Law tasks (System Registration, Pre-Deployment Assessment, etc.).',
        },
        tasks: {
            page_title: 'Review Tasks',
            title: 'Review Tasks',
            new: 'New Task',
            assigned_to: 'Assigned To',
            due_date: 'Due Date',
            checklist: 'Checklist',
            start: 'Start',
            complete: 'Complete',
            defer: 'Defer',
            block: 'Block',
            override_complete: 'Override Complete',
            link_evidence: 'Link Evidence',
            // Sprint 41.3.0 F4 — direct file attachments on a monitoring task
            direct_upload_label: 'Upload evidence file',
            instantiate: 'Instantiate from Templates',
            defer_reason: 'Reason for deferral',
            override_justification: 'Justification for override',
            filter_assignee: 'Filter by assignee',
            select_period: 'Select Period',
            select_program: 'Select Program',
            select_program_period_prompt: 'Select a program and period to view tasks.',
            assign_placeholder: 'Assign to...',
            loaded_count: 'Loaded {0} tasks',
            tasks_instantiated: 'Tasks instantiated from templates',
            overdue: 'Overdue',
            checklist_for: 'checklist for',
            manage_checklist: 'Manage Checklist',
            add_checklist_item: 'Add item',
            checklist_item_label: 'Item',
            checklist_item_description: 'Description',
            remove_checklist_item: 'Remove item',
            save_checklist: 'Save Checklist',
            no_checklist_items: 'No checklist items yet.',
            // fix-sprint-20260411-D F6 — confirm dialog before deleting audit evidence
            confirm_delete_attachment: 'Are you sure you want to delete this evidence file? This cannot be undone.',
        },
        kpi: {
            page_title: 'KPI Dashboard',
            title: 'KPI Dashboard',
            kpi_name: 'KPI Name',
            current_value: 'Current Value',
            target: 'Target',
            trends: 'Trends',
            capture_snapshot: 'Capture Snapshot',
            select_program: 'Select Program',
            select_program_prompt: 'Select a program to view KPIs.',
            snapshot: 'Snapshot',
            kpi_labels: {
                on_track: 'On Track',
                at_risk: 'At Risk',
                behind: 'Behind',
                rights_request_sla_rate: 'Rights Request SLA Rate',
                vendor_review_currency: 'Vendor Review Currency',
                dpia_completion_rate: 'DPIA Completion Rate',
                classification_coverage: 'Classification Coverage',
                consent_policy_currency: 'Consent Policy Currency',
                security_event_detection_rate: 'Security Event Detection Rate',
                evidence_preservation_compliance_rate: 'Evidence Preservation Compliance Rate',
                authority_response_timeliness: 'Authority Response Timeliness',
                localization_readiness_score: 'Localization Readiness Score',
                ai_register_completeness: 'AI System Register Completeness',
                pre_deployment_gate_pass_rate: 'Pre-Deployment Gate Pass Rate',
                transparency_disclosure_coverage: 'Transparency Disclosure Coverage',
                ai_incident_readiness_score: 'AI Incident Readiness Score',
            },
            pdpl_kpi_info: 'The predefined KPI codes above (Rights Request SLA Rate, DPIA Completion Rate, Vendor Review Currency, Classification Coverage, Consent Policy Currency) are PDPL-specific metrics. For Cybersecurity Law or AI Law monitoring, use framework-specific KPI codes.',
            cybersec_kpi_info: 'Cybersecurity Law KPIs: Security Event Detection Rate, Evidence Preservation Compliance Rate, Authority Response Timeliness, Localization Readiness Score.',
            ai_law_kpi_info: 'AI Law KPIs: AI System Register Completeness, Pre-Deployment Gate Pass Rate, Transparency Disclosure Coverage, AI Incident Readiness Score.',
            no_data: 'No data available.',
            snapshot_date: 'Snapshot Date',
            loaded_count: 'Loaded {0} KPI snapshots',
            snapshot_captured: 'KPI snapshot captured',
            showing_chart: 'Showing chart view',
            showing_table: 'Showing data table view',
            aria_current_values: 'KPI current values',
            kpi_label: 'KPI',
            actual: 'Actual',
            trend: 'Trend',
            notes: 'Notes',
            trend_up: 'Trending up',
            trend_down: 'Trending down',
            trend_stable: 'Stable',
        },
        ric: {
            page_title: 'RIC Cases',
            title: 'Regulator Inquiry Cases',
            new: 'New RIC Case',
            reference: 'Reference',
            regulator: 'Regulator',
            inquiry_type: 'Inquiry Type',
            deadline: 'Response Deadline',
            lead: 'Lead',
            follow_ups: 'Follow-ups',
            add_follow_up: 'Add Follow-Up',
            respond: 'Respond',
            question: 'Question',
            response: 'Response',
            asked_at: 'Asked At',
            no_follow_ups: 'No follow-ups yet.',
            received_date: 'Received Date',
            responded_at: 'Responded At',
            hide_follow_ups: 'Hide Follow-ups',
            inquiry_types: {
                general: 'General',
                investigation: 'Investigation',
                audit: 'Audit',
                complaint: 'Complaint',
                general_audit: 'General Audit',
                rights_requests_review: 'Rights Requests Review',
                cross_border_transfer_review: 'Cross-Border Transfer Review',
                vendor_governance_review: 'Vendor Governance Review',
                consent_management_review: 'Consent Management Review',
                data_mapping_ropa_review: 'Data Mapping / ROPA Review',
                incident_readiness_review: 'Incident Readiness Review',
                security_access_control_review: 'Security & Access Control Review',
                targeted_processing_review: 'Targeted Processing Review',
                bank_readiness_pack: 'Bank Readiness Pack',
            },
            link_evidence: 'Link Evidence',
            loaded_count: 'Loaded {0} RIC cases',
            to_follow_up: 'to follow-up question',
            overdue: 'Overdue',
            overdue_indicator: 'Response deadline exceeded',
            template_info: 'RIC templates are internal platform templates for structuring inquiry responses. They are not official government-mandated forms.',
            status_labels: {
                received: 'Received',
                in_triage: 'In Triage',
                evidence_gathering: 'Evidence Gathering',
                response_drafting: 'Response Drafting',
                internal_review: 'Internal Review',
                submitted: 'Submitted',
                follow_up: 'Follow Up',
                closed: 'Closed',
            },
        },
        // Sprint 21.3.0: Monitoring Programs Maturity
        framework_link: {
            title: 'Framework-Linked Monitoring',
            link_program: 'Link Program to Framework',
            framework_code: 'Framework Code',
            select_framework: 'Select a framework...',
            select_program: 'Select a program...',
            readiness_score: 'Readiness Score',
            no_links: 'No framework-linked programs found',
            seed_defaults: 'Seed Default Templates',
            program_id: 'Program ID',
            program: 'Program',
        },
        authority_readiness: {
            title: 'Authority Request Readiness',
            create_entry: 'New Readiness Entry',
            select_framework: 'Select a framework...',
            framework_code: 'Framework',
            readiness_status: 'Readiness Status',
            readiness_package: 'Readiness Package',
            run_drill: 'Run Readiness Drill',
            drill_success: 'Readiness drill recorded',
            last_drill: 'Last Drill',
            never_drilled: 'Never',
            no_readiness: 'No authority readiness data found',
            entry_created: 'Readiness entry created',
            ready: 'Ready',
            partial: 'Partial',
            not_ready: 'Not Ready',
            items: 'items',
        },
        change_review: {
            title: 'Regulatory Change Review',
            new_review: 'New Change Review',
            change_id: 'Change ID',
            select_change: 'Select a change...',
            select_framework: 'Select a framework...',
            impact_assessment: 'Impact Assessment',
            decision: 'Decision',
            no_action: 'No Action',
            internal_update: 'Internal Update',
            authority_filing: 'Authority Filing',
            no_reviews: 'No change reviews found',
            auto_cycle: 'Auto-Trigger Review Cycle',
            cadence: 'Review Cadence',
            framework: 'Framework',
        },
        // Sprint 27.7.1: Monitoring Operational Completeness
        export_csv: 'Export CSV',
        exporting: 'Exporting\u2026',
        export_success: 'CSV exported successfully.',
        export_failed: 'CSV export failed.',
        export_attestation_pack: 'Export Attestation Pack',
        staleness: {
            badge_label: '{0} stale items',
            days_since: '{0} days since last update',
            detail_title: 'Stale Items',
            accept_justification: 'Accept with Justification',
            justification_label: 'Justification',
            no_stale: 'No stale items found.',
            threshold_label: 'Staleness Threshold (days)',
            threshold_min: 'Minimum 30 days',
            threshold_max: 'Maximum 730 days',
            threshold_saved: 'Staleness threshold updated.',
        },
        csv_columns: {
            name: 'Name',
            status: 'Status',
            owner: 'Owner',
            created_at: 'Created At',
            updated_at: 'Updated At',
            scope: 'Scope',
            frameworks: 'Frameworks',
            period_label: 'Period Label',
            start_date: 'Start Date',
            end_date: 'End Date',
            task_count: 'Task Count',
            completion_rate: 'Completion Rate',
            title: 'Title',
            assigned_to: 'Assigned To',
            due_date: 'Due Date',
            priority: 'Priority',
            completed_at: 'Completed At',
            kpi_code: 'KPI Code',
            kpi_label: 'KPI Label',
            target: 'Target',
            actual: 'Actual',
            trend: 'Trend',
            reference: 'Reference',
            regulator: 'Regulator',
            inquiry_type: 'Inquiry Type',
            lead: 'Lead',
            received_date: 'Received Date',
            deadline: 'Deadline',
            framework_code: 'Framework Code',
            readiness_status: 'Readiness Status',
            package_count: 'Package Count',
            change_id: 'Change ID',
            impact_assessment: 'Impact Assessment',
            decision: 'Decision',
            program: 'Program',
            readiness_score: 'Readiness Score',
            category: 'Category',
            checklist_count: 'Checklist Count',
            effort_hours: 'Effort (hours)',
            version: 'Version',
            department: 'Department',
            function_area: 'Function Area',
            role: 'Role',
            assignee: 'Assignee',
            frequency: 'Frequency',
        },
        raci: {
            page_title: 'Governance RACI Matrix',
            title: 'Governance RACI Matrix',
            department: 'Department',
            function_area: 'Function Area',
            responsible: 'Responsible',
            accountable: 'Accountable',
            consulted: 'Consulted',
            informed: 'Informed',
            assign_role: 'Assign RACI Role',
            select_program: 'Select Program',
            select_program_prompt: 'Select a program to view RACI matrix.',
            no_assignments: 'No RACI assignments found.',
            role_updated: 'RACI assignment updated.',
            role_labels: {
                R: 'Responsible',
                A: 'Accountable',
                C: 'Consulted',
                I: 'Informed',
            },
            loaded_count: 'Loaded {0} RACI assignments',
            single_accountability_warning: 'Only one Accountable (A) role is allowed per function area. Assigning a new Accountable role will replace the existing one.',
            cybersec_function_areas: 'Cybersecurity Law function areas: Incident Response, Evidence Preservation, Authority Cooperation, Localization Review.',
            ai_law_function_areas: 'AI Law function areas: System Registration, Pre-Deployment Assessment, Transparency, Incident Handling.',
            suggested_function_areas: 'Suggested Function Areas',
        },
        prior_cycle: {
            link_label: 'Link to Prior Cycle',
            select_prior: 'Select prior cycle\u2026',
            linked: 'Year-over-Year',
            no_prior: 'No prior cycles available.',
            linked_success: 'Prior cycle linked.',
        },
        digest_table: {
            module: 'Module',
            entity_type: 'Entity Type',
            change_type: 'Change Type',
            description: 'Description',
            changed_at: 'Changed At',
        },
        view_related_ric: 'View Related RIC Cases',
        linked_program: 'Linked Program',
        nav_raci: 'RACI Matrix',
    },
    // ---------- Incidents module (Sprint 1.8.0) ----------
    incidents: {
        // Sprint 48.1.6 — Forseti authority response draft
        authority_response_draft: {
            draft_label: 'Draft response',
            drafting: 'Drafting…',
            results_heading: 'Drafted answers ({count})',
            tokens_label: '≈ {count} tokens',
            counsel_label: 'Counsel review required:',
            counsel_default: 'At least one answer requires counsel review before sending.',
            requires_counsel_label: 'Requires counsel review.',
            accept_label: 'Accept draft',
            reject_label: 'Reject draft',
            error_generic: 'Could not draft the response. Try again or refine the inquiry text.',
        },
        // Sprint 48.1.5 — Forseti breach notification timeline panel
        breach_timeline: {
            compute_label: 'Compute breach notification timeline',
            computing: 'Computing…',
            results_heading: 'Breach notification deadlines ({count})',
            tokens_label: '≈ {count} tokens',
            handoff_label: 'Forseti suggests:',
            empty_label: 'No applicable frameworks for the affected jurisdictions.',
            mandatory_label: 'Mandatory fields:',
            draft_label: 'Notification draft',
            remaining_label: '{count}h remaining',
            overdue_label: 'OVERDUE by',
            acknowledge_label: 'Acknowledge — tasks created externally',
            acknowledging: 'Acknowledging…',
            error_generic: 'Could not compute the breach timeline. Try again.',
        },
        breach_determination: {
            confirmed_breach: 'Confirmed Breach',
            suspected_breach: 'Suspected Breach',
            not_a_breach: 'Not a Breach',
            pending: 'Pending',
        },
        errors: {
            fetch: 'Failed to load incidents.',
            save: 'Failed to save incident.',
            fetch_failed: 'Failed to fetch incidents.',
            save_failed: 'Failed to save incident.',
            transition_failed: 'Failed to transition incident.',
            close_failed: 'Failed to close incident.',
            fetch_timeline_failed: 'Failed to fetch timeline.',
            fetch_decisions_failed: 'Failed to fetch decisions.',
            triage_failed: 'Failed to update triage.',
            impact_update_failed: 'Failed to update impact assessment.',
            breach_update_failed: 'Failed to update breach assessment.',
            create_timeline_failed: 'Failed to create timeline entry.',
            create_decision_failed: 'Failed to create decision.',
            notification_update_failed: 'Failed to update notification.',
            fetch_tasks_failed: 'Failed to fetch tasks.',
            create_task_failed: 'Failed to create task.',
            update_task_status_failed: 'Failed to update task status.',
            update_checklist_failed: 'Failed to update checklist.',
            assign_task_failed: 'Failed to assign task.',
            escalate_task_failed: 'Failed to escalate task.',
            fetch_templates_failed: 'Failed to fetch templates.',
            permission_denied: 'You do not have permission to access this section. Contact your administrator to request access.',
        },
        harm_likelihood: {
            unlikely: 'Unlikely',
            possible: 'Possible',
            likely: 'Likely',
            very_likely: 'Very Likely',
        },
        reported_via: {
            email: 'Email',
            phone: 'Phone',
            internal_system: 'Internal System',
            third_party: 'Third Party',
            automated_detection: 'Automated Detection',
        },
        task_transition_labels: {
            start: 'Start',
            complete: 'Complete',
            block: 'Block',
            defer: 'Defer',
        },
        transition_labels: {
            start: 'Start',
            complete: 'Complete',
            block: 'Block',
            defer: 'Defer',
        },
        nav: {
            all: 'All Incidents',
            tasks: 'Response Tasks',
            reports: 'Reports',
            breach_notifications: 'Breach Notifications',
            supplements: 'Supplements',
            remediation: 'Remediation',
            authority_requests: 'Authority Requests',
            disclosure_register: 'Disclosure Register',
        },
        list: {
            page_title: 'Incidents',
            title: 'Incidents',
            new: 'New Incident',
            edit: 'Edit Incident',
            incident_title: 'Title',
            severity: 'Severity',
            reported_at: 'Reported At',
            reported_by: 'Reported By',
            assigned_to: 'Assigned To',
            view_detail: 'View Detail',
            reference: 'Reference',
            lead: 'Lead',
            reported_via: 'Reported Via',
            discovery_date: 'Discovery Date',
            hide_details: 'Hide Details',
            close_incident: 'Close Incident',
            close_justification_prompt: 'Please provide a justification for closing this incident.',
            justification: 'Justification',
            lead_id: 'Lead ID',
            contact: 'Contact',
            category: 'Category',
            not_assessed: 'Not assessed',
        },
        detail: {
            page_title: 'Incident Detail',
            title: 'Incident Detail',
            tabs: {
                overview: 'Overview',
                triage: 'Triage',
                impact: 'Impact Assessment',
                breach: 'Breach Assessment',
                timeline: 'Timeline',
                decisions: 'Decisions',
                // Sprint 41.3.0 F3 — evidence attachments tab
                evidence: 'Evidence',
                audit_trail: 'Audit Trail',
            },
            // Sprint 41.3.0 F3 — evidence attachments strings
            evidence_upload_label: 'Attach evidence file',
            evidence_uploaded: 'Evidence uploaded',
            evidence_deleted: 'Evidence deleted',
            evidence_delete_error: 'Failed to delete evidence',
            evidence_uploaded_at: 'Uploaded',
            evidence_empty: 'No evidence files attached to this incident.',
            evidence_size: 'Size',
            triage_notes: 'Triage Notes',
            initial_classification: 'Initial Classification',
            individuals_affected: 'Number of Individuals Affected',
            data_categories: 'Data Categories Involved',
            systems_compromised: 'Systems Compromised',
            geographic_scope: 'Geographic Scope',
            breach_determination: 'Breach Determination',
            notification_required: 'Notification Required',
            notification_deadline: 'Notification Deadline',
            pdpc_reportable: 'PDPC Reportable',
            add_timeline: 'Add Timeline Entry',
            add_decision: 'Add Decision',
            not_found: 'Incident not found.',
            team_members: 'Team Members',
            checklist: 'Checklist',
            completed: 'Completed',
            impact_severity_score: 'Calculated Impact Severity Score',
            records_affected: 'Records Affected',
            potential_consequences: 'Potential Consequences',
            harm_likelihood: 'Harm Likelihood',
            legal_basis: 'Legal Basis',
            jurisdictions: 'Jurisdictions',
            jurisdictions_comma: 'Jurisdictions (comma-separated)',
            jurisdiction: 'Jurisdiction',
            authority_status: 'Authority Status',
            subject_status: 'Subject Status',
            authority_deadline: 'Authority Deadline',
            subject_deadline: 'Subject Deadline',
            mark_authority_done: 'Mark Authority Done',
            mark_subject_done: 'Mark Subject Done',
            not_assessed: 'Not assessed',
            event_type: 'Event Type',
            timestamp: 'Timestamp',
            actor: 'Actor',
            no_timeline_events: 'No timeline events yet.',
            decision_type: 'Decision Type',
            decision: 'Decision',
            rationale: 'Rationale',
            decision_made: 'Decision Made',
            no_decisions: 'No decisions recorded yet.',
            jurisdictions_comma_separated: 'VN, SG, EU',
            no_audit_events: 'No audit events recorded yet.',
            export_pdf: 'Export PDF',
            pdf_title: 'Incident Summary Report',
            description: 'Description',
            linked_records: 'Linked Records',
        },
        tasks: {
            page_title: 'Incident Response Tasks',
            title: 'Incident Response Tasks',
            new: 'New Task',
            edit: 'Edit Task',
            task_type: 'Task Type',
            assignee: 'Assignee',
            due_date: 'Due Date',
            dependencies: 'Dependencies',
            mark_complete: 'Mark Complete',
            templates: 'Task Templates',
            select_incident: 'Select Incident',
            all_statuses: 'All Statuses',
            all_categories: 'All Categories',
            new_response_task: 'New Response Task',
            category: 'Category',
            priority: 'Priority',
            depends_on: 'Depends On (comma-separated task IDs)',
            checklist_items: 'Checklist Items',
            remove: 'Remove',
            add: 'Add',
            new_checklist_item: 'New checklist item',
            no_templates: 'No templates available.',
            assign_task: 'Assign Task',
            assign_to: 'Assign To',
            assign: 'Assign',
            select_incident_prompt: 'Please select an incident to view its response tasks.',
            checklist: 'Checklist',
            hide_checklist: 'Hide Checklist',
            escalate: 'Escalate',
            escalations: 'Escalations',
            no_checklist_items: 'No checklist items.',
        },
        reports: {
            page_title: 'Incident Reports',
            title: 'Incident Reports',
            summary: 'Summary Statistics',
            by_severity: 'By Severity',
            by_status: 'By Status',
            recent: 'Recent Incidents',
            total_incidents: 'Total Incidents',
            open: 'Open',
            closed: 'Closed',
            export_csv: 'Export CSV',
            export_pdf: 'Export PDF',
            export_success: 'Report exported successfully.',
            export_error: 'Failed to export report.',
            reference: 'Reference',
            view_report: 'View Report',
        },
        statuses: {
            reported: 'Reported',
            triage: 'Triage',
            contained: 'Contained',
            investigation: 'Investigation',
            remediation: 'Remediation',
            closed: 'Closed',
            false_positive: 'False Positive',
        },
        task_statuses: {
            pending: 'Pending',
            in_progress: 'In Progress',
            completed: 'Completed',
            blocked: 'Blocked',
            cancelled: 'Cancelled',
        },
        severity: {
            low: 'Low',
            medium: 'Medium',
            high: 'High',
            critical: 'Critical',
        },
        task_types: {
            containment: 'Containment',
            investigation: 'Investigation',
            remediation: 'Remediation',
            communication: 'Communication',
            documentation: 'Documentation',
            notification: 'Notification',
        },
        categories: {
            data_breach: 'Data Breach',
            unauthorized_access: 'Unauthorized Access',
            data_loss: 'Data Loss',
            system_compromise: 'System Compromise',
            malware: 'Malware',
            phishing: 'Phishing',
            insider_threat: 'Insider Threat',
            other: 'Other',
        },
        reported_via_options: {
            email: 'Email',
            phone: 'Phone',
            internal_system: 'Internal System',
            third_party: 'Third Party',
            automated_detection: 'Automated Detection',
        },
        harm_likelihood_options: {
            unlikely: 'Unlikely',
            possible: 'Possible',
            likely: 'Likely',
            very_likely: 'Very Likely',
        },
        breach_determination_options: {
            confirmed_breach: 'Confirmed Breach',
            suspected_breach: 'Suspected Breach',
            not_a_breach: 'Not a Breach',
            pending: 'Pending',
        },
        timeline_event_types: {
            detection: 'Detection',
            escalation: 'Escalation',
            containment: 'Containment',
            investigation_start: 'Investigation Start',
            evidence_collected: 'Evidence Collected',
            notification_sent: 'Notification Sent',
            remediation_action: 'Remediation Action',
            status_change: 'Status Change',
            communication: 'Communication',
            other: 'Other',
        },
        decision_types: {
            containment: 'Containment',
            notification: 'Notification',
            remediation: 'Remediation',
            escalation: 'Escalation',
            closure: 'Closure',
            other: 'Other',
        },
        task_transitions: {
            start: 'Start',
            complete: 'Complete',
            block: 'Block',
            cancel: 'Cancel',
            unblock: 'Unblock',
        },
        announce: {
            loaded: '{count} incidents loaded',
            reports_loaded: '{count} incident reports loaded',
            tasks_loaded: '{count} tasks loaded',
            created: 'Incident created successfully',
            updated: 'Incident updated successfully',
            closed: 'Incident closed successfully',
            status_changed: 'Incident status changed',
            triage_completed: 'Triage completed successfully',
            impact_updated: 'Impact assessment updated',
            breach_updated: 'Breach assessment updated',
            timeline_added: 'Timeline event added',
            decision_recorded: 'Decision recorded',
            notification_updated: 'Notification status updated',
            task_created: 'Task created successfully',
            task_status_changed: 'Task status changed',
            checklist_completed: 'Checklist item completed',
            checklist_unchecked: 'Checklist item unchecked',
            task_assigned: 'Task assigned successfully',
            task_escalated: 'Task escalated',
            export_initiated: 'Export initiated',
        },
        // Sprint 21.1.0: Incident Response Maturity content blocks
        breach_notifications: {
            page_title: 'Breach Notifications',
            title: 'Breach Notifications',
            new_notification: 'New Notification',
            no_notifications: 'No breach notifications found',
            status: 'Status',
            submit_to_mps: 'Submit to MPS',
            decision: 'Decision',
            decision_maker: 'Decision Maker',
            notify: 'Notify',
            do_not_notify: 'Do Not Notify',
            rationale: 'Rationale',
            filing_deadline: 'Filing Deadline',
            overdue: 'Overdue',
            due_soon: 'Due Soon',
            on_track: 'On Track',
            legal_review: 'Legal Review',
            incident_id: 'Incident ID',
            notification_statuses: {
                assessment: 'Assessment',
                decision_to_notify: 'Decision to Notify',
                notification_drafted: 'Notification Drafted',
                legal_review: 'Legal Review',
                submitted_to_mps: 'Submitted to MPS',
                supplement_requested: 'Supplement Requested',
                supplement_submitted: 'Supplement Submitted',
                acknowledged: 'Acknowledged',
                closed: 'Closed',
            },
        },
        supplements: {
            page_title: 'Supplement Requests',
            title: 'Supplement Requests',
            new_supplement: 'New Supplement',
            no_supplements: 'No supplement requests found',
            request_details: 'Request Details',
            response_package: 'Response Package',
            submit_response: 'Submit Response',
            deadline: 'Deadline',
            notification_id: 'Notification ID',
            enter_notification_id: 'Enter a notification ID to view supplements.',
            supplement_statuses: {
                requested: 'Requested',
                drafting: 'Drafting',
                submitted: 'Submitted',
                accepted: 'Accepted',
                rejected: 'Rejected',
            },
        },
        remediation: {
            page_title: 'Remediation Actions',
            title: 'Remediation Actions',
            new_action: 'New Action',
            no_actions: 'No remediation actions found',
            description: 'Description',
            owner: 'Owner',
            department: 'Department',
            deadline: 'Deadline',
            statuses: {
                planned: 'Planned',
                in_progress: 'In Progress',
                completed: 'Completed',
                verified: 'Verified',
            },
            verify: 'Verify',
            incident_id: 'Incident ID',
            enter_incident_id: 'Enter an incident ID to view remediation actions.',
            start: 'Start',
            complete_action: 'Complete',
        },
        authority_requests: {
            page_title: 'Authority Requests',
            title: 'Authority Requests',
            new_request: 'New Request',
            no_requests: 'No authority requests found',
            authority_name: 'Authority Name',
            request_details: 'Request Details',
            response_package: 'Response Package',
            submission_channel: 'Submission Channel',
            submit_response: 'Submit Response',
            deadline: 'Deadline',
            confirmation: 'Confirmation',
            incident_id_optional: 'Incident ID (optional)',
            // Sprint 44.5.0 — response-context + materialisation-status UI
            response_context_label: 'Response context (for submission-artifact materialisation)',
            response_context_none: '(skip materialisation)',
            response_context_supplement: 'Supplement response',
            response_context_information: 'Information-request response',
            response_context_inspection: 'Inspection response',
            materialisation_status_column: 'Submission artifact',
            materialisation_pending: 'Pending',
            materialisation_success: 'Materialised',
            materialisation_failed: 'Failed',
            materialisation_retry_button: 'Retry',
            ar_statuses: {
                received: 'Received',
                verified: 'Verified',
                assembling: 'Assembling',
                submitted: 'Submitted',
                closed: 'Closed',
            },
        },
        disclosure_register: {
            page_title: 'Disclosure Register',
            title: 'Disclosure Register',
            new_entry: 'New Entry',
            no_entries: 'No disclosure register entries found',
            disclosure_type: 'Disclosure Type',
            disclosure_date: 'Disclosure Date',
            recipient: 'Recipient',
            authority_name: 'Authority Name',
            channel: 'Channel',
            content_summary: 'Content Summary',
            incident_id_optional: 'Incident ID (optional)',
        },
    },
    // ---------- Onboarding module (Sprint 1.9.0) ----------
    onboarding: {
        nav: {
            wizard: 'Setup Wizard',
            tours: 'Module Tours',
            sample_data: 'Sample Data',
        },
        wizard: {
            title: 'Setup Wizard',
            welcome: 'Welcome to AesirX ComplianceOne',
            welcome_message: "Let's get your organization set up. This wizard will guide you through the initial configuration.",
            setup_complete: 'Setup Complete',
            skipped_message: 'The setup wizard was skipped. You can run it anytime to configure your organization.',
            start_setup: 'Start Setup',
            skip_wizard: 'Skip Wizard',
            go_to_admin: 'Go to Admin',
            skip_for_now: 'Skip for Now',
            next_step: 'Next Step',
            previous_step: 'Previous Step',
            finish_setup: 'Finish Setup',
            rerun: 'Re-run Setup Wizard',
            step_label: 'Step {number}',
            validation_org_name_required: 'Organization name is required.',
            validation_admin_email_required: 'Admin email is required.',
            validation_admin_email_invalid: 'Please enter a valid email address.',
            validation_cmp_endpoint_required: 'CMP endpoint is required when CMP is enabled.',
            steps: {
                license_activation: 'License Activation',
                org_profile: 'Organization Profile',
                module_selection: 'Module Selection',
                cmp_integration: 'CMP Integration',
                notification_preferences: 'Notifications',
                review: 'Review & Confirm',
            },
            step_descriptions: {
                license_activation: 'Enter your license key to activate your package and determine which modules are available.',
                org_profile: 'Set up your organization name, jurisdiction, industry, size, and preferred language.',
                module_selection: 'Choose which compliance modules to activate for your organization.',
                cmp_integration: 'Configure Consent Management Platform integration (optional).',
                notification_preferences: 'Configure how you receive notifications for compliance events.',
                review: 'Review your configuration and finalize the setup.',
            },
            license_step: {
                validate_button: 'Validate License',
                skip_warning: 'Module access will be limited to the default package tier. You can activate a license later in Platform > License Settings.',
            },
            module_descriptions: {
                rights_requests: 'Handle data subject access and deletion requests.',
                data_mapping: 'Map systems, data flows, and processing activities.',
                governance: 'Manage vendor compliance and deletion playbooks.',
                consent: 'Manage consent policies and purpose catalogs.',
                consent_properties: 'Manage multi-property consent across digital properties.',
                assessments: 'Conduct impact assessments and cross-border transfers.',
                classification: 'Classify and label data assets with lifecycle tracking.',
                monitoring: 'Scheduled compliance reviews and KPI tracking.',
                incidents: 'Report and manage security and privacy incidents.',
                connectors: 'Connect to external systems for automated data exchange.',
                vendor_risk: 'Score vendors, track contracts, and manage due diligence.',
                deletion_orchestration: 'Coordinate data deletion across systems and vendors.',
                discovery: 'Discover and classify data across your infrastructure.',
                incident_ops: 'Advanced incident playbooks, SLA tracking, and escalations.',
                program_governance: 'Maturity assessments, executive reporting, and audit readiness.',
                access_accountability: 'Monitor user access, sessions, and justification workflows.',
                localization: 'Market-specific compliance packs and regulatory frameworks.',
                partner_workspace: 'Partner and reseller management workspace.',
                privacy_scanner: 'Real-time privacy monitoring of digital properties.',
                onboarding: 'Setup wizard, guided tours, and sample data management.',
                compliance_forms: 'Interactive compliance form templates and document generation.',
                audit_trail: 'Comprehensive immutable audit logging and retention.',
                organizations: 'Multi-organization and group management.',
                task_management: 'Unified task planning, workflow engine, and calendar views.',
            },
            module_categories: {
                data_privacy_rights: 'Data Privacy & Rights',
                vendor_third_party: 'Vendor & Third-Party Management',
                risk_assessment: 'Risk & Assessment',
                operations_automation: 'Operations & Automation',
                platform_admin: 'Platform & Administration',
            },
            package_tier_label: 'Your Package',
            package_module_unavailable: 'Not included in your package tier',
            select_all: 'Select All',
            deselect_all: 'Deselect All',
            load_sample_data: 'Load sample data after setup',
            load_sample_data_description: 'Install realistic demo data (Lotus Financial Services) to explore the platform. Can be removed later from Onboarding > Sample Data.',
            language_label: 'Preferred Language',
            language_description: 'Choose the default language for your organization.',
            admin_account_title: 'Administrator Account',
            admin_account_created: 'Administrator account created',
            admin_full_name: 'Full name',
            admin_email: 'Email address',
            admin_password: 'Password',
            admin_confirm_password: 'Confirm password',
            admin_show_password: 'Show password',
            admin_hide_password: 'Hide password',
            admin_name_placeholder: 'e.g. Jane Smith',
            admin_email_placeholder: 'admin@example.com',
            admin_password_placeholder: 'Minimum 8 characters',
            admin_password_confirm_placeholder: 'Re-enter password',
            validation_admin_name_required: 'Administrator name is required',
            validation_admin_password_required: 'Password is required',
            validation_admin_password_min_length: 'Password must be at least 8 characters',
            validation_passwords_mismatch: 'Passwords do not match',
            notification_email_enabled: 'Enable Email Notifications',
            notification_email_description: 'Receive email notifications for task assignments, SLA breaches, and compliance events.',
            notification_digest_frequency: 'Digest Frequency',
            notification_digest_daily: 'Daily',
            notification_digest_weekly: 'Weekly',
            notification_digest_none: 'No digest',
            notification_in_app_enabled: 'Enable In-Platform Notifications',
            notification_in_app_description: 'Show notification badge and drawer for real-time alerts.',
            jurisdictions: {
                vietnam: 'Vietnam',
                asean: 'ASEAN',
                eu_eea: 'EU/EEA',
                global: 'Global',
            },
            languages: {
                en: 'English',
                vi: 'Tiếng Việt',
                da: 'Dansk',
                sv: 'Svenska',
                nb: 'Norsk Bokmål',
                nn: 'Norsk Nynorsk',
                de: 'Deutsch',
                fi: 'Suomi',
                nl: 'Nederlands',
                fr: 'Français',
                lb: 'Lëtzebuergesch',
                ga: 'Gaeilge',
                es: 'Español',
                ca: 'Català',
                eu: 'Euskara',
                pt: 'Português',
            },
            industries: {
                financial_services: 'Financial Services',
                healthcare: 'Healthcare',
                technology: 'Technology',
                telecommunications: 'Telecommunications',
                government: 'Government',
                education: 'Education',
                manufacturing: 'Manufacturing',
                retail: 'Retail',
                other: 'Other',
            },
            placeholders: {
                org_name: 'e.g., Lotus Financial Services',
                admin_email: 'admin@company.vn',
                team_emails: 'user1@company.vn, user2@company.vn',
                cmp_endpoint: 'https://cmp.example.com/api',
            },
            fields: {
                org_name: 'Organization Name',
                jurisdiction: 'Jurisdiction',
                industry: 'Industry',
                org_size: 'Organization Size',
                language: 'Preferred Language',
                admin_email: 'Admin Email',
                team_emails: 'Team Member Emails (comma-separated)',
                enable_cmp: 'Enable CMP Integration',
                cmp_endpoint: 'CMP API Endpoint',
                email_notifications: 'Email Notifications',
                in_app_notifications: 'In-Platform Notifications',
                digest_frequency: 'Digest Frequency',
            },
        },
        tours: {
            title: 'Module Onboarding Tours',
            start: 'Start Tour',
            continue_tour: 'Continue Tour',
            replay: 'Replay Tour',
            exit: 'Exit Tour',
            skip: 'Skip Tour',
            next: 'Next',
            previous: 'Previous',
            finish: 'Finish Tour',
            step_of: 'Step {current} of {total}',
            info: 'Take guided tours of each module to learn key features and workflows. Tours can be replayed at any time.',
            autoplay: 'Auto-play',
            interactive: 'Interactive',
            play_all: 'Play All',
            search_tours: 'Search tours...',
            duration: 'Duration',
            steps_label: '{count} steps',
            mode: 'Mode',
            pause: 'Pause',
            resume: 'Resume',
            speed: 'Speed',
            restart: 'Restart',
            reset: 'Reset',
            reset_success: 'Tour progress has been reset.',
            category_core_privacy: 'Core Privacy',
            category_governance: 'Governance',
            category_operations: 'Operations',
            category_platform: 'Platform',
            category_advanced: 'Advanced',
            category_data_privacy_rights: 'Data Privacy & Rights',
            category_vendor_third_party: 'Vendor & Third-Party Management',
            category_risk_assessment: 'Risk & Assessment',
            category_operations_automation: 'Operations & Automation',
            category_platform_admin: 'Platform & Administration',
            steps_count: '{count} steps',
        },
        sample_data: {
            title: 'Sample Data Management',
            select_modules: 'Select Modules',
            current_status: 'Current Status',
            operation_history: 'Operation History',
            load: 'Load Sample Data',
            clear: 'Clear Sample Data',
            processing: 'Processing...',
            loaded: 'Loaded',
            not_loaded: 'Not Loaded',
            records: 'Sample Records',
            confirm_clear: 'Are you sure you want to clear all sample data for the selected modules?',
            confirm_clear_title: 'Clear Sample Data',
            confirm_load_title: 'Load Sample Data',
            confirm_load: 'Are you sure you want to load sample data for the selected modules?',
            action_load: 'Load',
            action_clear: 'Clear',
            info: 'Load realistic demo data for training and demonstrations using the fictional company "Lotus Financial Services". Sample data is tagged and can be safely removed without affecting real data.',
        },
        help: {
            title: 'Help Center',
            search: 'Search',
            search_placeholder: 'Search help articles...',
            clear: 'Clear',
            all_categories: 'All Categories',
            all_modules: 'All Modules',
            back: 'Back to Help Center',
            callout_important: 'Important',
            callout_tip: 'Tip',
            no_results: 'No articles found matching your search.',
            no_articles: 'No help articles available yet. Articles will appear here as content is added.',
            results_for: '{count} result(s) for "{query}"',
            quick_links: {
                getting_started: 'Getting Started',
                getting_started_desc: 'Installation, setup, and quick start guide.',
                user_guide: 'User Guide',
                user_guide_desc: 'Module-by-module usage documentation.',
                admin_guide: 'Admin Guide',
                admin_guide_desc: 'Configuration, maintenance, and troubleshooting.',
                faq: 'FAQ',
                faq_desc: 'Frequently asked questions and answers.',
            },
            search_articles_aria: 'Search help articles',
            article_metadata_aria: 'Article metadata',
            articles_list_aria: 'Help articles',
            category_nav_aria: 'Category navigation',
            quick_links_aria: 'Quick links to help categories',
            returned_to_list: 'Returned to article list',
            opening_article: 'Opening article: {title}',
            // fix-sprint-20260504-2137 F20 — HelpCenterPage aria-labels.
            read_article_aria: 'Read article: {title}',
            pagination_nav_aria: 'Articles pagination',
            error_unavailable: 'Help center is currently unavailable. Please try again later.',
            retry: 'Retry',
        },
    },
    // ---------- Common / shared strings ----------
    common: {
        save: 'Save',
        cancel: 'Cancel',
        system_actor: 'System',
        delete: 'Delete',
        edit: 'Edit',
        create: 'Create',
        update: 'Update',
        loading: 'Loading...',
        error: 'An error occurred',
        success: 'Success',
        logout: 'Logout',
        actions: 'Actions',
        retrying: 'Retrying…',
        permission_denied: 'You do not have access to this page.',
        status: 'Status',
        description: 'Description',
        name: 'Name',
        created_at: 'Created At',
        updated_at: 'Updated At',
        yes: 'Yes',
        no: 'No',
        confirm: 'Confirm',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        search: 'Search',
        filter: 'Filter',
        // Sprint 47.9.1a — shared pagination labels reused across the
        // Forseti list pages (and any future list page). Per the family
        // plan EN+VI-only contract; the 14 other locales fan out in 51.0.0.
        page_x_of_y: 'Page {page} of {total}',
        pagination_label: 'Pagination',
        // fix-sprint-20260411-D F10/F11/F12 — shared aria-label keys.
        view_mode_label: 'View mode',
        export: 'Export',
        // fix-sprint-20260415-0434 F11/F12/F16/F17 — ExportFormatMenu + AdminLayout i18n.
        exporting: 'Exporting…',
        sidebar_navigation: 'Admin sidebar navigation',
        items_count: '{count} items',
        items_count_one: '{count} item',
        export_success: 'Document generated successfully.',
        generate_pdf: 'Generate PDF',
        import: 'Import',
        download: 'Download',
        upload: 'Upload',
        view: 'View',
        close: 'Close',
        submit: 'Submit',
        saved: 'Saved successfully',
        saving: 'Saving...',
        submitting: 'Submitting...',
        created: 'Created successfully',
        all: 'All',
        none: 'None',
        select: 'Select',
        select_user: 'Select user...',
        custom_input: 'Type manually',
        required: 'Required',
        optional: 'Optional',
        notes: 'Notes',
        tags: 'Tags',
        archive: 'Archive',
        clone: 'Clone',
        refresh: 'Refresh',
        retry: 'Retry',
        reset: 'Reset',
        add: 'Add',
        remove: 'Remove',
        // fix-sprint-20260410-0410 F5/F6 — shared AttachmentField + EntityReferenceSelect
        removing: 'Removing…',
        attachment: 'Attachment',
        upload_file: 'Upload file',
        upload_file_required: 'Upload file (required)',
        select_entity_type_first: 'Select an entity type first',
        // fix-sprint-20260411-0337 H10/M12/M13/M14 — missing i18n keys
        failed_to_remove_file: 'Failed to remove file.',
        entity_type_label: 'Entity type',
        entity_label: 'Entity',
        entity_reference: 'Entity reference',
        failed_to_load_entities: 'Failed to load entities',
        enable: 'Enable',
        disable: 'Disable',
        no_data: 'No data found',
        confirm_delete: 'Are you sure you want to delete this item?',
        confirm_action: 'Are you sure you want to proceed?',
        save_success: 'Saved successfully',
        save_error: 'Failed to save',
        delete_success: 'Deleted successfully',
        delete_error: 'Failed to delete',
        hour: 'hour',
        hours: 'hours',
        load_error: 'Failed to load data',
        operation_error: 'Operation failed',
        something_went_wrong: 'Something went wrong',
        go_home: 'Go to Dashboard',
        access_denied: 'Access Denied',
        no_permission: 'You do not have permission to access this page.',
        no_permission_title: 'Insufficient Permissions',
        no_permission_message: 'You do not have permission to access this page.',
        unknown: 'Unknown',
        back_to_dashboard: 'Back to Dashboard',
        id: 'ID',
        type: 'Type',
        date: 'Date',
        created_by: 'Created By',
        updated_by: 'Updated By',
        page_of: 'Page {page} of {total}',
        showing_items: 'Showing {count} items',
        items_per_page: 'Items per page',
        no_results: 'No records match your filters',
        no_items: 'No items found',
        sort_by: 'Sort by',
        ascending: 'Ascending',
        descending: 'Descending',
        all_statuses: 'All Statuses',
        all_types: 'All Types',
        all_categories: 'All Categories',
        all_severities: 'All Severities',
        date_range: 'Date Range',
        from_date: 'From',
        to_date: 'To',
        clear_filters: 'Clear Filters',
        apply_filters: 'Apply Filters',
        save_changes: 'Save Changes',
        discard_changes: 'Discard Changes',
        confirm_discard: 'You have unsaved changes. Discard them?',
        copy: 'Copy',
        duplicate: 'Duplicate',
        move: 'Move',
        assign: 'Assign',
        unassign: 'Unassign',
        approve: 'Approve',
        reject: 'Reject',
        deleting: 'Deleting...',
        processing: 'Processing...',
        loading_page: 'Loading page...',
        operation_success: 'Operation completed successfully',
        operation_failed: 'Operation failed. Please try again.',
        empty_dash: '—',
        network_error: 'Network error. Check your connection.',
        session_expired: 'Session expired. Please log in again.',
        test: 'Test',
        pause: 'Pause',
        resume: 'Resume',
        activate: 'Activate',
        last_sync: 'Last Sync',
        health: 'Health',
        email: 'Email',
        role: 'Role',
        title: 'Title',
        owner: 'Owner',
        priority: 'Priority',
        category: 'Category',
        details: 'Details',
        overview: 'Overview',
        configuration: 'Configuration',
        history: 'History',
        new_item: 'New',
        edit_item: 'Edit',
        select_option: 'Select...',
        total: 'Total',
        active: 'Active',
        inactive: 'Inactive',
        enabled: 'Enabled',
        disabled: 'Disabled',
        last_login: 'Last Login',
        never: 'Never',
        password_changed: 'Password changed. You will be logged out.',
        password_change_failed: 'Failed to change password',
        passwords_mismatch: 'Passwords do not match',
        install: 'Install',
        uninstall: 'Uninstall',
        installed: 'Installed',
        available: 'Available',
        catalog: 'Catalog',
        published: 'Published',
        version: 'Version',
        market: 'Market',
        language: 'Language',
        framework: 'Framework',
        template: 'Template',
        connector_type: 'Connector Type',
        polling_interval: 'Polling Interval (minutes)',
        new_instance: 'New Instance',
        change_password: 'Change Password',
        current_password: 'Current Password',
        new_password: 'New Password',
        confirm_password: 'Confirm Password',
        comments: 'Comments',
        completed: 'Completed',
        contact: 'Contact',
        hide: 'Hide',
        hide_details: 'Hide Details',
        label: 'Label',
        max: 'Max',
        min: 'Min',
        na: 'N/A',
        of: 'of',
        showing: 'Showing',
        page: 'Page',
        preview: 'Preview',
        print: 'Print',
        show: 'Show',
        status_active: 'Active',
        status_completed: 'Completed',
        status_draft: 'Draft',
        status_in_progress: 'In Progress',
        status_pending: 'Pending',
        view_details: 'View Details',
        all_priorities: 'All Priorities',
        pipeline: 'Pipeline',
        all_requests: 'All Requests',
        schedule: 'Schedule',
        deploy: 'Deploy',
        rollback: 'Rollback',
        advance_stage: 'Advance Stage',
        submit_for_approval: 'Submit for Approval',
        approval_log: 'Approval Log',
        approver: 'Approver',
        decision: 'Decision',
        decided_at: 'Decided At',
        rollout_stages: 'Rollout Stages',
        approval_comment: 'Approval Comment',
        optional_comment: 'Optional comment...',
        impact_summary: 'Impact Summary',
        target_property_ids: 'Target Property IDs (comma-separated)',
        policy_id: 'Policy ID',
        target_version: 'Target Version',
        target_version_placeholder: 'e.g., 2.0.0',
        scheduled_label: 'Scheduled',
        scheduled_start: 'Scheduled Start',
        scheduled_end: 'Scheduled End',
        properties_count: '{count} properties',
        results: 'Results',
        comparison_results: 'Comparison Results',
        past_comparison_reports: 'Past Comparison Reports',
        scanning: 'Scanning...',
        run_inconsistency_scan: 'Run Inconsistency Scan',
        property_ids_label: 'Property IDs (comma-separated, min 2)',
        comparison_type: 'Comparison Type',
        golden_template_id: 'Golden Template ID (optional)',
        leave_blank_compare: 'Leave blank to compare properties against each other',
        add_notes_placeholder: 'Add your analysis notes here...',
        covered_of_active: '{covered} of {active} active properties covered',
        fetch_error: 'Failed to fetch data. Please try again.',
        run_comparison_error: 'Failed to run comparison. Please try again.',
        save_notes_error: 'Failed to save notes. Please try again.',
        scan_complete: 'Inconsistency scan completed successfully.',
        scan_error: 'Failed to run inconsistency scan. Please try again.',
        min_two_properties: 'Please provide at least 2 property IDs for comparison.',
        comma_separated: 'comma-separated',
        language_english: 'English',
        language_vietnamese: 'Vietnamese',
        language_vietnamese_native: 'Tiếng Việt',
        author: 'Author',
        dependencies: 'Dependencies',
        checksum: 'Checksum',
        changelog: 'Changelog',
        tier: 'Tier',
        phone: 'Phone',
        launch: 'Launch',
        complete: 'Complete',
        back_to_list: 'Back to List',
        clear_selection: 'Clear Selection',
        deactivate: 'Deactivate',
        scope: 'Scope',
        deadline: 'Deadline',
        progress: 'Progress',
        frequency: 'Frequency',
        reviewer: 'Reviewer',
        user: 'User',
        campaign: 'Campaign',
        items: 'Items',
        next_run: 'Next Run',
        last_run: 'Last Run',
        overdue: 'OVERDUE',
        creating: 'Creating...',
        launching: 'Launching...',
        loaded: 'loaded',
        page_size: 'Page size',
        per_page: '/ page',
        urgent: 'Urgent',
        pagination: 'Pagination',
        metric: 'Metric',
        value: 'Value',
        country: 'Country',
        showing_chart_view: 'Showing chart view',
        showing_table_view: 'Showing data table view',
        policies: 'Policies',
        select_content_type: 'Please select at least one content type.',
        compliant: 'Compliant',
        non_compliant_status: 'Non-Compliant',
        all_data: 'All data',
        sr_urgent: 'Urgent',
        n_per_page: '{n} / page',
        select_user_placeholder: '-- Select user --',
        field_required: 'This field is required',
        results_found: 'results found',
        select_placeholder: '-- Select --',
        unknown_user: 'Unknown User',
        export_csv: 'Export CSV',
        not_applicable: 'N/A',
        aria_overdue_since: 'Overdue since {date}',
        placeholders: {
            enter_justification: 'Enter justification',
            justification: 'Justification',
            campaign_name_example: 'Q1 2026 Access Review',
            optional_description: 'Optional description',
            enter_user_id: 'Enter the user ID of the reviewer',
            enter_user_id_short: 'Enter user ID',
            schedule_name_example: 'Quarterly Access Attestation',
            enter_property_id: 'Enter property ID',
            enter_policy_id: 'Enter policy ID',
            property_or_group_id: 'Property or group ID',
            describe_template_purpose: 'Describe the template purpose...',
            step_title: 'Step title...',
            detailed_instructions: 'Detailed instructions...',
            filter_by_taxonomy_id: 'Filter by Taxonomy ID...',
            describe_classification_rule: 'Describe the classification rule...',
            describe_dedup_rule: 'Describe the dedup rule...',
            describe_import_template: 'Describe the import template...',
            reason_for_rejection: 'Reason for rejection...',
            jurisdiction_codes_example: 'VN, SG, EU',
            filter_by_locale: 'Filter by locale...',
            filter_by_event_type: 'Filter by event type',
            filter_by_actor_id: 'Filter by actor ID',
            filter_by_market_code: 'Filter by market code...',
            filter_by_framework_id: 'Filter by framework ID...',
            add_comment: 'Add a comment...',
            provide_rejection_reason: 'Provide a reason for rejecting this submission...',
            filter_by_jurisdiction: 'Filter by jurisdiction code...',
            enter_report_title: 'Enter report title',
            enter_name: 'Enter name',
            enter_description: 'Enter description',
            enter_org_id: 'Enter Org ID to add',
            describe_evidence_gap: 'Describe the evidence gap...',
            expected_evidence: 'What evidence is expected',
            entity_identifier: 'Entity identifier',
            remediation_steps: 'Steps to remediate this gap',
            user_id_or_name: 'User ID or name',
            reporting_period_id: 'Reporting period ID',
            maturity_model_id: 'Maturity model ID',
            describe_maturity_model: 'Describe this maturity model...',
            describe_role_purpose: "Describe this role\\'s purpose...",
            provide_deny_reason: 'Provide a reason for denying this request',
            describe_emergency: 'Describe the emergency situation requiring elevated access (minimum 20 characters)',
            enter_delegate_id: 'Enter user ID of the delegate',
            provide_delegation_reason: 'Provide a reason for this delegation',
            client_identifier: 'Client identifier',
            client_secret: 'Client secret',
            time_format: 'HH:MM',
            describe_sod_rule: 'Describe the purpose of this SoD rule...',
            enter_role_id: 'Enter role ID',
            explain_exception: 'Explain why this exception is needed...',
            describe_compensating_controls: 'Describe compensating controls in place...',
            full_name: 'Full name',
            min_chars: 'Min 12 chars',
            enter_override_justification: 'Enter justification for overriding this gate evaluation',
            enter_evidence_id: 'Enter evidence ID to link:',
            date_format: 'DD/MM/YYYY',
            describe_pack_contents: 'Describe the pack contents and purpose',
            select_reviewers: 'Select reviewers...',
        },
        aria_previous_page: 'Previous page',
        aria_next_page: 'Next page',
        aria_results_per_page: 'Results per page',
        per_page_25: '25 / page',
        per_page_50: '50 / page',
        per_page_100: '100 / page',
        select_language: 'Select language',
        today: 'Today',
        tomorrow: 'Tomorrow',
        dashboard_load_error: 'Failed to load dashboard. Please try again.',
        enum_grade: {
            red: 'Red',
            amber: 'Amber',
            yellow: 'Yellow',
            green: 'Green',
        },
        enum_severity: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        enum_priority: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        enum_status: {
            pending: 'Pending',
            in_progress: 'In Progress',
            completed: 'Completed',
            blocked: 'Blocked',
            escalated: 'Escalated',
            cancelled: 'Cancelled',
            overdue: 'Overdue',
            not_started: 'Not Started',
            new: 'New',
            open: 'Open',
            closed: 'Closed',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
        },
        enum_department: {
            unassigned: 'Unassigned',
        },
        enum_form_category: {
            assessment: 'Assessment',
            assessment_report: 'Assessment Report',
            dpia: 'DPIA',
            authority_submission: 'Authority Submission',
            cross_border_transfer: 'Cross-Border Transfer',
            vendor: 'Vendor',
            breach: 'Breach',
            consent: 'Consent',
            governance: 'Governance',
            register: 'Register',
            monitoring: 'Monitoring',
            dpa: 'DPA',
            dsr: 'DSR',
            privacy: 'Privacy',
            incident: 'Incident',
            org: 'Organization',
            sector: 'Sector',
            classification: 'Classification',
            discovery: 'Discovery',
        },
        enum_module: {
            rights_requests: 'Rights Requests',
            assessments: 'Assessments',
            incidents: 'Incidents',
            incident_ops: 'Incident Ops',
            monitoring: 'Monitoring',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion',
            access_review: 'Access Review',
            consent: 'Consent',
            consent_properties: 'Consent Properties',
            forms: 'Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Classification',
            discovery: 'Discovery',
            manual: 'Manual',
            data_mapping: 'Data Mapping',
        },
        enum_risk_level: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        enum_request_type: {
            all: 'All',
            access: 'Access (DSAR)',
            deletion: 'Deletion',
            correction: 'Correction',
            objection: 'Objection',
            portability: 'Portability',
            restriction: 'Restriction',
            consent_withdrawal: 'Consent Withdrawal',
        },
        enum_locale: {
            en: 'English',
            vi: 'Vietnamese',
            all: 'Both',
        },
        enum_event_type: {
            case_created: 'Case Created',
            status_changed: 'Status Changed',
            case_assigned: 'Case Assigned',
            note_added: 'Note Added',
            attachment_added: 'Attachment Added',
            attachment_deleted: 'Attachment Deleted',
            reference_added: 'Reference Added',
            checklist_updated: 'Checklist Updated',
            identity_verified: 'Identity Verified',
            closure_summary_added: 'Closure Summary',
            approval_granted: 'Approval Granted',
            template_applied: 'Template Applied',
            export_generated: 'Export Generated',
            sla_warning: 'SLA Warning',
            sla_overdue: 'SLA Overdue',
        },
        more: 'more',
        use: 'Use',
        ok: 'OK',
    },
    // ---------- Common status labels used across modules ----------
    common_statuses: {
        removed: 'Removed',
        draft: 'Draft',
        active: 'Active',
        inactive: 'Inactive',
        pending: 'Pending',
        in_progress: 'In Progress',
        completed: 'Completed',
        archived: 'Archived',
        published: 'Published',
        approved: 'Approved',
        rejected: 'Rejected',
        cancelled: 'Cancelled',
        blocked: 'Blocked',
        deferred: 'Deferred',
        not_started: 'Not Started',
        dismissed: 'Dismissed',
        failed: 'Failed',
        in_review: 'In Review',
        deprecated: 'Deprecated',
        scheduled: 'Scheduled',
        deploying: 'Deploying',
        deployed: 'Deployed',
        paused: 'Paused',
        rolled_back: 'Rolled Back',
        received: 'Received',
        processing: 'Processing',
        under_assessment: 'Under Assessment',
        implemented: 'Implemented',
        pending_approval: 'Pending Approval',
        pending_deletion: 'Pending Deletion',
        on_hold: 'On Hold',
        deleted: 'Deleted',
        expired: 'Expired',
        outdated: 'Outdated',
        drifted: 'Drifted',
        decommissioned: 'Decommissioned',
        match: 'Match',
        different: 'Different',
        missing: 'Missing',
        confirmed: 'Confirmed',
        revoked: 'Revoked',
        escalated: 'Escalated',
        system: 'System',
        custom: 'Custom',
        warning: 'Warning',
        blocking: 'Blocking',
        analyst: 'Analyst',
        viewer: 'Viewer',
        daily: 'Daily',
        weekly: 'Weekly',
        policies: 'Policies',
        denied: 'Denied',
        verified: 'Verified',
        detected: 'Detected',
        ignored: 'Ignored',
        open: 'Open',
        resolved: 'Resolved',
        suspended: 'Suspended',
        retired: 'Retired',
        review: 'Review',
        generated: 'Generated',
        exported: 'Exported',
    },
    // ---------- Common severity / risk labels ----------
    severity: {
        low: 'Low',
        medium: 'Medium',
        high: 'High',
        critical: 'Critical',
    },
    // ---------- Common sensitivity labels ----------
    sensitivity: {
        standard: 'Standard',
        sensitive: 'Sensitive',
        special_category: 'Special Category',
        // fix-sprint-20260410-0410 F7 — moved from consent.policies to unify
        // placement across all 16 locales.
        badge_sensitive: 'Sensitive',
    },
    // ---------- Change request type labels ----------
    change_types: {
        policy_update: 'Policy Update',
        new_binding: 'New Binding',
        remove_binding: 'Remove Binding',
        configuration_change: 'Configuration Change',
        banner_update: 'Banner Update',
    },
    // ---------- Comparison type labels ----------
    comparison_types: {
        full: 'Full',
        banner_only: 'Banner Only',
        purposes_only: 'Purposes Only',
        legal_bases_only: 'Legal Bases Only',
        consent_settings: 'Consent Settings',
    },
    // ---------- Lifecycle stages ----------
    lifecycle_stages: {
        active: 'Active',
        archived: 'Archived',
        pending_deletion: 'Pending Deletion',
        deleted: 'Deleted',
        on_hold: 'On Hold',
    },
    // ---------- Object type labels ----------
    object_types: {
        personal_data: 'Personal Data',
        consent_record: 'Consent Record',
        user_account: 'User Account',
        audit_log: 'Audit Log',
        document: 'Document',
        backup: 'Backup',
    },
    // ---------- Transfer mechanism labels ----------
    transfer_mechanisms: {
        adequacy_decision: 'Adequacy Decision',
        standard_contractual_clauses: 'Standard Contractual Clauses',
        binding_corporate_rules: 'Binding Corporate Rules',
        explicit_consent: 'Explicit Consent',
        public_interest: 'Public Interest',
        legal_claims: 'Legal Claims',
        vital_interests: 'Vital Interests',
    },
    // ---------- Risk assessment categories ----------
    risk_categories: {
        data_breach: 'Data Breach',
        unauthorized_access: 'Unauthorized Access',
        data_loss: 'Data Loss',
        regulatory_non_compliance: 'Regulatory Non-Compliance',
        third_party_risk: 'Third-Party Risk',
        operational: 'Operational',
        technical: 'Technical',
        legal: 'Legal',
    },
    // ---------- Mitigation types ----------
    mitigation_types: {
        technical: 'Technical',
        organizational: 'Organizational',
        contractual: 'Contractual',
        procedural: 'Procedural',
    },
    // ---------- Safeguard types ----------
    safeguard_types: {
        encryption_in_transit: 'Encryption in Transit',
        encryption_at_rest: 'Encryption at Rest',
        access_controls: 'Access Controls',
        contractual_clauses: 'Contractual Clauses',
        data_minimization: 'Data Minimization',
        pseudonymization: 'Pseudonymization',
        audit_rights: 'Audit Rights',
        breach_notification: 'Breach Notification',
        data_localization: 'Data Localization',
        other: 'Other',
    },
    // ---------- Scoring methods ----------
    scoring_methods: {
        weighted_average: 'Weighted Average',
        simple_average: 'Simple Average',
        maximum: 'Maximum',
        manual: 'Manual',
    },
    // ---------- DPA status labels ----------
    dpa_status: {
        not_required: 'Not Required',
        required: 'Required',
        in_progress: 'In Progress',
        completed: 'Completed',
        signed: 'Signed',
    },
    // ---------- Disposal method labels ----------
    disposal_methods: {
        anonymize: 'Anonymize',
        pseudonymize: 'Pseudonymize',
        aggregate: 'Aggregate',
        delete: 'Delete',
    },
    // ---------- Main Dashboard ----------
    dashboard: {
        title: 'Dashboard',
        no_data: 'No dashboard data available.',
        no_data_heading: 'No live data yet',
        no_data_detail: 'The dashboard widgets below show the layout. Data will populate once the backend is connected to the database.',
        /* Sprint 43.5.0 E1 — hero-tile metrics */
        consent_coverage: 'Consent coverage',
        ctia_filings: 'CTIA filings',
        next_dpia_review: 'Next DPIA review',
        next_review_none: 'None scheduled',
        incident_mps_filings: 'Incident MPS filings',
        compliance_score: 'Compliance Score',
        grade: 'Grade',
        no_score: 'No compliance score calculated yet.',
        rights_requests: 'Rights Requests',
        open: 'Open',
        sla_breached: 'SLA Breached',
        sla_approaching: 'SLA Due Soon',
        closed_month: 'Closed (Month)',
        incidents: 'Incidents & Breaches',
        active: 'Active',
        breaches: 'Breaches',
        overdue_tasks: 'Overdue Tasks',
        breach_72h_notice: '72-hour regulatory notification deadline applies to active breaches',
        recommendations: 'Recommendations',
        take_action: 'Take Action',
        drift_events: 'Drift & Approvals',
        open_events: 'Open Events',
        pending_approvals: 'Pending Approvals',
        monitoring: 'Monitoring Tasks',
        programs: 'Programs',
        overdue: 'Overdue',
        pending: 'Pending',
        ric_cases: 'RIC Cases',
        coverage: 'Data Coverage',
        systems: 'Systems',
        activities: 'Activities',
        vendors: 'Vendors',
        dpias: 'DPIAs',
        open_gaps: 'open gaps',
        view_as_table: 'View as table',
        view_as_chart: 'View as chart',
        table_metric_header: 'Metric',
        table_value_header: 'Value',
        announce_chart_view: 'Showing chart view',
        announce_table_view: 'Showing data table view',
        section_core_metrics: 'Core Metrics',
        section_task_management: 'Task Management',
        section_compliance_insights: 'Compliance Insights',
        configure_dashboard: 'Configure Dashboard',
        config_col_active: 'Active',
        config_col_widget: 'Widget',
        collapse_section: 'Collapse',
        expand_section: 'Expand',
        reset_to_defaults: 'Reset to defaults',
        preferences_saved: 'Dashboard preferences saved',
        preferences_reset: 'Dashboard reset to role defaults',
        preferences_load_error: 'Failed to load dashboard preferences',
        factor_avg_system_score: 'Average system score',
        factor_avg_vendor_score: 'Average vendor score',
        factor_processing_coverage: 'Processing activity coverage',
        factor_ropa_completeness: 'RoPA completeness',
        factor_drift_penalty: 'Drift events penalty',
        rec_improve_system_gov: 'Improve system governance documentation',
        rec_improve_vendor_gov: 'Improve vendor governance documentation',
        rec_document_processing: 'Document processing activities for uncovered systems',
        rec_generate_ropa: 'Generate RoPA records for active processing activities',
        rec_resolve_drift: 'Resolve open critical and high severity drift events',
        score_good: 'Good',
        score_fair: 'Fair',
        score_poor: 'Poor',
        score_critical: 'Critical',
        drag_to_reorder: 'Drag to reorder',
        widget_moved: 'Widget moved',
        press_space_to_grab: 'Press Space to grab, arrow keys to move, Space to drop',
        reorder_instructions: 'Use drag and drop or keyboard shortcuts to reorder widgets',
        task_widgets: {
            tasks_overview: 'Tasks Overview',
            pending: 'Pending',
            in_progress: 'In Progress',
            overdue: 'Overdue',
            done_7d: 'Done (7d)',
            by_priority: 'By Priority',
            view_all_tasks: 'View All Tasks',
            upcoming_tasks: 'Upcoming Tasks (Next 7 Days)',
            no_upcoming: 'No tasks due in the next 7 days.',
            view_calendar: 'View Calendar',
            overdue_alerts: 'Overdue',
            no_overdue: 'No overdue tasks. Everything is on track.',
            assigned_to: 'Assigned to',
            view_all_overdue: 'View All Overdue',
            notifications: 'Recent Notifications',
            unread: 'unread',
            no_notifications: "No notifications. You're all caught up!",
            view_all_notifications: 'View All Notifications',
            load_error: 'Failed to load data.',
            day_late: 'day late',
            days_late_plural: 'days late',
            due_today: 'Due today',
            just_now: 'just now',
            minutes_short: 'm',
            hours_short: 'h',
            days_short: 'd',
            cross_org_stats: 'Cross-Organization Stats',
            by_organization: 'By Organization',
            overdue_across_orgs: 'overdue tasks across organizations',
            total: 'Total',
            tasks: 'tasks',
            organization: 'Organization',
            current_org: 'Current Organization',
            all_orgs: 'All orgs',
            all_organizations: 'All Organizations',
            filter_by_org: 'Filter by organization',
            cross_org: 'Cross-org',
            empty_calendar: 'No tasks for this period. Create your first task or check your filters.',
            empty_my_tasks: 'You have no tasks assigned. Great work!',
            empty_overdue: 'No overdue tasks. Everything is on track.',
            empty_notifications: "No notifications. You're all caught up!",
            empty_workflows: 'No workflows configured. Browse templates to get started.',
        },
        my_tasks_widget: {
            title: 'My Tasks',
            no_tasks: 'No tasks assigned to you.',
            load_error: 'Failed to load tasks.',
            overdue: 'overdue',
            remaining: 'left',
            view_all: 'View All Tasks',
        },
        my_section_status_widget: {
            title: 'My Section Status',
            overall_completion: 'Overall',
            sections_count: 'Sections',
            no_sections: 'No sections assigned to your department.',
            load_error: 'Failed to load section status.',
        },
        revision_requests_widget: {
            title: 'Revision Requests',
            no_revisions: 'No revision requests. All sections are on track.',
            load_error: 'Failed to load revision requests.',
        },
        // Sprint 43.5.6 Epic B — new Light-tier widgets.
        consent_summary: {
            title: 'Consent',
            active: 'Active',
            withdrawn_30d: 'Withdrawn (30d)',
            expiring_7d: 'Expiring (7d)',
        },
        transfer_assessments: {
            title: 'Transfer Assessments',
            open: 'Open',
            approved: 'Approved',
            gaps_flagged: 'Gaps flagged',
        },
        breach_notifications: {
            title: 'Breach Notifications',
            deadline_72h: '72h deadline',
            submitted: 'Submitted',
            pending: 'Pending',
        },
        authority_requests: {
            title: 'Authority Requests',
            pending: 'Pending',
            overdue: 'Overdue',
            responded_30d: 'Responded (30d)',
        },
        data_subjects_widget: {
            title: 'Data Subjects',
            total: 'Total',
            high_risk: 'High risk',
            active_30d: 'Active (30d)',
        },
        // Sprint 45.1.0 — AUDIT_TRAIL + ONBOARDING Light-tier widgets.
        audit_activity_summary: {
            title: 'Audit Activity',
            events_today: 'Events today',
            events_7d: 'Events (7d)',
            unique_actors_30d: 'Unique actors (30d)',
        },
        onboarding_progress: {
            title: 'Onboarding Progress',
            completion_pct: 'Complete',
            status_label: 'Status',
            current_step: 'Current step',
            status: {
                in_progress: 'In progress',
                completed: 'Completed',
                skipped: 'Skipped',
                not_started: 'Not started',
            },
        },
        sector_overlay_reports_due: {
            title: 'Sector Overlay Reports Due',
        },
        persona_widgets: {
            framework_filter: 'Filter by Framework',
            all_frameworks: 'All Frameworks',
            filing_lifecycle: 'Filing Lifecycle',
            filing_pipeline: 'Filing Pipeline',
            draft: 'Draft',
            internal_review: 'Internal Review',
            approved: 'Approved',
            submitted: 'Submitted',
            supplement_requested: 'Supplement Requested',
            resubmitted: 'Resubmitted',
            accepted: 'Accepted',
            rejected: 'Rejected',
            closed: 'Closed',
            deadline: 'Deadline',
            days_remaining: 'days remaining',
            days_overdue: 'days overdue',
            no_filings: 'No active filings.',
            department_consolidation: 'Department Consolidation',
            departments_completed: 'Departments Completed',
            departments_pending: 'Departments Pending',
            departments_overdue: 'Departments Overdue',
            no_consolidation_cases: 'No multi-department cases in progress.',
            vendor_risk: 'Vendor & Third-Party Risk',
            pending_assessment: 'Pending Assessment',
            overdue_questionnaires: 'Overdue Questionnaires',
            dpa_review_queue: 'DPA Review Queue',
            risk_distribution: 'Risk Distribution',
            total_vendors: 'Total Vendors',
            by_risk_level: 'By Risk Level',
            no_vendor_data: 'No vendor risk data available.',
            discovery_summary: 'Data Discovery',
            discovery_classification: 'Data Discovery & Classification',
            active_scans: 'Active Scans',
            labels_pending: 'Labels Pending Review',
            exceptions_unresolved: 'Exceptions Unresolved',
            pending_review: 'Pending Review',
            exceptions: 'Exceptions',
            approved_labels: 'Approved Labels',
            total_classified: 'Total Classified',
            no_discovery_data: 'No discovery data available.',
            ai_governance: 'AI System Governance',
            systems_inventory: 'Systems in Inventory',
            systems_in_inventory: 'Systems in Inventory',
            classification_breakdown: 'Classification Breakdown',
            dossier_completion: 'Dossier Completion',
            by_classification: 'By Classification',
            labeling_status: 'Labeling Status',
            not_started: 'Not Started',
            no_ai_data: 'No AI governance data available.',
            breach_response: 'Breach & Incident Response',
            active_breaches: 'Active Breaches',
            notification_status: 'Notification Status',
            remediation_progress: 'Remediation Progress',
            supplement_loops: 'Supplement Loops',
            no_breach_data: 'No active breach cases.',
            audit_readiness: 'Audit & Assessment Readiness',
            assessments: 'Assessments',
            gap_findings: 'Gap Findings',
            evidence_completion: 'Evidence Completion',
            export_ready: 'Export Ready',
            not_ready: 'Not Ready',
            no_audit_data: 'No audit readiness data available.',
            framework_label_vn_pdpl: 'VN PDPL 2025',
            framework_label_vn_cybersecurity: 'VN Cybersecurity 2018',
            framework_label_vn_ecommerce: 'VN E-Commerce 2023',
            framework_label_vn_telecom: 'VN Telecom 2023',
            framework_label_vn_ai: 'VN AI Law 2025',
            framework_label_gdpr: 'GDPR',
            framework_label_unspecified: 'Unspecified',
            my_department: 'My Department',
            my_tasks: 'My Tasks',
            section_status: 'Section Status',
            revision_requests: 'Revision Requests',
            completion: 'Completion',
            reason: 'Reason',
            requested_at: 'Requested At',
            no_tasks_assigned: 'No tasks assigned to you.',
            no_sections: 'No sections assigned to your department.',
            no_revisions: 'No revision requests.',
            load_error: 'Failed to load data.',
        },
    },
    // ---------- Connectors module (Sprint 2.0.0) ----------
    connectors: {
        nav: {
            instances: 'Instances',
            credentials: 'Credential Vault',
            event_bus: 'Event Bus',
            definitions: 'Definitions',
            brds: 'BRD Capture',
            security_reviews: 'Security Reviews',
            poc_scorecards: 'POC Scorecards',
            automation_planning: 'Automation Planning',
            system_inventory: 'System Inventory',
            traceability_matrix: 'Traceability Matrix',
            gap_register: 'Gap Register',
        },
        instances: {
            title: 'Connector Instances',
            page_title: 'Connectors',
            add: 'Add Instance',
            test: 'Test Connection',
            sync_now: 'Sync Now',
            new_instance: 'New Connector Instance',
            no_instances: 'No connector instances found',
            instance_count: '{count} instance(s)',
            confirm_delete: 'Delete connector instance "{name}"?',
            all_connector_types: 'All Connector Types',
            select_connector: 'Select a connector...',
            fetch_error: 'Failed to fetch connector instances. Please try again.',
            fetch_definitions_error: 'Failed to fetch connector definitions. Please try again.',
            create_error: 'Failed to create connector instance',
            test_error: 'Failed to test connector. Please try again.',
            status_error: 'Failed to update connector status. Please try again.',
            delete_error: 'Failed to delete connector instance. Please try again.',
        },
        credentials: {
            title: 'Credential Vault',
            page_title: 'Connector Credentials',
            add: 'Add Credential',
            rotate: 'Rotate',
            expired: 'Expired',
            expiring: 'Expiring Soon',
            orphaned: 'Orphaned',
            new_credential: 'New Credential',
            no_credentials: 'No credentials found',
            no_expiry: 'No Expiry',
            linked_connectors: 'Linked Connectors',
            expires_at: 'Expires At',
            value_label: 'Value * (write-only)',
            confirm_delete_title: 'Confirm Delete',
            confirm_delete_message: 'Delete this credential? Linked connectors will lose access.',
            rotate_title: 'Rotate Credential',
            new_credential_value: 'New credential value',
            fetch_error: 'Failed to load credentials. Please try again.',
            health_error: 'Failed to load health. Please try again.',
            create_error: 'Failed to create credential',
            rotate_error: 'Failed to rotate credential',
            delete_error: 'Failed to delete credential',
        },
        event_bus: {
            title: 'Event Bus',
            page_title: 'Event Bus',
            events: 'Events',
            subscriptions: 'Subscriptions',
            dlq: 'Dead Letter Queue',
            publish: 'Publish Event',
            retry: 'Retry',
            dismiss: 'Dismiss',
            search_events: 'Search events...',
            filter_event_type: 'Filter by event type...',
            event_type: 'Event Type',
            source_module: 'Source Module',
            timestamp: 'Timestamp',
            delivery_status: 'Delivery Status',
            correlation_id: 'Correlation ID',
            tracking_id: 'Tracking ID',
            delivery_status_hint: 'Delivery status is updated automatically as events are processed.',
            no_events: 'No events found',
            total_with_count: '{count} total',
            prev: 'Prev',
            new_subscription: '+ New Subscription',
            subscriber_type: 'Subscriber Type',
            subscriber_id: 'Subscriber ID',
            event_type_pattern: 'Event Type Pattern',
            event_type_pattern_placeholder: 'e.g. incident.*',
            filter_expression: 'Filter Expression',
            filter_expression_placeholder: 'Optional filter',
            subscriber_type_hint: 'Choose how events will be delivered: webhook sends HTTP requests, email sends notifications, queue routes to a message queue.',
            subscriber_id_hint: 'Enter the destination for events. For webhooks, use a URL. For email, use an address. For queues, use the queue name.',
            event_type_pattern_hint: 'Match events by type using wildcards. Use * to match any segment (e.g. incident.* matches incident.created, incident.resolved).',
            filter_expression_hint: 'Optional. Further filter events by their content (e.g. severity=high).',
            subscriber_id_placeholder_webhook: 'e.g. https://your-app.com/webhook',
            subscriber_id_placeholder_email: 'e.g. alerts@your-company.com',
            subscriber_id_placeholder_queue: 'e.g. events-queue',
            event_pattern: 'Event Pattern',
            filter: 'Filter',
            enabled: 'Enabled',
            delivery_stats: 'Delivery Stats',
            no_subscriptions: 'No subscriptions found',
            on: 'ON',
            off: 'OFF',
            delivered_failed: '{delivered} delivered / {failed} failed',
            confirm_delete_subscription: 'Delete this subscription?',
            unresolved: 'Unresolved',
            resolved: 'Resolved',
            event_id: 'Event ID',
            subscription_id: 'Subscription ID',
            error_code: 'Error Code',
            error_message: 'Error Message',
            attempts: 'Attempts',
            last_failed: 'Last Failed',
            no_dlq_entries: 'No dead letter entries found',
            dlq_count: '{count} entry/entries total',
            fetch_events_error: 'Failed to fetch events. Please try again.',
            fetch_subscriptions_error: 'Failed to fetch subscriptions. Please try again.',
            fetch_dlq_error: 'Failed to fetch DLQ entries. Please try again.',
            create_subscription_error: 'Failed to create subscription',
            toggle_subscription_error: 'Failed to toggle subscription',
            delete_subscription_error: 'Failed to delete subscription',
            retry_error: 'Retry failed',
            dismiss_error: 'Dismiss failed',
            // fix-sprint-20260411-D F14 — semantic toast keys (replaces tKey fallbacks)
            event_published: 'Event published',
            publish_event_error: 'Failed to publish event. Please try again.',
            priority_label: 'Priority',
            priority_low: 'Low',
            priority_normal: 'Normal',
            priority_high: 'High',
            payload_helper_text: 'Enter event data as JSON. Example: {"incident_id": "INC-001", "severity": "high"}',
            subscription_created: 'Subscription created',
            subscription_toggled: 'Subscription toggled',
            subscription_deleted: 'Subscription deleted',
            dlq_retried: 'DLQ message retried',
            dlq_dismissed: 'DLQ message dismissed',
        },
        definitions: {
            title: 'Connector Definitions',
            page_title: 'Connector Definitions',
            add: 'Add Definition',
            new_definition: 'New Connector Definition',
            edit_definition: 'Edit Connector Definition',
            no_definitions: 'No connector definitions found',
            definition_count: '{count} definition(s)',
            all_categories: 'All Categories',
            select_category: 'Select a category...',
            capabilities: 'Capabilities',
            capabilities_hint: 'Capabilities (comma-separated)',
            capabilities_placeholder: 'e.g. read, write, sync',
            created: 'Connector definition created',
            updated: 'Connector definition updated',
            fetch_error: 'Failed to fetch connector definitions. Please try again.',
            create_error: 'Failed to create connector definition',
            update_error: 'Failed to update connector definition',
        },
        brds: {
            title: 'BRD Capture',
            page_title: 'BRD Capture',
            create_brd: 'Create BRD',
            brd_title: 'BRD Title',
            description: 'Description',
            requirements: 'Requirements',
            status: 'Status',
            stakeholders: 'Stakeholders',
            connector_label: 'Connector',
            select_connector: 'Select a connector...',
            approval: 'Approval',
            no_brds_found: 'No BRDs found',
            decision_records: 'Decision Records',
            create_decision: 'Create Decision Record',
            alternatives_considered: 'Alternatives Considered',
            alternative_name: 'Alternative Name',
            reason_rejected: 'Reason Rejected',
            add_alternative: 'Add Alternative',
            remove_alternative: 'Remove alternative {name}',
            alternatives: 'Alternatives',
            rationale: 'Rationale',
            selected_connector: 'Selected Connector',
            comma_separated_names: 'Comma-separated names',
            brd_created: 'BRD created',
            decision_record_created: 'Decision record created',
            no_decision_records_found: 'No decision records found',
            aria_brds_table: 'BRDs',
            aria_decision_records_table: 'Decision Records',
            aria_approve: 'Approve {name}',
            aria_archive: 'Archive {name}',
            brd_title_help: 'A short, human-readable name for this BRD — e.g. "Jira ticketing integration", "Okta SSO rollout".',
            brd_title_placeholder: 'e.g. Jira ticketing integration',
            description_help: 'The business problem this connector solves. What outcome do you expect once it is live?',
            description_placeholder: 'e.g. Automate incident ticket creation from monitoring alerts so on-call gets paged within 60s.',
            connector_help: 'Optional. Pick a connector this BRD is for — leave empty if the connector has not been chosen yet.',
            requirements_help: 'Comma-separated list of must-have capabilities — e.g. "OAuth2 login, webhook delivery, audit export".',
            requirements_placeholder: 'OAuth2 login, webhook delivery, audit export',
            stakeholders_help: 'Comma-separated names or emails of people who must sign off.',
            selected_connector_help: 'Pick the connector this decision applies to.',
            rationale_help: 'Why this connector was chosen. Cover the deciding factors so a future reader understands the call.',
            rationale_placeholder: 'e.g. Chose Jira because it already holds our ticket history and the team is trained on it.',
            alternatives_considered_help: 'Other connectors evaluated and why they were not picked. Adds defensibility for audit.',
            alternative_name_placeholder: 'e.g. ServiceNow',
            reason_rejected_placeholder: 'e.g. Cost 3× higher, no advantage for our use case',
        },
        security_reviews: {
            title: 'Security Reviews',
            page_title: 'Security Reviews',
            create_review: 'Create Security Review',
            connector_name: 'Connector Name',
            connector_label: 'Connector',
            select_connector: 'Select a connector...',
            reviewer: 'Reviewer',
            select_reviewer: 'Select a reviewer...',
            findings: 'Findings (one per line)',
            risk_level: 'Risk Level',
            risk_level_placeholder: 'e.g. high, medium, low',
            notes: 'Notes',
            review_status: 'Review Status',
            checklist: 'Checklist',
            privacy_impact: 'Privacy Impact',
            approval_gate: 'Approval Gate',
            no_reviews_found: 'No security reviews found',
            review_created: 'Security review created',
            create_control_mapping: 'Create Control Mapping',
            control_mapping_created: 'Control mapping created',
            connector_id_label: 'Connector ID',
            control_id_label: 'Control ID',
            control_name_label: 'Control Name',
            aria_approve: 'Approve {name}',
            aria_reject: 'Reject {name}',
            scan_imported: 'Scan imported successfully',
            import_scan: 'Import Scan',
            scan_format: 'Scan Format',
            scan_data: 'Scan Data',
            import_results: 'Import Results',
            recommendations: 'Recommendations',
            auto_populated_from: 'Auto-populated from {tool}',
            manual_entry: 'Manual entry',
            automation_type: 'Automation Type',
            select_automation_type: 'Select automation type...',
            automation_full: 'Full — connector fully automates this control',
            automation_partial: 'Partial — connector contributes evidence; humans still act',
            automation_monitoring: 'Monitoring only — connector observes, does not enforce',
            control_id_help: 'Use the code from your compliance scheme (e.g. ISO-27001-A.12.1, SOC2-CC6.1, or your internal control ID).',
            control_name_help: 'Short human-readable name (e.g. "Network access restriction").',
            description_label: 'Description',
            load_sarif_example: 'Load SARIF example',
            load_json_example: 'Load custom JSON example',
            scan_format_help: 'SARIF is the OASIS standard emitted by most scanners (Snyk, CodeQL, Semgrep). Use custom JSON for tools that do not emit SARIF.',
            scan_data_help: 'Paste the full scan output, upload a .sarif/.json file, or click a Load Example button to see the shape.',
            upload_file_label: 'Upload .sarif or .json file',
            invalid_scan_json: 'Could not parse scan data as JSON. Check for syntax errors near the highlighted character.',
            save_as_review: 'Save as Security Review',
            discard_preview: 'Discard preview',
            preview_hint: 'This is a preview — nothing is saved yet. Click "Save as Security Review" to create a pending review pre-filled with these values.',
            reviewer_required_for_save: 'Pick a reviewer to save this scan as a security review.',
        },
        poc_scorecards: {
            title: 'POC Scorecards',
            page_title: 'POC Scorecards',
            create_scorecard: 'Create Scorecard',
            connector_label: 'Connector',
            select_connector: 'Select a connector...',
            vendor_name: 'Vendor Name',
            recommendation: 'Recommendation',
            recommendation_placeholder: 'e.g. Proceed, Do not proceed, Needs further evaluation',
            criteria: 'Evaluation Criteria',
            criterion_name: 'Criterion Name',
            score: 'Score',
            max_score: 'Max Score',
            notes: 'Notes',
            add_criterion: 'Add Criterion',
            remove_criterion: 'Remove criterion {name}',
            total_score: 'Total Score',
            api_quality: 'API Quality',
            reliability: 'Reliability',
            support_quality: 'Support Quality',
            cost_score: 'Cost Score',
            overall_score: 'Overall Score',
            evidence: 'Evidence',
            no_scorecards_found: 'No POC scorecards found',
            scorecard_created: 'POC scorecard created',
            connector_help: 'Pick the connector this POC evaluation is for.',
            vendor_name_help: 'The vendor or product being evaluated — e.g. "Okta", "Datadog", "Snowflake".',
            vendor_name_placeholder: 'e.g. Okta, Datadog, Snowflake',
            recommendation_help: 'Your final call after the evaluation. Keep it short and decisive.',
            criteria_help: 'Score the vendor on each evaluation criterion. Add one row per criterion (API quality, reliability, support, cost, etc.).',
            criterion_name_help: 'What you are scoring — e.g. "API Quality", "Reliability", "Support", "Cost".',
            criterion_name_placeholder: 'e.g. API Quality',
            score_help: 'Score awarded for this criterion. Must not exceed Max Score.',
            max_score_help: 'Top score available for this criterion. Use the same scale across rows so totals are comparable.',
            notes_help: 'Optional. Add evidence, caveats, or links — anything that explains the score.',
        },
        common_labels: {
            aria_filter_instances: 'Filter connector instances',
            aria_filter_definitions: 'Filter connector definitions',
            aria_filter_credentials: 'Filter credentials',
            aria_filter_events: 'Filter events',
            aria_filter_dlq: 'Filter dead letter queue',
            aria_instance_summary: 'Connector instance summary',
            aria_active_connectors: 'Active connectors',
            aria_error_connectors: 'Error connectors',
            aria_credential_health: 'Credential health summary',
            aria_items_per_page: 'Items per page',
            show_password: 'Show password',
            hide_password: 'Hide password',
            payload_json_label: 'Payload (JSON)',
            payload_invalid_json: 'Payload must be valid JSON',
            placeholder_event_type: 'e.g. incident.created',
            placeholder_source_module: 'e.g. monitoring',
            placeholder_version: 'e.g. 1.0',
        },
        statuses: {
            configuring: 'Configuring',
            testing: 'Testing',
            active: 'Active',
            paused: 'Paused',
            error: 'Error',
            disabled: 'Disabled',
            draft: 'Draft',
            approved: 'Approved',
            archived: 'Archived',
            pending: 'Pending',
            rejected: 'Rejected',
        },
        system_types: {
            application: 'Application',
            database: 'Database',
            middleware: 'Middleware',
            infrastructure: 'Infrastructure',
            saas: 'SaaS',
            other: 'Other',
        },
        hosting_types: {
            on_premise: 'On-Premise',
            cloud: 'Cloud',
            hybrid: 'Hybrid',
            managed: 'Managed',
        },
        system_statuses: {
            active: 'Active',
            decommissioned: 'Decommissioned',
            planned: 'Planned',
            under_review: 'Under Review',
        },
        automation_statuses: {
            planning: 'Planning',
            in_progress: 'In Progress',
            completed: 'Completed',
            on_hold: 'On Hold',
        },
        gap_types: {
            functional: 'Functional',
            technical: 'Technical',
            security: 'Security',
            compliance: 'Compliance',
            process: 'Process',
        },
        severity_labels: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        gap_statuses: {
            open: 'Open',
            in_progress: 'In Progress',
            mitigated: 'Mitigated',
            closed: 'Closed',
            accepted: 'Accepted',
        },
        verification_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            verified: 'Verified',
            failed: 'Failed',
        },
        subscriber_types: {
            webhook: 'Webhook',
            internal: 'Internal',
            email: 'Email',
            queue: 'Queue',
        },
        credential_types: {
            password: 'Password',
            api_key: 'API Key',
            oauth2_client: 'OAuth2 Client',
            certificate: 'Certificate',
            token: 'Token',
            ssh_key: 'SSH Key',
            imap_credentials: 'IMAP Credentials',
        },
        categories: {
            hr: 'HR',
            crm: 'CRM',
            erp: 'ERP',
            cloud: 'Cloud',
            security: 'Security',
            itsm: 'ITSM',
            custom: 'Custom',
        },
        delivery_statuses: {
            pending: 'Pending',
            delivered: 'Delivered',
            partial: 'Partial',
            failed: 'Failed',
        },
        automation_planning: {
            title: 'Automation Planning',
            page_title: 'Automation Planning',
            program_name: 'Program Name',
            program_status: 'Status',
            description: 'Description',
            linked_artifacts: 'Linked Artifacts',
            create_program: 'Create Program',
            edit_program: 'Edit Program',
            no_programs_found: 'No automation programs found',
            program_created: 'Automation program created',
            program_updated: 'Automation program updated',
            target_date: 'Target Date',
            owner: 'Owner',
            select_status: 'Select a status...',
            aria_programs_table: 'Automation programs',
            aria_edit: 'Edit {name}',
            export_programs: 'Export Programs',
            title_label: 'Program Title',
            title_help: 'A short, human-readable name for the automation program — e.g. "Q3 incident-to-Jira rollout", "PDPL access-request automation".',
            title_placeholder: 'e.g. Q3 incident-to-Jira rollout',
            description_help: 'What this program covers and the outcome you expect when it is live. Optional but recommended.',
            description_placeholder: 'e.g. Wire monitoring alerts into Jira so on-call gets paged within 60s.',
            owner_label: 'Program Owner',
            owner_help: 'The person accountable for this program. They will receive notifications about progress and gates.',
            select_owner: 'Select an owner...',
            frameworks_in_scope: 'Frameworks in Scope',
            frameworks_in_scope_help: 'Compliance frameworks this program supports. Helps the audit trail show which regulations the automation is governed by.',
            select_frameworks: 'Select frameworks...',
            status_draft: 'Draft',
            status_active: 'Active',
            status_completed: 'Completed',
            status_archived: 'Archived',
            change_status: 'Change Status',
            status_updated: 'Status updated',
            aria_change_status: 'Change status for {name}',
        },
        system_inventory: {
            title: 'System Inventory',
            page_title: 'System Inventory',
            system_name: 'System Name',
            system_type: 'System Type',
            hosting: 'Hosting',
            status: 'Status',
            description: 'Description',
            create_system: 'Create System',
            edit_system: 'Edit System',
            no_systems_found: 'No systems found',
            system_created: 'System created',
            system_updated: 'System updated',
            select_type: 'Select a type...',
            select_hosting: 'Select hosting...',
            select_status: 'Select a status...',
            owner: 'Owner',
            department: 'Department',
            data_categories: 'Data Categories',
            data_categories_placeholder: 'Comma-separated, e.g. PII, Financial, Health',
            jurisdiction: 'Jurisdiction',
            jurisdiction_placeholder: 'e.g. EU, US, Global',
            interfaces: 'Interfaces',
            interfaces_placeholder: 'Comma-separated, e.g. REST API, SFTP, JDBC',
            owner_placeholder: 'Select an accountable owner...',
            department_placeholder: 'Select the responsible org unit...',
            system_name_hint: 'Use the business-recognized system or application name.',
            system_type_hint: 'Classify the system so reporting can group similar assets.',
            hosting_hint: 'Identify where the system runs for residency, security, and vendor review.',
            status_hint: 'Set the lifecycle state used by reviews, exports, and audit evidence.',
            owner_hint: 'Choose the active user accountable for system governance and follow-up tasks.',
            department_hint: 'Use the org unit responsible for operating or governing this system.',
            data_categories_hint: 'List the data classes processed by this system, separated by commas.',
            jurisdiction_hint: 'Record the main legal or hosting jurisdiction for compliance mapping.',
            interfaces_hint: 'List connector or integration paths used to exchange data.',
            description_hint: 'Summarize what the system does and why it matters to the data map.',
            owner_department_required: 'Owner and Department are required.',
            aria_systems_table: 'System inventory',
            aria_edit: 'Edit {name}',
            export_systems: 'Export Systems',
        },
        traceability_matrix: {
            title: 'Traceability Matrix',
            page_title: 'Traceability Matrix',
            brd_selector: 'Select BRD',
            requirement: 'Requirement',
            linked_artifact: 'Linked Artifact',
            verification_status: 'Verification Status',
            verified_by: 'Verified By',
            create_entry: 'Create Entry',
            edit_entry: 'Edit Entry',
            no_entries_found: 'No traceability entries found',
            entry_created: 'Traceability entry created',
            entry_updated: 'Traceability entry updated',
            select_brd: 'Select a BRD...',
            select_status: 'Select a status...',
            aria_matrix_table: 'Traceability matrix',
            aria_edit: 'Edit entry for {name}',
            export_matrix: 'Export Matrix',
        },
        gap_register: {
            title: 'Gap Register',
            page_title: 'Gap Register',
            gap_title: 'Gap Title',
            gap_type: 'Gap Type',
            severity: 'Severity',
            status: 'Status',
            description: 'Description',
            remediation_plan: 'Remediation Plan',
            assigned_to: 'Assigned To',
            due_date: 'Due Date',
            create_gap: 'Create Gap',
            edit_gap: 'Edit Gap',
            no_gaps_found: 'No gaps found',
            gap_created: 'Gap created',
            gap_updated: 'Gap updated',
            select_type: 'Select a type...',
            select_severity: 'Select severity...',
            select_status: 'Select a status...',
            aria_gaps_table: 'Gap register',
            aria_edit: 'Edit {name}',
            export_gaps: 'Export Gaps',
            gap_title_hint: 'Provide a concise name for the identified gap.',
            gap_type_hint: 'Select the nature of the gap (e.g., missing coverage or integration issue).',
            severity_hint: 'The impact of this gap on compliance and operations.',
            status_hint: 'Current stage of the gap resolution lifecycle.',
            owner_hint: 'The person responsible for tracking and remediating this gap.',
            description_hint: 'Detailed context regarding the gap, its root cause, and impact.',
            mitigation_plan_hint: 'Steps planned or taken to address and close the gap.',
            linked_requirement_ids_hint: 'Comma-separated requirement codes this gap affects.',
            linked_system_ids_hint: 'Comma-separated system IDs associated with this gap.',
            gap_types: {
                coverage: 'Coverage',
                capability: 'Capability',
                integration: 'Integration',
                compliance: 'Compliance',
            },
        },
        regulatory: {
            system_inventory_governance_info: 'Each system in this inventory aligns with the Data Law enterprise governance model. System types (Application, Database, Middleware, Infrastructure, SaaS) map to regulatory categories, and the hosting classification (On-Premise, Cloud, Hybrid, Managed) determines which jurisdiction-specific controls apply. Use the framework linkage field to connect each system to applicable regulatory frameworks.',
            automation_planning_workflow_info: 'Automation programs align with compliance workflow obligations defined by applicable regulatory frameworks. The framework scope field determines which framework requirements the program addresses, and procedure linkage connects each program to documented compliance procedures. This ensures automation initiatives maintain a traceable chain from regulatory mandate to implementation.',
            traceability_regulatory_source_info: 'Each traceability entry connects a BRD requirement to its regulatory source. The regulatory source field identifies the specific regulation, directive, or standard that mandates the requirement. Verification status tracks whether the implementation has been confirmed to satisfy the regulatory obligation.',
            security_reviews_control_mapping_info: 'Security reviews evaluate framework-specific controls applicable to each connector. The risk level field captures the assessed exposure, and findings document deviations from control requirements. Use the control mapping feature to link each review to the specific framework controls (e.g., ISO 27001 A.12, GDPR Art. 32) that were evaluated.',
            gap_register_framework_info: 'The Gap Register captures compliance gaps per regulatory framework. Use the framework filter to view gaps for a specific framework. Severity levels (Critical, High, Medium, Low) align with the framework risk classification, and the status workflow (Open, In Progress, Mitigated, Closed, Accepted) tracks remediation progress through the compliance lifecycle.',
            poc_scorecards_criteria_info: 'POC scorecards evaluate connectors against regulatory criteria specific to the applicable framework. Criteria structure supports weighted scoring with evidence notes. The vendor linkage field connects the evaluation to the vendor record, and the recommendation field provides the compliance-informed procurement decision.',
            evidence_trail_info: 'All connector module actions generate audit events for cross-module evidence trail integrity. CSV exports preserve referential integrity by including linked entity identifiers. Audit events are traceable across System Inventory, Automation Planning, Traceability Matrix, Security Reviews, Gap Register, and POC Scorecards.',
        },
        system_inventory_extra: {
            framework_linkage: 'Framework Linkage',
            framework_linkage_placeholder: 'e.g. GDPR, ISO 27001, NIS2',
        },
        automation_planning_extra: {
            framework_scope: 'Framework Scope',
            framework_scope_placeholder: 'e.g. GDPR, PCI-DSS',
            procedure_linkage: 'Procedure Linkage',
            procedure_linkage_placeholder: 'e.g. SOP-001, PROC-DATA-003',
        },
        traceability_matrix_extra: {
            regulatory_source: 'Regulatory Source',
            regulatory_source_placeholder: 'e.g. GDPR Art. 30, ISO 27001 A.8',
        },
        security_reviews_extra: {
            control_mapping_col: 'Control Mapping',
            control_mappings_section: 'Control Mappings',
            no_control_mappings: 'No control mappings yet. Click "Create Control Mapping" to add one.',
            automation_type_col: 'Automation',
        },
        gap_register_extra: {
            framework_filter: 'Framework',
            framework_id: 'Framework ID',
            framework_id_placeholder: 'e.g. GDPR, ISO 27001',
            all_frameworks: 'All Frameworks',
        },
        poc_scorecards_extra: {
            criteria_count: 'Criteria',
        },
        export_labels: {
            export_brds: 'Export BRDs',
            export_decision_records: 'Export Decision Records',
            export_security_reviews: 'Export Security Reviews',
            export_poc_scorecards: 'Export POC Scorecards',
            export_instances: 'Export Instances',
            export_definitions: 'Export Definitions',
            export_credentials: 'Export Credentials',
            export_events: 'Export Events',
            exporting: 'Exporting...',
            export_success: 'Export completed',
            export_error: 'Export failed',
        },
    },
    // ---------- Vendor Risk Scoring module (Sprint 2.1.0) ----------
    vendor_risk: {
        // Sprint 48.2.4 — Forseti risk acceptance reasoning generator
        risk_acceptance_reasoning: {
            draft_label: 'Draft reasoning',
            drafting: 'Drafting reasoning…',
            results_heading: 'AI-drafted reasoning ({risk_level})',
            tokens_label: '≈ {count} tokens',
            narrative_label: 'Justification narrative',
            controls_heading: 'Compensating controls',
            no_controls: 'No compensating controls suggested.',
            citations_label: 'Citations',
            accept_label: 'Accept reasoning',
            reject_label: 'Reject draft',
            accept_hint: 'Accept records the reasoning on the persisted draft for IAT lineage. The narrative + controls do not auto-update the acceptance record — operator paste-back is required.',
            resolve_summary: 'Resolution: {status}',
            error_generic: 'Could not draft reasoning. Try again or check the risk acceptance status.',
        },
        // Sprint 48.2.3 — Forseti contract redline assistant + clause library
        redline_assistant: {
            run_label: 'Redline with library',
            running: 'Running redline…',
            results_heading: 'Redline suggestions ({count})',
            tokens_label: '≈ {count} tokens',
            no_suggestions: 'No standard-clause gaps detected.',
            jurisdiction_label: 'Jurisdiction',
            col_kind: 'Kind',
            col_severity: 'Severity',
            col_title: 'Suggestion',
            col_excerpt: 'Matched excerpt',
            col_apply: 'Apply',
            kind_missing: 'Missing standard',
            kind_weaker: 'Weaker than standard',
            kind_jurisdiction: 'Jurisdiction overlay',
            proposed_label: 'Proposed text',
            citations_label: 'Citations',
            accept_label: 'Accept selected',
            reject_label: 'Reject report',
            partial_label: 'Apply selection',
            accept_hint: 'Apply selection records the chosen suggestions on the draft for IAT lineage. No automatic edit is applied to the contract text — the operator drives the redline.',
            resolve_summary: 'Resolution: {status} · {count} suggestion(s) applied',
            error_generic: 'Could not produce a redline. Try again or check the contract status.',
            clause_library_link: 'Manage clause library',
        },
        // Sprint 48.2.3 — Clause library admin page
        clause_library: {
            page_title: 'Clause Library',
            add_label: 'Add clause',
            edit_label: 'Edit',
            delete_label: 'Delete',
            slug_label: 'Slug',
            title_label: 'Title',
            body_label: 'Body',
            jurisdiction_label: 'Jurisdiction',
            tags_label: 'Tags (comma separated)',
            must_contain_label: 'Must-contain markers (comma separated)',
            severity_label: 'Severity when missing',
            no_clauses: 'No clauses yet. Add one to seed the redline assistant.',
            save_label: 'Save',
            cancel_label: 'Cancel',
            load_error: 'Could not load clause library.',
            save_error: 'Could not save clause.',
            delete_error: 'Could not delete clause.',
            delete_confirm: 'Delete clause "{slug}"? This cannot be undone.',
        },
        // Sprint 48.2.2 — Forseti contract obligation extraction panel
        obligation_extraction: {
            extract_label: 'Extract obligations',
            extracting: 'Extracting…',
            results_heading: 'Extracted obligations ({count})',
            tokens_label: '≈ {count} tokens',
            no_obligations: 'No obligation patterns detected in this contract text.',
            col_class: 'Class',
            col_summary: 'Summary (editable)',
            col_excerpt: 'Clause excerpt',
            col_due_date: 'Due date (editable)',
            col_priority: 'Priority',
            due_date_placeholder: 'YYYY-MM-DD',
            class_notice: 'Notice period',
            class_audit: 'Audit right',
            class_insurance: 'Insurance',
            class_sla: 'Response SLA',
            class_breach: 'Breach notification',
            class_renewal: 'Renewal window',
            citations_label: 'Citations',
            accept_hint: 'Accept will create one task per obligation (cap 8) with the priority + due date shown.',
            accept_label: 'Accept obligations',
            reject_label: 'Reject report',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created',
            error_generic: 'Could not extract obligations. Try again or check the contract text.',
        },
        // Sprint 48.2.1 — Forseti sub-processor change impact panel
        subprocessor_impact: {
            assess_label: 'Assess impact',
            assessing: 'Assessing impact…',
            results_heading: 'Sub-processor impact ({severity})',
            tokens_label: '≈ {count} tokens',
            risk_delta_label: 'Risk delta',
            notification_heading: 'Customer notification',
            subject_label: 'Subject',
            deadline_label: 'Notification deadline',
            body_label: 'Notification body',
            required_fields_label: 'Required Art.28(4) fields',
            mitigation_heading: 'Mitigation steps',
            citations_label: 'Citations',
            accept_label: 'Accept assessment',
            reject_label: 'Reject assessment',
            accept_hint: 'Accept will create a customer-notification task with the SLA deadline as the due date.',
            resolve_summary: 'Resolution: {status}{task}',
            error_generic: 'Could not assess this sub-processor change. Try again or check the change record.',
        },
        // Sprint 48.2.0 — Forseti onboarding evidence collector
        onboarding_evidence: {
            collect_label: 'Run AI evidence collection',
            collecting: 'Collecting evidence…',
            results_heading: 'Evidence collection ({tier})',
            tokens_label: '≈ {count} tokens',
            findings_heading: 'Existing evidence',
            missing_heading: 'Missing artefacts',
            no_findings: 'No existing evidence detected.',
            no_missing: 'No required artefacts missing for this risk tier.',
            next_step_label: 'Next step',
            class_dpa: 'Data Processing Agreement',
            class_soc2: 'SOC 2 report',
            class_iso27001: 'ISO 27001 certificate',
            class_insurance: 'Cyber insurance policy',
            class_incident_history: 'Prior incident',
            accept_hint: 'Accept will create tasks for each missing artefact (capped at 5). Findings remain draft until you attach them as evidence.',
            accept_label: 'Accept report',
            reject_label: 'Reject report',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created',
            error_generic: 'Could not run AI evidence collection. Try again or check the onboarding case status.',
        },
        no_permission_title: 'Insufficient Permissions',
        no_permission_message: 'You do not have permission to access vendor risk management.',
        unknown_vendor: 'Unknown Vendor',
        nav: {
            dashboard: 'Risk Dashboard',
            scoring: 'Risk Scoring',
            questionnaires: 'Questionnaires',
            contracts: 'Contracts',
            gates: 'Approval Gates',
            reviews: 'Vendor Reviews',
            signals: 'Risk Signals',
            reassessments: 'Reassessments',
            portfolio: 'Risk Portfolio',
            acceptances: 'Risk Acceptance',
            review_routing: 'Review Routing',
            onboarding_cases: 'Onboarding Cases',
            sub_processor_register: 'Sub-Processor Register',
        },
        dashboard: {
            title: 'Vendor Risk Dashboard',
            page_title: 'Vendor Risk Dashboard',
            loading: 'Loading dashboard data...',
            risk_tier_header: 'Risk Tier',
            count_header: 'Count',
            percentage_header: 'Percentage',
            sections_nav: 'Vendor risk sections',
            urgent_expiry: 'Urgent: expires within 14 days',
            no_previous_tier: 'No previous tier',
            view_as_chart: 'View as chart',
            view_as_table: 'View as table',
            showing_chart: 'Showing chart view',
            showing_table: 'Showing data table view',
            load_error: 'Failed to load vendor risk dashboard',
            risk_distribution: 'Risk Distribution Overview',
            recent_tier_changes: 'Recent Tier Changes',
            no_tier_changes: 'No recent tier changes.',
            review_kpis: 'Review KPIs',
            total_vendors_reviewed: 'Total Vendors Reviewed',
            reviews_by_outcome: 'Reviews by Outcome',
            contract_coverage: 'Contract Coverage',
            total_vendors: 'Total Vendors',
            with_active_dpa: 'With Active DPA',
            without_dpa: 'Without DPA',
            dpa_coverage: 'DPA Coverage',
            active_contracts_by_type: 'Active Contracts by Type',
            expiring_contracts: 'Expiring Contracts (Next 90 Days)',
            no_expiring_contracts: 'No contracts expiring within the next 90 days.',
            vendor: 'Vendor',
            previous_tier: 'Previous Tier',
            current_tier: 'Current Tier',
            changed_at: 'Changed At',
            expires_at: 'Expires At',
            days_remaining: 'Days Remaining',
            days: 'days',
            vendors_label: 'vendors',
            no_data: 'No data',
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
            expiring_acceptances_heading: 'Expiring risk acceptances',
            expiring_30d_label: 'Expiring in 30 days',
            expiring_60d_label: 'Expiring in 60 days',
            expiring_90d_label: 'Expiring in 90 days',
        },
        scoring: {
            title: 'Risk Scoring',
            page_title: 'Vendor Risk Scoring',
            filter_scores: 'Filter risk scores',
            filter_by_tier: 'Filter by risk tier',
            scores_pagination: 'Risk scores pagination',
            config_dimensions: 'Scoring dimensions configuration',
            scores_loaded: '{count} risk scores loaded',
            weight_must_equal_100: 'Weight total must equal 100 (current: {current})',
            out_of_10: 'out of 10, rated {tier}',
            dimension_name: 'Dimension {index} name',
            dimension_weight: '{name} weight (percentage)',
            must_equal_100: 'must equal 100',
            config_title: 'Scoring Configuration',
            scores_title: 'Vendor Risk Scores',
            new_score_title: 'Score a Vendor',
            new: 'New Score',
            vendor_id: 'Vendor ID',
            risk_tier: 'Risk Tier',
            score: 'Score',
            scored_at: 'Scored At',
            no_scores: 'No risk scores found.',
            all_tiers: 'All Tiers',
            load_config_error: 'Failed to load risk scoring configuration',
            update_config: 'Update Scoring Config',
            update_config_error: 'Failed to update risk scoring configuration',
            load_scores_error: 'Failed to load vendor risk scores',
            calculate_error: 'Failed to calculate vendor risk score',
            weight: 'weight',
            score_range: 'Score (1-5)',
            method: 'Method',
        },
        questionnaires: {
            title: 'Questionnaires',
            page_title: 'Vendor Questionnaires',
            filter_questionnaires: 'Questionnaire filters',
            questionnaire_views: 'Questionnaire views',
            templates_list: 'Questionnaire templates list',
            instances_list: 'Questionnaire instances list',
            filter_by_status: 'Filter by questionnaire status',
            templates_per_page: 'Templates per page',
            instances_per_page: 'Instances per page',
            select_template: 'Select questionnaire template',
            template_category: 'Template category',
            templates_tab: 'Templates',
            instances_tab: 'Instances',
            new: 'New Questionnaire',
            new_template: 'New Template',
            new_instance: 'New Instance',
            vendor_id: 'Vendor ID',
            template_id: 'Template ID',
            assigned_to: 'Assigned To',
            due_date: 'Due Date',
            responses: 'Responses',
            risk_score: 'Risk Score',
            no_questionnaires: 'No questionnaires found.',
            load_templates_error: 'Failed to load questionnaire templates',
            load_instances_error: 'Failed to load questionnaires',
            create_template_error: 'Failed to create questionnaire template',
            initiate_error: 'Failed to initiate questionnaire',
            submit_error: 'Failed to submit questionnaire',
            review_error: 'Failed to review questionnaire',
            resubmit: 'Resubmit',
            approve_error: 'Failed to approve questionnaire',
            reject_error: 'Failed to reject questionnaire',
            resubmit_error: 'Failed to resubmit questionnaire',
            templates_loaded: '{count} questionnaire templates loaded',
            instances_loaded: '{count} questionnaire instances loaded',
            fill_responses_cta: 'Fill responses',
            fill_responses_title: 'Fill questionnaire responses',
            template_missing: 'Template not found.',
            responses_saved: 'Responses saved.',
            load_template_error: 'Failed to load template.',
            // Sprint 41.2.0 E9 — version history disclosure for file_upload responses
            version_history: 'Version history',
            download_previous_version: 'Download previous version',
            // Sprint 43.6.0 W1 — Trigger label rename, Reviewer selector, choice renderers
            trigger: 'Trigger',
            template_label: 'Template',
            reviewer: 'Reviewer',
            reviewer_required: 'A reviewer must be assigned for {trigger} questionnaires (separation of duties).',
            select_option: 'Select an option',
            multi_choice_hint: 'Select all that apply',
            category_initial_onboarding: 'Initial onboarding',
            category_annual_review: 'Annual review',
            category_incident_triggered: 'Incident-triggered',
            category_contract_renewal: 'Contract renewal',
            // Sprint 43.6.0 W2 — Template Builder UI keys
            template_editor: {
                page_title: 'Edit questionnaire template',
                preview_page_title: 'Preview questionnaire template',
                edit_structure: 'Edit structure',
                preview: 'Preview',
                actions: 'Actions',
                back_to_list: 'Back to templates',
                load_error: 'Failed to load template',
                save_error: 'Failed to save template structure',
                saved: 'Template structure saved.',
                sections_heading: 'Sections',
                questions_heading: 'Questions',
                add_section: 'Add section',
                edit_section: 'Edit section',
                delete_section: 'Delete section',
                move_section_up: 'Move section up',
                move_section_down: 'Move section down',
                confirm_delete_section: 'Delete section "{title}"? All its questions will be removed.',
                section_title: 'Section title',
                section_description: 'Section description',
                section_id: 'Section ID',
                section_id_hint: 'Auto-generated unless you set one explicitly.',
                no_sections: 'No sections yet. Add a section to begin.',
                no_questions: 'No questions in this section yet.',
                add_question: 'Add question',
                edit_question: 'Edit question',
                delete_question: 'Delete question',
                move_question_up: 'Move question up',
                move_question_down: 'Move question down',
                confirm_delete_question: 'Delete question "{text}"?',
                question_text: 'Question text',
                question_type: 'Question type',
                question_id: 'Question ID',
                evidence_required: 'Evidence required',
                risk_weight: 'Risk weight',
                scoring_map: 'Scoring map',
                scoring_map_json_hint: 'JSON object mapping answer values to numeric scores.',
                scoring_map_invalid_json: 'Scoring map must be valid JSON.',
                options_heading: 'Answer options',
                add_option: 'Add option',
                remove_option: 'Remove option',
                option_label: 'Label',
                option_value: 'Value',
                option_risk_weight: 'Risk weight',
                min_options_error: 'Choice questions must have at least 2 options.',
                option_label_required: 'Each option needs a label.',
                option_value_required: 'Each option needs a value.',
                option_value_unique: 'Option values must be unique.',
                branching_rules_heading: 'Branching rules',
                branching_rules_hint: 'Show or hide this question based on the answer to an earlier question.',
                add_branching_rule: 'Add branching rule',
                remove_branching_rule: 'Remove branching rule',
                branching_condition_question: 'If question',
                branching_condition_value: 'has value',
                branching_action: 'then',
                branching_action_show: 'show this question',
                branching_action_hide: 'hide this question',
                branching_select_condition_question: 'Select earlier question',
                branching_select_condition_value: 'Select value',
                save: 'Save structure',
                saving: 'Saving...',
                cancel: 'Cancel',
                close: 'Close',
                question_text_required: 'Question text is required.',
                section_title_required: 'Section title is required.',
                // Question type display labels
                type_single_choice: 'Single choice',
                type_multi_choice: 'Multiple choice',
                type_free_text: 'Free text',
                type_numeric: 'Numeric',
                type_date: 'Date',
                type_file_upload: 'File upload',
                type_yes_no: 'Yes / No',
                preview_heading: 'Template preview',
                preview_subtitle: 'Read-only preview of how the questionnaire appears to a vendor.',
                preview_no_content: 'This template has no sections or questions yet.',
                preview_branching_notice: 'Branching rules are evaluated during fill-out; preview shows every question.',
            },
            // Sprint 43.6.0 W3 — Review interface keys
            review_modal: {
                title: 'Review questionnaire responses',
                open_cta: 'Review',
                loading: 'Loading questionnaire...',
                template_missing: 'Template not found.',
                load_error: 'Failed to load questionnaire for review.',
                annotation_heading: 'Annotations',
                annotation_placeholder: 'Add a reviewer annotation for this question...',
                annotation_status_label: 'Annotation status',
                annotation_status_acceptable: 'Acceptable',
                annotation_status_concern: 'Concern',
                annotation_status_requires_action: 'Requires action',
                review_comments_label: 'Overall review comments',
                review_comments_placeholder: 'Summarize your review findings...',
                justification_label: 'Decision justification',
                justification_placeholder: 'Explain the rationale for your decision...',
                reject_requires_comments: 'Rejecting a questionnaire requires review comments.',
                decision_heading: 'Decision',
                decision: {
                    approved: 'Approve',
                    rejected: 'Reject',
                    additional_info_requested: 'Request additional information',
                },
                submit: 'Submit review',
                submitting: 'Submitting review...',
                cancel: 'Cancel',
                submit_success: 'Review submitted.',
                submit_error: 'Failed to submit review.',
                separation_of_duties_error: 'You cannot review a questionnaire you were assigned to complete. Separation of duties requires a different reviewer.',
                decision_required: 'Please select a review decision.',
                announce_opened: 'Review modal opened.',
                announce_submitted: 'Review submitted.',
            },
            // Sprint 43.6.0 W3 — Instance detail review-field labels
            instance_detail: {
                submitted_at: 'Submitted',
                reviewed_at: 'Reviewed',
                reviewer: 'Reviewer',
                review_decision: 'Decision',
                review_comments: 'Review comments',
                decision_approved: 'Approved',
                decision_rejected: 'Rejected',
                decision_additional_info_requested: 'More info requested',
                decision_pending: 'Pending review',
                expand_comments: 'Show full comments',
                collapse_comments: 'Hide full comments',
                no_reviewer: 'No reviewer assigned',
            },
            // Sprint 43.6.0 W4 — Export + branching i18n
            export: {
                portfolio_label: 'Export portfolio',
                instance_label: 'Export',
                portfolio_success: 'Portfolio export ready.',
                instance_success: 'Questionnaire export ready.',
                portfolio_error: 'Failed to export questionnaire portfolio.',
                instance_error: 'Failed to export questionnaire.',
            },
            branching: {
                hidden_notice: 'Some questions are hidden by branching rules.',
                cycle_warning: 'A cycle was detected in branching rules. Affected questions default to visible.',
            },
        },
        contracts: {
            title: 'Contracts',
            page_title: 'Vendor Contracts',
            new: 'New Contract',
            vendor_id: 'Vendor ID',
            filter_contracts: 'Filter contracts',
            filter_by_document_type: 'Filter by document type',
            filter_by_status: 'Filter by contract status',
            per_page: 'Contracts per page',
            expired_notice: 'This contract has expired',
            document_type: 'Document Type',
            expiry_date: 'Expiry Date',
            no_contracts: 'No contracts found.',
            all_document_types: 'All Document Types',
            load_error: 'Failed to load contracts',
            create_error: 'Failed to create contract',
            delete_error: 'Failed to delete contract',
            update_error: 'Failed to update status',
            confirm_delete: 'Delete contract "{title}"?',
            renewal_date: 'Renewal Date',
            effective_date: 'Effective Date',
            parties: 'Parties',
            summary: 'Summary',
            n_contracts: '{count} contracts',
            n_contract: '{count} contract',
            contracts_loaded: '{count} contracts loaded',
            doc_type_dpa: 'DPA',
            doc_type_security_addendum: 'Security Addendum',
            doc_type_scc_annex: 'SCC Annex',
            doc_type_sub_processor_list: 'Sub-Processor List',
            doc_type_bcr: 'BCR',
            doc_type_nda: 'NDA',
            doc_type_msa: 'MSA',
            doc_type_sla: 'SLA',
            doc_type_other: 'Other',
            status_draft: 'Draft',
            status_under_negotiation: 'Under Negotiation',
            status_pending_signature: 'Pending Signature',
            status_active: 'Active',
            status_expired: 'Expired',
            status_terminated: 'Terminated',
            status_superseded: 'Superseded',
        },
        gates: {
            title: 'Approval Gates',
            page_title: 'Gate Evaluations',
            new: 'New Gate Evaluation',
            vendor_id: 'Vendor ID',
            gate_name: 'Gate Name',
            outcome: 'Outcome',
            evaluator: 'Evaluator',
            no_gates: 'No gate evaluations found.',
            load_error: 'Failed to load gate evaluations',
            evaluate_error: 'Failed to evaluate vendor gate',
            detail_error: 'Failed to fetch evaluation detail',
            override_error: 'Failed to override gate evaluation',
            criteria_results: 'Criteria Results',
            evaluate_form: 'Evaluate vendor gate',
            per_page: 'Evaluations per page',
            passed: 'Passed',
            failed: 'Failed',
            override: 'Override',
            override_justification: 'Override Justification',
            override_placeholder: 'Enter justification for overriding this gate evaluation',
            approval_id: 'Approval ID',
            overall_passed: 'Passed',
            overall_failed: 'Failed',
            overall_overridden: 'Overridden',
            evaluations_loaded: '{count} gate evaluations loaded',
            create_approval_cta: 'Create approval',
        },
        reviews: {
            title: 'Vendor Reviews',
            page_title: 'Vendor Reviews',
            new: 'New Review',
            vendor_id: 'Vendor ID',
            filter_by_outcome: 'Filter by review outcome',
            per_page: 'Reviews per page',
            kpi_summary: 'Review KPI summary',
            review_outcome_label: 'Review outcome',
            edit_outcome: 'Edit review outcome',
            reviews_loaded: '{count} vendor reviews loaded',
            reviewer: 'Reviewer',
            review_type: 'Review Type',
            outcome: 'Outcome',
            findings: 'Findings',
            no_reviews: 'No vendor reviews found.',
            load_kpis_error: 'Failed to fetch vendor review KPIs',
            load_error: 'Failed to fetch vendor reviews',
            update_error: 'Failed to update vendor review',
            create_error: 'Failed to create vendor review',
            total_reviewed: 'Total Vendors Reviewed',
            total_reviews: 'Total Reviews',
            satisfactory: 'Satisfactory',
            concerns_identified: 'Concerns Identified',
            remediation_required: 'Remediation Required',
            escalated: 'Escalated',
            program_id: 'Program ID',
            period_id: 'Period ID',
            task_id: 'Task ID',
            recommendations: 'Recommendations',
        },
        risk_signals: {
            title: 'Risk Signals',
            page_title: 'Risk Signals',
            new_signal: 'New Risk Signal',
            signal_type: 'Signal Type',
            severity: 'Severity',
            description: 'Description',
            source: 'Source',
            vendor_id: 'Vendor ID',
            certification_expiry: 'Certification Expiry',
            breach_notification: 'Breach Notification',
            financial_instability: 'Financial Instability',
            compliance_change: 'Compliance Change',
            custom: 'Custom',
            active: 'Active',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            no_signals: 'No risk signals found',
            source_external_feed: 'External Feed',
            source_manual: 'Manual',
            source_integration: 'Integration',
            source_regulatory: 'Regulatory',
            source_media: 'Media',
            trigger_reassessment_prompt: 'This signal is high severity. Start a vendor reassessment now?',
            reassessment_triggered: 'Reassessment triggered.',
            reassessment_trigger_error: 'Failed to trigger reassessment.',
        },
        reassessments: {
            title: 'Reassessment Tasks',
            page_title: 'Reassessment Tasks',
            trigger: 'Trigger Reassessment',
            trigger_success: 'Reassessment triggered successfully',
            reason: 'Reason',
            deadline: 'Deadline',
            assigned_to: 'Assigned To',
            vendor_id: 'Vendor ID',
            outcome: 'Outcome',
            new_risk_level: 'New Risk Level',
            complete: 'Complete Reassessment',
            complete_success: 'Reassessment completed successfully',
            pending: 'Pending',
            in_progress: 'In Progress',
            completed: 'Completed',
            cancelled: 'Cancelled',
            no_tasks: 'No reassessment tasks found',
            risk_trend: 'Risk Trend',
            notes: 'Notes',
        },
        portfolio: {
            title: 'Risk Portfolio',
            page_title: 'Risk Portfolio',
            dashboard: 'Portfolio Dashboard',
            vendor_id: 'Vendor ID',
            total_vendors: 'Total Vendors',
            average_score: 'Average Risk Score',
            high_risk_count: 'High Risk Vendors',
            trend_direction: 'Trend Direction',
            improved: 'Improved',
            stable: 'Stable',
            degraded: 'Degraded',
            vendor_trend: 'Vendor Trend',
            outstanding_questionnaires: 'Outstanding Questionnaires',
            overdue_assessments: 'Overdue Assessments',
            no_outstanding: 'No outstanding questionnaires',
            no_overdue: 'No overdue assessments',
            due_date: 'Due Date',
        },
        risk_acceptance: {
            title: 'Risk Acceptance Register',
            page_title: 'Risk Acceptance Register',
            new_acceptance: 'New Risk Acceptance',
            vendor_id: 'Vendor ID',
            risk_level: 'Risk Level',
            conditions: 'Conditions',
            justification: 'Justification',
            expiry_date: 'Expiry Date',
            create_success: 'Risk acceptance created successfully',
            review_condition: 'Review Condition',
            condition_met: 'Condition Met',
            condition_not_met: 'Condition Not Met',
            review_success: 'Condition reviewed successfully',
            export_register: 'Export Register',
            active: 'Active',
            expired: 'Expired',
            revoked: 'Revoked',
            no_acceptances: 'No risk acceptances found',
            met_count: '{met}/{total} met',
        },
        review_routing: {
            title: 'Review Routing',
            page_title: 'Review Routing',
            route_to_reviewers: 'Route to Reviewers',
            route_success: 'Routed to reviewers successfully',
            vendor_id: 'Vendor ID',
            reviewer_id: 'Reviewer ID',
            reviewer_role: 'Reviewer Role',
            questionnaire_id: 'Questionnaire ID',
            review_status: 'Review Status',
            reviewers_label: 'Reviewers (user_id, role per line)',
            pending: 'Pending',
            in_review: 'In Review',
            completed: 'Completed',
            no_routing: 'No review routing found',
            primary_reviewer: 'Primary Reviewer',
            secondary_reviewer: 'Secondary Reviewer',
            approver: 'Approver',
        },
        onboarding: {
            title: 'Vendor Onboarding Cases',
            page_title: 'Vendor Onboarding Cases',
            new_case: 'New Onboarding Case',
            case_detail: 'Onboarding Case',
            // Sprint 41.2.0 E8 — UC-VN-14 Step 10 MPS dossier filing from case
            open_mps_dossier_filing: 'Open MPS Dossier Filing',
            dossier_filing_description: 'Create a new MPS dossier filing pre-filled from this onboarding case. The filing will be linked back on the case timeline.',
            dossier_filing_dpia_id: 'DPIA ID',
            dossier_filing_dpia_id_placeholder: 'e.g. dpia_01TEST000000000000000001',
            dossier_filing_title: 'Filing title',
            dossier_filing_form_type: 'Form type',
            dossier_filing_submit: 'Create filing',
            dossier_filing_validation_error: 'DPIA ID and filing title are required.',
            dossier_filing_created: 'MPS dossier filing created',
            dossier_filing_error: 'Failed to create MPS dossier filing.',
            // Sprint 41.2.0 E10 — Enhanced tab summary metadata
            linked_questionnaire_due_date: 'Due date',
            linked_questionnaire_assignee: 'Assignee',
            linked_questionnaire_submitted_at: 'Submitted at',
            score_last_scored: 'Last scored',
            score_dimensions: 'Dimensions',
            linked_contract_document_type: 'Document type',
            linked_contract_effective_date: 'Effective date',
            linked_contract_expiry_date: 'Expiry date',
            linked_sub_processor_countries: 'Processing countries',
            linked_sub_processor_last_assessment: 'Last assessment',
            linked_sub_processor_next_assessment: 'Next assessment',
            case_id: 'Case ID',
            vendor: 'Vendor',
            case_type: 'Case Type',
            service_description: 'Service Description',
            data_categories: 'Data Categories',
            systems_accessed: 'Systems Accessed',
            jurisdiction: 'Jurisdiction',
            hosting_model: 'Hosting Model',
            controller_role: 'Controller/Processor Role',
            status_pipeline: 'Status Pipeline',
            tabs_label: 'Onboarding case sections',
            per_page: 'Cases per page',
            filter_cases: 'Filter onboarding cases',
            cases_count: 'cases',
            cases_loaded: 'onboarding cases loaded',
            no_cases: 'No onboarding cases found.',
            vendor_lookup_placeholder: 'Enter vendor ID to look up',
            data_categories_placeholder: 'e.g., personal_data, financial, health',
            systems_placeholder: 'e.g., CRM, ERP, HR System',
            // Case statuses
            status_open: 'Open',
            status_in_progress: 'In Progress',
            status_awaiting_approval: 'Awaiting Approval',
            status_approved: 'Approved',
            status_remediation_required: 'Remediation Required',
            status_closed: 'Closed',
            // Case types
            type_new_vendor: 'New Vendor',
            type_service_change: 'Service Change',
            type_renewal: 'Renewal',
            type_reassessment: 'Reassessment',
            // Tab names
            tab_overview: 'Overview',
            tab_due_diligence: 'Due Diligence',
            tab_security_assessment: 'Security Assessment',
            tab_contractual_controls: 'Contractual Controls',
            tab_sub_processors: 'Sub-Processors',
            tab_approval_gates: 'Approval Gates',
            tab_evidence: 'Evidence',
            tab_placeholder: 'Content for this section will be populated during the onboarding process.',
            // Completion indicators
            completion_not_started: 'Not Started',
            completion_in_progress: 'In Progress',
            completion_completed: 'Completed',
            completion_requiring_attention: 'Requiring Attention',
            // Approval gates
            gate_name: 'Gate Name',
            approver: 'Approver',
            decision: 'Decision',
            comments: 'Comments',
            decide: 'Make Decision',
            decision_approved: 'Approved',
            decision_rejected: 'Rejected',
            decision_deferred: 'Deferred',
            no_approvals: 'No approval gates configured for this case.',
            approval_success: 'Approval decision recorded successfully',
            approval_error: 'Failed to record approval decision',
            // Status transitions
            transition_status: 'Transition Status',
            confirm_transition: 'Transition case from "{from}" to "{to}"?',
            transition_success: 'Case status transitioned successfully',
            transition_error: 'Failed to transition case status',
            // CRUD
            create_success: 'Onboarding case created successfully',
            create_error: 'Failed to create onboarding case',
            load_error: 'Failed to load onboarding cases',
            detail_error: 'Failed to load case details',
            export: 'Export',
            export_error: 'Failed to export onboarding case',
            evidence_tab_description: 'Upload and export evidence for the onboarding case.',
            upload_evidence_label: 'Upload evidence',
            attached_evidence_heading: 'Attached evidence',
            no_evidence_attached: 'No evidence attached yet.',
            export_bundle_button: 'Export evidence bundle',
            load_attachments_error: 'Failed to load evidence.',
            linked_questionnaires_heading: 'Linked questionnaires',
            no_linked_questionnaires: 'No linked questionnaires.',
            linked_scores_heading: 'Linked risk scores',
            no_linked_scores: 'No linked scores.',
            latest_score_label: 'Latest score',
            linked_contracts_heading: 'Linked contracts',
            no_linked_contracts: 'No linked contracts.',
            linked_sub_processors_heading: 'Linked sub-processors',
            no_linked_sub_processors: 'No linked sub-processors.',
        },
        sub_processors: {
            title: 'Sub-Processor Register',
            page_title: 'Sub-Processor Register',
            new_record: 'New Sub-Processor',
            edit_record: 'Edit Sub-Processor',
            onboarding_case: 'Onboarding Case',
            services: 'Services Provided',
            data_categories: 'Data Categories',
            processing_countries: 'Processing Countries',
            dpa_chain_status: 'DPA Chain Status',
            certifications: 'Certifications',
            last_assessment: 'Last Assessment',
            next_assessment: 'Next Assessment',
            change_history: 'Change History',
            history: 'History',
            changed_by: 'Changed by',
            comma_separated: 'Comma-separated values',
            filter_records: 'Filter sub-processor records',
            filter_by_dpa: 'Filter by DPA chain status',
            all_dpa_statuses: 'All DPA Statuses',
            per_page: 'Records per page',
            records_count: 'sub-processors',
            records_loaded: 'sub-processor records loaded',
            no_records: 'No sub-processor records found.',
            no_history: 'No change history available.',
            // DPA statuses
            dpa_active: 'Active',
            dpa_pending: 'Pending',
            dpa_expired: 'Expired',
            dpa_not_required: 'Not Required',
            // CRUD
            create_success: 'Sub-processor created successfully',
            create_error: 'Failed to create sub-processor',
            update_success: 'Sub-processor updated successfully',
            update_error: 'Failed to update sub-processor',
            load_error: 'Failed to load sub-processor register',
            history_error: 'Failed to load change history',
            // Export
            export_csv: 'Export CSV',
            export_json: 'Export JSON',
            export_error: 'Failed to export sub-processor data',
        },
    },
    // ---------- Deletion Orchestration module (Sprint 2.2.0) ----------
    deletion_orchestration: {
        nav: {
            dashboard: 'Orchestration Dashboard',
            runs: 'Orchestration Runs',
            tasks: 'Execution Tasks',
            templates: 'Checklist Templates',
            vendor_requests: 'Vendor Requests',
            proof_chain: 'Proof Chain',
            dsr_integration: 'DSR Integration',
            vendor_verification: 'Vendor Verification',
            completeness: 'Completeness Dashboard',
        },
        dashboard: {
            title: 'Orchestration Dashboard',
            loading: 'Loading dashboard data...',
            active_runs: 'Active Runs',
            completion_rate: 'Completion Rate',
            avg_completion_time: 'Avg Completion Time',
            overdue_tasks: 'Overdue Tasks',
            vendor_compliance: 'Vendor Compliance',
            recent_runs: 'Recent Runs',
            no_recent_runs: 'No recent orchestration runs.',
            run_id: 'Run ID',
            ticket_id: 'Ticket ID',
            progress: 'Progress',
            systems: 'Systems',
            vendors: 'Vendors',
            load_error: 'Failed to load orchestration dashboard. Please try again.',
            aria_table: 'Recent orchestration runs',
        },
        runs: {
            title: 'Orchestration Runs',
            new: 'New Run',
            title_label: 'Title',
            run_type: 'Run Type',
            run_id: 'Run ID',
            ticket_id: 'Ticket ID',
            progress: 'Progress',
            systems: 'Systems',
            vendors: 'Vendors',
            data_subject_ref: 'Data Subject Ref',
            data_categories: 'Data Categories',
            enable_proof_chain: 'Enable Proof Chain',
            run_type_labels: {
                data_subject_request: 'Data Subject Request',
                retention_expiry: 'Retention Expiry',
                manual: 'Manual',
                scheduled: 'Scheduled',
            },
            field_guidelines: {
                title: 'A descriptive name for this orchestration run.',
                run_type: 'The origin or trigger for the deletion process.',
                ticket_id: 'The associated ticket ID from your DSR or support system.',
                data_subject_ref: 'A unique identifier for the data subject (e.g., internal ID, email hash).',
                data_categories: 'Specific categories of personal data to be deleted (comma-separated).',
                proof_enabled: 'Maintains an immutable cryptographic chain of evidence for this run.',
            },
            start: 'Start',
            started: 'Run started',
            mark_ready: 'Mark Ready',
            marked_ready: 'Run marked as ready',
            complete: 'Complete',
            complete_exceptions: 'Complete w/ Exceptions',
            pause: 'Pause',
            resume: 'Resume',
            cancel: 'Cancel',
            no_runs: 'No orchestration runs found.',
            load_error: 'Failed to load orchestration runs. Please try again.',
            create_error: 'Failed to create orchestration run',
            aria_filter_status: 'Filter by run status',
            aria_table: 'Orchestration runs',
            actions: 'Actions',
            progress_guideline: 'Calculated based on completed system tasks and vendor requests.',
            placeholder_title: 'e.g., Q2 2026 Customer Deletion Batch',
            placeholder_ticket_id: 'e.g., TKT-000123',
            placeholder_data_subject_ref: 'e.g., DS-REF-001',
            placeholder_data_categories: 'e.g., personal_data, financial, health',
        },
        tasks: {
            title: 'Execution Tasks',
            task_id: 'Task ID',
            run_id: 'Run ID',
            system_name: 'System Name',
            assigned_to: 'Assigned To',
            sla_deadline: 'SLA Deadline',
            progress: 'Progress',
            enter_run_id: 'Enter Run ID to filter...',
            select_run: 'Select a run to view tasks',
            unassigned: 'Unassigned',
            overdue: 'Overdue',
            assign_task: 'Assign Task',
            assignee_id: 'Assignee ID',
            update_status: 'Update Status',
            new_status: 'New Status',
            no_transitions: 'This task is in a final state and cannot change status.',
            select_status_placeholder: 'Select a new status',
            skip_justification: 'Skip Justification',
            failure_reason: 'Failure Reason',
            no_tasks: 'No execution tasks found.',
            new_task: 'New Task',
            system_id: 'System ID',
            data_classification: 'Data Classification',
            load_error: 'Failed to load execution tasks. Please try again.',
            assign_error: 'Failed to assign task',
            status_error: 'Failed to update task status',
            no_tasks_for_run: 'This deletion run has no execution tasks yet.',
            aria_filter_run_id: 'Filter by run ID',
            aria_filter_status: 'Filter by task status',
            aria_table: 'Execution tasks',
            // Sprint 41.3.0 F2 — step-level evidence
            view_steps: 'View Steps',
            steps_modal_title: 'Execution Steps',
            step_title: 'Step',
            step_status: 'Step Status',
            step_notes: 'Notes',
            step_evidence: 'Evidence Files',
            step_evidence_required: 'Evidence is required for this step',
            step_evidence_missing: 'No evidence uploaded yet',
            step_save: 'Save Step',
            step_saved: 'Step updated',
            step_save_error: 'Failed to update step',
            create_success: 'Execution task created',
            create_error: 'Failed to create execution task',
            step_upload_label: 'Attach evidence',
        },
        templates: {
            title: 'Checklist Templates',
            new: 'New Template',
            template_id: 'Template ID',
            system_type: 'System Type',
            est_duration: 'Est. Duration',
            steps: 'Steps',
            action_type: 'Action Type',
            instructions: 'Instructions',
            mandatory: 'Mandatory',
            add_step: 'Add Step',
            step_n: 'Step',
            no_templates: 'No checklist templates found.',
            all_system_types: 'All System Types',
            load_error: 'Failed to load checklist templates. Please try again.',
            create_error: 'Failed to create checklist template',
            aria_filter_system_type: 'Filter by system type',
            aria_filter_status: 'Filter by template status',
            aria_table: 'Checklist templates',
            placeholder_name: 'e.g., Database Deletion Checklist',
            guideline_name: 'Use a clear operating name, for example “Database Deletion Checklist”.',
            guideline_system_type: 'Choose the system category this checklist will usually run against.',
            guideline_status: 'Draft templates can be reviewed before activation; active templates can be reused in runs.',
            guideline_description: 'Summarise scope, retention context, and when teams should select this template.',
            update_success: 'Template updated successfully',
            create_success_msg: 'Template created successfully',
            update_error: 'Failed to update template',
            activate_success: 'Template activated successfully',
            activate_error: 'Failed to activate template',
            archive_success: 'Template archived successfully',
            archive_error: 'Failed to archive template',
            clone_success: 'Template cloned successfully',
            clone_error: 'Failed to clone template',
        },
        vendor_requests: {
            title: 'Vendor Deletion Requests',
            new: 'New Request',
            request_id: 'Request ID',
            vendor_name: 'Vendor Name',
            vendor_id: 'Vendor ID',
            data_subject_ref: 'Data Subject Ref',
            data_categories: 'Data Categories',
            legal_basis: 'Legal Basis',
            sla_days: 'SLA Days',
            requested_completion: 'Requested Completion',
            communications: 'Communications',
            total_requests: 'Total Requests',
            no_requests: 'No vendor deletion requests found.',
            filter_vendor_id: 'Filter by Vendor ID...',
            change_status: 'Change Status',
            new_status: 'New Status',
            actions: 'Actions',
            load_error: 'Failed to load vendor deletion requests. Please try again.',
            create_error: 'Failed to create vendor deletion request',
            dashboard_error: 'Failed to load vendor deletion dashboard. Please try again.',
            aria_filter_status: 'Filter by request status',
            aria_filter_vendor_id: 'Filter by vendor ID',
            aria_table: 'Vendor deletion requests',
            placeholder_vendor_id: 'e.g., VND-001',
            placeholder_vendor_name: 'e.g., Acme Analytics',
            placeholder_data_subject_ref: 'e.g., DS-REF-001',
            placeholder_data_categories: 'e.g., personal_data, analytics',
            placeholder_legal_basis: 'e.g., PDPL Article 19',
        },
        proof_chain: {
            title: 'Proof Chain Viewer',
            enter_run_id: 'Enter Run ID...',
            load_chain: 'Load Chain',
            verify_chain: 'Verify Chain',
            verifying: 'Verifying...',
            chain_valid: 'Chain is valid',
            chain_invalid: 'Chain is broken',
            chain_length: 'Chain Length',
            broken_at_index: 'Broken at index',
            no_proof_records: 'No proof records found for this run.',
            proof_id: 'Proof ID',
            entity_type: 'Entity Type',
            entity_id: 'Entity ID',
            event_hash: 'Event Hash',
            previous_hash: 'Previous Hash',
            attestation: 'Attestation',
            attestation_actor: 'Performed by',
            attestation_signed_at: 'Signed At',
            attestation_signature: 'Signature',
            attestation_evidence_refs: 'Evidence References',
            run_field_label: 'Deletion Run',
            select_run: 'Select a deletion run…',
            aria_select_run: 'Deletion run for proof chain lookup',
            chain_broken_here: 'Chain broken here',
            load_error: 'Failed to load proof chain. Please try again.',
            verify_error: 'Failed to verify proof chain. Please try again.',
            aria_run_id: 'Run ID for proof chain lookup',
        },
        dsr_integration: {
            change_status: 'Change status',
            request_type: 'Request Type',
            data_subject_hint: 'Identifier of the data subject whose data will be erased (e.g. email or customer ID). Usually matches the requester on the selected DSR case.',
            title: 'DSR Deletion Integration',
            create_run: 'Create DSR Deletion Run',
            dsr_case_id: 'DSR Case ID',
            data_subject_id: 'Data Subject ID',
            systems: 'Target Systems',
            route_to_department: 'Route to Department',
            confirm_deletion: 'Confirm Deletion',
            no_runs: 'No DSR deletion runs found',
            status: 'Deletion Status',
            deadline: 'Deadline',
        },
        vendor_verification: {
            title: 'Vendor Deletion Verification',
            create_followup: 'Create Follow-up',
            deadline: 'Confirmation Deadline',
            escalation_contact: 'Escalation Contact',
            record_confirmation: 'Record Confirmation',
            overdue: 'Overdue Deletions',
            escalate: 'Escalate',
            confirm_escalation: 'Are you sure you want to escalate this vendor deletion request?',
            no_followups: 'No vendor follow-ups found',
            no_overdue: 'No overdue vendor deletions',
            vendor_request: 'Vendor Request',
            vendor: 'Vendor',
            unknown_vendor: 'Unknown vendor',
            overdue_badge: 'Overdue',
            mark_confirmed: 'Mark Confirmed',
            mark_confirmed_for: 'Mark deletion confirmed for {vendor}',
            confirm_confirmation: 'Mark this vendor deletion as confirmed?',
            confirmation_success: 'Vendor deletion confirmed',
            confirmation_failed: 'Failed to confirm vendor deletion',
            select_request: 'Select a vendor request…',
            escalation_contact_placeholder: 'e.g. dpo@vendor.example',
            create_success: 'Vendor follow-up created successfully',
            create_failed: 'Failed to create vendor follow-up',
            deadline_hint: 'This page lists only follow-ups whose deadline has passed.',
            deadline_future_warning: 'This deadline is in the future, so the follow-up won’t appear in the overdue list until the date passes.',
            // fix-sprint-20260411-D F7 — semantic toast/announce keys
            escalation_success: 'Vendor deletion escalated successfully',
            escalation_failed: 'Failed to escalate vendor deletion',
        },
        completeness: {
            title: 'Deletion Completeness',
            check_conflicts: 'Check Retention Conflicts',
            no_conflicts: 'No retention conflicts found',
            conflict_resolution: 'Conflict Resolution',
            extend_retention: 'Extend Retention',
            proceed_deletion: 'Proceed with Deletion',
            escalate_conflict: 'Escalate',
            resolve: 'Resolve Conflict',
            data_subject_id: 'Data Subject ID',
            placeholder_data_subject_id: 'Enter data subject ID',
            data_subject_hint: 'Identifier of the person whose deletion you are verifying (e.g. email, customer ID, or DSR reference). Pick a known subject or type one.',
            guidance: 'Enter or select a data subject ID, optionally choose systems, then run the check to see deletion completeness and retention conflicts.',
            completion: 'Deletion Completion',
            pending_items: 'Pending Items',
            system: 'System',
            policy: 'Policy',
            retention_end: 'Retention End',
            completed_items: 'Completed Items',
            last_review_label: 'Last review',
            days_ago: 'days ago',
            review_overdue: 'Review cycle exceeded. Re-review required.',
        },
        // ── Enum label maps (Sprint 28.4.0) ──────────────────────────
        run_status_labels: {
            pending: 'Pending',
            planning: 'Planning',
            ready: 'Ready',
            in_progress: 'In Progress',
            paused: 'Paused',
            completed: 'Completed',
            completed_with_exceptions: 'Completed with Exceptions',
            cancelled: 'Cancelled',
        },
        task_status_labels: {
            pending: 'Pending',
            assigned: 'Assigned',
            in_progress: 'In Progress',
            awaiting_verification: 'Awaiting Verification',
            verified: 'Verified',
            failed: 'Failed',
            skipped: 'Skipped',
        },
        system_type_labels: {
            database: 'Database',
            saas_application: 'SaaS Application',
            file_storage: 'File Storage',
            email_system: 'Email System',
            analytics_platform: 'Analytics Platform',
            backup_system: 'Backup System',
            vendor_managed: 'Vendor Managed',
            custom: 'Custom',
        },
        template_status_labels: {
            draft: 'Draft',
            active: 'Active',
        },
        action_type_labels: {
            manual_check: 'Manual Check',
            api_call: 'API Call',
            sql_query: 'SQL Query',
            script_execution: 'Script Execution',
            file_deletion: 'File Deletion',
            verification: 'Verification',
            notification: 'Notification',
            approval: 'Approval',
        },
        request_status_labels: {
            draft: 'Draft',
            sent: 'Sent',
            acknowledged: 'Acknowledged',
            in_progress: 'In Progress',
            completed: 'Completed',
            overdue: 'Overdue',
            escalated: 'Escalated',
            disputed: 'Disputed',
        },
        event_type_labels: {
            initiation: 'Initiation',
            data_deletion: 'Data Deletion',
            completion: 'Completion',
            verification: 'Verification',
            rollback: 'Rollback',
        },
        // ── Count labels ─────────────────────────────────────────────
        count_labels: {
            one_template: '1 template',
            n_templates: '{n} templates',
            one_run: '1 run',
            n_runs: '{n} runs',
            one_task: '1 task',
            n_tasks: '{n} tasks',
            one_request: '1 request',
            n_requests: '{n} requests',
        },
        // ── Aria labels ──────────────────────────────────────────────
        aria: {
            completion_rate: 'Completion rate: {pct}%',
            vendor_compliance: 'Vendor compliance: {pct}%',
            run_progress: 'Run progress: {pct}%',
            run_progress_systems: 'Run progress: {completed} of {total} systems processed',
            chain_broken: 'Chain broken at this record',
            chain_valid: 'Chain valid at this record',
            chain_unknown: 'Chain status unknown',
            escalate_vendor: 'Escalate {id}',
            resolve_conflict: 'Resolve conflict for {system}',
        },
        regulatory: {
            annual_review_cycle_info: 'Deletion completeness must be reviewed at least every 365 days. Records exceeding this threshold are flagged for mandatory re-review to maintain regulatory compliance.',
            runs_data_d1_lifecycle_info: 'Orchestration run states map to the DATA-D1 lifecycle: planning = catalog, ready = classify, in_progress = apply rules, completed = evidence generated, completed_with_exceptions = review required. Each transition is recorded in the proof chain.',
            vendor_record_linkage_info: 'Each vendor deletion request is linked to its vendor record via vendor_id. Completion confirmation requires cryptographic evidence from the proof chain before the request can be marked as completed.',
            templates_internal_classification_info: 'All deletion checklist templates are classified as internal organizational documents. They do not carry official regulatory form IDs and should not be submitted as regulatory filings.',
            proof_chain_evidence_info: 'Each proof record captures the data subject/category, timestamp, actor, legal basis, and a SHA-256 hash. Together these fields produce cryptographic deletion evidence that satisfies regulatory audit requirements.',
            retention_data_law_info: 'Retention schedules on this dashboard are derived from the Data Law classification assigned to each data category. When a classification changes, linked retention rules update automatically to ensure regulatory alignment.',
            dsr_erasure_route_info: 'DSR erasure runs are linked to the originating Rights Request case. The regulatory deadline (typically 30 days under GDPR Art. 17) is tracked automatically. Overdue cases are flagged for escalation.',
        },
    },
    // ---------- Data Discovery module (Sprint 2.3.0) ----------
    discovery: {
        nav: {
            dashboard: 'Discovery Dashboard',
            scans: 'Discovery Scans',
            staging_area: 'Staging Area',
            dedup_rules: 'Dedup Rules',
            classification_rules: 'Classification Rules',
            suggestions: 'Suggestion Queue',
            analytics: 'Suggestion Analytics',
            import_templates: 'Import Templates',
            connectors: 'Connector Management',
            discovery_cycles: 'Discovery Cycles',
            owner_review: 'Owner Review',
            classification_lineage: 'Classification Lineage',
        },
        dashboard: {
            title: 'Discovery Dashboard',
            loading: 'Loading dashboard data...',
            total_scans: 'Total Scans',
            active_scans: 'Active Scans',
            completed_30d: 'Completed (30d)',
            failed_30d: 'Failed (30d)',
            systems_discovered: 'Systems Discovered',
            pending_staged_records: 'Pending Staged Records',
            recent_scans: 'Recent Scans',
            no_recent_scans: 'No recent scans.',
            scan_id: 'Scan ID',
            discovered: 'Discovered',
            view_all_scans: 'View All Scans',
            staging_area: 'Staging Area',
            suggestion_queue: 'Suggestion Queue',
            records_reviewed: 'Records Reviewed',
            records_approved: 'Records Approved',
            exceptions_pending: 'Exceptions Pending',
            view_as_chart: 'View as chart',
            view_as_table: 'View as table',
        },
        scans: {
            title: 'Discovery Scans',
            new: 'New Scan',
            scan_id: 'Scan ID',
            scan_type: 'Scan Type',
            progress: 'Progress',
            discovered: 'Discovered',
            no_scans: 'No discovery scans found.',
            all_scan_types: 'All Scan Types',
            source_config: 'Source Config (JSON)',
            invalid_json: 'Invalid JSON in source config',
            load_error: 'Failed to load discovery scans. Please try again.',
            create_error: 'Failed to create discovery scan',
            change_status: 'Change Status',
            new_status: 'New Status',
            actions: 'Actions',
            type_endpoint: 'Endpoint',
            type_spreadsheet_import: 'Spreadsheet Import',
            type_cmdb_sync: 'CMDB Sync',
            type_cloud_sync: 'Cloud Sync',
            status_pending: 'Pending',
            status_running: 'Running',
            status_completed: 'Completed',
            status_failed: 'Failed',
            status_cancelled: 'Cancelled',
            source_config_placeholder: '{"host": "...", "port": 443}',
        },
        staging_area: {
            title: 'Staging Area',
            record_id: 'Record ID',
            source_scan: 'Source Scan',
            entity_type: 'Entity Type',
            suggested_name: 'Suggested Name',
            confidence: 'Confidence',
            no_records: 'No staged records found.',
            promote: 'Promote',
            discard: 'Discard',
            review: 'Review',
            merge: 'Merge',
            merge_title: 'Merge Record',
            merge_target_id: 'Merge Target ID',
            reject_reason_placeholder: 'Reason for rejection...',
            promotion_status: 'Promotion Status',
            dedup_status: 'Dedup Status',
            all_source_types: 'All Source Types',
            all_promotion_statuses: 'All Promotion Statuses',
            all_dedup_statuses: 'All Dedup Statuses',
            load_error: 'Failed to load staged records. Please try again.',
            promote_error: 'Failed to promote record',
            reject_error: 'Failed to reject record',
            merge_error: 'Failed to merge record',
            review_error: 'Failed to mark record for review',
            bulk_promote_error: 'Failed to bulk promote records',
            bulk_reject_error: 'Failed to bulk reject records',
            service_type_web_server: 'Web Server',
            service_type_database: 'Database',
            service_type_api_gateway: 'API Gateway',
            service_type_storage: 'Storage',
            service_type_authentication: 'Authentication',
            service_type_analytics: 'Analytics',
            service_type_unknown: 'Unknown',
            source_type_endpoint_scan: 'Endpoint Scan',
            source_type_spreadsheet: 'Spreadsheet',
            source_type_cmdb: 'CMDB',
            source_type_cloud_inventory: 'Cloud Inventory',
            promotion_status_new: 'New',
            promotion_status_under_review: 'Under Review',
            promotion_status_promoted: 'Promoted',
            promotion_status_rejected: 'Rejected',
            promotion_status_merged: 'Merged',
            dedup_status_unmatched: 'Unmatched',
            dedup_status_potential_match: 'Potential Match',
            dedup_status_exact_match: 'Exact Match',
            dedup_status_resolved: 'Resolved',
            confirm_bulk_reject: 'Are you sure you want to reject the selected records?',
            scan_batch_id: 'Batch ID',
            promote_to_registry_title: 'Promote to Data Registry',
            promote_modal_instructions: 'Creates a Data Registry item with collection_method: observed and provenance linkage to this discovery record. The item starts in draft status for review.',
            promote_label_category: 'Category',
            promote_label_name_override: 'Name (optional override)',
            placeholder_name_override: "Defaults to the discovery record's name/hostname",
            promote_label_sensitivity: 'Proposed sensitivity',
            inherit_from_category: 'Inherit from category',
        },
        dedup_rules: {
            title: 'Dedup Rules',
            new: 'New Rule',
            rule_name: 'Rule Name',
            match_fields: 'Match Fields',
            threshold: 'Threshold',
            no_rules: 'No dedup rules found.',
            seed_defaults: 'Seed Defaults',
            match_type: 'Match Type',
            similarity_threshold: 'Similarity Threshold',
            field_name: 'Field Name',
            load_error: 'Failed to load dedup rules. Please try again.',
            create_error: 'Failed to create dedup rule',
            toggle_error: 'Failed to toggle dedup rule',
            seed_error: 'Failed to seed defaults',
            priority: 'Priority',
            enabled_status: 'Enabled',
            disabled_status: 'Disabled',
            default_badge: 'Default',
            enable: 'Enable',
            disable: 'Disable',
            match_type_exact: 'Exact',
            match_type_fuzzy: 'Fuzzy',
            match_type_regex: 'Regex',
            match_type_ip_range: 'IP Range',
        },
        classification_rules: {
            title: 'Classification Rules',
            new: 'New Rule',
            rule_name: 'Rule Name',
            pattern: 'Pattern',
            classification: 'Classification',
            no_rules: 'No classification rules found.',
            seed_defaults: 'Seed Defaults',
            run_rules_now: 'Run Rules Now',
            run_rules_success: 'Generated {count} new classification suggestion(s)',
            run_rules_no_results: 'No new suggestions generated — all matching rules already have pending suggestions',
            run_rules_error: 'Failed to run classification rules. Please try again.',
            taxonomy_id: 'Taxonomy ID',
            level_id: 'Level ID',
            condition_logic: 'Condition Logic',
            weight: 'Weight',
            conditions: 'Conditions',
            condition_field: 'Field',
            condition_operator: 'Operator',
            condition_value: 'Value',
            effectiveness: 'Effectiveness',
            match_count: 'Matches',
            approval_count: 'Approvals',
            rejection_count: 'Rejections',
            filter_taxonomy: 'Filter by Taxonomy ID...',
            load_error: 'Failed to load classification rules. Please try again.',
            create_error: 'Failed to create classification rule',
            seed_error: 'Failed to seed defaults',
            toggle_error: 'Failed to toggle rule. Please try again.',
            priority: 'Priority',
            enabled_status: 'Enabled',
            disabled_status: 'Disabled',
            enable: 'Enable',
            disable: 'Disable',
            logic_and: 'AND',
            logic_or: 'OR',
            operator_equals: 'Equals',
            operator_contains: 'Contains',
            operator_matches_regex: 'Matches Regex',
            operator_in_list: 'In List',
            operator_not_equals: 'Not Equals',
            operator_not_contains: 'Not Contains',
            enabled_label: 'Enabled',
            disabled_label: 'Disabled',
            taxonomy_label: 'Taxonomy',
            operator_label: 'Operator',
            value_label: 'Value',
        },
        suggestions: {
            title: 'Suggestion Queue',
            entity_type: 'Entity Type',
            suggested_name: 'Suggested Name',
            source: 'Source',
            confidence: 'Confidence',
            accept: 'Accept',
            reject: 'Reject',
            no_suggestions: 'No suggestions in queue.',
            modify_approve: 'Modify & Approve',
            delegate: 'Delegate',
            delegate_user_id: 'Delegate User ID',
            new_level_id: 'New Level ID',
            reject_reason_placeholder: 'Reason for rejection...',
            current_level: 'Current Level',
            suggested_level: 'Suggested Level',
            filter_taxonomy: 'Filter by Taxonomy ID...',
            all_confidence: 'All Confidence Bands',
            load_error: 'Failed to load suggestions. Please try again.',
            approve_error: 'Failed to approve suggestion',
            reject_error: 'Failed to reject suggestion',
            modify_error: 'Failed to modify & approve suggestion',
            delegate_error: 'Failed to delegate suggestion',
            bulk_approve_error: 'Failed to bulk approve suggestions',
            bulk_reject_error: 'Failed to bulk reject suggestions',
            suggestion_type_new_label: 'New Label',
            suggestion_type_validation: 'Validation',
            suggestion_type_reclassification: 'Reclassification',
            status_pending: 'Pending',
            status_approved: 'Approved',
            status_rejected: 'Rejected',
            status_superseded: 'Superseded',
            confidence_high: 'High',
            confidence_medium: 'Medium',
            confidence_low: 'Low',
            confirm_bulk_reject: 'Are you sure you want to reject the selected suggestions?',
        },
        analytics: {
            title: 'Suggestion Analytics',
            total_suggestions: 'Total Suggestions',
            accepted: 'Accepted',
            rejected: 'Rejected',
            pending: 'Pending',
            acceptance_rate: 'Acceptance Rate',
            by_entity_type: 'By Entity Type',
            by_source: 'By Source',
            avg_decision_time: 'Avg Decision Time',
            rule_effectiveness: 'Rule Effectiveness',
            by_status: 'By Status',
            by_confidence: 'By Confidence Band',
            load_error: 'Failed to load suggestion analytics. Please try again.',
            confidence_high: 'High',
            confidence_medium: 'Medium',
            confidence_low: 'Low',
            by_confidence_band: 'By Confidence Band',
            approval_rate: 'Approval Rate',
            by_label_state: 'By Label State',
        },
        import_templates: {
            title: 'Import Templates',
            new: 'New Template',
            template_name: 'Template Name',
            file_format: 'File Format',
            column_mappings: 'Column Mappings',
            no_templates: 'No import templates found.',
            source_column: 'Source Column',
            target_field: 'Target Field',
            transform: 'Transform',
            source_column_label: 'Source Column',
            target_field_label: 'Target Field',
            transform_label: 'Transform',
            confirm_delete: 'Are you sure you want to delete this import template?',
            load_error: 'Failed to load import templates. Please try again.',
            save_error: 'Failed to save import template',
            delete_error: 'Failed to delete import template',
            file_type_csv: 'CSV',
            file_type_xlsx: 'XLSX',
        },
        connectors: {
            title: 'Connector Management',
            new: 'New Connector',
            connector_name: 'Connector Name',
            connector_type: 'Type',
            last_sync: 'Last Sync',
            no_connectors: 'No connectors found.',
            cmdb_syncs: 'CMDB Syncs',
            cloud_syncs: 'Cloud Syncs',
            new_sync: 'New Sync',
            no_syncs: 'No syncs found.',
            cmdb_error: 'Failed to load CMDB scans. Please try again.',
            cloud_error: 'Failed to load cloud scans. Please try again.',
            trigger_error: 'Failed to trigger sync',
            trigger_sync_error: 'Failed to trigger {type}',
            cmdb_section_title: 'CMDB Syncs',
            cloud_section_title: 'Cloud Syncs',
            column_connector: 'Connector',
            action_start: 'Start',
            confirm_cancel_sync: 'Cancel this sync?',
            start_error: 'Failed to start sync',
            cancel_error: 'Failed to cancel sync',
            type_endpoint: 'Endpoint Scan',
            type_spreadsheet_import: 'Spreadsheet Import',
            type_cmdb_sync: 'CMDB Sync',
            type_cloud_sync: 'Cloud Sync',
        },
        discovery_cycles: {
            title: 'Discovery Cycles',
            new_cycle: 'New Discovery Cycle',
            cycle_name: 'Cycle Name',
            scope_type: 'Scope Type',
            scope_ids: 'Scope IDs',
            cadence_days: 'Cadence (Days)',
            status: 'Status',
            no_cycles: 'No discovery cycles found',
            compare: 'Compare Cycles',
            delta_report: 'Delta Report',
            new_records: 'New Records',
            changed_records: 'Changed Records',
            removed_records: 'Removed Records',
            schedule: 'Schedule Scan',
            scope_organization: 'Organization',
            scope_department: 'Department',
            scope_system: 'System',
            scope_systems: 'Systems',
            bulk_actions: 'Bulk Actions',
            selected: '{count} selected',
            bulk_create: 'Bulk Create Cycles',
            bulk_assign_scope: 'Bulk Assign Scope',
            bulk_advance: 'Bulk Advance Status',
            confirm_bulk_action: 'Confirm Bulk Action',
            bulk_confirm_message: 'Are you sure you want to perform this action?',
            cycles_affected: '{count} cycles will be affected',
            select_all: 'Select all',
            select_cycle: 'Select cycle {name}',
            from_template: 'from template',
            bulk_results: 'Bulk Results',
            failed_label: 'failed',
            template_id: 'Template ID',
            count: 'Count',
            advance_to: 'Target status',
            bulk_success: 'Bulk operation completed',
        },
        owner_review: {
            title: 'Owner Review Tasks',
            assign_owner: 'Assign to Owner',
            owner_id: 'Owner',
            decision: 'Decision',
            approve: 'Approve',
            modify: 'Modify',
            reject: 'Reject',
            escalate: 'Escalate',
            no_tasks: 'No review tasks found',
            bulk_resolve: 'Bulk Resolve',
            exceptions: 'Exception Items',
            no_exceptions: 'No exception items found',
            record_id_label: 'Record ID',
            selected: 'selected',
            notes: 'Notes',
            select_all: 'Select All',
            select: 'Select',
            confirm_resolve: 'Confirm',
        },
        classification_lineage: {
            title: 'Classification Lineage',
            link_suggestion: 'Link to Proposed Label',
            suggestion_id: 'Suggestion',
            proposed_label_id: 'Proposed Label',
            no_lineage: 'No lineage data found',
            full_lineage: 'Full Lineage',
            record_id_label: 'Record ID',
            staged_record_label: 'Staged Record',
            enter_staged_record_id: 'Enter staged record ID',
            scan_id_label: 'Scan ID',
            select_record: 'Select a record',
            select_suggestion: 'Select a suggestion',
            select_label: 'Select a label',
        },
        common_labels: {
            n_rules: '{n} rules',
            one_rule: '1 rule',
            n_scans: '{n} scans',
            one_scan: '1 scan',
            n_syncs: '{n} syncs',
            one_sync: '1 sync',
            n_records: '{n} records',
            one_record: '1 record',
            n_templates: '{n} templates',
            one_template: '1 template',
            n_suggestions: '{n} suggestions',
            one_suggestion: '1 suggestion',
            n_selected: '{n} selected',
            dashboard_loaded: 'Discovery dashboard loaded',
            n_scans_loaded: '{n} scans loaded',
            n_staged_records_loaded: '{n} staged records loaded',
            confirm_delete_import_template: 'Are you sure you want to delete this import template?',
            invalid_json_source_config: 'Invalid JSON in source_config',
            scope_organization: 'Organization',
            scope_department: 'Department',
            scope_system: 'System',
            aria_filter_scans: 'Filter scans',
            aria_filter_staged_records: 'Filter staged records',
            aria_filter_suggestions: 'Filters',
            aria_filter_by_taxonomy_id: 'Filter by taxonomy ID',
            aria_filter_by_enabled_status: 'Filter by enabled status',
            aria_filter_by_source_type: 'Filter by source type',
            aria_filter_by_promotion_status: 'Filter by promotion status',
            aria_filter_by_dedup_status: 'Filter by dedup status',
            aria_filter_by_scan_type: 'Filter by scan type',
            aria_filter_by_status: 'Filter by status',
            aria_filter_by_confidence: 'Filter by confidence band',
            aria_select_all_records: 'Select all records',
            aria_select_record: 'Select record {id}',
            aria_select_all_suggestions: 'Select all suggestions',
            aria_select_suggestion: 'Select suggestion {id}',
            aria_promote_record: 'Promote record {id}',
            aria_discard_record: 'Discard record {id}',
            aria_merge_record: 'Merge record {id}',
            aria_review_record: 'Review record {id}',
            aria_accept_suggestion: 'Accept suggestion {id}',
            aria_reject_record: 'Reject record',
            aria_merge_record_dialog: 'Merge record',
            aria_create_new_scan: 'Create new scan',
            aria_sync_progress: 'Sync progress: {processed} of {total} items processed',
            aria_scan_progress: 'Scan progress: {processed} of {total} items processed',
            aria_confidence: 'Confidence: {pct}%',
            aria_effectiveness: 'Effectiveness: {pct}',
            aria_promote_n_records: 'Promote {n} selected records',
            aria_discard_n_records: 'Discard {n} selected records',
            placeholder_pii_detection_rule: 'e.g., PII Detection Rule',
            placeholder_tax_id: 'e.g., TAX-001',
            placeholder_lvl_id: 'e.g., LVL-001',
            placeholder_service_type: 'e.g., service_type',
            placeholder_database: 'e.g., database',
            placeholder_hostname_match: 'e.g., Hostname Exact Match',
            placeholder_hostname: 'e.g., hostname',
            placeholder_hr_systems_csv: 'e.g., HR Systems CSV Import',
            placeholder_server_name: 'e.g., Server Name',
            placeholder_hostname_field: 'e.g., hostname',
            placeholder_lowercase: 'e.g., lowercase',
            placeholder_source_config: '{"host": "...", "port": 443}',
            placeholder_stg_id: 'e.g., STG-000123',
            placeholder_lvl_002: 'e.g., LVL-002',
            placeholder_usr_id: 'e.g., USR-000123',
            decision_for: 'Decision for {id}',
        },
        exports: {
            title: 'Export',
            inventory: 'Data Inventory',
            classification_register: 'Classification Register',
            raci: 'RACI Matrix',
            exceptions: 'Exceptions Report',
            evidence_pack: 'Evidence Pack',
            delta_report: 'Delta Report',
            executive_summary: 'Executive Summary',
            export_button: 'Export',
            downloading: 'Downloading...',
            download_complete: 'Download complete.',
            download_error: 'Failed to download export. Please try again.',
        },
        flags: {
            title: 'Downstream Flags',
            pending: 'Pending Flags',
            cross_border_review_needed: 'Cross-Border Review Needed',
            sharing_disclosure_needed: 'Sharing Disclosure Needed',
            dpia_review_needed: 'DPIA Review Needed',
            acknowledge: 'Acknowledge',
            resolve: 'Resolve',
            no_flags: 'No downstream flags.',
        },
        delta: {
            title: 'Delta Summary',
            new_count: '{n} new',
            removed_count: '{n} removed',
            changed_count: '{n} changed',
            re_review_triggered: 'Re-review Triggered',
            create_re_review: 'Create Re-review',
            recent_alerts: 'Recent Delta Alerts',
            no_alerts: 'No recent delta alerts.',
            threshold_exceeded: 'Delta threshold exceeded (>10%)',
        },
        regulatory: {
            dashboard_governance_info: 'This dashboard shows cycle-level governance status: systems scanned, records staged, records reviewed, records approved, and exceptions pending. This matches the governance lead success criteria: "live dashboard of who has updated their inventory, classification, and sharing logs" (P-VN-05).',
            export_artifacts_info: 'Discovery exports include all required artifact types per UC-VN-10 and DATA-EVD-01: approved data inventory, classification register, ownership/RACI mapping, exception queue, executive summary, evidence pack, and delta report.',
            scan_inventory_pipeline_info: 'Each completed scan produces records compatible with the vn_data_law_data_inventory_register template (requirement 88dd9877). Discovered data assets automatically populate the data inventory register to satisfy the Data Law obligation to maintain a digital data inventory.',
            connector_linkage_info: 'Each connector configuration must include: system type (e.g., CMDB, cloud platform), data store reference (database, file store, SaaS), and business process linkage (UC-VN-10 required supporting records). These fields ensure discovered assets are traceable to their source system and business context.',
            owner_routing_info: 'Review tasks are routed to the correct department or application owner based on system/data ownership (UC-VN-10 step 5). Routing uses the system ownership register, not ad-hoc assignment.',
            owner_actions_audit_info: 'Owners can approve, modify, reject, or escalate each classification (UC-VN-10 step 6). Every action captures actor, timestamp, and reason, producing governance audit evidence events per requirement f12fc090 (Annual governance attestation). The audit trail shows who reviewed, what was changed, and when.',
            escalation_timers_info: 'Escalation timers: owner review deadline is 5 business days; exception escalation is 48 hours (UC-VN-10 timers). Unresolved items auto-escalate when the deadline expires. Timers are configurable per organizational policy.',
            original_suggestion_preserved_info: 'The original machine/system classification suggestion is preserved alongside every owner override (UC-VN-10 evidence requirement). The original suggestion is never overwritten and remains visible for audit purposes.',
            dedup_lineage_info: 'Deduplication rules prevent duplicate inventory entries when the same data asset is discovered across multiple scans or connectors. The most recent scan metadata is preserved as the primary record while maintaining lineage references to all prior discoveries for audit traceability.',
            label_states_info: 'Analytics distinguish 4 label states for governance review: machine-suggested (pending), owner-approved, owner-modified, and rejected. These states enable auditors to assess automation accuracy and owner engagement.',
            import_pipeline_info: 'Import templates must produce records compatible with the discovery pipeline. Imported records enter staging with the same metadata requirements as scanned records: source system identification, batch ID, and timestamp. Column mappings should target fields defined in vn_data_law_data_inventory_register.',
            lineage_full_path_info: 'Classification lineage traces the full path: scan/import -> staging -> owner review -> approved label -> register entry (UC-VN-10 steps 1-10). Each stage shows record ID, actor, timestamp, and state transition for complete audit traceability.',
            staging_workflow_info: 'Staged records are visually distinguished from approved inventory entries by their promotion status badge (UC-VN-10 step 3). Each record retains provenance metadata: source system, scan batch ID, and discovery timestamp for full audit traceability.',
            suggestion_data_d1_info: 'Suggestions align with Data Law classification procedure DATA-D1: (1) catalog data assets, (2) classify by sensitivity level, (3) define handling rules. Each suggestion includes sensitivity and importance dimensions per requirement 18e4bfd1.',
            downstream_flags_info: 'Items flagged for sharing, cross-border transfer, or high-risk treatment trigger linked downstream workflows: cross-border transfer assessment, sharing risk assessment, and DPIA review (UC-VN-10 step 8).',
            delta_detection_info: 'Discovery cycles support delta detection per UC-VN-10 step 11: each cycle shows which records are new, changed, or removed since the last approved baseline. Material changes (>10% delta) automatically trigger re-review workflows.',
        },
    },
    // ---------- Incident Operations module (Sprint 2.4.0) ----------
    incident_ops: {
        errors: {
            fetch: 'Failed to load.',
            save: 'Failed to save.',
            fetch_escalation_log_failed: 'Failed to fetch escalation log.',
            fetch_activations_failed: 'Failed to fetch activations.',
            deactivate_failed: 'Failed to deactivate.',
            complete_activation_failed: 'Failed to complete activation.',
            fetch_playbook_templates_failed: 'Failed to fetch playbook templates.',
            create_playbook_template_failed: 'Failed to create playbook template.',
            clone_template_failed: 'Failed to clone template.',
            update_status_failed: 'Failed to update status.',
            deprecate_template_failed: 'Failed to deprecate template.',
            seed_defaults_failed: 'Failed to seed defaults.',
            fetch_regulator_exports_failed: 'Failed to fetch regulator exports.',
            create_regulator_export_failed: 'Failed to create regulator export.',
            update_submission_status_failed: 'Failed to update submission status.',
            fetch_response_metrics_failed: 'Failed to fetch response metrics.',
            fetch_sla_dashboard_failed: 'Failed to fetch SLA dashboard.',
            fetch_sla_definitions_failed: 'Failed to fetch SLA definitions.',
            create_sla_definition_failed: 'Failed to create SLA definition.',
            seed_sla_defaults_failed: 'Failed to seed SLA defaults.',
            fetch_vendor_coordination_failed: 'Failed to fetch vendor coordination.',
            fetch_vendor_detail_failed: 'Failed to fetch vendor detail.',
            create_vendor_coordination_failed: 'Failed to create vendor coordination.',
            add_communication_failed: 'Failed to add communication.',
            add_info_request_failed: 'Failed to add info request.',
            add_action_request_failed: 'Failed to add action request.',
        },
        nav: {
            playbooks: 'Playbook Templates',
            sla_definitions: 'SLA Definitions',
            sla_dashboard: 'SLA Dashboard',
            escalation_log: 'Escalation Log',
            regulator_exports: 'Regulator Exports',
            deadlines: 'Notification Deadlines',
            vendor_coordination: 'Vendor Coordination',
            metrics: 'Response Metrics',
            playbook_links: 'Playbook Links',
            regulatory_sla: 'Regulatory SLA',
            coordination_dashboard: 'Coordination',
            evidence_chain: 'Evidence Chain',
            post_review: 'Post-Incident Review',
        },
        playbooks: {
            page_title: 'Playbook Templates',
            activations_page_title: 'Playbook Activations',
            title: 'Playbook Templates',
            new: 'New Template',
            seed_defaults: 'Seed Defaults',
            scenario_type: 'Scenario Type',
            severity_applicability: 'Severity Applicability',
            all_scenario_types: 'All Scenario Types',
            new_playbook: 'New Playbook Template',
            clone: 'Clone',
            clone_template: 'Clone Template',
            template_name: 'Template name',
            deprecate: 'Deprecate',
            confirm_deprecate: 'Deprecate template "{name}"?',
            create_template: 'Create Template',
            update_status: 'Update Status',
            no_templates: 'No playbook templates found',
            deactivation_reason_placeholder: 'Reason for deactivation...',
            description_placeholder: 'Playbook description...',
            name_placeholder: 'Playbook name...',
        },
        sla_definitions: {
            page_title: 'SLA Definitions',
            title: 'SLA Definitions',
            new: 'New SLA Definition',
            sla_name: 'SLA Name',
            target_hours: 'Target Hours',
            escalation_after: 'Escalation After',
            applies_to: 'Applies To',
            no_definitions: 'No SLA definitions found.',
            target_value_placeholder: 'Target value',
            name_placeholder: 'SLA name...',
            threshold_hours_label: 'Threshold Hours',
            severity_label: 'Severity',
            all_types: 'All Types',
            all_severities: 'All Severities',
            default_label: 'Default',
        },
        sla_dashboard: {
            compliant: 'Compliant',
            page_title: 'SLA Dashboard',
            title: 'SLA Dashboard',
            loading: 'Loading SLA data...',
            compliance_rate: 'Compliance Rate',
            breached: 'Breached',
            at_risk: 'At Risk',
            on_track: 'On Track',
            by_severity: 'By Severity',
            total_records: 'Total Records',
            met: 'Met',
            sla_records: 'SLA Records',
            sla_reference: 'SLA Reference',
            record_id: 'Record ID',
            metric_type: 'Metric Type',
            severity: 'Severity',
            elapsed: 'Elapsed',
            clock_start: 'Clock Start',
        },
        escalation_log: {
            page_title: 'Escalation Log',
            title: 'Escalation Log',
            incident_id: 'Incident ID',
            escalated_by: 'Escalated By',
            escalated_to: 'Escalated To',
            reason: 'Reason',
            escalated_at: 'Escalated At',
            no_escalations: 'No escalations found.',
            filter_incident_id: 'Filter by Incident ID',
            escalation_id: 'Escalation ID',
            level: 'Level',
            level_range: 'Level (L1\u2013L4)',
            create_escalation: 'Create Escalation',
            incident_id_placeholder: 'e.g., INC-001',
            target_role_placeholder: 'e.g., dpo, incident_manager',
            select_placeholder: '— select —',
        },
        regulator_exports: {
            page_title: 'Regulator Exports',
            title: 'Regulator Exports',
            new: 'New Export',
            incident_id: 'Incident ID',
            regulator: 'Regulator',
            export_format: 'Format',
            exported_at: 'Exported At',
            no_exports: 'No regulator exports found.',
            enter_incident_id: 'Enter Incident ID',
            export_id: 'Export ID',
            watermark: 'Watermark',
            submission_status: 'Submission Status',
            all_jurisdictions: 'All Jurisdictions',
            no_transitions: 'No further transitions available from',
            select_new_status: '\u2014 select new status \u2014',
            sections_included: 'Sections Included',
            form_classification: 'Classification',
            submission_channel: 'Channel',
        },
        deadlines: {
            title: 'Notification Deadlines',
            incident_id: 'Incident ID',
            deadline: 'Deadline',
            regulator: 'Regulator',
            remaining: 'Remaining',
            no_deadlines: 'No notification deadlines found.',
        },
        vendor_coordination: {
            page_title: 'Vendor Coordination',
            title: 'Vendor Coordination',
            new: 'New Coordination',
            incident_id: 'Incident ID',
            vendor_name: 'Vendor Name',
            contact: 'Contact',
            coordination_status: 'Coordination Status',
            no_items: 'No vendor coordination items found.',
            assessment_placeholder: 'Initial assessment from vendor...',
            coordination_id: 'Coordination ID',
            communications: 'Communications',
            info_requests: 'Info Requests',
            action_requests: 'Action Requests',
            direction: 'Direction',
            channel_label: 'Channel',
            no_transitions: 'No further transitions available.',
            vendor_ref_number: 'Reference Number',
            report_date: 'Report Date',
            contact_email: 'Email',
            contact_phone: 'Phone',
            channel_email: 'Email',
            channel_meeting: 'Meeting',
            channel_phone: 'Phone',
            channel_portal: 'Portal',
            incident_id_placeholder: 'Filter by Incident ID...',
            priority_critical: 'Critical',
            priority_high: 'High',
            priority_low: 'Low',
            priority_medium: 'Medium',
            vendor_id_placeholder: 'Filter by Vendor ID...',
            description_placeholder: 'Description...',
            filter_vendor_id: 'Filter by Vendor ID',
            inbound: 'Inbound',
            outbound: 'Outbound',
            summary_placeholder: 'Summary...',
            priority_label: 'Priority',
            due_date_label: 'Due date',
            info_request_desc: 'Information request description',
            action_request_desc: 'Action request description',
            content_label: 'Content',
            select_incident: 'Select Incident',
            select_vendor: 'Select Vendor',
            discovery_date: 'Discovery Date',
        },
        metrics: {
            page_title: 'Response Metrics',
            title: 'Response Metrics',
            loading: 'Loading metrics data...',
            avg_response_time: 'Avg Response Time',
            avg_resolution_time: 'Avg Resolution Time',
            total_incidents: 'Total Incidents',
            by_severity: 'By Severity',
            by_status: 'By Status',
            trend: 'Trend',
            start_sla_clock: 'Start SLA Clock',
            sla_records: 'SLA Records',
            no_sla_records: 'No SLA records yet. Click "Start SLA Clock" to create one.',
            record_id: 'Record ID',
            severity: 'Severity',
            progress: 'Progress',
            incident_id_required: 'Incident ID is required',
            sla_definition_required: 'SLA Definition is required',
            metric_type_required: 'Metric type is required',
            threshold_required: 'Threshold hours is required',
            sla_clock_started: 'SLA clock started',
            sla_definition: 'SLA Definition',
            no_definitions: 'No SLA definitions found. Create one under SLA Definitions first.',
            select_definition: 'Select SLA definition\u2026',
            select_metric_type: 'Select metric type\u2026',
            select_severity: 'Select severity\u2026',
            threshold_hours: 'Threshold (hours)',
            warning_threshold_pct: 'Warning threshold (%)',
            start_clock: 'Start Clock',
            starting: 'Starting\u2026',
            metric_label: 'Metric',
            value_label: 'Value',
            incident_id: 'Incident ID',
            threshold_placeholder: 'e.g. 24',
            metric_types: {
                triage_time: 'Triage Time',
                containment_time: 'Containment Time',
                notification_time: 'Notification Time',
                resolution_time: 'Resolution Time',
            },
            severities: {
                critical: 'Critical',
                high: 'High',
                medium: 'Medium',
                low: 'Low',
            },
        },
        // Sprint 21.2.0: Incident Operations Maturity
        playbook_links: {
            page_title: 'Playbook Links',
            title: 'Playbook Notification Links',
            new_link: 'Link Playbook to Notification',
            activation_id: 'Activation ID',
            notification_id: 'Notification ID',
            select_activation: 'Select an activation...',
            select_notification: 'Select a notification...',
            no_links: 'No playbook links found',
            seed_pdpl: 'Seed PDPL Playbooks',
            // fix-sprint-20260411-D F7 — semantic toast keys
            created: 'Playbook link created',
            seeded: 'PDPL playbooks seeded',
            create_failed: 'Failed to create playbook link',
            seed_failed: 'Failed to seed PDPL playbooks',
        },
        regulatory_sla: {
            page_title: 'Regulatory SLA Mappings',
            title: 'Regulatory SLA Mappings',
            new_mapping: 'New SLA Mapping',
            sla_definition: 'SLA Definition',
            select_sla_definition: 'Select an SLA definition...',
            framework_code: 'Framework Code',
            deadline_type: 'Deadline Type',
            deadline_hours: 'Deadline Hours',
            auto_escalate: 'Auto-Escalate',
            seed_pdpl: 'Seed PDPL Defaults',
            no_mappings: 'No regulatory SLA mappings found',
            dashboard: 'Regulatory SLA Dashboard',
            deadline_types: {
                initial_notification: 'Initial Notification',
                supplement_response: 'Supplement Response',
                authority_response: 'Authority Response',
                remediation_report: 'Remediation Report',
            },
            statutory_reference: 'Statutory Reference',
        },
        coordination_dashboard: {
            page_title: 'Coordination Dashboard',
            title: 'Coordination Dashboard',
            incident_id: 'Incident ID',
            select_incident: 'Select an incident...',
            team_role: 'Team Role',
            members: 'Members',
            tasks: 'Tasks',
            sla_status: 'SLA Status',
            no_teams: 'No coordination teams found',
            new_team: 'New Coordination Team',
            enter_incident_id: 'Enter an incident ID to view coordination teams.',
            member_ids_placeholder: 'user1, user2',
            edit_team: 'Edit Team',
            task_count: 'Total Tasks',
            tasks_completed: 'Completed',
            team_roles: {
                security: 'Security',
                legal: 'Legal',
                it: 'IT',
                management: 'Management',
                department_owner: 'Department Owner',
                external: 'External',
            },
        },
        evidence_chain: {
            page_title: 'Evidence Chain',
            title: 'Evidence Chain of Custody',
            new_entry: 'New Evidence Entry',
            select_incident: 'Select an incident...',
            actor: 'Actor',
            select_actor: 'Select an actor...',
            evidence_id: 'Evidence ID',
            action: 'Action',
            recipient: 'Recipient',
            no_entries: 'No evidence chain entries found',
            enter_incident_id: 'Enter an incident ID to view evidence chain.',
            evidence_id_placeholder: 'e.g. EVD-2026-0001',
            evidence_id_help: 'A unique reference for the evidence item — a log file, disk image, or screenshot. A suggestion is prefilled; edit it to match your evidence catalogue.',
            select_incident_first: 'Select an incident above before adding an evidence entry.',
            for_incident: 'For incident',
            actions: {
                collected: 'Collected',
                shared: 'Shared',
                exported: 'Exported',
                archived: 'Archived',
            },
        },
        post_review: {
            page_title: 'Post-Incident Review',
            title: 'Post-Incident Review',
            new_review: 'New Review',
            select_incident: 'Select an incident...',
            lessons_learned: 'Lessons Learned',
            recommendations: 'Recommendations',
            improvement_actions: 'Improvement Actions',
            submit_for_review: 'Submit for Review',
            aria_submit_review: 'Submit review {name} for review',
            aria_sign_off: 'Sign off review {name}',
            sign_off: 'Sign Off',
            no_reviews: 'No post-incident reviews found',
            enter_incident_id: 'Enter an incident ID to view post-incident review.',
            one_per_line: 'One per line',
            statuses: {
                draft: 'Draft',
                in_review: 'In Review',
                signed_off: 'Signed Off',
                closed: 'Closed',
            },
        },
        activations: {
            title: 'Playbook Activations',
            activation_id: 'Activation ID',
            phases: 'Phases',
            activated_at: 'Activated At',
            activated_by: 'Activated By',
            deactivate: 'Deactivate',
            complete: 'Complete',
            deactivation_reason: 'Deactivation Reason',
            // Sprint 41.3.0 F9 — activation evidence
            evidence_button: 'Evidence',
            evidence_modal_title: 'Activation Evidence',
            no_evidence: 'No evidence attached to this activation.',
            upload_evidence_label: 'Upload activation evidence',
        },
    },
    // ---------- Program governance (Sprint 2.5.0) ----------
    program_governance: {
        // Sprint 48.2.5 — Forseti maturity walkthrough panel
        maturity_walkthrough: {
            run_label: 'Walk me through gaps',
            running: 'Walking gaps…',
            results_heading: 'Improvement roadmap ({count} gap{plural})',
            tokens_label: '≈ {count} tokens',
            no_gaps: 'No gaps detected — all domains at or above target.',
            col_priority: 'Priority',
            col_domain: 'Domain',
            col_levels: 'Current → Target',
            col_severity: 'Severity',
            col_actions: 'Remediation actions',
            citations_label: 'Citations',
            accept_label: 'Accept roadmap',
            reject_label: 'Reject draft',
            export_label: 'Export DOCX',
            exported_label: 'Roadmap exported',
            accept_hint: 'Accept records the roadmap on the persisted draft. Use Export DOCX to generate the deliverable; the operator paste-backs improvement_actions into the assessment.',
            resolve_summary: 'Resolution: {status}',
            error_generic: 'Could not run gap walkthrough. Try again or check the assessment status.',
        },
        // Sprint 48.2.6 — Forseti policy gap detection panel
        policy_gap: {
            run_label: 'Detect policy gaps',
            running: 'Detecting gaps…',
            results_heading: 'Policy gaps ({count})',
            tokens_label: '≈ {count} tokens',
            no_gaps: 'No coverage gaps detected.',
            col_severity: 'Severity',
            col_kind: 'Kind',
            col_clause: 'Clause / requirement',
            kind_missing: 'Missing clause',
            kind_weak: 'Weak coverage',
            kind_stale: 'Stale (review overdue)',
            remediation_label: 'Suggested remediation',
            citations_label: 'Citations',
            accept_label: 'Accept gap report',
            reject_label: 'Reject report',
            export_label: 'Export DOCX',
            accept_hint: 'Accept records the gap report on the persisted draft. Use Export DOCX to generate a Policy Audit Report; the operator drives the actual policy edit.',
            resolve_summary: 'Resolution: {status}',
            error_generic: 'Could not detect policy gaps. Try again or check the document status.',
        },
        // Sprint 48.2.7 — Forseti remediation plan generator panel
        remediation_plan_generator: {
            run_label: 'Generate plan from gaps',
            running: 'Generating plan…',
            results_heading: 'Remediation plan ({phase_count} phase{plural})',
            tokens_label: '≈ {count} tokens',
            phase_label: 'Phase {index}',
            window_label: 'Window',
            owner_label: 'Owner role',
            evidence_label: 'Evidence types required',
            tasks_label: 'Tasks',
            no_phases: 'No phases produced — input gap-set may be empty.',
            citations_label: 'Citations',
            accept_label: 'Accept plan',
            reject_label: 'Reject plan',
            export_label: 'Export DOCX',
            accept_hint: 'Accept creates phase-tasks linked to the plan record. Use Export DOCX for the full deliverable. Owner-role placeholders need operator follow-up.',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created',
            error_generic: 'Could not generate remediation plan. Try again or check the gap-set input.',
            // Sprint 48.2.7 fix — cross-module gap-set ingestion controls.
            source_picker_label: 'Source',
            source_all: 'All',
            source_consent: 'Consent gap analysis',
            source_policy: 'Policy gap detection',
            source_vendor: 'Vendor onboarding',
            load_upstream_label: 'Load from upstream',
            loading_upstream: 'Loading…',
            upstream_count: '{count} gap(s) loaded',
        },
        nav: {
            maturity_models: 'Maturity Models',
            maturity_assessments: 'Maturity Assessments',
            trend_dashboard: 'Trend Dashboard',
            reporting_periods: 'Reporting Periods',
            executive_summaries: 'Executive Summaries',
            audit_readiness: 'Audit Readiness',
            evidence_gaps: 'Evidence Gaps',
            program_health: 'Program Health',
            obligation_mappings: 'Obligation Mappings',
            governance_documents: 'Governance Documents',
            remediation_plans: 'Remediation Plans',
            governance_model: 'Governance Model',
            document_drafting: 'Document Drafting',
            document_issuance: 'Document Issuance',
            acknowledgment_tracking: 'Acknowledgment Tracking',
            change_log: 'Change Log',
            review_cadence: 'Review Cadence',
        },
        maturity_models: {
            title: 'Maturity Models',
            new: 'New Model',
            seed_defaults: 'Seed Defaults',
            domains: 'Domains',
            activate: 'Activate',
            no_models: 'No maturity models found',
            new_maturity_model: 'New Maturity Model',
            create_model: 'Create Model',
            load_error: 'Failed to load maturity models. Please try again.',
            create_error: 'Failed to create maturity model',
            status_error: 'Failed to update model status',
            seed_error: 'Failed to seed defaults',
            aria_seed_defaults: 'Seed default maturity models',
            aria_create_new: 'Create new maturity model',
            loading_sr: 'Loading maturity models',
            model_name_placeholder: 'e.g., Privacy Maturity Model',
            status_changed: 'Model status changed to {status}',
        },
        maturity_assessments: {
            title: 'Maturity Assessments',
            new: 'New Assessment',
            model: 'Model',
            assessor: 'Assessor',
            overall_score: 'Overall Score',
            overall_score_help: 'A weighted 0–5 maturity rating, calculated automatically when an assessment is finalized. Increase it by scoring each domain higher during evaluation, then moving the assessment through Submit for Review → Finalize.',
            assessed_at: 'Assessed At',
            no_assessments: 'No maturity assessments found.',
            load_error: 'Failed to load maturity assessments. Please try again.',
            create_error: 'Failed to create assessment',
            start_error: 'Failed to start assessment',
            archive_error: 'Failed to archive assessment',
            eval_error: 'Failed to load assessment for evaluation',
            eval_min_score: 'Please score at least one domain',
            eval_save_error: 'Failed to save domain evaluations',
            review_error: 'Failed to submit for review',
            finalize_error: 'Failed to finalize assessment',
            start: 'Start',
            start_confirm_title: 'Start Assessment',
            start_confirm_message: 'Are you sure you want to start this assessment?',
            archive_confirm_title: 'Archive Assessment',
            archive_confirm_message: 'Are you sure you want to archive this assessment?',
            evaluate_domains: 'Evaluate',
            evaluate_domains_title: 'Evaluate Domains',
            submit_for_review: 'Submit for Review',
            finalize: 'Finalize',
            archive: 'Archive',
            all_reviewers: 'All reviewers must approve',
            any_reviewer: 'Any reviewer may approve',
            select_level: 'Select maturity level...',
            findings_placeholder: 'Findings, notes, evidence references...',
            no_domains: 'No domains found for this model.',
        },
        trend_dashboard: {
            title: 'Trend Dashboard',
            loading: 'Loading trend data...',
            score_trend: 'Score Trend',
            domain_comparison: 'Domain Comparison',
            period: 'Period',
            load_error: 'Failed to load trend summary. Please try again.',
            metrics_load_error: 'Failed to load trend metrics. Please try again.',
            module: 'Module',
            metric: 'Metric',
            value: 'Value',
            trend: 'Trend',
            aria_filters: 'Trend metrics filters',
            showing_chart: 'Showing chart view',
            showing_table: 'Showing data table view',
        },
        reporting_periods: {
            title: 'Reporting Periods',
            new: 'New Period',
            period_name: 'Period Name',
            start_date: 'Start Date',
            end_date: 'End Date',
            no_periods: 'No reporting periods found.',
            load_error: 'Failed to load reporting periods. Please try again.',
            create_error: 'Failed to create reporting period',
            auto_generate_error: 'Failed to auto-generate reporting periods',
            status_error: 'Failed to update period status',
            start_collecting: 'Start Collecting',
            mark_as_draft: 'Mark as Draft',
            submit_for_review: 'Submit for Review',
            publish: 'Publish',
            auto_generate: 'Auto-generate reporting periods',
            period_label_placeholder: 'e.g., Q1 2026',
            status_updated: 'Period status updated',
        },
        executive_summaries: {
            title: 'Executive Summaries',
            new: 'New Summary',
            period: 'Period',
            generated_at: 'Generated At',
            generated_by: 'Generated By',
            no_summaries: 'No executive summaries found.',
            generate: 'Generate',
            load_error: 'Failed to load executive summaries. Please try again.',
            generate_error: 'Failed to generate executive summary',
            save_error: 'Failed to save executive summary',
        },
        audit_readiness: {
            title: 'Audit Readiness',
            loading: 'Loading audit readiness data...',
            overall_readiness: 'Overall Readiness',
            by_domain: 'By Domain',
            evidence_coverage: 'Evidence Coverage',
            gaps: 'Gaps',
            load_error: 'Failed to load latest readiness. Please try again.',
            history_error: 'Failed to load readiness history. Please try again.',
            calculate_error: 'Failed to calculate audit readiness',
            aria_recalculate: 'Recalculate audit readiness score',
            aria_current_readiness: 'Current audit readiness',
            aria_history: 'Readiness score history',
            recalculated: 'Audit readiness recalculated',
            loading_sr: 'Loading audit readiness data',
        },
        evidence_gaps: {
            title: 'Evidence Gaps',
            domain: 'Domain',
            control: 'Control',
            gap_type: 'Gap Type',
            severity: 'Severity',
            no_gaps: 'No evidence gaps found.',
            load_error: 'Failed to load evidence gaps. Please try again.',
            stats_error: 'Failed to load gap stats. Please try again.',
            create_error: 'Failed to create evidence gap',
            update_error: 'Failed to update evidence gap',
            status_error: 'Failed to update status',
            aria_create_new: 'Create new evidence gap',
            aria_statistics: 'Evidence gap statistics',
            loading_sr: 'Loading evidence gaps',
            status_updated: 'Gap status updated',
            entity_type_placeholder: 'e.g., vendor, system',
            all_modules: 'All Domains',
            all_severities: 'All Severities',
        },
        program_health: {
            title: 'Program Health',
            loading: 'Loading program health data...',
            overall_health: 'Overall Health',
            by_module: 'By Module',
            risk_areas: 'Risk Areas',
            recommendations: 'Recommendations',
            load_error: 'Failed to load program health data. Please try again.',
            aria_key_metrics: 'Program health key metrics',
            aria_module_scorecard: 'Module scorecard breakdown',
            loading_sr: 'Loading program health data',
            trending_up: 'Trending up',
            trending_down: 'Trending down',
            trending_stable: 'Stable',
            data_loaded: 'Program health data loaded',
        },
        obligation_mappings: {
            title: 'Obligation Mappings',
            create_mapping: 'Create Obligation Mapping',
            framework: 'Framework',
            select_framework: 'Select a framework...',
            obligation: 'Obligation',
            obligation_ref: 'Obligation Reference',
            obligation_description: 'Obligation Description',
            role: 'Role',
            assigned_role: 'Assigned Role',
            assigned_department: 'Assigned Department',
            select_department: 'Select a department...',
            control_ids: 'Control IDs',
            raci_assignments: 'RACI Assignments',
            create_raci: 'Create RACI Assignment',
            activity: 'Activity',
            activity_name: 'Activity Name',
            activity_description: 'Activity Description',
            responsible: 'Responsible',
            select_users: 'Select users...',
            accountable: 'Accountable',
            select_user: 'Select a user...',
            consulted: 'Consulted',
            informed: 'Informed',
            review_frequency: 'Review Frequency',
            frequency_monthly: 'Monthly',
            frequency_quarterly: 'Quarterly',
            frequency_semi_annual: 'Semi-Annual',
            frequency_annual: 'Annual',
            no_mappings_found: 'No obligation mappings found.',
            no_raci_found: 'No RACI assignments found.',
            mapping_created: 'Obligation mapping created',
            mapping_updated: 'Obligation mapping updated',
            mapping_deleted: 'Obligation mapping deleted',
            raci_created: 'RACI assignment created',
            raci_updated: 'RACI assignment updated',
            raci_deleted: 'RACI assignment deleted',
            aria_edit_mapping: 'Edit mapping {name}',
            aria_delete_mapping: 'Delete mapping {name}',
            aria_edit_raci: 'Edit RACI {name}',
            aria_delete_raci: 'Delete RACI {name}',
            confirm_delete_mapping: 'Delete this obligation mapping?',
            confirm_delete_raci: 'Delete this RACI assignment?',
            table_view: 'Table View',
            matrix_view: 'Matrix View',
            print_export: 'Print / Export',
            filter_framework: 'Filter by framework',
            no_matrix_data: 'No RACI assignments to display in matrix view.',
            aria_print_export: 'Print or export RACI matrix',
        },
        governance_documents: {
            title: 'Governance Documents',
            create_document: 'Create Governance Document',
            document_title: 'Document Title',
            document_type: 'Document Type',
            content_url: 'Content URL',
            content_url_placeholder: 'https://...',
            framework_codes: 'Framework Codes',
            select_frameworks: 'Select frameworks...',
            effective_date: 'Effective Date',
            review_date: 'Review Date',
            status: 'Status',
            version: 'Version',
            acknowledgments: 'Acknowledgments',
            acknowledge: 'Acknowledge',
            acknowledged_by: 'Acknowledged By',
            department: 'Department',
            no_documents_found: 'No governance documents found.',
            draft: 'Draft',
            review: 'Review',
            approved: 'Approved',
            issued: 'Issued',
            superseded: 'Superseded',
            document_created: 'Document created',
            document_acknowledged: 'Document acknowledged',
            policy: 'Policy',
            procedure: 'Procedure',
            standard: 'Standard',
            guideline: 'Guideline',
        },
        governance_model: {
            title: 'Governance Model',
            create_model: 'Create Governance Model',
            edit_model: 'Edit Governance Model',
            model_name: 'Model Name',
            description: 'Description',
            governance_type: 'Governance Type',
            type_centralized: 'Centralized',
            type_federated: 'Federated',
            type_hybrid: 'Hybrid',
            framework_codes: 'Framework Codes',
            select_frameworks: 'Select frameworks...',
            review_frequency: 'Review Frequency',
            freq_monthly: 'Monthly',
            freq_quarterly: 'Quarterly',
            freq_semi_annual: 'Semi-Annual',
            freq_annual: 'Annual',
            committees: 'Committees',
            committee_name: 'Committee Name',
            committee_members: 'Members (comma-separated)',
            committee_mandate: 'Mandate',
            add_committee: 'Add Committee',
            no_committees: 'No committees defined.',
            delegation_rules: 'Delegation Rules',
            from_role: 'From Role',
            to_role: 'To Role',
            scope: 'Scope',
            add_delegation_rule: 'Add Delegation Rule',
            control_owner_summary: 'Control Owner Summary',
            no_raci_data: 'No RACI data linked to this model.',
            activity: 'Activity',
            responsible: 'Responsible',
            accountable: 'Accountable',
            consulted: 'Consulted',
            informed: 'Informed',
            no_models_found: 'No governance models found.',
            model_created: 'Governance model created',
            model_updated: 'Governance model updated',
            model_deleted: 'Governance model deleted',
            confirm_delete: 'Delete this governance model?',
            expand: 'Expand',
            collapse: 'Collapse',
            aria_expand: 'Expand details for {name}',
            aria_edit: 'Edit governance model {name}',
            aria_delete: 'Delete governance model {name}',
            aria_export_csv: 'Export {name} as CSV',
            aria_export_json: 'Export {name} as JSON',
            // Sprint 43.7.0 Wave 5 — typed selectors + export error surfacing
            framework_read_permission_warning: 'You need the regulatory_framework:read permission to select frameworks. Ask an admin to grant it.',
            select_members: 'Select committee members',
            select_from_role: 'Select delegating role',
            select_to_role: 'Select receiving role',
            // Sprint 43.7.1 Wave 4 — markdown mandate + tabbed scope picker.
            // scope_tab_obligation renamed from scope_tab_control (Sprint 43.7.1
            // refinement) since the platform tracks regulatory obligations.
            mandate_markdown_hint: 'Markdown supported: **bold**, lists, links.',
            scope_tab_obligation: 'Obligation',
            scope_tab_framework: 'Framework',
            scope_tab_free_text: 'Free text',
            select_obligation: 'Select an obligation',
            select_framework: 'Select a framework',
            aria_remove_committee: 'Remove committee {n}',
            aria_remove_delegation_rule: 'Remove delegation rule {n}',
            no_frameworks_available: 'No frameworks available. Add regulatory frameworks first.',
        },
        document_drafting: {
            title: 'Document Drafting & Approval',
            document_title: 'Document Title',
            document_type: 'Document Type',
            status: 'Status',
            drafter: 'Drafter',
            deadline: 'Deadline',
            assign_drafter: 'Assign Drafter',
            select_drafter: 'Select a drafter...',
            assign: 'Assign',
            submit_for_review: 'Submit for Review',
            legal_review: 'Legal Review',
            reviewer: 'Reviewer',
            select_reviewer: 'Select a reviewer...',
            comments: 'Comments',
            review_action: 'Review Action',
            approve: 'Approve',
            request_changes: 'Request Changes',
            submit_review: 'Submit Review',
            executive_approval: 'Executive Approval',
            no_drafts_found: 'No documents in draft, review, or approval stage.',
            drafter_assigned: 'Drafter assigned',
            status_updated: 'Document status updated',
            review_approved: 'Review approved',
            changes_requested: 'Changes requested',
            document_approved: 'Document approved',
            aria_assign: 'Assign drafter to {name}',
            aria_submit_review: 'Submit {name} for review',
            aria_review: 'Review {name}',
            aria_approve: 'Approve {name}',
        },
        document_issuance: {
            title: 'Document Issuance & Distribution',
            document_title: 'Document Title',
            document_type: 'Document Type',
            status: 'Status',
            version: 'Version',
            effective_date: 'Effective Date',
            distribution_scope: 'Distribution Scope',
            distribution_placeholder: 'e.g., All departments, Legal team',
            issuance_number: 'Issuance Number',
            issuance_number_placeholder: 'e.g., ISS-2026-001',
            supersedes: 'Supersedes Document',
            none: 'None',
            issue_document: 'Issue Document',
            ready_for_issuance: 'Ready for Issuance',
            issued_documents: 'Issued Documents',
            no_issued_documents: 'No issued documents found.',
            view_lineage: 'View Lineage',
            supersession_lineage: 'Supersession Lineage',
            document_issued: 'Document issued',
            document_superseded: 'Document superseded',
            aria_issue: 'Issue {name}',
            aria_view_lineage: 'View supersession lineage for {name}',
        },
        acknowledgment_tracking: {
            title: 'Acknowledgment Tracking',
            document_title: 'Document',
            acknowledged_count: 'Acknowledged',
            pending_count: 'Pending',
            percentage: 'Completion',
            details: 'Details',
            send_reminder: 'Send Reminder',
            acknowledgment_details: 'Acknowledgment Details',
            user: 'User',
            department: 'Department',
            acknowledged_at: 'Acknowledged At',
            status: 'Status',
            acknowledged: 'Acknowledged',
            no_issued_documents: 'No issued documents to track.',
            no_acknowledgments: 'No acknowledgments recorded yet.',
            reminder_sent: 'Reminder sent for pending acknowledgments',
            aria_expand: 'View acknowledgment details for {name}',
            aria_send_reminder: 'Send reminder for {name}',
        },
        change_log: {
            title: 'Document Change Log',
            date: 'Date',
            document: 'Document',
            change_type: 'Change Type',
            actor: 'Actor',
            summary: 'Summary',
            export_csv: 'Export CSV',
            filters: 'Change log filters',
            filter_document: 'Filter by document',
            all_documents: 'All documents',
            filter_change_type: 'Filter by change type',
            all_types: 'All types',
            date_from: 'Date from',
            date_to: 'Date to',
            version_history: 'Version History',
            version_archive: 'Version Archive',
            no_entries: 'No change log entries found.',
            no_history: 'No version history available.',
            exported: 'Change log exported',
            aria_export: 'Export change log as CSV',
            aria_view_history: 'View version history for {name}',
        },
        review_cadence: {
            title: 'Review Cadence',
            configure_cadence: 'Configure Review Cadence',
            edit_cadence: 'Edit Review Cadence',
            document: 'Document',
            select_document: 'Select a document...',
            review_frequency: 'Review Frequency',
            freq_30: '30 days',
            freq_60: '60 days',
            freq_90: '90 days',
            freq_180: '180 days',
            freq_365: '365 days',
            last_reviewed: 'Last Reviewed',
            next_review: 'Next Review',
            status: 'Status',
            overdue: 'Overdue',
            on_track: 'On Track',
            days: 'days',
            horizon: 'Review Horizon',
            overdue_warning: 'Overdue Reviews',
            overdue_count: '{count} document(s) have overdue reviews.',
            no_cadences: 'No review cadences configured.',
            cadence_created: 'Review cadence created',
            cadence_updated: 'Review cadence updated',
            aria_edit: 'Edit review cadence for {name}',
        },
        remediation_plans: {
            title: 'Remediation Plans',
            create_plan: 'Create Remediation Plan',
            gap_id: 'Gap Reference',
            select_gap: 'Select a gap...',
            plan_title: 'Title',
            description: 'Description',
            priority: 'Priority',
            priority_critical: 'Critical',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            assigned_to: 'Assigned To',
            select_user: 'Select a user...',
            target_date: 'Target Date',
            framework_code: 'Framework',
            select_framework: 'Select a framework...',
            gap_finding: 'Gap Finding',
            action_items: 'Action Items',
            owner: 'Owner',
            deadline: 'Deadline',
            planned: 'Planned',
            in_progress: 'In Progress',
            completed: 'Completed',
            verified: 'Verified',
            no_plans_found: 'No remediation plans found.',
            evidence: 'Evidence',
            plan_created: 'Remediation plan created',
            action_items_hint: 'Action Items (one per line)',
        },
        // --- Enum label maps (Sprint 27.6.0) ---
        statuses: {
            identified: 'Identified',
            assigned: 'Assigned',
            in_remediation: 'In Remediation',
            resolved: 'Resolved',
            accepted_risk: 'Accepted Risk',
            planned: 'Planned',
            in_progress: 'In Progress',
            under_review: 'Under Review',
            finalized: 'Finalized',
            archived: 'Archived',
            draft: 'Draft',
            active: 'Active',
            upcoming: 'Upcoming',
            collecting: 'Collecting',
            published: 'Published',
            review: 'Review',
            approved: 'Approved',
            issued: 'Issued',
            superseded: 'Superseded',
            completed: 'Completed',
            verified: 'Verified',
        },
        severities: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        priorities: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        gap_types: {
            missing: 'Missing',
            expired: 'Expired',
            insufficient: 'Insufficient',
        },
        document_types: {
            policy: 'Policy',
            procedure: 'Procedure',
            standard: 'Standard',
            guideline: 'Guideline',
            template: 'Template (Internal)',
        },
        maturity_levels: {
            level_1: 'Level 1 — Initial',
            level_2: 'Level 2 — Developing',
            level_3: 'Level 3 — Defined',
            level_4: 'Level 4 — Managed',
            level_5: 'Level 5 — Optimizing',
        },
        grades: {
            excellent: 'Excellent',
            good: 'Good',
            fair: 'Fair',
            poor: 'Poor',
            critical: 'Critical',
            needs_improvement: 'Needs Improvement',
            not_assessed: 'Not Assessed',
        },
        modules: {
            data_mapping: 'Data Mapping',
            consent: 'Consent',
            governance: 'Governance',
            incidents: 'Incidents',
            monitoring: 'Monitoring',
            classification: 'Classification',
            vendor_risk: 'Vendor Risk',
            discovery: 'Discovery',
            deletion: 'Deletion',
        },
        cadences: {
            monthly: 'Monthly',
            quarterly: 'Quarterly',
            semi_annual: 'Semi-Annual',
            annual: 'Annual',
        },
        section_labels: {
            opening: 'Opening',
            key_metrics: 'Key Metrics',
            highlights: 'Highlights',
            concerns: 'Concerns',
            recommendations: 'Recommendations',
            outlook: 'Outlook',
        },
        out_of_100: '/ 100',
        count_gaps: '{count} gap',
        count_gaps_plural: '{count} gaps',
        count_summaries: '{count} summary',
        count_summaries_plural: '{count} summaries',
        count_assessments: '{count} assessment',
        count_assessments_plural: '{count} assessments',
        count_models: '{count} model',
        count_models_plural: '{count} models',
        count_periods: '{count} period',
        count_periods_plural: '{count} periods',
        count_metrics: '{count} metric',
        count_metrics_plural: '{count} metrics',
        unknown_user: 'Unknown User',
        unknown_gap: 'Unknown Gap',
        no_permission_title: 'Insufficient Permissions',
        no_permission_message: 'You do not have permission to access this page.',
        // --- AC info boxes (Sprint 32.2.0 QA) ---
        ac_vn_frameworks_title: 'VN Framework Coverage',
        ac_vn_frameworks_body: 'Maturity assessments cover all 6 Vietnamese regulatory frameworks: PDPL, Data Law, Telecom Law, Cybersecurity Law, AI Law, and E-Commerce Law — each with framework-specific assessment criteria.',
        ac_controller_processor_title: 'Controller vs. Processor',
        ac_controller_processor_body: 'Obligation mappings distinguish between data controller and data processor responsibilities. Assign roles using the RACI matrix to ensure each obligation has a clear owner, whether the organization acts as controller or processor.',
        ac_versioned_evidence_title: 'Versioned Evidence Trail',
        ac_versioned_evidence_body: 'Every status transition (Draft, Review, Approved, Issued) creates a timestamped, versioned record that serves as auditable evidence. The Change Log captures the full document lifecycle history.',
        ac_remediation_gap_link_title: 'Linked to Assessment Gaps',
        ac_remediation_gap_link_body: 'Each remediation plan links directly to an identified assessment gap. Assign an owner and set a target deadline to track resolution progress from gap to closure.',
        ac_annual_attestation_title: 'Annual Attestation Cycle',
        ac_annual_attestation_body: 'Reporting periods align with the Vietnamese Data Law annual attestation requirement. Use the "Annual" cadence (365-day cycle) to generate attestation-aligned periods automatically.',
        ac_evidence_per_obligation_title: 'Evidence per Framework Obligation',
        ac_evidence_per_obligation_body: 'Evidence gaps identify missing, expired, or insufficient proof for each framework obligation. Filter by domain to see which regulatory requirements lack supporting evidence.',
        ac_cross_framework_title: 'Cross-Framework Completeness',
        ac_cross_framework_body: 'Audit readiness provides a cross-framework completeness view. Component scores reflect coverage across all mapped frameworks, highlighting areas that need attention before an audit.',
        ac_cadence_default_title: 'Default Review Cadence',
        ac_cadence_default_body: 'The default review cadence is 180 days. Overdue reviews are automatically flagged. Adjust frequency per document as needed — options range from 30 to 365 days.',
        ac_templates_internal_title: 'Template Classification',
        ac_templates_internal_body: 'Templates are classified as internal documents. Use the "Template" document type to mark reusable governance artifacts with internal-only distribution scope.',
    },
    // ---------- MCP Phase 2 — Pending writes queue (Sprint 48.3.0) ----------
    mcp_pending: {
        title: 'MCP — Pending writes',
        subtitle: 'External Claude clients propose writes here. Approve or decline each entry — Forseti only executes after admin sign-off.',
        no_pending: 'No pending writes.',
        col_tool: 'Tool',
        col_preview: 'Preview',
        col_key: 'API key',
        col_requested: 'Requested',
        col_expires: 'Expires',
        col_status: 'Status',
        status_pending: 'Pending',
        status_approved: 'Approved',
        status_declined: 'Declined',
        status_expired: 'Expired',
        status_executed: 'Executed',
        approve_label: 'Approve',
        decline_label: 'Decline',
        refresh_label: 'Refresh',
        error_generic: 'Could not load pending writes.',
        confirm_approve: 'Approving will execute the tool against your org with the API key holder’s resolved permissions. Continue?',
        confirm_decline: 'Decline this pending write? This cannot be undone.',
        advisory_banner: 'Pending writes expire 24 hours after creation. The hourly GC sweep auto-declines expired entries.',
    },
    // ---------- Audit walkthrough + Privilege creep (Sprint 48.2.9) ----------
    audit_walkthrough: {
        title: 'Forseti — Audit walkthrough generator',
        subtitle: 'Generate a structured walkthrough for an audit kickoff: per-control opening question, expected evidence, follow-up probes, potential gaps.',
        audit_picker_label: 'Audit',
        audit_picker_hint: 'Pick a planned audit to generate a walkthrough.',
        run_label: 'Generate walkthrough',
        running: 'Generating…',
        results_heading: 'Walkthrough — {count} control(s)',
        tokens_label: '≈ {count} tokens',
        no_controls: 'No controls in scope. Add controls on the audit record.',
        col_control: 'Control',
        col_question: 'Opening question',
        col_evidence: 'Evidence',
        col_gaps: 'Potential gaps',
        expected_evidence_label: 'Expected evidence',
        followup_probes_label: 'Follow-up probes',
        accept_label: 'Accept walkthrough',
        reject_label: 'Reject',
        export_label: 'Export DOCX',
        resolve_summary: 'Resolution: {status}',
        error_generic: 'Could not generate walkthrough. Try again or check the audit status.',
    },
    privilege_creep: {
        title: 'Forseti — Privilege creep detection',
        subtitle: 'Periodic scan of role assignments. Findings are advisory — Forseti never auto-revokes.',
        run_label: 'Scan now',
        running: 'Scanning…',
        findings_heading: '{count} finding(s)',
        no_findings: 'No privilege creep flagged.',
        col_user: 'User',
        col_job_function: 'Job function',
        col_severity: 'Severity',
        col_unexpected: 'Unexpected permissions',
        col_remediation: 'Suggested remediation',
        col_status: 'Status',
        severity_critical: 'Critical',
        severity_high: 'High',
        severity_medium: 'Medium',
        severity_low: 'Low',
        status_open: 'Open',
        status_dismissed: 'Dismissed',
        status_remediated: 'Remediated',
        dismiss_label: 'Dismiss',
        remediate_label: 'Mark remediated',
        note_label: 'Note (optional)',
        error_generic: 'Could not scan privilege creep. Try again later.',
        advisory_banner: 'These findings are advisory. Forseti never revokes permissions automatically — review with the role owner before acting.',
    },
    // ---------- Compliance Rules (Sprint 48.2.8) ----------
    compliance_rules: {
        rule_mapping: {
            title: 'Compliance rule auto-mapping',
            subtitle: 'Forseti walks an installed regulatory pack and suggests new or refreshed compliance rules grounded in the source articles.',
            pack_picker_label: 'Regulatory pack',
            pack_picker_hint: 'Select an installed pack to map.',
            run_label: 'Map from pack',
            running: 'Mapping…',
            results_heading: '{count} suggestion(s)',
            tokens_label: '≈ {count} tokens from your monthly bucket',
            no_suggestions: 'No suggestions — pack content may already be fully covered by existing rules.',
            kind_new: 'New rule',
            kind_refresh: 'Refresh existing',
            kind_no_change: 'No change required',
            col_kind: 'Kind',
            col_title: 'Proposed title',
            col_existing: 'Existing rule',
            col_source: 'Source',
            proposed_body_label: 'Proposed body',
            source_quote_label: 'Source quote',
            accept_selected: 'Accept selected',
            reject_all: 'Reject all',
            accept_all: 'Accept all',
            resolve_summary: 'Resolution: {status} · {created} rule(s) created',
            error_generic: 'Could not run rule mapping. Try again or check the pack code.',
        },
    },
    // ---------- Access Accountability (Sprint 2.6.0) ----------
    access_accountability: {
        nav: {
            session_dashboard: 'Session Dashboard',
            access_log: 'Access Log',
            justification_review: 'Justification Review',
            investigations: 'Investigations',
            pattern_analysis: 'Pattern Analysis',
            anchoring_status: 'Anchoring Status',
            configuration: 'Configuration',
            access_policies: 'Access Policies',
            review_campaigns: 'Review Campaigns',
            evidence_packages: 'Evidence Packages',
        },
        session_dashboard: {
            title: 'Session Dashboard',
            loading: 'Loading dashboard data...',
            total_sessions: 'Total Sessions',
            active_sessions: 'Active Sessions',
            avg_duration: 'Avg Duration',
            concurrent_peak: 'Concurrent Peak',
            auth_method_breakdown: 'Auth Method Breakdown',
            geo_country_breakdown: 'Geo Country Breakdown',
            recent_session_events: 'Recent Session Events',
            user_id: 'User ID',
            session_id: 'Session ID',
            ip_address: 'IP Address',
            auth_method: 'Auth Method',
            started_at: 'Started At',
            event_type: 'Event Type',
            all_event_types: 'All Event Types',
            filter_by_user: 'Filter by User ID...',
            no_active_sessions: 'No active sessions',
            no_session_events: 'No session events found',
            revoke: 'Revoke',
            revoke_session: 'Revoke Session',
            revoke_reason: 'Reason',
            revoke_placeholder: 'Provide a reason for revoking this session...',
            session_label: 'Session:',
            load_failed: 'Failed to load session dashboard. Please try again.',
            events_load_failed: 'Failed to load session events. Please try again.',
            revoke_failed: 'Failed to revoke session',
            session_events_loaded: '{n} session events loaded',
            event_types: {
                session_created: 'Session Created',
                session_refreshed: 'Session Refreshed',
                session_expired: 'Session Expired',
                session_revoked: 'Session Revoked',
                session_terminated: 'Session Terminated',
                login_success: 'Login Success',
                login_failure: 'Login Failure',
                logout: 'Logout',
                token_refresh: 'Token Refresh',
                mfa_challenge: 'MFA Challenge',
            },
        },
        access_log: {
            title: 'Access Log',
            user_id: 'User ID',
            resource: 'Resource',
            action: 'Action',
            ip_address: 'IP Address',
            timestamp: 'Timestamp',
            no_logs: 'No access log entries found.',
            filter_by_user: 'Filter by User ID...',
            load_failed: 'Failed to load access log. Please try again.',
            export_success: 'Export initiated successfully',
            export_failed: 'Failed to export access log',
            record_count: 'record',
            record_count_plural: 'records',
            unclassified: 'Unclassified',
            classification_missing: 'Classification Missing',
            response_code: 'Response Code',
            classification_levels: {
                public: 'Public',
                internal: 'Internal',
                confidential: 'Confidential',
                restricted: 'Restricted',
                unclassified: 'Unclassified',
            },
        },
        justification_review: {
            title: 'Justification Review',
            user_id: 'User ID',
            resource: 'Resource',
            justification: 'Justification',
            submitted_at: 'Submitted At',
            reviewer: 'Reviewer',
            no_reviews: 'No justification reviews found.',
            load_failed: 'Failed to load justifications. Please try again.',
            review_failed: 'Failed to review justification',
            decline_failed: 'Failed to decline access',
            justification_count: 'justification',
            justification_count_plural: 'justifications',
            reviewed: 'Reviewed',
            concerning: 'Concerning',
            escalated: 'Escalated',
            link_investigation_placeholder: 'Link to an investigation (optional)',
            review_queue: 'Review Queue',
            access_id_label: 'Access ID',
            access_id_placeholder: 'Access log entry ID',
            select_user_placeholder: 'Select user (optional)',
            resource_type_label: 'Resource Type',
            resource_type_placeholder: 'e.g. document, database',
            resource_id_label: 'Resource ID',
            resource_id_placeholder: 'Resource identifier',
            reason_placeholder: 'Reason for accessing this resource...',
            create_failed: 'Failed to create justification',
            unclassified: 'Unclassified',
            review_statuses: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                concerning: 'Concerning',
                escalated: 'Escalated',
            },
            reason_codes: {
                business_need: 'Business Need',
                legal_obligation: 'Legal Obligation',
                data_subject_request: 'Data Subject Request',
                audit_requirement: 'Audit Requirement',
                incident_response: 'Incident Response',
                research: 'Research',
                administrative: 'Administrative',
                other: 'Other',
            },
        },
        investigations: {
            title: 'Investigation Workspace',
            new: 'New Investigation',
            subject: 'Subject',
            investigator: 'Investigator',
            opened_at: 'Opened At',
            findings: 'Findings',
            no_investigations: 'No investigations found.',
            load_failed: 'Failed to load investigations. Please try again.',
            create_failed: 'Failed to create investigation',
            timeline_add_failed: 'Failed to add timeline entry',
            link_access_failed: 'Failed to link access IDs',
            link_justification_failed: 'Failed to link justification IDs',
            close_failed: 'Failed to close investigation',
            export_success: 'Export initiated successfully',
            export_failed: 'Failed to export investigation',
            refresh_failed: 'Failed to refresh investigation detail. Please try again.',
            update_failed: 'Failed to update investigation',
            investigation_count: 'investigation',
            investigation_count_plural: 'investigations',
            title_placeholder: 'Investigation title',
            description_placeholder: 'Detailed description of the investigation...',
            assignee_placeholder: 'User ID of assignee (optional)',
            timeline_action_placeholder: 'e.g., Reviewed access logs',
            timeline_note_placeholder: 'Optional note',
            access_ids_placeholder: 'access-id-1, access-id-2',
            justification_ids_placeholder: 'justification-id-1, justification-id-2',
            findings_placeholder: 'Summarize the investigation findings...',
            by_actor: 'by',
            close_investigation: 'Close Investigation',
            add_timeline_failed: 'Failed to add timeline entry.',
            fetch_failed: 'Failed to fetch investigations.',
            change_status: 'Change investigation status',
            details: 'Investigation details',
            link_evidence: 'Link evidence',
            link_justifications: 'Link justifications',
            access_ids_label: 'Access IDs',
            justification_ids_label: 'Justification IDs',
            access_ids_link_placeholder: 'access-id-1, access-id-2',
            justification_ids_link_placeholder: 'justification-id-1, justification-id-2',
            investigations_loaded: '{n} investigations loaded',
            status_changed_to: 'Status changed to {status}',
            add_timeline_entry: 'Add timeline entry',
            add_access_ids: 'Add access IDs',
            add_justification_ids: 'Add justification IDs',
            investigation_types: {
                anomaly_review: 'Anomaly Review',
                complaint_driven: 'Complaint Driven',
                scheduled_review: 'Scheduled Review',
                whistleblower_report: 'Whistleblower Report',
            },
            severity_labels: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            status_labels: {
                open: 'Open',
                in_progress: 'In Progress',
                pending_review: 'Pending Review',
                closed_substantiated: 'Closed (Substantiated)',
                closed_unsubstantiated: 'Closed (Unsubstantiated)',
                closed_inconclusive: 'Closed (Inconclusive)',
            },
            disposition_labels: {
                substantiated: 'Substantiated',
                unsubstantiated: 'Unsubstantiated',
                inconclusive: 'Inconclusive',
            },
        },
        pattern_analysis: {
            title: 'Pattern Analysis',
            loading: 'Loading pattern data...',
            anomalies_detected: 'Anomalies Detected',
            patterns: 'Patterns',
            by_user: 'By User',
            by_resource: 'By Resource',
            time_range: 'Time Range',
            load_failed: 'Failed to analyze patterns. Please try again.',
            top_resources: 'Top 10',
            access_by_hour: 'Access by Hour of Day',
            access_by_day: 'Access by Day of Week',
            fetch_failed: 'Failed to fetch pattern analysis.',
            analysis_loaded: 'Pattern analysis loaded for {user}, {n} sessions found',
            date_format_placeholder: 'DD/MM/YYYY',
            access_by_hour_heading: 'Access by Hour of Day',
            access_by_day_heading: 'Access by Day of Week',
            by_classification: 'By Classification Level',
            top_accessed_resources: 'Most Accessed Resources',
            user_access_summary: 'User Access Summary',
            days: {
                sunday: 'Sunday',
                monday: 'Monday',
                tuesday: 'Tuesday',
                wednesday: 'Wednesday',
                thursday: 'Thursday',
                friday: 'Friday',
                saturday: 'Saturday',
            },
            linked_access_logs: 'Linked Access Logs',
            linked_access_logs_desc: 'Access log entries associated with the detected patterns for this user.',
        },
        anchoring_status: {
            title: 'Anchoring Status',
            loading: 'Loading anchoring data...',
            anchored: 'Anchored',
            pending: 'Pending',
            failed: 'Failed',
            last_anchor: 'Last Anchor',
            no_records: 'No anchoring records found.',
            status_load_failed: 'Failed to load anchoring status. Please try again.',
            proofs_load_failed: 'Failed to load access proofs. Please try again.',
            verify_failed: 'Failed to verify proof chain. Please try again.',
            anchor_failed: 'Failed to anchor access proof',
            batch_anchor_failed: 'Failed to batch anchor access proofs',
            proof_count: 'proof',
            proof_count_plural: 'proofs',
            anchor_single: 'Anchor Single',
            anchor_batch: 'Anchor Batch',
            access_id_placeholder: 'Enter access ID to anchor',
            batch_ids_placeholder: 'access-id-1, access-id-2, access-id-3',
            verify_chain: 'Verify Chain',
            chain_breaks: 'Chain Breaks',
            total_checked: 'Total Checked',
            anchor_placeholder: 'Enter access ID to anchor',
            fetch_failed: 'Failed to fetch anchoring status.',
            fetch_proofs_failed: 'Failed to fetch proofs.',
            access_id: 'Access ID',
            chain_sequence: 'Chain Seq',
            proof_hash_label: 'Proof Hash',
            batch_id: 'Batch ID',
            proofs_loaded: '{n} access proofs loaded',
            anchor_statuses: {
                pending: 'Pending',
                submitted: 'Submitted',
                confirmed: 'Confirmed',
                failed: 'Failed',
            },
            anchor_types: {
                sha256_chain: 'SHA-256 Chain',
                blockchain_anchor: 'Blockchain Anchor',
                zkp_stub: 'ZKP Stub',
            },
        },
        configuration: {
            title: 'Configuration',
            session_settings: 'Session Settings',
            log_retention: 'Log Retention (days)',
            anomaly_threshold: 'Anomaly Threshold',
            anchoring_enabled: 'Anchoring Enabled',
            save_config: 'Save Configuration',
            load_failed: 'Failed to load configuration. Please try again.',
            save_success: 'Configuration saved successfully',
            save_failed: 'Failed to save configuration. Please try again.',
            justification_rules: 'Justification Rules',
            reason_codes: 'Reason Codes',
            anchoring_settings: 'Anchoring Settings',
            anchoring_method: 'Anchoring Method',
            anchoring_frequency: 'Anchoring Frequency',
            batch_interval: 'Batch Interval (minutes)',
            eligibility_criteria: 'Eligibility Criteria',
            add_criterion_placeholder: 'Add eligibility criterion...',
            retention_settings: 'Retention Settings',
            standard_retention: 'Standard Retention (days)',
            sensitive_retention: 'Sensitive Retention (days)',
            working_hours: 'Working Hours',
            work_start: 'Start Time',
            work_end: 'End Time',
            timezone: 'Timezone',
            timezone_placeholder: 'e.g., Asia/Ho_Chi_Minh',
            working_days: 'Working Days',
            concurrent_sessions: 'Concurrent Session Limits',
            concurrent_threshold: 'Concurrent Session Threshold',
            classification_level: 'Classification Level',
            prompt_mode: 'Prompt Mode',
            grace_period: 'Grace Period (s)',
            code_placeholder: 'code',
            is_custom: 'Custom',
            label_placeholder: 'Label',
            description_placeholder: 'Description',
            custom: 'Custom',
            fetch_failed: 'Failed to fetch configuration.',
            classification_levels: {
                public: 'Public',
                internal: 'Internal',
                confidential: 'Confidential',
                restricted: 'Restricted',
                unclassified: 'Unclassified',
            },
            prompt_modes: {
                reason_only: 'Reason Only',
                reason_text: 'Reason + Text',
                reason_text_supervisor: 'Reason + Text + Supervisor',
            },
            anchoring_methods: {
                sha256_chain: 'SHA-256 Chain',
                blockchain_anchor: 'Blockchain Anchor',
                zkp_stub: 'ZKP Stub',
            },
            anchoring_frequencies: {
                realtime: 'Real-time',
                batched: 'Batched',
                on_demand: 'On Demand',
            },
        },
        access_policies: {
            title: 'Access Policies',
            create_policy: 'Create Access Policy',
            policy_name: 'Policy Name',
            framework_code: 'Framework',
            select_framework: 'Select a framework...',
            review_frequency: 'Review Frequency',
            scope: 'Scope',
            no_policies_found: 'No access policies found.',
            policy_created: 'Access policy created',
            frequency_labels: {
                monthly: 'Monthly',
                quarterly: 'Quarterly',
                semi_annual: 'Semi-Annual',
                annual: 'Annual',
            },
        },
        review_campaigns: {
            title: 'Access Review Campaigns',
            create_campaign: 'Create Review Campaign',
            reviewer: 'Reviewer',
            due_date: 'Due Date',
            certify: 'Certify',
            no_campaigns_found: 'No access review campaigns found.',
            status_open: 'Open',
            status_in_review: 'In Review',
            status_completed: 'Completed',
            campaign_created: 'Review campaign created',
            review_certified: 'Access review certified',
            certify_campaign: 'Certify campaign {id}',
            policy_id: 'Policy ID',
            select_policy: 'Select a policy...',
            reviewer_ids_label: 'Reviewers',
            select_reviewers: 'Select reviewers...',
            status_labels: {
                open: 'Open',
                in_review: 'In Review',
                completed: 'Completed',
            },
            evidence_package_id: 'Evidence Package ID',
        },
        evidence_packages: {
            title: 'Evidence Packages',
            generate_package: 'Generate Evidence Package',
            export_type: 'Export Type',
            package_type: 'Package Type',
            framework_scope: 'Framework Scope',
            period: 'Period',
            generated_by: 'Generated By',
            generated_at: 'Generated At',
            no_packages_found: 'No evidence packages found.',
            package_generated: 'Evidence package generated',
            export_type_full: 'Full Export',
            export_type_summary: 'Summary',
            export_type_certification_only: 'Certification Only',
            package_type_access_review: 'Access Review',
            package_type_investigation: 'Investigation',
            package_type_certification: 'Certification',
            package_type_authority_request: 'Authority Request',
            period_start: 'Period Start',
            period_end: 'Period End',
            select_frameworks: 'Select frameworks...',
            completeness_col: 'Completeness',
            status_col: 'Status',
            generated_by_col: 'Generated By',
        },
        evidence_package_detail: {
            title: 'Evidence Package Detail',
            not_found: 'Evidence package not found.',
            package_metadata: 'Package Metadata',
            status: 'Status',
            item_count: 'Item Count',
            disposition: 'Disposition',
            closed_at: 'Closed At',
            completeness_dashboard: 'Completeness Dashboard',
            no_completeness_data: 'No completeness data available.',
            controls_covered: 'controls covered',
            missing: 'Missing',
            sign_off_tracker: 'Sign-Off Tracker',
            stage_framework_owner: 'Framework Owner',
            stage_legal_compliance: 'Legal & Compliance',
            stage_internal_audit: 'Internal Audit',
            approved: 'Approved',
            returned: 'Returned',
            pending: 'Pending',
            approve: 'Approve',
            return_btn: 'Return',
            sign_off_submitted: 'Sign-off submitted',
            artifacts: 'Artifacts',
            no_artifacts: 'No artifacts found.',
            template_id: 'Template ID',
            description: 'Description',
            classification: 'Classification',
            evidence_type: 'Evidence Type',
            framework_section: 'Framework Section',
            history: 'Approval History',
            expand: 'Expand',
            collapse: 'Collapse',
            action: 'Action',
            actor: 'Actor',
            timestamp: 'Timestamp',
            comment: 'Comment',
            close_case: 'Close Case',
            confirm_close: 'Confirm Close',
            closure_notes: 'Closure Notes',
            disposition_compliant: 'Compliant',
            disposition_non_compliant: 'Non-Compliant',
            disposition_partial: 'Partial',
            case_closed: 'Evidence package closed',
            export_package: 'Export Package',
            export_complete: 'Evidence package exported',
        },
    },
    // ---------- Consent Properties (Sprint 2.7.0) ----------
    consent_properties: {
        nav: {
            dashboard: 'Dashboard',
            properties: 'Properties',
            groups: 'Groups',
            bindings: 'Bindings',
            change_requests: 'Change Requests',
            comparison: 'Comparison',
            inconsistencies: 'Inconsistencies',
            snapshot_schedules: 'Snapshot Schedules',
            golden_templates: 'Golden Templates',
            evidence_packs: 'Evidence Packs',
            audit: 'Audit',
        },
        select_property: 'Select property…',
        select_policy: 'Select policy…',
        select_properties: 'Select properties…',
        select_target_properties: 'Select target properties…',
        select_owner: 'Select owner…',
        select_technical_contact: 'Select technical contact…',
        property_statuses: {
            active: 'Active',
            inactive: 'Inactive',
            pending: 'Pending',
            decommissioned: 'Decommissioned',
        },
        dashboard: {
            title: 'Cross-Property Dashboard',
            loading: 'Loading dashboard data...',
            total_properties: 'Total Properties',
            active_properties: 'Active Properties',
            snapshot_coverage: 'Snapshot Coverage',
            properties_covered: 'properties covered',
            total_bindings: 'Total Bindings',
            deployed_bindings: 'Deployed',
            pending_bindings: 'Pending Deployment',
            deployment_progress: 'Deployment Progress',
            on_target: 'On target',
            below_target: 'Below target',
            target_label: 'Target',
            total_groups: 'Total Groups',
            pending_changes: 'Pending Changes',
            inconsistencies: 'Inconsistencies',
            recent_activity: 'Recent Activity',
            no_recent_activity: 'No recent activity.',
            fetch_error: 'Failed to load dashboard. Please try again.',
        },
        properties: {
            title: 'Property Registry',
            new: 'New Property',
            edit: 'Edit Property',
            property_name: 'Name',
            property_type: 'Type',
            identifier: 'Identifier',
            environment: 'Environment',
            owner_id: 'Owner ID',
            technical_contact_id: 'Technical Contact ID',
            decommission: 'Decommission',
            confirm_decommission: 'Are you sure you want to decommission this property?',
            no_properties: 'No properties found.',
            all_environments: 'All Environments',
            all_types: 'All Types',
            cmp_instance_id: 'CMP Instance ID',
            deployment_date: 'Deployment Date',
            identifier_placeholder: 'e.g., www.example.com or com.example.app',
            owner_name: 'Owner',
            technical_contact_name: 'Technical Contact',
            sensitive_data_categories: 'Sensitive Data Categories',
            sensitive_data_categories_placeholder: 'Select sensitive data categories',
            requires_enhanced_review: 'Requires Enhanced Review',
            sensitive_review_status: 'Sensitive Data Review',
            sensitive_review_pending: 'Pending Review',
            sensitive_review_approved: 'Approved',
            sensitive_review_rejected: 'Rejected',
            sensitive_review_submit: 'Submit Review',
            sensitive_review_approve: 'Approve',
            sensitive_review_reject: 'Reject',
            sensitive_review_notes: 'Review Notes',
            sensitive_review_notes_placeholder: 'Enter review notes...',
            record_withdrawal: 'Record Withdrawal',
            withdrawal_subject_id: 'Subject ID',
            withdrawal_consent_id: 'Consent ID',
            withdrawal_reason: 'Reason',
            withdrawal_downstream_systems: 'Downstream Systems',
            withdrawal_downstream_placeholder: 'system-1, system-2',
            withdrawal_success: 'Withdrawal propagated successfully.',
            withdrawal_error: 'Failed to propagate withdrawal.',
            withdrawal_data_subject_id: 'Data Subject ID',
            withdrawal_data_subject_id_placeholder: 'e.g. usr_abc123',
            withdrawal_consent_record_id: 'Consent Record ID',
            withdrawal_consent_record_id_placeholder: 'e.g. consent_xyz789',
            withdrawal_purposes: 'Withdrawn Purposes',
            withdrawal_purposes_placeholder: 'Select purposes to withdraw consent for...',
            withdrawal_withdrawn_at: 'Withdrawal Date',
            withdrawal_tasks_created: 'Tasks Created',
            withdrawal_affected_bindings: 'Affected Bindings',
            withdrawal_affected_activities: 'Affected Processing Activities',
            withdrawal_history: 'Withdrawal History',
        },
        groups: {
            title: 'Property Groups',
            new: 'New Group',
            group_name: 'Group Name',
            member_count: 'Members',
            no_groups: 'No property groups found.',
            policies: 'Policies',
            confirm_delete: 'Are you sure you want to delete this group?',
        },
        bindings: {
            title: 'Binding Matrix',
            property: 'Property',
            purpose: 'Purpose',
            legal_basis: 'Legal Basis',
            bound: 'Bound',
            no_bindings: 'No bindings found.',
            bind_policy: 'Bind Policy',
            bulk_bind: 'Bulk Bind',
            bind_policy_to_property: 'Bind Policy to Property',
            bulk_bind_policy: 'Bulk Bind Policy',
            unbound: 'Unbound',
            policy_id: 'Policy ID',
            version_placeholder: 'e.g., 1.0.0',
            property_ids_placeholder: 'prop-id-1, prop-id-2, prop-id-3',
            binding_name: 'Binding',
            property_name: 'Property Name',
            policy_name: 'Policy Name',
            bindings_list: 'Bindings List',
            binding_actions: {
                start_deploy: 'Start Deploy',
                mark_deployed: 'Mark Deployed',
                rollback: 'Rollback',
            },
        },
        change_requests: {
            title: 'Change Request Pipeline',
            new: 'New Change Request',
            requester: 'Requester',
            change_type: 'Change Type',
            property: 'Property',
            submitted_at: 'Submitted At',
            no_requests: 'No change requests found.',
            fetch_pipeline_error: 'Failed to fetch pipeline. Please try again.',
            fetch_list_error: 'Failed to fetch change requests. Please try again.',
            create_error: 'Failed to create change request. Please try again.',
            action_error: 'Failed to perform action. Please try again.',
            approver_name: 'Approver',
            placeholder_target_props: 'prop-id-1, prop-id-2',
            view_all: 'All',
            view_my_properties: 'My Properties',
            stage_requests: '{stage} requests',
        },
        comparison: {
            title: 'Comparison Workspace',
            select_properties: 'Select Properties to Compare',
            compare: 'Compare',
            no_comparison: 'Select at least two properties to compare.',
            fetch_error: 'Failed to fetch comparison reports. Please try again.',
            run_error: 'Failed to run comparison. Please try again.',
            save_notes_error: 'Failed to save notes. Please try again.',
            scan_complete: 'Inconsistency scan completed successfully.',
            scan_error: 'Failed to run inconsistency scan. Please try again.',
            min_two_properties: 'Please provide at least 2 property IDs for comparison.',
            view_inconsistencies: 'View Inconsistencies',
            click_to_select: 'Click to select two or more properties',
            no_properties_available: 'No properties available',
            no_matching_properties: 'No matching properties',
            select_at_least_two_props: 'Select at least two properties from the dropdown above.',
            no_differences_found: 'No differences found between the selected properties.',
            found_inconsistencies: 'Found {count} inconsistencies across {properties} properties and {bindings} bindings.',
        },
        inconsistencies: {
            title: 'Inconsistency List',
            property: 'Property',
            field: 'Field',
            expected: 'Expected',
            actual: 'Actual',
            severity: 'Severity',
            no_inconsistencies: 'No inconsistencies found.',
            trend: 'Inconsistency Trend',
            trend_chart_label: 'Inconsistency trend chart showing counts over time',
            acknowledge: 'Acknowledge',
            resolve: 'Resolve',
            suppress: 'Suppress',
            cr_id_placeholder: 'CR ID',
            view_all: 'All',
            view_my_properties: 'My Properties',
            view_dpo_review: 'DPO Review Queue',
            change_request_id_required: 'Change request ID is required to resolve this inconsistency.',
        },
        snapshot_schedules: {
            title: 'Snapshot Schedules',
            new: 'New Schedule',
            schedule_name: 'Schedule Name',
            frequency: 'Frequency',
            next_run: 'Next Run',
            last_run: 'Last Run',
            no_schedules: 'No snapshot schedules found.',
            target_type: 'Target Type',
            target_id: 'Target ID',
            custom_frequency_days: 'Custom Frequency (days)',
            time_of_day: 'Time of Day',
            time: 'Time',
            retention_days: 'Retention (days)',
            execute_now: 'Execute Now',
            placeholder_days: 'e.g., 10',
            target_name: 'Target Name',
        },
        golden_templates: {
            title: 'Golden Templates',
            new: 'New Template',
            template_name: 'Template Name',
            property_type: 'Property Type',
            no_templates: 'No golden templates found.',
            applicable_group_ids: 'Applicable Group IDs (comma-separated)',
            applicable_groups: 'Applicable Groups',
            purposes: 'Purposes',
            legal_bases: 'Legal Bases',
            banner_config: 'Banner Config',
            consent_settings: 'Consent Settings',
            group_ids_placeholder: 'group-id-1, group-id-2',
            apply_to_property: 'Apply to Property',
            apply_to_group: 'Apply to Group',
            apply_success: 'Template applied successfully.',
            applications: 'Application History',
            no_applications: 'No template applications yet.',
            no_configuration_details: 'Template has no configuration details. Edit the template to add purposes, legal bases, banner settings, or consent settings.',
        },
        rollouts: {
            title: 'Template Rollouts',
            rollout_status: 'Rollout Status',
            confirmed: 'Confirmed',
            pending: 'Pending',
            failed: 'Failed',
            confirm: 'Confirm Property',
            confirm_success: 'Property confirmed in rollout.',
            can_close: 'All properties confirmed — rollout complete',
            cannot_close: 'Awaiting property confirmations',
            no_rollouts: 'No rollouts found.',
            in_progress: 'In Progress',
            completed: 'Completed',
            cancelled: 'Cancelled',
            reject: 'Reject Property',
            reject_success: 'Property rejected from rollout.',
            confirm_all: 'Confirm All',
            reject_all: 'Reject All',
            confirm_label: 'Confirm rollout for',
            reject_label: 'Reject rollout for',
            confirm_all_label: 'Confirm All Pending Rollouts',
            reject_all_label: 'Reject All Pending Rollouts',
            no_properties_in_rollout: 'This rollout has no properties.',
        },
        analytics: {
            title: 'Property Analytics',
            opt_in_rate: 'Opt-in Rate',
            opt_out_rate: 'Opt-out Rate',
            withdrawal_rate: 'Withdrawal Rate',
            total_consents: 'Total Consents',
            total_withdrawals: 'Total Withdrawals',
            compliance_score: 'Compliance Score',
            non_compliant: 'Non-Compliant Properties',
            no_non_compliant: 'All properties are compliant.',
            threshold: 'Compliance Threshold',
            property_id: 'Property ID',
            compliant: 'Compliant',
            non_compliant_status: 'Non-Compliant',
            compliance_help_summary: 'How is the compliance score calculated, and how do I fix it?',
            compliance_help_intro: 'A property is flagged as Non-Compliant when its score is below 80%. The score is calculated as:',
            compliance_help_with_template_label: 'If a Golden Template is applied:',
            compliance_help_with_template_text: '% of configuration keys (purposes, legal_bases, banner_config, consent_settings) that match the template exactly.',
            compliance_help_no_template_label: 'If no Golden Template is applied:',
            compliance_help_no_template_text: '% of bindings (consent types assigned to the property) with deployment_status = deployed. A property with no bindings defaults to 100%.',
            compliance_help_fix_intro: 'To remove a property from this list, do one of the following:',
            compliance_help_fix_deploy_label: 'Deploy its bindings',
            compliance_help_fix_deploy_text: "open the Binding Matrix page and set each binding's deployment status to deployed.",
            compliance_help_fix_template_label: 'Apply a Golden Template',
            compliance_help_fix_template_text: "use Apply to Group on the template, then confirm the rollout for this property. Make sure the property's configuration matches the template.",
            compliance_help_fix_decommission_label: 'Decommission the property',
            compliance_help_fix_decommission_text: "use the Decommission button in the property detail modal if it's no longer in use.",
            compliance_help_fix_no_bindings_label: 'Remove all bindings',
            compliance_help_fix_no_bindings_text: 'if a property has no template and no bindings, it scores 100% by default.',
        },
        evidence_packs: {
            title: 'Evidence Packs',
            new: 'New Pack',
            pack_name: 'Pack Name',
            properties: 'Properties',
            generated_at: 'Generated At',
            no_packs: 'No evidence packs found.',
            generate: 'Generate',
            content_type_consent_records: 'Consent Records',
            content_type_policy_versions: 'Policy Versions',
            content_type_binding_history: 'Binding History',
            content_type_change_requests: 'Change Requests',
            content_type_inconsistency_reports: 'Inconsistency Reports',
            content_type_snapshot_data: 'Snapshot Data',
            content_type_audit_trail: 'Audit Trail',
        },
        audit: {
            title: 'Consent Property Audit',
            event_type: 'Event Type',
            entity_type: 'Entity Type',
            entity_id: 'Entity ID',
            actor: 'Actor',
            timestamp: 'Timestamp',
            no_events: 'No audit events found.',
            entity_type_property: 'Property',
            entity_type_group: 'Group',
            entity_type_binding: 'Binding',
            entity_type_change_request: 'Change Request',
            entity_type_golden_template: 'Golden Template',
            entity_type_snapshot_schedule: 'Snapshot Schedule',
            entity_type_inconsistency: 'Inconsistency',
            entity_type_evidence_pack: 'Evidence Pack',
            action_created: 'Created',
            action_updated: 'Updated',
            action_deleted: 'Deleted',
            action_decommissioned: 'Decommissioned',
            action_status_changed: 'Status Changed',
            action_submitted: 'Submitted',
            action_approved: 'Approved',
            action_rejected: 'Rejected',
            action_deployed: 'Deployed',
            action_rolled_back: 'Rolled Back',
            action_paused: 'Paused',
            action_resumed: 'Resumed',
            action_cancelled: 'Cancelled',
            action_acknowledged: 'Acknowledged',
            action_resolved: 'Resolved',
            action_suppressed: 'Suppressed',
            action_executed: 'Executed',
        },
        property_types: {
            website: 'Website',
            mobile_app: 'Mobile App',
            ott: 'OTT',
            iot_device: 'IoT Device',
            kiosk: 'Kiosk',
            api_service: 'API Service',
            sdk: 'SDK',
        },
        environments: {
            production: 'Production',
            staging: 'Staging',
            development: 'Development',
            testing: 'Testing',
        },
        group_types: {
            brand: 'Brand',
            region: 'Region',
            product_line: 'Product Line',
            business_unit: 'Business Unit',
            custom: 'Custom',
        },
        target_types: {
            property: 'Property',
            group: 'Group',
            all: 'All',
        },
        frequencies: {
            hourly: 'Hourly',
            daily: 'Daily',
            weekly: 'Weekly',
            monthly: 'Monthly',
            custom: 'Custom',
        },
        binding_statuses: {
            current: 'Current',
            outdated: 'Outdated',
            pending: 'Pending',
            deploying: 'Deploying',
            rolled_back: 'Rolled Back',
        },
        sensitive_categories: {
            health: 'Health Data',
            biometric: 'Biometric Data',
            genetic: 'Genetic Data',
            racial_ethnic: 'Racial / Ethnic Origin',
            political_opinions: 'Political Opinions',
            religious_beliefs: 'Religious Beliefs',
            trade_union: 'Trade Union Membership',
            sexual_orientation: 'Sexual Orientation',
            criminal_records: 'Criminal Records',
            financial: 'Financial Data',
            children: "Children's Data",
            geolocation: 'Precise Geolocation',
        },
        export_csv: 'Export CSV',
        exporting: 'Exporting...',
        page_x_of_y: 'Page {page} of {total}',
        regulatory: {
            capture_method_guidance: 'PDPL requires consent proof to match the collection channel. Property types map to consent capture methods: website/OTT = banner-based consent, mobile app/SDK = in-app consent flow, kiosk = physical/on-screen consent capture, API service = programmatic consent, IoT device = device-level consent mechanism.',
            dashboard_metrics_info: 'This dashboard provides the DPO with a single view of consent posture across all digital properties. Key metrics include: binding coverage percentage (deployed vs total bindings), template adherence via the inconsistencies widget, pending change requests requiring review, and property status breakdown showing properties with active, inactive, or decommissioned states. Properties with sensitive data categories are tracked via the property registry.',
            change_request_impact_info: 'Change requests affecting consent properties must document regulatory impact: which PDPL requirements are affected, whether the change affects sensitive data processing (Art. 2.4), and whether the change requires re-consent from data subjects. The impact summary in each change request details the PDPL regulatory references for audit evidence.',
            outdated_binding_warning: 'Bindings with "outdated" status indicate that a property runs a policy version that is not current. These properties may not reflect the latest PDPL requirements or organizational policy updates. Review and update outdated bindings promptly to maintain PDPL compliance across all consent properties.',
            template_pdpl_alignment: 'Per PDPL Art. 11, golden templates must link purposes to legal bases. Banner configurations must enforce opt-in (not opt-out) per Art. 11.3 explicit consent. Templates must specify a withdrawal mechanism per Art. 12. For sensitive data (Art. 2.4), templates must enforce explicit written or equivalent consent -- click-through acceptance is insufficient.',
        },
    },
    // ---------- Sprint 2.8.0: Localization ----------
    localization: {
        nav: {
            dashboard: 'Dashboard',
            market_packs: 'Market Packs',
            language_packs: 'Language Packs',
            frameworks: 'Regulatory Frameworks',
            mappings: 'Module Mappings',
            submissions: 'Pack Submissions',
            translations: 'Translation Preview',
            audit: 'Audit Trail',
        },
        dashboard: {
            title: 'Localization Dashboard',
            loading: 'Loading dashboard data...',
            active_market_packs: 'Active Market Packs',
            published_packs_available: 'Published market packs available',
            language_packs: 'Language Packs',
            total_language_packs: 'Total language packs registered',
            active_regulatory_frameworks: 'Active Regulatory Frameworks',
            frameworks_active: 'Frameworks with active status',
            pending_pack_submissions: 'Pending Pack Submissions',
            submissions_awaiting_review: 'Submissions awaiting review',
            recent_audit_events: 'Recent Audit Events',
            no_recent_audit: 'No recent audit events.',
            event_type: 'Event Type',
            entity_type: 'Entity Type',
            entity_id: 'Entity ID',
            actor: 'Actor',
            timestamp: 'Timestamp',
        },
        market_packs: {
            title: 'Market Packs Catalog',
            new: 'New Market Pack',
            pack_name: 'Pack Name',
            market: 'Market',
            locale: 'Locale',
            no_packs: 'No market packs found.',
        },
        language_packs: {
            title: 'Language Packs',
            new: 'New Language Pack',
            language: 'Language',
            locale_code: 'Locale Code',
            completeness: 'Completeness',
            completeness_detail: 'Completeness Detail',
            overall_completeness: 'Overall Completeness',
            total_keys: 'Total Keys',
            translated_keys: 'Translated Keys',
            missing_keys: 'Missing Keys',
            namespace_breakdown: 'Namespace Breakdown',
            namespace: 'Namespace',
            translated: 'Translated',
            no_packs: 'No language packs found.',
            install_language_pack: 'Install Language Pack',
            available_packs: 'Available Language Packs',
            install: 'Install',
            all_installed: 'All language packs are already installed.',
            install_success: 'Language pack installed successfully.',
            market: 'Market',
            featured: 'Featured',
        },
        frameworks: {
            title: 'Regulatory Frameworks',
            new: 'New Framework',
            framework_name: 'Framework Name',
            jurisdiction: 'Jurisdiction',
            effective_date: 'Effective Date',
            no_frameworks: 'No regulatory frameworks found.',
            view_requirements: 'View Requirements',
            hide_requirements: 'Hide Requirements',
            show_legacy: 'Show legacy frameworks',
            effective_until: 'Effective until',
            effective_on: 'Effective on',
            replaced_by: 'Replaced by',
            replaces_label: 'Replaces',
            status_legacy: 'Legacy',
            status_upcoming: 'Upcoming',
            watchlist: 'Watchlist',
            watchlist_empty: 'No draft or upcoming frameworks',
            watchlist_empty_desc: 'Frameworks with status "draft" or "upcoming" will appear here for monitoring.',
            expected_effective: 'Expected effective',
            effective_date_tbd: 'Effective date: TBD',
            activate: 'Activate',
            // AI Law (Sprint 15.9.0)
            ai_law_title: 'Vietnam AI Law',
            ai_risk_classification: 'AI Risk Classification',
            ai_system_dossier: 'AI System Dossier',
            ai_labeling_checklist: 'AI Labeling & Disclosure',
            ai_incident_report: 'AI Incident Report',
            ai_impact_assessment: 'AI Impact Assessment',
            ai_sandbox_application: 'Sandbox Application',
            ai_sandbox_monitoring: 'Sandbox Monitoring',
            ai_sandbox_completion: 'Sandbox Completion',
            ai_submission_tracker: 'Submission Tracker',
            ai_evidence_capture: 'Evidence Capture',
            risk_level_minimal: 'Minimal Risk',
            risk_level_limited: 'Limited Risk',
            risk_level_high: 'High Risk',
            risk_level_unacceptable: 'Unacceptable Risk',
            submission_status_draft: 'Draft',
            submission_status_submitted: 'Submitted',
            submission_status_returned: 'Returned for Amendment',
            submission_status_accepted: 'Accepted',
            submission_status_rejected: 'Rejected',
            submission_status_closed: 'Closed',
            // E-Commerce Law (Sprint 16.0.0)
            ecommerce_law_title: 'Vietnam E-Commerce Law',
            ecommerce_platform_classification: 'Platform Classification',
            ecommerce_seller_register: 'Seller Register',
            ecommerce_complaint_register: 'Complaint Register',
            ecommerce_takedown_register: 'Takedown Register',
            ecommerce_submission_tracking: 'Submission Tracking',
            ecommerce_dossier_checklist: 'Dossier Checklist',
            ecommerce_cross_border: 'Cross-Border Compliance',
            ecommerce_trustmark: 'Trustmark Program',
            ecommerce_notification: 'Notification',
            ecommerce_registration: 'Registration',
            ecommerce_termination: 'Termination',
            ecommerce_reporting_readiness: 'Reporting Readiness',
            ecommerce_evidence_capture: 'Evidence Capture',
            submission_status_approved: 'Approved',
            cybersec_law_2025_title: 'Vietnam Cybersecurity Law (2025)',
            cybersec_system_classification: 'System Classification',
            cybersec_risk_assessment: 'Risk Assessment',
            cybersec_incident_report: 'Incident Report',
            cybersec_authority_cooperation: 'Authority Cooperation',
            cybersec_data_localization: 'Data Localization',
            cybersec_vendor_security: 'Vendor Security',
            cybersec_submission_tracker: 'Submission Tracker',
            cybersec_evidence_capture: 'Evidence Capture',
            cybersec_dossier_checklist: 'Dossier Checklist',
            cybersec_classification_general: 'General',
            cybersec_classification_important: 'Important',
            cybersec_classification_critical: 'Critical',
            data_law_title: 'Vietnam Data Law',
            data_inventory: 'Data Inventory',
            data_classification: 'Data Classification',
            data_sharing: 'Data Sharing',
            data_governance: 'Data Governance',
            data_submission_tracker: 'Submission Tracker',
            data_evidence_capture: 'Evidence Capture',
            data_dossier_checklist: 'Dossier Checklist',
            telecom_law_title: 'Vietnam Telecom Law',
            telecom_authority_request: 'Authority Request',
            telecom_disclosure: 'Disclosure',
            telecom_confidentiality: 'Confidentiality',
            telecom_submission_tracker: 'Submission Tracker',
            telecom_evidence_capture: 'Evidence Capture',
            telecom_dossier_checklist: 'Dossier Checklist',
            pdpl_law_title: 'Vietnam Personal Data Protection Law',
            pdpl_breach_notification: 'Breach Notification',
            pdpl_dpo_notification: 'DPO Appointment Notification',
            pdpl_data_subject_notification: 'Data Subject Notification',
            pdpl_submission_tracker: 'Submission Tracker',
            pdpl_evidence_capture: 'Evidence Capture',
            pdpl_dossier_checklist: 'Dossier Checklist',
            pdpl_authority_filing: 'Authority Filing',
            pdpl_mops_filing: 'MoPS Filing',
        },
        mappings: {
            title: 'Module Mappings',
            new: 'New Mapping',
            module: 'Module',
            market_pack: 'Market Pack',
            mapping_status: 'Mapping Status',
            no_mappings: 'No module mappings found.',
            incomplete_entries_warning: '{count} mapping(s) hidden due to missing framework ID, requirement ID, or module.',
        },
        submissions: {
            title: 'Pack Submissions',
            new: 'New Submission',
            submitter: 'Submitter',
            pack: 'Pack',
            submitted_at: 'Submitted At',
            no_submissions: 'No pack submissions found.',
            installed_sector_overlays_title: 'Sector Overlays ({count})',
            installed_sector_overlays_aria: 'Installed Sector Overlays',
            installed_col_name: 'Name',
            installed_col_code: 'Code',
            installed_col_sector: 'Sector',
            installed_col_mode: 'Mode',
            installed_col_status: 'Status',
            installed_refresh: 'Refresh',
        },
        translations: {
            title: 'Translation Preview',
            select_locale: 'Select Locale',
            select_module: 'Select Module',
            key: 'Key',
            value: 'Value',
            no_translations: 'No translations found.',
        },
        audit: {
            title: 'Localization Audit Trail',
            event_type: 'Event Type',
            entity_type: 'Entity Type',
            entity_id: 'Entity ID',
            entity_name: 'Entity Name',
            actor: 'Actor',
            timestamp: 'Timestamp',
            payload: 'Payload',
            collapse_payload: 'Collapse payload details',
            expand_payload: 'Expand payload details',
            collapse: 'Collapse',
            expand: 'Expand',
            all_entity_types: 'All Entity Types',
            no_events: 'No audit events found.',
        },
        market_packs_detail: {
            installed_at: 'Installed At',
            pack_details_label: 'Pack Details',
            market_code: 'Market Code',
            supported_locales: 'Supported Locales',
            min_platform_version: 'Min Platform Version',
            pack_information: 'Pack information',
            pack_contents: 'Pack Contents',
            included_frameworks: 'Included Frameworks',
            pack_artifacts: 'Pack Artifacts',
            no_artifacts: 'No artifacts found for this pack',
            pack_not_found: 'Pack not found',
        },
        mappings_form: {
            framework_id: 'Framework ID',
            requirement_id: 'Requirement ID',
            module_label: 'Module',
            mapping_type: 'Mapping Type',
            configuration_json: 'Configuration (JSON)',
            conflict_resolution: 'Conflict Resolution',
            is_override: 'Is Override',
            all_modules: 'All Modules',
            all_mapping_types: 'All Mapping Types',
        },
        submissions_detail: {
            submission_id: 'Submission ID',
            validation: 'Validation',
            reviewers: 'Reviewers',
            start_review: 'Start Review',
            hide_comments: 'Hide Comments',
            review_comments: 'Review Comments',
            loading_comments: 'Loading comments...',
            no_comments: 'No comments yet.',
            add_comment: 'Add Comment',
            reject_submission: 'Reject Submission',
            reason_for_rejection: 'Reason for Rejection',
            reviewer_ids: 'Reviewer IDs (comma-separated)',
            submit_new_pack: 'Submit New Pack',
            pack_type: 'Pack Type',
            market_code: 'Market Code',
            target_locales: 'Target Locales',
            pack_file: 'Pack File',
            pack_format_info: 'Pack format: Market packs should include locale-specific JSON files with namespace/key translations. Language packs should provide translations for the specified target locales. See documentation for detailed schema.',
            accepted_formats: 'Accepted formats: .json or .zip. JSON files should contain a flat or namespaced object of translation key-value pairs. ZIP files should contain one or more JSON locale files.',
            comma_separated_locales: 'Comma-separated locale codes for this language pack',
            confirm_approval: 'Confirm Approval',
            approve_message: 'Approve this pack submission?',
            submission_label: 'Submission',
            all_pack_types: 'All Pack Types',
            passed: 'passed',
            failed_label: 'failed',
            market_code_required: 'Market code is required',
            version_required: 'Version is required',
            pack_type_regulatory: 'Regulatory',
            framework_code: 'Framework Code',
            jurisdiction_code: 'Jurisdiction Code',
            pack_source: 'Source',
            pack_source_manual: 'Manual',
            pack_version: 'Pack Version',
            install_regulatory_pack: 'Install Regulatory Pack',
            regulatory_pack_desc: 'Upload a regulatory framework pack (.zip)',
            framework_required: 'Framework code is required',
            jurisdiction_required: 'Jurisdiction code is required',
        },
        catalog_detail: {
            packs_installed_of: 'of {total} packs installed',
            install_all: 'Install All',
            installing: 'Installing...',
            removing: 'Removing...',
            requires: 'Requires',
            tab_catalog: 'Catalog',
            tab_installed: 'Installed',
            details: 'Details',
        },
        translations_detail: {
            source_locale: 'Source Locale',
            target_locale: 'Target Locale',
            locales_must_differ: 'Source and target locales must be different.',
            all_keys_translated: 'All keys translated',
            keys_missing: '{count} key(s) missing',
            show_missing_only: 'Show missing only',
            namespace: 'Namespace',
            source: 'Source',
            target: 'Target',
        },
        frameworks_detail: {
            tab_frameworks: 'Frameworks',
            tab_watchlist: 'Watchlist',
            authority: 'Authority',
            requirements_count: 'Requirements',
            all_types: 'All Types',
            collapse_all: 'Collapse All',
            expand_all: 'Expand All',
            filtered_to: 'Filtered to',
            clear_filter: 'Clear filter',
            no_requirements: 'No requirements found for this framework.',
            requirements_label: 'Requirements',
            status_updated: 'Status updated successfully',
            // Sprint 25.5.0: Framework detail tabs
            overview_tab: 'Overview',
            forms_tab: 'Forms',
            procedures_tab: 'Procedures',
            fines_risks_tab: 'Fines & Risks',
            deadlines_tab: 'Deadlines',
            // Sprint 46.9.0: Guidance tab + actions
            guidance_tab: 'Guidance',
            guidance_empty: 'No guidance content for this framework.',
            guidance_loading: 'Loading guidance…',
            guidance_section: 'Guidance section',
            guidance_view: 'View Guidance',
            guidance_map_to_workflow: 'Map to Workflow',
            guidance_link_evidence: 'Link Evidence',
            guidance_filter_type: 'Type',
            guidance_filter_category: 'Category',
            guidance_filter_module: 'Module',
            guidance_filter_status: 'Status',
            guidance_filter_search: 'Search guidance',
            guidance_filter_all: 'All',
            guidance_table_reference: 'Reference',
            guidance_table_name: 'Guidance Name',
            guidance_table_type: 'Type',
            guidance_table_category: 'Category',
            guidance_table_effective_date: 'Effective Date',
            guidance_table_modules: 'Related Modules',
            guidance_table_actions: 'Actions',
            guidance_reference_only_banner: 'Reference only — applies to {{sector}} sector but not to your organization.',
            guidance_related_link: 'Related Guidance',
            deadlines_empty: 'No regulatory deadlines found for this framework.',
            deadlines_sla_type: 'SLA Type',
            deadlines_value: 'Deadline',
            deadlines_unit: 'Unit',
            deadlines_extension: 'Extension',
            deadlines_reference: 'Legal Reference',
            framework_stack: 'Framework Stack',
            layer_parent_law: 'Parent Law',
            layer_implementing_decree: 'Implementing Decree',
            layer_enforcement_decree: 'Enforcement Decree',
            layer_admin_procedures: 'Administrative Procedures',
            status_active: 'Active',
            status_draft: 'Draft',
            draft_enforcement_badge: 'Draft — not yet promulgated',
            effective_date: 'Effective Date',
            forms_table_id: 'Form ID',
            forms_table_name: 'Form Name',
            forms_table_source: 'Source Layer',
            forms_table_category: 'Category',
            forms_table_type: 'Type',
            fill_form: 'Fill Form',
            view_reference: 'View Reference',
            forms_official: 'Official',
            forms_internal: 'Internal',
            forms_empty: 'No forms found for this framework.',
            forms_loading: 'Loading forms...',
            filter_by_source_layer: 'Filter by source layer',
            all_layers: 'All Layers',
            procedures_id: 'Procedure ID',
            procedures_name: 'Procedure',
            procedures_trigger: 'Trigger',
            procedures_steps: 'Steps',
            procedures_outputs: 'Outputs',
            procedures_related_forms: 'Related Forms',
            procedures_submission_channels: 'Submission Channels',
            procedures_empty: 'No procedures found for this framework.',
            fines_area: 'Violation Area',
            fines_articles: 'Draft Articles',
            fines_individual: 'Fine (Individual)',
            fines_organization: 'Fine (Organization)',
            fines_revenue: 'Revenue Escalation',
            fines_deadlines: 'Deadlines',
            fines_remediation: 'Remediation',
            fines_related: 'Related Procedures',
            fines_empty: 'No enforcement data available for this framework.',
            fines_enforcement_pending: 'Enforcement framework not yet published. Sanctions data will be added when the implementing instrument is promulgated.',
            view_details: 'View Details',
            back_to_list: 'Back to Frameworks',
            description: 'Description',
            jurisdiction: 'Jurisdiction',
            lineage: 'Lineage',
            replaces: 'Replaces',
            replaced_by: 'Replaced By',
            requirements: 'Requirements',
            tab_navigation: 'Framework detail tabs',
            // Sprint 25.5.0.1: Forms tab action link
            forms_table_actions: 'Actions',
            create_form: 'Create Form',
            // Sprint 25.5.0.2: Enforcement decree status tracking
            mark_promulgated: 'Mark as Promulgated',
            status_promulgated: 'Promulgated',
            promulgated_success: 'Layer status updated to promulgated',
            // Transition Review (framework succession lifecycle)
            transition_review: 'Transition Review',
            transition_review_desc: 'This framework has a pending transition review for successor law adoption.',
            transition_review_by: 'Review Deadline',
            transition_review_status: 'Review Status',
            transition_review_notes: 'Notes',
            transition_review_pending: 'Pending',
            transition_review_in_progress: 'In Progress',
            transition_review_completed: 'Completed',
            transition_review_successor: 'Successor Framework',
            transition_review_lead_time: 'Lead Time',
            transition_review_lead_time_days: '{days} days before effective date',
            transition_review_forms_heading: 'Form Transition Reviews',
            transition_review_forms_desc: 'Review each operational template for relevance under the successor framework.',
            transition_review_outcome: 'Outcome',
            transition_review_outcome_carry_forward: 'Carry Forward',
            transition_review_outcome_carry_forward_revised: 'Carry Forward (Revised)',
            transition_review_outcome_retire: 'Retire',
            transition_review_outcome_split: 'Split',
            transition_review_outcome_awaiting_decree: 'Awaiting Decree',
            transition_review_no_forms: 'No form transition reviews configured.',
            transition_review_update_success: 'Transition review updated successfully',
            transition_review_operational_note: 'These are process-enabling operational templates, not statutory form counts. Legal attribution remains with the legacy framework until the successor law takes effect.',
            // Sprint 26.4.0.2: Rights tab
            rights_tab: 'Rights',
            rights_empty: 'No data subject rights defined for this framework.',
            rights_code: 'Code',
            rights_name: 'Right',
            rights_legal_basis: 'Legal Basis',
            rights_elaborating_articles: 'Elaborating Articles',
            rights_description: 'Description',
            // Requirement table columns
            requirements_reference: 'Reference',
            requirements_mandatory: 'Mandatory',
            requirements_yes: 'Yes',
            requirements_no: 'No',
            requirements_source: 'Source',
            // Requirement category labels
            category_legal_basis: 'Legal Basis',
            category_data_subject_rights: 'Data Subject Rights',
            category_notification: 'Notification',
            category_assessment: 'Assessment',
            category_governance: 'Governance',
            category_record_keeping: 'Record Keeping',
            category_cross_border: 'Cross-Border',
            category_technical_measures: 'Technical Measures',
            category_incident_reporting: 'Incident Reporting',
            category_risk_management: 'Risk Management',
            category_supply_chain: 'Supply Chain',
            category_certification: 'Certification',
            // Framework list labels
            child_frameworks: '{count} child framework',
            child_frameworks_plural: '{count} child frameworks',
            replaces_prefix: 'Replaces:',
            replaced_by_prefix: 'Replaced by:',
            responsible_prefix: 'Responsible:',
            watchlist_empty_hint: 'Frameworks with status "draft" or "upcoming" will appear here for monitoring.',
            framework_count: '{count} framework',
            framework_count_plural: '{count} frameworks',
            hiding_legacy: '(hiding {count} legacy)',
            // Framework code display labels
            label_VN_PDPL: 'PDPL (Decree 13/2023)',
            label_VN_PDPD_DECREE_13: 'PDPD Decree 13/2023',
            label_VN_PDPL_LAW_2025: 'PDPL Law 91/2025',
            label_VN_PDPL_DECREE_356_2025: 'Decree 356/2025/ND-CP',
            label_VN_PDPL_ADMIN_PROCEDURES_778_2026: 'QĐ 778/QĐ-BCA-A05',
            label_VN_PDPL_ENFORCEMENT_DRAFT_2026: 'Draft Cyber+PDPL Enforcement 2026',
            label_VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: 'NQ22 Temporary Overlay 2026',
            label_VN_DATA_LAW: 'Data Law',
            label_VN_DATA_LAW_DECREE_165_2025: 'Data Law Decree 165/2025',
            label_VN_DATA_EXCHANGES_DRAFT_2026: 'Data Exchanges Draft 2026',
            label_VN_TELECOM_LAW_2023: 'Telecommunications Law 2023',
            label_VN_TELECOM_DECREE_163_2024: 'Telecom Decree 163/2024',
            label_VN_CYBERSEC_LAW: 'Cybersecurity Law (24/2018)',
            label_VN_CYBERSEC_DECREE_53: 'Cybersec Decree 53/2022',
            label_VN_CYBERSEC_LAW_2025: 'Cybersecurity Law 116/2025',
            label_VN_ECOMMERCE_LAW_2025: 'E-Commerce Law 122/2025',
            label_VN_AI_LAW_2026: 'AI Law 2026',
            label_VN_AI_DECREE_142_2026: 'AI Decree 142/2026/NĐ-CP',
            // Issuer labels
            issuer_company: 'Company',
            issuer_authority_a05_mps: 'MPS/A05',
            issuer_authority_mic: 'MIC',
            issuer_authority_mps_ndc: 'MPS/NDC',
            issuer_other_authority: 'Authority',
            // Deadline SLA type labels
            sla_type_rights_request_ack: 'Rights request acknowledgment',
            'sla_type_Access control review': 'Access control review',
            'sla_type_Annual governance attestation': 'Annual governance attestation',
            'sla_type_Audit review cycle': 'Audit review cycle',
            'sla_type_Authority request response': 'Authority request response',
            'sla_type_Change notification processing': 'Change notification processing',
            'sla_type_Complaint acknowledgment': 'Complaint acknowledgment',
            'sla_type_Complaint resolution': 'Complaint resolution',
            'sla_type_Confidentiality policy review': 'Confidentiality policy review',
            'sla_type_Cybersecurity incident full report': 'Cybersecurity incident full report',
            'sla_type_Cybersecurity incident initial report': 'Cybersecurity incident initial report',
            'sla_type_Data inventory review': 'Data inventory review',
            'sla_type_Disclosure log review cycle': 'Disclosure log review cycle',
            'sla_type_Incident classification decision': 'Incident classification decision',
            'sla_type_Information system classification review': 'Information system classification review',
            'sla_type_Notification processing (sales website/app)': 'Notification processing (sales website/app)',
            'sla_type_Pen test cycle': 'Pen test cycle',
            'sla_type_Registration processing (service platform)': 'Registration processing (service platform)',
            'sla_type_Risk assessment cycle': 'Risk assessment cycle',
            'sla_type_Takedown decision (after review)': 'Takedown decision (after review)',
            'sla_type_Termination notice (public)': 'Termination notice (public)',
            sla_type_ai_impact_assessment_completion: 'AI impact assessment completion',
            sla_type_ai_sandbox_application_review: 'AI sandbox application review',
            sla_type_ai_serious_incident_report: 'AI serious incident report',
            sla_type_ai_system_registration: 'AI system registration',
            sla_type_authority_request_response: 'Authority request response',
            sla_type_certificate_issuance_review: 'Certificate issuance review',
            sla_type_complaint_handling_approach_notice: 'Complaint handling approach notice',
            sla_type_complaint_incomplete_info_request: 'Complaint incomplete info request',
            sla_type_data_governance_attestation: 'Data governance attestation',
            sla_type_dsr_acknowledgment: 'DSR acknowledgment',
            sla_type_dsr_cl2_withdraw_restrict_object: 'DSR (Cl.2) — Withdraw / restrict / object',
            sla_type_dsr_cl2_withdraw_restrict_object_third_party: 'DSR (Cl.2) — Third party',
            sla_type_dsr_cl3_access_correct_provide: 'DSR (Cl.3) — Access / correct / provide',
            sla_type_dsr_cl3_access_correct_provide_third_party: 'DSR (Cl.3) — Third party',
            sla_type_dsr_cl4_deletion: 'DSR (Cl.4) — Deletion',
            sla_type_dsr_cl4_deletion_third_party: 'DSR (Cl.4) — Deletion, third party',
            sla_type_dsr_cl5_protective_measures: 'DSR (Cl.5) — Protective measures',
            sla_type_telecom_authority_request_response: 'Telecom authority request response',
            sla_type_rights_request_completion: 'Rights request completion',
            sla_type_incident_authority_notification: 'Incident authority notification',
            sla_type_incident_subject_notification: 'Incident subject notification',
            sla_type_dpia_completion: 'DPIA completion',
            sla_type_data_retention_review: 'Data retention review',
            sla_type_authority_filing_review: 'Authority filing review',
            sla_type_authority_supplement_response: 'Authority supplement response',
            sla_type_authority_resubmission: 'Authority resubmission',
            sla_type_authority_filing_supplement_response: 'Authority filing supplement response',
            sla_type_authority_filing_resubmission: 'Authority filing resubmission',
            // Deadline unit labels
            unit_working_days: 'working days',
            unit_calendar_days: 'calendar days',
            unit_hours: 'hours',
            // Form category labels
            form_category_dpa: 'DPA',
            form_category_dsr: 'DSR',
            form_category_consent: 'Consent',
            form_category_privacy: 'Privacy',
            form_category_breach: 'Breach',
            form_category_dpia: 'DPIA',
            form_category_register: 'Register',
            form_category_monitoring: 'Monitoring',
            form_category_vendor: 'Vendor',
            form_category_org: 'Organizational',
            form_category_sector: 'Sector',
            form_category_data_mapping: 'Data Mapping',
            form_category_governance: 'Governance',
            form_category_incident: 'Incident',
            form_category_authority_submission: 'Authority Submission',
            form_category_notice: 'Notice',
            form_category_assessment_report: 'Assessment Report',
            form_category_request: 'Request',
            form_category_dossier: 'Dossier',
            form_category_filing: 'Filing',
            // Module code labels
            module_consent: 'Consent',
            module_rights_request: 'Rights Request',
            module_classification: 'Classification',
            module_dpia: 'DPIA',
            module_governance: 'Governance',
            module_monitoring: 'Monitoring',
            module_register: 'Register',
            module_risk: 'Risk',
            module_data_mapping: 'Data Mapping',
            module_incident: 'Incident',
            module_assessment: 'Assessment',
            module_vendor: 'Vendor',
            module_policy: 'Policy',
            module_audit: 'Audit',
            module_training: 'Training',
            module_discovery: 'Discovery',
            // Sprint 47.3.0 — Guidance enum maps + requirement category
            // headers + per-tab Related-Guidance advisories. See
            // FrameworkDetailView.humanizeEnum() for the lookup contract.
            enum_guidance_type_calculation_guidance: 'Calculation Guidance',
            enum_guidance_type_implementation_guidance: 'Implementation Guidance',
            enum_guidance_type_interpretive_note: 'Interpretive Note',
            enum_guidance_type_legal_guidance: 'Legal Guidance',
            enum_guidance_type_procedural_guidance: 'Procedural Guidance',
            enum_guidance_type_sector_guidance: 'Sector Guidance',
            enum_guidance_type_technical_methodology: 'Technical Methodology',
            enum_guidance_category_annual_reporting: 'Annual Reporting',
            enum_guidance_category_authority_cooperation: 'Authority Cooperation',
            enum_guidance_category_authority_coordination: 'Authority Coordination',
            enum_guidance_category_business_continuity: 'Business Continuity',
            enum_guidance_category_capital_adequacy: 'Capital Adequacy',
            enum_guidance_category_complaint_handling: 'Complaint Handling',
            enum_guidance_category_consent: 'Consent',
            enum_guidance_category_consent_management: 'Consent Management',
            enum_guidance_category_consumer_protection: 'Consumer Protection',
            enum_guidance_category_control_environment: 'Control Environment',
            enum_guidance_category_cross_border: 'Cross-Border',
            enum_guidance_category_cross_border_transfers: 'Cross-Border Transfers',
            enum_guidance_category_data_classification: 'Data Classification',
            enum_guidance_category_data_localization: 'Data Localization',
            enum_guidance_category_data_protection_impact_assessment: 'Data Protection Impact Assessment',
            enum_guidance_category_direct_marketing: 'Direct Marketing',
            enum_guidance_category_dpia: 'DPIA',
            enum_guidance_category_governance: 'Governance',
            enum_guidance_category_ict_risk_management: 'ICT Risk Management',
            enum_guidance_category_incident_management: 'Incident Management',
            enum_guidance_category_internal_audit: 'Internal Audit',
            enum_guidance_category_isms_documentation: 'ISMS Documentation',
            enum_guidance_category_lawful_basis: 'Lawful Basis',
            enum_guidance_category_licensing: 'Licensing',
            enum_guidance_category_local_presence: 'Local Presence',
            enum_guidance_category_logging_and_retention: 'Logging & Retention',
            enum_guidance_category_logical_and_physical_access: 'Logical & Physical Access',
            enum_guidance_category_operational_resilience_testing: 'Operational Resilience Testing',
            enum_guidance_category_outsourcing: 'Outsourcing',
            enum_guidance_category_platform_registration: 'Platform Registration',
            enum_guidance_category_quality_of_service: 'Quality of Service',
            enum_guidance_category_risk_classification: 'Risk Classification',
            enum_guidance_category_risk_management: 'Risk Management',
            enum_guidance_category_scope_definition: 'Scope Definition',
            enum_guidance_category_sectoral_localisation: 'Sectoral Localisation',
            enum_guidance_category_subscriber_protection: 'Subscriber Protection',
            enum_guidance_category_supply_chain: 'Supply Chain',
            enum_guidance_category_system_classification: 'System Classification',
            enum_guidance_category_system_operations: 'System Operations',
            enum_guidance_category_telecommunications_data: 'Telecommunications Data',
            enum_guidance_category_third_party_risk: 'Third-Party Risk',
            enum_guidance_category_transitional_provisions: 'Transitional Provisions',
            enum_guidance_category_vulnerability_management: 'Vulnerability Management',
            enum_guidance_status_active: 'Active',
            enum_guidance_status_draft: 'Draft',
            enum_module_access_management: 'Access Management',
            enum_module_ai_governance: 'AI Governance',
            enum_module_asset_management: 'Asset Management',
            enum_module_audit_trail: 'Audit Trail',
            enum_module_breach_notification: 'Breach Notification',
            enum_module_capital_adequacy: 'Capital Adequacy',
            enum_module_classification: 'Classification',
            enum_module_consent: 'Consent',
            enum_module_consent_management: 'Consent Management',
            enum_module_cross_border_transfers: 'Cross-Border Transfers',
            enum_module_data_localization: 'Data Localization',
            enum_module_data_mapping: 'Data Mapping',
            enum_module_data_protection: 'Data Protection',
            enum_module_data_subject_rights: 'Data Subject Rights',
            enum_module_dpia: 'DPIA',
            enum_module_encryption: 'Encryption',
            enum_module_governance: 'Governance',
            enum_module_incident: 'Incident',
            enum_module_incident_management: 'Incident Management',
            enum_module_internal_audit: 'Internal Audit',
            enum_module_isms_documentation: 'ISMS Documentation',
            enum_module_isms_governance: 'ISMS Governance',
            enum_module_logging_and_monitoring: 'Logging & Monitoring',
            enum_module_monitoring: 'Monitoring',
            enum_module_policy_management: 'Policy Management',
            enum_module_risk: 'Risk',
            enum_module_risk_management: 'Risk Management',
            enum_module_training: 'Training',
            enum_module_vendor: 'Vendor',
            enum_module_vendor_management: 'Vendor Management',
            enum_module_vendor_risk: 'Vendor Risk',
            enum_module_vulnerability_management: 'Vulnerability Management',
            category_access: 'Access',
            category_availability: 'Availability',
            category_change_management: 'Change Management',
            category_classification: 'Classification',
            category_communication_and_information: 'Communication & Information',
            category_compliance: 'Compliance',
            category_confidentiality: 'Confidentiality',
            category_control_activities: 'Control Activities',
            category_control_environment: 'Control Environment',
            category_incident: 'Incident',
            category_internal_audit: 'Internal Audit',
            category_internal_control: 'Internal Control',
            category_logical_and_physical_access: 'Logical & Physical Access',
            category_mis_data: 'Management Information System Data',
            category_monitoring: 'Monitoring',
            category_monitoring_activities: 'Monitoring Activities',
            category_outsourcing: 'Outsourcing',
            category_privacy: 'Privacy',
            category_processing_integrity: 'Processing Integrity',
            category_reporting: 'Reporting',
            category_risk_assessment: 'Risk Assessment',
            category_risk_mitigation: 'Risk Mitigation',
            category_sector_applicability: 'Sector Applicability',
            category_system_operations: 'System Operations',
            category_training: 'Training',
            category_vendor: 'Vendor',
            guidance_related_forms_advisory: 'Some forms have related guidance — view in the',
            guidance_related_procedures_advisory: 'Some procedures have related guidance — view in the',
            guidance_related_requirements_advisory: 'Some requirements have related guidance — view in the',
        },
        channels: {
            title: 'Submission Channels & Deadlines',
            channels: 'Submission Channels',
            add_channel: 'Add Channel',
            channel_type: 'Type',
            name: 'Name',
            instructions: 'Instructions',
            active: 'Active',
            no_channels: 'No submission channels configured',
            portal: 'Portal',
            in_person: 'In Person',
            postal: 'Postal',
            email: 'Email',
            add: 'Add',
            deadlines: 'Regulatory Deadlines',
            add_deadline: 'Add Deadline',
            label: 'Label',
            days: 'Days',
            day_type: 'Day Type',
            calendar_days: 'Calendar Days',
            working_days: 'Working Days',
            no_deadlines: 'No deadlines configured',
            description: 'Description',
            failed_load: 'Failed to load channel data',
            channel_added: 'Channel added',
            channel_added_announce: 'Submission channel added',
            failed_add_channel: 'Failed to add channel',
            deadline_added: 'Deadline added',
            deadline_added_announce: 'Regulatory deadline added',
            failed_add_deadline: 'Failed to add deadline',
        },
        module_links: {
            title: 'Framework Linkage',
            modules: 'Linked Modules',
            add_module: 'Add Module',
            module_code: 'Module Code',
            required: 'Required',
            link: 'Link',
            no_modules: 'No modules linked',
            notes: 'Notes',
            personas: 'Linked Personas',
            add_persona: 'Add Persona',
            persona_id: 'Persona ID',
            role_description: 'Role Description',
            no_personas: 'No personas linked',
            failed_load: 'Failed to load framework links',
            module_linked: 'Module linked',
            module_linked_announce: 'Module linked successfully',
            failed_link_module: 'Failed to link module',
            persona_linked: 'Persona linked',
            persona_linked_announce: 'Persona linked successfully',
            failed_link_persona: 'Failed to link persona',
        },
        regulatory_updates: {
            statuses: {
                pending: 'Pending',
                assessed: 'Assessed',
                applied: 'Applied',
                dismissed: 'Dismissed',
            },
            title: 'Regulatory Updates & Framework Dashboard',
            page_description: 'Coverage at a glance for every installed regulatory framework, plus the in-product change log of regulatory updates: amendments, effective dates, and impact assessments.',
            coverage_dashboard: 'Framework Coverage Dashboard',
            no_frameworks: 'No frameworks found',
            framework: 'Framework',
            modules: 'Modules',
            templates: 'Templates',
            forms: 'Forms',
            channels: 'Channels',
            deadlines: 'Deadlines',
            pending: 'Pending Updates',
            updates: 'Regulatory Updates',
            create_update: 'Create Update',
            framework_id: 'Framework ID',
            update_title: 'Title',
            description: 'Description',
            effective_date: 'Effective Date',
            amendment_ref: 'Amendment Reference',
            create: 'Create',
            no_updates: 'No regulatory updates',
            status: 'Status',
            impact: 'Impact',
            change_summary: 'Change Summary',
            assess: 'Assess',
            failed_load: 'Failed to load data',
            update_created: 'Update created',
            update_created_announce: 'Regulatory update created',
            failed_create: 'Failed to create update',
            update_assessed: 'Update assessed',
            failed_assess: 'Failed to assess update',
        },
        // ---------- Sprint 46.2.0 — Sector overlay pattern ----------
        sector_overlay: {
            // Sector display labels (12 sectors)
            sector_labels: {
                banking: 'Banking',
                insurance: 'Insurance',
                healthcare: 'Healthcare',
                ict_critical: 'Critical ICT',
                telecom: 'Telecommunications',
                energy: 'Energy',
                transport: 'Transport',
                digital_services: 'Digital services',
                digital_technology_industry: 'Digital technology industry',
                public_administration: 'Public administration',
                payments: 'Payments',
                other: 'Other',
            },
            sector_label: 'Sector',
            direct_scope_label: 'Direct legal scope',
            // Mode chip + banner
            chip_direct: 'Directly applicable',
            chip_reference: 'Reference only',
            banner_direct_copy: 'Directly applicable to your organization. Sector reports and deadlines are active.',
            banner_reference_copy: 'Reference only — your organization is not classified in this sector. Sector reports and deadlines are not active.',
            // Edit-mode form
            edit_mode_button: 'Edit mode',
            edit_form_title: 'Change install mode',
            mode_direct_label: 'Direct — legally applicable',
            mode_direct_help: 'Schedules sector deadlines, surfaces dashboard widgets, and contributes to your compliance score.',
            mode_reference_label: 'Reference only',
            mode_reference_help: 'Browseable + authorable, but no deadlines, widgets, or score contribution.',
            reason_note_label: 'Reason (optional)',
            reason_note_placeholder: 'e.g. Bank subsidiary acquired',
            cancel: 'Cancel',
            confirm: 'Confirm',
            confirming: 'Saving…',
            save: 'Save',
            saving: 'Saving…',
            toast_mode_changed: 'Sector overlay mode updated.',
            toast_installed: 'Sector overlay installed.',
            toast_sectors_saved: 'Sector self-identification saved.',
            // Install modal (AC-6)
            install_modal_title: 'Install sector overlay',
            install_modal_intro: 'Sector overlays are opt-in regulatory packs scoped to a single industry. Choose how this pack should apply to your organization.',
            install_modal_footer: 'You can change install mode later from the framework detail page.',
            install_button: 'Install',
            installed_label: 'Installed',
            uninstall_button: 'Uninstall',
            uninstall_confirm: 'Confirm uninstall',
            uninstall_cancel: 'Cancel',
            suggested_label: 'Suggested',
            // Catalog page (AC-6)
            catalog_page_title: 'Sector overlays',
            catalog_page_intro: 'Optional regulatory packs scoped to a single industry sector. Pick Direct if your organization is in the sector, Reference for browse-only access.',
            catalog_empty_title: 'No sector overlays available',
            catalog_empty_description: 'Sector overlay packs published in your platform will appear here.',
            authority_label: 'Authority',
            // Org Sectors page (AC-5 FE)
            org_sectors_title: 'Industry sectors',
            org_sectors_intro: 'Select the industry sectors your organization operates in. Drives the default install mode (Direct vs Reference) for sector-overlay regulatory packs. Sector changes do not affect existing installs — flip those manually from the framework detail page.',
            // Dashboard widget (AC-8)
            widget_title: 'Sector overlay reports due',
            widget_aria_label: 'Sector overlay reports due',
            widget_empty: 'No upcoming sector overlay deadlines.',
            // Audit-log event labels
            audit_event_mode_changed: 'Sector overlay install mode changed',
            audit_event_installed: 'Sector overlay installed',
            view_details_button: 'View details →',
            banner_aria_label: 'Sector overlay status',
            toast_load_failed: 'Failed to load sector overlays',
            toast_install_failed: 'Failed to install sector overlay',
            toast_uninstall_confirm_required: 'Type "{packCode}" to confirm uninstall.',
            toast_uninstalled: 'Uninstalled {packCode}.',
            toast_uninstall_failed: 'Failed to uninstall sector overlay',
            uninstall_warning_destructive_label: 'Destructive action.',
            uninstall_warning_body: 'Uninstalling will delete the framework record and any persisted regulatory deadlines tied to {frameworkCode} from this organisation.',
            uninstall_warning_type_to_confirm: 'Type {packCode} to confirm.',
            uninstall_in_progress: 'Uninstalling…',
            widget_view_all: 'View all sector overlays →',
            widget_due_in_days: 'Due in {days}d',
            widget_overdue_days: 'Overdue by {days}d',
            widget_due_today: 'Due today',
            widget_due_unknown: '—',
        },
        // ---------- Sprint 46.3.0 — Phased deadline engine ----------
        phased_obligations: {
            panel_title: 'Phased obligations',
            panel_aria_label: 'Phased obligations',
            panel_intro: 'Some clauses in this framework activate at a later date than the framework itself. Track activation status and record early implementation here.',
            phased_effective_date_label: 'Phased effective:',
            status_pending: 'Pending',
            status_active: 'Active',
            status_early: 'Active (early-implemented)',
            notice_window_label: 'Authority notice window:',
            days_label: 'days',
            recipient_label: 'Recipient:',
            early_implement_button: 'Mark early-implemented',
            early_implement_modal_title: 'Mark phased obligation as early-implemented',
            early_implement_modal_intro: 'Recording an early implementation activates the obligation now (instead of waiting for the phased effective date) and schedules the regulator notice timer.',
            effective_at_label: 'Effective from',
            reason_label: 'Reason (optional)',
            reason_placeholder: 'e.g. Board approved early adoption 2026-09-01',
            confirm: 'Confirm',
            toast_early_implemented: 'Early implementation recorded.',
            audit_event_early_implemented: 'Phased obligation marked early-implemented',
            widget_rolling_window_label: 'Phased activations within 90 days',
        },
    },
    // ---------- Partner Marketplace (Sprint 3.5.0) ----------
    partner_marketplace: {
        nav: {
            dashboard: 'Dashboard',
            partner_orgs: 'Partner Orgs',
            service_tiers: 'Service Tiers',
            deliverable_templates: 'Deliverable Templates',
            deliverables: 'Deliverables',
            brand_profiles: 'Brand Profiles',
            support_tickets: 'Support Tickets',
            workflow_templates: 'Workflow Templates',
            workflows: 'Workflows',
            escalation: 'Escalation',
            reports: 'Reports',
            audit: 'Audit Trail',
        },
        dashboard: {
            title: 'Partner Dashboard',
            loading: 'Loading dashboard data...',
            total_managed_tenants: 'Total Managed Tenants',
            avg_health_score: 'Avg Health Score',
            open_tickets: 'Open Tickets',
            upcoming_deliverables: 'Upcoming Deliverables',
            escalated_tickets: 'Escalated Tickets',
            managed_tenants: 'Managed Tenants',
            partner_tier: 'Partner Tier',
            managed_tenants_count: 'Managed Tenants Count',
            onboarding_date: 'Onboarding Date',
            view_details: 'View Details',
            no_partner_orgs: 'No partner organizations found.',
        },
        partner_orgs: {
            title: 'Partner Organizations',
            new: 'New Partner Org',
            contact_name: 'Contact Name',
            contact_email: 'Contact Email',
            partner_tier: 'Partner Tier',
            managed_orgs: 'Managed Orgs',
            onboarding_date: 'Onboarding Date',
            no_orgs: 'No partner organizations found.',
            contact_phone: 'Contact Phone',
            metadata_json: 'Metadata (JSON)',
            change_status: 'Change Status',
            new_status: 'New Status',
            invalid_json_metadata: 'Invalid JSON in metadata field',
            suspended: 'Suspended',
            offboarded: 'Offboarded',
            all_tiers: 'All Tiers',
            silver: 'Silver',
            gold: 'Gold',
            platinum: 'Platinum',
            status_offboarded: 'Offboarded',
            status_suspended: 'Suspended',
            tier_gold: 'Gold',
            tier_platinum: 'Platinum',
            tier_silver: 'Silver',
        },
        service_tiers: {
            title: 'Service Tiers',
            new: 'New Tier',
            tier_name: 'Tier Name',
            monthly_fee: 'Monthly Fee',
            features: 'Features',
            no_tiers: 'No service tiers found.',
            tier_code: 'Tier Code',
            user_seats: 'User Seats',
            user_seat_limit: 'User Seat Limit',
            storage_gb: 'Storage (GB)',
            storage_limit_gb: 'Storage Limit (GB)',
            response_time_hrs: 'Response Time (hrs)',
            resolution_time_hrs: 'Resolution Time (hrs)',
            review_frequency: 'Review Frequency',
            sla_response_time: 'SLA Response Time (hours)',
            sla_resolution_time: 'SLA Resolution Time (hours)',
            module_profile_json: 'Module Profile (JSON)',
            comparing: 'Comparing...',
            compare: 'Compare',
            comparison: 'Service Tier Comparison',
            attribute: 'Attribute',
            deprecate: 'Deprecate',
            deprecated: 'Deprecated',
            invalid_json_module: 'Invalid JSON in module profile field',
            select_compare: 'Select at least 2 tiers to compare',
            comparison_title: 'Service Tier Comparison',
            status_deprecated: 'Deprecated',
            activate: 'Activate',
            invalid_json_module_profile: 'Invalid JSON in module profile',
            resolution_time: 'Resolution Time',
            response_time: 'Response Time',
            select_at_least_two: 'Select at least 2 tiers',
        },
        deliverable_templates: {
            title: 'Deliverable Templates',
            new: 'New Template',
            template_name: 'Template Name',
            deliverable_type: 'Deliverable Type',
            no_templates: 'No deliverable templates found.',
            frequency: 'Frequency',
            est_effort_hrs: 'Est. Effort (hrs)',
            estimated_effort: 'Estimated Effort (hours)',
            associated_module: 'Associated Module',
            linked_tiers_count: 'Linked Tiers Count',
            evidence_requirements: 'Evidence Requirements (comma-separated)',
            tier_ids: 'Tier IDs (comma-separated)',
            frequency_one_time: 'One Time',
            frequency_monthly: 'Monthly',
            frequency_quarterly: 'Quarterly',
            frequency_annually: 'Annually',
            type_report: 'Report',
            type_review: 'Review',
            type_assessment: 'Assessment',
            type_meeting: 'Meeting',
            type_configuration: 'Configuration',
            freq_one_time: 'One Time',
            freq_monthly: 'Monthly',
            freq_quarterly: 'Quarterly',
            freq_annually: 'Annually',
            status_deprecated: 'Deprecated',
        },
        deliverables: {
            title: 'Deliverables',
            new: 'New Deliverable',
            partner: 'Partner',
            deliverable_type: 'Deliverable Type',
            due_date: 'Due Date',
            no_deliverables: 'No deliverables found.',
            assigned_to: 'Assigned To',
            completion_date: 'Completion Date',
            update_status: 'Update Status',
            hide_details: 'Hide Details',
            view_details: 'View Details',
            evidence_ids: 'Evidence IDs',
            client_approval: 'Client Approval',
            bulk_update: 'Bulk Update',
            bulk_update_status: 'Bulk Update Status',
            generate_schedule: 'Generate Schedule',
            generate_deliverable_schedule: 'Generate Deliverable Schedule',
            organization_id: 'Organization ID',
            service_tier_id: 'Service Tier ID',
            filter_assigned_to: 'Filter by Assigned To',
            select_status: 'Select status...',
            new_status: 'New Status',
            upcoming: 'Upcoming',
            overdue: 'Overdue',
            status_deferred: 'Deferred',
            status_overdue: 'Overdue',
            status_upcoming: 'Upcoming',
        },
        brand_profiles: {
            title: 'Brand Profiles',
            new: 'New Brand Profile',
            partner: 'Partner',
            brand_name: 'Brand Name',
            primary_color: 'Primary Color',
            logo_url: 'Logo URL',
            no_profiles: 'No brand profiles found.',
            secondary_color: 'Secondary Color',
            header_text: 'Header Text',
            footer_text: 'Footer Text',
            contact_email: 'Contact Email',
            contact_phone: 'Contact Phone',
            contact_website: 'Contact Website',
            contact_address: 'Contact Address',
            is_default: 'Is Default',
            set_default: 'Set Default',
            preview: 'Preview',
            report_header_preview: 'Report Header Preview',
            report_footer_preview: 'Report Footer Preview',
            preview_description: 'This is a preview of the report header with the selected brand colors.',
            contact_website_short: 'Website',
            logo_preview: 'Logo preview',
        },
        support_tickets: {
            title: 'Support Tickets',
            new: 'New Ticket',
            partner: 'Partner',
            subject: 'Subject',
            assigned_to: 'Assigned To',
            no_tickets: 'No support tickets found.',
            category: 'Category',
            priority: 'Priority',
            escalation_tier: 'Escalation Tier',
            sla_status: 'SLA Status',
            update_status: 'Update Status',
            escalate: 'Escalate',
            add_note: 'Add Note',
            escalate_ticket: 'Escalate Ticket',
            escalate_to_tier: 'Escalate To Tier',
            notes_timeline: 'Notes Timeline',
            no_notes: 'No notes yet.',
            attachments: 'Attachments',
            no_attachments: 'No attachments.',
            client_org_id: 'Client Org ID',
            content: 'Content',
            note_type: 'Type',
            view_detail: 'View Detail',
            hide: 'Hide',
            new_status: 'New Status',
            all_categories: 'All Categories',
            all_priorities: 'All Priorities',
            all_tiers: 'All Tiers',
            ticket_not_found: 'Ticket not found',
            ticket_id: 'Ticket ID',
            client_org: 'Client Org',
            deescalate: 'De-escalate',
            deescalate_ticket: 'De-escalate Ticket',
            deescalate_to_tier: 'De-escalate To Tier',
            add_attachment: 'Add Attachment',
            file: 'File',
            linked_entity: 'Linked Entity',
            sla_target: 'Target',
            breached: 'Breached',
            remaining_hours: 'remaining',
            na: 'N/A',
            tickets_found: 'tickets found',
            reason: 'Reason',
            target: 'Target',
            upload: 'Upload',
        },
        workflow_templates: {
            title: 'Workflow Templates',
            new: 'New Template',
            template_name: 'Template Name',
            steps: 'Steps',
            no_templates: 'No workflow templates found.',
            category: 'Category',
            view_steps: 'View Steps',
            hide_steps: 'Hide Steps',
            step: 'Step',
            step_title: 'Title',
            step_description: 'Description',
            assignee_role: 'Assignee Role',
            sla_hours: 'SLA Hours',
            evidence_required: 'Evidence Required',
            guidance_notes: 'Guidance Notes',
            add_step: 'Add Step',
            no_steps: 'No steps defined.',
            no_steps_defined: 'No steps defined.',
            templates_found: 'templates found',
            all_categories: 'All Categories',
        },
        workflows: {
            title: 'Workflow Instances',
            new: 'New Workflow',
            template: 'Template',
            partner: 'Partner',
            current_step: 'Current Step',
            progress: 'Progress',
            no_workflows: 'No workflow instances found.',
            instances_found: 'instances found',
        },
        escalation: {
            title: 'Escalation Dashboard',
            ticket: 'Ticket',
            partner: 'Partner',
            escalated_at: 'Escalated At',
            reason: 'Reason',
            no_escalations: 'No escalations found.',
            time_in_tier: 'Time in Tier',
            auto_evaluate: 'Auto Evaluate',
            priority_critical: 'Critical',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            deescalated_from_dashboard: 'De-escalated from dashboard',
            sla_breached: 'SLA Breached',
            sla_remaining: 'SLA Remaining',
        },
        reports: {
            title: 'Reports',
            partner: 'Partner',
            report_type: 'Report Type',
            generated_at: 'Generated At',
            no_reports: 'No reports found.',
            generate: 'Generate Report',
        },
        audit: {
            title: 'Partner Audit Trail',
            event_type: 'Event Type',
            entity_type: 'Entity Type',
            entity_id: 'Entity ID',
            actor: 'Actor',
            timestamp: 'Timestamp',
            no_events: 'No audit events found.',
            events_found: 'events found',
            event_type_created: 'Created',
            event_type_updated: 'Updated',
            event_type_deleted: 'Deleted',
            event_type_status_change: 'Status Change',
            event_type_escalation: 'Escalation',
            event_type_assignment: 'Assignment',
            entity_type_partner_org: 'Partner Org',
            entity_type_service_tier: 'Service Tier',
            entity_type_deliverable_template: 'Deliverable Template',
            entity_type_deliverable_instance: 'Deliverable Instance',
            entity_type_brand_profile: 'Brand Profile',
            entity_type_support_ticket: 'Support Ticket',
            entity_type_workflow_template: 'Workflow Template',
            entity_type_workflow_instance: 'Workflow Instance',
            entity_type_generated_report: 'Generated Report',
            entity_type_escalation_rule: 'Escalation Rule',
        },
        no_permission_title: 'Insufficient Permissions',
        no_permission_message: 'You do not have permission to access the partner workspace.',
        // Enum labels
        status_active: 'Active',
        status_suspended: 'Suspended',
        status_offboarded: 'Offboarded',
        status_completed: 'Completed',
        status_cancelled: 'Cancelled',
        status_deprecated: 'Deprecated',
        status_new: 'New',
        status_triaged: 'Triaged',
        status_in_progress: 'In Progress',
        status_waiting_on_client: 'Waiting on Client',
        status_waiting_on_vendor: 'Waiting on Vendor',
        status_escalated: 'Escalated',
        status_resolved: 'Resolved',
        status_closed: 'Closed',
        status_pending: 'Pending',
        status_skipped: 'Skipped',
        category_onboarding: 'Onboarding',
        category_incident_escalation: 'Incident Escalation',
        category_config_change: 'Config Change',
        category_periodic_review: 'Periodic Review',
        category_offboarding: 'Offboarding',
        category_custom: 'Custom',
        category_configuration: 'Configuration',
        category_incident: 'Incident',
        category_compliance_question: 'Compliance Question',
        category_feature_request: 'Feature Request',
        category_bug_report: 'Bug Report',
        priority_critical: 'Critical',
        priority_high: 'High',
        priority_medium: 'Medium',
        priority_low: 'Low',
        tier_silver: 'Silver',
        tier_gold: 'Gold',
        tier_platinum: 'Platinum',
        report_type_compliance_summary: 'Compliance Summary',
        report_type_risk_overview: 'Risk Overview',
        report_type_incident_report: 'Incident Report',
        report_type_consent_status: 'Consent Status',
        report_type_program_maturity: 'Program Maturity',
        report_type_executive_pack: 'Executive Pack',
        note_type_internal: 'Internal',
        note_type_client_visible: 'Client Visible',
        // Announce success messages
        status_updated: 'Status updated successfully',
        deliverable_status_updated: 'Deliverable status updated successfully',
        ticket_status_updated: 'Ticket status updated successfully',
        ticket_escalated: 'Ticket escalated successfully',
        note_added: 'Note added successfully',
        attachment_uploaded: 'Attachment uploaded successfully',
        ticket_deescalated: 'Ticket de-escalated successfully',
        auto_evaluation_completed: 'Auto-evaluation completed',
        template_created: 'Workflow template created successfully',
        template_updated: 'Workflow template updated successfully',
        brand_set_default: 'Brand profile set as default successfully',
        brand_created: 'Brand profile created successfully',
        brand_updated: 'Brand profile updated successfully',
        tier_created: 'Service tier created successfully',
        tier_updated: 'Service tier updated successfully',
        tier_deprecated: 'Service tier deprecated successfully',
        tier_activated: 'Service tier activated successfully',
        // Reports page labels
        period: 'Period',
        format: 'Format',
        period_start: 'Period Start',
        period_end: 'Period End',
        reports_count: '{count} reports',
        reports_count_one: '{count} report',
        // Misc labels
        metric: 'Metric',
        value: 'Value',
        showing_chart_view: 'Showing chart view',
        showing_table_view: 'Showing data table view',
        evidence_ids_placeholder: 'e.g. ev-001, ev-002',
        confirm_cancel_workflow: 'Are you sure you want to cancel this workflow? This action cannot be undone.',
        export_csv: 'Export CSV',
        // QA sweep — compliance context info boxes (Sprint 32.8.0)
        compliance_qualifications_title: 'Compliance Service Qualifications',
        compliance_qualifications_desc: 'Partner tier (Silver/Gold/Platinum) determines compliance service scope. Gold and Platinum partners are qualified for regulated-industry engagements. Use the Metadata field to record certifications (ISO 27001, SOC 2), specializations, and jurisdictional qualifications for each partner.',
        regulatory_output_title: 'Regulatory Output Alignment',
        regulatory_output_desc: 'Each template type maps to a regulatory output category: Reports produce compliance documentation, Reviews validate control effectiveness, Assessments cover risk evaluations (DPIA, gap analysis), Meetings document governance sessions. Evidence Requirements define the artifacts each deliverable must reference for audit readiness.',
        sla_compliance_title: 'SLA Tracking for Compliance Delivery',
        sla_compliance_desc: 'Response and resolution time SLAs are contractually binding for compliance service delivery. Review Frequency determines how often partner compliance deliverables are evaluated. SLA breaches on compliance-critical tickets trigger automatic escalation and are logged in the Partner Audit Trail for regulatory evidence.',
        regulatory_engagement_title: 'Regulatory Engagement Patterns',
        regulatory_engagement_desc: 'Workflow categories map to regulatory engagement types: Onboarding covers initial compliance setup and gap analysis, Periodic Review handles recurring compliance assessments, Incident Escalation manages regulatory breach response, Offboarding ensures data retention obligations are met. Each step can require evidence collection for audit trail completeness.',
        compliance_escalation_title: 'Compliance-Critical Escalation Procedures',
        compliance_escalation_desc: 'L1 handles standard operational issues. L2 covers compliance-impacting failures (missed regulatory deadlines, control gaps). L3 is reserved for regulatory breach scenarios requiring immediate executive attention. SLA breach on L2/L3 tickets generates audit trail entries automatically. All escalation and de-escalation actions are permanently logged.',
        audit_regulatory_title: 'Regulatory Evidence Trail',
        audit_regulatory_desc: 'This audit trail provides tamper-evident records for regulatory compliance. Every partner action (create, update, delete, escalation, status change) is logged with actor identity, timestamp, and full payload diff. Use Entity Type filters to isolate evidence by compliance domain. Export filtered results to produce audit-ready documentation for regulatory inspections.',
        evidence_trail_title: 'Cross-Module Evidence Trail',
        evidence_trail_desc: 'Each workflow step can collect Evidence IDs linking to deliverables, audit events, and compliance artifacts across modules. Advancing a step with evidence IDs creates a verifiable chain connecting partner workflows to regulatory documentation. Completed workflows with full evidence chains serve as proof of compliance engagement delivery.',
        evidence_ids_label: 'Evidence IDs',
    },
    // ---------- Org Groups (Sprint 11.5.0) ----------
    org_groups: {
        title: 'Org Groups',
        new: 'Create Group',
        group_name: 'Group Name',
        member_count: 'Member Count',
        root_org: 'Root Org',
        no_groups: 'No org groups found.',
        add_organization: 'Add Organization',
        select_organization: 'Select an organization...',
        no_available_orgs: 'No available organizations to add.',
        no_members: 'No member organizations in this group.',
        member_added: 'Organization added to group.',
        member_removed: 'Organization removed from group.',
        already_in_group: 'This organization is already in a group.',
        org_not_found: 'Organization not found.',
        add_member_error: 'Failed to add organization to group.',
        remove_member_error: 'Failed to remove organization from group.',
        confirm_remove_member: 'Are you sure you want to remove "{name}" from this group?',
        load_error: 'Failed to load org group details.',
        // Page titles for usePageTitle
        page_title_list: 'Organization Groups',
        page_title_detail: 'Organization Group Details',
        page_title_create: 'Create Organization Group',
        page_title_settings: 'Organization Group Settings',
        page_title_users: 'Group Users',
        page_title_reports: 'Group Reports',
        page_title_dashboard: 'Group Dashboard',
        page_title_audit: 'Group Audit',
        page_title_org_audit: 'Organization Group Audit',
        page_title_shared_vendors: 'Shared Vendors',
        page_title_shared_frameworks: 'Shared Frameworks',
        page_title_shared_forms: 'Shared Form Templates',
        page_title_merged_vendors: 'Merged Vendors',
        page_title_settings_inheritance: 'Settings Inheritance',
        // Announce messages
        announce_loaded_groups: 'Loaded {count} organization groups',
        announce_loaded_group: 'Loaded group {name}',
        announce_loaded_users: 'Loaded {count} group users',
        announce_loaded_reports: 'Loaded {count} reports',
        announce_loaded_vendors: 'Loaded {count} shared vendors',
        announce_loaded_frameworks: 'Loaded {count} shared frameworks',
        announce_loaded_templates: 'Loaded {count} shared form templates',
        announce_loaded_audit: 'Loaded {count} audit events',
        announce_loaded_merged: 'Loaded {shared} shared and {local} local vendors',
        announce_dashboard_loaded: 'Dashboard loaded.',
        announce_dashboard_refreshed: 'Dashboard refreshed.',
        announce_dashboard_refresh_failed: 'Failed to refresh dashboard.',
        announce_refreshing: 'Refreshing dashboard...',
        announce_settings_loaded: 'Settings inheritance loaded.',
        announce_settings_saved: 'Settings saved successfully.',
        announce_saving_settings: 'Saving settings...',
        announce_error_saving: 'Error saving settings.',
        announce_group_dissolved: 'Group dissolved.',
        announce_creating_group: 'Creating organization group...',
        announce_group_created: 'Organization group created successfully.',
        announce_error_creating: 'Error creating organization group.',
        announce_vendor_published: 'Vendor published successfully.',
        announce_vendor_retired: 'Vendor retired.',
        announce_framework_published: 'Framework published successfully.',
        announce_framework_retired: 'Framework retired.',
        announce_template_published: 'Form template published successfully.',
        announce_template_retired: 'Form template retired.',
        announce_role_assigned: 'Role assigned successfully.',
        announce_error_assign_role: 'Error assigning role.',
        announce_role_revoked: 'Role revoked successfully.',
        announce_report_generating: 'Generating report...',
        announce_report_requested: 'Report generation requested.',
        announce_error_report: 'Error requesting report.',
        announce_report_deleted: 'Report deleted.',
        announce_search_applied: 'Search applied.',
        announce_search_cleared: 'Search cleared.',
        announce_tab_selected: '{tab} tab selected',
        announce_error_add_member: 'Error adding member to group.',
        announce_category_reverted: '{category} reverted to inherited from parent.',
        announce_category_overridden: '{category} set to local override.',
        announce_override_saved: '{category} override saved.',
        announce_export_started: 'Exporting audit as {format}...',
        announce_export_completed: 'Export as {format} completed.',
        announce_export_failed: 'Export failed.',
        announce_filter_org: 'Filtered by organization {name}',
        announce_filter_org_cleared: 'Organization filter cleared',
        announce_filter_event: 'Filtered by event type {type}',
        announce_filter_cleared: 'Filter cleared',
        announce_download: 'Downloading {name} as {format}',
        // Error messages
        error_load_groups: 'Failed to load org groups. Please try again.',
        error_load_users: 'Failed to load group users.',
        error_load_reports: 'Failed to load group reports.',
        error_load_dashboard: 'Failed to load group dashboard.',
        error_load_settings: 'Failed to load group settings.',
        error_load_vendors: 'Failed to load merged vendors.',
        error_save_settings: 'Failed to save settings.',
        error_dissolve_group: 'Failed to dissolve group.',
        error_assign_role: 'Failed to assign role.',
        error_create_group: 'Failed to create org group. Please try again.',
        error_request_report: 'Failed to request report.',
        error_group_name_required: 'Group name is required.',
        // Success messages
        success_settings_saved: 'Settings saved successfully.',
        // Confirm dialogs
        confirm_retire_vendor: 'Are you sure you want to retire vendor "{name}"?',
        confirm_retire_framework: 'Are you sure you want to retire framework "{name}"?',
        confirm_retire_template: 'Are you sure you want to retire form template "{name}"?',
        confirm_dissolve_group: 'Are you sure you want to dissolve "{name}"? This action cannot be undone.',
        confirm_revoke_role: 'Are you sure you want to revoke the "{role}" role from "{name}"?',
        confirm_delete_report: 'Are you sure you want to delete the report "{name}"?',
        // Dissolve
        dissolve_group: 'Dissolve Group',
        dissolve_warning: 'This will permanently dissolve the organization group. All member organizations will be removed.',
        // Pluralization
        count_groups: '{count} group',
        count_groups_plural: '{count} groups',
        count_users: '{count} user',
        count_users_plural: '{count} users with group roles',
        count_events: '{count} event',
        count_events_plural: '{count} events',
        count_reports: '{count} report',
        count_reports_plural: '{count} reports',
        count_vendors: '{count} vendor shared across the group',
        count_vendors_plural: '{count} vendors shared across the group',
        count_frameworks: '{count} framework shared across the group',
        count_frameworks_plural: '{count} frameworks shared across the group',
        count_templates: '{count} template shared across the group',
        count_templates_plural: '{count} templates shared across the group',
        count_shared_vendors: '{count} shared vendors',
        count_local_vendors: '{count} local vendors',
        merged_combined_view: 'Combined view of {shared} shared and {local} local vendors',
        // Dashboard labels
        dashboard: {
            title: 'Group Dashboard',
            loading: 'Loading dashboard data...',
            total_groups: 'Total Groups',
            total_members: 'Total Members',
            active_groups: 'Active Groups',
            overall_compliance_score: 'Overall Compliance Score',
            group_risk_level: 'Group Risk Level',
            sla_compliance: 'SLA Compliance',
            compliance_posture: 'Compliance Posture',
            risk_heatmap: 'Risk Heatmap',
            sla_performance: 'SLA Performance',
            recent_incidents: 'Recent Incidents',
            recent_activity: 'Recent Activity',
            refresh_label: 'Refresh dashboard data',
            // Score labels
            score_good: 'Good',
            score_needs_attention: 'Needs attention',
            score_critical: 'Critical',
            // Trend labels
            trend_up: 'Trending up',
            trend_down: 'Trending down',
            trend_stable: 'Stable',
            // Risk level labels
            risk_low: 'Low',
            risk_medium: 'Medium',
            risk_high: 'High',
            risk_critical: 'Critical',
            risk_unknown: 'Unknown',
            // Posture table headers
            col_organization: 'Organization',
            col_score: 'Score',
            col_trend: 'Trend',
            col_open_issues: 'Open Issues',
            col_critical_issues: 'Critical Issues',
            col_health: 'Health',
            // Risk heatmap headers
            col_vendor_risk: 'Vendor Risk',
            col_data_risk: 'Data Risk',
            col_compliance_risk: 'Compliance Risk',
            // SLA headers
            col_total_cases: 'Total Cases',
            col_on_time: 'On Time',
            col_breached: 'Breached',
            col_sla_percent: 'SLA %',
            col_avg_days: 'Avg Days',
            // Screen reader
            sr_vendor_risk: 'Vendor risk score: {score}',
            sr_data_risk: 'Data risk score: {score}',
            sr_compliance_risk: 'Compliance risk score: {score}',
            sr_sla_breaches: 'Warning: {count} SLA breaches',
            sr_severity: 'Severity: {severity}',
        },
        detail: {
            title: 'Group Detail',
            members: 'Members',
            settings: 'Settings',
            audit: 'Audit',
            sr_loading: 'Loading organization group details',
            sr_loading_hierarchy: 'Loading hierarchy',
            member_org: 'member organization',
            root_org_label: 'root organization',
        },
        users: {
            title: 'Group Users',
            add_user: 'Add User',
            no_users: 'No users in this group.',
            role_group_admin: 'Group Admin',
            role_group_reader: 'Group Reader',
            sr_loading: 'Loading group users',
        },
        reports: {
            title: 'Group Reports',
            no_reports: 'No reports found.',
            generate_report: 'Generate new report',
            report_type_compliance: 'Compliance Summary',
            report_type_risk: 'Risk Overview',
            report_type_sla: 'SLA Performance',
            report_type_incident: 'Incident Report',
            report_type_executive: 'Executive Brief',
            report_type_board: 'Board Report',
            status_queued: 'Queued',
            status_generating: 'Generating',
            status_completed: 'Completed',
            status_failed: 'Failed',
            period_start: 'Period Start',
            period_end: 'Period End',
            sr_loading: 'Loading group reports',
        },
        shared_vendors: {
            title: 'Shared Vendors',
            no_vendors: 'No shared vendors found.',
            merged_vendors: 'Merged Vendors',
            publish_form_label: 'Publish shared vendor',
            sr_loading: 'Loading shared vendors',
            sr_loading_merged: 'Loading merged vendors',
            local_vendors: 'Local Vendors',
            source_shared: 'Shared from group',
            source_local: 'Local vendor',
            // Category labels
            category_technology: 'Technology',
            category_consulting: 'Consulting',
            category_outsourcing: 'Outsourcing',
            category_cloud: 'Cloud',
            category_data_processing: 'Data Processing',
            category_other: 'Other',
            // Risk tier labels
            risk_tier_low: 'Low',
            risk_tier_medium: 'Medium',
            risk_tier_high: 'High',
            risk_tier_critical: 'Critical',
            // Compliance status labels
            compliance_compliant: 'Compliant',
            compliance_non_compliant: 'Non-Compliant',
            compliance_pending: 'Pending',
            compliance_under_review: 'Under Review',
            // Status labels
            status_active: 'Active',
            status_retired: 'Retired',
        },
        shared_frameworks: {
            title: 'Shared Frameworks',
            no_frameworks: 'No shared frameworks found.',
            publish_form_label: 'Publish shared framework',
            sr_loading: 'Loading shared frameworks',
            status_active: 'Active',
            status_retired: 'Retired',
        },
        shared_forms: {
            title: 'Shared Form Templates',
            no_forms: 'No shared form templates found.',
            publish_form_label: 'Publish shared form template',
            sr_loading: 'Loading shared form templates',
            status_active: 'Active',
            status_retired: 'Retired',
        },
        audit: {
            title: 'Group Audit',
            consolidated_title: 'Consolidated Audit Trail',
            consolidated_subtitle: 'Cross-organization audit events for this group',
            audit_log_title: 'Group Audit Log',
            event_type: 'Event Type',
            actor: 'Actor',
            timestamp: 'Timestamp',
            source_org: 'Source Org',
            org: 'Org',
            summary: 'Summary',
            details: 'Details',
            no_events: 'No audit events found.',
            all_events: 'All Events',
            all_orgs: 'All Orgs',
            organization: 'Organization',
            from: 'From',
            to: 'To',
            search_events: 'Search events...',
            clear: 'Clear',
            events_24h: 'Events (24h)',
            events_7d: 'Events (7d)',
            active_orgs: 'Active Orgs',
            exporting: 'Exporting...',
            export_csv: 'Export CSV',
            export_json: 'Export JSON',
            back_to_group: 'Back to Group',
            back_to_detail: 'Back to Detail',
            sr_loading: 'Loading audit events',
            sr_loading_summary: 'Loading audit summary',
        },
        settings: {
            title: 'Group Settings',
            inheritance: 'Settings Inheritance',
            inheritance_subtitle: 'Configure which settings categories inherit from the group vs. use local overrides',
            sla_settings: 'SLA Settings',
            calendar_settings: 'Calendar Settings',
            notification_settings: 'Notification Settings',
            intake_settings: 'Intake Settings',
            cmp_integration: 'CMP Integration',
            overridden: 'Overridden',
            inheriting: 'Inheriting',
            hide_defaults: 'Hide Defaults',
            show_group_defaults: 'Show Group Defaults',
            group_defaults: 'Group Defaults',
            inherit_from_group: 'Inherit from Group',
            use_local_override: 'Use Local Override',
            local_override_json: 'Local Override (JSON)',
            save_override: 'Save Override',
            saving: 'Saving...',
            sr_loading: 'Loading settings inheritance',
            sr_loading_settings: 'Loading group settings',
            sr_enabled: 'Enabled',
            sr_disabled: 'Disabled',
            sr_override_active: 'Local override active',
            sr_inherited: 'Inherited from parent',
            danger_zone: 'Danger Zone',
            sharing_options: 'Sharing Options',
            advanced_options: 'Advanced Options',
        },
        // Status badge labels
        status_active: 'Active',
        status_dissolved: 'Dissolved',
        status_pending: 'Pending',
        status_suspended: 'Suspended',
    },
    // ---------- Integrations: Webhooks, Domains, Exports (Sprint 3.6.0) ----------
    integrations: {
        nav: {
            webhooks: 'Webhooks',
            domains: 'Domains',
            export_schedules: 'Export Schedules',
            email_log: 'Email Log',
        },
    },
    // ---------- Platform: Users, Roles, Profile (Sprint 3.0.0) ----------
    platform: {
        nav: {
            users: 'Users',
            roles: 'Roles',
            profile: 'My Profile',
        },
        security: {
            mfa: 'MFA Settings',
            sso: 'SSO Configuration',
            api_keys: 'API Keys',
            service_accounts: 'Service Accounts',
            ip_allowlist: 'IP Allowlist',
            delegations: 'Delegations',
            break_glass: 'Break-Glass Access',
            session_settings: 'Session Settings',
            active_sessions: 'Active Sessions',
            access_reviews: 'Access Reviews',
            my_reviews: 'My Reviews',
            role_analytics: 'Role Analytics',
            attestation_schedules: 'Attestation Schedules',
            sod_rules: 'SoD Rules',
            sod_violations: 'SoD Violations',
        },
        module_config: {
            title: 'Module Configuration',
            subtitle: 'Enable or disable feature modules for your organization. Disabled modules are hidden from the navigation.',
            enabled: 'Enabled',
            disabled: 'Disabled',
            save: 'Save Configuration',
            saving: 'Saving...',
            saved: 'Configuration saved successfully',
            nav_label: 'Module Configuration',
            settings: 'Settings',
            webhooks: 'Webhooks',
            domains: 'Domains',
            export_schedules: 'Export Schedules',
            email_log: 'Email Log',
        },
        // Sprint 41.4.0 F14 — Platform → Files Configuration & Browser
        // EN translations. The 14 other locales are deferred to Sprint 51.0.0
        // per the kickoff scope amendment; only en.ts and vi.ts contain
        // these keys in this sprint.
        files_nav: {
            section: 'Files',
            configuration: 'Configuration',
            browser: 'File Browser',
            dashboard: 'Files Dashboard',
        },
        // Immutable Audit Trails — shared shell strings for the four sub-pages
        // (Settings, Proof Status, Integrity Center, Usage & Credits). EN + VI
        // are authored here; the other 14 locales fall back to EN via the
        // deepMerge in i18n/index.ts.
        immutable_audit_trails: {
            nav_group_label: 'Audit & Proof',
            nav_settings: 'Settings',
            nav_status: 'Proof Status',
            nav_integrity_center: 'Integrity Center',
            nav_usage: 'Usage & Credits',
            settings_title: 'Immutable Audit Trails — Settings',
            settings_subtitle: 'Per-org settings for the Immutable Audit Trails add-on.',
            settings_banner: 'The Immutable Audit Trails add-on is not enabled for your organization. Enable it here to start anchoring proof for Tier-A and Tier-B audit events.',
            status_title: 'Immutable Audit Trails — Proof Status',
            status_subtitle: 'Per-event proof relationships across modules.',
            status_banner: 'The Immutable Audit Trails add-on is not enabled for your organization. Visit Settings to enable it.',
            integrity_center_title: 'Immutable Audit Trails — Integrity Center',
            integrity_center_subtitle: 'Verify proofs and export Integrity Packs.',
            integrity_center_banner: 'The Immutable Audit Trails add-on is not enabled for your organization. Visit Settings to enable it before running verification or exporting Integrity Packs.',
            usage_title: 'Immutable Audit Trails — Usage & Credits',
            usage_subtitle: 'Credit balance, package purchases, and usage history.',
            usage_banner: 'Enable Immutable Audit Trails in Settings to start consuming proof credits.',
            module_tips_placeholder: 'Tips and guidance for this surface are available in the help center.',
            column_label: 'Proof',
            no_proof_aria: 'No proof recorded',
            proof_status_receipt_label: 'receipt {id}',
            in_progress_hint: 'In progress — updates automatically',
            status_pills: {
                pending: 'Pending',
                submitted: 'Submitted',
                anchored: 'Anchored',
                confirmed: 'Confirmed',
                retrying: 'Retrying',
                paused_cap: 'Paused (cap)',
                failed: 'Failed',
                failed_auth: 'Auth failed',
            },
            status_tooltips: {
                pending: 'Pending — queued for the proof service. Will move to Submitted shortly; no action needed.',
                submitted: 'Submitted — your event was sent to the proof service. For batch mode it is waiting to be sealed and anchored on-chain. Updates automatically; no action needed.',
                anchored: 'Anchored — the batch has been written on-chain and is awaiting final confirmation. Updates automatically; no action needed.',
                confirmed: 'Confirmed — the proof is anchored on-chain and verifiable.',
                retrying: 'Retrying — a transient error occurred. The reconciler is retrying automatically; no action needed.',
                paused_cap: 'Paused — your monthly proof-credit cap has been reached. Redeem more credits or wait until next billing month.',
                failed: 'Failed — anchoring did not succeed. The reconciler will retry automatically; contact support if it persists.',
                failed_auth: 'Auth failed — the proof service rejected our credentials. Check the Immutable Audit Trails Settings page.',
            },
            mode_pills: {
                direct: 'Direct',
                batch: 'Batch',
            },
            drawer: {
                title: 'Proof details',
                close: 'Close',
                section_proof: 'Proof',
                section_batch: 'Batch membership',
                status: 'Status',
                proof_reference: 'Proof reference',
                receipt_id: 'Receipt ID',
                taxonomy_class: 'Taxonomy class',
                units_consumed: 'Units consumed',
                created_at: 'Created',
                verification_ref: 'Chain verification reference',
                chain: 'Chain',
                tx_hash: 'Tx hash',
                block_ref: 'Block ref',
                batch_reference: 'Batch reference',
                leaf_index: 'Leaf index',
                local_event_hash: 'Local event hash',
                show_inclusion: 'Show inclusion path',
                hide_inclusion: 'Hide inclusion path',
                privacy_note: 'Payload is hashed; no personal data left ComplianceOne for this event.',
                verify_now: 'Verify now',
                verify_pending: 'Verification in progress…',
                verify_verified: 'Verified — local + chain-side checks passed.',
                verify_issue_prefix: 'Verification issue: ',
                verify_failed: 'Verification call failed.',
                verify_result_prefix: 'Verification verdict: ',
                verify_pending_note: 'Verification pending: this proof is still awaiting on-chain anchoring. Try again after the batch is sealed.',
                awaiting_anchor_note: 'This proof is part of an open batch. Verification becomes available once the batch is sealed and anchored on-chain — typically within the org’s configured batch window. The status badge will update automatically.',
                terminal_error_note: 'This proof failed to anchor. The proof-status reconciler will retry automatically; manual verification has nothing to check yet.',
            },
            integrity_center: {
                section_run: 'Run verification',
                section_progress: 'Run progress',
                section_issues: 'Issues',
                period_start: 'Period start',
                period_end: 'Period end',
                module_filter: 'Modules (comma-separated, optional)',
                modules_placeholder: 'rights_requests, incidents',
                run_button: 'Run verification',
                export_button: 'Export Integrity Pack',
                export_success: 'Integrity Pack exported.',
                export_failed: 'Export failed.',
                empty_disabled: 'Enable Immutable Audit Trails in Settings to run verification passes.',
                error_period_required: 'Pick a start and end date.',
                error_start_run: 'Failed to start run.',
                processed: 'Processed: {processed} of {total}',
                col_verdict: 'Verdict',
                col_count: 'Count',
                col_proof_link: 'Proof link',
                col_module: 'Module',
                col_event_type: 'Event type',
                col_expected: 'Expected hash',
                col_recomputed: 'Recomputed hash',
                col_raised_at: 'Raised at',
                any_verdict: 'Any verdict',
                no_issues: 'No issues found.',
            },
            settings_page: {
                load_error: 'Failed to load settings.',
                save: 'Save',
                save_success: 'Settings saved.',
                save_error: 'Save failed.',
                discard: 'Discard changes',
                read_only_banner: 'You have read-only access. Contact a Platform Admin to make changes.',
                section_status: 'Status & enablement',
                enabled_label: 'Add-on enabled',
                enabled_help: 'When enabled, every Tier-A and Tier-B audit event is hashed and anchored.',
                section_scope: 'Scope',
                scope_self: 'Self only',
                scope_self_and_children: 'Self and child orgs',
                scope_children_only: 'Children only (parent does not proof its own events)',
                section_mode: 'Default proof mode',
                mode_direct: 'Direct (one anchor per event)',
                mode_batch: 'Batch (Merkle-root anchor per group)',
                mode_hybrid: 'Hybrid (per-class registry default)',
                batch_window_label: 'Batch window (minutes, 1–60)',
                section_taxonomy: 'Protected event taxonomy',
                taxonomy_help: 'Per-event-class overrides. Tier A → direct mode, Tier B → batch mode, Off → not proofed.',
                taxonomy_search: 'Search overrides',
                taxonomy_reset: 'Reset to defaults',
                taxonomy_event_class: 'Event class',
                taxonomy_override: 'Override',
                taxonomy_actions: 'Actions',
                taxonomy_clear: 'Clear',
                taxonomy_no_overrides: 'No overrides — using registry defaults.',
                taxonomy_add_class: 'event_class.code',
                taxonomy_add: 'Add override',
                section_privacy: 'Privacy controls',
                privacy_log_actor_ip: 'Log actor IP',
                privacy_log_user_agent: 'Log user agent',
                privacy_privileged_access_proofing: 'Proof privileged-access reads (Tier A)',
                privacy_note: 'Changes apply prospectively only.',
                section_caps: 'Caps & limits',
                cap_label: 'Monthly cap (units; blank = unlimited)',
                warn_label: 'Warn threshold (units; must be less than cap)',
                auto_pause_label: 'Auto-pause new proofs once cap is reached',
                section_export: 'Export access',
                export_help: 'Role IDs allowed to export Integrity Packs. Comma-separated; blank = use the IMMUTABLE_AUDIT_TRAILS_EXPORT permission default.',
                section_group: 'Group participation',
                group_view_intro: 'You are configured as a group parent. Each child organisation controls its own enable/disable; their decisions roll up to your billing per the group billing rules.',
                group_view_rule_enabled: 'A child with enabled=false is shown as "Not participating". You see the absence; the child controls the toggle.',
                group_view_rule_billing: 'Child consumption rolls up to your monthly cap. Watch the Usage page to monitor combined burn-rate.',
                group_view_rule_edit: 'You cannot edit a child’s settings from this page. Open the child organisation directly to edit.',
                group_view_open_groups: 'Open Org Groups',
                section_credentials: 'Credentials',
                credential_id: 'Credential ID',
                credential_rotated_at: 'Last rotated',
                rotate_button: 'Rotate credential',
                rotate_success: 'Credential rotated. Old credential valid for {n} more minutes.',
                rotate_failed: 'Credential rotation failed.',
                rotate_modal_title: 'Rotate AesirX credential?',
                rotate_modal_body: 'Rotation issues a new credential. The old credential remains accepted by AesirX for a 60-minute grace window so in-flight workers can finish their current jobs. After 60 minutes the old credential is revoked.',
                rotate_modal_confirm: 'Rotate now',
                tier_a_off_modal_title: 'Disable proofing for an event class?',
                tier_a_off_modal_body: 'Setting an event class to "Off" stops the proof tap from anchoring those events. Architecture §6 explains the regulatory implication: Tier-A events are regulator-relevant; turning them off may break your audit trail defensibility.',
                tier_a_off_modal_confirm: 'I understand — apply override',
                error_warn_lt_cap: 'Warn threshold must be less than monthly cap.',
                error_window_range: 'Batch window must be between 1 and 60 minutes.',
                error_run_in_progress: 'A verification run is in progress; cannot disable the add-on right now.',
            },
            status_page: {
                filter_status: 'Status',
                filter_mode: 'Proof mode',
                filter_module: 'Module',
                filter_source_collection: 'Source collection',
                filter_from_date: 'From',
                filter_to_date: 'To',
                filter_search: 'Search',
                any_status: 'Any status',
                any_mode: 'Any mode',
                col_module: 'Module',
                col_event_type: 'Event type',
                col_proof_status: 'Status',
                col_proof_mode: 'Mode',
                col_created_at: 'Created',
                col_units: 'Units',
                empty_disabled: 'The Immutable Audit Trails add-on is not enabled for your organization. Visit Settings to enable it.',
                empty_enabled_no_rows: 'No proof links match the current filters.',
                export_csv: 'Export CSV',
                export_xlsx: 'Export XLSX',
                export_success: 'Export complete',
                export_failed: 'Export failed',
            },
            usage_page: {
                section_alerts: 'Alerts',
                alert_dismiss: 'Dismiss',
                alert_dismiss_aria: 'Dismiss alert {id}',
                alert_dismissed: 'Alert dismissed',
                alert_dismiss_failed: 'Failed to dismiss alert',
                section_balance: 'Credit balance',
                balance_label: 'Available credits',
                balance_hint: 'Units remaining (1 unit = 1 protected event proof).',
                section_current_month: 'Current month',
                consumed_label: 'Consumed',
                granted_label: 'Granted',
                refunded_label: 'Refunded',
                net_label: 'Net',
                section_history: 'Usage history',
                history_kind: 'Kind',
                history_units: 'Units',
                history_balance_after: 'Balance after',
                history_notes: 'Notes',
                history_at: 'When',
                cap_warning_banner: 'You have crossed your warn threshold for this billing month. Consider redeeming additional credits.',
                cap_paused_banner: 'Monthly cap reached. Proof submissions are paused until next month or until cap is increased.',
                section_redeem: 'Redeem credit package',
                redeem_purchase_id: 'Invoice Number',
                redeem_package: 'Package',
                redeem_button: 'Redeem',
                redeem_pending: 'Redeeming…',
                redeem_success: 'Credits redeemed',
                redeem_failed: 'Redeem failed',
                redeem_option_template: '{name} — {units} {units_label} — {price}',
                redeem_option_units: 'units',
                alert_dismissing: 'Dismissing…',
                // fix-sprint-20260515-2129 Findings 16 + 17 — localized alert
                // kind + usage kind labels so end-users see human-readable
                // phrasing instead of snake_case enum identifiers.
                alert_kind: {
                    cap_reached: 'Monthly cap reached',
                    low_balance: 'Low balance',
                    auth_failure: 'Authentication failure',
                    merkle_mismatch: 'Merkle mismatch',
                    batch_terminal_failure: 'Batch terminal failure',
                    direct_terminal_failure: 'Direct submission terminal failure',
                },
                usage_kind: {
                    consume: 'Consume',
                    refund: 'Refund',
                    grant: 'Grant',
                    adjustment: 'Adjustment',
                    reservation: 'Reservation',
                },
                package_starter: 'Starter',
                package_growth: 'Growth',
                package_enterprise: 'Enterprise',
                // Sprint 48.5.x — self-service "Buy More" billing (Flow A + B).
                buy_more: {
                    buy_button: 'Buy more credits',
                    purchase_title: 'Buy more credits',
                    purchase_subtitle: 'Choose a package. You will be taken to secure Stripe checkout, and your invoice will arrive by email.',
                    checkout_button: 'Continue to checkout',
                    checkout_pending: 'Opening checkout…',
                    cancel_button: 'Cancel',
                    purchase_error: 'Could not start checkout. Please try again shortly.',
                    redeem_subtitle: 'Already paid? Select the package you purchased and enter the invoice ID from your email to add the credits.',
                    error_not_found: 'No invoice was found with that number. Check the invoice ID and try again.',
                    error_not_paid: 'This invoice is not marked paid yet.',
                    error_org_mismatch: 'This invoice was not issued to your organisation.',
                    error_package_mismatch: 'This invoice does not match the selected package.',
                    error_billing_unavailable: 'Could not reach the billing service. Please try again shortly.',
                },
            },
        },
        files_config: {
            title: 'Files Configuration',
            subtitle: 'Configure file upload limits, retention policies, and per-owner-type overrides for the platform attachment surface.',
            section_global: 'Global defaults',
            section_global_help: 'These limits apply to every file uploaded by users in your organization unless a per-owner-type override below changes them.',
            section_overrides: 'Per-owner-type overrides',
            section_overrides_help: 'Override the global limits for a specific owner type. Per-owner-type values always win over the global setting.',
            max_file_size_label: 'Max file size',
            max_file_size_hint: 'Per individual upload (MiB)',
            max_total_attachments_label: 'Max total attachments',
            max_total_attachments_hint: 'Across the entire organization',
            max_total_size_label: 'Max total storage',
            max_total_size_hint: 'Across the entire organization (MiB)',
            allowed_mime_types_label: 'Allowed file types',
            allowed_mime_types_placeholder: 'Select MIME types',
            retention_days_label: 'Retention period (days)',
            retention_days_hint: 'How long files must be kept before they are eligible for deletion. Leave blank for no retention policy.',
            legal_hold_label: 'Legal hold',
            legal_hold_hint: 'When enabled, hard-deleting a file of this owner type requires the legal hold override permission and explicit operator opt-in.',
            save: 'Save changes',
            saving: 'Saving…',
            reset: 'Reset',
            save_success: 'Settings saved',
            save_version_conflict: 'Another operator updated these settings while you were editing. Reload to see the latest values.',
            empty_state_title: 'Using platform defaults',
            empty_state_body: 'Your organization is currently running on the platform default file policy. Edit any value below to create an org-level override.',
            read_only_banner: 'You have read-only access to file configuration. Contact a Platform Admin to make changes.',
            owner_type_default_badge: 'Default',
            owner_type_overridden_badge: 'Overridden',
            owner_type_label: 'Owner type',
            owner_type_collapse: 'Collapse',
            owner_type_expand: 'Expand',
            override_save: 'Save override',
            override_remove: 'Remove override',
            mime_labels: {
                'application/pdf': 'PDF document',
                'image/png': 'PNG image',
                'image/jpeg': 'JPEG image',
                'image/jpg': 'JPG image',
                'application/msword': 'Word document (legacy)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word document',
                'application/vnd.ms-excel': 'Excel spreadsheet (legacy)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel spreadsheet',
                'text/csv': 'CSV file',
                'text/plain': 'Plain text',
                'message/rfc822': 'Email message',
            },
        },
        files_browser: {
            title: 'File Browser',
            subtitle: 'Search, filter, and manage every attachment uploaded across the organization.',
            filter_owner_type: 'Owner type',
            filter_uploader: 'Uploaded by',
            filter_from: 'Uploaded after',
            filter_to: 'Uploaded before',
            filter_size_min: 'Min size (MiB)',
            filter_size_max: 'Max size (MiB)',
            filter_mime: 'File type',
            filter_deleted_state: 'Deletion state',
            filter_deleted_any: 'All files',
            filter_deleted_only: 'Only deleted',
            filter_deleted_exclude: 'Exclude deleted',
            filter_orphaned: 'Orphaned only',
            filter_search_placeholder: 'Search filename…',
            filter_apply: 'Apply filters',
            filter_clear: 'Clear filters',
            col_filename: 'Filename',
            col_owner_type: 'Owner type',
            col_owner_entity: 'Owner',
            col_uploader: 'Uploaded by',
            col_uploaded_at: 'Uploaded',
            col_size: 'Size',
            col_mime: 'Type',
            col_state: 'State',
            col_actions: 'Actions',
            state_active: 'Active',
            state_deleted: 'Deleted',
            state_orphaned: 'Orphaned',
            action_view_detail: 'View details',
            action_download: 'Download',
            // Sprint 47.5.3b — Forseti AI redline review link.
            action_ai_edits: 'AI Edits',
            action_soft_delete: 'Soft delete',
            action_restore: 'Restore',
            action_hard_delete: 'Hard delete',
            bulk_soft_delete: 'Soft-delete selected',
            bulk_export_csv: 'Export CSV',
            bulk_select_all: 'Select all',
            reason_label: 'Reason',
            reason_min_10_chars: 'Reason is required (at least 10 characters)',
            reason_min_20_chars: 'Reason is required (at least 20 characters)',
            legal_hold_override_label: 'Override legal hold',
            legal_hold_override_warning: 'This file is under legal hold. Hard-deleting it will be recorded in the audit trail with your reason and override flag.',
            empty_state_title: 'No files match your filters',
            empty_state_body: 'Adjust your filters to see results, or clear them to view all files.',
            pagination_prev: 'Previous',
            pagination_next: 'Next',
            pagination_page: 'Page {page} of {total}',
            pagination_size_label: 'Per page',
            pagination_total: 'Total: {count}',
            toast_soft_deleted: 'File soft-deleted',
            toast_restored: 'File restored',
            toast_hard_deleted: 'File permanently deleted',
            toast_bulk_deleted: '{count} file(s) deleted',
            toast_error: 'Operation failed',
            confirm_soft_delete_title: 'Soft delete file',
            confirm_restore_title: 'Restore file',
            confirm_hard_delete_title: 'Permanently delete file',
            confirm_bulk_soft_delete_title: 'Soft delete selected files',
            confirm_submit: 'Confirm',
            confirm_cancel: 'Cancel',
        },
        files_detail: {
            modal_title: 'File details',
            close: 'Close',
            meta_filename: 'Filename',
            meta_size: 'Size',
            meta_mime: 'Type',
            meta_owner_type: 'Owner type',
            meta_owner_entity: 'Owner entity',
            meta_uploader: 'Uploaded by',
            meta_uploaded_at: 'Uploaded',
            meta_storage_path: 'Storage path',
            meta_deleted_state: 'Deletion state',
            audit_section_title: 'Audit trail',
            audit_load_more: 'Load more events',
            audit_empty: 'No audit events recorded for this file.',
            audit_config_changed: 'Configuration changed',
            audit_soft_deleted: 'Soft-deleted',
            audit_restored: 'Restored',
            audit_hard_deleted: 'Permanently deleted',
            audit_downloaded: 'Downloaded',
            audit_quota_breached: 'Quota breach attempted',
            action_download: 'Download',
            action_soft_delete: 'Soft delete',
            action_restore: 'Restore',
            action_hard_delete: 'Hard delete',
            effective_settings_title: 'Effective policy',
            effective_max_file_size: 'Max file size',
            effective_retention_days: 'Retention',
            effective_legal_hold: 'Legal hold',
            effective_legal_hold_yes: 'Active',
            effective_legal_hold_no: 'None',
        },
        files_dashboard: {
            title: 'Files Dashboard',
            subtitle: 'Storage usage, growth, and orphaned files across the organization.',
            kpi_total_files: 'Total files',
            kpi_total_size: 'Total storage',
            kpi_near_quota: 'Quota usage',
            kpi_orphaned: 'Orphaned files',
            chart_by_owner_type: 'Files by owner type',
            chart_growth_30d: '30-day growth',
            toggle_count: 'By count',
            toggle_size: 'By size',
            orphaned_panel_title: 'Orphaned files (top 20)',
            orphaned_view_all: 'View all orphaned files',
            orphaned_empty: 'No orphaned files found.',
            growth_empty: 'No upload activity in the last 30 days.',
            retention_n_days: '{days} day(s)',
            retention_none: 'No policy',
        },
    },
    // ---------- Module labels (for sample data, tours, etc.) ----------
    // ---------- Help article category labels (Sprint 44.1.9.5) ----------
    // Raw category values from seed-help-articles.ts: user-guide, admin-guide,
    // getting-started, faq. Rendered in the article header chip next to Module.
    category_labels: {
        'user-guide': 'User Guide',
        'admin-guide': 'Admin Guide',
        'getting-started': 'Getting Started',
        faq: 'FAQ',
    },
    // Common help-article tags that are NOT module codes and therefore don't
    // resolve via module_labels. Kept short — unmapped tags fall back to the
    // raw string (which is usually already readable).
    tag_labels: {
        faq: 'FAQ',
        dsar: 'DSAR',
        dsr: 'DSR',
        cmp: 'CMP',
        hmac: 'HMAC',
        sso: 'SSO',
        mfa: 'MFA',
        rbac: 'RBAC',
        api: 'API',
        'api-keys': 'API Keys',
        webhook: 'Webhook',
        webhooks: 'Webhooks',
        admin: 'Admin',
        aesirx: 'AesirX',
        purposes: 'Purposes',
        preferences: 'Preferences',
        onboarding: 'Onboarding',
        'first-steps': 'First Steps',
        login: 'Login',
        navigation: 'Navigation',
        users: 'Users',
        user: 'User',
        roles: 'Roles',
        role: 'Role',
        'access-control': 'Access Control',
        'access-management': 'Access Management',
        permissions: 'Permissions',
        permission: 'Permission',
        consent: 'Consent',
        'consent-management': 'Consent Management',
        privacy: 'Privacy',
        pdpl: 'PDPL',
        gdpr: 'GDPR',
        iso27001: 'ISO 27001',
        audit: 'Audit',
        auditing: 'Auditing',
        breach: 'Breach',
        incident: 'Incident',
        incidents: 'Incidents',
        notification: 'Notification',
        notifications: 'Notifications',
        reporting: 'Reporting',
        risk: 'Risk',
        vendor: 'Vendor',
        vendors: 'Vendors',
        'vendor-risk': 'Vendor Risk',
        assessment: 'Assessment',
        assessments: 'Assessments',
        dpia: 'DPIA',
        'cross-border': 'Cross-Border',
        transfer: 'Transfer',
        transfers: 'Transfers',
        deletion: 'Deletion',
        retention: 'Retention',
        'data-mapping': 'Data Mapping',
        discovery: 'Discovery',
        classification: 'Classification',
        workflow: 'Workflow',
        workflows: 'Workflows',
        template: 'Template',
        templates: 'Templates',
        catalog: 'Catalog',
        monitoring: 'Monitoring',
        tasks: 'Tasks',
        task: 'Task',
        'task-management': 'Task Management',
        organizations: 'Organizations',
        org: 'Organization',
        governance: 'Governance',
        compliance: 'Compliance',
        forms: 'Forms',
        form: 'Form',
        connectors: 'Connectors',
        connector: 'Connector',
        integration: 'Integration',
        integrations: 'Integrations',
        security: 'Security',
        encryption: 'Encryption',
        backup: 'Backup',
        recovery: 'Recovery',
        maintenance: 'Maintenance',
        settings: 'Settings',
        configuration: 'Configuration',
        sla: 'SLA',
        escalation: 'Escalation',
        remediation: 'Remediation',
        evidence: 'Evidence',
        'case-management': 'Case Management',
        profile: 'Profile',
        delegation: 'Delegation',
        'service-accounts': 'Service Accounts',
        license: 'License',
        'license-management': 'License Management',
        plan: 'Plan',
        plans: 'Plans',
        dashboard: 'Dashboard',
        'web-consent': 'Web Consent',
        'partner-marketplace': 'Partner Marketplace',
        partner: 'Partner',
        partners: 'Partners',
        deliverables: 'Deliverables',
        'service-tiers': 'Service Tiers',
        'support-tickets': 'Support Tickets',
        'audit-trail': 'Audit Trail',
        'first-time-setup': 'First-Time Setup',
        setup: 'Setup',
        quickstart: 'Quick Start',
        tour: 'Tour',
        tours: 'Tours',
        'sample-data': 'Sample Data',
        'data-subject': 'Data Subject',
        'data-subjects': 'Data Subjects',
        pseudonymous: 'Pseudonymous',
        identified: 'Identified',
        'help-center': 'Help Center',
        article: 'Article',
        articles: 'Articles',
        search: 'Search',
        'platform-settings': 'Platform Settings',
        'platform-config': 'Platform Configuration',
        'module-config': 'Module Configuration',
        'module-configuration': 'Module Configuration',
        'rights-requests': 'Rights Requests',
        'access-accountability': 'Access Accountability',
        'data-discovery': 'Data Discovery',
        'incident-ops': 'Incident Operations',
        'deletion-orchestration': 'Deletion Orchestration',
        'compliance-forms': 'Compliance Forms',
        'program-governance': 'Program Governance',
        localization: 'Localization',
        'privacy-scanner': 'Privacy Scanner',
        'consent-properties': 'Consent Properties',
        acceptances: 'Acceptances',
        access: 'Access',
        'access-review': 'Access Review',
        'access-reviews': 'Access Reviews',
        accountability: 'Accountability',
        acknowledgment: 'Acknowledgment',
        'action-plans': 'Action Plans',
        activation: 'Activation',
        activations: 'Activations',
        'active-response': 'Active Response',
        activity: 'Activity',
        adequacy: 'Adequacy',
        'admin-guide': 'Admin Guide',
        aggregation: 'Aggregation',
        agreements: 'Agreements',
        ai: 'AI',
        alerts: 'Alerts',
        analytics: 'Analytics',
        anchoring: 'Anchoring',
        approval: 'Approval',
        approvals: 'Approvals',
        apps: 'Apps',
        assets: 'Assets',
        assignments: 'Assignments',
        attestation: 'Attestation',
        'audit-log': 'Audit Log',
        'audit-pack': 'Audit Pack',
        authentication: 'Authentication',
        authority: 'Authority',
        'auto-play': 'Auto-Play',
        automated: 'Automated',
        automation: 'Automation',
        baseline: 'Baseline',
        batches: 'Batches',
        bidirectional: 'Bidirectional',
        bilingual: 'Bilingual',
        bindings: 'Bindings',
        blockchain: 'Blockchain',
        board: 'Board',
        brand: 'Brand',
        brds: 'BRDs',
        'break-glass': 'Break-Glass',
        builder: 'Builder',
        bulk: 'Bulk',
        'bulk-sync': 'Bulk Sync',
        bundles: 'Bundles',
        calendar: 'Calendar',
        campaigns: 'Campaigns',
        cases: 'Cases',
        categories: 'Categories',
        category: 'Category',
        certification: 'Certification',
        chain: 'Chain',
        change: 'Change',
        'change-awareness': 'Change Awareness',
        'change-log': 'Change Log',
        'change-management': 'Change Management',
        'change-requests': 'Change Requests',
        'cmp-validation': 'CMP Validation',
        collection: 'Collection',
        comparison: 'Comparison',
        completeness: 'Completeness',
        completion: 'Completion',
        'compliance-posture': 'Compliance Posture',
        'compliance-proof': 'Compliance Proof',
        'compliance-reviews': 'Compliance Reviews',
        config: 'Config',
        confirmation: 'Confirmation',
        conflicts: 'Conflicts',
        consolidation: 'Consolidation',
        continuous: 'Continuous',
        contracts: 'Contracts',
        controls: 'Controls',
        cookies: 'Cookies',
        cooperation: 'Cooperation',
        coordination: 'Coordination',
        'corporate-governance': 'Corporate Governance',
        'corrective-actions': 'Corrective Actions',
        coverage: 'Coverage',
        'credential-vault': 'Credential Vault',
        credentials: 'Credentials',
        cycles: 'Cycles',
        dashboards: 'Dashboards',
        'data-assets': 'Data Assets',
        'data-flows': 'Data Flows',
        'data-sensitivity': 'Data Sensitivity',
        'data-steward': 'Data Steward',
        deadlines: 'Deadlines',
        'decree-356': 'Decree 356',
        dedup: 'Dedup',
        definitions: 'Definitions',
        deliverable: 'Deliverable',
        departments: 'Departments',
        'dependency-order': 'Dependency Order',
        deployment: 'Deployment',
        detection: 'Detection',
        diff: 'Diff',
        'digital-properties': 'Digital Properties',
        dimensions: 'Dimensions',
        disable: 'Disable',
        'disaster-recovery': 'Disaster Recovery',
        disclosure: 'Disclosure',
        documentation: 'Documentation',
        documents: 'Documents',
        domains: 'Domains',
        dossier: 'Dossier',
        dpa: 'DPA',
        drift: 'Drift',
        'drift-detection': 'Drift Detection',
        'due-diligence': 'Due Diligence',
        email: 'Email',
        emergency: 'Emergency',
        enable: 'Enable',
        'enterprise-map': 'Enterprise Map',
        erasure: 'Erasure',
        evaluation: 'Evaluation',
        'event-bus': 'Event Bus',
        exceptions: 'Exceptions',
        execution: 'Execution',
        executive: 'Executive',
        exercises: 'Exercises',
        export: 'Export',
        exports: 'Exports',
        filing: 'Filing',
        filter: 'Filter',
        filtering: 'Filtering',
        findings: 'Findings',
        flows: 'Flows',
        'follow-up': 'Follow-Up',
        forensic: 'Forensic',
        forensics: 'Forensics',
        'form-filler': 'Form Filler',
        framework: 'Framework',
        frameworks: 'Frameworks',
        'gap-analysis': 'Gap Analysis',
        'gap-report': 'Gap Report',
        gaps: 'Gaps',
        gates: 'Gates',
        'getting-started': 'Getting Started',
        'golden-templates': 'Golden Templates',
        grc: 'GRC',
        'group-management': 'Group Management',
        groups: 'Groups',
        'guided-tours': 'Guided Tours',
        guides: 'Guides',
        handling: 'Handling',
        'hands-on': 'Hands-On',
        health: 'Health',
        help: 'Help',
        hierarchy: 'Hierarchy',
        history: 'History',
        i18n: 'i18n',
        iam: 'IAM',
        'identity-provider': 'Identity Provider',
        implementation: 'Implementation',
        import: 'Import',
        improvement: 'Improvement',
        'incident-response': 'Incident Response',
        inconsistencies: 'Inconsistencies',
        instances: 'Instances',
        intake: 'Intake',
        'intake-key': 'Intake Key',
        interactive: 'Interactive',
        international: 'International',
        inventory: 'Inventory',
        investigation: 'Investigation',
        investigations: 'Investigations',
        'ip-allowlist': 'IP Allowlist',
        issues: 'Issues',
        justification: 'Justification',
        justifications: 'Justifications',
        kanban: 'Kanban',
        keyboard: 'Keyboard',
        keys: 'Keys',
        'knowledge-base': 'Knowledge Base',
        koncernstyring: 'Corporate Governance',
        kpi: 'KPI',
        labeling: 'Labeling',
        language: 'Language',
        'language-packs': 'Language Packs',
        languages: 'Languages',
        lawfulness: 'Lawfulness',
        layout: 'Layout',
        'legal-basis': 'Legal Basis',
        'lessons-learned': 'Lessons Learned',
        levels: 'Levels',
        library: 'Library',
        lifecycle: 'Lifecycle',
        lineage: 'Lineage',
        links: 'Links',
        listings: 'Listings',
        live: 'Live',
        locale: 'Locale',
        log: 'Log',
        logging: 'Logging',
        'managed-service': 'Managed Service',
        mapping: 'Mapping',
        mappings: 'Mappings',
        'market-packs': 'Market Packs',
        marketplace: 'Marketplace',
        maturity: 'Maturity',
        metrics: 'Metrics',
        models: 'Models',
        module: 'Module',
        modules: 'Modules',
        mps: 'MPS',
        'multi-factor': 'Multi-Factor',
        'multi-market': 'Multi-Market',
        'multi-org': 'Multi-Org',
        'multi-property': 'Multi-Property',
        'my-tasks': 'My Tasks',
        obligations: 'Obligations',
        offboarding: 'Offboarding',
        oidc: 'OIDC',
        orchestration: 'Orchestration',
        'org-groups': 'Org Groups',
        organization: 'Organization',
        overdue: 'Overdue',
        overview: 'Overview',
        'owner-review': 'Owner Review',
        package: 'Package',
        packages: 'Packages',
        packs: 'Packs',
        pagination: 'Pagination',
        'parent-child': 'Parent-Child',
        patterns: 'Patterns',
        performance: 'Performance',
        periodic: 'Periodic',
        periods: 'Periods',
        personal: 'Personal',
        'personal-data': 'Personal Data',
        personas: 'Personas',
        planning: 'Planning',
        platform: 'Platform',
        playbooks: 'Playbooks',
        poc: 'PoC',
        'point-in-time': 'Point-in-Time',
        policies: 'Policies',
        portfolio: 'Portfolio',
        'post-incident': 'Post-Incident',
        'power-user': 'Power User',
        practice: 'Practice',
        pricing: 'Pricing',
        'privacy-impact': 'Privacy Impact',
        procedures: 'Procedures',
        processes: 'Processes',
        processing: 'Processing',
        'processing-activities': 'Processing Activities',
        procurement: 'Procurement',
        productivity: 'Productivity',
        profiles: 'Profiles',
        programs: 'Programs',
        progress: 'Progress',
        proof: 'Proof',
        propagation: 'Propagation',
        properties: 'Properties',
        'pub-sub': 'Pub-Sub',
        'public-form': 'Public Form',
        quality: 'Quality',
        questionnaires: 'Questionnaires',
        'quick-links': 'Quick Links',
        'quick-start': 'Quick Start',
        'quick-starts': 'Quick Starts',
        raci: 'RACI',
        readiness: 'Readiness',
        'real-time': 'Real-Time',
        reassessments: 'Reassessments',
        reconciliation: 'Reconciliation',
        record: 'Record',
        records: 'Records',
        recurring: 'Recurring',
        register: 'Register',
        registry: 'Registry',
        regulations: 'Regulations',
        regulator: 'Regulator',
        regulatory: 'Regulatory',
        'regulatory-frameworks': 'Regulatory Frameworks',
        remap: 'Remap',
        reminders: 'Reminders',
        reports: 'Reports',
        requests: 'Requests',
        requirements: 'Requirements',
        response: 'Response',
        'response-plans': 'Response Plans',
        'response-team': 'Response Team',
        'response-templates': 'Response Templates',
        'response-time': 'Response Time',
        responses: 'Responses',
        responsibility: 'Responsibility',
        results: 'Results',
        review: 'Review',
        reviews: 'Reviews',
        'right-to-delete': 'Right to Delete',
        rights: 'Rights',
        'risk-tiers': 'Risk Tiers',
        rollouts: 'Rollouts',
        rollup: 'Rollup',
        ropa: 'ROPA',
        routing: 'Routing',
        rpo: 'RPO',
        rto: 'RTO',
        rules: 'Rules',
        runs: 'Runs',
        saml: 'SAML',
        scan: 'Scan',
        scanning: 'Scanning',
        scc: 'SCC',
        schedule: 'Schedule',
        scheduled: 'Scheduled',
        schedules: 'Schedules',
        scheduling: 'Scheduling',
        scores: 'Scores',
        scoring: 'Scoring',
        scripts: 'Scripts',
        'security-reviews': 'Security Reviews',
        'sensitive-data': 'Sensitive Data',
        sensitivity: 'Sensitivity',
        'separation-of-duties': 'Separation of Duties',
        service: 'Service',
        sessions: 'Sessions',
        'setup-wizard': 'Setup Wizard',
        severity: 'Severity',
        'shared-governance': 'Shared Governance',
        'shared-settings': 'Shared Settings',
        shortcuts: 'Shortcuts',
        sidebar: 'Sidebar',
        signals: 'Signals',
        'single-sign-on': 'Single Sign-On',
        snapshots: 'Snapshots',
        sod: 'SoD',
        sorting: 'Sorting',
        sources: 'Sources',
        staging: 'Staging',
        standards: 'Standards',
        status: 'Status',
        structure: 'Structure',
        'sub-processor': 'Sub-Processor',
        'sub-processors': 'Sub-Processors',
        submissions: 'Submissions',
        subprocessors: 'Subprocessors',
        subscription: 'Subscription',
        suggestions: 'Suggestions',
        summaries: 'Summaries',
        supplements: 'Supplements',
        support: 'Support',
        sync: 'Sync',
        system: 'System',
        systems: 'Systems',
        tables: 'Tables',
        tagging: 'Tagging',
        'tamper-proof': 'Tamper-Proof',
        taxonomies: 'Taxonomies',
        taxonomy: 'Taxonomy',
        tenants: 'Tenants',
        termination: 'Termination',
        'third-party': 'Third-Party',
        'ticket-sync': 'Ticket Sync',
        tickets: 'Tickets',
        tiers: 'Tiers',
        tokens: 'Tokens',
        totp: 'TOTP',
        traceability: 'Traceability',
        tracking: 'Tracking',
        trail: 'Trail',
        training: 'Training',
        translation: 'Translation',
        translations: 'Translations',
        transparency: 'Transparency',
        trends: 'Trends',
        unified: 'Unified',
        updates: 'Updates',
        validation: 'Validation',
        vault: 'Vault',
        'vendor-requests': 'Vendor Requests',
        verification: 'Verification',
        versioning: 'Versioning',
        versions: 'Versions',
        vietnam: 'Vietnam',
        violations: 'Violations',
        visualization: 'Visualization',
        waivers: 'Waivers',
        'web-records': 'Web Records',
        website: 'Website',
        websites: 'Websites',
        weights: 'Weights',
        'white-label': 'White-Label',
        withdrawal: 'Withdrawal',
    },
    module_labels: {
        rights_requests: 'Rights Requests',
        data_mapping: 'Data Mapping',
        governance: 'Vendor Governance',
        consent: 'Consent Governance',
        consent_properties: 'Consent Properties',
        assessments: 'DPIA & Assessments',
        classification: 'Data Classification',
        monitoring: 'Monitoring Programs',
        program: 'Monitoring Program',
        incidents: 'Incident Response',
        connectors: 'Connectors',
        vendor_risk: 'Vendor Risk',
        deletion_orchestration: 'Deletion Orchestration',
        discovery: 'Data Discovery',
        incident_ops: 'Incident Operations',
        program_governance: 'Program Governance',
        access_accountability: 'Access Accountability',
        localization: 'Localization',
        partner_workspace: 'Partner Workspace',
        privacy_scanner: 'Privacy Scanner',
        onboarding: 'Onboarding',
        compliance_forms: 'Compliance Forms',
        audit_trail: 'Audit Trail',
        data_subjects: 'Data Subjects',
        organizations: 'Organizations',
        task_management: 'Task Management',
        // Legacy aliases for sidebar/other references
        deletion_orch: 'Deletion Orchestration',
        forms: 'Compliance Forms',
        partner_marketplace: 'Partner Marketplace',
        dashboard: 'Dashboard',
        platform_settings: 'Platform Settings',
        help_center: 'Help Center',
    },
    // ---------- Module descriptions (Sprint 15.6.0) ----------
    module_descriptions: {
        rights_requests: 'Manage data subject rights requests including access, deletion, and portability. Track request lifecycle, assign handlers, and meet regulatory deadlines.',
        data_mapping: 'Map data flows across your organization. Track processing activities, data categories, purposes, retention policies, and system integrations.',
        governance: 'Manage vendor compliance and governance. Track vendor assessments, contracts, SLA performance, and regulatory obligations.',
        consent: 'Manage consent collection, storage, and withdrawal. Track consent records, purposes, and integration with consent management platforms.',
        consent_properties: 'Configure consent properties, groups, and golden templates. Define reusable consent configurations for consistent deployment.',
        assessments: 'Conduct Data Protection Impact Assessments (DPIAs) and privacy risk assessments. Evaluate processing activities for compliance risks.',
        classification: 'Classify data assets by sensitivity level. Apply taxonomy labels, track lifecycle records, and identify classification gaps.',
        monitoring: 'Set up monitoring programs to track compliance metrics, key risk indicators, and control effectiveness over time.',
        incidents: 'Report and manage privacy incidents and data breaches. Track investigation, notification, and remediation workflows.',
        connectors: 'Configure data source connectors and integrations. Connect external systems for automated data discovery and monitoring.',
        vendor_risk: 'Assess and monitor third-party vendor risks. Score vendors, track risk mitigation, and manage vendor risk configurations.',
        deletion_orchestration: 'Orchestrate data deletion across systems. Track deletion requests, verify proof chains, and ensure complete data removal.',
        discovery: 'Discover personal data across connected systems. Scan data sources to build a comprehensive data inventory.',
        incident_ops: 'Operational incident management with playbooks, escalation workflows, and SLA tracking for incident resolution.',
        program_governance: 'Track overall privacy program maturity, compliance scores, evidence gaps, and executive reporting across your organization.',
        access_accountability: 'Investigate and audit user access patterns. Track access configurations, monitor suspicious activity, and enforce accountability.',
        localization: 'Install and manage market localization packs. Add regulatory frameworks, language packs, and region-specific compliance content.',
        partner_workspace: 'Collaborate with external partners. Share compliance data, track partner obligations, and manage partner relationships.',
        privacy_scanner: 'Scan websites for privacy compliance issues. Monitor cookie usage, tracking scripts, and privacy policy presence in real-time.',
        onboarding: 'Guide new organizations through platform setup. Step-by-step onboarding wizard for initial configuration and data import.',
        compliance_forms: 'Create and manage compliance forms from a catalog of templates. Fill out regulatory forms, track submissions, and generate reports.',
        audit_trail: 'View a complete audit trail of all system actions. Track who did what, when, and where across all modules.',
        organizations: 'Manage organizations and multi-tenant settings. Configure organization profiles, hierarchies, and cross-org relationships.',
    },
    // ---------- Privacy Scanner (Sprint 11.3.0, expanded Sprint 29.1.0) ----------
    privacy_scanner: {
        // Sprint 48.1.7 — Forseti anomaly explanation panel
        anomaly_explain: {
            explain_label: 'Explain anomalies',
            explaining: 'Explaining…',
            results_heading: 'Anomaly explanations ({severity})',
            tokens_label: '≈ {count} tokens',
            items_label: '{count} items',
            remediation_label: 'Remediation steps',
            items_detail_label: 'Affected items',
            accept_label: 'Accept explanation',
            reject_label: 'Reject explanation',
            error_generic: 'Could not explain the anomalies. Try again or check the scan completed successfully.',
        },
        // Sprint 48.1.8 — Per-cookie / per-tracker AI insight
        cookie_insight: {
            insight_label: 'Cookie & tracker AI insight',
            producing: 'Producing insight…',
            results_heading: 'Cookie & tracker insight ({severity})',
            tokens_label: '≈ {count} tokens',
            items_label: '{count} items',
            classification_label: 'Classification',
            severity_label: 'Severity',
            banner_copy_label: 'Suggested banner copy',
            retention_label: 'Retention advice',
            citations_label: 'Citations',
            kind_cookie: 'Cookie',
            kind_tracker: 'Tracker',
            class_necessary: 'Necessary',
            class_analytics: 'Analytics',
            class_marketing: 'Marketing',
            class_social: 'Social',
            accept_label: 'Accept insight',
            reject_label: 'Reject insight',
            no_items: 'No cookies or trackers were detected in this scan.',
            error_generic: 'Could not produce the cookie insight. Try again or check the scan completed successfully.',
        },
        monitoring: {
            title: 'Privacy Monitoring',
            new_scan: 'New Scan',
            scan_url: 'URL to Scan',
            scan_frequency: 'Scan Frequency',
            last_scan: 'Last Scan',
            score: 'Privacy Score',
            issues: 'Issues Found',
            history: 'Scan History',
            settings: 'Monitoring Settings',
        },
        real_time: {
            title: 'Real-Time Scanner',
            scan_now: 'Scan Now',
            scanning: 'Scanning...',
            results: 'Scan Results',
            no_results: 'No scan results yet.',
            cookies: 'Cookies',
            trackers: 'Trackers',
            consent_banners: 'Consent Banners',
            third_party: 'Third-Party Scripts',
            archive: 'Results Archive',
            settings: 'Scanner Settings',
        },
        // Sprint 29.1.0 — full page-level keys for all 6 privacy scanner pages
        dashboard: {
            page_title: 'Privacy Monitoring Dashboard',
            privacy_monitoring: 'Privacy Monitoring',
            plan_label: '{package} plan',
            expires_label: 'expires {date}',
            settings: 'Settings',
            refresh: 'Refresh',
            refreshing_dashboard: 'Refreshing monitoring dashboard',
            refresh_aria: 'Refresh monitoring dashboard',
            not_configured_title: 'Privacy Monitoring Not Configured',
            not_configured_desc: 'Connect your AesirX Privacy Monitoring service to start tracking website compliance.',
            configure_monitoring: 'Configure Monitoring',
            license_expired_title: 'License Expired',
            license_expired_desc: 'Your AesirX Privacy Monitoring license has expired. Please renew it to avoid service interruption.',
            compliance_score: 'Compliance Score',
            cookies_detected: 'Cookies Detected',
            trackers_label: 'Trackers',
            open_issues: 'Open Issues',
            score_good: 'Good',
            score_needs_improvement: 'Needs improvement',
            score_critical: 'Critical',
            action_required: 'Action required',
            cookies_sr: 'cookies detected',
            trackers_sr: 'trackers found',
            open_issues_sr: 'open issues',
            action_required_sr: 'action required',
            metric_label: 'Metric',
            value_label: 'Value',
            status_label: 'Status',
            aria_metrics: 'Privacy monitoring metrics',
            plan_limit_warning: 'Scanning URL: {current}/{max} — you have reached your plan limit.',
            manage_domains: 'Manage domains or upgrade your plan.',
            monitored_domains: 'Monitored Domains',
            domain_added: 'Domain {domain} added',
            domain_removed: 'Domain {domain} removed',
            failed_add_domain: 'Failed to add domain',
            failed_remove_domain: 'Failed to remove domain',
            failed_load: 'Failed to load monitoring dashboard',
            new_domain_aria: 'New domain URL',
            add_domain: 'Add Domain',
            adding_domain: 'Adding\u2026',
            no_domains: 'No monitored domains configured yet.',
            aria_domains_table: 'Monitored domains',
            col_domain: 'Domain',
            col_compliance_score: 'Compliance Score',
            col_status: 'Status',
            col_cookies: 'Cookies',
            col_trackers: 'Trackers',
            col_issues: 'Issues',
            col_last_scan: 'Last Scan',
            col_action: 'Action',
            compliance_status_sr: 'compliance status',
            showing_chart: 'Showing chart view',
            showing_table: 'Showing data table view',
            loading_sr: 'Loading monitoring dashboard',
            // AC1 — framework tagging, continuous monitoring timestamps, audit trail
            col_framework_tags: 'Framework Tags',
            col_last_monitored: 'Last Monitored',
            framework_tags_sr: 'applicable regulatory frameworks',
            last_monitored_sr: 'continuous monitoring timestamp',
            audit_trail_heading: 'Audit Trail',
            audit_trail_info: 'Every monitoring scan is timestamped and logged. Domain-level compliance changes produce an immutable audit record that can be exported for regulatory review.',
            cybersecurity_law_info_heading: 'Cybersecurity Law Detection',
            cybersecurity_law_info: 'Monitored domains are automatically checked against cybersecurity law requirements (e.g., Vietnam Cybersecurity Law, NIS2 Directive). Framework tags indicate which regulations apply. Issues are categorized by regulatory framework for targeted remediation.',
        },
        scan: {
            page_title: 'Real-Time Scan',
            title: 'Real-Time Privacy Scanner',
            settings: 'Settings',
            results_archive: 'Results Archive',
            launch_scan: 'Launch Scan',
            target_url: 'Target URL',
            protocol_aria: 'Protocol',
            url_required: 'URL is required',
            url_whitespace: 'Please remove whitespace from the URL',
            url_invalid: 'Please enter a valid domain (e.g. example.com)',
            add_subpages: 'Add Subpages',
            add_subpages_aria: 'Add subpages, {count} added',
            start_scan: 'Start Scan',
            launching: 'Launching\u2026',
            scan_launched: 'Scan launched successfully',
            scan_failed: 'Failed to launch scan',
            scan_progress: 'Scan Progress',
            scanning_in_progress: 'Scanning in progress\u2026',
            scan_id: 'Scan ID',
            status_label: 'Status',
            status_unknown: 'unknown',
            progress_label: 'Progress',
            pages_label: 'Pages',
            refresh_status: 'Refresh Status',
            new_scan: 'New Scan',
            scan_completed: 'Scan completed, results available',
            failed_check_status: 'Failed to check scan status',
            scan_results: 'Scan Results',
            main_page: 'Main Page',
            page_n: 'Page {n}',
            result_pages_nav: 'Result pages',
            page_url: 'Page URL',
            risk_score: 'Risk Score',
            risk_score_sr: 'risk score',
            cookies_label: 'Cookies',
            cookies_sr: 'cookies detected',
            scripts_label: 'Scripts',
            scripts_sr: 'scripts detected',
            issues_label: 'Issues',
            issues_sr: 'issues found',
            issues_found: 'Issues Found',
            aria_issues_table: 'Issues found during scan',
            col_severity: 'Severity',
            col_category: 'Category',
            col_title: 'Title',
            col_remediation: 'Remediation',
            severity_sr: 'severity',
            scan_result_metrics: 'Scan result metrics',
            view_full_detail: 'View Full Detail',
            // AC3 — compliance case linkage, consent categorization, rule config, audit trail
            col_consent_category: 'Consent Category',
            consent_category_sr: 'consent categorization',
            compliance_evidence_heading: 'Compliance Evidence',
            compliance_evidence_info: 'Scan findings are linked to compliance cases automatically. Each issue can be traced to a consent category (essential, functional, analytics, marketing) and produces audit-ready evidence for regulatory review.',
            rule_config_heading: 'Rule Configuration',
            rule_config_info: 'Scan rules are configurable per regulatory framework. Cookie checks, script detection, and vendor analysis can be enabled or disabled via Settings. Custom rules can be defined to match your organization\u2019s compliance requirements.',
            case_linkage_heading: 'Compliance Case Linkage',
            case_linkage_info: 'Issues found during real-time scans can be linked to compliance cases for tracking and remediation. Navigate to the Scan Result Detail page to view case associations.',
            subpages_modal_title: 'Add Subpages',
            subpages_modal_desc: 'Add up to 5 subpage URLs to scan alongside the main domain.',
            subpage_n: 'Subpage {n}',
        },
        result_detail: {
            page_title: 'Scan Result Detail',
            title: 'Scan Result',
            back_to_archive: 'Back to Archive',
            failed_load: 'Failed to load scan result',
            loading_sr: 'Loading scan result\u2026',
            scan_id: 'Scan ID',
            target_url: 'Target URL',
            status_label: 'Status',
            status_sr: 'status',
            started: 'Started',
            completed: 'Completed',
            pages_scanned: 'Pages Scanned',
            https_label: 'HTTPS',
            highest_risk_score: 'Highest Risk Score',
            most_cookies: 'Most Cookies',
            most_vendors: 'Most 3rd-party Vendors',
            most_issues: 'Most Issues',
            across_pages: 'across pages',
            highest_values_aria: 'Highest values across all pages',
            scanned_pages_nav: 'Scanned pages',
            main_page: 'Main Page',
            page_n: 'Page {n}',
            page_url: 'Page URL',
            risk_score: 'Risk Score',
            overall_risk_sr: 'overall risk score',
            cookies_label: 'Cookies',
            cookies_sr: 'cookies detected',
            vendors_label: '3rd-party Vendors',
            vendors_sr: 'third-party vendors detected',
            issues_label: 'Issues',
            issues_sr: 'issues found',
            risk_breakdown_aria: 'Risk score breakdown',
            issues_found: 'Issues Found',
            aria_issues_table: 'Issues found during scan',
            col_severity: 'Severity',
            col_category: 'Category',
            col_title: 'Title',
            col_remediation: 'Remediation',
            severity_sr: 'severity',
            cookies_heading: 'Cookies ({count})',
            aria_cookies_table: 'Cookies detected',
            col_name: 'Name',
            col_domain: 'Domain',
            col_path: 'Path',
            col_secure: 'Secure',
            col_httponly: 'HttpOnly',
            col_samesite: 'SameSite',
            vendors_heading: 'Third-party Vendors ({count})',
            aria_vendors_table: 'Third-party vendors detected',
            col_hostname: 'Hostname',
            scripts_heading: 'Scripts / Beacons ({count})',
            aria_scripts_table: 'Scripts and beacons detected',
            col_url: 'URL',
            col_type: 'Type',
            links_heading: 'Links',
            first_party: 'First-party',
            third_party: 'Third-party',
            // AC2 — risk category mapping, actionable detail
            col_risk_category: 'Risk Category',
            col_regulatory_framework: 'Regulatory Framework',
            col_actionable_detail: 'Actionable Detail',
            risk_category_sr: 'regulatory risk category',
            risk_category_mapping_heading: 'Risk Category Mapping',
            risk_category_mapping_info: 'Each scan finding is mapped to a regulatory risk category (data collection, cross-border transfer, consent management, data retention, security controls). This mapping enables targeted remediation prioritized by regulatory impact.',
            compliance_case_heading: 'Compliance Case Association',
            compliance_case_info: 'Scan results are linked to compliance cases for end-to-end tracking. Issues can be escalated directly from scan results into the compliance case workflow.',
        },
        results_archive: {
            page_title: 'Scan Results Archive',
            title: 'Scan Results Archive',
            back_to_scanner: 'Back to Scanner',
            failed_load: 'Failed to load scan results',
            results_loaded: 'Scan results loaded, {count} records',
            total_label: 'Total: {count}',
            aria_table: 'Scan results archive',
            col_scan_date: 'Scan Date',
            col_target_url: 'Target URL',
            col_duration: 'Duration',
            col_issues_found: 'Issues Found',
            col_risk_score: 'Risk Score',
            col_status: 'Status',
            col_actions: 'Actions',
            scan_status_sr: 'scan status',
            view_result: 'View',
            no_results: 'No scan results found.',
            loading_sr: 'Loading scan results',
            pagination_aria: 'Scan results pagination',
            // AC2 — archive retention, export with context
            col_risk_category: 'Risk Category',
            risk_category_sr: 'regulatory risk category',
            archive_retention_heading: 'Archive Retention',
            archive_retention_info: 'Scan results are retained for the duration specified in your organization\u2019s data retention policy. Archived results include full scan context (framework tags, risk categories, remediation guidance) and can be exported as CSV for regulatory evidence.',
            export_context_info: 'Exported CSV files include regulatory context: risk category, applicable frameworks, and actionable remediation details for each finding.',
        },
        scanner_settings: {
            page_title: 'Scanner Settings',
            title: 'Scanner Settings',
            back_to_scanner: 'Back to Scanner',
            failed_load: 'Failed to load scanner configuration',
            endpoint_url: 'Endpoint URL',
            save_success: 'Scanner configuration saved successfully.',
            save_failed: 'Failed to save scanner configuration',
            test_connection: 'Test Connection',
            testing: 'Testing...',
            save_configuration: 'Save Configuration',
            saving: 'Saving...',
            loading_sr: 'Loading scanner settings',
        },
        monitoring_settings: {
            page_title: 'Privacy Monitoring Settings',
            title: 'Monitoring Settings',
            back_to_dashboard: 'Back to Dashboard',
            failed_load: 'Failed to load monitoring configuration',
            credentials_heading: 'AesirX Credentials',
            connected_as: 'Connected as {username}',
            credentials_desc: 'Enter your AesirX account credentials to connect Privacy Monitoring. Find your Member ID in your AesirX account profile page.',
            client_id: 'Client ID',
            client_secret: 'Client Secret',
            username: 'AesirX Username (email)',
            password: 'AesirX Password',
            placeholder_keep: '(leave blank to keep existing)',
            credential_form_aria: 'AesirX credential form',
            test_connection: 'Test Connection',
            testing: 'Testing\u2026',
            test_aria: 'Test AesirX connection',
            testing_aria: 'Testing connection',
            save_configuration: 'Save Configuration',
            saving: 'Saving\u2026',
            save_aria: 'Save credentials',
            saving_aria: 'Saving credentials',
            credentials_saved: 'AesirX credentials saved successfully.',
            credentials_saving: 'AesirX credentials saved. Reloading configuration\u2026',
            credentials_save_failed: 'Failed to save credentials',
            client_id_required: 'Client ID and username are required.',
            all_fields_required: 'All credential fields are required.',
            email_heading: 'Email Notifications',
            receive_emails: 'Receive scan report emails',
            email_toggle_aria: 'Toggle scan report emails',
            only_on_change: 'Only email when risk status changes',
            only_on_change_aria: 'Toggle email only on risk status change',
            emails_enabled: 'Scan report emails enabled',
            emails_disabled: 'Scan report emails disabled',
            only_change_on: 'Only emailing on risk change',
            only_change_off: 'Emailing on every scan',
            email_failed: 'Failed to update email setting',
            email_settings_aria: 'Email notification settings',
            loading_sr: 'Loading monitoring settings',
        },
        entity_types: {
            user: 'User',
            case: 'Case',
            incident: 'Incident',
            assessment: 'Assessment',
            vendor: 'Vendor',
            consent: 'Consent',
            policy: 'Policy',
            data_map: 'Data Map',
        },
        actions: {
            create: 'Create',
            update: 'Update',
            delete: 'Delete',
            view: 'View',
            export: 'Export',
            status_change: 'Status Change',
        },
    },
    // ---------- Compliance Forms (Sprint 11.3.0) ----------
    forms: {
        dashboard: {
            title: 'Forms Dashboard',
            total_templates: 'Total Templates',
            installed: 'Installed',
            available: 'Available',
            by_market: 'By Market',
            back_dashboard: 'Back to Dashboard',
            browse_templates: 'Browse Templates',
            catalog_coverage: 'Catalog Coverage',
            quick_start: 'Quick Start',
            recent_instances: 'Recent Instances',
            total_instances: 'Total Instances',
            fetch_failed: 'Failed to fetch dashboard data.',
            no_instances: 'No instances found.',
            form_metrics: 'Form metrics overview',
            templates_total: 'templates total',
            instances_total: 'instances total',
            installed_of_available: '{installed} of {available} installed',
            templates_in_category: 'templates in {category}',
        },
        templates: {
            title: 'Form Templates',
            new: 'New Template',
            edit: 'Edit Template',
            name: 'Template Name',
            form_type: 'Form Type',
            market: 'Market',
            language: 'Language',
            schema: 'Form Schema',
            preview: 'Preview',
            duplicate: 'Duplicate Template',
            grid_view: 'Grid View',
            list_view: 'List View',
            fetch_failed: 'Failed to fetch templates.',
            filter_templates: 'Filter templates',
            version_history: 'Version History',
            no_version_history: 'No version history — this is the initial version.',
            form_id: 'Form ID',
            source_instrument: 'Source Instrument',
            issuer: 'Issuer',
            all_frameworks: 'All Frameworks',
            view_mode: 'View mode',
        },
        catalog: {
            title: 'Form Catalog',
            install: 'Install',
            uninstall: 'Uninstall',
            install_all: 'Install All',
            installed_tab: 'Installed',
            catalog_tab: 'Catalog',
            search_placeholder: 'Search templates...',
            no_templates: 'No templates found.',
            template_count: '{count} templates',
            all_markets: 'All Markets',
            sections: 'Sections',
            all_categories: 'All Categories',
            category: 'Category',
            category_install_failed: 'Failed to install category templates.',
            category_install_success: 'Category templates installed.',
            fetch_failed: 'Failed to fetch templates.',
            fetch_installed_failed: 'Failed to fetch installed templates.',
            fields: 'Fields',
            install_failed: 'Failed to install template.',
            install_success: 'Template installed successfully.',
            installing: 'Installing...',
            removing: 'Removing...',
            uninstall_confirm: 'Are you sure you want to uninstall this template?',
            uninstall_failed: 'Failed to uninstall template.',
            uninstall_success: 'Template uninstalled successfully.',
            installation_summary: 'Installation summary',
            filter_catalog: 'Filter catalog',
            all_frameworks: 'All Frameworks',
            templates_installed_count: '{count} template(s) installed successfully',
            all_already_installed: 'All templates in this category are already installed',
            form_id: 'Form ID',
            issuer: 'Issuer',
            catalog_tabs: 'Form catalog tabs',
        },
        filler: {
            back_templates: 'Back to Templates',
            no_sections: 'No sections found.',
            draft_created: 'Draft created.',
            draft_saved: 'Draft saved.',
            fetch_template_failed: 'Failed to fetch template.',
            save_draft: 'Save Draft',
            save_draft_failed: 'Failed to save draft.',
            saving: 'Saving...',
            select_option: 'Select an option',
            submit: 'Submit',
            submit_failed: 'Failed to submit.',
            submitting: 'Submitting...',
            title: 'Form Wizard',
            form_id_label: 'Form ID',
            schema_label: 'Schema',
            framework_label: 'Framework',
            issuer_label: 'Issuer',
            source_label: 'Source',
            market_label: 'Market',
            section_of: 'Section {current} of {total}',
            form_navigation: 'Form navigation',
            form_progress: 'Form progress: Section {current} of {total}',
            removed_item: 'Removed item {index} from {field}',
            added_item: 'Added new item to {field}',
            go_to_previous: 'Go to previous section, section {current} of {total}',
            go_to_next: 'Go to next section, section {current} of {total}',
            risk_matrix_placeholder: '1–5',
        },
        filing: {
            title: 'Filing Status',
            advance: 'Advance filing status...',
            not_started: 'Not Started',
            drafting: 'Drafting',
            internal_review: 'Internal Review',
            approved_for_submission: 'Approved for Submission',
            submitted_to_authority: 'Submitted to Authority',
            supplement_requested: 'Supplement Requested',
            supplement_in_progress: 'Supplement In Progress',
            resubmitted: 'Resubmitted',
            accepted: 'Accepted',
            rejected: 'Rejected',
            closed: 'Closed',
        },
        instances: {
            approved_by: 'Approved By',
            form_answers: 'Form Answers',
            no_answers: 'No answers found.',
            view_outputs: 'View Outputs',
            approve: 'Approve',
            approve_failed: 'Failed to approve.',
            approving: 'Approving...',
            detail_title: 'Instance Detail',
            fetch_failed: 'Failed to fetch instances.',
            generate_failed: 'Failed to generate output.',
            generate_output: 'Generate Output',
            generating: 'Generating...',
            submitted_at: 'Submitted At',
            instance_metadata: 'Form instance metadata',
            filing_status_steps: 'Filing status steps',
            current_status: 'current status',
            filing_status_update_failed: 'Failed to update filing status',
        },
        outputs: {
            back_instance: 'Back to Instance',
            no_outputs_msg: 'No outputs have been generated yet.',
            no_outputs_title: 'No Outputs',
            output_name: 'Output Name',
            download: 'Download',
            download_failed: 'Failed to download.',
            fetch_failed: 'Failed to fetch outputs.',
            format: 'Format',
            generated_at: 'Generated At',
            generated_by: 'Generated By',
            size: 'Size',
            title: 'Form Outputs',
            print: 'Print',
            print_outputs: 'Print form outputs',
            printing: 'Printing form outputs',
            pdf_preview: 'PDF Preview',
            downloading: 'Downloading {name}',
            preview_in_new_window: 'Preview {name} ({format}) in new window',
        },
        // Cybersecurity form domain headings — Sprint 14.9.0
        cybersec_domains: {
            scope: 'Scope & Applicability',
            risk: 'Risk Assessment & Control Mapping',
            policy: 'Organizational Security Policies',
            localization: 'Data Localization & Local Presence',
            vendor: 'Third-Party & Supply Chain',
            authority: 'Authority Request Handling',
            incident: 'Incident Response & Evidence',
            training: 'Training & Readiness',
        },
        // Market finalization form domain headings — Sprint 15.0.0
        market_finalization_domains: {
            data_law: 'Data Law — Governance & Operational Controls',
            pdpl_2025: 'PDPL 2025 — Privacy Governance & Data Subject Rights',
            decree_356: 'Decree 356/2025 — Vendor & Cross-Border Controls',
            telecom: 'Telecom Law — Confidentiality & Authority Requests',
        },
        nav: {
            filing_case_links: 'Filing Case Links',
            section_routing: 'Section Routing',
            submission_packages: 'Submission Packages',
        },
        filing_case_links: {
            title: 'Filing Case Links',
            link_form: 'Link Form to Filing Case',
            filing_case_type: 'Filing Case Type',
            form_instance: 'Form Instance',
            select_form_instance: 'Select a form instance...',
            select_filing_case: 'Select a filing case...',
            no_links_found: 'No filing case links found.',
            linked: 'Linked',
            filing_case_id: 'Filing Case ID',
            link_created: 'Filing case link created',
            link_deleted: 'Filing case link deleted',
            aria_delete_link: 'Delete link {name}',
            case_type_dpia_dossier: 'DPIA Dossier',
            case_type_cross_border_filing: 'Cross-Border Filing',
            case_type_breach_notification: 'Breach Notification',
            no_instances_guidance: 'No form instances yet. Create one from Form Templates.',
            no_filings_guidance: 'No filing cases yet. Create one from Assessments.',
        },
        section_routing: {
            title: 'Section Routing',
            assign_section: 'Assign Section',
            department: 'Department',
            select_department: 'Select a department...',
            assignee: 'Assignee',
            select_assignee: 'Select an assignee...',
            section: 'Section',
            select_section: 'Select a section...',
            select_form_instance: 'Select a form instance...',
            no_assignments_found: 'No section assignments found.',
            select_instance_prompt: 'Select a form instance to view section assignments.',
            complete_section: 'Complete Section',
            status_pending: 'Pending',
            status_in_progress: 'In Progress',
            status_completed: 'Completed',
            status_reviewed: 'Reviewed',
            form_instance_id: 'Form Instance ID',
            assignment_created: 'Section assignment created',
            assignment_deleted: 'Section assignment deleted',
            status_updated: 'Section status updated',
            aria_delete_assignment: 'Delete assignment {name}',
            deadline: 'Deadline',
            deadline_help: 'Optional. Assignments past this date show as overdue.',
            overdue: 'Overdue',
            days_remaining: 'days remaining',
            approval_requested: 'Submitted for review',
            form_approved: 'Form approved',
            form_rejected: 'Form rejected',
            no_departments_guidance: 'No departments configured. Set up in Organization Settings.',
            no_sections_guidance: 'No sections defined in the template. Ensure the form template has sections.',
            select_instance_first: 'Select a form instance first to load available sections.',
        },
        submission_packages: {
            title: 'Submission Packages',
            assemble_package: 'Assemble Submission Package',
            dossier: 'Dossier',
            select_dossier: 'Select a dossier...',
            package_label: 'Package Label',
            cover_letter: 'Cover Letter',
            checklist: 'Checklist',
            evidence_index: 'Evidence Index',
            completeness: 'Completeness',
            incomplete_warning: 'Cannot assemble: form has incomplete sections.',
            no_packages_found: 'No submission packages found.',
            version: 'Version',
            package_assembled: 'Submission package assembled',
            package_deleted: 'Submission package deleted',
            aria_delete_package: 'Delete package {name}',
            status_draft: 'Draft',
            status_complete: 'Complete',
            status_submitted: 'Submitted',
            status_accepted: 'Accepted',
            status_rejected: 'Rejected',
            package_updated: 'Submission package updated',
            document_generated: 'Document generated',
            documents: 'Documents',
            generate: 'Generate',
            preview: 'Preview',
            download: 'Download',
            download_all: 'Download All',
            no_dossiers_guidance: 'No dossiers yet. Create one from Assessments.',
            package_plan_help: 'Include in package plan (document auto-generation available):',
            cover_letter_help: 'Official cover letter addressed to the receiving authority',
            checklist_help: 'Compliance checklist verifying all submission requirements are met',
            evidence_index_help: 'Index of supporting evidence documents referenced in the submission',
            completeness_skipped: 'Completeness check skipped — no form instance linked to this dossier.',
            completeness_unavailable: 'Completeness check unavailable — proceeding with package assembly.',
            duplicate_package_error: 'A submission package already exists for this dossier.',
            doc_cover_letter: 'Cover Letter',
            doc_checklist: 'Checklist',
            doc_evidence_index: 'Evidence Index',
            export_csv: 'Export CSV',
            export_success: 'Filing case links exported successfully',
            no_assignments_guidance: 'No section assignments yet. Assign sections to departments and team members to begin routing.',
        },
        // Sprint 26.3.3: Template Picker
        template_picker: {
            title: 'Select a Template',
            hint: 'Choose a regulatory form template or start with a blank form.',
            market_filter: 'Filter by market',
            framework_filter: 'Filter by framework',
            all_markets: 'All Markets',
            all_frameworks: 'All Frameworks',
            search_placeholder: 'Search templates...',
            blank_form: 'Blank / Generic Form',
            blank_form_desc: 'Create without a template — fill in fields manually.',
            no_templates: 'No templates found for this filter.',
            select: 'Select',
            link_error: 'Template linking failed — you can link a template later from the Edit view.',
            internal_badge: 'Internal template',
            legacy_badge: 'Legacy',
            draft_badge: 'Draft',
            upcoming_badge: 'Upcoming',
            hidden_badge: 'Hidden',
            show_hidden_toggle: 'Show hidden / legacy templates',
            show_hidden_toggle_hint: 'Admin view — include templates flagged internal_only, hidden, legacy, or draft.',
        },
        // FU2b (2026-04-21) — Cover-letter preview-and-edit modal
        cover_letter_modal: {
            title: 'Cover letter — preview & edit',
            iframe_title: 'Cover letter preview',
            stage_label: 'Stage',
            stage_initial: 'Initial submission',
            stage_supplement: 'Supplement response',
            stage_resubmission: 'Resubmission',
            subject_label: 'Subject',
            body_label: 'Body paragraph',
            notes_label: 'Additional notes',
            refresh_preview: 'Refresh preview',
            confirm: 'Confirm & save',
            regenerate: 'Regenerate',
            snapshot_saved: 'Cover letter saved',
        },
        // Sprint 25.6.0: Related Forms panel
        related_panel: {
            title: 'Related Regulatory Forms',
            open_form: 'Fill Form',
            no_forms: 'No related forms found.',
            hint_vn: 'VN-specific regulatory forms available — select a form to open the compliance form wizard.',
            internal_badge: 'Internal',
            official_badge: 'Official',
            loading: 'Loading related forms...',
            error: 'Failed to load related forms.',
            form_id: 'Form ID',
            form_name: 'Form Name',
            framework: 'Framework',
        },
        view_form: 'View Form',
        edit_form: 'Edit Form',
        save_draft: 'Save Draft',
        module_settings: 'Module Settings',
        validation_errors: 'Please fix validation errors',
        custom_name_help: 'Optional — defaults to template name if left blank',
        select_placeholder: 'Select...',
        previous_section: 'Previous',
        next_section: 'Next',
        // fix-sprint-20260411-B F7 — keys used by FormSectionRenderer,
        // FormAnswersModal, UnifiedCreationModal (replacing hardcoded
        // English strings).
        loading: 'Loading...',
        array_add: 'Add',
        array_remove: 'Remove',
        array_item_label: '{label} item {index}',
        array_add_item: 'Add item to {label}',
        array_remove_item: 'Remove item {index} from {label}',
        legacy_banner: 'This record was created before unified forms. Some data may differ between the entity and form instance.',
        field_required: '{label} is required',
        // fix-sprint-20260411-0417 Findings 1, 2, 3 — replace hardcoded
        // English in FormSectionRenderer + UnifiedCreationModal.
        section_announcement: 'Section {current} of {total}: {title}',
        sections_nav_label: 'Form sections',
        no_sections: 'No template sections available.',
        section_label_tpl: 'Section {index}: {title}',
        module_settings_auto_applied: 'Module-specific settings are applied automatically.',
        // fix-sprint-20260411-0417 Finding 17 — shared fallbacks for the
        // FormAnswersModal / UnifiedCreationModal extractErrorMessage path.
        operation_failed: 'Operation failed',
        failed_to_load_form: 'Failed to load form',
        failed_to_save_form: 'Failed to save form',
        failed_to_submit_form: 'Failed to submit form',
        failed_to_load_template: 'Failed to load template',
        failed_to_save_draft: 'Failed to save draft',
        failed_to_create: 'Failed to create',
    },
    // ---------- Pack Catalog (Sprint 11.3.0) ----------
    pack_catalog: {
        title: 'Pack Catalog',
        install: 'Install Pack',
        uninstall: 'Uninstall Pack',
        install_batch: 'Install Cluster',
        installed_tab: 'Installed',
        catalog_tab: 'Catalog',
        pack_details: 'Pack Details',
        frameworks: 'Frameworks',
        requirements: 'Requirements',
        artifacts: 'Artifacts',
        language_pack: 'Language Pack',
        dependencies: 'Dependencies',
        no_packs: 'No packs found.',
    },
    // ---------- Platform: Users, Roles, Profile details (Sprint 11.3.0) ----------
    platform_pages: {
        users: {
            title: 'Users',
            new: 'New User',
            invite: 'Invite User',
            edit: 'Edit User',
            name: 'Name',
            email: 'Email',
            role: 'Role',
            status: 'Status',
            identity_type: 'Identity Type',
            last_login: 'Last Login',
            deactivate: 'Deactivate',
            reactivate: 'Reactivate',
            unlock: 'Unlock',
            reset_password: 'Reset Password',
            remove: 'Remove',
            remove_confirm: 'Remove "{name}"? This action is permanent and the user will lose all access. The record is retained for audit purposes.',
        },
        roles: {
            title: 'Roles',
            new: 'New Role',
            edit: 'Edit Role',
            name: 'Role Name',
            description: 'Description',
            permissions: 'Permissions',
            users_count: 'Users',
            detail: 'Role Detail',
            all_tiers: 'All Tiers',
            role_name_placeholder: 'e.g. Regional Compliance Lead',
        },
        profile: {
            title: 'My Profile',
            name: 'Name',
            display_name: 'Display Name',
            email: 'Email',
            phone: 'Phone',
            department: 'Department',
            job_title: 'Job Title',
            language: 'Language',
            role: 'Role',
            persona: 'Detected Persona',
            last_login: 'Last Login',
            save: 'Save Changes',
            save_success: 'Profile updated successfully',
            module_tips_section: 'Module Tips',
            tips_toggle: 'Show regulatory tips in modules',
            dismissed_tips: 'Dismissed tips',
            reset_dismissed: 'Reset All Dismissed Tips',
            notification_prefs: 'Notification Preferences',
            notification_prefs_link: 'Manage notification preferences →',
            change_password: 'Change Password',
            current_password: 'Current Password',
            new_password: 'New Password',
            confirm_password: 'Confirm Password',
            accessibility: 'Accessibility',
            high_contrast: 'High Contrast Mode',
            high_contrast_desc: 'Increase contrast for better readability. Meets WCAG 2.1 AAA standards.',
            passwords_no_match: 'Passwords do not match',
            password_changed_logout: 'Password changed. You will be logged out.',
            failed_update: 'Failed to update profile',
            failed_change_password: 'Failed to change password',
            none: 'None',
            never: 'Never',
            password_requirements: 'Min 12 chars, uppercase, lowercase, digit, special character',
        },
        security: {
            mfa_title: 'MFA Settings',
            mfa_enable: 'Enable MFA',
            mfa_disable: 'Disable MFA',
            mfa_setup: 'Scan this QR code with your authenticator app',
            sso_title: 'SSO Configuration',
            sso_provider: 'Provider',
            sso_client_id: 'Client ID',
            sso_redirect: 'Redirect URI',
            api_keys_title: 'API Keys',
            api_keys_new: 'New API Key',
            api_keys_name: 'Key Name',
            api_keys_last_used: 'Last Used',
            api_keys_expires: 'Expires',
            api_keys_revoke: 'Revoke',
            service_accounts_title: 'Service Accounts',
            ip_allowlist_title: 'IP Allowlist',
            ip_add: 'Add IP Address',
            ip_address: 'IP Address',
            ip_description: 'Description',
            delegations_title: 'Delegations',
            delegations_new: 'New Delegation',
            delegations_from: 'From User',
            delegations_to: 'To User',
            delegations_expires: 'Expires',
            break_glass_title: 'Break-Glass Access',
            break_glass_request: 'Request Access',
            break_glass_reason: 'Reason for Access',
            break_glass_duration: 'Duration',
            session_settings_title: 'Session Settings',
            session_timeout: 'Session Timeout (minutes)',
            session_max_concurrent: 'Max Concurrent Sessions',
            active_sessions_title: 'Active Sessions',
            active_sessions_device: 'Device',
            active_sessions_ip: 'IP Address',
            active_sessions_started: 'Started',
            active_sessions_revoke: 'Revoke Session',
            active_sessions_confirm_terminate: 'Are you sure you want to terminate this session?',
            active_sessions_confirm_terminate_current: 'This is your current session. Terminating it will sign you out immediately. Continue?',
            active_sessions_confirm_terminate_all: 'Are you sure you want to terminate all other sessions? Only your current session will remain active.',
            active_sessions_terminated: 'Session terminated successfully.',
            active_sessions_all_terminated: 'All other sessions have been terminated.',
            active_sessions_terminate_all: 'Terminate All Other Sessions',
            active_sessions_last_activity: 'Last Activity',
            active_sessions_current: 'Current Session',
            active_sessions_revoke_selected: 'Revoke Selected',
            active_sessions_confirm_terminate_selected: 'Are you sure you want to terminate the selected sessions?',
            active_sessions_selected_terminated: 'Selected sessions terminated successfully.',
            ip_allowlist_desc: 'Manage allowed IP addresses for API access.',
            ip_no_entries: 'No IP addresses configured.',
            session_settings_desc: 'Configure session timeout and concurrent session limits.',
            session_settings_save_policy: 'Save Session Policy',
            session_settings_saved: 'Session settings updated successfully.',
            session_settings_idle_timeout: 'Idle Timeout (minutes)',
            session_settings_absolute_timeout: 'Absolute Timeout (minutes)',
            session_settings_help_idle: 'Session will expire after this period of inactivity.',
            session_settings_help_absolute: 'Session will expire after this total duration regardless of activity.',
            session_settings_help_max: 'Maximum number of simultaneous sessions per user.',
            sso_note: 'Changes to SSO configuration may require users to re-authenticate.',
            service_accounts_warning: 'This API key will only be shown once. Copy it now and store it securely.',
            service_accounts_new: 'New Service Account',
            service_accounts_name: 'Account Name',
            service_accounts_role: 'Role',
            service_accounts_created: 'Created',
            service_accounts_desc: 'Manage machine-to-machine service accounts for API integrations.',
            break_glass_warning: 'Break-glass access is audited and logged. Use only for genuine emergencies.',
            break_glass_min_chars: '{count} / {min} minimum characters',
            break_glass_permission_count: '{count} permission(s) selected',
            break_glass_submitted: 'Break-glass request submitted. Awaiting approval.',
            break_glass_approved: 'Break-glass request approved.',
            break_glass_denied: 'Break-glass request denied.',
            break_glass_confirm_activate: 'Approve this break-glass request? This will grant emergency access immediately.',
            break_glass_confirm_title: 'Confirm Activation',
            break_glass_pending: 'Pending Requests',
            break_glass_active_sessions: 'Active Break-Glass Sessions',
            break_glass_time_remaining: 'Time Remaining',
            break_glass_expired: 'Expired',
            break_glass_approved_denied_by: 'Approved/Denied By',
            delegations_desc: 'Manage temporary permission delegations between users.',
            delegations_my: 'My Delegations',
            delegations_to_me: 'Delegated to Me',
            delegations_scope_all: 'All Permissions',
            delegations_scope_specific: 'Specific Permissions',
            delegations_created: 'Delegation created successfully.',
            delegations_revoked: 'Delegation revoked.',
            delegations_confirm_revoke: 'Are you sure you want to revoke this delegation?',
            delegations_confirm_revoke_title: 'Confirm Revoke',
            delegations_permission_count: '{count} permission(s)',
            api_keys_status: 'Status',
            api_keys_prefix: 'Key Prefix',
            api_keys_permissions_count: 'Permissions',
            api_keys_ip_count: 'IP Restrictions',
            api_keys_created: 'Created',
            api_keys_confirm_revoke_title: 'Confirm Revoke',
            api_keys_confirm_revoke_message: 'Are you sure you want to revoke the API key "{name}"? This action cannot be undone.',
            api_keys_confirm_rotate_title: 'Confirm Rotation',
            api_keys_confirm_rotate_message: 'Are you sure you want to rotate the API key "{name}"? The current key will stop working immediately.',
            api_keys_created_success: 'API key created successfully.',
            api_keys_revoked_success: 'API key "{name}" has been revoked.',
            api_keys_rotated_success: 'API key "{name}" has been rotated. Copy the new key below.',
            api_keys_load_error: 'Failed to load API keys.',
            api_keys_create_error: 'Failed to create API key.',
            api_keys_revoke_error: 'Failed to revoke API key.',
            api_keys_rotate_error: 'Failed to rotate API key.',
            break_glass_duration_15: '15 minutes',
            break_glass_duration_30: '30 minutes',
            break_glass_duration_60: '60 minutes (1h)',
            break_glass_duration_120: '120 minutes (2h)',
            break_glass_duration_240: '240 minutes (4h)',
            sod_rules_title: 'Segregation of Duties Rules',
            sod_violations_report: 'Violations Report',
            sod_create_rule: 'Create Rule',
            sod_col_name: 'Name',
            sod_col_description: 'Description',
            sod_col_conflict_count: 'Conflict Count',
            sod_col_severity: 'Severity',
            sod_col_status: 'Status',
            sod_col_actions: 'Actions',
            sod_no_rules: 'No SoD rules found',
            sod_severity_warning: 'Warning',
            sod_severity_blocking: 'Blocking',
            sod_active: 'Active',
            sod_inactive: 'Inactive',
            sod_edit: 'Edit',
            sod_exceptions: 'Exceptions',
            sod_deactivate: 'Deactivate',
            sod_confirm_deactivate_title: 'Confirm Deactivation',
            sod_confirm_deactivate_message: 'Deactivate rule "{name}"? It will no longer flag violations.',
            sod_deactivate_error: 'Failed to deactivate rule',
            sod_rule_count: '{count} {count, plural, one {rule} other {rules}}',
        },
        access_reviews: {
            title: 'Access Reviews',
            campaigns: 'Review Campaigns',
            new_campaign: 'New Campaign',
            campaign_name: 'Campaign Name',
            scope: 'Scope',
            reviewers: 'Reviewers',
            due_date: 'Due Date',
            progress: 'Progress',
            my_reviews: 'My Reviews',
            approve: 'Approve',
            revoke: 'Revoke',
            analytics: 'Role Analytics',
            analytics_title: 'Role Analytics',
            permission_usage: 'Permission Usage',
            role_coverage: 'Role Coverage',
            campaign_detail: 'Campaign Details',
            campaign_description: 'Description',
            campaign_status: 'Status',
            campaign_deadline: 'Deadline',
            campaign_scope_type: 'Scope Type',
            campaign_completion: 'Completion',
            campaign_total_items: 'Total Items',
            campaign_pending_items: 'Pending',
            campaign_confirmed_items: 'Confirmed',
            campaign_revoked_items: 'Revoked',
            campaign_escalated_items: 'Escalated',
            campaign_launch: 'Launch Campaign',
            campaign_cancel: 'Cancel Campaign',
            campaign_confirm_launch: 'Are you sure you want to launch this campaign?',
            campaign_confirm_cancel: 'Are you sure you want to cancel this campaign?',
            campaign_launched: 'Campaign launched successfully.',
            campaign_cancelled: 'Campaign cancelled.',
            review_items: 'Review Items',
            review_item_user: 'User',
            review_item_role: 'Role',
            review_item_reviewer: 'Reviewer',
            review_item_decision: 'Decision',
            review_item_justify: 'Justification',
            review_item_confirm: 'Confirm',
            review_item_revoke: 'Revoke',
            review_item_escalate: 'Escalate',
            all_decisions: 'All Decisions',
            bulk_action: 'Bulk Action',
            bulk_confirm: 'Bulk Confirm',
            bulk_revoke: 'Bulk Revoke',
            selected_count: '{count} selected',
            justify_required: 'Justification is required',
            scope_all_users: 'All Users',
            scope_role_owner: 'Role Owner',
            scope_department: 'Department',
            back_to_campaigns: 'Back to Campaigns',
            recommendations: 'Recommendations',
            refreshing: 'Refreshing...',
            decide: 'Decide',
            decision_by: 'Decision By',
            loading_items: 'Loading items...',
            loading_review_items: 'Loading review items...',
            campaign_not_found: 'Campaign not found',
            loading_campaign: 'Loading campaign details...',
            campaign_completed: 'Campaign completed successfully.',
            confirm_cancel_title: 'Confirm Cancel',
            justify_bulk_confirm: 'Justification for bulk confirm:',
            justify_bulk_revoke: 'Justification for bulk revoke:',
            items_selected: '{count} item(s) selected',
            justify_bulk_required: 'Justification is required for bulk actions',
            create_campaign_title: 'Create Access Review Campaign',
            name_required: 'Name is required',
            scope_type_required: 'Scope type is required',
            reviewer_strategy_required: 'Reviewer strategy is required',
            deadline_required: 'Deadline is required',
            reviewer_id_required: 'Reviewer ID is required for specific user strategy',
            invalid_json: 'Invalid JSON',
            scope_filter_label: 'Scope Filter (JSON)',
            reviewer_strategy_label: 'Reviewer Strategy',
            reviewer_id_label: 'Reviewer ID',
            deadline_label: 'Deadline',
            create_campaign_btn: 'Create Campaign',
            scope_type_label: 'Scope Type',
            scope_by_role: 'By Role',
            scope_by_department: 'By Department',
            scope_custom: 'Custom',
            reviewer_manager: 'Manager',
            reviewer_specific_user: 'Specific User',
            enter_json_filter: 'Enter JSON filter',
            hint_label: 'Hint:',
            role_usage: 'Role Usage',
            over_privileged_users: 'Over-Privileged Users',
            role_name_col: 'Role Name',
            assigned_users: 'Assigned Users',
            active_users_30d: 'Active Users (30d)',
            total_perms: 'Total Perms',
            used_perms: 'Used Perms',
            unused_perms: 'Unused Perms',
            no_data_available: 'No data available',
            roles_col: 'Roles',
            total_col: 'Total',
            used_col: 'Used',
            unused_pct: 'Unused %',
            current_count: 'Current Count',
            recommended_count: 'Recommended Count',
            rationale: 'Rationale',
            loading_analytics: 'Loading analytics...',
            loading_role_analytics: 'Loading role analytics data...',
            computed_at: 'Computed at:',
            form_errors: 'Form has {count} error(s). Please correct them.',
            scope_filter_invalid_json: 'Scope filter contains invalid JSON',
            scope_hint_all_users: 'No filter needed -- all users will be included.',
            review_type_label: 'Review Type',
            review_type_required: 'Review type is required',
            reviewer_ids_label: 'Reviewer IDs',
            reviewer_ids_hint: 'Enter user IDs separated by commas',
            reviewer_ids_required: 'At least one reviewer ID is required',
            start_date_label: 'Start Date',
            start_date_required: 'Start date is required',
            end_date_label: 'End Date',
            end_date_required: 'End date is required',
            review_type_user_access: 'User Access',
            review_type_role_permissions: 'Role Permissions',
            review_type_service_accounts: 'Service Accounts',
            review_type_privileged_access: 'Privileged Access',
            review_type_application_access: 'Application Access',
            auto_revoke_label: 'Auto-Revoke on Deny',
            campaigns_title: 'Access Review Campaigns',
            create_campaign_label: '+ Create Campaign',
            no_campaigns_found: 'No campaigns found',
            loading_campaigns: 'Loading campaigns...',
            no_reviews_assigned: 'No reviews assigned to you',
            loading_reviews: 'Loading reviews...',
            review_decision_title: 'Review Decision',
            justification_label: 'Justification',
            announce_loaded_review_items: 'Loaded {count} review items',
            announce_loaded_campaigns: 'Loaded {count} campaigns',
            announce_decision_submitted: 'Decision "{decision}" submitted for review item',
            announce_decision_submitted_for: 'Decision "{decision}" submitted for {name}',
            announce_bulk_decision: 'Bulk {decision} applied to {count} items',
        },
        attestation: {
            title: 'Attestation Schedules',
            new: 'New Schedule',
            edit: 'Edit Schedule',
            create_title: 'Create Attestation Schedule',
            edit_title: 'Edit Attestation Schedule',
            schedule_history: 'Schedule History',
            next_run_date: 'Next Run Date',
            schedule_name: 'Schedule Name',
            frequency: 'Frequency',
            next_run: 'Next Run',
            last_run: 'Last Run',
            history: 'Attestation History',
            description: 'Description',
            scope: 'Scope',
            status: 'Status',
            created_at: 'Created',
            updated_at: 'Updated',
            trigger_now: 'Trigger Now',
            back_to_schedules: 'Back to Schedules',
            confirm_trigger: 'Are you sure you want to trigger this schedule now?',
            confirm_delete: 'Are you sure you want to delete this schedule?',
            triggered: 'Schedule triggered successfully.',
            deleted: 'Schedule deleted.',
            saved: 'Schedule saved successfully.',
            campaigns_generated: 'Campaigns Generated',
            run_date: 'Run Date',
            run_status: 'Run Status',
            create_schedule: 'Create Schedule',
            view_history: 'View History',
            deactivate: 'Deactivate',
            overdue: 'Overdue',
            overdue_sr: 'Schedule is overdue',
            confirm_trigger_title: 'Confirm Action',
            confirm_trigger_message: 'Are you sure you want to trigger this schedule now? This will create a new access review campaign.',
            confirm_deactivate_title: 'Confirm Deactivation',
            confirm_deactivate_message: 'Are you sure you want to deactivate this schedule?',
            triggered_success: 'Schedule triggered successfully.',
            deactivated_success: 'Schedule deactivated.',
            name_label: 'Name *',
            frequency_label: 'Frequency *',
            scope_type_label: 'Scope Type *',
            scope_filter_label: 'Scope Filter (JSON)',
            reviewer_strategy_label: 'Reviewer Strategy *',
            reviewer_id_label: 'Reviewer ID *',
            frequency_quarterly: 'Quarterly',
            frequency_semi_annual: 'Semi-Annual',
            frequency_annual: 'Annual',
            scope_all_users: 'All Users',
            scope_by_role: 'By Role',
            scope_by_department: 'By Department',
            scope_framework: 'Framework',
            scope_custom: 'Custom',
            reviewer_manager: 'Manager',
            reviewer_role_owner: 'Role Owner',
            reviewer_specific_user: 'Specific User',
            computed_next_run_date: 'Computed Next Run Date',
            current_scheduled_date: 'Current scheduled date',
            recomputed_frequency_changed: '(recomputed -- frequency changed)',
            estimated_days_hint: 'Estimated based on {days} days from today',
            validation_name_required: 'Name is required',
            validation_frequency_required: 'Frequency is required',
            validation_scope_type_required: 'Scope type is required',
            validation_reviewer_strategy_required: 'Reviewer strategy is required',
            validation_reviewer_id_required: 'Reviewer ID is required for specific user strategy',
            validation_invalid_json: 'Invalid JSON',
            validation_scope_filter_invalid_json: 'Scope filter contains invalid JSON',
            form_errors: 'Form has {count} error(s). Please correct them.',
            create_schedule_btn: 'Create Schedule',
            schedule_history_title: 'Schedule History',
            campaign_name_col: 'Campaign Name',
            items_col: 'Items',
            no_campaigns_yet: 'No campaigns generated yet',
            loading_history: 'Loading history...',
            loading_schedule: 'Loading schedule...',
            total_campaigns: 'Total campaigns: {count}',
            results_per_page: 'Results per page',
            page_size_option: '{size} / page',
        },
        sod: {
            title: 'Separation of Duties Rules',
            new: 'New Rule',
            edit: 'Edit Rule',
            create_title: 'Create SoD Rule',
            edit_title: 'Edit SoD Rule',
            rule_name: 'Rule Name',
            conflicting_permissions: 'Conflicting Permissions',
            permission_a: 'Permission A',
            permission_b: 'Permission B',
            violations: 'SoD Violations',
            violation_user: 'User',
            violation_rule: 'Rule Violated',
            violation_date: 'Detected At',
            exceptions: 'SoD Exceptions',
            exception_reason: 'Exception Reason',
            exception_approver: 'Approved By',
            exception_status: 'Exception Status',
            has_exception: 'Has Exception',
            no_exception: 'No Exception',
            grant_exception: 'Grant Exception',
            granting: 'Granting...',
            grant_new_exception: 'Grant New Exception',
            exceptions_for: 'Exceptions for: {name}',
            back_to_rules: 'Back to Rules',
            no_violations: 'No violations found',
            no_exceptions_found: 'No exceptions found for this rule',
            all_exception_statuses: 'All Exception Statuses',
            user_id_label: 'User ID',
            justification_label: 'Justification',
            compensating_controls_label: 'Compensating Controls',
            expires_at_label: 'Expires At',
            granting_exception_for: 'Granting an exception for rule: {name}',
            severity: 'Severity',
            severity_warning: 'Warning',
            severity_blocking: 'Blocking',
            violation_count: '{count} violation(s)',
            exception_count: '{count} exception(s)',
            rule_description: 'Description',
            confirm_delete: 'Are you sure you want to delete this rule?',
            deleted: 'Rule deleted.',
            framework_code: 'Framework',
            name_label: 'Name *',
            severity_label: 'Severity *',
            conflict_pairs_legend: 'Conflict Permission Pairs',
            conflict_pairs_hint: 'Each pair defines two permissions that should not be held by the same user.',
            placeholder_permission_a: '-- Permission A --',
            placeholder_permission_b: '-- Permission B --',
            permission_a_for_pair: 'Permission A for pair {index}',
            permission_b_for_pair: 'Permission B for pair {index}',
            conflict_pair_label: 'Conflict pair {index}',
            remove_conflict_pair: 'Remove conflict pair {index}',
            add_pair: '+ Add Pair',
            placeholder_name: 'e.g. Payment Approval Separation',
            create_rule: 'Create Rule',
            loading_rule: 'Loading SoD rule...',
            failed_load_rule: 'Failed to load rule.',
            failed_create_rule: 'Failed to create SoD rule.',
            failed_update_rule: 'Failed to update SoD rule.',
            validation_name_required: 'Name is required.',
            validation_pair_required: 'At least one conflict permission pair is required.',
            validation_pair_both_required: 'Pair {index}: Both permissions must be selected.',
            validation_pair_must_differ: 'Pair {index}: Permission A and Permission B must be different.',
            added_conflict_pair: 'Added conflict pair {index}',
            removed_conflict_pair: 'Removed conflict pair {index}',
            announce_loaded_violations: 'Loaded {count} violations',
            announce_loaded_exceptions: 'Loaded {count} exceptions',
            announce_loaded_rules: 'Loaded {count} SoD rules',
            announce_loading_exceptions: 'Loading exceptions…',
            regulatory_info: {
                sod_context: 'SoD rules enforce regulatory role separation required by PDPL (controller vs processor roles must not be combined). Violations linked to a framework_code can be included in framework-specific evidence packs for cross-framework audit inspection (UC-VN-09). Severity levels distinguish regulatory violations from operational policy violations per fines-risks.json.',
                violation_export_context: 'SoD violation exports include framework_code, conflicting_roles, severity, created_by, and detected_at fields. This data feeds into cross-framework evidence packs reviewed by the Internal Audit Lead (P-VN-10) during audit inspections.',
                exception_context: 'SoD exceptions produce audit-grade evidence: approver identity, justification, compensating controls, and expiry date. Expired exceptions remain visible for audit trail continuity. These records support gap and exception identification in cross-framework audit packs (UC-VN-09 Step 5).',
            },
        },
        regulatory_info: {
            attestation_context: 'Attestation schedules support the annual governance attestation cycle required by Data Law (DATA-D4). When scope_type is set to "framework", schedules can be linked to specific regulatory frameworks (e.g. VN_DATA_LAW) so attestation reminders fire at the correct governance cycle cadence. Schedule history shows who attested, when, and department completion rates for staleness detection (UC-VN-04 Step 6).',
            api_keys_context: 'API key telemetry constitutes account evidence per VN_CYBERSEC_LAW_2025 Section B.5. Each key record includes last_used_at, ip_allowlist, permissions, status, and expires_at. Key lifecycle events (create/revoke/expire) appear in the unified audit trail with entity_type "api_key" for Cybersecurity evidence packs (UC-VN-05 Step 5). Revoked and expired keys remain visible for audit trail continuity.',
            session_context: 'Session management enforces access controls per Telecom Law confidentiality requirements (Section B.4). Session timeout, max concurrent sessions, and eviction policy are configurable. Session events (login, logout, timeout, eviction) produce audit trail entries. Active sessions show IP address, user agent, and login timestamp for incident investigation (UC-VN-05 Step 3).',
            break_glass_context: 'Break-glass procedures produce regulatory justification evidence: requestor identity, justification, permissions requested, duration, approver identity, approval/denial timestamps, and denial reason. Expired and denied records are retained and searchable for cross-framework evidence packs (UC-VN-09 Step 5). Break-glass events appear in the unified audit trail.',
            license_context: 'License settings gate modules by tier using PACKAGE_DEFINITIONS from shared types. Framework-specific modules (e.g. AI Law modules) require appropriate license tiers. Tier upgrade/downgrade events produce audit trail entries with old_tier, new_tier, changed_by, and timestamp for compliance evidence.',
            module_config_context: 'Module configuration supports framework-driven enablement. Disabling a module required by an active regulatory framework may break evidence completeness. Framework dependency indicators help the Enterprise DPO (P-VN-01) understand which modules are required for installed framework packs.',
            notification_context: 'Compliance-critical notifications (breach deadlines, attestation due dates, authority response deadlines) are marked with a compliance_critical flag. These notifications cannot be fully suppressed to ensure regulatory deadline adherence. The notification queue shows pending, sent, and failed status with compliance context.',
            delegation_context: 'Delegation governance produces audit-grade evidence: delegator_id, delegate_id, permission scope, start/end dates, and status. Delegation creation and expiry events appear in the unified audit trail. Expired and revoked delegations remain visible for audit trail continuity, supporting cross-framework evidence packs.',
            access_control_context: 'Access control configuration meets Cybersecurity Law requirements (VN_CYBERSEC_LAW_2025): IP allowlist restricts access with change audit trail, MFA configuration changes are audit-trailed, service accounts show last_used_at/permissions/status lifecycle, and SSO provider changes produce audit trail entries. All security settings changes are logged for compliance evidence.',
        },
    },
    // ---------- CMP Integration pages (Sprint 11.3.0) ----------
    cmp: {
        integration: {
            title: 'CMP Integration',
            status: 'Connection Status',
            endpoint: 'CMP Endpoint',
            last_sync: 'Last Sync',
            configure: 'Configure',
        },
        purpose_mapping: {
            title: 'Purpose Mapping',
            cmp_purpose: 'CMP Purpose',
            grc_purpose: 'GRC Purpose',
            mapping_status: 'Mapping Status',
            auto_map: 'Auto Map',
            new_mapping: 'New Mapping',
        },
        webhook: {
            title: 'Webhook Configuration',
            webhook_url: 'Webhook URL',
            secret: 'Webhook Secret',
            events: 'Event Types',
            test_webhook: 'Test Webhook',
        },
        bulk_sync: {
            title: 'Bulk Sync',
            sync_now: 'Sync Now',
            last_run: 'Last Run',
            records_synced: 'Records Synced',
            sync_history: 'Sync History',
            syncing: 'Syncing...',
        },
        sync_settings: {
            sync_failures: 'Sync Failures',
            sync_secret_warning: 'Sync Secret (save this now — it will not be shown again):',
            push_description: 'Configure the GRC Suite to push case status updates to AesirX CMP.',
            technical_description: 'This configuration controls how the GRC Suite pushes case status updates to AesirX CMP.',
            direction_label: 'Direction',
            direction_value: 'GRC Suite → AesirX CMP (outbound)',
            auth_label: 'Authentication',
            auth_value: 'HMAC-SHA256 signature in X-GRC-Signature header',
            retry_label: 'Retry Policy',
            retry_value: '3 retries with exponential backoff (30s, 2min, 10min)',
            inbound_label: 'Inbound Config',
            inbound_value: 'Inbound webhook (CMP → GRC) is configured in',
            cmp_webhook_settings: 'CMP Webhook Settings',
            page_title: 'CMP Sync Settings',
            outbound_sync_configured: 'Outbound sync configured.',
            configure_sync_failed: 'Failed to configure sync',
            configuration_updated: 'Configuration updated.',
            update_configuration_failed: 'Failed to update configuration',
            connection_test_failed: 'Connection test failed',
            test_result_message: 'Test sent to {endpoint}. Status: {status}',
            endpoint_placeholder: 'https://your-cmp-domain/api/grc-sync',
            events: {
                new_to_under_review: 'New → Under Review (assigned)',
                under_review_to_identity_verification: 'Under Review → Identity Verification',
                under_review_to_in_progress: 'Under Review → In Progress',
                in_progress_to_completed: 'In Progress → Completed',
                in_progress_to_denied: 'In Progress → Denied',
                any_to_closed: 'Any → Closed',
            },
        },
    },
    // ---------- Settings pages (Sprint 11.3.0) ----------
    settings_pages: {
        webhooks: {
            title: 'Webhooks',
            new: 'New Webhook',
            url: 'URL',
            url_placeholder: 'https://example.com/webhook',
            events: 'Events',
            secret: 'Secret',
            active: 'Active',
            test: 'Test',
            create_failed: 'Failed to create webhook.',
            create_success: 'Webhook created successfully.',
            deactivate_failed: 'Failed to deactivate webhook.',
            deactivate_success: 'Webhook deactivated.',
            fetch_failed: 'Failed to fetch webhooks.',
            send_test_failed: 'Failed to send test.',
            test_failed: 'Webhook test failed.',
            test_success: 'Webhook test succeeded.',
            edit_title: 'Edit Webhook',
            announce_loaded: 'Webhooks loaded',
            update_success: 'Webhook updated successfully.',
            update_failed: 'Failed to update webhook.',
            activate_success: 'Webhook activated.',
            activate_failed: 'Failed to activate webhook.',
        },
        domains: {
            title: 'Domains',
            new: 'New Domain',
            domain_name: 'Domain Name',
            verified: 'Verified',
            verify: 'Verify',
            add_failed: 'Failed to add domain.',
            add_success: 'Domain added successfully.',
            domain_placeholder: 'e.g., grc.example.com',
            fetch_failed: 'Failed to fetch domains.',
            remove_failed: 'Failed to remove domain.',
            remove_success: 'Domain removed successfully.',
            txt_record: 'TXT Record',
            verify_dns_failed: 'DNS verification failed.',
            verify_failed: 'Failed to verify domain.',
            verify_success: 'Domain verified successfully.',
        },
        export_schedules: {
            title: 'Export Schedules',
            new: 'New Schedule',
            schedule_name: 'Name',
            frequency: 'Frequency',
            format: 'Format',
            last_run: 'Last Run',
            next_run: 'Next Run',
            create_failed: 'Failed to create schedule.',
            create_success: 'Schedule created successfully.',
            day_of_month: 'Day of Month',
            day_of_week: 'Day of Week',
            deactivate_failed: 'Failed to deactivate schedule.',
            deactivate_success: 'Schedule deactivated.',
            fetch_failed: 'Failed to fetch schedules.',
            freq_daily: 'Daily',
            freq_monthly: 'Monthly',
            freq_weekly: 'Weekly',
            recipients: 'Recipients',
            time_utc: 'Time (UTC)',
            trigger_failed: 'Failed to trigger export.',
            trigger_success: 'Export triggered successfully.',
            type_audit_trail: 'Audit Trail',
            type_compliance_report: 'Compliance Report',
            type_custom: 'Custom',
            type_incident_report: 'Incident Report',
            type_ropa: 'ROPA',
        },
        email_log: {
            title: 'Email Log',
            recipient: 'Recipient',
            subject: 'Subject',
            sent_at: 'Sent At',
            status: 'Status',
            resend: 'Resend',
            fetch_failed: 'Failed to fetch email logs.',
        },
    },
    // ---------- Phase 9 Extension: Organization Management ----------
    organizations: {
        title: 'Organizations',
        create: 'Create Organization',
        edit: 'Edit Organization',
        name: 'Name',
        slug: 'Slug',
        timezone: 'Timezone',
        locale: 'Default Locale',
        parent: 'Parent Organization',
        group: 'Group',
        org_group: 'Organization Group',
        no_group: '(None - not in a group)',
        created: 'Created',
        no_parent: '(None - standalone)',
        no_orgs: 'No organizations found',
        load_error: 'Failed to load organizations',
        save_error: 'Failed to save organization',
        created_msg: 'Organization created',
        updated: 'Organization updated',
        deactivate: 'Deactivate',
        deactivated: 'Organization deactivated',
        confirm_deactivate: 'Are you sure you want to deactivate this organization?',
        loaded_results: 'Organizations loaded, {count} results',
        form_loaded: 'Organization form loaded',
        locale_vi_vn: 'Tiếng Việt (vi-VN)',
        locale_en_us: 'English (en-US)',
    },
    // ---------- Phase 9 Extension: Public Intake Configs ----------
    intake_configs: {
        title: 'Public Intake Configs',
        create: 'Create Config',
        edit: 'Edit Intake Config',
        label_col: 'Label',
        key: 'Intake Key',
        key_hint: 'Public URL will be: /rights-request?key=',
        mapped_org: 'Target Organization',
        status: 'Status',
        enabled: 'Enabled',
        disabled: 'Disabled',
        request_types: 'Enabled Request Types',
        language: 'Default Language',
        public_url: 'Public URL',
        no_configs: 'No intake configs found. Create one to enable public rights request forms.',
        load_error: 'Failed to load intake configs',
        save_error: 'Failed to save',
        delete_error: 'Failed to delete',
        created_msg: 'Config created',
        updated: 'Config updated',
        deleted: 'Intake config deleted',
        confirm_delete: 'Delete this intake config?',
        select_types: 'Select at least one request type',
        select_org: 'Select a target organization',
        routing_mode: 'Routing Mode',
        single_org: 'Single Organization',
        org_group_mode: 'Organization Group',
        org_group: 'Organization Group',
        select_group: 'Select an organization group...',
        group_root_hint: 'Auto-set to the group root organization when group is selected.',
        loaded_results: 'Intake configurations loaded, {count} results',
        form_loaded: 'Intake configuration form loaded',
    },
    // ---------- Frequency labels ----------
    frequency: {
        weekly: 'Weekly',
        monthly: 'Monthly',
        quarterly: 'Quarterly',
        semi_annually: 'Semi-Annually',
        annually: 'Annually',
        custom: 'Custom',
    },
    // ---------- Task Management module (Sprint 17.4.0) ----------
    task_management: {
        nav: {
            calendar: 'Calendar',
            all_tasks: 'All Tasks',
            my_tasks: 'My Tasks',
            overdue: 'Overdue',
            board: 'Board',
            workflows: 'Workflows',
        },
        list: {
            title: 'All Tasks',
            my_title: 'My Tasks',
            overdue_title: 'Overdue Tasks',
            new_task: 'Create Task',
            no_tasks: 'No tasks found.',
            empty_state: 'No tasks found. Tasks are generated from module workflows like assessments, incidents, and review campaigns.',
            total: 'Total Tasks',
            search_placeholder: 'Search tasks...',
            bulk_actions: 'Bulk Actions',
            change_status: 'Change Status',
            change_priority: 'Change Priority',
            assign: 'Assign',
            sr_my_tasks_count: '{count} tasks assigned to you',
            sr_days_overdue: '{count} days overdue',
            sla_label: 'SLA',
        },
        detail: {
            title: 'Task Detail',
            description: 'Description',
            workflow_progress: 'Workflow Progress',
            checklist: 'Checklist',
            dependencies: 'Dependencies',
            comments: 'Comments',
            activity_timeline: 'Activity Timeline',
            assignees: 'Assignees',
            add_assignee: 'Add Assignee',
            remove_assignee: 'Remove Assignee',
            due_date: 'Due Date',
            start_date: 'Start Date',
            sla_status: 'SLA Status',
            sla_on_track: 'On track',
            sla_at_risk: 'At risk',
            sla_breached: 'Breached',
            tags: 'Tags',
            source_entity: 'Source Entity',
            view_source: 'View Source',
            add_dependency: 'Add Dependency',
            remove_dependency: 'Remove Dependency',
            write_comment: 'Write a comment...',
            post_comment: 'Post',
            edit_comment: 'Edit',
            delete_comment: 'Delete',
            add_checklist_item: 'Add item',
            no_dependencies: 'No dependencies',
            no_comments: 'No comments yet',
            blocks: 'Blocks',
            blocked_by: 'Blocked by',
            related: 'Related',
            working_days_remain: 'working days remain',
            created_by: 'Created by',
            completed_at: 'Completed at',
            no_assignees: 'No assignees',
            add_assignee_placeholder: 'Enter user ID...',
            edited: 'edited',
            timeline: 'Activity Timeline',
            no_timeline: 'No activity yet',
            no_workflow: 'No workflow assigned',
            add_dependency_placeholder: 'Enter task ID...',
            sr_status_updated: 'Status updated to {status}',
            sr_priority_updated: 'Priority updated to {priority}',
            // Sprint 47.5.8.5 — task → workflow-run conversion + linked chip
            convert_to_workflow: 'Run as Workflow',
            convert_modal_title: 'Convert task to workflow run',
            convert_workflow_label: 'Workflow',
            convert_workflow_placeholder: 'Pick a workflow…',
            convert_attachments_label: 'Attachments',
            convert_attachments_empty: 'No attachments on this task.',
            convert_persona_label: 'Persona override (optional)',
            convert_submit: 'Start workflow run',
            convert_starting: 'Starting workflow run…',
            convert_started: 'Workflow run started — opening run…',
            convert_disabled_in_flight: 'A workflow run from this task is already in progress.',
            linked_workflow_chip: 'From {workflow_title}',
            linked_chat_chip: 'From AI Chat',
            linked_more: '+{count} more',
        },
        calendar: {
            title: 'Task Calendar',
            page_title: 'Calendar',
            create_task: 'Create Task',
            month: 'Month',
            week: 'Week',
            day: 'Day',
            today: 'Today',
            more_tasks: '+{count} more',
            no_tasks_day: 'No tasks this day',
            all_day: 'All day',
            quick_create_tooltip: 'Create task for this day',
            view_changed: 'Calendar view changed to {view}',
            tasks_in_view: '{count} tasks in view',
            task_in_view: '{count} task in view',
        },
        filters: {
            assignee: 'Assignee',
            me: 'Me',
            role: 'Role',
            status: 'Status',
            priority: 'Priority',
            task_type: 'Task Type',
            source_module: 'Source Module',
            organization: 'Organization',
            sla_breached_only: 'Show only SLA breached',
            clear_filters: 'Clear Filters',
            apply_filters: 'Apply',
            // Sprint 47.5.8.5 — source-kind chip on TaskListPage
            source_label: 'Source',
            source_manual: 'Manual',
            source_module_kind: 'Module-generated',
            source_chat: 'From AI Chat',
            source_workflow_run: 'From AI Workflow Run',
        },
        status: {
            pending: 'Pending',
            in_progress: 'In Progress',
            blocked: 'Blocked',
            completed: 'Completed',
            overdue: 'Overdue',
            escalated: 'Escalated',
            cancelled: 'Cancelled',
        },
        priority: {
            critical: 'Critical',
            high: 'High',
            medium: 'Medium',
            low: 'Low',
        },
        task_types: {
            manual_task: 'Manual Task',
            dsr_case: 'DSR Case',
            dsr_review: 'DSR Review',
            dsr_escalation: 'DSR Escalation',
            dpia_review: 'DPIA Review',
            dpia_approval: 'DPIA Approval',
            transfer_review: 'Transfer Review',
            incident_task: 'Incident Task',
            incident_notification: 'Incident Notification',
            playbook_step: 'Playbook Step',
            regulator_deadline: 'Regulator Deadline',
            monitoring_task: 'Monitoring Task',
            kpi_alert: 'KPI Alert',
            vendor_review: 'Vendor Review',
            vendor_questionnaire: 'Vendor Questionnaire',
            governance_review: 'Governance Review',
            drift_alert: 'Drift Alert',
            governance_approval: 'Governance Approval',
            deletion_task: 'Deletion Task',
            deletion_verification: 'Deletion Verification',
            access_review: 'Access Review',
            sod_alert: 'SoD Alert',
            consent_workflow: 'Consent Workflow',
            consent_alert: 'Consent Alert',
            property_review: 'Property Review',
            inconsistency_alert: 'Inconsistency Alert',
            form_review: 'Form Review',
            dossier_deadline: 'Dossier Deadline',
            submission_task: 'Submission Task',
            translation_review: 'Translation Review',
            governance_task: 'Governance Task',
            reporting_deadline: 'Reporting Deadline',
            scanner_alert: 'Scanner Alert',
            classification_review: 'Classification Review',
            lifecycle_task: 'Lifecycle Task',
            discovery_review: 'Discovery Review',
        },
        task_type_groups: {
            manual: 'Manual',
            dsr: 'DSR / Rights Requests',
            assessments: 'Assessments',
            incidents: 'Incidents',
            incident_ops: 'Incident Ops',
            monitoring: 'Monitoring',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion: 'Deletion',
            access_review: 'Access Review',
            consent: 'Consent',
            consent_properties: 'Consent Properties',
            forms: 'Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Classification',
            discovery: 'Discovery',
        },
        create: {
            title: 'Create Task',
            task_title: 'Title',
            task_description: 'Description',
            task_type: 'Task Type',
            task_type_label: 'Task Type',
            task_type_placeholder: 'Select a task type',
            priority: 'Priority',
            assignees: 'Assignees',
            assignee_search_placeholder: 'Search users...',
            due_date: 'Due Date',
            start_date: 'Start Date',
            checklist: 'Checklist',
            tags: 'Tags',
            add_checklist_item: 'Add checklist item',
            validation: {
                title_required: 'Title is required',
                title_max_length: 'Title must be 500 characters or fewer',
                description_max_length: 'Description must be 5000 characters or fewer',
                due_date_before_start: 'Due date must be after start date',
                max_reminders: 'Maximum 5 reminders per task',
            },
            reminders: {
                title: 'Reminders',
                add: 'Add Reminder',
                at_due_date: 'At due date',
                minutes_before: '{count} minutes before',
                hours_before: '{count} hours before',
                days_before: '{count} days before',
                weeks_before: '{count} weeks before',
                custom: 'Custom',
                channel_platform: 'Platform',
                channel_email: 'Email',
                channel_both: 'Both',
            },
            workflow: {
                label: 'Workflow',
                none: 'None (manual status management)',
                steps_count: '{count} steps',
                preview_label: 'Workflow Steps',
            },
        },
        errors: {
            fetch_failed: 'Failed to load tasks.',
            save_failed: 'Failed to save task.',
            delete_failed: 'Failed to delete task.',
            status_change_failed: 'Failed to change task status.',
            assign_failed: 'Failed to assign task.',
            comment_failed: 'Failed to save comment.',
            checklist_failed: 'Failed to update checklist.',
            dependency_failed: 'Failed to update dependency.',
            workflow_failed: 'Failed to save workflow.',
            workflow_activate_failed: 'Failed to activate workflow.',
            workflow_delete_failed: 'Failed to delete workflow.',
            template_install_failed: 'Failed to install template.',
            search_failed: 'Failed to search tasks.',
            // Sprint 47.5.8.5
            convert_failed: 'Could not start the workflow run.',
            convert_quota_exhausted: 'Token bucket low. Top up before re-running.',
        },
        dependency_types: {
            blocks: 'Blocks',
            blocked_by: 'Blocked by',
            related: 'Related',
        },
        workflow: {
            current_step: 'Current Step',
            transition: 'Transition',
            history: 'Step History',
            no_workflow: 'No workflow assigned',
            detail_title: 'Workflow Details',
            list_title: 'Workflow Definitions',
            create_workflow: 'Create Workflow',
            browse_templates: 'Browse Templates',
            template_catalog_title: 'Workflow Template Catalog',
            name: 'Name',
            description: 'Description',
            task_types: 'Task Types',
            steps: 'Steps',
            version: 'Version',
            created: 'Created',
            actions: 'Actions',
            status_draft: 'Draft',
            status_active: 'Active',
            status_archived: 'Archived',
            activate: 'Activate',
            archive: 'Archive',
            save: 'Save',
            edit: 'Edit',
            delete: 'Delete',
            clone: 'Clone',
            install: 'Install',
            installed: 'Installed',
            system: 'System',
            general_settings: 'General Settings',
            default_assignment: 'Default Assignment',
            default_sla: 'Default SLA',
            workflow_steps: 'Workflow Steps',
            add_step: 'Add Step',
            step_name: 'Step Name',
            step_status: 'Status Value',
            assignment_strategy: 'Assignment Strategy',
            assignment_role: 'Assignment Role',
            sla_hours: 'SLA Hours',
            sla_calendar_type: 'Calendar Type',
            working_days: 'Working Days',
            calendar_days: 'Calendar Days',
            is_terminal: 'Terminal Step',
            required_fields: 'Required Fields',
            checklist_template: 'Checklist Template',
            transitions: 'Transitions',
            add_transition: 'Add Transition',
            from_step: 'From',
            to_step: 'To',
            permission: 'Permission',
            sla_action: 'SLA Action',
            notify: 'Notify',
            escalation_rules: 'Escalation Rules',
            add_rule: 'Add Rule',
            trigger: 'Trigger',
            threshold: 'Threshold',
            action: 'Action',
            target_role: 'Target Role',
            trigger_sla_threshold: 'SLA Threshold',
            trigger_sla_breach: 'SLA Breach',
            trigger_blocked_duration: 'Blocked Duration',
            trigger_manual: 'Manual',
            action_notify: 'Notify',
            action_escalate: 'Escalate Tier',
            action_reassign: 'Reassign',
            sla_start: 'Start',
            sla_pause: 'Pause',
            sla_resume: 'Resume',
            sla_stop: 'Stop',
            strategy_manual: 'Manual',
            strategy_role_based: 'Role-based',
            strategy_round_robin: 'Round Robin',
            strategy_least_loaded: 'Least Loaded',
            strategy_creator: 'Creator',
            confirm_delete: 'Are you sure you want to delete this workflow?',
            confirm_archive: 'Are you sure you want to archive this workflow?',
            cannot_delete_active: 'Active workflows cannot be deleted. Archive first.',
            validation_errors: 'Workflow validation errors',
            step_count: '{count} steps',
            no_workflows: 'No workflows found.',
            no_templates: 'No templates available.',
            sr_templates_count: '{count} templates available',
            template_installed: 'Template installed successfully.',
            activate_success: 'Workflow activated successfully.',
            archive_success: 'Workflow archived successfully.',
            save_success: 'Workflow saved successfully.',
            delete_success: 'Workflow deleted successfully.',
        },
        columns: {
            priority: 'Priority',
            title: 'Title',
            status: 'Status',
            type: 'Type',
            assignees: 'Assignee(s)',
            due_date: 'Due Date',
            source: 'Source',
            actions: 'Actions',
        },
        groups: {
            overdue: 'Overdue',
            due_today: 'Due Today',
            due_this_week: 'Due This Week',
            due_later: 'Due Later',
            no_due_date: 'No Due Date',
        },
        board: {
            title: 'Task Board',
            drag_hint: 'Drag tasks between columns to change status',
            empty_column: 'No tasks',
            keyboard_hint: 'Keyboard: Arrow keys to navigate, Space to pick up a task, Arrow Left/Right to select column, Enter to drop, Escape to cancel.',
            sr_moving: 'Moving {title}. Over {column}. Press Enter to drop, Escape to cancel.',
            sr_tasks_shown: '{count} tasks shown',
            sr_picked_up: 'Picked up {title}',
            sr_returned: '{title} returned to {column}',
            sr_dropped: 'Dropped {title} in {column}, position {position} of {total}',
            sr_move_cancelled: 'Move cancelled',
            sr_over_column: 'Over {column}',
            sr_moved_to: '{title} moved to {column}',
            invalid_transition: 'Cannot move task from {from} to {to}.',
            terminal_status: 'This task is in a terminal status and cannot be moved.',
            invalid_drop_target: 'Invalid drop target',
        },
        team: {
            title: 'Team Workload',
            assignee: 'Assignee',
            active: 'Active',
            overdue: 'Overdue',
            blocked: 'Blocked',
            completed_7d: 'Completed (7d)',
            load: 'Load',
            load_high: 'High',
            load_medium: 'Medium',
            load_normal: 'Normal',
        },
        graph: {
            title: 'Dependency Graph',
            list_view: 'List View',
            graph_view: 'Graph View',
            zoom_in: 'Zoom In',
            zoom_out: 'Zoom Out',
            reset_zoom: 'Reset',
            blocks_label: 'blocks',
            related_label: 'related',
            no_dependencies: 'No dependencies to visualize.',
        },
        add_dependency_modal: {
            title: 'Add Dependency',
            search_placeholder: 'Search tasks by title or ID...',
            no_results: 'No tasks found.',
            this_blocks: 'This task blocks selected',
            this_blocked_by: 'This task is blocked by selected',
            related: 'Related',
            max_dependencies: 'Maximum 10 dependencies per task.',
            circular_error: 'Cannot add: would create a circular dependency.',
        },
        conflict_detection: {
            title: 'Conflict Detection',
            unresolved: 'unresolved',
            no_conflicts: 'No conflicts detected',
            section: 'Section',
            field: 'Field',
            dept_a: 'Department A',
            value_a: 'Value A',
            dept_b: 'Department B',
            value_b: 'Value B',
            severity: 'Severity',
            status: 'Status',
            resolved: 'Resolved',
            open: 'Open',
            resolve: 'Resolve',
            load_failed: 'Failed to load conflicts',
            resolve_success: 'Conflict resolved successfully',
            resolve_failed: 'Failed to resolve conflict',
            severity_critical: 'Critical',
            severity_warning: 'Warning',
            severity_info: 'Info',
        },
        consolidation: {
            title: 'Consolidation Progress',
            add_child: 'Add Child Task',
            child_task_id: 'Child Task ID',
            department_id: 'Department ID',
            link: 'Link',
            overall_completion: 'Overall Completion',
            departments_complete: 'Departments Complete',
            overdue: 'Overdue',
            ready: 'Ready',
            not_ready: 'Not Ready',
            master_status: 'Master Status',
            department_breakdown: 'Department Breakdown',
            department: 'Department',
            status: 'Status',
            completion: 'Completion',
            due_date: 'Due Date',
            overdue_flag: 'Overdue',
            child_tasks: 'Child Tasks',
            task: 'Task',
            load_failed: 'Failed to load consolidation data',
            child_linked: 'Child task linked successfully',
            link_failed: 'Failed to link child task',
            // fix-sprint-20260411-D F8 — confirm + semantic toast keys for unlink
            confirm_unlink: 'Are you sure you want to unlink this child task?',
            child_unlinked: 'Child task unlinked',
            unlink_failed: 'Failed to unlink child task',
            unlink: 'Unlink',
            status_completed: 'Completed',
            status_in_progress: 'In Progress',
            status_pending: 'Pending',
            select_child_task: 'Select child task',
            select_department: 'Select department',
        },
        section_assignments: {
            title: 'Section Assignments',
            assign_section: 'Assign Section',
            section_ref: 'Section Ref',
            section_label: 'Section Label',
            department_id: 'Department ID',
            department: 'Department',
            assignee_id: 'Assignee ID',
            assignee: 'Assignee',
            mandatory: 'Mandatory',
            assign: 'Assign',
            no_assignments: 'No section assignments',
            status: 'Status',
            completion: 'Completion',
            mark_complete: 'Complete',
            load_failed: 'Failed to load section assignments',
            assign_success: 'Section assigned successfully',
            assign_failed: 'Failed to assign section',
            status_updated: 'Status updated',
            status_update_failed: 'Failed to update status',
            section_ref_placeholder: 'e.g. 3.2',
            status_completed: 'Completed',
            status_in_progress: 'In Progress',
            status_pending: 'Pending',
            select_department: 'Select department',
            select_assignee: 'Select assignee',
        },
        overdue_columns: {
            days_overdue: 'Days Overdue',
        },
        regulatory_info: {
            deadline_context: 'When creating DPIA-related tasks, due dates should align with PDPL filing deadlines: department input = 10 working days (from vn-pdpl/deadlines.json). Overdue flagging triggers when this window is exceeded. Cybersecurity authority response deadlines (48h legal review, 72h response preparation) and Telecom deadlines (5 working days response, 2 working days legal approval) also apply.',
            overdue_deadline_context: 'Regulatory deadlines from pack data: PDPL department input = 10 working days, supplement response = 15 working days, resubmission = 10 working days. Cybersecurity authority response and telecom preparation deadlines are also enforced. SLA-breached tasks are visually distinguished.',
            section_assignment_context: 'DPIA multi-department workflow (PDPL-P1): the Enterprise DPO assigns dossier sections to departments (e.g. Retail Banking, HR, Marketing). Each assignment captures section_ref, section_label, department, assignee, mandatory flag, and completion percentage. Department Data Owners see only their assigned sections in My Tasks. Department input deadline: 10 working days (configurable per vn-pdpl/deadlines.json).',
            template_catalog_context: 'System workflow templates cover framework-specific compliance workflows: PDPL DPIA filing, Data Law annual governance attestation, Cybersecurity incident response, Telecom authority request, and AI system registration. Each template includes step_count, sla_summary, and task_types aligned with pack procedure data. System templates (is_system = true) are non-editable to preserve regulatory procedure integrity.',
            workflow_instantiation_context: 'Installed workflow templates can be instantiated into live workflows from this page. When instantiated, tasks are automatically created with due dates calculated from the workflow SLA and the current date. Active workflows enforce step-by-step status transitions via the task board. System templates from the catalog are non-editable to preserve regulatory procedure integrity.',
        },
    },
    // ---------- Sprint 17.3.0: Notifications ----------
    notifications: {
        bell: {
            title: 'Notifications',
            unread_count: 'unread',
        },
        drawer: {
            title: 'Notifications',
            mark_all_read: 'Mark all as read',
            no_notifications: 'No notifications',
            filter: {
                all: 'All',
                unread: 'Unread',
                tasks: 'Tasks',
                approvals: 'Approvals',
                alerts: 'Alerts',
            },
        },
        preferences: {
            title: 'Notification Preferences',
            description: 'Configure how you receive notifications',
            category: {
                label: 'Category',
                task_assigned: 'Task Assigned',
                task_sla_approaching: 'SLA Approaching',
                task_overdue: 'Task Overdue',
                task_completed: 'Task Completed',
                task_escalated: 'Task Escalated',
                workflow_transition: 'Workflow Transition',
                approval_requested: 'Approval Requested',
                comment_added: 'Comment Added',
                weekly_digest: 'Weekly Digest',
                sla_breach_report: 'SLA Breach Report',
                system_alert: 'System Alert',
            },
            channel: {
                platform: 'Platform',
                email: 'Email',
            },
            quiet_hours: {
                title: 'Quiet Hours',
                start: 'Start',
                end: 'End',
                timezone: 'Timezone',
            },
            digest: {
                title: 'Digest Frequency',
                daily: 'Daily',
                weekly: 'Weekly',
                none: 'None',
            },
            watched: {
                title: 'Watched Entities',
                empty: 'No watched entities',
            },
            save: 'Save Preferences',
            saving: 'Saving...',
            saved_success: 'Preferences saved successfully',
            load_error: 'Could not load saved preferences. Showing defaults \u2014 changes will be saved when you click Save.',
            enabled: 'Enabled',
            disabled: 'Disabled',
            unwatch: 'Unwatch',
        },
        admin_queue: {
            title: 'Notification Admin Queue',
            description: 'Monitor email delivery status and retry failed messages',
            load_error: 'Failed to load email queue data',
            retry_error: 'Failed to retry email',
            filter_status: 'Filter by status',
            all_statuses: 'All',
            refresh: 'Refresh',
            status_queued: 'Queued',
            status_sent: 'Sent',
            status_failed: 'Failed',
            status_bounced: 'Bounced',
            col_recipient: 'Recipient',
            col_category: 'Category',
            col_template: 'Template',
            col_status: 'Status',
            col_retries: 'Retries',
            col_created: 'Created',
            col_sent: 'Sent At',
            col_actions: 'Actions',
            no_items: 'No email queue items found',
            retry: 'Retry',
            retrying: 'Retrying...',
            prev: 'Prev',
            next: 'Next',
            access_denied_title: 'Admin Access Required',
            access_denied: 'This page is only available to administrators. Please contact your admin if you need access.',
            announce_queue_loaded: 'Email queue loaded, {count} items',
            announce_processed: 'Processed {count} email(s)',
            process_now: 'Process Now',
            processing_ellipsis: 'Processing…',
        },
    },
    // ---------- UI Assist (Sprint 17.8.0) ----------
    ui_assist: {
        toolbar: {
            title: 'UI Assist',
            activate: 'Activate',
            deactivate: 'Deactivate',
            connected: 'Connected',
            disconnected: 'Disconnected',
            disconnected_hint: 'Disconnected — run `npm run relay` to connect to Claude CLI',
            open_panel: 'Open UI Assist panel',
            history: 'History',
            settings: 'Settings',
        },
        prompt: {
            placeholder: 'Describe the change you want to make…',
            send: 'Send',
            cancel: 'Cancel',
            char_count: '{count} / {max}',
        },
        session: {
            streaming: 'Processing…',
            complete: 'Complete',
            error: 'Error',
            abort: 'Abort',
            abort_confirm: 'Are you sure you want to abort?',
            aborted_hint: 'Session was interrupted — you can continue from here.',
            undo: 'Undo',
            redo: 'Redo',
            retry: 'Retry',
            dismiss: 'Dismiss',
        },
        label: {
            component: 'Component',
            element: 'Element',
            file: 'File',
        },
        response: {
            you: 'You',
            claude: 'Claude',
            empty: 'No messages yet. Select elements and send a prompt to get started.',
        },
        panel: {
            tab_conversation: 'Chat',
            tab_tasks: 'Tasks',
            tab_sprint: 'Sprint',
            tab_sessions: 'Sessions',
            tab_lobby: 'Lobby',
            follow_up: 'Follow Up',
            followup_placeholder: 'Ask a follow-up question…',
            interrupt_placeholder: 'Type to interrupt and redirect…',
            send_interrupt: 'Send & Interrupt',
            will_interrupt: 'Will abort current response and send this instead',
            expand: 'Expand to full screen',
            collapse: 'Collapse to side panel',
            no_sessions: 'No previous sessions. Select elements and send a prompt to start.',
            session_active: 'Active',
            session_ended: 'Ended',
            session_messages: '{count} messages',
            switch_session: 'Switch to this session',
            load_session: 'Load session history',
            session_restored: 'Session restored from history',
            archive_session: 'Archive session',
            archive_confirm: 'Archive this session? The session file will be deleted.',
            new_session: 'New Session',
            new_session_desc: 'Use Activate to select a UI element and start a new session',
            start_new_session: 'Start New Session',
            no_active_session_chat: 'Start a new session or use Activate to select a UI element on the page to begin.',
            no_active_session_tasks: 'Start a new session or select an existing session to create tasks.',
            no_active_session_sprint: 'Start a new session or select an existing session to plan a sprint.',
            previous_sessions: 'Previous sessions',
        },
        tasks: {
            none_found: 'No tasks found in the response. Ask Claude to suggest tasks.',
            selected: 'selected',
            create_selected: 'Create Tasks',
            execute_tasks: 'Execute Tasks',
            creating: 'Creating…',
            created: 'tasks created',
            create_error: 'Failed to create task',
            plan_sprint: 'Plan Sprint',
            priority_critical_desc: 'Blocker — must fix immediately',
            priority_high_desc: 'Important — address this sprint',
            priority_medium_desc: 'Normal — plan for upcoming sprint',
            priority_low_desc: 'Nice to have — backlog candidate',
            add_custom: 'Add Task',
            add_title_placeholder: 'Task title…',
            add_desc_placeholder: 'Description (optional)',
            add_save: 'Add',
            add_cancel: 'Cancel',
        },
        sprint: {
            title: 'Sprint Planner',
            assigned: 'assigned',
            new_placeholder: 'Sprint name (e.g. Sprint 18.1.0)',
            add: 'Add',
            backlog: 'Backlog',
            move_to: 'Move to…',
            empty: 'Create a sprint group and assign tasks to it.',
            create_all: 'Create All Tasks',
            remove: 'Remove',
            drop_here: 'Assign tasks from the backlog below',
            add_task: 'Add task from backlog…',
        },
        feature_disabled: 'UI Assist is not enabled',
        permission_denied: 'You do not have permission to use UI Assist',
    },
    // ---------- Sprint 18.0.0: License & Package Management ----------
    license: {
        title: 'License & Package',
        current_license: 'Current License',
        license_key: 'License Key',
        activate: 'Activate License',
        deactivate: 'Deactivate License',
        validate_now: 'Validate Now',
        status: 'License Status',
        active: 'Active',
        expired: 'Expired',
        suspended: 'Suspended',
        invalid: 'Invalid',
        pending: 'Pending',
        no_license: 'No License',
        expires_at: 'Expires',
        last_validated: 'Last Validated',
        domain: 'Licensed Domain',
        activation_success: 'License activated successfully.',
        activation_failed: 'License activation failed.',
        domain_mismatch: 'License domain does not match the current domain.',
        deactivation_success: 'License deactivated.',
        validation_success: 'License validated successfully.',
        validation_failed: 'License validation failed.',
        enter_key: 'Enter your license key',
        confirm_deactivate: 'Are you sure you want to deactivate this license? Module restrictions will apply based on the default tier.',
    },
    package_mgmt: {
        title: 'Package',
        module_column: 'Module',
        current_tier: 'Current Package',
        light: 'Light',
        standard: 'Standard',
        enterprise_unlimited: 'Enterprise Unlimited',
        change_package: 'Change Package',
        upgrade: 'Upgrade',
        downgrade: 'Downgrade',
        target_tier: 'Target Package',
        new_license_key: 'License Key for Target Package',
        confirm_change: 'Confirm Package Change',
        change_success: 'Package changed successfully.',
        change_failed: 'Package change failed.',
        modules_included: 'Modules Included',
        modules_restricted: 'Not Available in Current Package',
        upgrade_to_enable: 'Upgrade to enable this module.',
        module_availability: 'Module Availability',
        comparison: 'Package Comparison',
        power_users: 'Power Users',
        unlimited: 'Unlimited',
        impact_warning: 'Downgrading will disable the following modules:',
        invalid_license: 'The provided license key is not valid for this package.',
        tier_mismatch: 'The license key is for a different package tier.',
    },
    // ---------- Sprint 18.2.0: Board Report UX ----------
    board_report: {
        title: 'Board Compliance Report',
        subtitle: 'Executive compliance summary across all modules',
        date_from: 'Date From',
        date_to: 'Date To',
        language: 'Report Language',
        generate: 'Generate Report',
        generating: 'Generating Report...',
        download_pdf: 'Download PDF',
        close: 'Close Report',
        open_board_report: 'Generate Board Report',
        close_board_report: 'Close Board Report',
        report_period: 'Report Period',
        generated_at: 'Generated At',
        generated_by: 'Generated By',
        organization: 'Organization',
        section_rights_requests: 'Rights Requests',
        section_vendors: 'Vendors',
        section_dpias: 'DPIAs',
        section_consent: 'Consent Policies',
        section_data_mapping: 'Data Mapping',
        section_incidents: 'Incidents',
        section_classification: 'Data Classification',
        section_governance: 'Governance',
        open: 'Open',
        closed: 'Closed',
        sla_compliance: 'SLA Compliance',
        total: 'Total',
        expired_dpa: 'Expired DPA',
        high_risk: 'High Risk',
        completed: 'Completed',
        overdue: 'Overdue',
        policies_active: 'Active Policies',
        coverage: 'Coverage',
        systems: 'Systems',
        completeness: 'Completeness',
        avg_resolution_days: 'Avg. Resolution (days)',
        labeled: 'Labeled',
        unlabeled_systems: 'Unlabeled Systems',
        compliance_score: 'Compliance Score',
        drift_events_open: 'Open Drift Events',
        no_data: 'No data available for the selected period.',
        error_date_range: 'Please select both start and end dates.',
        table_category: 'Category',
        table_metric: 'Metric',
        table_value: 'Value',
        showing_chart_view: 'Showing chart view',
        showing_table_view: 'Showing data table view',
        error_generate: 'Failed to generate board report.',
        // Sprint 41.2.0 E5 — playbook execution evidence summary
        playbook_evidence_title: 'Playbook Execution Evidence',
        playbook_evidence_description: 'Per-playbook execution activity and attached evidence files for the reporting period.',
        playbook_name: 'Playbook',
        playbook_execution_count: 'Executions',
        playbook_steps_total: 'Steps',
        playbook_steps_required: 'Evidence Required',
        playbook_steps_attached: 'Evidence Attached',
        playbook_coverage_pct: 'Coverage',
        playbook_evidence_files: 'Evidence Files',
        playbook_no_evidence: 'No playbook executions in this period.',
        playbook_download_file: 'Download file',
    },
    framework_module_links: {
        title: 'Framework Linkage',
        modules: 'Linked Modules',
        add_module: 'Add Module',
        module_code: 'Module Code',
        required: 'Required',
        notes: 'Notes',
        link: 'Link',
        no_modules: 'No modules linked',
        personas: 'Linked Personas',
        add_persona: 'Add Persona',
        persona_id: 'Persona ID',
        role_description: 'Role Description',
        no_personas: 'No personas linked',
    },
    framework_channels: {
        title: 'Submission Channels & Deadlines',
        channels: 'Submission Channels',
        add_channel: 'Add Channel',
        channel_type: 'Type',
        name: 'Name',
        instructions: 'Instructions',
        active: 'Active',
        no_channels: 'No submission channels configured',
        portal: 'Portal',
        in_person: 'In Person',
        postal: 'Postal',
        email: 'Email',
        add: 'Add',
        deadlines: 'Regulatory Deadlines',
        add_deadline: 'Add Deadline',
        label: 'Label',
        days: 'Days',
        day_type: 'Day Type',
        calendar_days: 'Calendar Days',
        working_days: 'Working Days',
        description: 'Description',
        no_deadlines: 'No deadlines configured',
    },
    regulatory_updates: {
        title: 'Regulatory Updates & Framework Dashboard',
        coverage_dashboard: 'Framework Coverage Dashboard',
        no_frameworks: 'No frameworks found',
        framework: 'Framework',
        status: 'Status',
        modules: 'Modules',
        templates: 'Templates',
        forms: 'Forms',
        channels: 'Channels',
        deadlines: 'Deadlines',
        pending: 'Pending Updates',
        updates: 'Regulatory Updates',
        create_update: 'Create Update',
        framework_id: 'Framework ID',
        update_title: 'Title',
        description: 'Description',
        effective_date: 'Effective Date',
        create: 'Create',
        no_updates: 'No regulatory updates',
        impact: 'Impact',
        assess: 'Assess',
    },
    module_tips: {
        show_guide: 'Show guide',
        hide_guide: 'Hide guide',
        title: 'Module Guide',
        title_prefix: 'Module tip:',
        loading_aria: 'Loading module tip',
        related_frameworks: 'Related frameworks',
        quick_start_label: 'Quick start',
        related_forms_note: 'Related compliance forms are available below — look for the Mẫu số badge to identify official Vietnamese regulatory forms.',
        read_full_guide: 'Read full guide',
        view_full_requirements: 'View full requirements',
        relevant_for: 'Relevant for',
        key_procedure: 'Key procedure',
        deletion_orchestration: 'Coordinate data deletion requests across systems and third-party vendors with verification workflows and audit trails.',
        rights_requests: 'Manage data subject rights requests including access, deletion, correction, and portability. Required under PDPL Law 91/2025 and GDPR.',
        consent: 'Manage consent collection policies and workflows. Required for PDPL, E-Commerce Law, and GDPR compliance.',
        consent_properties: 'Configure consent collection points and CMP integration properties for web and app consent tracking.',
        data_mapping: 'Map data flows across your organization — processing activities, systems, and cross-border transfers.',
        data_mapping_registry: 'Maintain a per-field inventory of every personal-data element your organization holds. Attach each element to a Data Category, override sensitivity or retention when needed, and import from regulatory register submissions (PDPL personal-data inventory) without losing operator edits.',
        data_mapping_ropa_completeness: 'Per-field hygiene audit of every Processing Activity against the framework’s required ROPA fields (PDPL, GDPR, Decree 165). Identify critical / high / conditional misses before regulatory submission and jump straight into the activity editor to fix each one.',
        assessments: 'Create and manage Data Protection Impact Assessments (DPIAs) and AI system risk assessments. Required under PDPL Law 91/2025 Article 24.',
        classification: 'Classify and label data assets by sensitivity, category, and regulatory requirements.',
        incidents: 'Record and manage security incidents and data breaches. Required for breach notification under PDPL and Cybersecurity Law.',
        incident_ops: 'Coordinate incident response operations with playbook activations, escalation paths, and real-time coordination dashboards.',
        monitoring: 'Monitor compliance KPIs, program health, and task completion rates across your organization.',
        vendor_risk: 'Assess and manage vendor risk through questionnaires, risk portfolios, and compliance scoring.',
        governance: 'Manage vendor governance, playbooks, and third-party compliance requirements.',
        program_governance: 'Configure program governance maturity assessments, dashboard metrics, and compliance scoring frameworks.',
        discovery: 'Discover and inventory data assets across your organization with automated scanning and staging workflows.',
        discovery_cycles: 'Schedule and run recurring discovery cycles that re-scan systems, surface delta changes, and route findings through review, approval, and publishing.',
        connectors: 'Configure integrations and data connectors for automated compliance data collection.',
        task_management: 'Manage compliance tasks, assignments, and deadlines across teams and modules.',
        audit_trail: 'View and search the unified audit trail for all compliance activities and evidence collection.',
        evidence_packs: 'Assemble, verify, and submit audit evidence packs for regulatory compliance. Evidence packs collect audit events by framework, entity type, and time period into exportable bundles for internal review or regulatory submission.',
        audit_periods: 'Define and manage audit reporting periods per regulatory framework. Audit periods set the time boundaries and retention policies for evidence collection and compliance reporting cycles.',
        qs_evidence_pack: 'Assemble your first evidence pack',
        qs_audit_period: 'Create an audit reporting period',
        compliance_forms: 'Browse the compliance form catalog and create form instances for regulatory filings.',
        partner_workspace: 'Manage partner and reseller workspaces with scoped access, compliance review workflows, and collaboration tools.',
        localization: 'Manage regulatory frameworks, language packs, and localization settings.',
        onboarding: 'Track onboarding progress, complete quick starts, and follow persona-specific compliance paths.',
        organizations: 'Manage organization structure, departments, and multi-org configurations.',
        privacy_scanner: 'Monitor website privacy compliance with automated scanning and issue detection.',
        access_accountability: 'Configure access review campaigns and accountability policies for data access governance.',
        dashboard: 'View the compliance dashboard overview with real-time status summaries, framework coverage, and key compliance metrics.',
        platform: 'Manage platform security settings including API keys, break-glass emergency access, and SSO configuration.',
        framework_vn_pdpl: 'VN Personal Data Protection Law (PDPL)',
        framework_vn_cybersecurity: 'VN Cybersecurity Law',
        framework_vn_ecommerce: 'VN E-Commerce Law',
        framework_vn_ai_law: 'VN AI Law',
        framework_vn_telecom: 'VN Telecom Law',
        framework_vn_banking_circular_83: 'VN Banking Circular 83/2025/TT-NHNN — Internal Control System',
        framework_gdpr: 'EU GDPR',
        cross_framework: 'Cross-framework — supports all regulatory compliance activities',
        qs_dpia_filing: 'Complete your first DPIA filing',
        qs_dept_compliance: 'Submit your first department compliance section',
        qs_ecommerce_consent: 'Configure e-commerce consent collection',
        qs_telecom_data: 'Set up telecom subscriber data handling',
        qs_data_flow: 'Map your first data flow',
        qs_incident_response: 'Configure incident response workflow',
        qs_ai_risk: 'Run an AI system risk assessment',
        qs_cross_border: 'Assess a cross-border data transfer',
        qs_breach_notification: 'Simulate a breach notification workflow',
        qs_audit_trail: 'Review a compliance audit trail',
        procedure_data_deletion_fulfillment: 'Data Deletion Fulfillment',
        procedure_cross_system_deletion: 'Cross-System Deletion',
        procedure_dsar_intake: 'Data Subject Access Request Intake',
        procedure_cmp_property_config: 'CMP Property Configuration',
        procedure_consent_collection: 'Consent Collection Policy',
        procedure_ropa_filing: 'Record of Processing Activities Filing',
        procedure_cross_border_assessment: 'Cross-Border Data Transfer Assessment',
        procedure_data_classification: 'Data Classification',
        procedure_breach_notification: 'Breach Notification Process',
        procedure_incident_timeline: 'Incident Timeline Tracking',
        procedure_playbook_escalation: 'Playbook Escalation',
        procedure_compliance_kpi_monitoring: 'Compliance KPI Monitoring',
        procedure_vendor_risk_assessment: 'Vendor Risk Assessment',
        procedure_transfer_mechanism_review: 'Transfer Mechanism Review',
        procedure_program_maturity: 'Program Maturity Assessment',
        procedure_data_discovery: 'Data Asset Discovery',
        procedure_connector_setup: 'Connector Setup and Configuration',
        procedure_task_assignment: 'Task Assignment and Delegation',
        procedure_audit_evidence: 'Audit Evidence Collection',
        procedure_form_submission: 'Compliance Form Submission',
        procedure_partner_onboarding: 'Partner Onboarding Workflow',
        procedure_partner_access: 'Partner Access and Permissions',
        procedure_framework_pack_install: 'Framework Pack Installation',
        procedure_language_pack_deploy: 'Language Pack Deployment',
        procedure_org_onboarding: 'Organization Onboarding Setup',
        procedure_multi_org_config: 'Multi-Organization Configuration',
        procedure_privacy_scan: 'Privacy Compliance Scan',
        procedure_access_review: 'Access Review Campaign',
        procedure_dpia_filing: 'DPIA Filing Process',
        security: 'Platform security configuration: API key management, session policies, MFA enforcement, SSO integration, IP allowlists, and break-glass emergency access controls.',
        settings: 'Platform-wide configuration: notification preferences, domain management, export schedules, and module configuration for your organization.',
        users: 'User account management: create and manage user accounts, assign roles, configure department membership, and track login activity.',
        roles: 'Role and permission management: define custom roles, configure granular permissions per module, and enforce separation of duties policies.',
        profile: 'Your personal profile settings: display name, contact details, language preference, module guide preferences, accessibility options, and password management.',
        consent_properties_bindings: 'Manage consent policy bindings across digital properties. Track which policy version is deployed to each property, identify outdated bindings, and coordinate policy rollouts.',
        consent_properties_templates: 'Golden template management: define standard consent configurations, compare properties against templates, and roll out template changes across property groups.',
        consent_properties_compliance: 'Consent compliance monitoring: detect configuration inconsistencies between properties and golden templates, manage change requests through approval workflows, and generate evidence packs for audit readiness.',
        consent_properties_operations: 'Operational management for consent properties: configure automated snapshot schedules, organize properties into groups, and review the consent property audit trail.',
        consent_properties_audit: 'Consent property audit and analytics: review configuration change history, analyze property compliance trends, and generate compliance reports for regulatory review.',
        classification_taxonomies: 'Define and manage data classification taxonomies with sensitivity and importance dimensions. Configure classification levels, handling requirements, and AI risk tiers.',
        classification_labeling: 'Apply classification labels to data assets. Submit labels for review, manage bulk classification, and track labeling status across your data inventory.',
        classification_lifecycle: 'Track the lifecycle of classified data assets through stages: draft, active use, under review, archival, and pending deletion. Monitor retention periods and trigger deletion workflows.',
        classification_gaps: 'Detect and manage classification gaps across your data inventory. Run gap detection to identify unlabeled assets and missing lifecycle assignments, then track remediation.',
        classification_reviews: 'Manage classification review tasks with escalation timers. Review proposed labels, handle exceptions, and ensure timely classification decisions.',
        incidents_breach: 'Manage data breach notifications and authority reporting. Create notification packages, track regulatory deadlines, and document the complete breach response timeline.',
        incidents_authority: 'Handle formal authority requests from regulatory bodies. Track incoming inquiries, assemble evidence packages, manage verification checklists, and meet response deadlines.',
        incidents_disclosure: 'Maintain the centralized disclosure register for all communications with regulatory authorities. Record disclosure dates, channels, recipients, and content summaries for audit trail compliance.',
        incidents_reports: 'Generate and manage incident reports for internal review and regulatory submission. Create detailed incident summaries, track report status, and export documentation for authority filings.',
        incidents_tasks: 'Coordinate incident response tasks across teams. Create task checklists, assign owners, track priorities and dependencies, escalate blocked items, and monitor completion progress.',
        incidents_supplements: 'Manage supplementary documentation requested by regulatory authorities after initial breach notification. Track supplement requests, prepare response packages, and monitor submission deadlines.',
        incidents_remediation: 'Track corrective actions assigned to departments and users to address the root cause and impact of incidents. Monitor implementation deadlines, verify completion, and document remediation evidence.',
        incident_ops_playbooks: 'Create and manage incident response playbooks. Define escalation paths, assign response teams, and activate playbooks during live incidents.',
        incident_ops_sla: 'Track incident response SLAs and regulatory deadlines. Monitor compliance with statutory notification timelines and measure response performance metrics.',
        incident_ops_evidence: 'Maintain incident evidence chains and conduct post-incident reviews. Generate regulator-ready export packages with complete incident documentation.',
        data_mapping_systems: 'Register and manage systems that process personal data. Document system ownership, data categories, processing purposes, and compliance scoring.',
        data_mapping_flows: 'Map data flows between systems, including cross-border transfers. Document data movement paths, transfer mechanisms, and recipient jurisdictions.',
        data_mapping_activities: 'Document processing activities and generate Records of Processing Activities (RoPA). Link activities to legal bases, purposes, and data categories.',
        data_mapping_governance: 'Manage data governance assignments, attestation cycles, and risk assessments. Track governance ownership and review schedules across your data inventory.',
        data_mapping_reference: 'Manage reference data for data mapping: data categories, processing purposes, retention policies, and export configurations.',
        data_mapping_categories: 'Manage the data category catalog: names, sensitivity tiers, examples, and tags. Foundation for every classification and mapping activity.',
        data_mapping_purposes: 'Catalog processing purposes with their legal basis. Used by ROPA, DPIA, and lawfulness audits.',
        data_mapping_retention: 'Configure retention policies, disposal methods, and review cadence. Aligns with Decree 13/2023 and Decree 356/2025 requirements.',
        data_mapping_exports: 'Export ROPA, systems, purposes, and categories to CSV, XLSX, PDF, or DOCX. All exports land in the platform File Browser.',
        data_mapping_attestation: 'Schedule and monitor data attestation cycles. Track attestation completion across your data inventory.',
        data_mapping_review: 'Classification review queue: review tasks, quality signals, and approval status.',
        data_mapping_risk: 'Risk assessment for processing activities: scoring frameworks, mitigation plans, and effectiveness tracking.',
        data_mapping_evidence: 'Evidence tied to processing activities: security certificates, DPA contracts, DPIA reports.',
        data_mapping_inventory: 'Classification inventory overview: tags, sensitivity tiers, and organisation-wide distribution.',
        data_mapping_exceptions: 'Exception register for classifications: rationale, approver, validity window, and remediation path.',
        data_mapping_history: 'Classification change history: who changed what, when, and why.',
        access_accountability_logs: 'Real-time stream of privileged access events. Pair with the policies page to define who can reach each data zone; pair with investigations to follow up on anomalies.',
        access_accountability_policies: 'Access-zone policy authoring: declare which roles and users may reach each data zone, with time-bounded exceptions. Drives the enforcement checks that surface in the logs.',
        access_accountability_anchoring: 'Tamper-evident anchoring of the access log to a hash chain. Required for VN Cybersecurity Law audit trail integrity.',
        access_accountability_investigations: 'Case workspace for investigating suspicious access events. Timeline, linked logs, evidence packs, and case closure workflow in one view.',
        assessment_triggers: 'Rules that auto-open a DPIA or dossier filing when upstream changes (new processing activity, new vendor, new cross-border flow) meet the trigger condition.',
        connectors_gap_register: 'Catalog of unimplemented or partial connectors. Triage items, assign owners, track gap closure over the roadmap.',
        connectors_system_inventory: 'Inventory of every system reachable via a connector. Single source of truth for the data map.',
        connectors_traceability: 'End-to-end lineage of a data element across systems linked through connectors. Essential for ROPA accuracy and subject-request fulfilment.',
        consent_properties_change_requests: 'Pipeline of proposed changes to a consent property (added purpose, new language, updated policy link). Governance review before production rollout.',
        consent_properties_comparison: 'Side-by-side comparison of the consent property shape across domains or deployments. Flags drift that would weaken enforceability.',
        governance_playbooks: 'Playbook templates driving repeatable governance workflows (vendor onboarding, DPA review, transfer assessment).',
        incident_ops_coordination: 'Cross-team coordination dashboard for an active incident. One place to see who is on it, what they are doing, and what is blocked.',
        incident_ops_escalation: 'Escalation log for active incidents. Severity transitions, stakeholder notifications, and executive briefs all recorded here.',
        incident_ops_post_review: 'Post-incident review: root-cause analysis, timeline, remediation actions, and lessons learned. Feeds into training and playbook updates.',
        incident_ops_regulator_exports: 'Generate regulator submission packages for notifiable incidents. Mẫu số 08 (breach) + supporting evidence bundle.',
        incident_ops_vendor_coordination: 'Coordinate response with the vendor whose system was breached. Contract obligations, response SLAs, and shared evidence chain.',
        monitoring_authority_readiness: 'Readiness checklist and evidence pack for upcoming regulator visits. Consolidated view of outstanding gaps and completed controls.',
        monitoring_change_review: 'Periodic review of control changes across the monitoring program. Detects drift before it becomes a compliance gap.',
        monitoring_framework_link: 'Link monitoring controls to specific regulatory framework articles. Generates the evidence map regulators expect.',
        monitoring_ric: 'Regulatory Inquiry and Correspondence cases. Track questions and responses from the regulator across time.',
        forms: 'Regulatory form catalog, form filler, form instances, and filing case links. Backbone for Mẫu số 02a/02b and related submissions.',
        monitoring_tasks: 'Monitoring task queue: scheduled control verifications, attestations, and evidence refreshes.',
        partner_workspace_reports: 'Reports shared across the partner network: compliance health, monthly summaries, cross-org metrics.',
        partner_workspace_tickets: 'Support tickets raised between partners on shared workflows and escalations.',
        program_governance_audit_readiness: 'Audit-readiness scoring with drill-down into missing evidence, gap register, and remediation owners.',
        program_governance_drafting: 'Document drafting workspace with versioning, review routing, and approval workflow.',
        program_governance_evidence_gaps: 'Evidence-gap register: which controls lack current evidence and when the refresh is due.',
        program_governance_executive_summaries: 'Executive summary templates with auto-populated KPIs for board and executive briefings.',
        program_governance_health: 'Overall program health dashboard: maturity, coverage, and trend indicators.',
        program_governance_issuance: 'Document issuance workflow: approval, publication, stakeholder notification.',
        program_governance_model: 'Governance operating model: roles, responsibilities, committees, and decision rights.',
        program_governance_remediation: 'Remediation plans tied to identified gaps. Owner, deadline, status, and verification evidence.',
        program_governance_review_cadence: 'Scheduled review cadence for governance artefacts: policies, procedures, controls, evidence.',
        program_governance_trends: 'Trend dashboard: maturity over time, evidence freshness, gap closure velocity.',
        task_management_conflicts: 'Detect conflicting task assignments (double-booked owners, overlapping deadlines) and resolve via reassignment.',
        task_management_consolidation: 'Consolidate tasks auto-generated by multiple modules into a single workflow for the assignee.',
        localization_dashboard: 'Localization module overview: framework catalog health, pack status, translation coverage, and pending regulatory updates in one dashboard.',
        localization_frameworks: 'Regulatory framework catalog: PDPL, Decree 13/2023, Decree 356/2025, Cybersecurity Law, and GDPR. Each framework links articles to platform controls.',
        localization_framework_channels: 'Regulator submission channels linked to each framework (MPS portal, MIC endpoint, authority email). Drives the routing of Mẫu số filings.',
        localization_framework_detail: 'Single-framework detail view: articles, procedures, personas, linked forms, and implementation depth across the platform.',
        localization_framework_module_links: 'Framework → module coverage map. Which regulatory articles are enforced by which platform module.',
        localization_language_packs: 'Language pack catalog across 16 locales: install status, translation completeness, and pack submission history.',
        localization_audit: 'Audit trail for localization changes: framework edits, pack updates, submission events.',
        localization_market_packs: 'Market pack catalog bundling framework + language + forms per market (VN, EU member states, etc.).',
        localization_module_mappings: 'Module mapping register: which framework article applies to which module at the feature level.',
        localization_pack_detail: 'Single pack detail view: version, frameworks included, translation state, installation history.',
        localization_pack_submissions: 'Pack submission pipeline: partner submissions, review states, publication approvals.',
        localization_regulatory_updates: 'Regulatory update feed: new decrees, amendments, and guidance impacting the framework catalog.',
        localization_sector_overlays: 'Sector overlay catalog: optional industry-specific regulatory packs (e.g. banking, healthcare). Install in Direct mode for in-scope organizations or Reference mode for cross-sector visibility.',
        localization_help_content_packs: 'Help-Content Pack manager: install, version, and translate the in-app help articles, module guides, and quick-starts that surface across the admin UI.',
        localization_translation_preview: 'Live preview of UI strings across locales. Side-by-side comparison for translation QA.',
        organizations_groups_create: 'Create a new organization group (koncern): pick root org, select members, define inheritance mode per category.',
        organizations_groups_detail: 'Single org group detail: members, inheritance matrix, shared resources, and audit summary.',
        organizations_groups_settings: 'Org group settings: SLA, calendar, notifications, intake, CMP integration. Per-category inherit/override toggle.',
        organizations_groups_audit: 'Per-group audit log: membership changes, inheritance toggles, shared-resource add/remove.',
        organizations_groups_consolidated_audit: 'Consolidated audit view across all member orgs of a group. Filter by entity, actor, time window.',
        organizations_groups_dashboard: 'Cross-member dashboard: compliance health, open items, SLA breaches rolled up for the whole group.',
        organizations_groups_reports: 'Group-level reports: executive summary, regulator-facing rollup, cross-member KPI comparison.',
        organizations_groups_users: 'Users visible across the group: cross-org role assignments, shared administrators, impersonation chain.',
        organizations_groups_merged_vendors: 'Deduplicated vendor view across member orgs — a single vendor appears once even if onboarded separately.',
        organizations_groups_shared_templates: 'Form templates published at the group level. Members opt in to inherit a template; opt-out is per-org.',
        organizations_groups_shared_frameworks: 'Regulatory frameworks shared across the group. Central update flows to every member with mode=inherit.',
        organizations_groups_shared_vendors: 'Group-level vendor registry. Contract, DPA status, and risk score visible to every member.',
        onboarding_help_center: 'Help Center: browse all 309+ articles by module, category, and persona. Full-text search across EN + VI content.',
        onboarding_progress: 'Onboarding progress tracker: which go-live steps are complete, which are pending, who owns the blockers.',
        onboarding_personas: 'Persona-driven onboarding journeys for the 10 VN roles (P-VN-01 through P-VN-10). Each journey is a curated list of first actions.',
        onboarding_quick_starts: 'Quick-start scenarios: run a complete compliance workflow end-to-end in the sandbox. Ideal for training.',
        onboarding_sample_data: 'Sample data generator: load realistic demo rows into any module. Useful for sales demos and training.',
        onboarding_tours: 'Interactive guided tours. Launch a module-specific walkthrough that narrates each page as you navigate.',
        security_sessions: 'Live view of authenticated sessions: device, IP, last activity, MFA factor used. Terminate with one click.',
        security_api_keys: 'Service-account API keys: scope, rotation, last-used, and per-key audit trail.',
        security_break_glass: 'Emergency break-glass access: time-bounded super-admin grant with mandatory audit trail and post-event review.',
        security_delegations: 'Temporary role delegations: out-of-office coverage, approval chains, automatic expiry.',
        security_ip_allowlist: 'IP allowlist configuration: restrict admin login to specific corporate networks or VPN ranges.',
        security_mfa: 'Multi-factor authentication policy: enforcement per role, factor allowlist (TOTP/SMS/WebAuthn), reset flow.',
        security_service_accounts: 'Service accounts for system-to-system integration. Scoped, audited, and rotated separately from human users.',
        security_session_settings: 'Session policy: idle timeout, max session age, concurrent-session limit, re-authentication triggers.',
        security_sso: 'Single sign-on configuration: SAML / OIDC providers, attribute mapping, auto-provisioning rules.',
        rights_requests_create: 'Create a new data-subject rights request case. Captures requester identity, request type, channel, and SLA clock start.',
        rights_requests_list: 'All rights-request cases with SLA countdown, assignee, status, and filter chips for channel + request type.',
        rights_requests_detail: 'Single case workspace: correspondence, fulfillment actions, evidence, response drafting, SLA transitions.',
        rights_requests_cmp_sync: 'CMP integration sync settings: webhook endpoint, auto-case creation rules, retry policy.',
        rights_requests_extensions: 'SLA extension requests: justification, approver routing, and audit-trail entry at grant/decline.',
        rights_requests_fulfillment: 'Request fulfillment workspace: gather data packages, apply redactions, package for delivery.',
        rights_requests_responses: 'Response drafting with template insertion, merge fields, and side-by-side EN/VI preview.',
        rights_requests_response_templates: 'Response template catalog: approved templates per request type + jurisdiction, with version history.',
        compliance_forms_consolidation_create: 'Create a new consolidation case: group related form submissions under a single filing package destined for one regulator.',
        compliance_forms_consolidation_detail: 'Single consolidation case workspace: section inputs, reviewer approvals, export of the complete dossier to the regulator.',
        compliance_forms_consolidation_cases: 'All consolidation cases with filing status, reviewer assignments, and deadline tracking.',
        compliance_forms_consolidation_review: 'Review workspace for consolidation cases: approve section inputs, request revisions, sign off before export.',
        compliance_forms_dossier_export: 'Export the consolidated dossier as a regulator-ready PDF bundle with cover letter, annexes, and signatures.',
        compliance_forms_section_input: 'Department owner workspace for completing a single section of a consolidation case.',
        compliance_forms_filing_case_links: 'Links between form instances and filing cases. Ensures every filing is backed by the right form data.',
        compliance_forms_section_routing: 'Section routing rules: which department owns which section of each form template.',
        assessments_dashboard: 'Assessments dashboard: DPIA count, transfer assessments, gap summary, upcoming review deadlines.',
        assessments_dossier_filing_detail: 'Single dossier filing: form type (Mẫu số 02a/02b), section assignments, section status, submission details, MPS timers.',
        assessments_dossier_filing_list: 'All dossier filings with form type, status, and section-assignment progress. Filter by status and framework.',
        assessments_dpia_list: 'DPIA register: all in-progress, approved, and archived Data Protection Impact Assessments.',
        assessments_gap_report: 'Gap Report: DPIA coverage, unclassified DPIA requirement, unmitigated risks, missing filings. Primary audit-readiness surface.',
        assessments_transfer_list: 'Transfer assessments for cross-border data flows. Each assessment documents mechanism, safeguards, and risk rating.',
        deletion_orchestration_templates: 'Checklist templates for deletion runs. Each template is a scripted sequence of tasks tied to specific systems and retention rules.',
        deletion_orchestration_dsr_integration: 'DSR-to-deletion integration: auto-trigger a deletion run from an approved erasure request.',
        deletion_orchestration_tasks: 'Execution tasks for active deletion runs. Each task tracks system, action, owner, and verification evidence.',
        deletion_orchestration_dashboard: 'Deletion orchestration dashboard: active runs, completion rate, pending verifications, proof-chain freshness.',
        deletion_orchestration_proof_chain: 'Tamper-evident proof chain for every deletion action. Required for PDPL compliance on erasure.',
        organizations_compliance_rollup: 'Compliance rollup across entities: KPI by entity, framework-level coverage, outstanding obligations.',
        organizations_departments_admin: 'Admin workspace for departments: create, reassign owners, manage team membership, set routing rules.',
        organizations_departments: 'Departments list across entities. Drives section-routing, approval flows, and evidence attribution.',
        organizations_entity_frameworks: 'Assign regulatory frameworks to each entity. Drives which Mẫu số forms apply and which audits are mandatory.',
        organizations_entity_form: 'Create or edit a single organization entity: legal name, business registration, address, representative.',
        organizations_list: 'All organization entities across the tenant. Primary list view with status filters and quick-access actions.',
        organizations_retention_policies_admin: 'Retention policy administration scoped to the organization: default rules, per-entity overrides.',
        task_management_calendar: 'Calendar view of tasks and deadlines. Color-coded by module and SLA state.',
        task_management_my_tasks: 'Personal task queue: assigned to me, due soon, overdue. Primary daily workspace.',
        task_management_overdue: 'Overdue tasks across the organization. Filters by owner, module, and days-overdue bucket.',
        task_management_section_assignments: 'Section-assignment overview: which department owns which section of each active form filing.',
        task_management_board: 'Kanban board view of tasks. Drag between columns (To-Do / In-Progress / Review / Done) to update status.',
        task_management_detail: 'Single task workspace: description, comments, attachments, sub-tasks, audit trail, status transitions.',
        task_management_list: 'Flat list of all tasks with filters: owner, module, status, priority, due-date range.',
        consent_cmp_sync: 'CMP sync workspace: trigger syncs, review sync history, inspect failures. Keeps CMP state aligned with the platform.',
        consent_cmp_bulk_sync: 'Bulk CMP sync: re-synchronise all consent records across all properties. Used for tenant migrations and large CMP changes.',
        consent_cmp_integration: 'CMP integration settings: endpoint, authentication, sync policy, and connection health.',
        consent_cmp_purpose_mapping: 'Map CMP purposes to platform purposes. Ensures consent signals flow correctly between the two systems.',
        consent_cmp_webhook: 'CMP webhook config: inbound event routing, signing secrets, retry policy.',
        partner_workspace_brand_profiles: 'Partner brand profiles: logo, tagline, contact, service offerings. Drives the partner marketplace listing.',
        partner_workspace_deliverable_templates: 'Deliverable template catalog: standardised service packages partners can offer (DPIA review, vendor audit, training).',
        partner_workspace_deliverables: 'Active deliverables across partner engagements. Track scope, due dates, acceptance criteria.',
        partner_workspace_service_tiers: 'Service tier definitions: Bronze / Silver / Gold with scope, SLA, and pricing bands.',
        partner_workspace_workflow_instances: 'Active workflow instances across partners: onboarding, review, escalation, offboarding runs.',
        partner_workspace_workflow_templates: 'Workflow template catalog for partner engagements. Each template defines steps, owners, SLAs, and outputs.',
        access_accountability_justification_reviews: 'Review access justifications submitted by users. Approve, challenge, or escalate per zone policy.',
        access_accountability_review_campaigns: 'Scheduled access-review campaigns: recurrence, scope, reviewer assignment, completion tracking.',
        access_reviews_list: 'All access-review campaigns with filters: cycle, status, reviewer, target system.',
        access_reviews_campaign_detail: 'Single campaign workspace: scope, reviewers, item queue, certification state, audit trail.',
        access_reviews_my_reviews: 'Reviews assigned to me: pending decisions with context, certify or revoke per item.',
        attestation_schedules_list: 'Attestation schedule registry: recurrence, scope, next-due date, completion rate per schedule.',
        attestation_schedules_create: 'Create a new attestation schedule: pick frequency, scope, attesters, and evidence requirements.',
        attestation_schedules_edit: 'Edit an existing attestation schedule. Changes apply to the next cycle; past cycles remain unchanged.',
        attestation_schedules_history: 'Historical completion timeline of an attestation schedule. Evidence artefacts linked per cycle.',
        governance_board_report: 'Board-ready compliance report: executive summary, score trends, outstanding risks, remediation status.',
        governance_compliance_score: 'Compliance score computation across frameworks. Drill-down into per-control contribution.',
        governance_drift_monitor: 'Detect control drift: settings changes, permission grants, policy exceptions that weaken posture.',
        governance_audit: 'Governance-specific audit log: workflow events, approval decisions, drift signals.',
        governance_evidence: 'Evidence catalog for governance controls. Supporting documents, screenshots, attestation receipts.',
        partner_workspace_escalation: 'Cross-partner escalation dashboard: open escalations, severity, SLA clocks, and next-action owners.',
        partner_workspace_audit: 'Audit log for partner workspace: engagement changes, deliverable updates, escalation transitions.',
        partner_workspace_partner_detail: 'Single partner organisation profile: engagement history, active deliverables, contacts, performance score.',
        partner_workspace_partner_list: 'Partner organisation registry: active, onboarding, and offboarded partners with service tier + performance badges.',
        platform_license: 'License settings: tier, seat counts, renewal status, feature toggles driven by the installed license.',
        platform_notifications_admin_queue: 'Admin notification queue: in-flight alerts, delivery failures, retry state across email/SMS/in-app channels.',
        platform_notifications_prefs: 'Per-user notification preferences: channel opt-in, digest schedule, muted topics.',
        settings_overview: 'Tenant settings overview: quick links to domains, email, exports, webhooks, and other platform-wide config.',
        settings_domains: 'Allowed domains for sign-up and SSO. Restricts account creation to verified corporate domains.',
        settings_email: 'Outbound email configuration: SMTP provider, sender identity, DKIM/SPF status, test-email tool.',
        settings_export_schedules: 'Scheduled exports: recurring CSV/XLSX/PDF generation delivered to File Browser on a cron schedule.',
        settings_webhooks: 'Outbound webhook endpoints: subscribe external systems to platform events. Signing, retry, audit of every delivery.',
        sod_rules_create: 'Create a Segregation-of-Duties rule: pick conflicting role pairs, scope, and enforcement mode (block/warn).',
        sod_rules_edit: 'Edit an existing SoD rule. Changes take effect immediately; existing violations are re-evaluated on next access.',
        sod_rules_list: 'All SoD rules across the tenant: enforcement mode, role pairs, exception count, violation count.',
        connectors_definitions: 'Connector type registry: definition of each integration (endpoint shape, auth, supported operations).',
        connectors_list: 'All connector instances wired to the tenant. Status, last sync, error log, sync-on-demand action.',
        connectors_credentials: 'Connector credentials vault: OAuth tokens, API keys, service-account passwords. Rotation schedule + audit.',
        connectors_event_bus: 'Connector event bus: inbound events from integrations, routing rules, replay dead-letter queue.',
        consent_change_awareness: 'Detect meaningful consent changes: purpose added, policy updated, scope widened. Drives re-consent campaigns.',
        consent_audit: 'Consent audit trail: every grant, update, withdrawal across workflows. Primary surface for regulator walkthroughs.',
        consent_reconciliation: 'Reconcile consent state across CMP, platform, and downstream systems. Flags discrepancies.',
        consent_withdrawal_propagation: 'Withdrawal propagation pipeline: when a subject withdraws consent, ensure it flows to every linked system.',
        consent_policies: 'Consent policy catalog: privacy policy versions, language variants, activation windows.',
        consent_purpose_catalog: 'Purpose catalog used by consent workflows. Each purpose links to a legal basis and a retention rule.',
        consent_snapshots: 'Consent snapshots: point-in-time capture of all active consents. Required for audits and legal holds.',
        consent_workflows: 'Consent workflow catalog. Each workflow defines triggers, collection flows, and downstream actions.',
        discovery_classification_lineage: 'Classification lineage: trace how a data element was classified across scans and manual review.',
        discovery_classification_rules: 'Classification rules registry: regex, vocabulary, ML patterns used to auto-classify discovered fields.',
        discovery_dedup_rules: 'Deduplication rules for discovered records. Prevents the same field from appearing multiple times.',
        discovery_import_templates: 'Import templates for bringing existing data catalogs into the Discovery module via CSV or external tools.',
        discovery_owner_review: 'Owner-review queue: suggested classifications awaiting data-owner approval or rejection.',
        discovery_staging: 'Staging area for newly discovered fields. Triage before promotion to the Data Mapping catalog.',
        discovery_suggestion_analytics: 'Analytics for the suggestion engine: accuracy, coverage, most-overridden classifications.',
        discovery_suggestion_queue: 'Review queue of suggested classifications before they are applied. Accept, modify, or reject per item.',
        compliance_forms_filler: 'Interactive form filler: step through a regulatory form with validation, autosave, and co-author support.',
        compliance_forms_instance_detail: 'Single form instance: current state, history, linked filing case, exports.',
        compliance_forms_outputs: 'Generated outputs per form instance: PDF, DOCX, JSON packages with cover letter and signatures.',
        compliance_forms_submission_packages: 'Submission package catalog: regulator-ready bundles with manifest, signatures, and delivery receipt.',
        incident_ops_regulatory_sla: 'Regulatory SLA configuration for incident notifications (VN Cybersecurity Law 72-hour window, PDPL 72-hour, GDPR 72-hour).',
        incident_ops_response_metrics: 'Incident response metrics: MTTD, MTTR, SLA compliance rate, escalation counts.',
        incident_ops_sla_dashboard: 'SLA dashboard: active incidents against their SLA clock, at-risk warnings, breach history.',
        incident_ops_sla_definitions: 'SLA definitions: severity tiers, per-tier response and resolution windows, framework citations.',
        monitoring_governance_raci: 'RACI matrix for governance controls: responsible, accountable, consulted, informed per control across the program.',
        monitoring_periods: 'Monitoring periods: annual/quarterly/monthly review cycles aligned to regulatory filing windows.',
        monitoring_programs_list: 'Monitoring program catalog: framework-aligned programs with scope, owner, cadence.',
        monitoring_task_templates: 'Task templates for monitoring cycles. Each template defines scope, assignee role, evidence requirements.',
        access_accountability_evidence_detail: 'Single evidence package detail: chain of custody, signatures, export options for regulator submission.',
        access_accountability_evidence_packages: 'Evidence package catalog for access-accountability: signed, tamper-evident bundles ready for audit.',
        access_reviews_role_analytics: 'Role analytics: role-usage density, entitlement creep, certification-decision trends.',
        access_accountability_pattern_analysis: 'Pattern analysis over access logs: unusual access times, geographical outliers, privilege escalation signals.',
        access_accountability_session_dashboard: 'Session dashboard: live authenticated sessions, session heatmap by hour/day, inactive-session alerts.',
        classification_exports: 'Classification exports: export the classification inventory as CSV/XLSX for external audit tools.',
        classification_discovery_cycles: 'Classification discovery cycles: scheduled scans that surface newly-added or changed data elements.',
        classification_enterprise_data_map: 'Enterprise data map: aggregated classification view across systems, departments, and sensitivity tiers.',
        connectors_automation_planning: 'Automation planning: define trigger → connector → action chains for event-driven data operations.',
        connectors_brd_capture: 'Business Requirements Document capture for new connector build-outs.',
        connectors_poc_scorecards: 'Proof-of-concept scorecards for evaluating new connector integrations against acceptance criteria.',
        discovery_connector_management: 'Discovery connector management: which scanners are installed, their health, and which systems they target.',
        discovery_scans_list: 'All discovery scans with status, coverage, and discoveries. Drill-down into per-scan findings.',
        governance_approvals: 'Governance approvals queue: pending decisions across vendor onboarding, DPA reviews, transfer assessments.',
        governance_offboarding: 'Vendor offboarding workflow: contract termination, data return/deletion, access revocation, final audit.',
        governance_periodic_review: 'Periodic vendor review: scheduled reassessment of risk, DPA currency, subprocessor list.',
        incident_ops_playbook_activations: 'Active playbook instances: which playbook is running on which incident, progress, outstanding tasks.',
        incident_ops_playbook_links: 'Playbook → incident linkage registry. Also links to regulator notification templates per playbook.',
        incident_ops_playbook_templates: 'Playbook template catalog: reusable incident-response scripts per incident type and severity.',
        organizations_groups_settings_inheritance: 'Settings inheritance matrix across group members. Per-category inherit/override toggle with audit trail.',
        organizations_intake_config_form: 'Create or edit an intake config: public-form URL path, rate limits, captcha, notification routing.',
        organizations_intake_configs_list: 'Intake config catalog: public-facing rights-request entry points per market/brand.',
        privacy_scanner_monitoring_dashboard: 'Privacy-scanner monitoring dashboard: property coverage, scan cadence, signals flagged per property.',
        privacy_scanner_monitoring_settings: 'Monitoring settings: scan frequency, threshold rules, alert routing, retention of scan results.',
        privacy_scanner_settings: 'Scanner settings: enabled scan categories, signature updates, AesirX scanner endpoint credentials.',
        privacy_scanner_real_time_scan: 'Real-time scan: trigger an ad-hoc scan and watch results stream in. Ideal for rapid verification.',
        privacy_scanner_scan_result_detail: 'Detail view of a single scan result: cookies, beacons, trackers found, classification, remediation actions.',
        privacy_scanner_results_archive: 'Archive of historical scan results. Filter by property, date range, signal type.',
        task_management_workflow_detail: 'Single workflow instance: all steps, owners, SLAs, branching logic, completion evidence.',
        task_management_workflow_list: 'All workflow instances across modules: status, owner, deadline, origin module.',
        task_management_workflow_templates: 'Workflow template catalog: reusable multi-step workflows that can be instantiated from any source module.',
        audit_trail_classic: 'Classic audit trail view (legacy). Being deprecated in favour of the unified audit module.',
        audit_trail_unified: 'Unified audit trail across every module. Primary audit surface.',
        iat_settings: 'Configure the Immutable Audit Trails add-on: enablement, scope, default proof mode, taxonomy overrides, caps, and AesirX credential rotation. Read the canonical module guide first; the deep companion is "Enabling and Configuring".',
        iat_status: 'Browse every proof_link with status, mode, module, and date filters. Click any badge to open the proof drawer. Deep companion: "Understanding Proof Status".',
        iat_integrity_center: 'Run drift-detection verification passes over a date range; export the Integrity Pack for any audit scope. Deep companion: "Using the Integrity Center".',
        iat_usage: 'Track current balance, monthly consumption, cap state, and redeem credit packages. Deep companion: "Credits and Caps".',
        classification_exceptions: 'Classification exception queue: records flagged as edge cases, awaiting manual review.',
        classification_review_tasks: 'Classification review task queue: auto-suggested classifications awaiting owner confirmation.',
        consent_properties_audit_log: 'Audit trail for consent property changes: who updated what, when, old/new values.',
        consent_properties_analytics: 'Consent property analytics: consent capture rate, withdrawal rate, purpose mix trends.',
        consent_properties_evidence_packs: 'Evidence packs for consent properties: regulator-ready bundles proving consent capture compliance.',
        consent_properties_inconsistencies: 'Consent property inconsistencies: properties where the platform and CMP disagree on state.',
        consent_properties_golden_templates: 'Golden consent-banner templates: approved, reviewed, governance-ready banner shapes deployed across properties.',
        consent_properties_template_rollouts: 'Template rollout pipeline: stage, pilot, production waves for pushing golden templates to properties.',
        consent_properties_property_groups: 'Property groups: cluster related properties for governance and reporting rollups.',
        consent_properties_snapshot_schedules: 'Snapshot schedules: automate periodic consent-state captures for audit and legal-hold purposes.',
        deletion_orchestration_completeness: 'Completeness tracker: verifies every system in the retention scope confirmed deletion.',
        deletion_orchestration_runs_list: 'All deletion runs: status, duration, systems covered, completion rate.',
        deletion_orchestration_vendor_requests: 'Outbound deletion requests to vendors: tracking, SLA, verification artefacts.',
        deletion_orchestration_vendor_verification: 'Vendor verification workflow: confirm that vendor systems have completed erasure per our request.',
        compliance_forms_catalog: 'Compliance form catalog: every regulatory form (Mẫu số, GDPR Art 30, ISO 27701) available for filing.',
        compliance_forms_template_browser: 'Template browser: discover approved form templates across frameworks.',
        governance_cross_border: 'Cross-border transfer registry at governance level: mechanisms, safeguards, country risk assessment.',
        governance_subprocessors: 'Subprocessor registry: who our vendors use, DPA cascade, notification of changes.',
        incidents_detail: 'Single incident workspace: timeline, owner, severity, remediation, regulator-notification status.',
        incidents_list: 'Incident register: all active + historical incidents with filters for severity, channel, framework.',
        partner_workspace_partner_dashboard: 'Partner dashboard: top-level KPIs for all partner engagements, escalation counts, health trend.',
        partner_workspace_support_tickets: 'Partner support ticket catalog. Shared between partner and tenant teams with SLA tracking.',
        program_governance_acknowledgment_tracking: 'Document acknowledgment tracking: who has read and acknowledged each policy or procedure.',
        program_governance_change_log: 'Document change log: every edit to a governance document with diff and approval chain.',
        roles_detail: 'Single role detail: permissions bound to this role, users assigned, audit of membership changes.',
        roles_list: 'Role catalog: every built-in and custom role with member count and permission scope.',
        consent_properties_cross_property_dashboard: 'Cross-property dashboard: consent health across every property. Aggregates capture rate, withdrawal rate, drift indicators.',
        consent_properties_property_registry: 'Property registry: every property the tenant manages with its consent banner state, linked CMP, policy version.',
        // Sprint 47.7.4 AC-6 — Forseti module-tip summaries (EN+VI; 14-locale fan-out → 50.10.0).
        forseti_projects_overview: 'Forseti workspaces: a project groups documents, conversations, workflow runs, and tabular reviews into one container so the AI advisor keeps context across the whole engagement.',
        forseti_workflows_list: 'Saved AI prompt templates that produce tasks and draft evidence on demand. Use built-in pack workflows or author your own; runs are charged against your monthly token bucket.',
        forseti_tabular_reviews_list: 'Repeatable, column-by-column review of a document corpus. Define the questions once, run the same review across many attachments, and get a side-by-side answer grid with citations.',
        forseti_document_review_index: 'Triage inbox for documents with AI-suggested edits or Forseti-sourced versions. Documents enter the queue from chats, workflow runs, or the "Send a document for review" CTA on this page.',
        forseti_audit_lineage: 'Trace every AI-generated artefact attached to any record: chats, tool calls, tasks, evidence drafts, and document versions. Required for evidence defensibility and post-action audits.',
        forseti_draft_evidence: 'AI-drafted evidence pending human review. Every draft must be approved by an authorized reviewer before it becomes formal evidence — Forseti never auto-files.',
        forseti_usage: 'Token consumption and bucket status for this organisation. Tracks monthly billable units, remaining bucket, and recent activity by user / feature / tool.',
        // Sprint 47.7.4.2 AC-8 — Forseti chat surface module-tip summaries.
        forseti_overview: 'Entry point to the Forseti AI Legal Advisor: pick a persona, browse recent chats, and start a new conversation. Persona selection here sets the default voice for new chats.',
        forseti_chat_detail: 'Single Forseti conversation: view the message history, switch personas mid-chat, send new messages, and export the transcript as PDF for evidence purposes.',
        forseti_cross_framework_conflicts: 'Curated catalog of conflicts between the regulatory packs your organisation has installed. Filter by framework / topic / severity / acknowledgement status, click a row to see side-by-side framework positions and acknowledge / assign / resolve. Forseti surfaces the conflicts; humans decide.',
        users_detail: 'Single user profile: identity, role, MFA, session history, audit trail.',
        users_list: 'User directory: all active, invited, and deactivated users with quick-filters and bulk actions.',
        governance_vendors: 'Manage vendor governance lifecycle: onboarding pipeline, periodic reviews, approvals, and offboarding. Track vendor compliance status and playbook adherence.',
        governance_compliance: 'Monitor organizational compliance posture with scoring, drift detection, and board reporting. Track compliance trends and generate executive-ready reports.',
        governance_transfers: 'Manage cross-border data transfers, DPA reviews, and sub-processor governance. Document transfer mechanisms and ensure regulatory compliance for international data flows.',
        vendor_risk_assessment: 'Assess and score vendor risk across your portfolio. Monitor risk signals, track reassessments, and maintain a comprehensive risk portfolio view.',
        vendor_risk_contracts: 'Manage vendor contracts, due diligence questionnaires, and gate evaluations. Track contract terms, renewal dates, and compliance requirements.',
        vendor_risk_onboarding: 'Manage the vendor onboarding lifecycle: intake assessment, review routing, approval workflows, and risk acceptance decisions.',
        vendor_risk_dashboard: 'Central overview of your vendor risk posture: portfolio distribution by tier, recent tier changes, contracts expiring within 90 days, review KPIs, and risk-acceptance health. Start here for an at-a-glance read before drilling into scoring, signals, or gates.',
        vendor_risk_scoring: 'Configure scoring dimensions and evaluate vendors against weighted risk criteria. Track composite scores, assigned tiers, and score history over time.',
        vendor_risk_signals: 'Track adverse events that may warrant vendor reassessment: certification expiry, breach notifications, financial instability, and compliance changes. Signals drive downstream reassessment tasks.',
        vendor_risk_reviews: 'Conduct periodic vendor reviews with contract-status snapshots, findings, and recommendations. Track review outcomes (satisfactory, concerns identified, remediation required, escalated) across programs and periods.',
        vendor_risk_reassessments: 'Trigger and manage reassessment tasks when signals or periodic schedules indicate changed risk posture. Record outcomes and updated risk levels; completed reassessments flow back to scoring.',
        vendor_risk_acceptance: 'Formally accept residual vendor risk with explicit conditions, expiry dates, and justification. Maintain the Risk Acceptance Register — the auditable record of active, expired, and revoked acceptance decisions.',
        vendor_risk_onboarding_cases: 'Manage the vendor onboarding case lifecycle: new onboarding, material-change reviews, and periodic reviews. Work through staged tabs with completion indicators and trigger approval gates when ready.',
        vendor_risk_review_routing: 'Route vendor review tasks to specific reviewers by user and role. Assign questionnaires, track reviewer assignments, and capture routing outcomes with full audit trail.',
        vendor_risk_gates: 'Evaluate procurement approval gates before vendors go live. Configure gate criteria (certifications, DPA signed, questionnaire complete, risk within threshold), record override justifications, and link evidence.',
        vendor_risk_questionnaires: 'Build due-diligence questionnaire templates and assign them to vendors. Track response completion, review annotations, and findings that feed into risk scoring and approval gates.',
        monitoring_programs: 'Create and manage compliance monitoring programs linked to regulatory frameworks. Assign tasks via RACI governance and track program execution.',
        monitoring_kpi: 'Track compliance KPIs, authority readiness scores, and change review status. Dashboard view of monitoring program performance across all active frameworks.',
        discovery_scans: 'Run data discovery scans across connected systems. Manage discovery cycles with delta detection, configure connectors, and track scan results.',
        discovery_classification: 'Review discovery suggestions, manage the staging area for newly discovered data, and conduct owner review with classification recommendations.',
        discovery_management: 'Configure deduplication rules, manage import templates, and trace classification lineage from discovery through to approved classification labels.',
        connectors_integration: 'Configure system connectors, manage credentials, and monitor the event bus for real-time data synchronization across integrated systems.',
        connectors_automation: 'Plan automation strategies, capture business requirements, and evaluate proof-of-concept scorecards for system integration projects.',
        connectors_governance: 'Manage the system inventory, conduct security reviews, maintain traceability matrices, and track integration gap registers.',
        task_management_tasks: 'View and manage compliance tasks: task list, personal task queue, overdue tasks, task board, and calendar views for tracking work across all modules.',
        task_management_workflows: 'Design and manage compliance workflows: create workflow templates, configure section assignments, and monitor workflow execution.',
        compliance_forms_templates: 'Browse and manage compliance form templates. Access the template catalog, create custom templates, and configure form layouts for regulatory submissions.',
        compliance_forms_submissions: 'Complete and submit compliance forms. Track form instances, review outputs, and manage submission packages for regulatory filing.',
        compliance_forms_filing: 'Manage filing case links, section routing, and multi-department consolidation for regulatory dossier preparation and submission.',
        organizations_entities: 'Manage organization entities, assign regulatory frameworks per entity, and configure department hierarchies for multi-entity compliance.',
        organizations_groups: 'Manage organization groups for multi-entity enterprises. Share vendors, frameworks, and form templates across group members with consolidated reporting.',
        organizations_intake: 'Configure intake settings and manage settings inheritance across organization hierarchies.',
        access_accountability_sessions: 'Monitor user sessions, review access logs, and analyze access patterns. Track session activity with anchoring status for accountability evidence.',
        access_accountability_reviews: 'Run access review campaigns, manage justification reviews, and track reviewer progress. Coordinate periodic access certification across the organization.',
        access_accountability_sod: 'Define separation of duties rules, detect violations, and manage exceptions. Enforce conflict-of-interest controls across roles and permissions.',
        access_accountability_evidence: 'Generate evidence packages for access reviews, conduct investigations, and analyze role usage patterns for access accountability reporting.',
        program_governance_maturity: 'Assess and track compliance program maturity across frameworks. Define maturity models, run assessments, and monitor improvement trends over time.',
        program_governance_obligations: 'Map regulatory obligations to organizational units with RACI governance. Define the governance model and track remediation plans for identified gaps.',
        program_governance_documents: 'Manage the governance document register: draft, review, issue, and track acknowledgment of compliance policies and operating documents.',
        program_governance_reporting: 'Generate executive summaries, audit readiness reports, and evidence gap analyses. Track reporting periods and monitor program health metrics.',
        deletion_orchestration_runs: 'Execute and monitor deletion orchestration runs. Track execution tasks, verify completeness, and manage the deletion dashboard.',
        deletion_orchestration_vendors: 'Coordinate vendor deletion requests, verify vendor compliance with deletion obligations, and maintain proof chain documentation.',
        consent_management: 'Manage consent policies, purpose catalogs, and consent workflows. Configure consent collection rules and track consent snapshots over time.',
        consent_cmp: 'Integrate with Consent Management Platforms (CMPs): configure webhooks, map CMP purposes to GRC catalog purposes, and synchronize consent records.',
        consent_compliance: 'Monitor consent compliance: detect configuration changes, reconcile mismatches, track withdrawal propagation, and review the consent audit trail.',
        partner_workspace_partners: 'Manage partner organizations, conduct partner audits, and handle escalations. Track partner onboarding status and compliance requirements.',
        partner_workspace_operations: 'Manage partner deliverables, workflow templates, service tiers, and brand profiles. Configure operational workflows for partner collaboration.',
        privacy_scanner_scanning: 'Run real-time privacy scans on websites and applications. Review scan results, track pre-consent tracking evidence, and manage the scan archive.',
        privacy_scanner_monitoring: 'Configure privacy scanner monitoring dashboards and settings. Set up automated scanning schedules and manage scanner configuration.',
        consent_import: 'Bulk import consent records from CSV files. Ideal for registering paper-based consent (employment agreements, parent/school consent forms) at scale. Supports grant, withdrawal, restriction, and objection workflow types.',
        cases_import: 'Bulk import data subject rights request cases from CSV files. Supports multi-channel intake sources including phone, postal, email, and in-person submissions for organizations receiving high volumes of DSARs.',
        data_subjects: 'View unified data subject profiles aggregating all rights request cases and consent records for each individual. Track the complete privacy lifecycle per person — consent history, DSR cases, and interaction timeline.',
        files_config: 'Set the platform-wide file upload policy — size limits, type allow-list, retention defaults, and legal-hold rules. Per-owner-type overrides let you tighten policy for sensitive modules (e.g. rights request evidence) while keeping the rest of the org on a default. Retention defaults support the data-minimisation expectations under PDPL Law 91/2025 Article 3; legal hold suspends deletion during open investigations or disputes.',
        files_browser: 'Browse, search, and manage every attachment uploaded across the organization. Filter by owner module, uploader, date, and MIME type; open the attachment detail drawer to see audit history, linked entity, and retention status. An operational view — not framework-specific — used by admins and auditors to find and triage files.',
        files_dashboard: 'Operational overview of the file surface: total storage, growth over time, orphaned attachments (no parent entity), and retention policy compliance by owner type. Use it to spot policy drift (e.g. a module retaining files past the global retention window) and to budget storage. Operational tooling — not tied to a specific regulatory framework.',
    },
    framework_rights: {
        be_informed: 'Right to be informed',
        consent: 'Right to consent',
        refuse_consent: 'Right to refuse consent',
        withdraw_consent: 'Right to withdraw consent',
        access: 'Right of access',
        rectification: 'Right to rectification',
        data_provision: 'Right to data provision',
        deletion: 'Right to deletion',
        restriction_of_processing: 'Right to restriction of processing',
        object_to_processing: 'Right to object to processing',
        complaint: 'Right to lodge a complaint',
        denunciation: 'Right to denunciation',
        lawsuit: 'Right to bring legal action',
        claim_damages: 'Right to claim damages',
        request_protection_measures: 'Right to request protection measures',
    },
    // ---------- Framework Content i18n (Sprint 30.1.0) ----------
    framework_content: {
        EU_EPRIVACY_DIRECTIVE: {
            display_name: 'Directive 2002/58/EC (ePrivacy Directive)',
            description: 'Parent directive for national ePrivacy transpositions. Covers cookies, electronic communications privacy, direct marketing. Each member state must transpose into national law.',
        },
        EU_GDPR: {
            display_name: 'General Data Protection Regulation (EU) 2016/679',
            description: 'The General Data Protection Regulation (GDPR) is an EU regulation on data protection and privacy. It applies directly in all EU/EEA member states.',
        },
        EU_NIS2_DIRECTIVE: {
            display_name: 'Directive (EU) 2022/2555 (NIS2)',
            description: 'Parent directive for national NIS2 transpositions. Covers cybersecurity risk management, incident reporting, supply chain security for essential and important entities.',
        },
        VN_AI_LAW_2026: {
            display_name: 'Vietnam AI Law (134/2025/QH15) — Effective 01 Mar 2026',
            description: 'Vietnam AI Law (Luật Trí tuệ nhân tạo 134/2025/QH15) governing the development, deployment, and use of artificial intelligence systems. Establishes risk classification, transparency and labeling duties, incident reporting requirements, impact assessment obligations, and a controlled testing (sandbox) mechanism for AI activities in Vietnam.',
        },
        VN_AI_DECREE_142_2026: {
            display_name: 'Decree 142/2026/NĐ-CP — AI Law Implementation',
            description: 'Decree 142/2026/NĐ-CP (Nghị định số 142/2026/NĐ-CP) detailing certain articles and implementing measures of the Vietnam AI Law (Law 134/2025/QH15). Issued 2026-04-30, effective 2026-05-01. Lead authority: Ministry of Science and Technology. Operationalises AI risk classification, classification dossier, reclassification review, high-risk conformity assessment, high-risk risk-management system, transparency / explanation / technical marking / visible labeling, serious AI incident reporting (72h / 5wd / 15d), state-agency AI impact assessment (Mẫu AI02), and the controlled testing / AI sandbox lifecycle (Mẫu AI03a-AI09b). Submission via the AI one-stop e-portal or MoST transitional channel. No statutory fines.',
        },
        VN_CYBERSEC_LAW: {
            display_name: 'Vietnam Cybersecurity Law (24/2018 era)',
            description: "Vietnam's Law on Cybersecurity (Law No. 24/2018/QH14) establishing requirements for cybersecurity, data localization, and incident reporting. Active until superseded by Law 116/2025/QH15 effective 2026-07-01.",
        },
        VN_CYBERSEC_DECREE_53: {
            display_name: 'Decree 53 — Cybersecurity Law Implementation',
            description: 'Decree 53/2022/ND-CP implementing the Cybersecurity Law, covering in-scope determination, data localization triggers, local presence requirements, and authority cooperation workflows.',
        },
        VN_CYBERSEC_LAW_2025: {
            display_name: 'Law on Cybersecurity (116/2025/QH15)',
            description: "Vietnam's consolidated Law on Cybersecurity (Law 116/2025/QH15), replacing Law 24/2018/QH14, effective July 1, 2026. Consolidates scope of Decree 53/2022/NĐ-CP (see VN_CYBERSEC_DECREE_53).",
        },
        VN_DATA_LAW: {
            display_name: 'Vietnam Data Law',
            description: "Vietnam's Data Law (Law 60/2024/QH15) establishing comprehensive data governance requirements including data inventory, classification, sharing governance, and cross-border data transfer governance. Overlaps with VN_PDPL_LAW_2025 (personal data aspects) and VN_CYBERSEC_LAW_2025 (data localization/classification).",
        },
        VN_DATA_LAW_DECREE_165_2025: {
            display_name: 'Decree 165/2025/NĐ-CP (Data Law Implementation)',
            description: 'Decree 165/2025/NĐ-CP implementing the Vietnam Data Law (Law 60/2024/QH15). Provides official forms for cross-border data transfer impact assessment, annual risk assessment reporting, and national database access. Defines 11 official Mẫu số forms.',
        },
        VN_DATA_EXCHANGES_DRAFT_2026: {
            display_name: 'Draft Decree on Data Exchange Operations 2026',
            description: 'Draft decree on the operation of data exchange platforms (Dự thảo Nghị định quy định về hoạt động của sàn dữ liệu). Regulates platform classification, participant eligibility, data product and service listing controls, sandbox testing, transaction workflow, electronic contracting, certificates of property rights over data, and complaint/dispute handling. Currently in public consultation; not yet promulgated. Cites Law on Data 60/2024/QH15, Law on Personal Data Protection 91/2025/QH15, Law on Cybersecurity 116/2025/QH15, and Law on Electronic Transactions 20/2023/QH15.',
        },
        VN_ECOMMERCE_LAW_2025: {
            display_name: 'Law on Electronic Commerce (122/2025/QH15)',
            description: 'Vietnam Law on Electronic Commerce (Luật Thương mại điện tử 122/2025/QH15) establishing a unified national regime for electronic commerce, covering domestic and cross-border platform operations, platform governance and accountability, consumer protection, seller and affiliate controls, complaint handling, content moderation and takedown enforcement, and administrative procedure readiness for the state e-commerce management system.',
        },
        VN_PDPL: {
            display_name: 'Vietnam PDPL (Decree 13/2023 era)',
            description: "Vietnam's Personal Data Protection Decree (Decree 13/2023/ND-CP) establishing comprehensive data protection requirements. Legacy framework — superseded by Law 91/2025/QH15 and Decree 356/2025/ND-CP effective 2026-01-01.",
        },
        VN_PDPD_DECREE_13: {
            display_name: 'Decree 13 — PDPL Operational Baseline',
            description: 'Decree 13/2023/ND-CP, the operational instrument for PDPL duties including dossier filing, governance artifacts, cross-border mechanics, and MoPS administrative procedures. Replaced by Decree 356/2025/ND-CP effective 2026-01-01.',
        },
        VN_PDPL_LAW_2025: {
            display_name: 'Personal Data Protection Law (91/2025/QH15)',
            description: "Vietnam's Personal Data Protection Law (Law 91/2025/QH15) establishing comprehensive personal data protection framework, effective January 1, 2026. Covers compliance themes A–J: lawful processing bases, sensitive data consent, data subject rights (access/rectification/erasure/restriction/objection/portability), breach notification to authority and data subjects, DPIA for high-risk processing, cross-border transfer governance, DPO designation, records of processing activities, processor contractual obligations. Authority filing obligations via MoPS (VN_PDPL_ADMIN_PROCEDURES_778_2026). Cross-references: VN_PDPL_DECREE_356_2025 (implementing decree), VN_CYBERSEC_LAW_2025 (cybersecurity overlaps), VN_DATA_LAW (data governance).",
        },
        VN_PDPL_DECREE_356_2025: {
            display_name: 'Decree 356/2025/ND-CP (PDPL Implementation)',
            description: 'Decree 356/2025/ND-CP implementing the Personal Data Protection Law, replacing Decree 13/2023/ND-CP. Covers dossier filing, governance artifacts, cross-border mechanics, and service provider certification. Annex form inventory: Phụ lục I–VIII (impact assessment, cross-border transfer, certification). MoPS administrative procedures via VN_PDPL_ADMIN_PROCEDURES_778_2026 (Decision 778/QĐ-BCA-A05). Certification workflow: application → issuance → change request → reissue → revocation. Cross-references: VN_PDPL_LAW_2025 (parent law), VN_CYBERSEC_LAW_2025 (data localization), VN_DATA_LAW (data governance overlaps).',
        },
        VN_PDPL_ADMIN_PROCEDURES_778_2026: {
            display_name: 'MPS A05 Administrative Procedures (Decision 778/QĐ-BCA-A05)',
            description: 'Decision 778/QĐ-BCA-A05 (February 2026) — Administrative procedures for personal data protection under A05 (MPS). Defines dossier submission, processing, and result issuance procedures including DLCN-HC forms and Mẫu số statutory forms.',
        },
        VN_PDPL_ENFORCEMENT_DRAFT_2026: {
            display_name: 'Draft Cybersecurity + PDPL Enforcement Decree 2026',
            description: '116-page draft enforcement and sanctions decree consolidating sanctions for violations of the Personal Data Protection Law (91/2025/QH15) AND the Cybersecurity Law (116/2025/QH15). Canonical code VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026; existing code VN_PDPL_ENFORCEMENT_DRAFT_2026 preserved as legacy alias. Draft enforcement overlay only — fine amounts MUST NOT be presented as final law until promulgation.',
        },
        VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: {
            display_name: 'Government Resolution NQ22/2026 — MoPS Administrative Simplification (Temporary Overlay)',
            description: 'Temporary procedural overlay (effective 2026-04-29, expiring 2027-03-01) introducing three official channels (National Public Service Portal, direct submission to MoPS, postal submission to MoPS) for PDPL Decree 356/2025 and Decision 778/2026 dossier submissions. Email is NOT a NQ22 official channel — a separate unofficial email-ZIP submission guidance (VN_PDPL_GUIDANCE_EMAIL_ZIP_DOSSIER_SUBMISSION_2026) is shipped with requires_legal_review: true. Procedural overlay only — does NOT alter substantive obligations, fine ranges, or the binding force of underlying frameworks. Review task scheduled 2027-02-01.',
        },
        VN_TELECOM_LAW_2023: {
            display_name: 'Vietnam Telecommunications Law 2023',
            description: 'Vietnam Telecommunications Law 2023 (Luật Viễn thông), a sector overlay for telco/ISP/platform companies with confidentiality, disclosure control, and authority request handling obligations. Cross-references: VN_CYBERSEC_LAW_2025 (information system security), VN_PDPL_LAW_2025 (user data processing), VN_DATA_LAW (data governance).',
        },
        VN_TELECOM_DECREE_163_2024: {
            display_name: 'Decree 163/2024/NĐ-CP (Telecom Law Implementation)',
            description: 'Decree 163/2024/NĐ-CP implementing the Telecommunications Law (Law 24/2023/QH15). Provides official forms for telecom service notification, licensing, registration, and data center development plans. Defines 7 official Mẫu số forms.',
        },
    },
    framework_requirements: {
        d163_r4: {
            title: 'Data center development plan filing',
            description: 'Data center development plans must be filed with the competent authority per Decree 163/2024/NĐ-CP.',
        },
        d163_r3: {
            title: 'Telecom service registration',
            description: 'Registration of telecommunications services must be filed with the competent authority using the prescribed form.',
        },
        d163_r2: {
            title: 'Telecom license application',
            description: 'Applications for telecommunications licenses must be submitted using the prescribed form in accordance with Decree 163/2024/NĐ-CP.',
        },
        d163_r1: {
            title: 'Telecom service provision notification',
            description: 'Telecom service providers must notify the competent authority of service provision using the prescribed form before commencing service.',
        },
        vn_dp_draft_f: {
            title: 'Complaints, disputes, and risk management',
            description: 'Platform operators must provide complaint intake, dispute handling workflow with evidence preservation, risk identification and early-warning controls, and periodic review of privacy, cybersecurity, and access-management risks.',
        },
        vn_dp_draft_e: {
            title: 'Transaction workflow and certificate of property rights over data',
            description: 'Transactions require account registration, listing approval, electronic contracting, payment confirmation, delivery record, and issuance of a certificate of property rights over data where applicable. Transaction history and traceability must be preserved in a transaction register.',
        },
        vn_dp_draft_d: {
            title: 'Sandbox and testing environment controls',
            description: 'Test environments must be isolated, access-restricted, security-monitored, and reviewed before any output is extracted. No source data may be extracted from the test environment, and closure/cleanup of test sessions must be recorded.',
        },
        vn_dp_draft_c: {
            title: 'Data product and service listing controls',
            description: 'Listings must be supported by lawful-source and ownership/usage-right evidence, technical validation records, quality disclosures, and platform-side listing review and approval records. Prohibited data must be filtered before listing.',
        },
        vn_dp_draft_b: {
            title: 'Participant eligibility and onboarding',
            description: 'Platform operators must verify legal status, legal capacity, and conditional-business requirements for sellers and buyers, register transaction and payment accounts, and maintain a participant register.',
        },
        vn_dp_draft_a: {
            title: 'Data platform classification and operating model',
            description: 'Platform operators must establish a classification of platform type (national data platform vs other data platforms), define operator eligibility and responsibilities, and document pricing, fees, rules of operation, and technical governance.',
        },
        d165_r5: {
            title: 'National database information access',
            description: 'Requests to extract/use information from the national digital data database use prescribed forms (Mẫu số 07a/07b).',
        },
        d165_r4: {
            title: 'Annual risk assessment reporting',
            description: 'Annual risk assessment reports must be filed using prescribed forms (Mẫu số 05a/05b, 06).',
        },
        d165_r3: {
            title: 'Cross-border transfer termination notice',
            description: 'Termination of cross-border transfer must be notified using prescribed forms (Mẫu số 04a/04b).',
        },
        d165_r2: {
            title: 'Assessment result notification',
            description: 'Authority issues assessment result notices using prescribed form (Mẫu số 03).',
        },
        d165_r1: {
            title: 'Cross-border data transfer impact assessment filing',
            description: 'Organizations must file cross-border data transfer/processing impact assessment dossiers using prescribed forms (Mẫu số 01a/01b, 02).',
        },
        a964190d_8d1d_4284_bc7d_4023e2866751: {
            title: 'Consent for cookies and similar technologies',
            description: 'Storing or accessing information on a terminal equipment is only allowed with prior informed consent, except for technically necessary storage.',
        },
        '8437d96d_cf01_4f87_89f6_4faaf8eefdf6': {
            title: 'Confidentiality of communications',
            description: 'Member States shall ensure the confidentiality of communications and related traffic data by means of a public communications network and publicly available electronic communications services.',
        },
        c5420073_50c6_4981_816a_398460e5f546: {
            title: 'Consent for direct marketing (opt-in)',
            description: 'Use of electronic communications systems for direct marketing requires prior consent of the subscriber.',
        },
        c8bf67d2_874b_4647_92d7_39aaa3230819: {
            title: 'Soft opt-in for existing customers',
            description: 'Direct marketing to existing customers for similar products/services is permitted without consent if opt-out is provided.',
        },
        b546df40_9217_4204_b71c_5af5022a0ff9: {
            title: 'Breach notification for telecom providers',
            description: 'Providers of publicly available electronic communications services shall notify the competent national authority of personal data breaches.',
        },
        '5bd66ae4_dee3_4611_9104_71742df55484': {
            title: 'Right to object to direct marketing',
            description: 'Subscribers shall be given the opportunity to object to receiving direct marketing messages at any time and free of charge.',
        },
        a10af31f_ba28_494e_ab50_f8679e86a0b0: {
            title: 'Traffic data retention and erasure',
            description: 'Traffic data relating to subscribers and users must be erased or made anonymous when no longer needed for transmission, except for billing or interconnection payments.',
        },
        '8577e60f_66a8_4651_8336_d561db9929ad': {
            title: 'Location data processing conditions',
            description: 'Location data other than traffic data may only be processed when made anonymous or with the consent of the user, to the extent and for the duration necessary.',
        },
        '7f3abb41_8713_4276_93b5_6902f419a016': {
            title: 'Lawfulness of processing — consent',
            description: 'Processing shall be lawful only if the data subject has given consent to the processing of their personal data for one or more specific purposes.',
        },
        '1c2a945a_8eac_428d_8105_e80e95288ea4': {
            title: 'Lawfulness of processing — contract',
            description: 'Processing is necessary for the performance of a contract to which the data subject is party.',
        },
        '2b50c0fb_ee96_412e_a092_c3c1f3f817e8': {
            title: 'Lawfulness of processing — legal obligation',
            description: 'Processing is necessary for compliance with a legal obligation to which the controller is subject.',
        },
        '702ea760_9e5c_4c32_babc_0b5094ca9735': {
            title: 'Lawfulness of processing — vital interests',
            description: 'Processing is necessary to protect the vital interests of the data subject or another natural person.',
        },
        '8b55ce88_2d5e_467b_a55c_8c5b0efab67e': {
            title: 'Lawfulness of processing — public interest',
            description: 'Processing is necessary for the performance of a task carried out in the public interest or in the exercise of official authority.',
        },
        '8a72dd16_ee3c_4947_b5cd_9484719adad8': {
            title: 'Lawfulness of processing — legitimate interests',
            description: 'Processing is necessary for the purposes of the legitimate interests pursued by the controller or a third party.',
        },
        afefc965_8f83_4d75_a9fc_92d4d4f549d6: {
            title: 'Processing of special categories (sensitive data)',
            description: 'Processing of special categories of personal data (racial origin, political opinions, health, etc.) is prohibited unless specific conditions in Art. 9(2) are met.',
        },
        '10a2d63f_744d_4222_91f6_86a5a4cdaefd': {
            title: 'Conditions for consent',
            description: 'Controller must demonstrate consent was given. Consent must be freely given, specific, informed, and unambiguous. Withdrawal must be as easy as giving consent.',
        },
        '27697f99_efef_49e6_b8e7_c2dc550d1791': {
            title: "Child's consent — digital services",
            description: 'Processing of personal data of a child below the age of 16 (or lower age set by member state, not below 13) requires parental consent for information society services.',
        },
        '53883480_508e_4f8e_984f_9354c18de285': {
            title: 'Transparent information and communication',
            description: 'Controller shall provide information relating to processing in a concise, transparent, intelligible, and easily accessible form.',
        },
        b5eb22b7_78f2_4c55_b342_827d5f8ac90c: {
            title: 'Information for direct collection',
            description: 'Where personal data are collected from the data subject, the controller shall provide specified information at the time of collection.',
        },
        e55f7dbf_cd2a_4713_887e_8c64744e2d95: {
            title: 'Information for indirect collection',
            description: 'Where personal data have not been obtained from the data subject, the controller shall provide specified information within a reasonable period.',
        },
        '6bc5ec07_0589_4593_882a_13f41c31e1f6': {
            title: 'Right of access',
            description: 'The data subject shall have the right to obtain confirmation of processing and access to the personal data and supplementary information.',
        },
        '0e011e25_dcea_4a1e_ae55_583e683cea44': {
            title: 'Right to rectification',
            description: 'The data subject shall have the right to obtain rectification of inaccurate personal data without undue delay.',
        },
        e8ec5a55_f657_4a99_9b92_3e9861067d28: {
            title: 'Right to erasure (right to be forgotten)',
            description: 'The data subject shall have the right to obtain erasure of personal data without undue delay where specified grounds apply.',
        },
        '5b0c92fd_cd08_47be_bfd2_0540ad73f84f': {
            title: 'Right to restriction of processing',
            description: 'The data subject shall have the right to obtain restriction of processing where specified conditions apply.',
        },
        '3b829862_39f4_41be_8f2b_d469469a801b': {
            title: 'Notification obligation — rectification/erasure/restriction',
            description: 'Controller shall communicate any rectification, erasure, or restriction to each recipient to whom data was disclosed.',
        },
        '9cf34be6_121b_4fdf_ad7c_c4773696522e': {
            title: 'Right to data portability',
            description: 'The data subject shall have the right to receive their personal data in a structured, commonly used, machine-readable format.',
        },
        becae433_90b9_4cc0_bd33_df50976c1561: {
            title: 'Right to object',
            description: 'The data subject shall have the right to object to processing based on public interest or legitimate interests, including profiling.',
        },
        '4ef387f1_439c_4579_82a3_01565e6dc05d': {
            title: 'Automated individual decision-making, including profiling',
            description: 'The data subject shall have the right not to be subject to a decision based solely on automated processing which produces legal or similarly significant effects.',
        },
        '667ae5eb_a39a_4809_a032_923cc804eaed': {
            title: 'Breach notification to supervisory authority (72h)',
            description: 'In case of a personal data breach, the controller shall notify the supervisory authority within 72 hours of becoming aware, unless unlikely to result in risk.',
        },
        '40e4c2ad_bc85_436c_814f_265f02c8c3b1': {
            title: 'Breach notification to data subjects (high risk)',
            description: 'When a breach is likely to result in a high risk to rights and freedoms, the controller shall communicate the breach to the data subject without undue delay.',
        },
        '9dbb47bd_172f_4335_b7f3_4558476c4192': {
            title: 'Response to data subject requests (30 days)',
            description: 'Controller shall respond to data subject requests without undue delay and within one month of receipt, extendable by two further months for complex requests.',
        },
        '175d16b7_a0fc_44ff_bb9e_5ccb5d3de5a9': {
            title: 'Data protection impact assessment',
            description: 'Where processing is likely to result in a high risk to rights and freedoms, the controller shall carry out a DPIA prior to the processing.',
        },
        e54a7b09_33dd_4cda_8e21_d10a51e5e925: {
            title: 'Prior consultation with supervisory authority',
            description: 'Controller shall consult the supervisory authority where a DPIA indicates that processing would result in high risk without mitigation measures.',
        },
        '047b7838_bae5_4861_9c38_c99fedec9c96': {
            title: 'DPIA mandatory processing list',
            description: 'DPIA is required for systematic evaluation/profiling, large-scale processing of special categories, and systematic monitoring of public areas.',
        },
        aa08dc83_7c1a_441d_a8e7_960f65c8de87: {
            title: 'Records of processing activities (controller)',
            description: 'Each controller shall maintain a record of processing activities under its responsibility, including purposes, categories, recipients, transfers, and retention.',
        },
        b7eb6296_5962_449c_b267_d322d7919122: {
            title: 'Records of processing activities (processor)',
            description: 'Each processor shall maintain a record of all categories of processing activities carried out on behalf of a controller.',
        },
        '0d57e6d5_bbf5_4ea6_9a89_0f57bed61b5b': {
            title: 'Security of processing',
            description: 'Controller and processor shall implement appropriate technical and organisational measures to ensure security appropriate to the risk.',
        },
        ad85edfc_3c92_4ce4_a414_3ba6186cae98: {
            title: 'Data protection by design and by default',
            description: 'Controller shall implement appropriate measures for ensuring that only personal data necessary for each specific purpose is processed (data minimisation).',
        },
        '933b438d_53b8_4b6a_b1db_8e7f1b429ede': {
            title: 'Data protection officer designation',
            description: 'Controller and processor shall designate a DPO where processing is carried out by a public authority, involves regular and systematic monitoring, or large-scale processing of special categories.',
        },
        '28810a5d_d818_411a_9e8c_792d1b2c9b2f': {
            title: 'General principle for transfers',
            description: 'Any transfer of personal data to a third country shall take place only if the conditions of Chapter V are complied with.',
        },
        '60add64b_bb0c_44e7_baaf_666af65b2d0f': {
            title: 'Transfers based on adequacy decision',
            description: 'A transfer may take place where the Commission has decided that the third country ensures an adequate level of protection.',
        },
        cde8c7cd_3727_40ce_a321_e6b72b335179: {
            title: 'Transfers subject to appropriate safeguards',
            description: 'In the absence of an adequacy decision, transfers may take place where appropriate safeguards are provided, including SCCs or BCRs.',
        },
        '0e8edaa1_2156_44d9_95f2_1b284f581658': {
            title: 'Standard contractual clauses',
            description: 'Standard contractual clauses adopted by the Commission or by a supervisory authority provide appropriate safeguards for international transfers.',
        },
        '608a8de7_6c75_4f88_bd4c_1cc308780ab9': {
            title: 'Binding corporate rules',
            description: 'Binding corporate rules approved by a competent supervisory authority provide appropriate safeguards for intra-group international transfers.',
        },
        '420ced80_0d3b_4a28_adad_14ba54df3e40': {
            title: 'Derogations for specific situations',
            description: 'In the absence of adequacy or safeguards, transfers may take place based on explicit consent, contract necessity, public interest, legal claims, vital interests, or public register.',
        },
        c86fbc0d_7188_486c_8ea5_96b3fe957c6f: {
            title: 'Management body responsibility and training',
            description: 'Management bodies of essential and important entities must approve cybersecurity risk-management measures and undergo regular training.',
        },
        ffb95a09_993c_4db0_a4bb_c48000e43b33: {
            title: 'Cybersecurity risk-management measures',
            description: 'Essential and important entities shall take appropriate and proportionate technical, operational, and organisational measures to manage cybersecurity risks.',
        },
        '6a7c8c8f_4b81_4b47_adb7_c6abd12acd4b': {
            title: 'Incident handling policies and procedures',
            description: 'Entities shall implement policies on incident handling including detection, analysis, containment, and response procedures.',
        },
        de019eab_9992_4361_be7c_3e592f209d76: {
            title: 'Business continuity and crisis management',
            description: 'Entities shall implement business continuity measures including backup management, disaster recovery, and crisis management.',
        },
        '6b5f482f_301c_4f69_bd91_f9ae1b21de6c': {
            title: 'Supply chain security',
            description: 'Entities shall address security-related aspects of relationships with direct suppliers and service providers, including supply chain risk assessments.',
        },
        c8e7738b_358c_4c79_9fc2_a564f15a6e37: {
            title: 'Security in network and information systems',
            description: 'Entities shall implement security in network and information system acquisition, development, and maintenance, including vulnerability handling.',
        },
        '72520ed1_51ff_4983_a785_9be1f72cf7dd': {
            title: 'Vulnerability handling and disclosure',
            description: 'Entities shall implement policies and procedures for coordinated vulnerability disclosure and handling.',
        },
        f009e65d_d55e_4c98_9532_63c150694698: {
            title: 'Cybersecurity risk assessment effectiveness',
            description: 'Entities shall implement policies and procedures to assess the effectiveness of cybersecurity risk-management measures.',
        },
        '00e88824_675d_4b37_960c_757b3176c079': {
            title: 'Cryptography and encryption policies',
            description: 'Entities shall implement policies and procedures regarding the use of cryptography and, where appropriate, encryption.',
        },
        de29d287_81f1_46b1_b327_9cbf06ddad3d: {
            title: 'HR security, access control, asset management',
            description: 'Entities shall implement human resources security, access control policies, and asset management.',
        },
        '750f4459_9ba4_415e_bc47_5b0ec27d35a2': {
            title: 'Multi-factor authentication',
            description: 'Entities shall use multi-factor authentication or continuous authentication solutions, secured voice/video/text communications, and secured emergency communication systems.',
        },
        '359474f9_1820_4e3e_9031_a593b66ccde4': {
            title: 'Early warning to CSIRT (24h)',
            description: 'Entities shall submit an early warning to the competent CSIRT within 24 hours of becoming aware of a significant incident.',
        },
        '66acd92c_3091_4148_b935_68b0d24308e7': {
            title: 'Incident notification to CSIRT (72h)',
            description: 'Entities shall submit an incident notification to the competent CSIRT within 72 hours of becoming aware of a significant incident.',
        },
        '68269688_06b2_43ea_8e3e_a84532086557': {
            title: 'Final report to CSIRT (1 month)',
            description: 'Entities shall submit a final report to the competent CSIRT not later than one month after the incident notification.',
        },
        c8512bde_b064_4926_b333_06b9c95cecd7: {
            title: 'Applicability determination for AI activities',
            description: 'Determine whether the organization conducts AI activities subject to the Vietnam AI Law and document the applicability decision.',
        },
        '3a87ad63_179a_45ba_8743_98d865df80ab': {
            title: 'AI system risk classification by risk level',
            description: 'Classify each AI system by risk level (minimal, limited, high, unacceptable) using the guided classification methodology.',
        },
        '0f4475d3_77a6_4b41_bfe2_d4b299b1bd45': {
            title: 'AI System Register entry maintenance',
            description: 'Maintain a register of AI systems with system name, owner, purpose, deployment context, risk level, and vendor involvement.',
        },
        '2a73ce94_906a_4c95_8a45_a5bdf2a8118c': {
            title: 'AI System Dossier with artifact checklist and evidence',
            description: 'Build and maintain a system dossier with required artifact checklist, evidence library, and version history.',
        },
        e92cfb42_d91b_444f_adbc_c03903844eac: {
            title: 'Transparency obligations for AI system operators',
            description: 'Implement transparency measures for AI systems including user notification, disclosure of AI involvement, and system documentation.',
        },
        '45eb43d7_9b63_4166_aa0d_fa6aeeaa70ba': {
            title: 'AI output labeling, watermarking, and metadata signing',
            description: 'Implement labeling and watermarking for AI-generated content including metadata signing where applicable.',
        },
        '9566c94d_0dd3_419c_869f_6707d2a972f9': {
            title: 'Disclosure requirements for AI-generated content',
            description: 'Disclose to users when content is AI-generated and provide mechanisms for users to identify AI involvement.',
        },
        '21d39ea5_df78_45c6_8710_b203db229e25': {
            title: 'Explainability and user-facing transparency duties',
            description: 'Provide explainable outputs for AI decisions affecting individuals and maintain user-facing transparency documentation.',
        },
        f523fbc2_156d_466d_ad50_6a0c51b3ac9d: {
            title: 'Human oversight obligations for high-risk AI systems',
            description: 'Ensure human oversight mechanisms are in place for high-risk AI systems including escalation and override capabilities.',
        },
        bd180a89_4976_4790_9e50_f1c5c97dc576: {
            title: 'AI incident monitoring and detection',
            description: 'Implement monitoring capabilities to detect AI system incidents, malfunctions, and adverse outcomes.',
        },
        e7c1c60b_856c_4d3d_b6c9_092cde9b56bd: {
            title: 'Serious AI incident reporting to authority',
            description: 'Report serious AI incidents to the competent authority within the prescribed timeline (72 hours).',
        },
        '97f15adc_558a_49f2_bb06_86597cb9579b': {
            title: 'AI incident notification timeline compliance',
            description: 'Comply with notification timelines for AI incidents including initial notification, updates, and closure reports.',
        },
        '96c33d7e_fe52_4730_adac_70c8c477c88e': {
            title: 'AI impact assessment for high-risk systems',
            description: 'Conduct impact assessments for high-risk AI systems covering risks, mitigations, human oversight, and monitoring plans.',
        },
        '0f3b72c0_1030_44ff_ba53_596f45601cd7': {
            title: 'Controlled testing / sandbox eligibility determination',
            description: 'Determine eligibility for controlled testing (sandbox) participation and document the eligibility assessment.',
        },
        '4a6aed20_f655_4182_a760_aef1f329d5df': {
            title: 'Sandbox application submission and approval tracking',
            description: 'Submit sandbox applications to the competent authority and track approval status through the submission lifecycle.',
        },
        '8376988d_cd32_491e_a5b8_3340d3973358': {
            title: 'Sandbox monitoring checkpoints and completion/exit',
            description: 'Conduct periodic monitoring checkpoints during sandbox participation and prepare completion/exit reports.',
        },
        '7cb2b544_0512_4702_a092_9f1c10872376': {
            title: 'Labeling exemption/exception documentation with rationale',
            description: 'Document any labeling exemptions or exceptions with rationale and supporting evidence.',
        },
        dccd7fbf_872a_4716_87a4_b9c5ce8e2387: {
            title: 'Data localization for Vietnamese users',
            description: 'Data of Vietnamese users on services provided in Vietnam must be stored within the territory of Vietnam.',
        },
        '9eb86a03_e165_4963_8813_2272b638d529': {
            title: 'Cybersecurity incident reporting',
            description: 'Cybersecurity incidents must be reported to competent authorities in a timely manner.',
        },
        '736ac7de_7a6a_4bfd_b2be_c594e290038a': {
            title: 'Information system classification',
            description: 'Information systems must be classified by importance level and appropriate security measures applied.',
        },
        ffb807cf_f121_4b52_8e50_e091c33180c9: {
            title: 'Cybersecurity risk assessment',
            description: 'Regular cybersecurity risk assessments must be conducted for information systems.',
        },
        a4de518b_a5f1_481f_9b08_f8d2bc9db7e5: {
            title: 'In-scope determination (decision tree)',
            description: 'Organizations must determine whether they are in scope of Decree 53 using the prescribed decision tree.',
        },
        '01f99e9a_5e30_4d14_aae5_a2ce322a700b': {
            title: 'Data localization assessment and readiness',
            description: 'Organizations must assess data localization requirements and demonstrate readiness.',
        },
        cf24f011_dc23_4d84_aef5_994dde440571: {
            title: 'Storage and retention readiness checklist',
            description: 'A storage and retention readiness checklist must be completed for data localization compliance.',
        },
        b3884cb8_c785_4258_9517_9e393be4f4e1: {
            title: 'Local presence requirement assessment',
            description: 'Organizations must assess whether local presence requirements apply and plan accordingly.',
        },
        c1dc645b_cb1a_42a6_8ca5_9b15cfa84156: {
            title: 'Authority request receipt and verification',
            description: 'Authority requests must be received, verified, and processed according to prescribed procedures.',
        },
        d4571651_f39d_4c51_8f2d_7248c37f7b14: {
            title: 'Authority request response within deadline',
            description: 'Responses to authority requests must be provided within the prescribed deadline.',
        },
        '66c1df70_3456_40e9_b645_6c6bd3be702e': {
            title: 'Authority request handling log',
            description: 'A log of all authority request handling activities must be maintained.',
        },
        '04bbba2a_9804_4199_a517_054b6f4689fb': {
            title: 'Applicability and in-scope determination (consolidated)',
            description: 'Organizations must determine whether they are in scope under the consolidated cybersecurity law, covering both the old Law 24/2018 and Decree 53/2022 scope.',
        },
        '89a21eaf_cf09_4fec_bbb4_a1dadd652f6e': {
            title: 'Information system classification by importance level',
            description: 'Information systems must be classified by importance level (general, important, critical) with corresponding security obligations.',
        },
        '83ad981e_23ff_4a92_bebd_cd24487e912f': {
            title: 'Cybersecurity governance structure and responsible personnel',
            description: 'Organizations must establish cybersecurity governance structures and designate responsible personnel.',
        },
        '98d9a523_fefb_400b_bb7f_ebaf609a6ed4': {
            title: 'Periodic cybersecurity risk assessment',
            description: 'Regular cybersecurity risk assessments must be conducted for all classified information systems.',
        },
        '5236539f_4c1a_4eff_b2eb_fc11eb4eab23': {
            title: 'Security measures per information system classification',
            description: 'Technical security measures must be applied appropriate to each information system classification level.',
        },
        b69a8be2_ae0c_408c_b8f1_73b573eca876: {
            title: 'Access control and identity management',
            description: 'Access control and identity management measures must be implemented for information systems.',
        },
        ac774c26_feb7_4123_b799_c54f77bd87b5: {
            title: 'Logging, monitoring, and audit trail',
            description: 'Information systems must maintain logging, monitoring, and audit trail capabilities.',
        },
        a86cb01d_239b_44e2_9e09_e6973bcf9658: {
            title: 'Vulnerability management and patching',
            description: 'Vulnerability management and patching processes must be established and maintained.',
        },
        '826a789f_0c03_4918_81a1_6aa1cc2c205c': {
            title: 'Data localization for Vietnamese user data',
            description: 'Data of Vietnamese users on services provided in Vietnam must be stored within the territory of Vietnam.',
        },
        '8bee2b51_ace8_4118_89c8_010b96640016': {
            title: 'Local presence requirements for foreign service providers',
            description: 'Foreign service providers operating in Vietnam must maintain local presence as prescribed.',
        },
        '74d89250_b45f_41b6_bfe3_f915071580de': {
            title: 'Local storage architecture and cross-border flow documentation',
            description: 'Local storage architecture must be documented and cross-border data flows must be mapped and controlled.',
        },
        '04dec6f8_3280_494c_8506_292804b16d4d': {
            title: 'Cybersecurity incident detection and response',
            description: 'Organizations must have capabilities for detecting and responding to cybersecurity incidents.',
        },
        faeda71b_5050_497d_b08b_e3f3f5313ad4: {
            title: 'Mandatory incident reporting to competent authority',
            description: 'Qualifying cybersecurity incidents must be reported to MPS/MIC within prescribed timelines.',
        },
        d732b5a2_2b2c_48a0_955d_97a39daf132d: {
            title: 'Incident notification timeline compliance',
            description: 'Incident notifications must comply with prescribed timeline requirements (initial report within 24h, full report within 72h).',
        },
        '8e2dffb0_3481_44f0_baf2_130ddd8cfe12': {
            title: 'Authority request handling and cooperation',
            description: 'Authority requests must be handled promptly and cooperation provided according to prescribed procedures.',
        },
        e4c5b870_1d61_48fe_a6c2_1b9f5a458ea3: {
            title: 'Authority request case register and disclosure log',
            description: 'All authority requests and disclosures must be logged in a case register with full audit trail.',
        },
        '8eff8b17_65bc_4f94_8ece_c509a9fd9abb': {
            title: 'Vendor and third-party security assessment',
            description: 'Third-party vendors with access to information systems must undergo security assessment.',
        },
        '69891e1b_97f6_464c_9cce_b1276794fe4a': {
            title: 'Vendor data access and incident notification controls',
            description: 'Vendor data access must be documented and contractual incident notification obligations established.',
        },
        d89b6bc0_7a9e_4816_b31e_7c0abc02e90c: {
            title: 'Security awareness training and exercises',
            description: 'Regular cybersecurity awareness training and tabletop exercises must be conducted.',
        },
        f9c5d3cc_5bb2_4b30_a5fb_ada34e407981: {
            title: 'Periodic pen test and security audit',
            description: 'Periodic penetration testing and security audits must be performed on classified information systems.',
        },
        '88dd9877_d2e2_4e41_a248_ae39b1ff478f': {
            title: 'Maintain digital data inventory',
            description: 'Organizations must maintain a comprehensive digital data inventory cataloging all data assets.',
        },
        '18e4bfd1_42cf_4d87_870f_5a34242962ef': {
            title: 'Data classification by sensitivity and importance',
            description: 'Data must be classified according to sensitivity and importance levels as defined by the Data Law.',
        },
        b89981ac_7559_46f9_a74c_a143976dde4e: {
            title: 'Define handling rules by classification level',
            description: 'Handling rules must be established for each data classification level.',
        },
        '3bf8019d_b72c_41d7_82cf_fc367efd9526': {
            title: 'Track data sharing and disclosure with approvals',
            description: 'All data sharing and disclosure activities must be tracked with appropriate approvals.',
        },
        '8623eca2_4cf9_41bd_bfbd_2300c06ca1f3': {
            title: 'Cross-border data transfer governance',
            description: 'Cross-border data transfers must comply with governance requirements under the Data Law.',
        },
        '006b8aa6_9379_4eff_b8ab_935f9a087601': {
            title: 'Maintain governance policies and responsibilities',
            description: 'Organizations must maintain data governance policies and assign governance responsibilities.',
        },
        f12fc090_5c3e_4fa8_9813_74bea0359c5c: {
            title: 'Annual governance attestation',
            description: 'An annual governance attestation must be completed to confirm ongoing compliance.',
        },
        f6c3060a_5e52_4f97_b1af_c6a5f00bfaee: {
            title: 'Data risk assessment',
            description: 'A data risk assessment must be conducted to identify and mitigate data governance risks.',
        },
        '6f228998_1c61_49cf_a585_3d3e9baff55a': {
            title: 'Platform operator accountability and governance obligations',
            description: 'Establish platform operator accountability structures including governance policies, monitoring responsibilities, and enforcement obligations.',
        },
        eee1d423_3783_4530_bc64_eef6fd9d02dc: {
            title: 'Platform rules publication and transparency of information',
            description: 'Publish platform rules, trading conditions, and operational disclosures in a transparent and accessible manner.',
        },
        '50b57433_7a51_4e92_9c84_c748d6865b70': {
            title: 'E-commerce sales website/app notification to MOIT',
            description: 'Submit notification to MOIT for e-commerce sales websites and applications via Online.gov.vn or designated channels.',
        },
        '92854abe_6654_4155_8495_f3419a8a8da0': {
            title: 'E-commerce service platform/marketplace registration with MOIT',
            description: 'Register e-commerce service platforms and marketplaces with MOIT including dossier submission and approval tracking.',
        },
        b8bff437_805e_4a4a_a62e_89cb90b89d3f: {
            title: 'Seller register maintenance (domestic and foreign)',
            description: 'Maintain a register of all sellers on the platform including domestic and foreign sellers with onboarding, verification, and lifecycle tracking.',
        },
        '75778639_5235_4d13_a728_fa772c27fc0c': {
            title: 'Livestream seller register and compliance controls',
            description: 'Maintain a register of livestream sellers with identity verification, activity tracking, and compliance controls.',
        },
        '405f2932_742a_4bcb_9784_1a3d81f2e714': {
            title: 'Affiliate and marketing partner register',
            description: 'Maintain a register of affiliate marketers and referral partners with contract status, content guidelines, and compliance acknowledgements.',
        },
        cce5a4eb_a576_459c_95b5_bdd0e8ed30a8: {
            title: 'Product listing and prohibited/conditional goods enforcement',
            description: 'Implement product listing controls including prohibited and conditional goods screening, monitoring, and enforcement actions.',
        },
        '9fc01e19_18dd_4d60_b95a_c1f4bcdbb6ae': {
            title: 'Consumer complaints register with intake, actions, and audit trail',
            description: 'Maintain a complaints register with intake records, investigation actions, resolution outcomes, and complete audit trail.',
        },
        '8a21764e_e5f4_4934_9913_c235f4c462ca': {
            title: 'Complaint handling procedure and resolution timelines',
            description: 'Establish complaint handling procedures with defined acknowledgement, investigation, and resolution timelines.',
        },
        '8406373c_ec31_4bf8_810f_72d9d73b3de8': {
            title: 'Content moderation, monitoring, and takedown enforcement',
            description: 'Implement content moderation, monitoring, and takedown enforcement procedures for illegal, prohibited, or violating content.',
        },
        '6f32c315_323b_4ce6_a4bb_3262a5b56afe': {
            title: 'Takedown and enforcement actions register with evidence preservation',
            description: 'Maintain a register of takedown and enforcement actions with decision records, evidence preservation, and seller notifications.',
        },
        c0bbbfbf_29bd_4813_89f1_956eab9f1a23: {
            title: 'Platform policy and disclosure register (versions, publication dates, acceptance)',
            description: 'Maintain a register of platform policies and disclosures with version history, publication dates, and acceptance evidence.',
        },
        c9fcbe90_622d_43ca_9ec6_db26c0eda490: {
            title: 'Consumer information duties - accurate product info and anti-deception',
            description: 'Ensure accurate, clear product and service information and implement measures to prevent deceptive practices on the platform.',
        },
        '8f49603b_454c_4470_ab07_2f845a447595': {
            title: 'Foreign platform operating conditions and registration/notification',
            description: 'Comply with operating conditions for foreign platforms serving Vietnamese users including registration or notification requirements.',
        },
        e0dc85cc_5d2a_4142_b1a6_cc2fde891f7b: {
            title: 'Local representation or authorization for foreign platforms',
            description: 'Designate a local representative or authorized entity in Vietnam for foreign platforms as required by law.',
        },
        '5719f47c_f298_429e_90c3_df6ceed856e1': {
            title: 'Cross-border seller and complaint handling coordination',
            description: 'Coordinate cross-border seller handling and complaint resolution including escalation and evidence management.',
        },
        c0b3db29_9161_4cef_a889_94e3b85d78fe: {
            title: 'State e-commerce management system interaction and reporting readiness',
            description: 'Prepare for interaction with the state e-commerce management system including data fields mapping and reporting capability.',
        },
        '145a7016_720a_4a06_badd_00145842821e': {
            title: 'Post-approval material change notification',
            description: 'Submit notifications for material changes to platform details, policies, or operations after initial approval.',
        },
        '4bc0ee2a_610d_490d_8122_d6d1afadaa22': {
            title: 'Termination and deactivation notification',
            description: 'Submit termination or deactivation notifications when ceasing e-commerce operations including public notice and consumer protection handling.',
        },
        be95bc85_2b25_4de7_a3e7_154b780289e4: {
            title: 'Trustmark and credibility rating program eligibility and maintenance',
            description: 'Assess eligibility for trustmark and credibility rating programs and maintain compliance evidence for ongoing participation.',
        },
        f639afe2_18f2_4c0a_82d9_626308994267: {
            title: 'Consent for personal data processing',
            description: 'Personal data processing requires consent of the data subject unless an exemption applies.',
        },
        '5dd8e1b9_ac2d_48af_8c62_538937f0a4f7': {
            title: 'Consent for sensitive data processing',
            description: 'Processing of sensitive personal data requires explicit consent with separate, clear notification to the data subject.',
        },
        '5a958c76_6943_4449_a15c_ddb503ce526f': {
            title: 'Right of access',
            description: 'Data subjects have the right to access their personal data held by the data controller.',
        },
        '1ae0a58b_65a5_4a32_9b2c_0baed7799a32': {
            title: 'Right to rectification',
            description: 'Data subjects have the right to request rectification of inaccurate personal data.',
        },
        '25c61e57_273f_48cb_a318_d6315ad744f1': {
            title: 'Right to erasure',
            description: 'Data subjects have the right to request erasure of their personal data.',
        },
        '0eafe047_ed8b_4a9a_bda9_b4ef2ae353c2': {
            title: 'Right to restrict processing',
            description: 'Data subjects have the right to request restriction of processing of their personal data.',
        },
        '77f9677a_f436_43ec_a947_3bdc44fc192a': {
            title: 'Right to object',
            description: 'Data subjects have the right to object to the processing of their personal data.',
        },
        c8b19e70_782c_49c9_8a71_14bef4147d6b: {
            title: 'Right to withdraw consent',
            description: 'Data subjects have the right to withdraw previously given consent at any time.',
        },
        '7f472a1c_b37f_498d_adc7_df8d8eafbae9': {
            title: 'Breach notification to authority (72h)',
            description: 'Data controller must notify the Ministry of Public Security within 72 hours of discovering a personal data breach.',
        },
        '98bed7ab_e814_43f5_8e70_e95c413266a1': {
            title: 'Breach notification to data subjects',
            description: 'Data controller must notify affected data subjects of a personal data breach without undue delay.',
        },
        '404a3576_dd84_4bac_acf4_29719615c45e': {
            title: 'DPIA for high-risk processing',
            description: 'A Data Protection Impact Assessment must be conducted for processing activities that are likely to result in high risk.',
        },
        '29d9ed94_9360_457c_9aca_37aefac66969': {
            title: 'Cross-border transfer assessment',
            description: 'An impact assessment must be conducted before transferring personal data outside Vietnam.',
        },
        '9620ff08_b83c_4e1f_9350_eba835b145fe': {
            title: 'Record of processing activities',
            description: 'Data controllers must maintain a Record of Processing Activities (RoPA) documenting all processing activities.',
        },
        '96ee0ff3_3ead_431e_aa57_08ccff4821fc': {
            title: 'Data processing agreement records',
            description: 'Records of data processing agreements with processors and sub-processors must be maintained.',
        },
        '434dc319_1922_49e4_b757_37622135c4b2': {
            title: 'Cross-border transfer safeguards',
            description: 'Personal data transfers outside Vietnam require appropriate safeguards and documented assessments.',
        },
        '3372c651_d5a8_4cae_ae57_2fc684ad0768': {
            title: 'Acknowledgment within 2 working days',
            description: 'Data subject requests must be acknowledged within 2 working days of receipt.',
        },
        b15fa62d_3f52_4e42_8442_f4969e08883a: {
            title: 'Completion within 15 working days',
            description: 'Data subject requests must be completed within 15 working days, with a possible 5 working day extension.',
        },
        '5d81233c_f671_4827_b874_827762edfc20': {
            title: 'MoPS impact assessment dossier filing',
            description: 'Organizations conducting high-risk processing must file an impact assessment dossier with the Ministry of Public Security using the prescribed administrative procedure.',
        },
        '83bf5bf8_edbd_4f0a_9f8e_5f47db01324d': {
            title: 'MoPS cross-border transfer dossier filing',
            description: 'Organizations transferring personal data outside Vietnam must file a cross-border transfer impact assessment dossier with the Ministry of Public Security.',
        },
        '9454e465_e618_4b6f_80b3_d79fdd9cc5cb': {
            title: 'Processing service provider certification',
            description: 'Organizations providing personal data processing as a service must obtain a service processing certificate from the Ministry of Public Security.',
        },
        b88aec34_046a_4e0a_9c77_ddad27ea4bc4: {
            title: 'Processing impact assessment dossier when triggers met',
            description: 'A processing impact assessment dossier must be filed when prescribed triggers are met.',
        },
        '9d34a206_b85e_4649_a23b_e2c6f1398402': {
            title: 'Cross-border transfer impact dossier',
            description: 'A cross-border transfer impact dossier must be prepared and filed for international data transfers.',
        },
        '210d3ec3_e6f2_424b_ae25_cad76bc13320': {
            title: 'Data protection officer designation',
            description: 'A data protection officer must be designated as required by Decree 13.',
        },
        '776183b2_2797_40e8_8d81_c4f512c566d2': {
            title: 'Governance artifacts (policies, roles, procedures)',
            description: 'Governance artifacts including policies, roles, and procedures must be maintained.',
        },
        '4f1385f1_6dfc_4368_b4af_1040cd3f77b7': {
            title: 'Evidence pack completeness and immutable audit trail',
            description: 'Evidence packs must be complete and an immutable audit trail must be maintained.',
        },
        f6826e26_8991_4a0f_9117_481490230bdd: {
            title: 'Filing case workflow support',
            description: 'Filing case workflows must be supported for impact assessment and cross-border dossiers.',
        },
        '8a28ee33_305a_491f_a93f_90e59f8da91b': {
            title: 'Processor contractual requirements',
            description: 'Contractual requirements for data processors must be established and monitored.',
        },
        '4fd36e83_8286_40e0_b9b2_a4bc5e8377b3': {
            title: 'Annual compliance review',
            description: 'An annual compliance review must be conducted to verify ongoing adherence.',
        },
        '8b6b6c13_0d32_4439_819f_4bd8cff02217': {
            title: 'Authority filing submission records',
            description: 'Records of all authority filing submissions must be maintained.',
        },
        e7ce6c23_03dc_42dd_bccd_fa571c3afee3: {
            title: 'Lawful bases for personal data processing',
            description: 'Personal data processing must be based on lawful bases as defined by the PDPL.',
        },
        c919c66c_6a74_4403_bb97_04b773d270bf: {
            title: 'Consent requirements for sensitive data',
            description: 'Processing of sensitive personal data requires explicit consent with specific requirements.',
        },
        '419cc7c3_a257_4293_a054_304dc2fcb8df': {
            title: 'Data subject rights (access, rectification, erasure, restrict, object, portability)',
            description: 'Data subjects have comprehensive rights including access, rectification, erasure, restriction, objection, and portability.',
        },
        '638e6e25_3a74_4864_8bff_8e21ab82860b': {
            title: 'Breach notification to authority',
            description: 'Data controllers must notify the competent authority of personal data breaches within the prescribed timeframe.',
        },
        '6e75c111_66b1_4726_8c27_6537846edb30': {
            title: 'Breach notification to data subjects',
            description: 'Data controllers must notify affected data subjects of personal data breaches.',
        },
        f686123d_3bdd_4a61_a53c_efe5b52d9058: {
            title: 'Data protection impact assessment',
            description: 'A data protection impact assessment must be conducted for high-risk processing activities.',
        },
        '554ea5b6_092d_4295_a57a_1bd9b2eef291': {
            title: 'Cross-border transfer governance',
            description: 'Cross-border transfers of personal data must comply with governance requirements.',
        },
        cf9a5b29_46fd_4072_b877_13b8a69df82c: {
            title: 'Data protection officer designation',
            description: 'Organizations must designate a data protection officer as required.',
        },
        f5a4965d_eb0c_47df_9933_f185ae46463b: {
            title: 'Records of processing activities',
            description: 'Organizations must maintain records of all processing activities.',
        },
        ce8986ff_684d_41ec_8147_b3f2a1c5206c: {
            title: 'Processor contractual obligations',
            description: 'Contractual obligations must be established with data processors.',
        },
        '7a3d7554_7b6f_49d7_8864_4a5cd71fd6bd': {
            title: 'Impact assessment dossier filing',
            description: 'Impact assessment dossiers must be filed with the competent authority as prescribed.',
        },
        '65dba62f_0788_44c1_89e3_09d802e28eb9': {
            title: 'Cross-border transfer impact dossier',
            description: 'A cross-border transfer impact dossier must be prepared and filed for international data transfers.',
        },
        '0eccb520_b1fa_4fa3_a3ab_2d30823dd00e': {
            title: 'DPO appointment and notification',
            description: 'A data protection officer must be appointed and the authority notified.',
        },
        '43ead19e_982a_4615_bafd_e6a3a4e80b3a': {
            title: 'Governance policies and procedures',
            description: 'Governance policies and procedures must be established and maintained.',
        },
        '9ebc7a42_e1f1_49c4_ac90_22a691fd2219': {
            title: 'Evidence pack and immutable audit trail',
            description: 'Evidence packs must be complete and an immutable audit trail must be maintained.',
        },
        '5b0ba1c0_7b86_4c80_b9f9_26e4ff9652c2': {
            title: 'Filing case lifecycle',
            description: 'Filing case lifecycles must be managed through prescribed stages.',
        },
        '83f2f346_47bb_4377_a711_3cef26742f39': {
            title: 'Processor contractual requirements',
            description: 'Contractual requirements for data processors must be established and monitored.',
        },
        cb972e06_f773_492e_8ad5_f65d1e999b79: {
            title: 'Service processing provider certification',
            description: 'Organizations providing data processing services must obtain certification.',
        },
        '4ba36ab5_39f0_4d79_927e_c39587bed226': {
            title: 'Authority filing submission records',
            description: 'Records of all authority filing submissions must be maintained.',
        },
        f7a62c88_3cda_4031_bc52_1598661a5128: {
            title: 'Annual compliance review',
            description: 'An annual compliance review must be conducted to verify ongoing adherence.',
        },
        '3e97f4d4_60ad_46a8_ad2b_615d4ca04aee': {
            title: 'Data security technical measures',
            description: 'Technical measures for data security must be implemented and maintained.',
        },
        '45f4e745_a923_4879_94ce_5a2b41b7e886': {
            title: 'Decree 356 official Mẫu số form requirements',
            description: 'The 13 official Mẫu số administrative procedure forms (Mẫu số 01a–10) prescribed by Decree 356/2025/NĐ-CP must be used for the corresponding filings with the Ministry of Public Security. The Phụ lục I–VIII items are internal data-structure mappings, not official regulatory forms.',
        },
        ff452604_7461_4c12_aeab_cb7dd1fb5ba3: {
            title: 'DPIA dossier submission to A05',
            description: 'Organizations must submit DPIA dossiers to A05/MPS for processing activities involving sensitive personal data or large-scale processing.',
        },
        '003600ae_7a0f_4034_b206_72f59409b8f3': {
            title: 'Cross-border transfer dossier submission',
            description: 'Organizations must submit cross-border data transfer dossiers for A05/MPS review and approval.',
        },
        '4632b20c_293a_4c7e_968e_4ce178c3b7d8': {
            title: 'Processing service certificate application',
            description: 'Data processing service providers must apply for certificates from A05/MPS.',
        },
        be96add4_ebdb_4b6c_9426_4dc87b7cd2ce: {
            title: 'Personal data processing notification',
            description: 'Controllers must notify A05/MPS of personal data processing activities using prescribed forms (Mẫu số 01a/01b).',
        },
        '39f50471_27cc_46bd_acff_9aa2211d43ab': {
            title: 'Annual situation reporting',
            description: 'Controllers must submit annual personal data processing situation reports (Mẫu số 09).',
        },
        '3b45c6fa_d66a_448a_9ff1_597caa055e09': {
            title: 'User information confidentiality by default',
            description: 'User information must be treated as confidential by default.',
        },
        '2994510e_b1f8_4cdd_947c_d9542746433b': {
            title: 'Minimum organizational and technical protection controls',
            description: 'Minimum organizational and technical protection controls must be implemented.',
        },
        '8643160f_9a10_4d93_baf5_082c6a7c768a': {
            title: 'Disclosure only with proper basis',
            description: 'Disclosure of user information is permitted only with a proper legal basis.',
        },
        c1fb7d85_fdc0_402e_ab0c_ff134bd72dfa: {
            title: 'Mandatory disclosure logging and auditability',
            description: 'All disclosures must be logged and auditable.',
        },
        efa36bc0_abae_417a_b676_9509af37f89d: {
            title: 'Internal approval workflow for disclosures',
            description: 'An internal approval workflow must be established for all disclosures.',
        },
        d086fdcd_9a23_4500_893c_db8ac62bd787: {
            title: 'Authority request response handling',
            description: 'Authority requests must be handled and responded to according to prescribed procedures.',
        },
    },
    framework_rights_descriptions: {
        be_informed: 'The data subject has the right to be informed about personal-data processing activities.',
        consent: 'The data subject has the right to consent to processing of their personal data.',
        refuse_consent: 'The data subject has the right to not consent to processing of their personal data.',
        withdraw_consent: 'The data subject has the right to request withdrawal of previously given consent.',
        access: 'The data subject has the right to view and access their personal data held by the controller.',
        rectification: 'The data subject has the right to correct or request correction of inaccurate personal data.',
        data_provision: 'The data subject has the right to request provision of their personal data (yêu cầu cung cấp dữ liệu cá nhân).',
        deletion: "The data subject has the right to request deletion or destruction of personal data, subject to the law's exceptions.",
        restriction_of_processing: 'The data subject has the right to request restriction of processing of their personal data.',
        object_to_processing: 'The data subject has the right to send a request objecting to the processing of their personal data.',
        complaint: 'The data subject has the right to lodge a complaint (khiếu nại) regarding personal data processing.',
        denunciation: 'The data subject has the right to make a denunciation (tố cáo) regarding violations of personal data protection.',
        lawsuit: 'The data subject has the right to bring legal action (khởi kiện) for violations of their data protection rights.',
        claim_damages: 'The data subject has the right to claim compensation for damage caused by violations of personal data protection.',
        request_protection_measures: 'The data subject has the right to request competent authorities or relevant organizations/individuals to implement measures to protect their personal data.',
    },
    framework_procedures: {
        VN_DP_DRAFT_P7: {
            name: 'Risk management and monitoring (draft)',
            steps: {
                '1': 'Identify risk categories',
                '2': 'Maintain early warning controls',
                '3': 'Review collection, storage, processing, sharing, and transaction risks',
                '4': 'Record privacy, cybersecurity, and access-management risks',
                '5': 'Trigger remediation actions',
                '6': 'Preserve review history',
            },
        },
        VN_DP_DRAFT_P6: {
            name: 'Complaints and disputes (draft)',
            steps: {
                '1': 'Receive complaint / dispute intake',
                '2': 'Check completeness of required information; request supplement within 24 hours if incomplete',
                '3': 'Within 3 working days of receiving a complete request, notify parties of handling approach and expected timeline',
                '4': 'Request responses and supporting documents',
                '5': 'Assess dispute facts and platform responsibility',
                '6': 'Record resolution outcome',
                '7': 'Preserve evidence and close case',
            },
        },
        VN_DP_DRAFT_P5: {
            name: 'Delivery and certificate of property rights over data (draft)',
            steps: {
                '1': 'Deliver product/service according to contract method',
                '2': 'Record delivery event and time',
                '3': 'Generate certificate of property rights over data',
                '4': 'Register transaction completion',
                '5': 'Enable later lookup / verification where required',
            },
        },
        VN_DP_DRAFT_P4: {
            name: 'Transaction initiation and contracting (draft)',
            steps: {
                '1': 'Review listing and evaluation result',
                '2': 'Initiate purchase, negotiation, or auction workflow',
                '3': 'Generate electronic contract',
                '4': "Secure both parties' acceptance",
                '5': 'Record payment completion',
                '6': 'Authorize delivery',
            },
        },
        VN_DP_DRAFT_P3: {
            name: 'Sandbox / test environment access (draft)',
            steps: {
                '1': 'Submit sandbox request',
                '2': 'Define scope, allowed data, time period, and processing permissions',
                '3': 'Provision isolated technical environment',
                '4': 'Monitor security and access during testing',
                '5': 'Review output before extraction',
                '6': 'Approve extraction only where permitted',
                '7': 'Close test session and clean up environment',
            },
        },
        VN_DP_DRAFT_P2: {
            name: 'Data product / service listing (draft)',
            steps: {
                '1': 'Create listing application',
                '2': 'Capture source and ownership / usage-right evidence',
                '3': 'Record technical characteristics, format, API details, and restrictions',
                '4': 'Record quality disclosures',
                '5': 'Submit listing dossier to platform review',
                '6': 'Platform performs completeness, legality, and technical checks',
                '7': 'Approve or reject listing',
                '8': 'If approved, publish listing with attached evaluation result',
            },
        },
        VN_DP_DRAFT_P1: {
            name: 'Participant onboarding (draft)',
            steps: {
                '1': 'Determine participant type: seller or buyer',
                '2': 'Verify legal status and legal capacity',
                '3': 'Verify additional conditions if conditional business lines apply',
                '4': 'Register transaction account',
                '5': 'Link payment account',
                '6': 'Approve platform participation',
                '7': 'Store participant record and audit trail',
            },
        },
        AI_A1: {
            name: 'AI System Registration / Notification',
            steps: {
                '1': 'Applicability check and AI system classification',
                '2': 'Build registration/notification dossier',
                '3': 'Internal approval',
                '4': 'Submit via channel',
                '5': 'Amendment loop — if returned, update and resubmit',
                '6': 'Close and store confirmation',
            },
        },
        AI_A2: {
            name: 'AI Risk Classification Workflow',
            steps: {
                '1': 'Create AI System Register entry',
                '2': 'Run classification wizard',
                '3': 'Attach evidence',
                '4': 'Approve classification',
                '5': 'Link outputs to system dossier',
            },
        },
        AI_A3: {
            name: 'Labeling and Disclosure Compliance',
            steps: {
                '1': 'Identify labeling obligations by system and use case',
                '2': 'Implement labeling notices',
                '3': 'Capture evidence — screenshots, UI copy, technical metadata',
                '4': 'Handle exemptions and record justification',
                '5': 'Approve and version',
            },
        },
        AI_A4: {
            name: 'Serious Incident Reporting',
            steps: {
                '1': 'Create AI Incident Record',
                '2': 'Classify severity',
                '3': 'Execute containment actions and evidence capture',
                '4': 'Generate Serious Incident Report template export',
                '5': 'Submit to authority via channel',
                '6': 'Track authority responses and closure',
            },
        },
        AI_A5: {
            name: 'AI Impact Assessment',
            steps: {
                '1': 'Create assessment record',
                '2': 'Populate required sections',
                '3': 'Attach evidence — data flows, model documentation, controls',
                '4': 'Internal approval',
                '5': 'Export report and store in dossier',
            },
        },
        AI_A6: {
            name: 'Controlled Testing / Sandbox Participation',
            steps: {
                '1': 'Complete eligibility and readiness record',
                '2': 'Prepare application and export',
                '3': 'Submit via channel',
                '4': 'Track approval',
                '5': 'Execute monitoring checkpoints and logs',
                '6': 'Complete completion report and closure package',
            },
        },
        CYBERSEC_C1: {
            name: 'Critical Information System Security Assessment',
            steps: {
                '1': 'Classify information systems according to national cybersecurity requirements',
                '2': 'Conduct security assessment and risk evaluation for classified systems',
                '3': 'Implement required security measures and baseline controls',
                '4': 'Document evidence of compliance and schedule periodic reviews',
            },
        },
        CYBERSEC_C2: {
            name: 'Cybersecurity Incident Notification',
            steps: {
                '1': 'Detect, classify, and assess the cybersecurity incident',
                '2': 'Execute immediate response actions and evidence preservation',
                '3': 'Report to authority and participate in national incident-response coordination',
                '4': 'Conduct post-incident review and document lessons learned',
            },
        },
        CYBERSEC_C3: {
            name: 'Data Localization Compliance Verification',
            steps: {
                '1': 'Assess whether data localization triggers apply based on service type and data categories',
                '2': 'Verify local storage architecture and cross-border flow controls are in place',
                '3': 'Assess local presence requirements (branch / representative office) where applicable',
                '4': 'Document compliance evidence and maintain for authority review',
            },
        },
        CYBERSEC_C4: {
            name: 'Authority Cooperation Request Response',
            steps: {
                '1': 'Receive and verify the legitimacy and scope of the authority request',
                '2': 'Gather and prepare requested information or execute required actions',
                '3': 'Submit response to authority within required timeline',
                '4': 'Log the disclosure and close the case',
            },
        },
        CYBERSEC_C5: {
            name: 'Cybersecurity Personnel Qualification Verification',
            steps: {
                '1': 'Assess vendor security posture using cybersecurity assessment framework',
                '2': 'Verify vendor data access scope and register in vendor data access log',
                '3': 'Review vendor incident notification clause and contractual cybersecurity requirements',
                '4': 'Schedule periodic vendor security reviews and monitor compliance',
            },
        },
        DATA_D1: {
            name: 'Data Inventory and Classification Setup',
            steps: {
                '1': 'Catalog all data assets with types and sources in data inventory register',
                '2': 'Classify each asset by sensitivity level',
                '3': 'Define handling rules per classification level',
                '4': 'Capture evidence — screenshots, architecture diagrams, access controls',
                '5': 'Schedule periodic reviews',
            },
        },
        DATA_D2: {
            name: 'Data Sharing Governance',
            steps: {
                '1': 'Evaluate new sharing arrangements via data sharing risk assessment',
                '2': 'Complete internal approval workflow',
                '3': 'Record sharing event in data sharing/disclosure log',
                '4': 'Capture evidence — approval records, contractual documents',
                '5': 'Periodic review of sharing arrangements',
            },
        },
        DATA_D3: {
            name: 'Cross-Border Data Transfer Assessment',
            steps: {
                '1': 'Identify cross-border transfers in data inventory',
                '2': 'Conduct transfer risk assessment',
                '3': 'Apply safeguards and governance measures',
                '4': 'Document in cross-border section of governance attestation',
                '5': 'Export assessment package for audit',
            },
        },
        DATA_D4: {
            name: 'Annual Governance Attestation',
            steps: {
                '1': 'Review governance RACI matrix',
                '2': 'Verify policy status (governance, classification, sharing, retention)',
                '3': 'Complete attestation pack',
                '4': 'Obtain internal approval',
                '5': 'Export and archive attestation',
            },
        },
        DATA_D5: {
            name: 'Data Incident Handling',
            steps: {
                '1': 'Classify the incident using decision record',
                '2': 'Execute incident response per playbook',
                '3': 'Capture evidence — logs, screenshots, communications',
                '4': 'Conduct post-incident review',
                '5': 'If authority notification required: create submission case and submit to MIC',
                '6': 'Close and archive',
            },
        },
        ECOM_P1: {
            name: 'Notification (Sales Website/App)',
            steps: {
                '1': 'Determine classification: sales site/app vs marketplace/service platform',
                '2': 'Create internal dossier record with company identity, website/app info, and required disclosures',
                '3': 'Generate evidence pack — screenshots of disclosures, policy URLs, version history',
                '4': 'Submit via Online.gov.vn — upload documents, record timestamp and confirmation',
                '5': 'Track processing — if returned for amendment, capture reasons and resubmit',
                '6': 'Close — store confirmation, link evidence and audit trail',
            },
        },
        ECOM_P2: {
            name: 'Registration (Service Platforms / Marketplaces)',
            steps: {
                '1': 'Determine platform model and obligations — marketplace vs mixed vs social commerce',
                '2': 'Build registration dossier — application form, service provision scheme, T&Cs, platform rules',
                '3': 'Internal approvals — assign legal/compliance owner, approve for submission',
                '4': 'Submit via Online.gov.vn — upload dossier, record timestamp',
                '5': 'Processing loop — if returned for amendment, update and resubmit',
                '6': 'Approval and operationalization — store confirmation, maintain ongoing compliance registers',
            },
        },
        ECOM_P3: {
            name: 'Update/Change Notification',
            steps: {
                '1': 'Identify change trigger — material change to dossier items',
                '2': 'Generate change package — change summary, updated documents, evidence updates',
                '3': 'Submit update and record submission status',
                '4': 'Track processing and close',
            },
        },
        ECOM_P4: {
            name: 'Termination / Deactivation',
            steps: {
                '1': 'Prepare termination package — cease date, reason, consumer protection handling plan, proof of public notice',
                '2': 'Submit termination — record timestamp and confirmation',
                '3': 'Close — store final status and evidence',
            },
        },
        ECOM_P5: {
            name: 'Trustmark / Credibility Rating Program',
            steps: {
                '1': 'Eligibility and readiness review',
                '2': 'Prepare dossier using official form',
                '3': 'Submit and track',
                '4': 'Maintain ongoing compliance evidence',
            },
        },
        PDPL_P1: {
            name: 'DPIA Submission Filing to MPS',
            steps: {
                '1': 'Complete DPIA using Decree 356 form pathway (Mẫu số 02a/02b)',
                '2': 'Assemble cover documents and supporting evidence',
                '3': 'Submit one original dossier to Ministry of Public Security (MPS)',
                '4': 'Monitor for supplement requests from MPS and respond within deadline',
                '5': 'Retain dossier and evidence of submission for audit trail',
            },
        },
        PDPL_P2: {
            name: 'Cross-Border Transfer Application',
            steps: {
                '1': 'Complete cross-border transfer dossier using Decree 356 forms (Mẫu số 01a/01b, Mẫu số 09)',
                '2': 'Submit one original dossier to MPS within 60 days of processing commencement',
                '3': 'Notify MPS after successful transfer with contact details of receiving party',
                '4': 'Respond to supplement / correction / inspection requests from MPS within deadline',
            },
        },
        PDPL_P3: {
            name: 'Data Processing Service Certificate Application',
            steps: {
                '1': 'Prepare certification application dossier per Decree 356 requirements',
                '2': 'Submit application to MPS with required supporting documents',
                '3': 'Respond to MPS review comments and supplement requests',
                '4': 'Maintain certificate and renewal schedule',
            },
        },
        PDPL_P4: {
            name: 'Data Subject Rights Request Processing',
            steps: {
                '1': 'Receive and log request; verify identity of data subject within 2 working days',
                '2': 'Assess request type and determine applicable deadline (10/15/20 days by type)',
                '3': 'Execute fulfillment actions across relevant systems and departments',
                '4': 'Prepare and send response to data subject with outcome and rationale',
                '5': 'Close case and retain evidence for audit; document any refusal rationale',
            },
        },
        PDPL_P5: {
            name: 'Breach Notification to MPS',
            steps: {
                '1': 'Detect and assess the breach; determine notification obligation within 2 working days',
                '2': 'Prepare notification package per PDPL requirements',
                '3': 'Submit notification to MPS within 2 working days of discovery',
                '4': 'Respond to MPS follow-up requests; submit supplements and coordination evidence',
            },
        },
        TELECOM_T1: {
            name: 'Authority Request Handling',
            steps: {
                '1': 'Receive and log the authority request via intake record',
                '2': 'Verify authority identity, legal basis, and scope of request',
                '3': 'Conduct internal legal review and obtain management approval',
                '4': 'Prepare response package with assembled evidence',
                '5': 'Draft and approve response letter',
                '6': 'Create submission case and submit response via channel',
                '7': 'Log disclosure and track authority acknowledgment',
                '8': 'Close case and retain evidence for audit trail',
            },
        },
        TELECOM_T2: {
            name: 'Disclosure Management',
            steps: {
                '1': 'Receive disclosure request (user consent, legal obligation, or authority request)',
                '2': 'Complete disclosure approval workflow',
                '3': 'Execute disclosure and record in log register',
                '4': 'Capture evidence — approval records, communication records',
                '5': 'Periodic review of disclosure log',
            },
        },
        TELECOM_T3: {
            name: 'Confidentiality Policy and Access Control Setup',
            steps: {
                '1': 'Draft and approve confidentiality policy',
                '2': 'Draft and approve access control and logging policy',
                '3': 'Document processing activities in user information processing register',
                '4': 'Capture evidence — policy documents, implementation evidence',
                '5': 'Schedule periodic reviews',
            },
        },
        TELECOM_T4: {
            name: 'Telecom Incident Assessment',
            steps: {
                '1': 'Classify and assess the incident',
                '2': 'Determine notification requirements',
                '3': 'If authority notification required: create submission case',
                '4': 'Capture and preserve evidence',
                '5': 'Resolution and closure',
            },
        },
        TELECOM_T5: {
            name: 'Retention and Deletion Management',
            steps: {
                '1': 'Define retention and deletion schedule per data category',
                '2': 'Implement deletion procedures',
                '3': 'Capture evidence — deletion records',
                '4': 'Periodic review of schedules',
            },
        },
    },
    framework_fines: {
        VN_CYBERSEC_LAW: {
            'CYBERSEC-FINE-A': 'Incident Handling and Reporting Failures',
            'CYBERSEC-FINE-B': 'System Security Monitoring and Baseline Control Failures',
            'CYBERSEC-FINE-C': 'Authority Request Cooperation Failures',
            'CYBERSEC-FINE-D': 'Data Localization and Local Presence Violations',
            'CYBERSEC-FINE-E': 'Malware Prevention and System Protection Failures',
            'CYBERSEC-FINE-F': 'Platform/Service Provider Response Failures',
            'CYBERSEC-FINE-G': 'Vendor / Third-Party Security Failures',
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-A': 'Registration / Notification / Administrative Readiness',
            'ECOM-FINE-B': 'Complaints and Consumer Protection',
            'ECOM-FINE-C': 'Monitoring and Takedown Enforcement',
            'ECOM-FINE-D': 'Cross-Border Operations',
            'ECOM-FINE-E': 'Platform Rules and Transparency',
        },
        VN_PDPL: {
            'PDPL-FINE-A': 'Failure to Conduct DPIA',
            'PDPL-FINE-B': 'Unauthorized Cross-Border Transfer',
            'PDPL-FINE-C': 'Processing Without Lawful Basis',
            'PDPL-FINE-D': 'Failure to Respond to DSR',
            'PDPL-FINE-E': 'Failure to Notify Data Breach',
            'PDPL-FINE-F': 'Non-Compliant Processing Agreement',
            'PDPL-FINE-G': 'Sensitive Data Processing Violations',
            'PDPL-FINE-H': "Children's Data Processing Violations",
            'PDPL-FINE-I': 'Automated Decision-Making Violations',
            'PDPL-FINE-J': 'Non-Compliance with Authority Orders',
        },
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-A': 'Authority Request Handling Failures',
            'TELECOM-FINE-B': 'Confidentiality and Unauthorized Disclosure',
            'TELECOM-FINE-C': 'Access Control and Logging Failures',
            'TELECOM-FINE-D': 'Retention and Deletion Violations',
            'TELECOM-FINE-E': 'Telecom Incident Assessment Failures',
        },
    },
    framework_fines_detail: {
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-E': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'Immediate upon detection',
                remediation: [
                    'Mandatory incident classification',
                    'Mandatory severity and decision records',
                    'Mandatory evidence preservation for confidentiality incidents',
                    'Mandatory post-incident closure',
                ],
            },
            'TELECOM-FINE-D': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory retention schedule implementation',
                    'Mandatory deletion evidence preservation',
                    'Mandatory retention exception decision records',
                ],
            },
            'TELECOM-FINE-C': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory access-control policy implementation',
                    'Mandatory logging of access events',
                    'Mandatory review of access rights',
                ],
            },
            'TELECOM-FINE-B': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory confidentiality policy implementation',
                    'Mandatory disclosure approval workflow',
                    'Mandatory disclosure trail maintenance',
                    'Mandatory user information access controls',
                ],
            },
            'TELECOM-FINE-A': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'As specified in the authority request',
                remediation: [
                    'Mandatory intake and verification of authority request',
                    'Mandatory response within prescribed timeline',
                    'Mandatory disclosure log entry',
                    'Mandatory evidence preservation',
                ],
            },
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-E': {
                fine_individual: 'Up to 80 million VND',
                fine_org: 'Up to 160 million VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory platform rules publication and versioning',
                    'Mandatory user-facing disclosures',
                    'Mandatory seller/affiliate/livestream obligations evidenced',
                    'Mandatory control history and approval chain',
                ],
            },
            'ECOM-FINE-D': {
                fine_individual: 'Up to 80 million VND',
                fine_org: 'Up to 160 million VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory cross-border obligations assessment',
                    'Mandatory authorized representative readiness',
                    'Mandatory cross-border operations register',
                    'Mandatory evidence of compliance action',
                ],
            },
            'ECOM-FINE-C': {
                fine_individual: 'Up to 80 million VND',
                fine_org: 'Up to 160 million VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory review of prohibited or unlawful listings',
                    'Mandatory takedown decision records',
                    'Mandatory evidence preservation',
                    'Mandatory seller notification and closure trail',
                ],
            },
            'ECOM-FINE-B': {
                fine_individual: 'Up to 80 million VND',
                fine_org: 'Up to 160 million VND',
                deadlines: 'As specified per complaint handling obligations',
                remediation: [
                    'Mandatory complaint intake procedure',
                    'Mandatory resolution record',
                    'Mandatory complaint audit trail',
                    'Mandatory escalation for cross-border seller issues',
                ],
            },
            'ECOM-FINE-A': {
                fine_individual: 'Up to 80 million VND',
                fine_org: 'Up to 160 million VND',
                deadlines: 'Before commencing e-commerce operations',
                remediation: [
                    'Mandatory platform classification',
                    'Mandatory notification or registration completion',
                    'Mandatory evidence of portal submission',
                    'Mandatory update/termination tracking',
                ],
            },
        },
        VN_CYBERSEC_LAW: {
            'CYBERSEC-FINE-G': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory vendor security assessment',
                    'Mandatory vendor data access controls',
                    'Mandatory vendor incident notification clause',
                    'Mandatory periodic vendor security review',
                ],
            },
            'CYBERSEC-FINE-F': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'As specified in authority request',
                remediation: [
                    'Mandatory blocking / takedown / deletion / prevention after authority request',
                    'Mandatory provision of information',
                    'Mandatory technical integration or cooperation',
                    'Stop provision of service / stop connectivity in certain cases',
                ],
            },
            'CYBERSEC-FINE-E': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory implementation of anti-malware measures',
                    'Mandatory filtering systems',
                    'Mandatory action after authority guidance',
                    'Mandatory technical measures for prevention/detection/blocking',
                ],
            },
            'CYBERSEC-FINE-D': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'As specified in authority decision; ongoing obligation where triggered',
                remediation: [
                    'Mandatory data storage in Vietnam where required',
                    'Mandatory establishment of local branch / representative office',
                    'Mandatory system-log retention for investigation support',
                    'Mandatory compliance with authority localization decision',
                ],
            },
            'CYBERSEC-FINE-C': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'As specified in the authority request',
                remediation: [
                    'Mandatory provision of requested information',
                    'Mandatory compliance with written requests',
                    'Mandatory takedown / blocking / prevention measures when required',
                ],
            },
            'CYBERSEC-FINE-B': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'Ongoing obligation; controls must be in place before system operation',
                remediation: [
                    'Mandatory implementation of security rules and baseline controls',
                    'Mandatory monitoring and evaluation of compliance',
                    'Mandatory execution of required protection measures',
                    'Mandatory deletion / takedown / control action after authority request',
                ],
            },
            'CYBERSEC-FINE-A': {
                fine_individual: 'Up to 100 million VND',
                fine_org: 'Up to 200 million VND',
                deadlines: 'Immediate response required; reporting within prescribed timeline',
                remediation: [
                    'Mandatory implementation of incident response plan',
                    'Mandatory establishment of incident response team',
                    'Mandatory participation in national incident-response coordination',
                    'Mandatory 24/7 contact and log retention',
                ],
            },
        },
        VN_PDPL: {
            'PDPL-FINE-A': {
                fine_individual: 'Up to 1.5 billion VND',
                fine_org: 'Up to 3 billion VND',
                deadlines: '60 days from commencement of processing to submit dossier to MPS',
                remediation: [
                    'Mandatory completion and submission of DPIA dossier',
                    'Mandatory correction or completion of dossier when requested',
                    'Mandatory retention of dossier',
                ],
            },
            'PDPL-FINE-B': {
                fine_individual: 'Up to 1.5 billion VND',
                fine_org: 'Up to 3 billion VND',
                revenue: '3%–5% of prior-year Vietnam revenue in severe cases',
                deadlines: '60 days from commencement of processing to submit dossier to MPS; post-transfer notification required',
                remediation: [
                    'Mandatory submission of transfer dossier',
                    'Mandatory correction of dossier when requested',
                    'Compliance with transfer inspection requests',
                    'Mandatory notification to MPS after successful transfer',
                ],
            },
            'PDPL-FINE-C': {
                fine_individual: 'Up to 1.5 billion VND',
                fine_org: 'Up to 3 billion VND',
                deadlines: 'Ongoing obligation; consent must be obtained before processing',
                remediation: [
                    'Mandatory cessation of unlawful processing',
                    'Mandatory implementation of protection measures',
                    'Mandatory deletion / destruction of unlawfully processed data',
                    'Return of unlawful gains',
                ],
            },
            'PDPL-FINE-D': {
                fine_individual: 'Up to 1.5 billion VND',
                fine_org: 'Up to 3 billion VND',
                deadlines: '2 working days procedural response; 10 days access/correction; 15 days withdrawal/restriction/objection; 20 days deletion',
                remediation: [
                    'Mandatory fulfillment of request within applicable deadline',
                    'Mandatory notification of inability within 2 working days',
                    'Mandatory correction or rectification',
                    'Mandatory deletion with evidence',
                ],
            },
            'PDPL-FINE-E': {
                fine_individual: 'Up to 1.5 billion VND',
                fine_org: 'Up to 3 billion VND',
                deadlines: '2 working days to notify MPS after discovering a violation',
                remediation: [
                    'Mandatory notification to MPS',
                    'Mandatory correction / completion of notification',
                    'Mandatory coordination with authority',
                    'Mandatory remediation plan',
                ],
            },
            'PDPL-FINE-F': {
                fine_individual: 'Up to 1.5 billion VND',
                fine_org: 'Up to 3 billion VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory implementation of protection measures',
                    'Mandatory designation of responsible person for sensitive data',
                    'Mandatory communication with specialized authority',
                    'Mandatory remediation plan',
                ],
            },
            'PDPL-FINE-G': {
                fine_individual: 'Up to 1.5 billion VND',
                fine_org: 'Up to 3 billion VND',
                deadlines: 'Ongoing obligation; controls must be in place before processing sensitive data',
                remediation: [
                    'Mandatory implementation of enhanced protection measures',
                    'Mandatory designation of responsible person',
                    'Mandatory policy and governance framework implementation',
                ],
            },
            'PDPL-FINE-H': {
                fine_individual: 'Up to 1.5 billion VND',
                fine_org: 'Up to 3 billion VND',
                deadlines: 'Ongoing obligation; parental consent required before processing',
                remediation: [
                    'Mandatory cessation of processing without valid parental consent',
                    'Mandatory deletion of unlawfully processed data',
                    'Mandatory implementation of age verification measures',
                ],
            },
            'PDPL-FINE-I': {
                fine_individual: 'Up to 1.5 billion VND',
                fine_org: 'Up to 3 billion VND',
                deadlines: 'Ongoing obligation',
                remediation: [
                    'Mandatory disclosure of automated decision-making logic',
                    'Mandatory provision of human review mechanism',
                    'Mandatory implementation of objection handling process',
                ],
            },
            'PDPL-FINE-J': {
                fine_individual: 'Up to 1.5 billion VND',
                fine_org: 'Up to 3 billion VND',
                revenue: 'Up to 5% of prior-year Vietnam revenue for repeat violations',
                deadlines: 'As specified in the authority order',
                remediation: [
                    'Mandatory compliance with authority orders',
                    'Mandatory cessation of unlawful processing',
                    'Return of unlawful gains',
                    'Public correction / apology',
                ],
            },
        },
    },
    framework_procedures_detail: {
        VN_DP_DRAFT_P7: {
            trigger: 'Ongoing operation of the platform',
            outputs: [
                'Risk assessment record',
                'Early warning log',
                'Remediation record',
                'Review trail',
            ],
            channels: ['Platform workflow'],
            responsible: {
                1: 'Platform Operator / Risk',
                2: 'Platform Operator / Security',
                3: 'Platform Operator / Risk',
                4: 'Platform Operator / Security',
                5: 'Platform Operator',
                6: 'Compliance',
            },
            step_outputs: {
                1: ['Risk assessment record'],
                2: ['Early warning log'],
                3: ['Risk review findings'],
                4: ['Risk register entries'],
                5: ['Remediation record'],
                6: ['Review trail'],
            },
        },
        VN_DP_DRAFT_P6: {
            trigger: 'A participant submits a complaint or dispute request',
            outputs: [
                'Complaint intake record',
                'Dispute resolution record',
                'Evidence pack',
                'Case closure record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                1: 'Platform Operator',
                2: 'Platform Operator',
                3: 'Platform Operator',
                4: 'Platform Operator',
                5: 'Platform Operator / Legal',
                6: 'Platform Operator',
                7: 'Compliance',
            },
            step_outputs: {
                1: ['Complaint intake record'],
                2: ['Completeness check', 'Supplement request (if any)'],
                3: ['Handling approach notice'],
                4: ['Evidence pack'],
                5: ['Assessment findings'],
                6: ['Dispute resolution record'],
                7: ['Case closure record', 'Archived evidence pack'],
            },
        },
        VN_DP_DRAFT_P5: {
            trigger: 'Delivery is completed after contract and payment conditions are met',
            outputs: [
                'Delivery record',
                'Certificate of property rights over data',
                'Transaction register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                1: 'Seller / Platform Operator',
                2: 'Platform Operator',
                3: 'Platform Operator',
                4: 'Platform Operator',
                5: 'Platform Operator',
            },
            step_outputs: {
                1: ['Delivery record'],
                2: ['Delivery event log entry'],
                3: ['Property rights certificate'],
                4: ['Transaction register entry'],
                5: ['Verification access record'],
            },
        },
        VN_DP_DRAFT_P4: {
            trigger: 'A buyer initiates a transaction for data, product, or service',
            outputs: [
                'Transaction initiation record',
                'Electronic contract',
                'Payment confirmation',
                'Delivery authorization record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                1: 'Buyer',
                2: 'Buyer / Platform Operator',
                3: 'Platform Operator',
                4: 'Buyer / Seller',
                5: 'Platform Operator / Payment',
                6: 'Platform Operator',
            },
            step_outputs: {
                1: ['Listing review decision'],
                2: ['Transaction initiation record'],
                3: ['Electronic contract record'],
                4: ['Contract acceptance record'],
                5: ['Payment confirmation'],
                6: ['Delivery authorization record'],
            },
        },
        VN_DP_DRAFT_P3: {
            trigger: 'A participant wants to evaluate data products/services in the isolated environment',
            outputs: [
                'Sandbox request',
                'Sandbox scope record',
                'Output review record',
                'Sandbox termination record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                1: 'Participant',
                2: 'Platform Operator / Participant',
                3: 'Platform Operator / IT',
                4: 'Platform Operator / Security',
                5: 'Platform Operator',
                6: 'Platform Operator',
                7: 'Platform Operator / IT',
            },
            step_outputs: {
                1: ['Sandbox request'],
                2: ['Sandbox scope record'],
                3: ['Provisioned environment'],
                4: ['Security monitoring log'],
                5: ['Output review record'],
                6: ['Extraction approval decision'],
                7: ['Sandbox termination record'],
            },
        },
        VN_DP_DRAFT_P2: {
            trigger: 'A seller seeks to list data, product, or data service for trading',
            outputs: [
                'Listing application',
                'Lawful-source verification record',
                'Technical validation record',
                'Quality disclosure record',
                'Listing approval record',
                'Listing register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                1: 'Seller',
                2: 'Seller / Legal',
                3: 'Seller / IT',
                4: 'Seller',
                5: 'Seller',
                6: 'Platform Operator',
                7: 'Platform Operator',
                8: 'Platform Operator',
            },
            step_outputs: {
                1: ['Listing application'],
                2: ['Lawful-source verification record'],
                3: ['Technical validation record'],
                4: ['Quality disclosure record'],
                5: ['Submitted listing dossier'],
                6: ['Review findings'],
                7: ['Listing approval record'],
                8: ['Published listing', 'Listing register entry'],
            },
        },
        VN_DP_DRAFT_P1: {
            trigger: 'A seller or buyer joins the data platform',
            outputs: [
                'Seller / buyer onboarding record',
                'Participant register entry',
                'Account setup evidence',
                'Approval record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                1: 'Platform Operator',
                2: 'Platform Operator / Legal',
                3: 'Platform Operator / Legal',
                4: 'Platform Operator',
                5: 'Platform Operator',
                6: 'Platform Operator',
                7: 'Compliance',
            },
            step_outputs: {
                1: ['Participant type decision'],
                2: ['Legal capacity verification record'],
                3: ['Conditional-business check record'],
                4: ['Transaction account registration record'],
                5: ['Payment account link record'],
                6: ['Participation approval record'],
                7: ['Participant register entry', 'Audit trail'],
            },
        },
        PDPL_P1: {
            trigger: 'Commencement of personal data processing requiring impact assessment',
            outputs: ['DPIA dossier', 'Submission confirmation', 'Supplement package'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                1: 'DPO / Privacy Team',
                2: 'Privacy Team',
                3: 'Legal / Compliance',
                4: 'DPO / Privacy Team',
                5: 'Compliance Team',
            },
            step_outputs: {
                1: ['Completed DPIA dossier'],
                2: ['Dossier package', 'Cover letter'],
                3: ['Submission confirmation', 'Tracking reference'],
                4: ['Supplement package', 'Updated dossier'],
                5: ['Archived dossier', 'Audit evidence pack'],
            },
        },
        PDPL_P2: {
            trigger: 'Intent to transfer personal data outside Vietnam',
            outputs: [
                'Transfer dossier',
                'Submission confirmation',
                'Post-transfer notification',
                'Supplement response',
            ],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                1: 'DPO / Privacy Team',
                2: 'Legal / Compliance',
                3: 'Legal / Compliance',
                4: 'DPO / Privacy Team',
            },
            step_outputs: {
                1: ['Transfer dossier package'],
                2: ['Submission confirmation', 'Tracking reference'],
                3: ['Post-transfer notification'],
                4: ['Supplement response', 'Updated dossier'],
            },
        },
        PDPL_P3: {
            trigger: 'Organization providing data processing services requiring certification',
            outputs: ['Certification dossier', 'Certificate', 'Renewal record'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                1: 'Legal / Compliance',
                2: 'Legal / Compliance',
                3: 'DPO / Privacy Team',
                4: 'Compliance Team',
            },
            step_outputs: {
                1: ['Certification application dossier'],
                2: ['Submission confirmation'],
                3: ['Supplement response'],
                4: ['Certificate record', 'Renewal tracker'],
            },
        },
        PDPL_P4: {
            trigger: 'Receipt of a data subject rights request',
            outputs: [
                'DSR intake record',
                'Identity verification',
                'Response letter',
                'Case closure report',
            ],
            channels: ['Online portal', 'Email', 'In-person'],
            responsible: {
                1: 'Privacy Team / Customer Service',
                2: 'DPO / Privacy Team',
                3: 'Department Leads / IT',
                4: 'DPO / Privacy Team',
                5: 'Compliance Team',
            },
            step_outputs: {
                1: ['DSR intake record', 'Identity verification record'],
                2: ['Request assessment', 'Deadline assignment'],
                3: ['Fulfillment evidence', 'Department confirmations'],
                4: ['Response letter'],
                5: ['Case closure report', 'Audit evidence pack'],
            },
        },
        PDPL_P5: {
            trigger: 'Discovery of a personal data protection violation or breach',
            outputs: [
                'Breach assessment record',
                'Notification package',
                'Submission confirmation',
                'Supplement package',
            ],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                1: 'Incident Response Team / DPO',
                2: 'DPO / Privacy Team',
                3: 'Legal / Compliance',
                4: 'DPO / Incident Response Team',
            },
            step_outputs: {
                1: ['Breach assessment record', 'Notification decision record'],
                2: ['Breach notification package'],
                3: ['Submission confirmation', 'Tracking reference'],
                4: ['Supplement package', 'Coordination evidence'],
            },
        },
    },
    framework_deadline_references: {
        'PDPL Art. 9': 'PDPL Art. 9',
        'PDPL Art. 17': 'PDPL Art. 17',
        'PDPL Art. 23': 'PDPL Art. 23',
        'Decree 13 Art. 24': 'Decree 13 Art. 24',
        'MoPS Admin Procedures': 'MoPS Admin Procedures',
        'Annual cycle': 'Annual cycle',
        'Decree 13/2023/NĐ-CP Art. 24': 'Decree 13/2023/NĐ-CP Art. 24',
        'Decree 356/2025/NĐ-CP Art. 25(4)': 'Decree 356/2025/NĐ-CP Art. 25(4)',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2': 'Decree 356/2025/NĐ-CP Art. 5 Cl. 2',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5': 'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 3': 'Decree 356/2025/NĐ-CP Art. 5 Cl. 3',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 4': 'Decree 356/2025/NĐ-CP Art. 5 Cl. 4',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 5': 'Decree 356/2025/NĐ-CP Art. 5 Cl. 5',
        'Decree 356/2025/NĐ-CP Art. 8(3)': 'Decree 356/2025/NĐ-CP Art. 8(3)',
        'Draft Decree on Data Platforms 2026': 'Draft Decree on Data Platforms 2026',
        'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025': 'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025',
        'Law 122/2025/QH15 Ch. IV (Consumer Protection)': 'Law 122/2025/QH15 Ch. IV (Consumer Protection)',
        'Law 122/2025/QH15 — Online.gov.vn operational procedure': 'Law 122/2025/QH15 — Online.gov.vn operational procedure',
        'Law 134/2025/QH15 Article 12': 'Law 134/2025/QH15 Article 12',
        'Law 134/2025/QH15 Article 21': 'Law 134/2025/QH15 Article 21',
        'Law 134/2025/QH15 Article 27': 'Law 134/2025/QH15 Article 27',
        'Law 134/2025/QH15 Articles 9–10': 'Law 134/2025/QH15 Articles 9–10',
        'Law 24/2023/QH15 — article mapping pending': 'Law 24/2023/QH15 — article mapping pending',
        'Law 60/2024/QH15 (internal control target)': 'Law 60/2024/QH15 (internal control target)',
        'Law 91/2025/QH15 Art. 21': 'Law 91/2025/QH15 Art. 21',
        'Law 91/2025/QH15 Art. 23': 'Law 91/2025/QH15 Art. 23',
        'Policy-defined': 'Policy-defined',
        Quarterly: 'Quarterly',
        'QĐ 778/QĐ-BCA-A05 administrative procedure': 'QĐ 778/QĐ-BCA-A05 administrative procedure',
        'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)': 'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)',
        'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)': 'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)',
        'source verification pending': 'source verification pending',
        'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)': 'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)',
        'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)',
        'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)',
        'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)',
        "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)": "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)",
        'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)',
        'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)',
        'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)',
        'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)': 'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)',
        'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)': 'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)',
        'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Governance': 'Circular 83/2025/TT-NHNN — Internal Audit Governance',
        'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)',
        'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)': 'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)',
        'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)',
        'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)': 'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)',
        'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)': 'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)',
        'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Controls': 'Circular 83/2025/TT-NHNN — Outsourcing Controls',
        'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)',
        'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)': 'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)',
        'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)': 'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)',
        'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01',
        'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)': 'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)',
        'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)': 'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)',
        'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)': 'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)',
        'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)',
        'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)',
        'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01',
        'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)',
        'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)',
        'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)': 'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)',
        'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)': 'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)',
        'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)',
    },
    framework_authorities: {
        'Quốc hội (National Assembly)': 'Quốc hội (National Assembly)',
        'Ministry of Public Security / A05 Department': 'Ministry of Public Security / A05 Department',
        'Ministry of Public Security': 'Ministry of Public Security',
        'Ministry of Information and Communications': 'Ministry of Information and Communications',
        'Government of Vietnam': 'Government of Vietnam',
        'European Data Protection Board (EDPB)': 'European Data Protection Board (EDPB)',
        'ENISA / National CSIRTs': 'ENISA / National CSIRTs',
        'State Bank of Vietnam': 'State Bank of Vietnam',
    },
    tour_titles: {
        access_accountability: 'Access Accountability Tour',
        assessments: 'Assessments Tour',
        audit_trail: 'Audit Trail Tour',
        classification: 'Classification Tour',
        compliance_forms: 'Compliance Forms Tour',
        connectors: 'Connectors Tour',
        consent_properties: 'Consent Properties Tour',
        consent: 'Consent Tour',
        dashboard: 'Dashboard Tour',
        data_mapping: 'Data Mapping Tour',
        data_subjects: 'Data Subjects Tour',
        deletion_orchestration: 'Deletion Orchestration Tour',
        discovery: 'Discovery Tour',
        governance: 'Governance Tour',
        help_center: 'Help Center Tour',
        incident_ops: 'Incident Operations Tour',
        incidents: 'Incidents Tour',
        localization: 'Localization Tour',
        monitoring: 'Monitoring Tour',
        onboarding: 'Onboarding Tour',
        organizations: 'Organizations Tour',
        partner_workspace: 'Partner Workspace Tour',
        platform_settings: 'Platform Settings Tour',
        privacy_scanner: 'Privacy Scanner Tour',
        program_governance: 'Program Governance Tour',
        rights_requests: 'Rights Requests Tour',
        task_management: 'Task Management Tour',
        vendor_risk: 'Vendor Risk Tour',
    },
    tour_descriptions: {
        access_accountability: 'As the Internal Audit Lead (P-VN-10), review access evidence, audit permission usage, and investigate anomalous patterns to demonstrate GDPR Article 5(1)(f) integrity and confidentiality.',
        assessments: 'As the Enterprise DPO (P-VN-01), conduct DPIAs following Decree 356 Mau so 02a, identify compliance gaps, and assess cross-border transfer risks under PDPL.',
        audit_trail: 'As the Internal Audit Lead (P-VN-10), query cross-module evidence, assemble audit packs, and export tamper-evident logs for regulatory submissions.',
        classification: 'As the Data Steward (P-VN-05), define data classification taxonomies, assign sensitivity labels, enforce retention policies, and resolve coverage gaps required for PDPL compliance.',
        compliance_forms: 'As the Enterprise DPO (P-VN-01), browse, complete, and submit official Vietnamese regulatory forms including Phu luc I, Phu luc II, and Mau so templates for PDP Committee filings.',
        connectors: 'As the IT Systems Lead (P-VN-05), capture business requirements for integrations, map connectors to compliance data flows, and verify POC readiness before going live.',
        consent_properties: 'As the Enterprise DPO (P-VN-01), configure CMP deployment: digital property registration, golden templates for consistent PDPL consent banners, and binding matrices that link properties to processing activities.',
        consent: 'As the Enterprise DPO (P-VN-01), manage the consent policy lifecycle: define collection purposes, enforce PDPL withdrawal rights, and integrate with your Consent Management Platform.',
        dashboard: 'As the Enterprise DPO (P-VN-01), orient yourself around the compliance posture command center that surfaces PDPL deadlines, breach SLAs, and audit-readiness KPIs.',
        data_mapping: 'As the Data Steward (P-VN-05), build your ROPA inventory, map cross-border data flows, and maintain the processing activity register required by PDPL.',
        data_subjects: 'As the Enterprise DPO (P-VN-01) or Education Compliance Lead (P-VN-13), manage unified data subject profiles that link consent records, rights requests, and timeline activity across the platform.',
        deletion_orchestration: 'As the DPO (P-VN-01), verify end-to-end data deletion across internal systems and third-party vendors, with cryptographic proof chains for GDPR Article 17 right-to-erasure compliance.',
        discovery: 'As the Data Steward (P-VN-05), run automated scans to discover personal data across connected systems, review staged findings, and feed results into the Classification module for PDPL labeling.',
        governance: 'As the Vendor Risk Manager (P-VN-08), maintain the vendor registry, track subprocessor chains, assess cross-border transfer risks, and enforce governance playbooks under PDPL.',
        help_center: 'For all personas -- DPOs, auditors, IT leads, and compliance managers -- find answers, how-to guides, and module-specific documentation to support your daily compliance workflows.',
        incident_ops: 'As the Incident Commander (P-VN-09), activate response playbooks, track SLA compliance against the 72-hour PDPL notification window, manage escalations, and coordinate vendor response.',
        incidents: 'As the Incident Commander (P-VN-09), detect and report data breaches with 72-hour PDPL notification obligations to the PDP Committee, triage by severity, and build audit-grade incident dossiers.',
        localization: 'As the Enterprise DPO (P-VN-01), manage the regulatory framework lifecycle: install Vietnamese market packs, activate PDPL and Decree 13 frameworks, map requirements to platform modules, and manage translations.',
        monitoring: 'As the Enterprise DPO (P-VN-01), establish ongoing compliance monitoring programs, track regulatory changes, measure KPIs, and manage regulatory interaction cases under PDPL.',
        onboarding: 'As the Compliance Trainer (P-VN-02), deliver training via guided tours, load realistic sample data for hands-on exercises, and run the setup wizard for quick-start scenarios.',
        organizations: 'As the DPO (P-VN-01), configure multi-organization compliance structures, establish department hierarchies, and route data subject requests across your corporate group.',
        partner_workspace: 'As a Partner Account Manager, onboard partner organizations, configure service tiers, track compliance deliverables, and manage the partner marketplace.',
        platform_settings: 'As the Platform Administrator, manage all operational configuration -- identity, security, integrations, and module access that underpin your compliance program.',
        privacy_scanner: 'As the Privacy Analyst (P-VN-06), run continuous monitoring scans to detect cookie consent violations, unauthorized trackers, and policy gaps -- generating evidence for ePrivacy and GDPR compliance.',
        program_governance: 'Navigate between Maturity Models, Assessments, Evidence Gaps, Executive Summaries, and more. Each sub-module contributes to a holistic view of your internal program health.',
        rights_requests: 'As the Enterprise DPO (P-VN-01), manage data subject rights requests under PDPL with 15-day statutory deadlines, identity verification, and audit-grade resolution trails.',
        task_management: 'Route compliance tasks across personas, track SLA deadlines, and automate workflows that keep your team accountable.',
        vendor_risk: 'As the Vendor Risk Manager (P-VN-08), score third-party risk, enforce gate reviews, and trigger reassessments -- distinct from Program Governance, which tracks your internal maturity.',
    },
    // ---------- Consolidation (Sprint 28.7.1) ----------
    consolidation: {
        title: 'Consolidation Cases',
        new_case: 'Create Consolidation Case',
        case_count: 'case',
        created: 'Consolidation case created',
        create: 'Create Case',
        creating: 'Creating...',
        cancel: 'Cancel',
        no_cases: 'No consolidation cases found.',
        all_statuses: 'All Statuses',
        all_purposes: 'All Purposes',
        filter_status: 'Filter by status',
        filter_purpose: 'Filter by purpose',
        col_title: 'Title',
        col_purpose: 'Purpose',
        col_status: 'Status',
        col_progress: 'Progress',
        col_deadline: 'Deadline',
        col_department: 'Department',
        col_version: 'Version',
        col_evidence: 'Evidence',
        sections_accepted: 'accepted',
        prev: 'Previous',
        next: 'Next',
        page: 'Page',
        prev_page: 'Previous page',
        next_page: 'Next page',
        field_title: 'Title',
        field_purpose: 'Purpose',
        field_framework: 'Framework Code',
        field_deadline: 'Deadline',
        field_reviewer: 'Reviewer User ID',
        title_placeholder: 'Enter case title',
        framework_placeholder: 'e.g., PDPD-2023',
        reviewer_placeholder: 'Enter reviewer user ID',
        purposes: {
            dpia_filing: 'DPIA Filing',
            cross_border_filing: 'Cross-Border Filing',
            annual_governance_attestation: 'Annual Governance Attestation',
            breach_package: 'Breach Package',
            audit_inspection_pack: 'Audit Inspection Pack',
        },
        statuses: {
            draft: 'Draft',
            collecting: 'Collecting',
            consolidating: 'Consolidating',
            review: 'Review',
            corrections: 'Corrections',
            pending_approval: 'Pending Approval',
            approved: 'Approved',
            completed: 'Completed',
            closed: 'Closed',
            rejected: 'Rejected',
        },
        section_statuses: {
            pending: 'Pending',
            in_progress: 'In Progress',
            submitted: 'Submitted',
            accepted: 'Accepted',
            needs_correction: 'Needs Correction',
            corrected: 'Corrected',
        },
        flag_types: {
            missing_section: 'Missing Section',
            conflicting_entry: 'Conflicting Entry',
            stale_entry: 'Stale Entry',
            missing_evidence: 'Missing Evidence',
            incomplete_data: 'Incomplete Data',
            duplicate_entry: 'Duplicate Entry',
        },
        flag_severities: {
            critical: 'Critical',
            warning: 'Warning',
            info: 'Info',
        },
        sections_title: 'Sections',
        no_sections: 'No sections assigned yet.',
        flags_title: 'Flags',
        no_flags: 'No flags detected.',
        open: 'open',
        approval_chain: 'Approval Chain',
        pending: 'Pending',
        audit_trail: 'Audit Trail',
        no_audit: 'No audit events.',
        auto_assign: 'Auto-Assign Sections',
        start_collection: 'Start Collection',
        review_case: 'Review Case',
        generate_export: 'Generate Export',
        review_title: 'Consolidation Review',
        advance_approval: 'Advance to Approval',
        back_to_case: 'Back to Case',
        accept: 'Accept',
        return_section: 'Return',
        return_comment: 'Return comment...',
        resolve: 'Resolve',
        dismiss: 'Dismiss',
        resolution_notes: 'Resolution notes...',
        notes: 'Notes',
        section_accepted: 'Section accepted',
        section_returned: 'Section returned for correction',
        advanced_to_approval: 'Case advanced to pending approval',
        section_data: 'Section Data',
        save_progress: 'Save Progress',
        submit_section: 'Submit Section',
        submitting: 'Submitting...',
        resubmit: 'Resubmit',
        progress_saved: 'Progress saved',
        section_submitted: 'Section submitted',
        correction_required: 'Correction Required',
        evidence_title: 'Evidence',
        no_evidence: 'No evidence attached.',
        add_evidence: 'Add Evidence',
        evidence_added: 'Evidence added',
        evidence_files: 'evidence files',
        uploaded_by: 'uploaded by',
        comments_title: 'Comments',
        no_comments: 'No comments yet.',
        comment_placeholder: 'Add a comment...',
        add_comment: 'Add Comment',
        comment_added: 'Comment added',
        view_data: 'View submitted data',
        export_title: 'Dossier Export',
        export_generated: 'Export package generated',
        regenerate: 'Regenerate',
        regenerating: 'Regenerating...',
        generated_at: 'Generated at',
        artifacts_title: 'Export Artifacts',
        artifact_type: 'Artifact',
        file_name: 'File Name',
        file_size: 'Size',
        generated: 'Generated',
        download: 'Download',
        artifacts: {
            master_report: 'Master Report',
            evidence_bundle: 'Evidence Bundle (ZIP)',
            contributor_lineage: 'Contributor Lineage',
            conflict_resolution: 'Conflict Resolution',
            approval_history: 'Approval History',
        },
        official_forms: 'Official Forms',
        forms_description: 'The following official forms can be generated from the consolidated data. Official forms are separate from internal working papers.',
        announce_loaded_cases: 'Loaded {count} consolidation cases',
        announce_case_detail_loaded: 'Case detail loaded',
        announce_case_transitioned: 'Case transitioned to {status}',
        announce_flag_resolved: 'Flag resolved',
        announce_flag_dismissed: 'Flag dismissed',
        case_detail_loaded: 'Case detail loaded',
        sections_auto_assigned: 'Sections auto-assigned from department hierarchy',
        errors: {
            load_failed: 'Failed to load data. Please try again.',
            create_failed: 'Failed to create case. Please try again.',
            required_fields: 'Please fill all required fields.',
            not_found: 'Not found.',
            comment_required: 'Comment is required when returning a section.',
            notes_required: 'Notes are required when resolving or dismissing a flag.',
        },
    },
    // ---------- Rights Request Import (Sprint 40.0.0) ----------
    rights_requests: {
        import: {
            title: 'Import Cases',
            nav_title: 'Bulk Import',
            description: 'Upload a CSV file to bulk import rights request cases.',
            select_file: 'Select CSV File',
            file_help: 'CSV with headers matching case fields.',
            file_too_large: 'File too large. Maximum size is 2MB.',
            preview: 'Preview',
            columns_detected: 'Columns detected: {count}',
            column_mapping: 'Column Mapping',
            import_button: 'Import',
            import_complete: 'Import complete',
            import_error: 'Import failed',
            result: 'Import Result',
            total: 'Total',
            imported: 'Imported',
            skipped: 'Skipped',
            failed: 'Failed',
            errors: 'Errors',
            error_row: 'Row',
            error_field: 'Field',
            error_message: 'Message',
            history: 'Import History',
            no_history: 'No import history.',
            history_file: 'Filename',
            history_date: 'Date',
        },
        // Sprint 48.1.1 (finish) — DSR Batch Process page (Forseti AI workflow).
        batch: {
            nav_title: 'Batch Process',
            page_title: 'DSR Batch Process',
            page_subtitle: 'Upload a CSV of pending DSRs. Forseti drafts a per-request acknowledgement response and queues a verification task per row. The DPO reviews + accepts each before send.',
            upload_heading: 'Upload',
            csv_hint: 'Required columns: request_id, requester_email, request_type. Optional: deadline (ISO-8601), notes.',
            rows_heading: '{count} rows parsed',
            submit_label: 'Run batch',
            submitting: 'Drafting…',
            col_request_id: 'Request id',
            col_email: 'Requester email',
            col_type: 'Type',
            col_status: 'Status',
            col_actions: 'Actions',
            status_queued: 'Queued',
            status_drafted: 'Drafted',
            status_failed: 'Failed',
            status_accepted: 'Accepted',
            status_rejected: 'Rejected',
            'status_parsing-error': 'Parse error',
            accept: 'Accept',
            reject: 'Reject',
            permission_denied: 'You do not have permission to run Forseti workflows.',
        },
    },
    // ---------- Assessment Triggers settings (Sprint 40.1.0) ----------
    assessment_triggers: {
        title: 'Assessment Triggers',
        subtitle: 'Configure automated assessment creation and DPO notifications for privacy workflows.',
        auto_create_dpia_label: 'Auto-create DPIA when processing activity marked as DPIA required',
        auto_notify_new_purpose_label: 'Auto-notify DPO when new purpose created with sensitive data categories',
        auto_notify_cross_border_label: 'Auto-notify DPO when cross-border transfer added to processing activity',
        default_dpo_label: 'Default DPO',
        default_dpo_placeholder: 'Select default DPO...',
        notification_channels_label: 'Notification Channels',
        channel_in_app: 'In-App',
        channel_email: 'Email',
        save_success: 'Assessment trigger settings saved.',
        save_error: 'Failed to save assessment trigger settings.',
        fetch_error: 'Failed to load assessment trigger settings.',
        fetch_users_error: 'Failed to load users.',
        announce_loaded: 'Assessment trigger settings loaded',
        dpia_status: 'DPIA Status',
        dpia_linked: 'DPIA Linked',
        dpia_required_no_dpia: 'DPIA Required — Not Created',
        create_dpia: 'Create DPIA',
        create_dpia_success: 'DPIA created and linked to activity.',
        create_dpia_error: 'Failed to create DPIA for activity.',
        view_dpia: 'View DPIA',
    },
    // ---------- Data Subject Profiles (Sprint 40.2.0) ----------
    data_subjects: {
        title: 'Data Subject Profiles',
        nav_title: 'Data Subjects',
        nav: {
            profiles: 'Profiles',
        },
        create: 'Create Profile',
        edit: 'Edit Profile',
        delete: 'Delete Profile',
        delete_confirm: 'Are you sure you want to delete this data subject profile?',
        display_name: 'Display Name',
        email: 'Email',
        phone: 'Phone',
        employee_id: 'Employee ID',
        student_id: 'Student ID',
        notes: 'Notes',
        external_refs: 'External References',
        cases_count: 'Cases',
        workflows_count: 'Consent Records',
        last_activity: 'Last Activity',
        timeline: 'Timeline',
        link_case: 'Add DSR Case',
        link_workflow: 'Add Consent Workflow',
        no_profiles: 'No data subject profiles found.',
        search_placeholder: 'Search by name, email, phone, or ID...',
        create_success: 'Data subject profile created.',
        create_error: 'Failed to create data subject profile.',
        update_success: 'Data subject profile updated.',
        update_error: 'Failed to update data subject profile.',
        delete_success: 'Data subject profile deleted.',
        delete_error: 'Failed to delete data subject profile.',
        link_case_success: 'Case linked to profile.',
        link_case_error: 'Failed to link case to profile.',
        link_workflow_success: 'Workflow linked to profile.',
        link_workflow_error: 'Failed to link workflow to profile.',
        load_error: 'Failed to load data subject profiles.',
        detail_load_error: 'Failed to load data subject profile.',
        timeline_load_error: 'Failed to load timeline.',
        tab_cases: 'DSR Cases',
        tab_consent: 'Consent Records',
        tab_timeline: 'Timeline',
        tab_consent_workflows: 'Consent Workflows',
        tab_web_consent: 'CMP Consent',
        web_consent_empty: 'No CMP consent records found for this subject.',
        web_consent_load_error: 'Failed to load CMP consent records.',
        col_policy_version: 'Policy Version',
        col_versions: 'Versions',
        col_consent_version: 'Consent',
        col_cookie_declaration_version: 'Cookie Declaration',
        col_purposes: 'Purposes',
        purpose_granted: 'Granted',
        purpose_denied: 'Denied',
        granted_domains: 'Granted',
        denied_domains: 'Denied',
        col_action: 'Action',
        col_timestamp: 'Timestamp',
        col_domain: 'Domain',
        no_cases: 'No linked cases.',
        no_workflows: 'No linked consent workflows.',
        no_timeline: 'No timeline events.',
        case_id: 'Case ID',
        case_status: 'Status',
        case_type: 'Request Type',
        case_created: 'Created',
        workflow_id: 'Workflow ID',
        workflow_type: 'Type',
        workflow_status: 'Status',
        workflow_created: 'Created',
        timeline_date: 'Date',
        timeline_event: 'Event',
        timeline_details: 'Details',
        timeline_time: 'Time',
        timeline_type: 'Type',
        timeline_summary: 'Summary',
        type_case: 'DSR Case',
        type_consent_workflow: 'Consent Workflow',
        profile_info: 'Profile Information',
        announce_loaded: 'Data subject profiles loaded',
        announce_detail_loaded: 'Data subject profile loaded',
        select_case: '— Select a case —',
        select_workflow: '— Select a workflow —',
        no_cases_hint: 'Link cases to see them here.',
        no_workflows_hint: 'Link consent workflows to see them here.',
    },
    // fix-sprint-20260415-0434 F13 — Visual Data Flow Map (FlowMapView) keys.
    flows: {
        map: {
            description_prefix: '{systems} systems · {flows} flows · {crossBorder} cross-border · {highRisk} high/critical risk',
            switch_to_list: 'Switch to list view',
            switch_to_list_aria: 'Switch to table view',
            svg_aria: 'Data flow map: {systems} systems, {flows} flows, {crossBorder} cross-border',
            selected_system: 'Selected system',
            drill_hint: 'Open the list tab to edit the linked flows, DPIAs, and vendor assignments for this system.',
        },
    },
    packages: {
        light: {
            name: 'Light',
            description: 'Essential privacy compliance tools for small teams',
        },
        standard: {
            name: 'Standard',
            description: 'Full-featured compliance platform for growing organizations',
        },
        enterprise_unlimited: {
            name: 'Enterprise Unlimited',
            description: 'Enterprise-grade compliance with multi-org support and dedicated services',
        },
    },
    // Sprint 47.5.1 — Forseti AI Legal Advisor: provider settings + usage dashboard
    forseti: {
        providerSettings: {
            title: 'Forseti AI Provider Settings',
            subtitle: 'Configure the Claude provider for the Forseti AI Legal Advisor.',
            healthHeading: 'Provider health',
            statusLabel: 'Status',
            statusOk: 'OK',
            statusFailed: 'Unavailable',
            providerLabel: 'Provider',
            providerClaudeApi: 'Claude API',
            providerCliRelay: 'Claude CLI relay',
            providerAesirManaged: 'Aesir-managed tokens',
            lastCheckedLabel: 'Last checked',
            reasonLabel: 'Reason',
            refreshButton: 'Refresh',
            apiKeyHeading: 'Claude API key',
            apiKeyHint: 'Setting an API key activates Claude API mode. Leave unset to use the local CLI relay (developer / self-hosted mode).',
            apiKeyLabel: 'API key',
            apiKeyMinLength: 'Minimum 32 characters. Encrypted at rest; never shown again.',
            apiKeyTooShort: 'API key must be at least 32 characters.',
            apiKeySaved: 'API key saved. Refreshing health…',
            apiKeyCleared: 'API key cleared. Forseti now uses the local CLI relay.',
            setKeyButton: 'Save API key',
            clearKeyButton: 'Clear API key (use CLI relay)',
        },
        usage: {
            title: 'Usage',
            subtitle: 'Token consumption and bucket status for this organization.',
            bucketHeading: 'Monthly token bucket',
            remainingLabel: 'Remaining',
            consumedLabel: 'Consumed',
            totalLabel: 'Total',
            unitsLabel: 'billable units',
            lastReconciledLabel: 'Last reconciled',
            recentHeading: 'Recent activity',
            historyHeading: 'Last 12 periods',
            noActivity: 'No activity yet this period.',
            featureLabel: 'Feature',
            whenLabel: 'When',
            periodLabel: 'Period',
            // Sprint 47.8.0e — Top-up CTA + summary-card polish.
            topUpButton: 'Request a top-up bundle',
            topUpHint: 'Contact your account manager to request a top-up bundle.',
            // Sprint 48.5.x — self-service "Buy More" billing (two flows).
            buyMore: {
                // Flow A — purchase
                buyButton: 'Buy more units',
                purchaseTitle: 'Buy more billing units',
                purchaseSubtitle: 'Choose a bundle. You will be taken to secure Stripe checkout, and your invoice will arrive by email.',
                packageLabel: 'Bundle',
                optionTemplate: '{name} — {units} {units_label} — {price}',
                packageSmall: 'Small',
                packageMedium: 'Medium',
                packageLarge: 'Large',
                checkoutButton: 'Continue to checkout',
                checkoutPending: 'Opening checkout…',
                cancelButton: 'Cancel',
                purchaseError: 'Could not start checkout. Please try again shortly.',
                // Flow B — redeem
                redeemTitle: 'Redeem an invoice',
                redeemSubtitle: 'Already paid? Select the bundle you purchased and enter the Invoice Number from your email to add the units.',
                invoiceLabel: 'Invoice Number',
                invoicePlaceholder: 'JJVExxxX-0001',
                redeemButton: 'Redeem',
                redeemPending: 'Redeeming…',
                redeemSuccess: 'Units added to your bucket.',
                redeemAlready: 'This invoice has already been redeemed.',
                errorNotFound: 'No invoice was found with that number. Check the Invoice Number and try again.',
                errorNotPaid: 'This invoice is not marked paid yet.',
                errorOrgMismatch: 'This invoice was not issued to your organisation.',
                errorPackageMismatch: 'This invoice does not match the selected bundle.',
                errorInvalidInvoice: 'Enter a valid Invoice Number.',
                errorGeneric: 'Redeem failed. Please try again shortly.',
            },
            // Sprint 47.5.9.6 — by-feature breakdown + admin per-user view.
            byFeatureHeading: 'This month by feature',
            byFeatureEmpty: 'No feature activity yet this period.',
            featureColumn: 'Feature',
            featureUnitsColumn: 'Units',
            featureRunsColumn: 'Runs',
            byUserHeading: 'This month by user (admin view)',
            byUserEmpty: 'No per-user activity yet this period.',
            userColumn: 'User',
            reconciliationLinkLabel: 'View reconciliation drift report',
            reconciliationLinkHint: 'Aesir-internal admin only.',
            featureLabels: {
                chat: 'Chat',
                workflow: 'Workflow run',
                tabular: 'Tabular extraction',
                redline: 'Redline review',
                title: 'Conversation title',
                unknown: 'Other',
            },
        },
        // Sprint 47.5.9.6 — Forseti reconciliation drift report (Aesir-internal only).
        reconciliation: {
            title: 'Forseti Quota Reconciliation',
            subtitle: 'Drift between the fast quota counter cache and the authoritative billing ledger. The reconciler runs every 5 minutes; this page surfaces the latest run and history. Aesir-internal admin only.',
            backLink: 'Back to Forseti Usage',
            stubHeading: 'Reconciliation history coming soon',
            stubBody: 'The reconciler worker logs drift via the structured log channel. A backend read endpoint that exposes the last 30 reconciliation runs is being scoped (deferred from Sprint 47.5.9.6 — see workfile). Once the endpoint lands, this page will show the run history table + P0 drift banner.',
            stubBackendOwner: 'Backend deferral: persist reconciliation runs in a new collection + add `GET /llm-relay/quota/reconciliation` route.',
            permissionDenied: 'This page is restricted to Aesir-internal administrators. If you believe you should have access, contact your AesirX representative.',
        },
        // Sprint 47.5.2 — Forseti Projects (workspace shell).
        projects: {
            title: 'Projects',
            subtitle: 'Workspaces for documents, conversations, workflows, and reviews.',
            modeLabel: 'View',
            tabMine: 'My projects',
            tabShared: 'Shared with me',
            searchLabel: 'Search projects',
            searchPlaceholder: 'Search…',
            newButton: 'New project',
            colName: 'Name',
            colVisibility: 'Visibility',
            colDocs: 'Documents',
            colMembers: 'Members',
            colUpdated: 'Updated',
            visibilityOwn: 'Own only',
            visibilityShared: 'Shared',
            emptyMine: 'No projects yet. Create one to get started.',
            emptyShared: 'No projects have been shared with you.',
            pageHint: 'Showing {shown} of {total}.',
            createTitle: 'New project',
            createSubmit: 'Create project',
            fieldName: 'Name',
            fieldDescription: 'Description (optional)',
            fieldCmNumber: 'Case/matter number (optional)',
            fieldVisibility: 'Visibility',
            errorNameRequired: 'Name is required.',
            errorSelectorRequired: 'Select a user or org group to add.',
            // Detail page
            renameButton: 'Rename',
            renamePrompt: 'Rename project',
            deleteButton: 'Delete',
            deleteConfirm: 'Delete this project? It will be retained for 30 days before permanent removal.',
            deleteFolderConfirm: 'Delete this folder? Documents inside will remain in the project, just unfiled.',
            removeMemberConfirm: 'Remove this member?',
            foldersHeading: 'Folders',
            foldersEmpty: 'No folders yet.',
            addFolderButton: 'New folder',
            addFolderTitle: 'New folder',
            documentsHeading: 'Documents',
            documentsEmpty: 'No documents attached yet.',
            colFilename: 'Filename',
            colAdded: 'Added',
            unattachButton: 'Unattach',
            membersHeading: 'Members',
            membersEmpty: 'Owner only.',
            addMemberButton: 'Add member',
            addMemberTitle: 'Add member',
            addMemberSubmit: 'Add member',
            colMember: 'Member',
            colRole: 'Role',
            selectorTypeLabel: 'Add by',
            selectorUser: 'User ID',
            selectorGroup: 'Org group ID',
            fieldUserId: 'User',
            fieldGroupId: 'Org group',
            fieldRole: 'Role',
            roleAdmin: 'Admin',
            roleEditor: 'Editor',
            roleViewer: 'Viewer',
            notFound: 'Project not found.',
            // Sprint 47.5.9.3.1 — selector picker copy + ConfirmDialog titles.
            selectUserPlaceholder: 'Select a user…',
            selectGroupPlaceholder: 'Select an org group…',
            noUsersAvailable: 'No users available.',
            noGroupsAvailable: 'No org groups available.',
            loadFailed: 'Failed to load options.',
            deleteProjectTitle: 'Delete project?',
            deleteFolderTitle: 'Delete folder?',
            removeMemberTitle: 'Remove member?',
            removeMemberConfirmGeneric: 'Remove this member from the project?',
            addDocumentButton: 'Add document',
            addDocumentTitle: 'Add document to project',
            fieldDocument: 'Select document',
            selectDocPlaceholder: 'Search for a document...',
            ownerLabel: 'System Owner',
        },
        // Sprint 47.5.4a — Forseti chat shell (read-only). Streaming + tools land in 47.5.4b.
        chat: {
            sidebarLabel: 'Chats',
            overviewTitle: 'Forseti',
            overviewSubtitle: 'Your AI Legal & Compliance Advisor — grounded in your tenant data and your installed regulatory packs.',
            personaSelectorLabel: 'Persona',
            personaHeading: 'Choose a persona',
            startChatButton: 'Start a new chat',
            startingChat: 'Starting chat…',
            recentHeading: 'Recent chats',
            recentEmpty: 'No chats yet — start one above.',
            untitledChat: 'New chat',
            messageListLabel: 'Conversation',
            detailSubtitle: 'Persona: {persona}',
            composeHeading: 'Send a message',
            composeLabel: 'Message Forseti',
            composeDisabledPlaceholder: 'Message composition lands in the next sprint (47.5.4b — SSE streaming + tool-call loop).',
            composeDisabledHint: 'Read-only preview shipped in 47.5.4a. Streaming + tool calls land in 47.5.4b.',
            switchPersonaHeading: 'Persona',
            presetPersona: 'Preset persona',
            changePersona: 'Change persona',
            hidePersonaOptions: 'Hide persona options',
            starterHeading: 'Start with one of these actions',
            renameButton: 'Rename',
            renamePrompt: 'Rename chat',
            deleteButton: 'Delete',
            deleteConfirm: 'Delete this chat? It will be retained for 30 days before permanent removal.',
            notFound: 'Chat not found.',
            emptyChat: 'No messages yet — say hello to start a conversation.',
            roleUser: 'You',
            roleAssistant: 'Forseti',
            roleSystem: 'System',
            thinkingLabel: 'Thinking',
            toolCallPlaceholder: 'Tool call: ',
            toolResultPlaceholder: 'Tool result hidden — full UI lands in 47.5.4b.',
            docEditPlaceholder: 'Document edits proposed (V{version}).',
            disclaimer: 'Forseti supports your judgement — it does not replace licensed counsel.',
            // Sprint 47.5.4b — streaming + defenses keys.
            sendButton: 'Send',
            sending: 'Sending…',
            composePlaceholder: 'Ask Forseti…',
            composingPlaceholder: 'Forseti is responding…',
            streamingPlaceholder: 'Forseti is typing…',
            promptInjectionTitle: 'Forseti dropped some control tokens.',
            promptInjectionBody: 'Stripped patterns: {patterns}. Forseti will only see your message text.',
            promptInjectionInline: 'Forseti dropped control tokens ({patterns}) from your message.',
            rateLimitTitle: "You're sending messages too fast.",
            rateLimitRetryAfter: 'Try again in {seconds}s.',
            counselReviewTitle: 'Forseti is uncertain here.',
            counselReviewBody: 'Confirm with licensed counsel before acting on this answer.',
            legalConclusionRefused: 'Forseti refused to issue a legal conclusion without grounding. Try search_regulatory first or escalate to counsel.',
            // Sprint 47.5.4c — ToolCallCard, CreateTaskFromChatModal,
            // AutoRouteHint, ExportChatButton, MessageList affordances.
            toolCallDone: 'Done.',
            toolCallArgsLabel: 'Arguments',
            toolCallResultLabel: 'Result',
            toolResultLogged: 'Tool result captured in audit log.',
            toolResultAria: 'tool result',
            documentEditAria: 'document edit',
            createTaskFromMessage: 'Create task from this',
            createTaskHeading: 'Create task from this message',
            createTaskTitleLabel: 'Title',
            createTaskTitleRequired: 'Title is required.',
            createTaskDescriptionLabel: 'Description',
            createTaskAssigneeLabel: 'Assignee',
            createTaskAssigneePlaceholder: 'Select assignee…',
            createTaskAssigneeNone: '— unassigned —',
            createTaskDueDateLabel: 'Due date',
            createTaskWorkflowLabel: 'Linked workflow (optional)',
            createTaskWorkflowPlaceholder: 'Select a workflow…',
            createTaskWorkflowNone: '— none —',
            // Sprint 47.5.9.3.1 — replace window.prompt rename + window.confirm delete.
            renameModalSubmit: 'Rename',
            renameModalSaving: 'Saving…',
            deleteConfirmTitle: 'Delete chat?',
            createTaskSubmit: 'Create task',
            createTaskSubmitting: 'Creating…',
            cancelButton: 'Cancel',
            autoRouteHintMessage: "This question is more in {persona}'s wheelhouse.",
            autoRouteSwitchButton: 'Switch to {persona}',
            autoRouteSwitching: 'Switching…',
            handoffSuggestionMessage: 'Or open a new chat with {persona} carrying this question forward.',
            handoffSuggestionButton: 'Start new chat with {persona}',
            handoffSuggestionWorking: 'Opening…',
            handoffSuggestionDismiss: 'Not now',
            persona_display: {
                'forseti-generalist': 'Forseti',
                'forseti-compliance': 'Compliance Specialist',
                'forseti-legal': 'Legal Counsel',
                'forseti-dpo': 'DPO Advisor',
                'forseti-risk': 'Risk Analyst',
                'forseti-auditor': "Auditor's Eye",
            },
            exportButton: 'Export PDF',
            exportRunning: 'Exporting…',
            exportSuccess: 'Exported: {filename} (open File Browser).',
            // Sprint 47.5.7 — citation pill (renders [FrameworkCode Art.X §Y]).
            citation: {
                verifyBadge: '[VERIFY]',
                verifyAria: 'citation could not be verified — review before relying',
                popoverAria: 'Source for {label}',
                openSource: 'Open source page',
            },
        },
        // Sprint 47.8.0c — Forseti badge on the existing /admin/audit-trail
        // page. Detected by event_type prefix client-side; the full
        // grc_ai_audit_events UNION lives on the dedicated lineage page.
        auditTrail: {
            forsetiBadge: 'Forseti',
            forsetiBadgeTooltip: 'AI-generated event from the Forseti audit trail.',
        },
        // Sprint 47.8.0b — row-action variant of AdvisorRunPanel surfaced on
        // every list page for the 16 record types. Status-blind on the
        // client; the tool runner enforces mutation refusal server-side.
        rowAction: {
            shortLabel: 'Forseti',
            ask: 'Ask Forseti',
            tooltipOpen: 'Ask Forseti about this {recordType}',
            tooltipLocked: 'This {recordType} is locked — Forseti can answer questions but cannot make changes.',
        },
        // Sprint 47.5.8 — embeddable advisor panel (cross-module entry points).
        // Sprint 47.8.0a — visual redesign: avatar + heading template + R-39
        // disclaimer banner + per-record-type friendly labels.
        advisorRunPanel: {
            workbenchLabel: 'Forseti Workbench',
            currentRecordLabel: 'Current record',
            title: 'Forseti — about this record',
            heading: 'Forseti — {recordType}',
            summaryLabel: 'Summary',
            suggestedPersona: 'Suggested persona',
            askForseti: 'Ask Forseti',
            runWorkflow: 'Run a Workflow',
            viewLineage: 'View Audit Lineage',
            createPdplEvidencePack: 'Create PDPL Evidence Pack',
            prepareBreachDraft: 'Prepare notification draft',
            primaryActions: {
                projectSummary: 'Summarize Project Document',
                supplementSummary: 'Summarize Supplement Request',
                authoritySummary: 'Summarize Authority Request',
                rightsRequestResponse: 'Draft DSR Response',
                dpiaQuickReview: 'Run DPIA Quick Review',
                taskControlWalkthrough: 'Walk Through Control Evidence',
                vendorRiskNarrative: 'Generate Vendor Risk Narrative',
                incidentLetter: 'Draft PDPL Breach Letter',
                ropaSufficiency: 'Run RoPA Sufficiency Check',
                vendorDpaReview: 'Run Vendor DPA Review',
            },
            builtinWorkflowViews: {
                ropaSufficiency: {
                    title: 'RoPA Sufficiency Check',
                    notice: 'Runs the existing RoPA sufficiency workflow for this processing activity. The run may create draft follow-up tasks; operators still review and execute them.',
                },
                vendorDpaReview: {
                    title: 'Vendor DPA Review',
                    notice: 'Runs the existing Vendor DPA Review workflow for this vendor contract. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                vendorRiskNarrative: {
                    title: 'Vendor Risk Narrative',
                    notice: 'Runs the existing vendor risk narrative workflow for this vendor. Output remains draft evidence; the human risk owner reviews and approves it before use.',
                },
                incidentLetter: {
                    title: 'PDPL Breach Notification Letter',
                    notice: 'Runs the existing VN PDPL breach-notification letter workflow for this incident or playbook activation. Output remains draft only; formal regulator filing still requires human sign-off.',
                },
                projectSummary: {
                    title: 'Project Document Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this Forseti project. The workflow is read-only and does not create tasks, evidence, or record updates.',
                },
                supplementSummary: {
                    title: 'Supplement Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this supplement request. The workflow is read-only and does not create tasks, evidence, record updates, or filing decisions.',
                },
                authoritySummary: {
                    title: 'Authority Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this authority request. The workflow is read-only and does not create tasks, evidence, record updates, filing decisions, or authority-response drafts.',
                },
                rightsRequestResponse: {
                    title: 'DSR Response Draft',
                    notice: 'Runs the existing DSR response draft workflow for this rights request. Output remains draft only; sending and case updates still require the existing human-reviewed rights-request flow.',
                },
                dpiaQuickReview: {
                    title: 'DPIA Quick Review',
                    notice: 'Runs the existing DPIA Quick Review workflow for this DPIA. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                taskControlWalkthrough: {
                    title: 'Control Evidence Walkthrough',
                    notice: 'Runs the existing compliance-control walkthrough for this task. The workflow must retrieve regulatory anchors before citing them, does not issue legal conclusions or mark controls/evidence approved, and only creates draft evidence placeholders when the operator explicitly requests them.',
                },
            },
            breachDraftNotice: 'Forseti prepares draft content for the linked Mẫu số 08 only. It does not decide whether notification is required and does not file or send the form.',
            breachDraftReady: 'Mẫu số 08 draft ready for review',
            breachDraftReviewBeforeSelecting: 'Review before selecting',
            breachDraftResolved: {
                accepted: 'Accepted: {count} fields applied',
                partially_accepted: 'Partially accepted: {count} fields applied',
                rejected: 'Rejected: no fields applied',
            },
            workflowHeading: 'Relevant workflows',
            workflowLoading: 'Loading relevant workflows…',
            workflowEmpty: 'No suitable workflow found. Create a guided workflow for this record.',
            workflowError: 'Could not load relevant workflows.',
            selectWorkflow: 'Select {title}',
            workflowSelected: 'Workflow selected and ready to run against this record.',
            workflowRunSelected: 'Run selected workflow',
            workflowStarting: 'Starting workflow…',
            workflowRunError: 'Could not start this workflow.',
            workflowQuotaExhausted: 'Your Forseti usage quota is too low to start this workflow.',
            workflowTopUp: 'Review usage and request a top-up',
            workflowCreateGuided: 'Create a guided workflow for this record',
            lineageHeading: 'Audit lineage for this record',
            lineageLoading: 'Loading audit lineage…',
            lineageEmpty: 'No Forseti lineage events are recorded for this record.',
            lineageError: 'Could not load audit lineage.',
            retry: 'Retry',
            chatContextLoading: 'Loading record context…',
            chatGuidance: {
                general: 'Forseti can summarize this record, identify missing information, and suggest review steps.',
                privacy: 'Forseti can review privacy facts, check installed-framework obligations, and help prepare a draft for human review.',
                compliance: 'Forseti can identify compliance gaps, ground requirements in installed sources, and suggest accountable next steps.',
                legal: 'Forseti can summarize the record, locate grounded source material, and help prepare a draft for counsel review.',
                risk: 'Forseti can assess recorded risks, flag missing facts, and suggest proportionate review steps.',
                audit: 'Forseti can inspect evidence gaps, relate the record to installed controls, and suggest audit-ready follow-up.',
            },
            starterPrompts: {
                identifyGaps: 'What information or evidence is missing from this record?',
                reviewFrameworks: 'Which obligations from our installed frameworks should I review for this record?',
                prepareDraft: 'Help me prepare a draft based only on this record and grounded installed sources.',
                nextSteps: 'What review steps should I take next?',
                summarizeRecord: 'Summarize this record and flag points that need human review.',
                assessRisk: 'Assess the recorded risks, identify missing facts, and suggest review steps.',
            },
            disclaimer: 'Forseti supports your judgement — it does not replace licensed counsel.',
            recordType: {
                project: 'Project',
                dpia: 'DPIA',
                dossier_filing: 'Dossier Filing',
                transfer_assessment: 'Cross-Border Transfer',
                vendor: 'Vendor',
                vendor_contract: 'Vendor Contract',
                vendor_acceptance: 'Vendor Risk Acceptance',
                incident: 'Incident',
                incident_op: 'Incident Operation',
                breach_notification: 'Breach Notification',
                authority_request: 'Authority Request',
                supplement_request: 'Supplement Request',
                rights_request: 'Rights Request',
                contract: 'Contract',
                task: 'Task',
                data_mapping_activity: 'RoPA Activity',
                compliance_rule: 'Compliance Rule',
                program: 'Program',
                governance_document: 'Governance Document',
                remediation_plan: 'Remediation Plan',
                cross_border_filing: 'Cross-Border Filing',
            },
        },
        // Sprint 47.9.6 — DPIA autocomplete cross-module workflow exemplar.
        // Pattern doc: docs/engineering/forseti-cross-module-workflows.md.
        autocomplete: {
            button_label: 'Ask Forseti to draft this',
            drafting: 'Drafting…',
            hint_pick_activity: 'Pick a linked processing activity to enable Forseti drafting.',
            draft_origin_label: 'Forseti draft — review per field',
            tokens_label: '≈ {count} tokens from your monthly bucket',
            accept_selected: 'Accept selected',
            reject_all: 'Reject draft',
            field_checkbox_label: 'Apply Forseti draft for {field}',
            // Sprint 48.4.x — translatable labels for the 9 DpiaAutocompleteFieldKey
            // enum values so the per-field review UI never renders raw English
            // snake_case identifiers (i18n-raw-key-as-label fix).
            field_labels: {
                description: 'Description',
                purpose: 'Purpose',
                data_subjects: 'Data subjects',
                data_types: 'Data types',
                necessity_justification: 'Necessity & proportionality',
                consultation_notes: 'Consultation notes',
                dpo_opinion: 'DPO opinion',
                system_ids: 'Linked systems',
                vendor_ids: 'Linked vendors',
            },
            error_generic: 'Forseti could not draft this DPIA. Try again or refine the activity record.',
            // Sprint 48.1.0 — framework variant chip group + conflict banner.
            variant_legend: 'Framework frame',
            variant_label_gdpr: 'GDPR',
            variant_label_pdpl: 'PDPL',
            variant_label_nis2: 'NIS2',
            variant_label_iso27001: 'ISO 27001',
            variant_label_soc2: 'SOC 2',
            conflict_banner: 'Heads up: {count} cross-framework conflict(s) apply to this variant. Review before accepting.',
        },
        // Sprint 48.1.3 — Cross-border TIA autocomplete (transfer-assessment workflow).
        tia: {
            button_label: 'Ask Forseti to draft this TIA',
            drafting: 'Drafting TIA…',
            hint_pick_destination: 'Set the destination country on the transfer record before Forseti can draft.',
            draft_origin_label: 'Forseti TIA draft — review per field',
            tokens_label: '≈ {count} tokens from your monthly bucket',
            usage_details: 'AI usage details',
            accept_selected: 'Accept selected',
            reject_all: 'Reject draft',
            applied_status: 'Draft accepted. Review the populated fields, then select Update.',
            recommended_updates_title: 'Recommended record updates',
            mechanism_recommendation: 'Transfer mechanism: {value}',
            dpo_signoff_recommendation: 'Require DPO sign-off for this sensitive transfer.',
            review_date_warning: 'Set a review deadline before updating this sensitive transfer.',
            processing_activity_warning: 'Link at least one processing activity / ROPA record before completing the TIA.',
            go_to_field: 'Go to field',
            field_checkbox_label: 'Apply Forseti draft for {field}',
            error_generic: 'Forseti could not draft this TIA. Try again or refine the transfer record.',
            adequacy_label: 'Destination regime',
            adequacy_status_adequacy_decision: 'Adequacy decision',
            adequacy_status_non_adequacy_with_sccs: 'Non-adequacy — SCCs required',
            adequacy_status_derogation: 'Art.49 derogation',
            adequacy_status_transitional: 'Transitional / EEA shortcut',
            adequacy_status_unknown: 'Unknown — set destination',
            conflict_banner: 'Heads up: {count} cross-framework conflict(s) apply to this transfer regime. Review before accepting.',
        },
        // Sprint 48.3.6 — shared draft output-guardrail labels (TIA + DPIA + all draft workflows).
        guardrails: {
            missing_facts_title: 'Missing required facts',
            missing_facts_hint: 'Forseti flagged facts that are not in the record. Fields that depend on them are not pre-selected — supply the facts or accept the field deliberately.',
            risk_label: 'Calibrated risk',
            risk_low: 'Low',
            risk_medium: 'Medium',
            risk_high: 'High',
            jurisdiction_label: 'Jurisdiction triggers',
            jurisdiction_gdpr: 'GDPR',
            jurisdiction_vn_pdpl: 'VN PDPL',
            trigger_applicable: 'Applies',
            trigger_not_applicable: 'Not triggered',
            trigger_unknown: 'Unknown — fact missing',
            recipient_role_label: 'Recipient role',
            recipient_role_controller: 'Controller',
            recipient_role_processor: 'Processor',
            recipient_role_joint_controller: 'Joint controller',
            recipient_role_unknown: 'Unknown — not stated',
            opt_in_hint: 'Legal conclusion — opt in only if you accept it with the facts on hand.',
        },
        // Sprint 47.9.7 — MCP connector admin (read-only Phase 1).
        mcp: {
            pageTitle: 'MCP Connector Keys',
            pageSubtitle: 'Issue API keys so external Claude clients can call read-only Forseti tools (regulatory search, document read, module search). Phase 1 — read-only. Write tools land in 48.3.0.',
            issueButton: 'Issue new key',
            issueModalTitle: 'Issue MCP API key',
            labelField: 'Label',
            labelHint: 'A short description (e.g. "AI capability lab — regulatory research").',
            permittedToolsField: 'Permitted tools',
            expiresAtField: 'Expires (optional)',
            expiresAtHint: 'Leave blank for never-expires.',
            submit: 'Issue key',
            cancel: 'Cancel',
            keyDisplayedOnce: 'Copy this key now — you will NOT be able to see it again. Store it in your secret manager (1Password, AWS Secrets Manager, Vault, etc.).',
            keyDisplayedOnceClose: 'I copied it — close',
            colLabel: 'Label',
            colPrefix: 'Prefix',
            colStatus: 'Status',
            colTools: 'Tools',
            colCreatedAt: 'Created',
            colLastUsed: 'Last used',
            colCalls: 'Calls',
            colActions: 'Actions',
            statusActive: 'Active',
            statusRevoked: 'Revoked',
            statusExpired: 'Expired',
            revokeAction: 'Revoke',
            rotateAction: 'Rotate',
            confirmRevokeTitle: 'Revoke this MCP key?',
            confirmRevokeBody: 'External Claude clients using this key will lose access immediately. This cannot be undone.',
            confirmRevoke: 'Revoke key',
            rotatedTitle: 'Key rotated',
            rotatedBody: 'The new plaintext key is below. The old key continues to work for 5 minutes so any in-flight calls finish.',
            emptyState: 'No MCP keys issued yet.',
            errorIssue: 'Could not issue the MCP key.',
            errorList: 'Could not load MCP keys.',
            errorRevoke: 'Could not revoke the key.',
            errorRotate: 'Could not rotate the key.',
            neverUsed: '—',
            neverExpires: 'Never',
        },
        // Sprint 48.0.0c — Forseti memory browse / curate.
        memory: {
            pageTitle: 'Forseti memory',
            pageSubtitle: 'Browse and curate the org-specific context Forseti has accumulated across conversations and decisions. Curated entries lock against nightly retention and stay available to every persona.',
            filterCategory: 'Category',
            filterAllCategories: 'All categories',
            totalCount: '{count} entries',
            colAnchor: 'Anchor',
            colCategory: 'Category',
            colSource: 'Source',
            colContent: 'Content',
            colCreatedAt: 'Created',
            colActions: 'Actions',
            editButton: 'Edit',
            deleteButton: 'Delete',
            promoteButton: 'Promote to curated',
            emptyState: 'No memories yet — Forseti will accumulate context as you chat.',
            categories: {
                framework_interpretation: 'Framework interpretation',
                process_preference: 'Process preference',
                escalation_threshold: 'Escalation threshold',
                decision_context: 'Decision context',
                glossary_term: 'Glossary term',
                auto_inferred: 'Auto-inferred',
            },
            sources: {
                admin_curated: 'Curated',
                user_explicit: 'Explicit',
                auto: 'Inferred',
            },
            deleteConfirmTitle: 'Delete this memory?',
            deleteConfirmBody: 'Deleting will remove this anchor from every future Forseti chat boot. Anchor: {anchor}',
            deleteConfirmLabel: 'Delete',
            deleteCancelLabel: 'Cancel',
            editModalTitle: 'Edit memory',
            editAnchorLabel: 'Anchor',
            editCategoryLabel: 'Category',
            editContentLabel: 'Content',
            editContentCount: '{n}/{max} characters',
            editEmptyError: 'Content cannot be empty.',
            editTooLongError: 'Content must be ≤1000 characters.',
            editAutoPromoteHint: 'Saving this auto-inferred memory will mark it as user-confirmed and remove it from the retention sweep.',
            editCancelButton: 'Cancel',
            editSaveButton: 'Save',
            editSaving: 'Saving…',
        },
        // Sprint 47.5.8 — audit lineage page (cross-module Forseti graph).
        auditLineage: {
            title: 'Audit lineage',
            subtitle: 'Trace every AI-generated artefact attached to a record. Chats, tool calls, tasks, evidence drafts, and document versions.',
            filterForm: 'Filters',
            recordType: 'Record type',
            recordId: 'Record ID',
            dateStart: 'From',
            dateEnd: 'To',
            search: 'Search',
            loading: 'Loading…',
            exportPdf: 'Export PDF',
            exportCsv: 'Export CSV',
            nodesHeading: 'Lineage nodes',
            colType: 'Type',
            colLabel: 'Label',
            colOccurredAt: 'Occurred at',
            colActor: 'Actor',
            colId: 'ID',
            noResults: 'No advisor activity for this record.',
            draftBadge: 'AI-generated draft',
            prev: 'Previous',
            next: 'Next',
            // Sprint 47.5.8.3 — optional graph view.
            viewToggleTable: 'View as table',
            viewToggleGraph: 'View as graph',
            graphAriaLabel: 'Lineage graph (visual). Use the table view for assistive technology.',
            graphLegendDraft: 'Draft AI evidence',
            graphEmpty: 'No nodes to graph.',
            // Sprint 47.5.9.5 — localised aria labels for the lineage page
            // pagination nav and the export status template (replaces the
            // hardcoded English `aria-label="pagination"` and the English
            // `Error: …` / `${format.toUpperCase()}: ${filename}` toasts).
            paginationAriaLabel: 'Pagination',
            exportSuccessTemplate: '{format}: {filename}',
            exportErrorTemplate: 'Export failed: {message}',
            recordTypes: {
                project: 'Project',
                dpia: 'DPIA',
                vendor: 'Vendor',
                incident: 'Incident',
                incident_op: 'Incident operation',
                rights_request: 'Rights request',
                contract: 'Contract',
                task: 'Task',
                data_mapping_activity: 'Data mapping activity',
                compliance_rule: 'Compliance rule',
                program: 'Program',
            },
            nodeTypes: {
                chat: 'Chat',
                tool_call: 'Tool call',
                workflow_run: 'Workflow run',
                task: 'Task',
                evidence: 'Evidence',
                document_version: 'Document version',
            },
        },
        // Sprint 47.5.x — Integrity Center verdict labels (consumed by IAT page).
        integrityCenter: {
            verdicts: {
                ok: 'OK',
                drifted: 'Drifted',
                hash_mismatch: 'Hash mismatch',
                merkle_mismatch: 'Merkle mismatch',
                source_missing: 'Source missing',
                not_found: 'Not found',
                chain_unreachable: 'Chain unreachable',
                service_unreachable: 'Service unreachable',
                pending: 'Pending anchoring',
            },
        },
        // Sprint 47.5.8 — evidence draft review chrome (move-to-review/approve/reject).
        evidenceDraft: {
            draftBadge: 'AI-generated DRAFT',
            pendingReviewBadge: 'Pending review',
            approvedBadge: 'Approved',
            rejectedBadge: 'Rejected',
            moveToReviewButton: 'Mark ready for review',
            approveButton: 'Approve',
            rejectButton: 'Reject',
            rejectReasonLabel: 'Rejection reason',
            humanSignoffBanner: 'Forseti supports your judgement — it does not replace licensed counsel. AI-generated evidence requires human approval before it counts as formal evidence.',
            // Sprint 47.5.8.6 — DraftEvidencePage chrome.
            pageTitle: 'AI evidence drafts',
            pageSubtitle: 'Review, approve, or reject AI-generated evidence. Drafts must be approved by a human before they count as formal evidence.',
            filterAll: 'All actionable',
            filterDrafts: 'Drafts only',
            filterPending: 'Pending review',
            filterApproved: 'Recently approved',
            filterRejected: 'Recently rejected',
            colId: 'Evidence ID',
            colStatus: 'Status',
            colSource: 'Source',
            colCreatedAt: 'Created',
            colActions: 'Actions',
            empty: 'No AI evidence drafts in this status.',
            confirmApprove: 'Approve this AI-generated evidence as formal?',
            confirmReject: 'Reject this evidence draft. This cannot be undone.',
            reasonRequired: 'A rejection reason is required.',
            rejectModalTitle: 'Reject draft evidence',
            // fix-sprint-20260503-2128 F39 — symmetric approve modal title.
            approveModalTitle: 'Approve draft evidence',
            operationFailed: 'Operation failed. Please try again.',
            moveToReviewSuccess: 'Marked ready for review.',
            approveSuccess: 'Approved.',
            rejectSuccess: 'Rejected.',
            // Sprint 47.5.9.3.3 (T-3) — source-label templates. The frontend
            // builds the source label at render time from the resolved title
            // (when projected by the backend) or the raw ID (legacy fallback).
            sourceLabelWorkflowRunTitle: 'Workflow: {title}',
            sourceLabelWorkflowRunId: 'Workflow run {id}',
            sourceLabelChatTitle: 'Chat: {title}',
            sourceLabelChatId: 'Forseti chat {id}',
            sourceLabelEmpty: '—',
            // Sprint 47.5.9.5 — localised aria-label for the filters group.
            filtersAriaLabel: 'Filter status',
        },
        // Sprint 47.5.5 — Forseti AI Workflows.
        workflows: {
            pageTitle: 'AI Workflows',
            pageSubtitle: 'Saved AI prompt templates that produce tasks and draft evidence.',
            libraryOverviewLabel: 'Workflow library overview',
            libraryEyebrow: 'Workflow library',
            libraryTitle: 'Turn compliance records into review-ready work',
            libraryDescription: 'Choose a governed workflow, select its source record, and review every result before it changes operational data.',
            availableLabel: 'Available',
            myWorkflowsLabel: 'Yours',
            customBadge: 'Custom',
            openWorkflow: 'Open workflow',
            governedWorkflowLabel: 'Governed built-in workflow',
            readyToRunHeading: 'Ready for an operator-reviewed run',
            workflowProfileHeading: 'Workflow profile',
            outputsHeading: 'Expected outputs',
            humanReviewLabel: 'Human review required',
            newWorkflow: 'New workflow',
            newWorkflowHeading: 'New AI workflow',
            tabAll: 'All',
            tabBuiltin: 'Built-in pack',
            tabMine: 'My workflows',
            tabDetail: 'Details',
            tabLinkedTasks: 'Linked tasks',
            builtinBadge: 'Built-in',
            builtinSubtitle: 'Built-in workflow.',
            userSubtitle: 'Custom workflow.',
            emptyHint: 'No workflows yet.',
            // Sprint 47.7.4 AC-7 — surface a hung load with a 10s timeout fallback.
            loadTimeoutError: 'Could not load workflows after 10 seconds. Retry, or check the browser Network tab for a hung request.',
            // fix-sprint-20260504-2137 F18 — workflow list error messages.
            loadError: 'Failed to load workflows.',
            hideError: 'Hide failed.',
            unhideError: 'Unhide failed.',
            retry: 'Retry',
            producesTasks: 'Produces tasks',
            producesEvidence: 'Produces draft evidence',
            hideButton: 'Hide from my list',
            unhideButton: 'Unhide',
            runButton: 'Run',
            editButton: 'Edit',
            shareButton: 'Share',
            backToList: 'Back to list',
            requireTitleAndPrompt: 'Title and prompt are required.',
            fieldTitle: 'Title',
            fieldDescription: 'Description',
            fieldType: 'Type',
            fieldPersona: 'Persona affinity',
            fieldPractice: 'Practice',
            fieldPrompt: 'Prompt (Markdown)',
            fieldProducesTasks: 'Produces tasks',
            fieldProducesEvidence: 'Produces draft evidence',
            fieldProjectId: 'Project (optional)',
            fieldAttachmentIds: 'Attachments',
            fieldParentRecord: 'Parent record (optional)',
            fieldPersonaOverride: 'Persona override (optional)',
            typeAssistant: 'Assistant',
            typeTabular: 'Tabular',
            personaNone: '— none —',
            parentNone: '— none —',
            parentIdPlaceholder: 'Parent record ID',
            // Sprint 47.5.9.3.1 — selector picker copy.
            selectProjectPlaceholder: 'Select a project…',
            selectProjectFirst: 'Select a project to choose attachments.',
            selectAttachmentsPlaceholder: 'Select documents…',
            noAttachmentsInProject: 'This project has no attached documents.',
            selectParentRecordPlaceholder: 'Select a record…',
            selectParentRecordFirst: 'Select a record type first.',
            noRecordsForType: 'No records of this type are available.',
            shareUserSelectorLabel: 'Recipients (users)',
            shareGroupSelectorLabel: 'Recipients (org groups)',
            selectUsersPlaceholder: 'Select users…',
            selectOrgGroupsPlaceholder: 'Select org groups…',
            loadingOptions: 'Loading…',
            loadFailed: 'Failed to load options.',
            parentRecordTypes: {
                dpia: 'DPIA',
                vendor: 'Vendor',
                incident: 'Incident',
                rights_request: 'Rights Request',
                contract: 'Contract',
                data_mapping_activity: 'RoPA Activity',
                compliance_rule: 'Compliance Rule',
                program: 'Program',
            },
            cancelButton: 'Cancel',
            closeButton: 'Close',
            createButton: 'Create',
            creating: 'Creating…',
            saveButton: 'Save',
            saving: 'Saving…',
            shareWorkflowHeading: 'Share workflow',
            shareIntro: 'Sharing: {title}',
            shareUserIds: 'User IDs (space or comma separated)',
            shareGroupIds: 'Org Group IDs (space or comma separated)',
            shareAllowEdit: 'Allow recipients to edit',
            shareRequireRecipient: 'Provide at least one user id or group id.',
            shareSuccess: 'Shared.',
            sharing: 'Sharing…',
            runWorkflowHeading: 'Run: {title}',
            runStarting: 'Starting…',
            runStatusLabel: 'Status: {status}',
            runProducedCounts: 'Tasks: {tasks} · Draft evidence: {evidence} · Attachments: {attachments}',
            runPanelHeading: 'Workflow run',
            runPanelHeadingWithTitle: 'Workflow run: {title}',
            runViewTasks: 'View generated tasks',
            runViewEvidence: 'View draft evidence',
            runResultHeading: 'Advisor result',
            runNoResult: 'The run finished but did not return a reviewable result. No tasks, evidence, or attachments were created. Try again with the required record or documents selected.',
            runCitationWarning: 'Some citations could not be verified automatically. Review them before relying on this result.',
            runLoadFailed: 'Could not load the workflow run.',
            runStreamFailed: 'Workflow progress connection failed.',
            // Sprint 47.5.9.5 — translated workflow-run status labels + locale-aware
            // SSE progress lines (replaces the English-only `Status: …`,
            // `Task created: …`, `Draft evidence created: …` strings rendered live
            // in the WorkflowRunPanel). Per finding 3.16 in the 47.5.9.3 review,
            // raw ObjectIds are stripped from the visible label — the user sees
            // the human action, not the database key.
            runStatusValues: {
                pending: 'Pending',
                running: 'Running',
                completed: 'Completed',
                succeeded: 'Succeeded',
                failed: 'Failed',
                cancelled: 'Cancelled',
            },
            runProgressStatus: 'Status: {status}',
            runProgressTaskCreated: 'Task created.',
            runProgressEvidenceDrafted: 'Draft evidence created.',
            runProgressAttachmentGenerated: 'Attachment generated.',
            guidedWorkflowTitle: 'Guided {recordType} workflow',
            guidedWorkflowDescription: 'Created for {recordType} records. Review and customize this workflow before saving.',
            guidedWorkflowPrompt: 'Review the supplied {recordType} record, identify missing facts, and propose grounded next steps for human review.',
            costHint: 'Pre-run quota check fires on submit; over-quota runs return 402 with a top-up CTA.',
            quotaExhausted: 'Token bucket low. Top up before re-running.',
            promptHeading: 'Prompt',
            howItWorksHeading: 'What happens when you run this workflow',
            builtinGuidance: 'Forseti reviews the records and documents you select, returns an operator-reviewable result, and creates draft tasks or evidence only when the result supports them. Nothing is approved or submitted automatically.',
            editWorkflowHeading: 'Edit workflow',
            linkedTasksHint: 'Tasks created by this workflow show up in the Tasks list filtered by this workflow id. Cross-link arrives in 47.5.8.',
            notFound: 'Workflow not found.',
            // Sprint 47.7.2 — scheduled workflow runs.
            schedules: {
                tabTitle: 'Schedules',
                sectionHeading: 'Scheduled runs',
                empty: 'No schedules yet. Create one to run this workflow automatically.',
                addButton: 'Add schedule',
                addHeading: 'Schedule workflow',
                editHeading: 'Edit schedule',
                fieldFrequency: 'Frequency',
                fieldDayOfWeek: 'Day of week',
                fieldDayOfMonth: 'Day of month',
                fieldTime: 'Time (UTC)',
                fieldTimezone: 'Timezone',
                fieldProject: 'Project (optional)',
                fieldAttachments: 'Default attachments',
                fieldPersonaOverride: 'Persona override (optional)',
                frequencyDaily: 'Daily',
                frequencyWeekly: 'Weekly',
                frequencyMonthly: 'Monthly',
                weekdaySun: 'Sunday',
                weekdayMon: 'Monday',
                weekdayTue: 'Tuesday',
                weekdayWed: 'Wednesday',
                weekdayThu: 'Thursday',
                weekdayFri: 'Friday',
                weekdaySat: 'Saturday',
                statusActive: 'Active',
                statusPaused: 'Paused',
                nextRunLabel: 'Next run',
                lastRunLabel: 'Last run',
                pauseButton: 'Pause',
                resumeButton: 'Resume',
                deleteButton: 'Delete',
                triggerButton: 'Run now',
                savingButton: 'Saving…',
                saveButton: 'Save',
                triggered: 'Run started — check the run history.',
            },
            // Sprint 47.7.2 — conditional branching in workflow prompts.
            branching: {
                helpHeading: 'Conditional prompt syntax',
                helpBody: 'Use {% if attachment.framework_code == "vn-pdpl" %}…{% endif %} to vary the prompt by context. Allowed variables: attachment.framework_code, attachment.locale, project.name, project.tier, persona.slug, org.country, org.locale. Nested {% if %} blocks are not supported.',
            },
        },
        // Sprint 47.7.3 — cross-framework conflict detection.
        crossFrameworkConflicts: {
            pageTitle: 'Cross-framework conflicts',
            pageSubtitle: 'Curated catalog of where your installed regulatory packs interact in conflicting ways. Forseti surfaces; humans decide.',
            noInstalledPacks: 'No regulatory packs installed for this organization — install at least two packs to surface cross-framework conflicts.',
            noConflicts: 'No conflicts match the current filters.',
            allAcknowledged: 'Every visible conflict has been acknowledged. Nice work.',
            columnSeverity: 'Severity',
            columnTitle: 'Title',
            columnFrameworks: 'Frameworks',
            columnTopic: 'Topic',
            columnStatus: 'Status',
            filterFrameworkPlaceholder: 'All frameworks',
            filterTopicPlaceholder: 'All topics',
            filterSeverityPlaceholder: 'All severities',
            filterStatusPlaceholder: 'All statuses',
            filterStatusAcknowledged: 'Acknowledged',
            filterStatusUnacknowledged: 'Unacknowledged',
            severityCritical: 'Critical',
            severityHigh: 'High',
            severityMedium: 'Medium',
            severityLow: 'Low',
            statusAcknowledged: 'Acknowledged',
            statusInReview: 'In review',
            statusResolved: 'Resolved',
            statusWontFix: "Won't fix",
            statusUnacknowledged: 'Unacknowledged',
            drawerSummaryHeading: 'Summary',
            drawerSideAHeading: 'Side A',
            drawerSideBHeading: 'Side B',
            drawerRequirementsLabel: 'Requirements cited',
            drawerSuggestedResolutionHeading: 'Suggested resolution',
            drawerLegalCounselBadge: 'Legal counsel review required',
            drawerAckHeading: 'Acknowledgement',
            drawerAckStatusLabel: 'Status',
            drawerAckAssigneeLabel: 'Assigned to',
            drawerAckDueDateLabel: 'Due date',
            drawerAckNotesLabel: 'Notes',
            drawerAckButtonAcknowledge: 'Acknowledge',
            drawerAckButtonUpdate: 'Update',
            drawerAckButtonUnacknowledge: 'Un-acknowledge',
            searchPlaceholder: 'Search title or summary…',
            // Sprint 47.9.1 — explicit search submit affordance.
            searchButton: 'Search',
            searchButtonAriaLabel: 'Run conflict search',
            // Sprint 47.9.1 — XLSX export sibling of the existing CSV path.
            exportSuccessTemplate: 'CSV: {filename}',
            exportSuccessTemplateXlsx: 'XLSX: {filename}',
            exportErrorTemplate: 'Export failed: {message}',
            exportAriaLabel: 'Export conflicts',
        },
        // Sprint 47.5.6 — Forseti Tabular Reviews (multi-document extraction grid).
        tabularReviews: {
            pageTitle: 'Tabular Reviews',
            pageSubtitle: 'Compare N documents across M attributes with grounded citations.',
            newReview: 'New review',
            newReviewHeading: 'New tabular review',
            emptyHint: 'No tabular reviews yet.',
            docsLabel: '{n} docs',
            colsLabel: '{n} cols',
            detailSubtitle: '{docs} docs · {cols} columns',
            addColumn: 'Add column',
            addColumnHeading: 'Add column',
            columnInfo: 'View prompt & format',
            columnInfoAria: 'Show prompt and format for column {name}',
            columnInfoPrompt: 'Prompt',
            columnInfoFormat: 'Return format',
            columnInfoPresets: 'Allowed values',
            columnInfoCitations: 'Citations',
            columnInfoCitationsRequired: 'Required',
            removeColumn: 'Remove column',
            removeColumnAria: 'Remove column {name}',
            removeColumnConfirm: 'Remove the column "{name}"? Its extracted cells will be deleted.',
            deleteReview: 'Delete',
            deleteReviewAria: 'Delete review {title}',
            deleteReviewConfirm: 'Delete the review "{title}"? This permanently removes its columns, cells, and discussions.',
            runAll: 'Run all',
            running: 'Running…',
            cancel: 'Cancel',
            exportXlsx: 'Export Excel',
            exportCsv: 'Export CSV',
            exporting: 'Exporting…',
            backToList: 'Back to list',
            notFound: 'Tabular review not found.',
            noDocuments: 'Attach documents to start the review.',
            noColumns: 'Add columns to define what to extract.',
            tableAriaLabel: 'Extraction grid',
            // fix-sprint-20260504-2137 F15 — TRTable thread aria-labels.
            threadsCountAria: '{n} discussion post(s)',
            startDiscussionAria: 'Start discussion',
            docColumn: 'Document',
            statusPending: 'Pending',
            statusRunning: 'Running…',
            statusFailed: 'Failed: {msg}',
            statusCancelled: 'Cancelled',
            citationsCount: '{n} citation(s)',
            citationsView: 'View source passages',
            citationPage: 'Page {n}',
            titleRequired: 'Title is required.',
            fieldTitle: 'Title',
            fieldProjectId: 'Project (optional)',
            fieldAttachmentIds: 'Attachments',
            fieldColumnName: 'Column name',
            fieldColumnNameHelp: 'A short, descriptive label for this column in the extraction grid.',
            columnNamePlaceholder: 'e.g. Contract Start Date',
            fieldExtractionPrompt: 'Extraction prompt',
            fieldExtractionPromptHelp: 'Describe the information you want to extract. Be specific to get better results.',
            columnPromptPlaceholder: 'What should Forseti look for in the documents?',
            fieldReturnFormat: 'Return format',
            fieldReturnFormatHelp: 'Controls how the AI formats its response (e.g. as a date or a number).',
            fieldPresets: 'Allowed values (one per line)',
            fieldPresetsPlaceholder: 'yes\nno\npartial',
            fieldCitationsRequired: 'Citation required',
            formatShortText: 'Short text',
            formatLongText: 'Long text',
            formatEnum: 'Enum',
            formatNumber: 'Number',
            formatDate: 'Date',
            formatList: 'List',
            columnRequireFields: 'Name and prompt are required.',
            addColumnError: 'Add column failed.',
            cancelButton: 'Cancel',
            createButton: 'Create',
            creating: 'Creating…',
            addButton: 'Add',
            adding: 'Adding…',
            runConfirmHeading: 'Run extraction',
            estimateBody: 'Estimated ~{units} billable units ({confidence} confidence).',
            costHint: 'Pre-run quota check fires on confirm; over-quota runs return 402 with a top-up CTA.',
            quotaExhausted: 'Token bucket low. Top up before re-running.',
            starting: 'Starting…',
            confirmRun: 'Confirm run',
            // Sprint 47.7.2 — column-level charts panel.
            charts: {
                title: 'Column charts',
                tabsAria: 'Chart columns',
                // fix-sprint-20260504-2137 F12/F13/F14 — chart aria/empty/excluded i18n.
                enumAria: 'Enum value distribution',
                numberAria: 'Number distribution histogram',
                dateAria: 'Date timeline',
                enumEmpty: 'No completed cells.',
                numberEmpty: 'No numeric values.',
                numberExcluded: '{n} cell(s) excluded (empty / non-numeric / not completed).',
                dateEmpty: 'No date values.',
                dateExcluded: '{n} cell(s) excluded.',
                unsupportedFormat: 'Unsupported column format.',
            },
            // Sprint 47.7.2 — per-cell discussion threads.
            threads: {
                drawerTitle: 'Discussion',
                loading: 'Loading…',
                empty: 'No posts yet. Start the conversation.',
                composerLabel: 'New comment',
                composerPlaceholder: 'Add a comment…',
                post: 'Post',
                posting: 'Posting…',
                edit: 'Edit',
                editLabel: 'Edit post',
                delete: 'Delete',
                save: 'Save',
                cancel: 'Cancel',
                close: 'Close',
                editedBadge: 'edited',
                // fix-sprint-20260504-2137 F19 — thread drawer error i18n.
                loadError: 'Failed to load thread',
                postError: 'Failed to post',
                editError: 'Failed to edit',
                deleteError: 'Failed to delete',
            },
            // Sprint 47.7.2 — cross-review comparison page.
            compare: {
                pageTitle: 'Compare reviews',
                leftReviewLabel: 'Left review',
                rightReviewLabel: 'Right review',
                pickReview: 'Pick a review to compare',
                noSharedDocuments: 'These reviews share no documents — comparison requires at least one common attachment.',
                sharedDocumentsHeading: 'Shared documents',
                leftOnlyHeading: 'Columns only in the left review',
                rightOnlyHeading: 'Columns only in the right review',
                differs: 'Differs',
                identical: 'Identical',
                emptyValue: '—',
                compareButton: 'Compare with…',
                // fix-sprint-20260504-2137 F2/F3/F4/F5 — compare page hardcoded strings.
                documentHeading: 'Document',
                errorMissingIds: 'Both left and right review ids are required.',
                errorComparisonFailed: 'Comparison failed.',
                errorLoadFailed: 'Failed to load reviews.',
                pickerLoading: 'Loading…',
                pickerNoOtherReviews: 'No other reviews available to compare with.',
                pickerCancel: 'Cancel',
            },
        },
        // Sprint 47.5.3a — Forseti Document Review (versions + AI edits).
        documentReview: {
            pageTitle: 'Document review',
            pageSubtitle: 'Accept or reject AI-suggested edits to this document.',
            sidePanelLabel: 'AI redlines',
            sidePanelHeading: 'AI redlines',
            noPendingEdits: 'No pending edits.',
            pendingCount: '{count} pending',
            acceptAllButton: 'Accept all',
            rejectAllButton: 'Reject all',
            acceptAllTitle: 'Accept all pending edits?',
            acceptAllConfirm: 'This will accept {count} pending edits and create one or more new versions. Continue?',
            editsHeading: 'Edits',
            editsEmpty: 'No edits to review.',
            versionsHeading: 'Version history',
            versionsEmpty: 'No versions yet.',
            editLabel: 'Edit',
            editLabelWithId: 'Edit #{id}',
            acceptButton: 'Accept',
            rejectButton: 'Reject',
            accepting: 'Accepting…',
            rejecting: 'Rejecting…',
            diffDeletedAria: 'Deleted',
            diffInsertedAria: 'Inserted',
            statusPending: 'Pending',
            statusAccepted: 'Accepted',
            statusRejected: 'Rejected',
            versionLabel: 'Version',
            versionAriaLabel: 'Version {n}',
            versionAriaLabelCurrent: 'Version {n} ({current})',
            versionCurrentTag: 'current',
            versionSourceLabel: 'Source',
            versionCreatedLabel: 'Created',
            versionModelLabel: 'Model',
            sourceUpload: 'Upload',
            sourceUserUpload: 'User upload',
            sourceAssistantEdit: 'Assistant edit',
            sourceUserAccept: 'User accepted edit',
            sourceUserReject: 'User rejected edit',
            sourceGenerated: 'Generated',
        },
        // Sprint 47.8.0a — Document Review index page (replaces blank screen at
        // `/admin/forseti/document-review`).
        documentReviewIndex: {
            sidebarLabel: 'Document Review',
            pageTitle: 'Document Review',
            pageSubtitle: 'Documents in your organisation that have AI-suggested edits pending review or Forseti-sourced versions.',
            colDocument: 'Document',
            colLinkedRecord: 'Linked record',
            colPendingEdits: 'Pending edits',
            colLastActivity: 'Last activity',
            colOpen: 'Open',
            open: 'Open',
            empty: 'No documents under Forseti review yet. Documents enter this queue when Forseti suggests an edit during a chat, when an AI workflow produces a redline, or when you send a document for review using the button above.',
            loading: 'Loading…',
            loadError: 'Failed to load documents under review.',
            retryButton: 'Retry',
            noLinkedRecord: '—',
            pendingZero: 'None',
            // Sprint 47.7.4 AC-4 — Send-document-for-review CTA + modal labels.
            sendForReviewButton: 'Send a document for review',
            sendForReviewTitle: 'Send a document for review',
            sendForReviewSubtitle: 'Pick an existing attachment from this organisation, or upload a new file. The document is added to the review queue and Forseti will produce a redline pass against it.',
            sendForReviewTabPick: 'Pick existing',
            sendForReviewTabUpload: 'Upload new',
            sendForReviewPickerLabel: 'Search attachments',
            sendForReviewPickerPlaceholder: 'Type to search…',
            sendForReviewPickerEmpty: 'No matching attachments. Try a different search.',
            sendForReviewSubmit: 'Send for review',
            sendForReviewCancel: 'Cancel',
            sendForReviewSubmitting: 'Sending…',
            sendForReviewError: 'Could not send the document for review. Please try again.',
            sendForReviewSuccess: 'Document sent for review.',
            // fix-sprint-20260504-2137 F16 — file validation messages.
            sendForReviewAcceptedExt: 'Accepted: {ext}.',
            sendForReviewMaxSize: 'Max size {mb} MB.',
        },
        // fix-sprint-20260504-2137 F17 — Forseti chats (export errors).
        chats: {
            exportError: 'Export failed.',
        },
    },
    // Sprint 58.4.0 (Phase 25 / Sprint E1a) — PDPL Evidence Pack provider-config wizard.
    // Sprint 58.4.1 + 58.4.2 extend this namespace with pack-builder, draft-editor,
    // send and timeline sub-namespaces; keep keys grouped under `providers` so the
    // later sprints can extend without breaking the existing surface.
    pdplEvidencePacks: {
        title: 'PDPL Evidence Packs',
        subtitle: 'Customer-controlled MoPS A05 evidence submission',
        nav: {
            emailProviders: 'Email Providers',
        },
        providers: {
            pageTitle: 'Email Providers',
            pageSubtitle: "Configure your organisation's outbound mail provider for PDPL evidence-pack submissions to MoPS A05.",
            architectureRuleHeading: 'Architecture rule (locked)',
            architectureRuleBody: 'AesirX is not a sender of evidence-pack email. Every submission uses a provider you own and control. AesirX never sends on your behalf.',
            empty: 'No providers configured yet.',
            createCta: 'Create provider',
            statusPendingConsent: 'Awaiting consent',
            statusPendingConnectionCheck: 'Awaiting connection check',
            statusActive: 'Active',
            statusConnectionCheckFailed: 'Connection check failed',
            statusDisabled: 'Disabled',
            acceptConsentCta: 'Accept consent',
            runConnectionCheckCta: 'Run connection check',
            disableCta: 'Disable',
            disableConfirm: 'Disabling this provider stops direct sends until a new provider is configured and consent is re-acknowledged. Continue?',
            columnDisplayName: 'Name',
            columnFromAddress: 'From address',
            columnStatus: 'Status',
            columnLastCheck: 'Last check',
            columnActions: 'Actions',
            createModalTitle: 'New SMTP provider',
            createDisplayNameLabel: 'Display name',
            createFromAddressLabel: 'From address',
            createReplyToLabel: 'Reply-to address (optional)',
            createSmtpHostLabel: 'SMTP host',
            createSmtpPortLabel: 'SMTP port',
            createSmtpUsernameLabel: 'SMTP username',
            createSmtpPasswordLabel: 'SMTP password',
            createSmtpEncryptionLabel: 'Encryption',
            createSmtpEncryptionStarttls: 'STARTTLS',
            createSmtpEncryptionTls: 'TLS (implicit)',
            createSmtpEncryptionNone: 'None',
            createOauthM365Tab: 'Microsoft 365',
            createOauthGoogleTab: 'Google Workspace',
            createOauthDisabledBadge: 'Coming soon',
            createOauthDisabledHint: 'OAuth providers are not yet enabled. Please use SMTP for now.',
            createSubmitCta: 'Create provider',
            createCancelCta: 'Cancel',
            createErrorInvalidFromAddress: 'From address must be a valid email mailbox.',
            createErrorDuplicateFromAddress: 'Another provider is already configured for this from-address.',
            createErrorGeneric: 'Could not create the provider. Please review the form and try again.',
            consentModalTitle: 'Acknowledge architecture rule',
            consentLocaleToggleEn: 'English',
            consentLocaleToggleVi: 'Tiếng Việt',
            consentAcknowledgeCta: 'I acknowledge — accept consent',
            consentCancelCta: 'Cancel',
            consentErrorMismatch: 'The consent text was modified and no longer matches the canonical disclaimer. Re-open this modal and submit again.',
            testModalTitle: 'Connection check',
            testRunningCta: 'Testing connection…',
            testRunCta: 'Run connection check',
            testRetryCta: 'Retry connection check',
            testSuccessHeading: 'Connection successful',
            testSuccessBody: 'Authenticated against {host}:{port} in {latencyMs} ms. The provider is ready for direct sends.',
            testFailureHeading: 'Connection failed',
            testFailureBody: 'The probe could not complete. Review the reason below and retry once corrected.',
            testFailureReasonLabel: 'Failure reason',
            testCloseCta: 'Close',
            detailDrawerTitle: 'Provider details',
            detailDrawerHistoryHeading: 'Connection check history',
            detailDrawerConsentHeading: 'Consent acknowledgement',
            detailDrawerNoHistory: 'No connection checks recorded yet.',
            detailDrawerNoConsent: 'Consent not yet acknowledged.',
            outcomeSuccess: 'Success',
            outcomeFailure: 'Failure',
        },
        // Sprint 58.4.1 (Phase 25 / Sprint E1b) — Pack Builder + Email Draft + Sent-Proof UI.
        builder: {
            pageTitle: 'Assemble evidence pack',
            pageSubtitle: 'Upload the 6 part files, pick the pack type, and assemble the deterministic 8-part ZIP for submission.',
            packTypeLabel: 'Pack type',
            packTypeDpia: 'DPIA (Mẫu số 02a)',
            packTypeOtia: 'OTIA (Mẫu số 02b)',
            part01CoverLetter: 'Part 01 — Cover letter (PDF)',
            part02AuthorityForm: 'Part 02 — Authority form (PDF)',
            part03Supporting: 'Part 03 — Supporting evidence (≥1)',
            part04Safeguards: 'Part 04 — Safeguards',
            part04SafeguardsRequiredOtia: '(required for OTIA)',
            part05PriorCorrespondence: 'Part 05 — Prior correspondence (optional)',
            part06Signature: 'Part 06 — Signature + stamp evidence (≥1)',
            uploadPickFile: 'Pick file',
            uploadPickFiles: 'Pick files',
            uploadRemoveFile: 'Remove',
            requiredMissing: 'Required part missing',
            assembleCta: 'Assemble pack',
            assembling: 'Assembling…',
            assembleSuccessHeading: 'Pack assembled',
            assembleSuccessSummary: '{fileCount} files · {bytes} bytes · SHA-256 {shaPrefix}…',
            composeNextCta: 'Compose email draft',
            warningsHeading: 'Validation warnings',
            assembleError: 'Could not assemble the pack. Review the inputs and try again.',
            forsetiAssistHeading: 'Forseti AI assistance (optional)',
            forsetiAssistHint: 'Run the PDPL Evidence Pack Assembly Advisor to surface missing parts, draft cover-letter prose, and flag safeguard gaps. AesirX is not the sender — the AI only advises; you approve every artefact.',
            forsetiAssistCta: 'Open Forseti workflow library',
            forsetiAiRunIdLabel: 'Linked Forseti AI run ID (paste to record AI assistance)',
        },
        draft: {
            pageTitle: 'Email draft',
            pageSubtitle: 'Compose and edit the email draft. Direct-send arrives in a follow-up release.',
            composeCta: 'Compose draft',
            composing: 'Composing…',
            noDraftYet: 'No draft yet. Compose one to get started.',
            localeToggleLabel: 'Locale',
            localeEn: 'English',
            localeVi: 'Tiếng Việt',
            subjectLabel: 'Subject',
            bodyLabel: 'Body',
            saveCta: 'Save changes',
            saving: 'Saving…',
            saveError: 'Could not save the draft. Please try again.',
            manifestSummaryLabel: 'Manifest summary',
            coverLetterLabel: 'Cover letter PDF',
            coverLetterDownloadCta: 'Download PDF',
            editHistoryHeading: 'Edit history',
            editHistoryEmpty: 'No edits yet.',
            editHistoryEntry: 'v{version} · {fields} · {when} · {who}',
            unofficialChannelHeading: 'NQ22 unofficial-channel notice',
            uploadSentProofCta: 'Upload manual sent-proof',
            versionLabel: 'Version',
            statusDraft: 'Draft',
            statusEdited: 'Edited',
            statusFinalized: 'Finalized',
        },
        sentProof: {
            modalTitle: 'Upload manual sent-proof',
            architectureLockNote: 'AesirX is not the sender. This proof captures the manual transmission you completed via your own mail client.',
            fileLabel: 'Proof file (.eml, .png, .jpg)',
            envelopeFromLabel: 'From',
            envelopeToLabel: 'To',
            envelopeSubjectLabel: 'Subject (at send)',
            envelopeSentAtLabel: 'Sent at',
            envelopeAttachmentShaLabel: 'Attachment SHA-256 (at send)',
            uploadCta: 'Upload proof',
            uploading: 'Uploading…',
            uploadSuccess: 'Sent-proof uploaded.',
            uploadErrorInvalidMime: 'File type not allowed. Use .eml, .png, .jpg, or .jpeg.',
            uploadErrorGeneric: 'Could not upload the proof. Please try again.',
            existingProofsHeading: 'Recorded sent-proofs',
            existingProofsEmpty: 'No sent-proofs recorded yet.',
            proofEntry: '{when} · {filename}',
        },
        packDetail: {
            pageTitle: 'Evidence pack',
            notFoundMessage: 'Pack not found — it may have been removed or the link is incorrect.',
            manifestHeading: 'Manifest',
            dossierLabel: 'Dossier',
            packShaLabel: 'Pack SHA-256',
            fileCountLabel: 'Files',
            bytesLabel: 'Size',
            composeDraftCta: 'Compose draft',
            sendComingSoonCta: 'Direct send: coming soon',
            sendComingSoonHint: 'The direct-send flow is in active development. Use Upload manual sent-proof on the draft page until it ships.',
            sendCta: 'Send via configured provider',
            sendDisabledNoProviderHint: 'Configure a customer-controlled email provider first. AesirX never sends on your behalf.',
            timelineHeading: 'Send history',
        },
        // Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + timeline + authority response + hard-copy follow-up.
        send: {
            // Send dialog
            dialogTitle: 'Send evidence pack',
            providerPickerLabel: 'Configured email provider',
            providerPickerHint: 'AesirX never sends on your behalf. Pick one of your configured providers below.',
            providerPickerEmpty: 'No active providers are configured. Configure one on the Email Providers page before sending.',
            providerPickerEmptyCta: 'Open Email Providers',
            draftPickerLabel: 'Email draft',
            draftPickerOption: '{locale} · v{version}',
            draftPickerEmpty: 'No drafts have been composed for this pack yet. Compose one on the draft page first.',
            recipientToLabel: 'To',
            recipientToHint: 'One or more email addresses, separated by commas.',
            recipientCcLabel: 'Cc (comma-separated)',
            recipientCcHint: 'Optional. Comma-separated list of email addresses.',
            recipientBccLabel: 'Bcc (comma-separated)',
            recipientBccHint: 'Optional. Comma-separated list of email addresses.',
            consentReaffirmHeading: 'Re-affirm provider consent',
            submitCta: 'Send now',
            sending: 'Sending…',
            cancelCta: 'Cancel',
            sendSuccess: 'Send recorded. The pack has been transmitted via your configured provider.',
            sendErrorGeneric: 'Could not send the pack. Please review the error below and try again.',
            sendErrorAlreadyFinalized: 'This draft has already been used for a successful send. Compose a new draft before sending again.',
            sendErrorInvalidRecipients: 'One or more recipient addresses are not valid email mailboxes.',
            // Timeline
            timelineEmpty: 'No sends recorded yet. Use Send via configured provider above to transmit through your customer-controlled SMTP / Microsoft 365 / Google Workspace provider.',
            timelineProviderLabel: 'Sent via your configured provider',
            timelineSentAtLabel: 'Sent',
            timelineRecipientLabel: 'Recipient',
            timelineOutcomeSent: 'Sent',
            timelineOutcomeFailed: 'Failed',
            timelineViewDetailsCta: 'View details',
            // Authority-response chips
            authorityChipNone: 'Awaiting response',
            authorityChipAcknowledgement: 'Acknowledgement',
            authorityChipAcceptance: 'Acceptance',
            authorityChipRejection: 'Rejection',
            authorityChipSupplementRequest: 'Supplement request',
            authorityChipNoResponse: 'No response',
            // Hard-copy chips
            hardCopyChipNone: 'No hard-copy follow-up',
            hardCopyChipPending: 'Posted — delivery pending',
            hardCopyChipDelivered: 'Delivered',
            // Drawer
            drawerTitle: 'Send record',
            drawerEnvelopeHeading: 'Envelope',
            drawerEnvelopeFromLabel: 'From',
            drawerEnvelopeToLabel: 'To',
            drawerEnvelopeCcLabel: 'Cc',
            drawerEnvelopeBccLabel: 'Bcc',
            drawerEnvelopeSubjectLabel: 'Subject',
            drawerEnvelopeMessageIdLabel: 'Message-ID',
            drawerOutcomeHeading: 'Outcome',
            drawerSnapshotHeading: 'Chain-of-custody snapshot',
            drawerSnapshotPackShaLabel: 'Pack SHA-256 at send',
            drawerSnapshotDraftVersionLabel: 'Draft version at send',
            drawerSnapshotAttachmentLabel: 'Transmitted ZIP',
            drawerSnapshotAttachmentDownloadCta: 'Download ZIP',
            drawerAuthorityResponseHeading: 'Authority response',
            drawerHardCopyHeading: 'Hard-copy follow-up',
            drawerSentByLabel: 'Sent by',
            drawerSendIdLabel: 'Send record ID',
            drawerSmtpResponseLabel: 'SMTP response',
            drawerFailureReasonLabel: 'Failure reason',
            drawerLatencyLabel: 'Latency',
            drawerLatencyValue: '{ms} ms',
            drawerAcceptedLabel: 'Accepted',
            drawerRejectedLabel: 'Rejected',
            drawerCloseCta: 'Close',
            // Authority-response panel
            authorityRecordCta: 'Record response',
            authorityReclassifyCta: 'Reclassify',
            authorityClassificationLabel: 'Classification',
            authorityReceivedAtLabel: 'Received at',
            authorityFreeTextLabel: 'Notes (optional)',
            authorityEvidenceLabel: 'Evidence file (.eml / .pdf / .png / .jpg — optional)',
            authorityReasonLabel: 'Reason for reclassification',
            authorityHistoryHeading: 'Reclassification history',
            authorityHistoryEmpty: 'No reclassifications recorded.',
            authorityHistoryEntry: '{when} · {previous} → {next} · {who}{reason}',
            authoritySubmitCta: 'Save response',
            authoritySubmitting: 'Saving…',
            authorityErrorInvalidMime: 'Evidence file type not allowed. Use .eml, .pdf, .png, or .jpg.',
            authorityErrorGeneric: 'Could not save the response. Please try again.',
            authorityRecordedAtLabel: 'Recorded at',
            authorityRecordedByLabel: 'Recorded by',
            authorityEvidenceFilenameLabel: 'Evidence',
            authorityEvidenceDownloadCta: 'Download evidence',
            // Hard-copy panel
            hardCopyRecordCta: 'Log hard-copy follow-up',
            hardCopyConfirmCta: 'Confirm delivery',
            hardCopyEditNotesCta: 'Edit delivery notes',
            hardCopyPostedAtLabel: 'Posted at',
            hardCopyCourierLabel: 'Courier',
            hardCopyTrackingLabel: 'Tracking number',
            hardCopyRecipientLabel: 'Recipient on label',
            hardCopyReceiptLabel: 'Receipt evidence (.pdf / .png / .jpg — optional)',
            hardCopyReceiptDownloadCta: 'Download receipt',
            hardCopyDeliveredAtLabel: 'Delivered at',
            hardCopyDeliveryNotesLabel: 'Delivery notes (optional)',
            hardCopySubmitCta: 'Save follow-up',
            hardCopySubmitting: 'Saving…',
            hardCopyErrorInvalidMime: 'Receipt file type not allowed. Use .pdf, .png, or .jpg.',
            hardCopyErrorGeneric: 'Could not save the follow-up. Please try again.',
        },
    },
};
//# sourceMappingURL=en.js.map